import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faFolder } from '@fortawesome/free-solid-svg-icons';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAuth } from './AuthContext';
import AdminPage from './AdminPage';
import ConfirmModal from './components/ConfirmModal';
import PollViewTabs from './components/PollViewTabs';
import DashboardViewTabs from './components/DashboardViewTabs';
import WatchlistViewTabs from './components/WatchlistViewTabs';
import GroupViewTabs from './components/GroupViewTabs';
import SettingsViewTabs from './components/SettingsViewTabs';
import ScheduleViewTabs from './components/ScheduleViewTabs';
import FilesTab from './components/FilesTab';
import ProfileMenu from './components/ProfileMenu';
import WaitlistPage from './components/WaitlistPage';
import useSwipe from './hooks/useSwipe';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { Card, CardHeader, CardContent, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';
import { Textarea } from './components/ui/textarea';
import { Separator } from './components/ui/separator';
import { 
  BookmarkIcon, 
  TrendingUpIcon, 
  SearchIcon, 
  PlusIcon, 
  BarChart3Icon,
  ExternalLinkIcon,
  UserIcon,
  CalendarIcon,
  HashIcon,
  ShareIcon,
  FolderIcon,
  FileTextIcon,
  UsersIcon,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AboutPage from './pages/AboutPage';
import SaveMethodsPage from './pages/SaveMethodsPage';
import QuickSavePage from './pages/QuickSavePage';
import WatchlistSpreadsheet from './pages/WatchlistSpreadsheet';
import PollSpreadsheet from './pages/PollSpreadsheet';
import AdminDashboard from './pages/AdminDashboard';
import LandingPageComponent from './components/LandingPageComponent';
import SubscriptionPricingModal from './components/SubscriptionPricingModal';
import TrialExpirationBanner from './components/TrialExpirationBanner';
import SubscriptionStatusBadge from './components/SubscriptionStatusBadge';
import ImportProgressIndicator from './components/ImportProgressIndicator';
import './App.css';

// Development-only logging utility - no-op in production
const isDev = process.env.NODE_ENV === 'development';
const devLog = isDev ? console.log.bind(console) : () => {};
const devWarn = isDev ? console.warn.bind(console) : () => {};
const devError = console.error.bind(console); // Always log errors

// Use empty string for API calls to leverage the CRACO proxy (which forwards /api/* to localhost:8001)
// This ensures the browser can reach the backend through the dev server proxy
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL !== undefined ? process.env.REACT_APP_BACKEND_URL : '';

// Format market cap/EV values for dashboard display (with $ prefix)
const formatFinancialValue = (value) => {
  if (value === null || value === undefined) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  // Handle negative numbers (e.g., negative EV for cash-heavy companies like BRK)
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (absNum >= 1e12) return `${sign}$${Math.round(absNum / 1e12)}T`;
  if (absNum >= 1e9) return `${sign}$${Math.round(absNum / 1e9)}B`;
  if (absNum >= 1e6) return `${sign}$${Math.round(absNum / 1e6)}M`;
  if (absNum >= 1e3) return `${sign}$${Math.round(absNum / 1e3)}K`;
  return `${sign}$${Math.round(absNum).toLocaleString()}`;
};

// Format large numbers without dollar sign (for share counts)
const formatLargeNumber = (value) => {
  if (value === null || value === undefined) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (absNum >= 1e12) return `${sign}${(absNum / 1e12).toFixed(2)}T`;
  if (absNum >= 1e9) return `${sign}${(absNum / 1e9).toFixed(2)}B`;
  if (absNum >= 1e6) return `${sign}${(absNum / 1e6).toFixed(2)}M`;
  if (absNum >= 1e3) return `${sign}${(absNum / 1e3).toFixed(2)}K`;
  return `${sign}${absNum.toLocaleString()}`;
};

// Format ratio values (P/E, EV/Sales, etc.)
const formatRatio = (value) => {
  if (value === null || value === undefined) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(2);
};

// Format percentage values
const formatPercent = (value) => {
  if (value === null || value === undefined) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return `${num.toFixed(2)}%`;
};

// Parse financial value with magnitude for sorting
const parseWithMagnitude = (val) => {
  if (typeof val === 'number') return val;
  if (val == null || val === '' || val === 'N/A' || val === '-') return null;
  const str = String(val).replace(/[$,%]/g, '').trim();
  const match = str.match(/^(-?\d+\.?\d*)\s*([TBMKtbmk])?$/);
  if (match) {
    let num = parseFloat(match[1]);
    const suffix = (match[2] || '').toUpperCase();
    if (suffix === 'T') num *= 1e12;
    else if (suffix === 'B') num *= 1e9;
    else if (suffix === 'M') num *= 1e6;
    else if (suffix === 'K') num *= 1e3;
    return num;
  }
  return parseFloat(str);
};

// Sort tickers within a category for Ideas List
const sortIdeasListTickers = (tickers, sortConfig, metricsCache) => {
  if (!tickers || tickers.length === 0) return [];
  if (!sortConfig.key || sortConfig.key === 'ticker') {
    const sorted = [...tickers].sort((a, b) => {
      const strA = String(a).toLowerCase();
      const strB = String(b).toLowerCase();
      return sortConfig.direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
    return sorted;
  }
  
  return [...tickers].sort((a, b) => {
    const dataA = metricsCache[a?.toUpperCase()] || {};
    const dataB = metricsCache[b?.toUpperCase()] || {};
    const valA = parseWithMagnitude(dataA[sortConfig.key]);
    const valB = parseWithMagnitude(dataB[sortConfig.key]);
    
    if (valA === null && valB === null) return 0;
    if (valA === null) return 1;
    if (valB === null) return -1;
    
    if (!isNaN(valA) && !isNaN(valB)) {
      return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    }
    return 0;
  });
};

// Test Dashboard Component for Multi-Model AI Comparison
function TestDashboard() {
  const [testPdfs, setTestPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Financial data testing states
  const [financialTickers, setFinancialTickers] = useState('AAPL,MSFT,GOOGL');
  const [financialData, setFinancialData] = useState(null);
  const [isLoadingFinancial, setIsLoadingFinancial] = useState(false);
  const [financialError, setFinancialError] = useState(null);

  // Fetch available PDF documents on component mount
  useEffect(() => {
    fetchTestPdfs();
  }, []);

  const fetchTestPdfs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-pdf-documents`);
      if (response.ok) {
        const data = await response.json();
        setTestPdfs(data.documents || []);
      } else {
        setError('Failed to fetch test documents');
      }
    } catch (err) {
      setError(`Error fetching test documents: ${err.message}`);
    }
  };

  const testFinancialData = async () => {
    setIsLoadingFinancial(true);
    setFinancialError(null);
    setFinancialData(null);

    try {
      const tickers = financialTickers.split(',').map(t => t.trim()).filter(t => t);
      
      const response = await fetch(`${API_BASE_URL}/api/financial-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tickers })
      });

      if (response.ok) {
        const data = await response.json();
        setFinancialData(data);
      } else {
        const errorData = await response.json();
        setFinancialError(`Financial data request failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setFinancialError(`Error fetching financial data: ${err.message}`);
    } finally {
      setIsLoadingFinancial(false);
    }
  };

  const formatCurrency = (value, currency) => {
    if (value === null || value === undefined) return 'N/A';
    
    let formatted;
    if (value >= 1_000_000_000_000) {
      formatted = `${Math.round(value / 1_000_000_000_000)}T`;
    } else if (value >= 1_000_000_000) {
      formatted = `${Math.round(value / 1_000_000_000)}B`;
    } else if (value >= 1_000_000) {
      formatted = `${Math.round(value / 1_000_000)}M`;
    } else {
      formatted = Math.round(value).toLocaleString();
    }
    
    const symbols = { 'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$' };
    const symbol = symbols[currency] || '';
    return `${symbol}${formatted} ${currency}`;
  };

  const runHaikuOnly = async (documentId) => {
    setIsLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/haiku-only-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_id: documentId })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        const errorData = await response.json();
        setError(`Haiku test failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error running Haiku test: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runPerplexityOnly = async (documentId) => {
    setIsLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/perplexity-only-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_id: documentId })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        const errorData = await response.json();
        setError(`Perplexity test failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error running Perplexity test: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runHaikuPerplexityTest = async (documentId) => {
    setIsLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/haiku-perplexity-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_id: documentId })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        const errorData = await response.json();
        setError(`Haiku + Perplexity test failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error running Haiku + Perplexity test: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runMultiModelComparison = async (documentId) => {
    setIsLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/multi-model-comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_id: documentId })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        const errorData = await response.json();
        setError(`Multi-model comparison failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Error running multi-model comparison: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-slate-800 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-2">🧪 Multi-Model AI Comparison</h1>
                <p className="text-slate-300">Compare Sonar, Sonar Pro, GPT-3.5 Turbo, and Gemini Flash 2.5 side-by-side</p>
              </div>
              <div className="bg-amber-500 text-amber-900 px-3 py-1 rounded-full text-sm font-medium">
                EXPERIMENTAL
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* PDF Selection */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4">Select PDF Document for Testing</h2>
              {testPdfs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <svg className="h-12 w-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p>No PDF documents available for testing</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {testPdfs.map((pdf, index) => (
                    <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-sm truncate">{pdf.sample_title}</h3>
                          <Badge variant="secondary" className="text-xs">{pdf.count} ideas</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mb-3 truncate">{pdf.filename}</p>
                        <div className="space-y-2">
                          <Button
                            onClick={() => runMultiModelComparison(pdf.sample_id)}
                            disabled={isLoading}
                            size="sm"
                            className="w-full"
                          >
                            {isLoading ? 'Comparing...' : 'Run All Models'}
                          </Button>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              onClick={() => runHaikuOnly(pdf.sample_id)}
                              disabled={isLoading}
                              size="sm"
                              variant="outline"
                              className="text-xs"
                            >
                              {isLoading ? 'Testing...' : 'Haiku Only'}
                            </Button>
                            <Button
                              onClick={() => runPerplexityOnly(pdf.sample_id)}
                              disabled={isLoading}
                              size="sm"
                              variant="outline"
                              className="text-xs"
                            >
                              {isLoading ? 'Testing...' : 'Perplexity Only'}
                            </Button>
                          </div>
                          <Button
                            onClick={() => runHaikuPerplexityTest(pdf.sample_id)}
                            disabled={isLoading}
                            size="sm"
                            variant="secondary"
                            className="w-full text-xs"
                          >
                            {isLoading ? 'Testing...' : 'Both (Combined)'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Financial Data Testing Section */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4">Financial Data Assistant Testing</h2>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Ticker Symbols (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={financialTickers}
                        onChange={(e) => setFinancialTickers(e.target.value)}
                        placeholder="AAPL,MSFT,GOOGL,TSLA"
                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Enter ticker symbols to get market capitalization and enterprise value data
                      </p>
                    </div>
                    
                    <Button
                      onClick={testFinancialData}
                      disabled={isLoadingFinancial}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {isLoadingFinancial ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Fetching Financial Data...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                          </svg>
                          Test Financial Data API
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Financial Data Results */}
            {financialError && (
              <Alert className="mb-6">
                <AlertDescription className="text-red-600">
                  {financialError}
                </AlertDescription>
              </Alert>
            )}

            {financialData && (
              <div className="mb-6">
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <h3 className="font-medium text-green-800">Financial Data Results</h3>
                      <Badge variant="outline" className="text-xs">
                        {financialData.data_count} companies
                      </Badge>
                    </div>
                    
                    <div className="space-y-3">
                      {financialData.financial_data && financialData.financial_data.map((company, index) => (
                        <div key={index} className="bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-sm">{company.company_name}</h4>
                              <div className="flex items-center gap-2 text-xs text-slate-600">
                                <Badge variant="secondary" className="text-xs">{company.ticker}</Badge>
                                <span>{company.exchange}</span>
                                <span>•</span>
                                <span>{company.currency}</span>
                              </div>
                            </div>
                            <div className="text-xs text-slate-500">
                              {company.last_updated && new Date(company.last_updated).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-slate-600">Market Cap:</span>
                              <div className="font-medium">
                                {company.market_cap ? formatCurrency(company.market_cap, company.currency) : 'N/A'}
                              </div>
                            </div>
                            <div>
                              <span className="text-slate-600">Enterprise Value:</span>
                              <div className="font-medium">
                                {company.enterprise_value ? formatCurrency(company.enterprise_value, company.currency) : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-xs text-slate-500">
                        Request processed at: {new Date(financialData.request_timestamp).toLocaleString()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <Alert className="mb-6">
                <AlertDescription className="text-red-600">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-8">
                <div className="inline-flex items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Running multi-model comparison...</span>
                </div>
              </div>
            )}

            {/* Test Results */}
            {testResults && (
              <div className="space-y-6">
                <div className="border-t pt-6">
                  {testResults.test_type === "haiku_only" && (
                    <h2 className="text-lg font-semibold mb-4">🔍 Haiku Only Test Results</h2>
                  )}
                  {testResults.test_type === "perplexity_only" && (
                    <h2 className="text-lg font-semibold mb-4">🔍 Perplexity Only Test Results</h2>
                  )}
                  {testResults.test_type === "haiku_perplexity_focused" && (
                    <h2 className="text-lg font-semibold mb-4">🔍 Haiku + Perplexity Debug Results</h2>
                  )}
                  {!testResults.test_type && (
                    <h2 className="text-lg font-semibold mb-4">🎯 Multi-Model Comparison Results</h2>
                  )}
                  
                  {/* Document Info */}
                  <Card className="mb-4 bg-blue-50">
                    <CardContent className="p-4">
                      <h3 className="font-medium mb-2">Document Information</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-600">Document:</span>
                          <div className="font-medium truncate">{testResults.document_info?.document_title}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Models Tested:</span>
                          <div className="font-medium">
                            {testResults.test_type === "haiku_only" || testResults.test_type === "perplexity_only" ? "1" :
                             testResults.test_type === "haiku_perplexity_focused" ? "2 (Haiku + Perplexity)" : 
                             (testResults.comparison_results?.models_tested?.length || testResults.test_results?.summary?.total_models_tested || 0)}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-600">Content Length:</span>
                          <div className="font-medium">{testResults.document_info?.content_length?.toLocaleString()} chars</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Total Time:</span>
                          <div className="font-medium">
                            {(testResults.result?.processing_time || testResults.comparison_results?.total_execution_time || testResults.test_results?.total_execution_time)?.toFixed(2)}s
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Single Model Results */}
                  {(testResults.test_type === "haiku_only" || testResults.test_type === "perplexity_only") && testResults.result && (
                    <Card className={`border-l-4 ${testResults.result.success ? 'border-l-green-500' : 'border-l-red-500'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Badge className={testResults.result.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {testResults.result.model_name}
                            </Badge>
                            {testResults.result.success && (
                              <Badge variant="outline">
                                {testResults.result.ideas_found} ideas • {testResults.result.processing_time?.toFixed(2)}s
                              </Badge>
                            )}
                          </div>
                          <div className="text-right text-sm text-slate-600">
                            {testResults.result.success ? '✅ Success' : '❌ Failed'}
                          </div>
                        </div>
                        
                        {testResults.result.error ? (
                          <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                            Error: {testResults.result.error}
                          </div>
                        ) : (
                          <>
                            {testResults.result.distinct_ideas && testResults.result.distinct_ideas.length > 0 && (
                              <div className="space-y-2">
                                <span className="text-xs font-medium text-slate-600">Investment Ideas Found:</span>
                                {testResults.result.distinct_ideas.map((idea, ideaIndex) => (
                                  <div key={ideaIndex} className="bg-slate-50 p-2 rounded text-sm">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge variant="secondary" className="text-xs">{idea.primary_ticker}</Badge>
                                      <span className="font-medium text-xs">{idea.title}</span>
                                    </div>
                                    <p className="text-xs text-slate-700">{idea.content}</p>
                                    {idea.confidence && (
                                      <div className="text-xs text-slate-500 mt-1">
                                        Confidence: {(idea.confidence * 100).toFixed(0)}%
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {testResults.result.distinct_ideas && testResults.result.distinct_ideas.length === 0 && (
                              <div className="text-slate-500 text-sm italic">No investment ideas found</div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Focused Test Summary */}
                  {testResults.test_type === "haiku_perplexity_focused" && testResults.test_results?.summary && (
                    <Card className="mb-4 bg-amber-50 border-amber-200">
                      <CardContent className="p-4">
                        <h3 className="font-medium mb-2">🔍 Debug Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-slate-600">Haiku Ideas:</span>
                            <div className={`font-medium ${testResults.test_results.summary.haiku_success ? 'text-green-700' : 'text-red-700'}`}>
                              {testResults.test_results.summary.haiku_ideas} {testResults.test_results.summary.haiku_success ? '✅' : '❌'}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-600">Perplexity Ideas:</span>
                            <div className={`font-medium ${testResults.test_results.summary.perplexity_success ? 'text-green-700' : 'text-red-700'}`}>
                              {testResults.test_results.summary.perplexity_ideas} {testResults.test_results.summary.perplexity_success ? '✅' : '❌'}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-600">Total Ideas:</span>
                            <div className="font-medium text-blue-700">{testResults.test_results.summary.total_ideas_found}</div>
                          </div>
                          <div>
                            <span className="text-slate-600">Success Rate:</span>
                            <div className="font-medium text-blue-700">
                              {testResults.test_results.summary.successful_models}/{testResults.test_results.summary.total_models_tested}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Multi-Model Comparison Summary */}
                  {testResults.comparison_results?.comparison_summary && (
                    <Card className="mb-4 bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <h3 className="font-medium mb-2">📊 Comparison Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-slate-600">Total Ideas:</span>
                            <div className="font-medium text-green-700">{testResults.comparison_results.comparison_summary.total_ideas_found}</div>
                          </div>
                          <div>
                            <span className="text-slate-600">Best Performer:</span>
                            <div className="font-medium text-green-700">{testResults.comparison_results.comparison_summary.best_performer_by_ideas?.model}</div>
                          </div>
                          <div>
                            <span className="text-slate-600">Fastest Model:</span>
                            <div className="font-medium text-green-700">{testResults.comparison_results.comparison_summary.fastest_model?.model}</div>
                          </div>
                          <div>
                            <span className="text-slate-600">Success Rate:</span>
                            <div className="font-medium text-green-700">
                              {testResults.comparison_results.comparison_summary.successful_models}/{testResults.comparison_results.comparison_summary.total_models_tested}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Individual Model Results */}
                  {(testResults.comparison_results?.detailed_results || testResults.test_results?.detailed_results) && (
                    <div className="space-y-4">
                      <h3 className="font-medium">Model-by-Model Results</h3>
                      {(testResults.comparison_results?.detailed_results || testResults.test_results?.detailed_results).map((result, index) => (
                        <Card key={index} className={`${result.success ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-red-500'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Badge className={result.success ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"}>
                                  {result.model_name}
                                </Badge>
                                <span className="text-sm text-slate-600">{result.provider}</span>
                                {result.success && (
                                  <Badge variant="outline">
                                    {result.ideas_found} ideas • {result.processing_time?.toFixed(2)}s
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right text-sm text-slate-600">
                                {result.success ? '✅ Success' : '❌ Failed'}
                              </div>
                            </div>
                            
                            {result.error ? (
                              <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                                Error: {result.error}
                              </div>
                            ) : (
                              <>
                                {/* Investment Ideas */}
                                {result.distinct_ideas && result.distinct_ideas.length > 0 && (
                                  <div className="space-y-2">
                                    <span className="text-xs font-medium text-slate-600">Investment Ideas Found:</span>
                                    {result.distinct_ideas.map((idea, ideaIndex) => (
                                      <div key={ideaIndex} className="bg-slate-50 p-2 rounded text-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge variant="secondary" className="text-xs">{idea.primary_ticker}</Badge>
                                          <span className="font-medium text-xs">{idea.title}</span>
                                        </div>
                                        <p className="text-xs text-slate-700">{idea.content}</p>
                                        {idea.confidence && (
                                          <div className="text-xs text-slate-500 mt-1">
                                            Confidence: {(idea.confidence * 100).toFixed(0)}%
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {result.distinct_ideas && result.distinct_ideas.length === 0 && (
                                  <div className="text-slate-500 text-sm italic">No investment ideas found</div>
                                )}
                              </>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Dashboard Component
function MainDashboard() {
  // Use Auth Context
  const { user, loading: authLoading, logout } = useAuth();

  // =============================================
  // ENGAGEMENT TRACKING UTILITY
  // =============================================
  const trackEngagement = React.useCallback(async (eventType, eventCategory, eventData = {}) => {
    try {
      await fetch(`${API_BASE_URL}/api/track-engagement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          event_category: eventCategory,
          event_data: eventData
        })
      });
    } catch (e) {
      // Silent fail - don't break UX for analytics
      console.debug('Engagement tracking:', eventType);
    }
  }, []);

  // =============================================
  // SESSION & TIME TRACKING
  // =============================================
  const sessionStartRef = useRef(Date.now());
  const currentTabRef = useRef('ideas'); // Track current active tab
  const tabStartTimeRef = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());

  // Detect device type (mobile vs desktop) and OS (iOS vs Android)
  const getDeviceInfo = React.useCallback(() => {
    const ua = navigator.userAgent;
    
    // Detect OS
    let os = 'other';
    if (/Windows/i.test(ua)) os = 'windows';
    else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macos';
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) os = 'linux';
    else if (/iPhone|iPad|iPod/i.test(ua)) os = 'ios';
    else if (/Android/i.test(ua)) os = 'android';
    
    // Detect device type
    let deviceType = 'desktop';
    if (/iPhone|iPod/i.test(ua) || (/Android/i.test(ua) && /Mobile/i.test(ua))) {
      deviceType = 'mobile';
    } else if (/iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) {
      deviceType = 'tablet';
    } else if (/Mobi|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      deviceType = window.innerWidth <= 768 ? 'mobile' : 'tablet';
    }
    
    // Detect browser
    let browser = 'other';
    if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) browser = 'chrome';
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'safari';
    else if (/Firefox/i.test(ua)) browser = 'firefox';
    else if (/Edg/i.test(ua)) browser = 'edge';
    
    return { deviceType, os, browser };
  }, []);

  // Track session start and device type on mount
  useEffect(() => {
    const deviceInfo = getDeviceInfo();
    trackEngagement('session_start', 'session', { 
      device_type: deviceInfo.deviceType,
      os: deviceInfo.os,
      browser: deviceInfo.browser,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight
    });

    // Track session end on page unload
    const handleUnload = () => {
      const sessionDuration = Math.round((Date.now() - sessionStartRef.current) / 1000); // seconds
      // Use sendBeacon for reliable tracking on page close
      // Must use Blob with proper content-type for FastAPI to parse it
      const data = JSON.stringify({
        event_type: 'session_end',
        event_category: 'session',
        event_data: { 
          duration_seconds: sessionDuration,
          device_type: deviceInfo.deviceType,
          os: deviceInfo.os
        }
      });
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/api/track-engagement`, blob);
    };

    // Track visibility changes (tab switching, minimizing)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Track time spent on current tab before user leaves
        const tabDuration = Math.round((Date.now() - tabStartTimeRef.current) / 1000);
        if (tabDuration > 2) { // Only track if > 2 seconds
          trackEngagement('tab_time', 'session', { 
            tab: currentTabRef.current, 
            duration_seconds: tabDuration,
            device_type: deviceInfo.deviceType,
            os: deviceInfo.os
          });
        }
      } else {
        // Reset tab timer when user returns
        tabStartTimeRef.current = Date.now();
      }
    };
    
    // Listen for poll refresh events from child components
    const handleRefreshPolls = (event) => {
      if (event.detail) {
        setFishingThreads(event.detail);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('refreshPolls', handleRefreshPolls);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('refreshPolls', handleRefreshPolls);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [trackEngagement, getDeviceInfo]);

  // Track tab/section changes
  const trackTabChange = React.useCallback((newTab) => {
    const tabDuration = Math.round((Date.now() - tabStartTimeRef.current) / 1000);
    const deviceInfo = getDeviceInfo();
    
    // Track time on previous tab (if > 2 seconds)
    if (tabDuration > 2 && currentTabRef.current) {
      trackEngagement('tab_time', 'session', { 
        tab: currentTabRef.current, 
        duration_seconds: tabDuration,
        device_type: deviceInfo.deviceType,
        os: deviceInfo.os
      });
    }
    
    // Track tab view
    trackEngagement('tab_view', 'navigation', { 
      tab: newTab,
      device_type: deviceInfo.deviceType,
      os: deviceInfo.os
    });
    
    // Update refs
    currentTabRef.current = newTab;
    tabStartTimeRef.current = Date.now();
  }, [trackEngagement, getDeviceInfo]);

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPostImportModal, setShowPostImportModal] = useState(false); // Show after free import completes
  const [showSubscribeBanner, setShowSubscribeBanner] = useState(false); // Persistent banner for free users
  const [showWorkspace, setShowWorkspace] = useState(false); // Workspace mode toggle
  
  // Workspace panel configuration (which view each column shows)
  const [workspacePanels, setWorkspacePanels] = useState({
    left: 'ideas',    // ideas, calendar, tickers, files, requests, groups
    middle: 'calendar',
    right: 'files'
  });
  const [workspaceResetKey, setWorkspaceResetKey] = useState(0); // Key to force remount on reset
  
  // Cross-panel linking state - when an idea is selected, other panels can react
  const [workspaceSelectedItem, setWorkspaceSelectedItem] = useState(null);
  // Structure: { ticker: string, bookmarkId: string, source: 'ideas'|'calendar'|'files' }

  // Toggle body class for workspace mode to override scrollbar and padding
  useEffect(() => {
    if (showWorkspace) {
      document.body.classList.add('workspace-mode');
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overflowY = 'hidden';
      document.body.style.paddingBottom = '0';
    } else {
      document.body.classList.remove('workspace-mode');
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
      document.body.style.paddingBottom = '';
    }
    return () => {
      document.body.classList.remove('workspace-mode');
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
      document.body.style.paddingBottom = '';
    };
  }, [showWorkspace]);

  // State for cross-panel highlighting
  const [workspaceHighlightedTicker, setWorkspaceHighlightedTicker] = useState('');

  const [bookmarks, setBookmarks] = useState([]); // Start with empty array - will be populated by fetchData
  const [researchItems, setResearchItems] = useState([]); // Research library items
  const [libraryAvailableSources, setLibraryAvailableSources] = useState([]); // Available sources for library items
  // Library pagination state
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryHasMore, setLibraryHasMore] = useState(true);
  const [libraryTotalCount, setLibraryTotalCount] = useState(0);
  const [libraryLoadingMore, setLibraryLoadingMore] = useState(false);
  // Library tab controls
  const [libraryView, setLibraryView] = useState('feed'); // 'feed' or 'sources'
  const [librarySourceFilter, setLibrarySourceFilter] = useState('all');
  const [showLibrarySourceDropdown, setShowLibrarySourceDropdown] = useState(false);
  const [librarySortBy, setLibrarySortBy] = useState('newest');
  const [showLibrarySortDropdown, setShowLibrarySortDropdown] = useState(false);

  const [watchlists, setWatchlists] = useState([]);
  const [watchlistView, setWatchlistView] = useState('my'); // 'my' or 'received' - for tab toggle
  const [tickers, setTickers] = useState([]);
  const [fishingThreads, setFishingThreads] = useState([]);
  const [sharedPolls, setSharedPolls] = useState([]); // Polls shared with this user
  const [sharedWatchlists, setSharedWatchlists] = useState([]); // Watchlists shared with this user
  const [selectedPoll, setSelectedPoll] = useState(null); // Currently selected poll for filtering
  const [showPollDropdown, setShowPollDropdown] = useState(false); // Toggle for polls dropdown
  const [showNewPollDropdown, setShowNewPollDropdown] = useState(false); // Toggle for new poll creation dropdown
  const [showPollSortMenu, setShowPollSortMenu] = useState(false); // Toggle for poll sort dropdown
  const [pollSortConfig, setPollSortConfig] = useState({ key: 'ticker', direction: 'asc' }); // Poll ticker sort
  const [pollAgeSortBy, setPollAgeSortBy] = useState('newest'); // Age-based sort: newest, fresh, warm, stale
  const [pollTypeFilter, setPollTypeFilter] = useState('all'); // Poll type filter: all, ideas, x_poll
  const [newPollMode, setNewPollMode] = useState('custom_list'); // New poll creation mode: custom_list, requests, x_poll
  const [showPollTypeFilterMenu, setShowPollTypeFilterMenu] = useState(false); // Toggle for poll type filter dropdown
  const [pollView, setPollView] = useState('my'); // 'my' or 'received' - for tab toggle
  const [ideasListSortConfig, setIdeasListSortConfig] = useState({ key: 'ticker', direction: 'asc' }); // Ideas List sort config
  // Ticker metrics cache - stored in localStorage to persist across page loads
  const [tickerMetricsCache, setTickerMetricsCache] = useState(() => {
    try {
      const cached = localStorage.getItem('tickerMetricsCache');
      const cacheTimestamp = localStorage.getItem('tickerMetricsCacheTimestamp');
      if (cached && cacheTimestamp) {
        const cacheDate = new Date(cacheTimestamp).toDateString();
        const today = new Date().toDateString();
        // Use cached data if it's from today
        if (cacheDate === today) {
          const parsedCache = JSON.parse(cached);
          // Filter out entries that don't have valid metrics for display
          // MUST have market_cap (primary UI metric) AND at least one financial metric
          const validCache = {};
          let invalidCount = 0;
          Object.entries(parsedCache).forEach(([ticker, data]) => {
            const hasMarketCap = data?.market_cap !== undefined && data?.market_cap !== null;
            const hasFinancialMetrics = data?.gross_margin !== undefined || 
                                        data?.operating_margin !== undefined || 
                                        data?.profit_margin !== undefined || 
                                        data?.shares_outstanding !== undefined ||
                                        data?.forward_pe !== undefined;
            if (hasMarketCap && hasFinancialMetrics) {
              validCache[ticker] = data;
            } else {
              invalidCount++;
            }
          });
          if (invalidCount > 0) {
            console.log(`📊 Loaded ${Object.keys(validCache).length} valid tickers from cache, filtered out ${invalidCount} incomplete entries`);
          } else {
            console.log(`📊 Loaded ${Object.keys(validCache).length} tickers from localStorage cache`);
          }
          return validCache;
        }
      }
    } catch (e) {
      console.warn('Failed to load ticker metrics cache from localStorage:', e);
    }
    return {};
  }); // Cache for Twelve Data metrics
  const [expandedRollups, setExpandedRollups] = useState(new Set()); // Track expanded rollup cards
  const [expandedNestedIdeas, setExpandedNestedIdeas] = useState(new Set()); // Track expanded nested ideas within rollups
  const [expandedCardGroups, setExpandedCardGroups] = useState(new Set()); // Track expanded stacked card groups
  const [expandedIdeaCards, setExpandedIdeaCards] = useState(new Set()); // Track expanded individual idea cards
  const [expandedGroupIdeaCards, setExpandedGroupIdeaCards] = useState(new Set()); // Track expanded group idea cards
  const [expandedIdeasRowKey, setExpandedIdeasRowKey] = useState(null); // Track expanded Ideas List row for mobile dropdown (format: "bookmarkId-ticker")
  const [ideaCardMetricsTab, setIdeaCardMetricsTab] = useState({}); // Track which tab (profile/valuation) is active per card
  const [groupIdeaCardMetricsTab, setGroupIdeaCardMetricsTab] = useState({}); // Track which tab (profile/valuation) is active per group idea card
  const [ideaViewMode, setIdeaViewMode] = useState({}); // Track which view mode (summary/signal/wwnbt) is active per card
  const [signalCache, setSignalCache] = useState({}); // Cache for structured signals
  const [signalLoading, setSignalLoading] = useState({}); // Track loading state for signal extraction
  const [wwnbtCache, setWwnbtCache] = useState({}); // Cache for WWNBT analysis
  const [wwnbtLoading, setWwnbtLoading] = useState({}); // Track loading state for WWNBT extraction
  const [wwnbtError, setWwnbtError] = useState({}); // Track extraction errors per idea
  const [wwnbtExpanded, setWwnbtExpanded] = useState({}); // Track expanded state for WWNBT content
  const [wwnbtAgentChat, setWwnbtAgentChat] = useState({}); // Chat history for WWNBT action agent per idea
  const [wwnbtAgentLoading, setWwnbtAgentLoading] = useState({}); // Loading state for agent responses
  const [wwnbtAgentInput, setWwnbtAgentInput] = useState({}); // Input field state per idea
  const [irContactsCache, setIrContactsCache] = useState({}); // Cache for IR contacts (Perplexity)
  const [irContactsLoading, setIrContactsLoading] = useState({}); // Track loading state for IR contacts
  const [irEmailComposer, setIrEmailComposer] = useState(null); // { ticker, email, companyName, subject, body }
  const [sourceExpanded, setSourceExpanded] = useState({}); // Track if source content is expanded within a card
  const [expandedDemoCards, setExpandedDemoCards] = useState(new Set()); // Track expanded demo group cards
  const [sourceFilter, setSourceFilter] = useState('all'); // Filter by source: all, email, bookmark, etc.
  const [showSourceDropdown, setShowSourceDropdown] = useState(false); // Toggle for modern dropdown
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null); // Filter groups in dropdown
  const [showGroupDropdown, setShowGroupDropdown] = useState(false); // Toggle for groups dropdown
  const [showGroupSortMenu, setShowGroupSortMenu] = useState(false); // Toggle for group sort dropdown
  const [groupAgeSortBy, setGroupAgeSortBy] = useState('newest'); // Age-based sort for groups: newest, fresh, warm, stale
  const [showGroupAgeSortMenu, setShowGroupAgeSortMenu] = useState(false); // Toggle for group age sort dropdown
  const [groupTypeFilter, setGroupTypeFilter] = useState('all'); // Group type filter (no options yet)
  const [showGroupTypeFilterMenu, setShowGroupTypeFilterMenu] = useState(false); // Toggle for group filter dropdown
  
  // View All mode states - default to true so users see all items on first load
  const [viewAllPollsMode, setViewAllPollsMode] = useState(true);
  const [expandedPollIds, setExpandedPollIds] = useState(new Set());
  const [viewAllGroupsMode, setViewAllGroupsMode] = useState(true);
  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());
  const [expandedThreadIds, setExpandedThreadIds] = useState(new Set()); // Track expanded full threads
  const [fullPostModeCards, setFullPostModeCards] = useState(new Set()); // Track cards showing "Full post" instead of "Grok summary"
  const [scrollToIdeaTarget, setScrollToIdeaTarget] = useState(null); // { ticker, ideaId } - for navigating from Folders to Ideas
  
  const [stats, setStats] = useState({ total_bookmarks: 0, total_tickers: 0, total_watchlists: 0 });
  const [pendingIdeas, setPendingIdeas] = useState([]);
  // Track seen pending idea IDs (persisted to localStorage)
  const [seenPendingIds, setSeenPendingIds] = useState(() => {
    try {
      const saved = localStorage.getItem('seenPendingIds');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [hasNewSharedIdeas, setHasNewSharedIdeas] = useState(false); // True when there are NEW unseen shared ideas
  
  // Pagination state for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreIdeas, setHasMoreIdeas] = useState(true);
  const [totalIdeas, setTotalIdeas] = useState(0);  // Total investment ideas (including Ideas List tickers) for display
  const [totalCards, setTotalCards] = useState(0);  // Actual loadable cards for pagination
  const [myIdeasCount, setMyIdeasCount] = useState(0);  // Count of user's own ideas from API
  const [hasEverHadIdeas, setHasEverHadIdeas] = useState(false);  // Track if user has ever had ideas (persists through filtering)
  const [receivedIdeasCount, setReceivedIdeasCount] = useState(0);  // Count of received/shared ideas from API
  const [totalHandles, setTotalHandles] = useState(0);  // Count of unique handles for @ search
  const [uniqueHandlesList, setUniqueHandlesList] = useState([]);  // List of unique handles for @ dropdown
  const [briefsList, setBriefsList] = useState([]);  // List of all briefs for brief: dropdown
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Refs for infinite scroll (avoids stale closure issues)
  const currentPageRef = React.useRef(1);
  const hasMoreIdeasRef = React.useRef(true);
  const loadingMoreRef = React.useRef(false);
  const loadingRef = React.useRef(false);
  
  // Ref to track URL parameter handling (prevents multiple notifications)
  const urlParamHandledRef = React.useRef(false);
  
  // Ref to track first-seen position of each idea (prevents shuffling during pagination)
  // Maps idea ID -> first seen index position
  const ideaFirstSeenIndexRef = React.useRef(new Map());
  const nextFirstSeenIndexRef = React.useRef(0);
  
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  
  // Import state for X bookmarks
  const [importing, setImporting] = useState(() => {
    try {
      return localStorage.getItem('tickler_importing') === 'true';
    } catch {
      return false;
    }
  });
  const [showImportBanner, setShowImportBanner] = useState(() => {
    try {
      return localStorage.getItem('tickler_importing') === 'true';
    } catch {
      return false;
    }
  });
  const [hasImportedBefore, setHasImportedBefore] = useState(false);
  const [importProgress, setImportProgress] = useState(() => {
    try {
      const saved = localStorage.getItem('tickler_import_progress');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [hasSeenProgress, setHasSeenProgress] = useState(false); // Lock in bigger view once progress seen
  const [cancelling, setCancelling] = useState(false);
  const progressIntervalRef = React.useRef(null);
  
  // Progress polling function
  const startProgressPolling = React.useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    const pollingStartTime = Date.now();
    const MAX_POLLING_TIME = 10 * 60 * 1000; // Increased to 10 minutes for large imports
    let lastProcessed = -1;
    let stalledCount = 0;
    
    progressIntervalRef.current = setInterval(async () => {
      try {
        if (Date.now() - pollingStartTime > MAX_POLLING_TIME) {
          console.warn('Progress polling timeout - stopping after 10 minutes');
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
          setImporting(false);
          setHasSeenProgress(false);
          localStorage.removeItem('tickler_importing');
          localStorage.removeItem('tickler_import_progress');
          return;
        }
        
        const progressRes = await fetch(`${API_BASE_URL}/api/x/import-progress/${encodeURIComponent(user.email)}`);
        if (!progressRes.ok) {
          console.warn('Progress fetch failed, will retry');
          return; // Don't stop polling, just skip this cycle
        }
        
        const progress = await progressRes.json();
        
        // Debug logging for stuck detection
        if (progress.processed === lastProcessed && progress.status === 'processing') {
          stalledCount++;
          if (stalledCount > 30) { // 30 seconds of no progress
            console.warn(`Progress stalled at ${progress.processed}/${progress.total} for 30+ seconds`);
          }
        } else {
          stalledCount = 0;
          lastProcessed = progress.processed;
        }
        
        setImportProgress(progress);
        
        // Lock in bigger view once we see any progress data
        if (progress.total > 0 || progress.processed > 0 || progress.status === 'processing') {
          setHasSeenProgress(true);
        }
        
        // ONLY stop polling when we get a terminal status
        if (progress.status === 'complete' || progress.status === 'cancelled' || progress.status === 'error') {
          console.log(`Import finished with status: ${progress.status}`);
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
          
          // Keep the modal visible for 3 seconds so user can see final counts
          setTimeout(() => {
            setImporting(false);
            setHasSeenProgress(false);
            localStorage.removeItem('tickler_importing');
            localStorage.removeItem('tickler_import_progress');
          }, 3000);
          
          if (progress.status === 'complete') {
            const imported = progress.imported || 0;
            const curations = progress.categories?.curations || 0;
            const requests = progress.categories?.requests || 0;
            // Don't show alert - user will receive email notification
            console.log(`Import complete: ${imported} bookmarks, ${curations} curations, ${requests} requests`);
            
            // Trigger blue dot for watchlists/curations tab if we imported any
            if (curations > 0) {
              setHasNewWatchlists(true);
            }
            
            // Dispatch custom event to trigger WatchlistSpreadsheet refresh
            window.dispatchEvent(new CustomEvent('importComplete', { detail: { curations } }));
            
            fetchData(); // Refresh dashboard
          }
        }
      } catch (error) {
        console.error('Progress polling error:', error);
      }
    }, 1000);  // Poll every 1 second for smoother updates
  }, [user?.email]);
  
  // Start progress polling when import begins
  useEffect(() => {
    if (importing && user?.email) {
      startProgressPolling();
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [importing, user?.email, startProgressPolling]);
  
  // Dashboard view toggle: 'ideas' (current) or 'sources' (new grouped view)
  const [dashboardView, setDashboardView] = useState('ideas');
  const [ideaView, setIdeaView] = useState('my'); // 'my' or 'received' - for My Ideas / Received tabs
  const [settingsView, setSettingsView] = useState('account'); // 'account' or 'schedules' - for Settings tabs
  const [scheduleTabView, setScheduleTabView] = useState('calendar'); // 'calendar' or 'settings' - for Schedule tab
  const [expandedSources, setExpandedSources] = useState(new Set()); // Track expanded source cards
  const [apiUsage, setApiUsage] = useState({ daily_calls: 0, daily_limit: 3, remaining_calls: 3 });
  
  // LLM Model Experimentation
  const [selectedLLMModel, setSelectedLLMModel] = useState('sonar-reasoning'); // Current model
  const [llmPreviewData, setLlmPreviewData] = useState(null); // Preview of re-analyzed ideas
  const [showLLMPreview, setShowLLMPreview] = useState(false); // Show preview modal
  const [llmAnalyzing, setLlmAnalyzing] = useState(false); // Loading state for analysis
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showOnePageSummary, setShowOnePageSummary] = useState(false);
  const [onePageSummaryData, setOnePageSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareComplianceMode] = useState('pointer'); // Standard share mode
  const [showLandingModal, setShowLandingModal] = useState(false);
  const [secAvailability, setSecAvailability] = useState({ loading: true, hasFilings: false, hasForeignFilings: false, message: '' });
  const [shareData, setShareData] = useState({ friendEmail: '', shareMessage: '' });
  const [showOriginalContentModal, setShowOriginalContentModal] = useState(false);
  const [searchFileViewer, setSearchFileViewer] = useState({ show: false, url: null, ticker: null }); // For viewing files from search
  const [searchIdeaFilter, setSearchIdeaFilter] = useState(null); // For filtering ideas from search (stores idea or array of ideas)
  const [selectedOriginalContent, setSelectedOriginalContent] = useState(null);
  const savedScrollPositionRef = useRef(0); // Save scroll position when modals open
  
  // Tweet validation state (X Content Compliance)
  const [tweetValidationStatus, setTweetValidationStatus] = useState({
    validating: false,
    is_deleted: false,
    is_protected: false,
    error: null,
    last_validated: null
  });
  
  // Lock body scroll when Original Content Modal is open, preserve scroll position
  useEffect(() => {
    if (showOriginalContentModal) {
      // Save current scroll position before locking
      savedScrollPositionRef.current = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollPositionRef.current}px`;
      document.body.style.width = '100%';
    } else {
      // Restore scroll position when modal closes
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (savedScrollPositionRef.current > 0) {
        window.scrollTo(0, savedScrollPositionRef.current);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showOriginalContentModal]);
  
  const [showAIReactionModal, setShowAIReactionModal] = useState(false);
  const [selectedIdeaForReaction, setSelectedIdeaForReaction] = useState(null);
  const [aiReaction, setAIReaction] = useState(null);
  const [aiReactionLoading, setAIReactionLoading] = useState(false);
  const [tourStep, setTourStep] = useState(0); // 0 = tour not started, 1+ = step number
  const [showExpandTip, setShowExpandTip] = useState(false); // One-time "+X more" tooltip
  const folderIconRef = useRef(null);
  
  // Blue dot notification system
  const [hasNewIdeas, setHasNewIdeas] = useState(false);
  const [hasNewLibrary, setHasNewLibrary] = useState(false);
  const [hasNewPolls, setHasNewPolls] = useState(false);
  const [hasNewWatchlists, setHasNewWatchlists] = useState(false);
  const [hasNewFiles, setHasNewFiles] = useState(false);
  const [filesRefreshTrigger, setFilesRefreshTrigger] = useState(0); // Increments to trigger FilesTab refresh in workspace

  // Custom confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    warningMessage: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    onConfirm: () => {}
  });

  // Helper function to show confirm modal
  const showConfirmModal = (options) => {
    return new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure?',
        warningMessage: options.warningMessage || null,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        confirmButtonClass: options.confirmButtonClass || 'bg-red-600 hover:bg-red-700',
        onConfirm: () => resolve(true)
      });
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Custom notification system (matches bookmarklet design)
  const [notification, setNotification] = useState(null);
  const lastNotificationRef = useRef({ message: '', timestamp: 0 });
  
  const showNotification = useCallback((message, type = 'info', duration = 4000) => {
    const now = Date.now();
    // Prevent duplicate notifications within 5 seconds
    // Also block any import-related notification if one was just shown
    const lastMsg = lastNotificationRef.current.message.toLowerCase();
    const currentMsg = message.toLowerCase();
    const timeSinceLast = now - lastNotificationRef.current.timestamp;
    
    if (timeSinceLast < 5000) {
      // Same message - block
      if (lastMsg === currentMsg) {
        console.log('Duplicate notification blocked:', message);
        return;
      }
      // Both are import-related - block the second one
      if (lastMsg.includes('import') && currentMsg.includes('import')) {
        console.log('Duplicate import notification blocked:', message);
        return;
      }
    }
    lastNotificationRef.current = { message, timestamp: now };
    setNotification({ message, type, id: now });
    if (duration > 0) {
      setTimeout(() => setNotification(null), duration);
    }
  }, []);

  // Handle cancel import request
  const handleCancelImport = useCallback(async () => {
    if (!user?.email || cancelling) return;
    
    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/x/import/cancel/${encodeURIComponent(user.email)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        showNotification('Cancel requested. Import will stop shortly.', 'info');
      } else {
        showNotification('Failed to cancel import', 'error');
      }
    } catch (error) {
      console.error('Error cancelling import:', error);
      showNotification('Error cancelling import', 'error');
    } finally {
      setCancelling(false);
    }
  }, [user?.email, cancelling, showNotification]);

  // Ref to prevent double-click on import
  const importInProgressRef = useRef(false);

  // Direct import handler - bypasses modal, triggers import immediately
  const handleDirectBookmarkImport = async () => {
    // Check if user has X account linked
    if (!user?.x_username) {
      showNotification('Please log in with your X account to import bookmarks', 'error', 5000);
      return;
    }

    // Prevent double-clicks using both ref AND localStorage
    const importLockKey = 'tickler_import_lock';
    const lastImportTime = localStorage.getItem(importLockKey);
    const now = Date.now();
    
    // Block if import was triggered in last 30 seconds
    if (lastImportTime && (now - parseInt(lastImportTime)) < 30000) {
      showNotification('Import already in progress. Please wait...', 'info', 3000);
      return;
    }
    
    if (importInProgressRef.current) {
      showNotification('Import already in progress. Please wait...', 'info', 3000);
      return;
    }
    
    // Set both guards immediately
    importInProgressRef.current = true;
    localStorage.setItem(importLockKey, now.toString());
    
    // Start import silently - user sees progress modal, email sent on completion
    setImporting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/x/import-bookmarks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: user.email || user.id,
          max_results: 50,
          is_initial_import: false,
          send_completion_email: true
        })
      });
      
      if (response.ok) {
        // Import started successfully - check eligibility to show subscription modal/banner
        const userIdentifier = user.id || user.user_id || user.email;
        try {
          const eligibilityResponse = await fetch(`${API_BASE_URL}/api/import/eligibility/${encodeURIComponent(userIdentifier)}`);
          if (eligibilityResponse.ok) {
            const newEligibility = await eligibilityResponse.json();
            console.log('📊 Post-import eligibility (direct):', newEligibility);
            setImportEligibility(newEligibility);
            
            // If this was their free import and they're not a subscriber, show the post-import modal
            if (newEligibility.has_used_free_import && !newEligibility.is_subscriber) {
              console.log('🎉 Showing post-import modal and subscribe banner (direct import)');
              // Delay slightly so they see the success notification first
              setTimeout(() => {
                setShowPostImportModal(true);
                setShowSubscribeBanner(true); // Persist the banner
              }, 2000);
            }
          }
        } catch (e) {
          console.error('Failed to refresh eligibility:', e);
        }
      } else if (!response.ok) {
        // Import didn't start - hide the progress modal and release the lock
        setImporting(false);
        setHasSeenProgress(false);
        localStorage.removeItem('tickler_importing');
        localStorage.removeItem('tickler_import_progress');
        importInProgressRef.current = false;
        localStorage.removeItem(importLockKey);
        const error = await response.json();
        if (response.status === 402) {
          // Payment required - show subscription modal only (no notification spam)
          if (setShowPricingModal) {
            setShowPricingModal(true);
          }
        } else if (response.status === 429) {
          const errorDetail = typeof error.detail === 'object' ? error.detail : { message: error.detail };
          showNotification(`Daily limit reached: ${errorDetail.message || 'Please try again later'}`, 'error', 5000);
        } else if (response.status === 401 && (typeof error.detail === 'string' && (error.detail.includes('authorization expired') || error.detail.includes('reconnect')))) {
          // Token expired - show reauthorize modal
          setShowReauthorizeModal(true);
        } else {
          // Handle any other error
          let errorMessage = 'Failed to import';
          if (error.detail) {
            if (typeof error.detail === 'string') {
              errorMessage = error.detail;
            } else if (typeof error.detail === 'object' && error.detail.message) {
              errorMessage = error.detail.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
          showNotification(`Error: ${errorMessage}`, 'error', 5000);
        }
      }
    } catch (error) {
      console.error('Error importing bookmarks:', error);
      setImporting(false);
      setHasSeenProgress(false);
      localStorage.removeItem('tickler_importing');
      localStorage.removeItem('tickler_import_progress');
      showNotification('Failed to start import. Please try again.', 'error', 5000);
    } finally {
      // Reset guard after a delay to allow re-import later
      setTimeout(() => {
        importInProgressRef.current = false;
        localStorage.removeItem(importLockKey);
      }, 30000); // 30 second cooldown
    }
  };

  const [ideasLastChecked, setIdeasLastChecked] = useState(null);
  const [libraryLastChecked, setLibraryLastChecked] = useState(null);
  
  // All sources from database (not just currently loaded ideas)
  const [allSourceCategories, setAllSourceCategories] = useState(new Map());
  const [allSourceTypes, setAllSourceTypes] = useState(['all']);

  // Total number of tour steps - focused on core features (removed "Expand for More" step)
  const TOUR_STEPS = 7;

  // Admin emails for admin dashboard access
  const ADMIN_EMAILS = ["urijmiller@gmail.com"];
  
  // Check admin status - check BOTH user.email AND localStorage
  // This handles both authenticated users and localStorage-cached user data
  const getAdminStatus = () => {
    // First check auth user email
    if (user?.email && ADMIN_EMAILS.includes(user.email)) return true;
    // Then check localStorage user email
    try {
      const data = localStorage.getItem('tickler_user');
      if (data) {
        const storedEmail = JSON.parse(data)?.email;
        if (storedEmail && ADMIN_EMAILS.includes(storedEmail)) return true;
      }
    } catch { /* ignore */ }
    return false;
  };
  const isAdmin = getAdminStatus();
  
  // Get actual user email for API calls
  const getActualUserEmail = () => {
    if (user?.email && user.email !== 'demo@example.com') return user.email;
    try {
      const data = localStorage.getItem('tickler_user');
      if (data) return JSON.parse(data)?.email || user?.email;
    } catch { /* ignore */ }
    return user?.email;
  };
  const actualUserEmail = getActualUserEmail();

  // State for admin dashboard modal
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);

  // Cache management: Only clear old dashboard caches on mount to prevent quota issues
  useEffect(() => {
    try {
      // Remove old dashboard idea caches only (not all localStorage)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dashboard_ideas_v')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Silent fail
        }
      });
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleared ${keysToRemove.length} old dashboard caches`);
      }
    } catch (err) {
      console.error('Cache cleanup error:', err);
    }
  }, []);

  // Check if user has completed the tour
  useEffect(() => {
    const tourCompleted = localStorage.getItem('tickler_tour_completed');
    if (!tourCompleted && user) {
      // Start tour after a brief delay
      setTimeout(() => {
        setTourStep(1);
      }, 1000);
    }
  }, [user]);

  const nextTourStep = () => {
    if (tourStep < TOUR_STEPS) {
      setTourStep(tourStep + 1);
    } else {
      completeTour();
    }
  };

  const skipTour = () => {
    completeTour();
  };

  const completeTour = () => {
    setTourStep(0);
    localStorage.setItem('tickler_tour_completed', 'true');
    // Navigate to Ideas tab after completing the tour
    setActiveTab('dashboard');
  };

  // Bookmarklet code for draggable folder icon
  // Note: API_BASE_URL is already defined at the top of the file (line 46), using it here
  const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const userEmail = user?.email || 'demo@example.com';
  const bookmarkletCode = `javascript:(function(){if(window.ticklerModal){return;}var userId='${user?.id || 'demo-user'}';var capturedText=(document.body.innerText||'').substring(0,10000);var capturedArticle=document.querySelector('article')||document.querySelector('main')||document.body;var capturedHtml=(capturedArticle.innerHTML||'');var capturedTickers=(capturedText.match(/\\$[A-Z]{1,5}\\b/g)||[]).join(',');var capturedTitle=document.title;var capturedUrl=location.href;var s=document.createElement('style');s.innerHTML='@keyframes tSlide{0%{transform:translate(-50%,-100%);opacity:0}100%{transform:translate(-50%,0);opacity:1}}@keyframes tFade{from{opacity:0}to{opacity:1}}@keyframes tSpin{to{transform:rotate(360deg)}}';document.head.appendChild(s);var o=document.createElement('div');o.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:999998;animation:tFade 0.3s;backdrop-filter:blur(4px)';var m=document.createElement('div');m.id='tickler-modal';window.ticklerModal=m;m.style.cssText='position:fixed;top:20px;left:50%;transform:translateX(-50%);max-width:420px;width:90%;background:white;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.1),0 1px 2px rgba(0,0,0,0.06);z-index:999999;animation:tSlide 0.5s ease-out;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;border:1px solid rgb(226,232,240)';m.innerHTML='<div style="padding:24px"><div style="display:flex;align-items:center;gap:8px;margin-bottom:16px"><svg style="width:20px;height:20px;color:rgb(249,115,22)" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg><h3 style="margin:0;font-size:18px;font-weight:700;color:rgb(15,23,42)">Save to Tickler</h3></div><p style="margin:0 0 20px;font-size:14px;color:rgb(100,116,139)">Choose where to save this content:</p><div style="display:flex;gap:12px"><button id="t-idea" style="flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px 16px;border-radius:8px;font-size:14px;font-weight:500;transition:all 0.15s;background:white;color:rgb(234,88,12);border:2px solid rgb(251,146,60);cursor:pointer"><svg style="width:16px;height:16px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>Idea</button><button id="t-lib" style="flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px 16px;border-radius:8px;font-size:14px;font-weight:500;transition:all 0.15s;background:white;color:rgb(37,99,235);border:2px solid rgb(96,165,250);cursor:pointer"><svg style="width:16px;height:16px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>Library</button></div></div>';document.body.appendChild(o);document.body.appendChild(m);var ideaBtn=document.getElementById('t-idea');var libBtn=document.getElementById('t-lib');ideaBtn.onmouseover=function(){this.style.background='rgb(249,115,22)';this.style.color='white';this.style.borderColor='rgb(249,115,22)'};ideaBtn.onmouseout=function(){this.style.background='white';this.style.color='rgb(234,88,12)';this.style.borderColor='rgb(251,146,60)'};libBtn.onmouseover=function(){this.style.background='rgb(59,130,246)';this.style.color='white';this.style.borderColor='rgb(59,130,246)'};libBtn.onmouseout=function(){this.style.background='white';this.style.color='rgb(37,99,235)';this.style.borderColor='rgb(96,165,250)'};function close(){o.remove();m.remove();window.ticklerModal=null}o.onclick=close;function toast(msg,ok){close();var t=document.createElement('div');t.style.cssText='position:fixed;top:20px;right:20px;background:'+(ok?'rgb(34,197,94)':'rgb(239,68,68)')+';color:white;padding:12px 20px;border-radius:8px;box-shadow:0 4px 6px rgba(0,0,0,0.1);font-size:14px;font-weight:600;z-index:999999;animation:tSlide 0.3s;display:flex;align-items:center;gap:8px';var icon=document.createElement('span');icon.textContent=ok?'✓':'✗';icon.style.cssText='font-size:18px';t.appendChild(icon);var txt=document.createElement('span');txt.textContent=msg;t.appendChild(txt);document.body.appendChild(t);setTimeout(function(){t.style.transition='opacity 0.3s';t.style.opacity='0';setTimeout(function(){t.remove()},300)},3000)}function save(c){m.innerHTML='<div style="padding:48px 24px;text-align:center"><div style="width:40px;height:40px;margin:0 auto 16px;border:3px solid rgb(226,232,240);border-top-color:rgb(59,130,246);border-radius:50%;animation:tSpin 0.8s linear infinite"></div><p style="margin:0;color:rgb(100,116,139);font-size:14px;font-weight:500">Saving to Tickler...</p></div>';fetch('${API_BASE_URL}/api/inbound-email',{method:'POST',headers:{'Content-Type':'application/json','X-Tickler-User-ID':userId,'X-Tickler-Source':'bookmarklet'},body:JSON.stringify({From:'${userEmail}',Subject:capturedTitle,'stripped-text':capturedText,'stripped-html':capturedHtml,url:capturedUrl,tickers:capturedTickers,classification:c})}).then(function(r){if(!r.ok)throw new Error('Failed');return r.json()}).then(function(){toast(c==='idea'?'Saved as Idea!':'Saved as Library!',true)}).catch(function(){toast('Error saving',false)})}ideaBtn.onclick=function(){save('idea')};libBtn.onclick=function(){save('library')}})();`;

  // Set up folder icon href
  useEffect(() => {
    if (folderIconRef.current && bookmarkletCode && user) {
      const link = folderIconRef.current;
      // Set href directly on DOM element to bypass React's security restrictions
      link.setAttribute('href', bookmarkletCode);
    }
  }, [bookmarkletCode, user]);

  // Extract domain from URL or email address
  const extractDomain = (urlOrEmail) => {
    if (!urlOrEmail) return null;
    
    try {
      // Check if it's an email
      if (urlOrEmail.includes('@')) {
        const domain = urlOrEmail.split('@')[1].split('>')[0].trim().toLowerCase();
        return domain;
      }
      
      // Check if it's a URL
      if (urlOrEmail.includes('http') || urlOrEmail.includes('www.')) {
        const url = new URL(urlOrEmail.startsWith('http') ? urlOrEmail : `https://${urlOrEmail}`);
        return url.hostname.replace('www.', '').toLowerCase();
      }
      
      return null;
    } catch (e) {
      return null;
    }
  };

  // Smart categorization based on domain detection
  const categorizByDomain = (idea) => {
    // Priority 1: Check URL domain
    let domain = extractDomain(idea.url);
    
    // Priority 2: Check from_address email domain
    if (!domain) {
      domain = extractDomain(idea.from_address);
    }
    
    // Priority 3: Check source_info.source_address
    if (!domain && idea.source_info?.source_address) {
      domain = extractDomain(idea.source_info.source_address);
    }
    
    // Priority 4: Check metadata.forwarded_info.original_from for forwarded emails (BEST SOURCE!)
    if (!domain && idea.metadata?.forwarded_info?.original_from) {
      domain = extractDomain(idea.metadata.forwarded_info.original_from);
    }
    
    // Priority 5: Check AI attribution reasoning for original sender email (fallback)
    if (!domain && idea.source_info?.ai_attribution?.reasoning) {
      const reasoning = idea.source_info.ai_attribution.reasoning;
      // Extract email from AI reasoning (e.g., "mscliffnotes@substack.com")
      const emailMatch = reasoning.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      if (emailMatch) {
        const originalEmail = emailMatch[0];
        domain = extractDomain(originalEmail);
      }
    }
    
    // Priority 5: Check source_address for Twitter
    if (!domain && idea.source_info?.source_address) {
      const sourceAddress = idea.source_info.source_address.toLowerCase();
      if (sourceAddress === 'twitter' || sourceAddress.includes('twitter.com') || sourceAddress.includes('x.com')) {
        domain = 'x.com';
      }
    }
    
    // Priority 6: Check source_name for @username (Twitter indicator)
    if (!domain && idea.source_info?.source_name) {
      const sourceName = idea.source_info.source_name.toLowerCase();
      if (sourceName.startsWith('@')) {
        domain = 'x.com';
      }
    }
    
    // Priority 7: Infer domain from company/source name
    if (!domain && idea.source_info?.source_name) {
      const sourceName = idea.source_info.source_name.toLowerCase();
      // Map common company names to domains
      if (sourceName.includes('motley fool')) domain = 'fool.com';
      else if (sourceName.includes('seeking alpha')) domain = 'seekingalpha.com';
      else if (sourceName.includes('bloomberg')) domain = 'bloomberg.com';
      else if (sourceName.includes('wall street journal') || sourceName.includes('wsj')) domain = 'wsj.com';
      else if (sourceName.includes('financial times')) domain = 'ft.com';
      else if (sourceName.includes('cnbc')) domain = 'cnbc.com';
      else if (sourceName.includes('marketwatch')) domain = 'marketwatch.com';
      else if (sourceName.includes('reuters')) domain = 'reuters.com';
      else if (sourceName.includes('barron')) domain = 'barrons.com';
      else if (sourceName.includes('value investors club')) domain = 'valueinvestorsclub.com';
      else if (sourceName.includes('sumzero')) domain = 'sumzero.com';
      else if (sourceName.includes('substack')) domain = 'substack.com';
    }
    
    // Priority 8: Check title for source name patterns
    if (!domain && idea.title) {
      const titleLower = idea.title.toLowerCase();
      if (titleLower.includes('value investors club')) domain = 'valueinvestorsclub.com';
      else if (titleLower.includes('seeking alpha')) domain = 'seekingalpha.com';
      else if (titleLower.includes('motley fool')) domain = 'fool.com';
      else if (titleLower.includes('bloomberg')) domain = 'bloomberg.com';
    }
    
    // Domain to logo/icon mapping - ALL with actual logos via Google Favicon or direct URLs
    const domainMappings = {
      // Financial News & Analysis - ALL use favicons
      'seekingalpha.com': { 
        name: 'Seeking Alpha', 
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        useFavicon: true
      },
      'fool.com': { 
        name: 'The Motley Fool', 
        color: 'bg-green-50 text-green-700 border-green-200',
        useFavicon: true
      },
      'bloomberg.com': { 
        name: 'Bloomberg', 
        color: 'bg-slate-50 text-slate-700 border-slate-200',
        useFavicon: true
      },
      'wsj.com': { 
        name: 'Wall Street Journal', 
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        useFavicon: true
      },
      'ft.com': { 
        name: 'Financial Times', 
        color: 'bg-pink-50 text-pink-700 border-pink-200',
        useFavicon: true
      },
      'cnbc.com': { 
        name: 'CNBC', 
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        useFavicon: true
      },
      'marketwatch.com': { 
        name: 'MarketWatch', 
        color: 'bg-green-50 text-green-700 border-green-200',
        useFavicon: true
      },
      'reuters.com': { 
        name: 'Reuters', 
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        useFavicon: true
      },
      'barrons.com': { 
        name: "Barron's", 
        color: 'bg-slate-50 text-slate-700 border-slate-200',
        useFavicon: true
      },
      
      // Investment Research & Platforms
      'valueinvestorsclub.com': { 
        name: 'Value Investors Club', 
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        useFavicon: true
      },
      'sumzero.com': { 
        name: 'SumZero', 
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        useFavicon: true
      },
      
      // Social & Content Platforms
      'twitter.com': { 
        name: 'X', 
        color: 'bg-sky-50 text-sky-700 border-sky-200',
        svg: 'M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z'
      },
      'x.com': { 
        name: 'X', 
        color: 'bg-sky-50 text-sky-700 border-sky-200',
        svg: 'M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z'
      },
      'substack.com': { 
        name: 'Substack', 
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        svg: 'M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z'
      },
      'medium.com': { 
        name: 'Medium', 
        color: 'bg-slate-50 text-slate-700 border-slate-200',
        useFavicon: true
      },
      'linkedin.com': { 
        name: 'LinkedIn', 
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        svg: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'
      },
      'reddit.com': { 
        name: 'Reddit', 
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        useFavicon: true // Use favicon instead of complex SVG
      },
      
      // Newsletter Platforms
      'convertkit.com': { 
        name: 'ConvertKit', 
        color: 'bg-pink-50 text-pink-700 border-pink-200',
        useFavicon: true
      },
      'mailchimp.com': { 
        name: 'Mailchimp', 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        useFavicon: true
      },
      'beehiiv.com': { 
        name: 'Beehiiv', 
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        useFavicon: true
      },
      
      // Generic email providers - use Gmail/Yahoo favicons
      'gmail.com': { 
        name: 'Personal Email', 
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        useFavicon: true
      },
      'yahoo.com': { 
        name: 'Personal Email', 
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        useFavicon: true
      },
      'outlook.com': { 
        name: 'Personal Email', 
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        useFavicon: true
      },
      'hotmail.com': { 
        name: 'Personal Email', 
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        useFavicon: true
      }
    };
    
    // Check for exact domain match
    if (domain && domainMappings[domain]) {
      return { ...domainMappings[domain], domain }; // Include domain in return
    }
    
    // Check for partial domain matches (e.g., mail.google.com → gmail.com)
    if (domain) {
      for (const [key, value] of Object.entries(domainMappings)) {
        if (domain.includes(key) || key.includes(domain)) {
          return { ...value, domain: key }; // Use the matched domain
        }
      }
      
      // If we have a domain but no mapping, return it with favicon
      return {
        name: 'Email',
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        useFavicon: true,
        domain: domain // Return the inferred/extracted domain
      };
    }
    
    // Fallback: Generic email icon (no domain at all)
    return { 
      name: 'Email', 
      color: 'bg-gray-50 text-gray-700 border-gray-200',
      emoji: '📧',
      domain: null
    };
  };

  // Legacy function for backward compatibility
  const getPlatformDisplay = (platformSource) => {
    const platforms = {
      'Substack': { icon: '📰', color: 'bg-orange-100 text-orange-800', name: 'Substack' },
      'Twitter': { icon: '🐦', color: 'bg-blue-100 text-blue-800', name: 'X' },
      'LinkedIn': { icon: '💼', color: 'bg-blue-100 text-blue-800', name: 'LinkedIn' },
      'Reddit': { icon: '🔴', color: 'bg-red-100 text-red-800', name: 'Reddit' },
      'Medium': { icon: '📝', color: 'bg-green-100 text-green-800', name: 'Medium' },
      'Personal': { icon: '👤', color: 'bg-purple-100 text-purple-800', name: 'Personal' },
      'Business': { icon: '🏢', color: 'bg-gray-100 text-gray-800', name: 'Business' },
      'Investor_Letter': { icon: '💼', color: 'bg-emerald-100 text-emerald-800', name: 'Investor Letter' },
      'Financial_Newsletter': { icon: '📊', color: 'bg-green-100 text-green-800', name: 'Financial Newsletter' },
      'Newsletter': { icon: '📬', color: 'bg-indigo-100 text-indigo-800', name: 'Newsletter' },
      'ConvertKit': { icon: '✉️', color: 'bg-pink-100 text-pink-800', name: 'ConvertKit' },
      'Mailchimp': { icon: '🐵', color: 'bg-yellow-100 text-yellow-800', name: 'Mailchimp' },
      'Beehiiv': { icon: '🐝', color: 'bg-yellow-100 text-yellow-800', name: 'Beehiiv' },
      'Ghost': { icon: '👻', color: 'bg-gray-100 text-gray-800', name: 'Ghost' }
    };
    
    return platforms[platformSource] || { icon: '📧', color: 'bg-gray-100 text-gray-800', name: platformSource || 'Email' };
  };

  // Content cleaning and formatting function for better display with image support
  const formatOriginalContent = (textContent, htmlContent, twitterMetadata = null) => {
    // DEBUG: Log what we're receiving
    console.log('🔍 formatOriginalContent called with:', {
      textContent_length: textContent?.length,
      textContent_preview: textContent?.substring(0, 150),
      has_htmlContent: !!htmlContent,
      has_twitterMetadata: !!twitterMetadata,
      is_thread: twitterMetadata?.is_thread,
      full_thread_count: twitterMetadata?.full_thread?.length
    });
    
    // THREAD DETECTION: Check if textContent contains raw thread formatting
    // If so, prefer the structured full_thread data for nice rendering
    const hasRawThreadFormat = textContent && (
      textContent.includes('🧵 THREAD') ||
      textContent.includes('📝 Tweet 1/') ||
      textContent.match(/={10,}/) // Long separator lines
    );
    
    // THREAD DISPLAY: Use full_thread data for nice rendering if available
    // Priority: Use full_thread when we have it AND (content is raw formatted OR is_thread flag is set)
    if (twitterMetadata?.full_thread && twitterMetadata.full_thread.length > 0 && 
        (hasRawThreadFormat || twitterMetadata?.is_thread)) {
      console.log('🔍 Using structured full_thread data for nice rendering');
      const threadHtml = twitterMetadata.full_thread.map((tweet, index) => {
        const tweetNum = index + 1;
        const totalTweets = twitterMetadata.full_thread.length;
        
        return `
          <div style="border-left: 3px solid #1DA1F2; padding-left: 16px; margin: 24px 0;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <span style="background: #1DA1F2; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 600;">
                ${tweetNum}/${totalTweets}
              </span>
              <span style="color: #536471; font-size: 13px;">
                ${tweet.created_at ? new Date(tweet.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
              </span>
            </div>
            <div style="color: #0f1419; font-size: 15px; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word;">
              ${(tweet.text || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}
            </div>
            ${tweet.media && tweet.media.length > 0 ? `
              <div style="margin-top: 12px;">
                ${tweet.media.map(m => {
                  if (m.type === 'photo' && m.url) {
                    return '<img src="' + m.url + '" alt="' + (m.alt_text || 'Image') + '" style="max-width: 100%; border-radius: 12px; margin-top: 8px; border: 1px solid #e1e8ed;" />';
                  } else if (m.type === 'video' && m.preview_image_url) {
                    return '<div style="position: relative; max-width: 100%; margin-top: 8px;"><img src="' + m.preview_image_url + '" alt="Video thumbnail" style="width: 100%; border-radius: 12px; border: 1px solid #e1e8ed;" /><div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.7); border-radius: 50%; padding: 12px;"><svg style="width: 24px; height: 24px; color: white;" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div></div>';
                  }
                  return '';
                }).join('')}
              </div>
            ` : ''}
            ${tweet.url ? `<a href="${tweet.url}" target="_blank" rel="noopener noreferrer" style="color: #1DA1F2; text-decoration: none; font-size: 13px; margin-top: 8px; display: inline-block;">
              View tweet →
            </a>` : ''}
          </div>
        `;
      }).join('');
      
      return `
        <div style="background: #f7f9fa; padding: 16px; border-radius: 12px; margin-bottom: 16px;">
          <div style="font-size: 14px; color: #536471; margin-bottom: 12px;">
            🧵 Thread with ${twitterMetadata.full_thread.length} tweets
          </div>
          ${threadHtml}
        </div>
      `;
    }
    
    // SIMPLE FORMAT: If we have textContent without thread formatting, return it formatted simply
    if (textContent && textContent.length > 0) {
      const simpleFormatted = textContent
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
        .trim();
      console.log('🔍 Returning simple formatted content, length:', simpleFormatted.length);
      return simpleFormatted;
    }
    
    // Use the original email content as received, no reconstruction
    const originalText = cleanTextContent(textContent);
    
    console.log('🔍 After cleanTextContent:', {
      originalText_length: originalText?.length,
      originalText_preview: originalText?.substring(0, 150)
    });
    
    // Convert URLs to clickable links (including image URLs)
    const linkifyText = (text) => {
      // Regex to match URLs
      const urlRegex = /(https?:\/\/[^\s<]+)/g;
      return text.replace(urlRegex, (url) => {
        // Clean up any trailing punctuation that might have been captured
        let cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
        const punctuation = url.substring(cleanUrl.length);
        
        // Check if this is an image URL (common image extensions)
        const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/i.test(cleanUrl);
        
        if (isImage) {
          // Display actual image inline with link
          return `<div style="margin: 12px 0;"><a href="${cleanUrl}" target="_blank" rel="noopener noreferrer"><img src="${cleanUrl}" alt="Media" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: block;"/></a></div>${punctuation}`;
        } else {
          // Regular link
          return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${cleanUrl}</a>${punctuation}`;
        }
      });
    };
    
    // Convert line breaks to HTML, linkify URLs, and remove excessive whitespace
    const formatted = linkifyText(originalText)
      .replace(/\n/g, '<br>')
      .replace(/<br>\s*<br>\s*<br>/g, '<br><br>') // Multiple line breaks to double
      .trim();
    
    console.log('🔍 formatOriginalContent returning:', {
      formatted_length: formatted?.length,
      formatted_preview: formatted?.substring(0, 150)
    });
    
    return formatted;
  };

  const formatHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    let formatted = htmlContent;
    
    // Extract and preserve meaningful image information (filter out UI elements)
    const meaningfulImages = [];
    formatted = formatted.replace(/<img[^>]+src="([^"]+)"[^>]*(?:alt="([^"]*)")?[^>]*>/gi, (match, src, alt) => {
      // Filter out Substack UI elements and icons
      const isSubstackUIElement = src.includes('substack.com/icon/') || 
                                  src.includes('NoteForwardIcon') ||
                                  src.includes('shareIcon') ||
                                  src.includes('likeIcon') ||
                                  src.includes('/icon/') ||
                                  src.includes('button') ||
                                  alt?.toLowerCase().includes('icon') ||
                                  alt?.toLowerCase().includes('button') ||
                                  src.includes('width=36') ||  // Small icons
                                  src.includes('height=36');   // Small icons
      
      // Filter out tiny images (likely icons)
      const isTinyImage = src.includes('w_36') || src.includes('h_36') || 
                         src.includes('width=16') || src.includes('height=16');
      
      if (isSubstackUIElement || isTinyImage) {
        return ''; // Remove UI elements completely
      }
      
      // Only include meaningful content images
      const imageIndex = meaningfulImages.length;
      const imageDescription = alt && alt.trim() && alt !== 'Image' ? alt : 'Content Image';
      meaningfulImages.push({ src, alt: imageDescription });
      
      // Show meaningful images with proper formatting
      return `\n\n📸 **Image ${imageIndex + 1}: ${imageDescription}**\n🔗 View: ${src}\n\n`;
    });
    
    // Clean up Substack redirect URLs
    formatted = formatted.replace(
      /https:\/\/substack\.com\/redirect\/[a-f0-9-]+\?j=[a-zA-Z0-9_.-]+/g,
      '[Link removed for readability]'
    );
    
    // Convert common HTML elements to readable text
    formatted = formatted.replace(/<h([1-6])[^>]*>(.*?)<\/h[1-6]>/gi, '\n\n** $2 **\n\n');
    formatted = formatted.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');
    formatted = formatted.replace(/<br\s*\/?>/gi, '\n');
    formatted = formatted.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');
    formatted = formatted.replace(/<b>(.*?)<\/b>/gi, '**$1**');
    formatted = formatted.replace(/<em>(.*?)<\/em>/gi, '*$1*');
    formatted = formatted.replace(/<i>(.*?)<\/i>/gi, '*$1*');
    formatted = formatted.replace(/<ul[^>]*>/gi, '\n');
    formatted = formatted.replace(/<\/ul>/gi, '\n');
    formatted = formatted.replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n');
    formatted = formatted.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '$2 ($1)');
    
    // Remove remaining HTML tags
    formatted = formatted.replace(/<[^>]*>/g, '');
    
    // Clean up HTML entities
    formatted = formatted.replace(/&nbsp;/g, ' ');
    formatted = formatted.replace(/&amp;/g, '&');
    formatted = formatted.replace(/&lt;/g, '<');
    formatted = formatted.replace(/&gt;/g, '>');
    formatted = formatted.replace(/&quot;/g, '"');
    formatted = formatted.replace(/&#39;/g, "'");
    
    // Clean up multiple line breaks
    formatted = formatted.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    // Clean up leading/trailing whitespace
    formatted = formatted.trim();
    
    return formatted;
  };

  const cleanAndFormatHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    let cleaned = htmlContent;
    
    // Remove Substack UI elements and tracking
    cleaned = cleaned.replace(/<img[^>]*src="[^"]*substack\.com\/icon\/[^"]*"[^>]*>/gi, '');
    cleaned = cleaned.replace(/<img[^>]*src="[^"]*NoteForwardIcon[^"]*"[^>]*>/gi, '');
    cleaned = cleaned.replace(/<img[^>]*src="[^"]*shareIcon[^"]*"[^>]*>/gi, '');
    cleaned = cleaned.replace(/<img[^>]*src="[^"]*likeIcon[^"]*"[^>]*>/gi, '');
    cleaned = cleaned.replace(/<img[^>]*width="?36"?[^>]*>/gi, ''); // Small icons
    cleaned = cleaned.replace(/<img[^>]*height="?36"?[^>]*>/gi, ''); // Small icons
    
    // Clean up Substack redirect URLs in links and preserve meaningful ones
    cleaned = cleaned.replace(
      /https:\/\/substack\.com\/redirect\/[a-f0-9-]+\?j=[a-zA-Z0-9_.-]+/g,
      '#'
    );
    
    // Remove Substack tracking and interface elements
    cleaned = cleaned.replace(/<div[^>]*class="[^"]*footer[^"]*"[^>]*>.*?<\/div>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*class="[^"]*subscription[^"]*"[^>]*>.*?<\/div>/gi, '');
    cleaned = cleaned.replace(/READ IN APP/gi, '');
    
    // Style images properly and make them responsive
    cleaned = cleaned.replace(/<img([^>]*)>/gi, (match, attrs) => {
      // Skip if this is a UI element we want to remove
      if (attrs.includes('icon') || attrs.includes('button') || attrs.includes('w_36') || attrs.includes('h_36')) {
        return '';
      }
      
      return `<img${attrs} style="max-width: 100%; height: auto; margin: 16px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">`;
    });
    
    // Clean up empty paragraphs and divs
    cleaned = cleaned.replace(/<p>\s*<\/p>/gi, '');
    cleaned = cleaned.replace(/<div>\s*<\/div>/gi, '');
    
    // Add some basic styling for better readability
    const styledContent = `
      <style>
        .original-content h1, .original-content h2, .original-content h3 {
          margin: 24px 0 16px 0;
          color: #1f2937;
          line-height: 1.4;
        }
        .original-content h1 { font-size: 24px; font-weight: 700; }
        .original-content h2 { font-size: 20px; font-weight: 600; }
        .original-content h3 { font-size: 18px; font-weight: 600; }
        .original-content p {
          margin: 12px 0;
          line-height: 1.6;
        }
        .original-content ul, .original-content ol {
          margin: 12px 0;
          padding-left: 24px;
        }
        .original-content li {
          margin: 4px 0;
        }
        .original-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .original-content a:hover {
          color: #1d4ed8;
        }
        .original-content blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 16px;
          margin: 16px 0;
          font-style: italic;
          color: #6b7280;
        }
        .original-content strong {
          font-weight: 600;
        }
        .original-content em {
          font-style: italic;
        }
      </style>
      <div class="original-content">
        ${cleaned}
      </div>
    `;
    
    return styledContent;
  };

  const cleanTextContent = (content) => {
    if (!content) return '';
    
    let cleaned = content;
    
    // Remove email forwarding headers
    cleaned = cleaned.replace(/---------- Forwarded message ---------.*?\n\n/gs, '');
    
    // Remove all Substack app-link URLs (these are tracking/action URLs)  
    cleaned = cleaned.replace(/<https:\/\/substack\.com\/app-link\/[^>]+>\s*/g, '');
    
    // Remove Substack redirect URLs
    cleaned = cleaned.replace(
      /https:\/\/substack\.com\/redirect\/[a-f0-9-]+\?j=[a-zA-Z0-9_.-]+/g,
      '[Link removed for readability]'
    );
    
    // Remove author profile links
    cleaned = cleaned.replace(/<https:\/\/substack\.com\/@[^>]+>\s*/g, '');
    
    // Remove subscription prompts and buttons
    cleaned = cleaned.replace(/READ IN APP\s*<[^>]+>\s*/gi, '');
    cleaned = cleaned.replace(/Thanks for reading.*?Subscribe.*?<[^>]+>\s*/gis, '');
    
    // Remove like/comment/restack buttons
    cleaned = cleaned.replace(/(?:Like|Comment|Restack)\s*<[^>]+>\s*/gi, '');
    
    // Remove unsubscribe footers
    cleaned = cleaned.replace(/©.*?Unsubscribe\s*<[^>]+>.*$/gis, '');
    
    // Clean up invisible characters common in email
    cleaned = cleaned.replace(/[͏­\u00ad\u200b\u200c\u200d\u2060\ufeff]+/g, '');
    
    // Normalize line endings and clean up whitespace
    cleaned = cleaned.replace(/\r\n/g, '\n');
    cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
    cleaned = cleaned.replace(/[ \t]+/g, ' ');
    cleaned = cleaned.replace(/^\s+|\s+$/gm, ''); // Trim each line
    
    // Preserve and enhance image placeholders  
    cleaned = cleaned.replace(/\[image: ([^\]]+)\]/gi, '📸 IMAGE: $1');
    
    return cleaned.trim();
  };
  
  // Subscription state - default to free tier for MVP
  const [subscription, setSubscription] = useState({ 
    tier: 'free', // 'free' or 'paid'
    limit: 5, // free tier shows last 5 items only
    total_available: 0
  });

  // Payment states
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [billingInterval, setBillingInterval] = useState('annual'); // 'monthly' or 'annual' - default to annual

  // Tweet generator modal state
  const [showTweetGenerator, setShowTweetGenerator] = useState(false);
  const [tweetMode, setTweetMode] = useState('poll'); // 'poll' or 'ideas'
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '', '', '']);
  const [ideasText, setIdeasText] = useState('');

  // OLD AUTH CODE REMOVED - NOW USING AUTH CONTEXT

  // REMOVED OLD AUTH FUNCTIONS - NOW USING AUTH CONTEXT

  // Payment utility functions
  const initiateSubscriptionPayment = async (planId = 'pro_monthly') => {
    if (!user || !user) {
      alert('Please log in to upgrade your subscription');
      return;
    }

    try {
      setIsProcessingPayment(true);
      setPaymentError('');
      
      const originUrl = window.location.origin;
      const response = await fetch(`${API_BASE_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planId,
          user_id: user.id || user.user_id,
          user_email: user.email,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      setPaymentError(error.message);
      console.error('Payment error:', error);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          // Refresh subscription status
          fetchSubscriptionStatus();
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return false;
    }
  };

  const fetchSubscriptionStatus = async () => {
    if (!user?.user_id && !user?.id && !user?.email) return;
    
    try {
      const userId = user.id || user.user_id || user.email;
      console.log('📊 Fetching subscription status for:', userId);
      const response = await fetch(`${API_BASE_URL}/api/subscription/status/${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Subscription status:', data);
        setSubscription({
          tier: data.subscription_tier,
          limit: data.limit,
          total_available: 0 // Will be updated by individual API calls
        });
        
        // Check if user should see the subscribe banner
        // Show if: has used free import AND is not a subscriber
        if (data.has_used_free_import && !data.is_subscriber) {
          console.log('🎯 Setting showSubscribeBanner to TRUE');
          setShowSubscribeBanner(true);
        } else {
          console.log('🎯 Setting showSubscribeBanner to FALSE - has_used_free_import:', data.has_used_free_import, 'is_subscriber:', data.is_subscriber);
          setShowSubscribeBanner(false);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  // Initial data loading - auth is handled by AuthContext
  useEffect(() => {
    if (user && !authLoading) {
      fetchData();
      fetchSubscriptionStatus(); // Show subscribe banner for free users who used their import
    }
  }, [user, authLoading]);

  // On return from Stripe checkout: verify payment + refresh subscription
  // (webhook may miss/lag - this ensures the paywall drops immediately)
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');
    if (sessionId && status === 'success') {
      console.log('🎉 Returned from Stripe checkout, verifying payment for session:', sessionId);
      (async () => {
        try {
          const paid = await checkPaymentStatus(sessionId);
          console.log('💳 Payment verification result:', paid);
        } catch (e) {
          console.error('Payment verification failed:', e);
        }
        // Refresh subscription status regardless (reconciliation runs server-side too)
        await fetchSubscriptionStatus();
        // Clean URL so a page refresh doesn't re-trigger
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      })();
    }
  }, [user]);

  // Update fetch functions to handle authentication and subscription data
  const fetchDataWithSubscription = async () => {
    if (!user || !user?.user_id) {

      return;
    }

    try {
      // Add timeout to all requests to prevent hanging
      const fetchWithTimeout = (url, timeout = 3000) => {
        return Promise.race([
          fetch(url),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), timeout))
        ]);
      };

      // Fetch subscription status first (essential)
      try {
        const subResponse = await fetchWithTimeout(`${API_BASE_URL}/api/subscription/status/${user.id}`);
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setSubscription({
            tier: subData.subscription_tier || 'free',
            limit: subData.limit || 5,
            total_available: 0 // Will be updated with actual data
          });
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        // Set default subscription if fetch fails
        setSubscription({ tier: 'free', limit: 5, total_available: 0 });
      }

      // Fetch core data with reasonable timeouts
      const fetchPromises = [
        fetchWithTimeout(`${API_BASE_URL}/api/tickers/${user.id}`, 5000).catch(e => ({ ok: false, error: e })),
        fetchWithTimeout(`${API_BASE_URL}/api/watchlists/${user.id}`, 5000).catch(e => ({ ok: false, error: e }))
      ];

      const [tickersResponse, watchlistsResponse] = await Promise.all(fetchPromises);

      // Handle tickers
      if (tickersResponse.ok) {
        const tickersData = await tickersResponse.json();
        setTickers(tickersData.tickers || []);
        
        if (tickersData.subscription) {
          setSubscription(prev => ({
            ...prev,
            total_available: Math.max(prev.total_available, tickersData.subscription.total_available || 0)
          }));
        }
      } else {
        setTickers([]);
      }

      // Handle watchlists
      if (watchlistsResponse.ok) {
        const watchlistsData = await watchlistsResponse.json();
        console.log('📋 Loaded watchlists from API:', watchlistsData.watchlists);
        setWatchlists(watchlistsData.watchlists || []);
      } else {
        console.error('❌ Failed to load watchlists:', watchlistsResponse.error || 'Unknown error');
        if (watchlistsResponse.error?.message === 'Request timeout') {
          console.error('⏱️ Watchlist API timed out - consider increasing timeout or optimizing backend');
        }
        setWatchlists([]);
      }

      // Load bookmarks separately (less critical for initial load)
      setTimeout(async () => {
        try {
          const bookmarksResponse = await fetchWithTimeout(`${API_BASE_URL}/api/bookmarks/${user.id}`, 5000);
          if (bookmarksResponse.ok) {
            const bookmarksData = await bookmarksResponse.json();
            // Only overwrite demo data if we have real bookmarks
            if (bookmarksData.bookmarks && bookmarksData.bookmarks.length > 0) {
              setBookmarks(bookmarksData.bookmarks || []);
            }
            
            if (bookmarksData.bookmarks) {
              setSubscription(prev => ({
                ...prev,
                total_available: Math.max(prev.total_available, bookmarksData.bookmarks.length)
              }));
            }
          }
        } catch (error) {
          console.error('Error fetching bookmarks:', error);
          // Don't clear bookmarks on error - keep demo data
        }
      }, 500);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values even if everything fails, but preserve demo bookmarks for unauthenticated users
      setTickers([]);
      setWatchlists([]);
      // Don't clear bookmarks on error - keep existing data (demo or real)
    }
  };

  // Load data when authentication changes - but with delay to avoid race conditions
  


  // Bookmark sync states
  const [isBookmarkSyncing, setIsBookmarkSyncing] = useState(false);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  
  // X Authentication states
  const [xAuthStatus, setXAuthStatus] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchHints, setShowSearchHints] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false); // Mobile search expansion
  const [searchResults, setSearchResults] = useState(null); // null = not searching, [] = no results, [...] = results
  const [searchTickerCount, setSearchTickerCount] = useState(0); // Total tickers across search results
  const [isSearching, setIsSearching] = useState(false);
  const [extractedTickers, setExtractedTickers] = useState([]);
  const [searchDisplayMode, setSearchDisplayMode] = useState('results'); // 'results' or 'handles' - controls what UI shows for @ searches
  const [globalTickerResults, setGlobalTickerResults] = useState(null); // Results from global ticker search ($TICKER)
  const [companySuggestions, setCompanySuggestions] = useState([]); // Company name suggestions for search

  // GLOBAL SEARCH: Debounced API call to search entire database
  // State for poll search results
  const [pollSearchResults, setPollSearchResults] = useState(null);
  const [listSearchResults, setListSearchResults] = useState(null);
  const [filesSearchResults, setFilesSearchResults] = useState(null);
  const [filesSearchQuery, setFilesSearchQuery] = useState(null);
  const [openFileViewerId, setOpenFileViewerId] = useState(null); // For opening specific file by viewerId on Files tab
  const [tickersCategoryResults, setTickersCategoryResults] = useState(null); // For tickers: category search
  const [groupsSearchResults, setGroupsSearchResults] = useState(null); // For groups: search
  const [ideasSearchResults, setIdeasSearchResults] = useState(null); // For ideas: search
  const [foldersSearchResults, setFoldersSearchResults] = useState(null); // For folders: search
  const [globalKeywordResults, setGlobalKeywordResults] = useState(null); // For keyword search (drones, AI, etc)
  
  // Company name suggestions state - the actual useEffect is defined after researchSchedule state
  
  useEffect(() => {
    console.log('🔍 SEARCH USEEFFECT TRIGGERED, searchQuery:', searchQuery);
    // Clear results if query is empty
    if (!searchQuery || !searchQuery.trim()) {
      setSearchResults(null);
      setPollSearchResults(null);
      setListSearchResults(null);
      setFilesSearchResults(null);
      setFilesSearchQuery(null);
      setTickersCategoryResults(null);
      setGroupsSearchResults(null);
      setIdeasSearchResults(null);
      setFoldersSearchResults(null);
      setGlobalTickerResults(null); // Clear global ticker search results
      setGlobalKeywordResults(null); // Clear global keyword search results
      setCompanySuggestions([]); // Clear company suggestions
      setIsSearching(false);
      setSearchDisplayMode('results'); // Reset display mode when clearing search
      return;
    }

    // Set display mode IMMEDIATELY based on query type (not inside debounce)
    // '@' alone shows handles list, '@username' shows results for that user
    // 'files:' alone shows files list
    // 'tickers:' alone shows category list
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery === '@') {
      console.log('🔍 Setting searchDisplayMode to HANDLES for query: @');
      setSearchDisplayMode('handles');
    } else if (trimmedQuery.toLowerCase().startsWith('files:')) {
      console.log('🔍 Setting searchDisplayMode to FILES for query:', trimmedQuery);
      setSearchDisplayMode('briefs');
    } else if (trimmedQuery.toLowerCase().startsWith('tickers:')) {
      console.log('🔍 Setting searchDisplayMode to TICKERS_CATEGORIES for query:', trimmedQuery);
      setSearchDisplayMode('tickers_categories');
    } else if (trimmedQuery.toLowerCase().startsWith('groups:')) {
      console.log('🔍 Setting searchDisplayMode to GROUPS for query:', trimmedQuery);
      setSearchDisplayMode('groups');
    } else if (trimmedQuery.toLowerCase().startsWith('ideas:')) {
      console.log('🔍 Setting searchDisplayMode to IDEAS for query:', trimmedQuery);
      setSearchDisplayMode('ideas');
    } else if (trimmedQuery.toLowerCase().startsWith('folders:')) {
      console.log('🔍 Setting searchDisplayMode to FOLDERS for query:', trimmedQuery);
      setSearchDisplayMode('folders');
    } else {
      console.log('🔍 Setting searchDisplayMode to RESULTS for query:', trimmedQuery);
      setSearchDisplayMode('results');
    }

    // Set up debounce timer
    const debounceTimer = setTimeout(async () => {
      const userId = user?.id || user?.user_id || user?.email;
      if (!userId) return;

      const query = searchQuery.trim();
      
      // Check for poll: prefix
      if (query.toLowerCase().startsWith('poll:')) {
        const pollQuery = query.slice(5).trim().toLowerCase();
        setIsSearching(true);
        
        // Filter polls locally
        const matchedPolls = fishingThreads.filter(poll => {
          if (!pollQuery) return true; // Show all polls if just "poll:"
          const title = (poll.title || '').toLowerCase();
          const content = (poll.original_tweet?.content || '').toLowerCase();
          const tickers = (poll.tickers_discovered || []).join(' ').toLowerCase();
          return title.includes(pollQuery) || content.includes(pollQuery) || tickers.includes(pollQuery);
        });
        
        setPollSearchResults(matchedPolls);
        setSearchResults(null);
        setListSearchResults(null);
        setIsSearching(false);
        trackEngagement('search', 'search', { search_type: 'poll', query_length: pollQuery.length });
        return;
      }
      
      // Check for tickers: prefix (hierarchical category search)
      if (query.toLowerCase().startsWith('tickers:')) {
        const subQuery = query.slice(8).trim().toLowerCase();
        setIsSearching(true);
        
        try {
          // Fetch ALL data from APIs to get complete counts (not just paginated local state)
          const userId = user?.id || user?.user_id || user?.email;
          const userEmail = user?.email;
          
          const [watchlistsRes, pollsRes, sharedWatchlistsRes, sharedPollsRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/watchlists/${userId}`),
            fetch(`${API_BASE_URL}/api/fishing-threads/${userId}`),
            userEmail ? fetch(`${API_BASE_URL}/api/shared-watchlists/${encodeURIComponent(userEmail)}`) : Promise.resolve({ ok: true, json: () => ({ watchlists: [] }) }),
            userEmail ? fetch(`${API_BASE_URL}/api/shared-polls/${encodeURIComponent(userEmail)}`) : Promise.resolve({ ok: true, json: () => ({ polls: [] }) })
          ]);
          
          const allWatchlists = watchlistsRes.ok ? (await watchlistsRes.json()).watchlists || [] : [];
          const allPolls = pollsRes.ok ? (await pollsRes.json()).threads || [] : [];
          const allSharedWatchlists = sharedWatchlistsRes.ok ? (await sharedWatchlistsRes.json()).watchlists || [] : [];
          const allSharedPolls = sharedPollsRes.ok ? (await sharedPollsRes.json()).polls || [] : [];
          
          // Sector names list
          const sectorNames = ['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'];
          
          // Get curations - watchlists that have source_info (came from bookmarks/ideas)
          const curations = allWatchlists.filter(wl => wl.source_info?.bookmark_id || wl.source_info?.author_name);
          // Get sectors - those that match sector names
          const sectors = allWatchlists.filter(wl => !wl.source_info?.bookmark_id && !wl.source_info?.author_name && sectorNames.includes(wl.name));
          // Get custom lists - user-created with list_type === 'custom_list'
          const customLists = allWatchlists.filter(wl => wl.list_type === 'custom_list');
          
          // Filter X Polls vs Requests from allPolls
          const xPolls = allPolls.filter(p => {
            return p.source === 'x_poll' || 
              (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
          });
          const requests = allPolls.filter(p => {
            return p.source === 'requests' || 
              (p.source !== 'x_poll' && p.source !== 'custom_list' && 
               !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
          });
          
          console.log('🔍 tickers: search - ALL watchlists:', allWatchlists.length, 'curations:', curations.length, 'sectors:', sectors.length, 'custom:', customLists.length, 'xPolls:', xPolls.length, 'requests:', requests.length);
          
          // Build category counts with ALL data
          const categories = {
            curations: { count: curations.length, items: curations },
            sectors: { count: sectors.length, items: sectors },
            custom: { count: customLists.length, items: customLists },
            requests: { count: requests.length, items: requests },
            polls: { count: xPolls.length, items: xPolls },
            received: { count: allSharedWatchlists.length + allSharedPolls.length, items: [...allSharedWatchlists, ...allSharedPolls] }
          };
          
          // If just "tickers:" show all categories
          if (!subQuery) {
            setTickersCategoryResults(categories);
            setListSearchResults(null);
            setPollSearchResults(null);
            setSearchResults(null);
            setFilesSearchQuery(null);
            setIsSearching(false);
            trackEngagement('search', 'search', { search_type: 'tickers_categories', query_length: 0 });
            return;
          }
          
          // Check for sub-category prefix (e.g., tickers:polls:, tickers:curations:)
          const categoryPrefixes = ['curations:', 'polls:', 'requests:', 'sectors:', 'custom:', 'received:'];
          const matchedPrefix = categoryPrefixes.find(prefix => subQuery.startsWith(prefix));
        
          if (matchedPrefix) {
            const categoryName = matchedPrefix.slice(0, -1); // Remove trailing colon
            const filterQuery = subQuery.slice(matchedPrefix.length).trim().toLowerCase();
            
            if (categoryName === 'curations') {
              const filtered = filterQuery 
                ? curations.filter(c => {
                    const name = (c.name || '').toLowerCase();
                    const tickers = (c.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
                    return name.includes(filterQuery) || tickers.includes(filterQuery);
                  })
                : curations;
              setTickersCategoryResults({ curations: { count: filtered.length, items: filtered, filtered: true } });
            } else if (categoryName === 'polls') {
              // X Polls only - with poll options pattern
              const xPolls = allPolls.filter(p => {
                const isXPoll = p.source === 'x_poll' || 
                  (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
                return isXPoll;
              });
              const filtered = filterQuery 
                ? xPolls.filter(p => {
                    const title = (p.title || p.tweet_text || '').toLowerCase();
                    return title.includes(filterQuery);
                  })
                : xPolls;
              setPollSearchResults(filtered);
              setTickersCategoryResults(null);
            } else if (categoryName === 'requests') {
              // Requests only - without poll options pattern
              const requests = allPolls.filter(p => {
                const isRequest = p.source === 'requests' || 
                  (p.source !== 'x_poll' && p.source !== 'custom_list' && 
                   !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
                return isRequest;
              });
              const filtered = filterQuery 
                ? requests.filter(p => {
                    const title = (p.title || p.tweet_text || '').toLowerCase();
                    return title.includes(filterQuery);
                  })
                : requests;
              setPollSearchResults(filtered);
              setTickersCategoryResults(null);
            } else if (categoryName === 'sectors') {
              const filtered = filterQuery 
                ? sectors.filter(wl => {
                    const name = (wl.name || '').toLowerCase();
                    const tickers = (wl.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
                    return name.includes(filterQuery) || tickers.includes(filterQuery);
                  })
                : sectors;
              setListSearchResults(filtered);
              setTickersCategoryResults(null);
            } else if (categoryName === 'custom') {
              const filtered = filterQuery 
                ? customWatchlists.filter(wl => {
                    const name = (wl.name || '').toLowerCase();
                    const tickers = (wl.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
                    return name.includes(filterQuery) || tickers.includes(filterQuery);
                  })
                : customWatchlists;
              setListSearchResults(filtered);
              setTickersCategoryResults(null);
            } else if (categoryName === 'received') {
              const allReceived = [...allSharedWatchlists, ...allSharedPolls];
              const filtered = filterQuery 
                ? allReceived.filter(item => {
                    const name = (item.name || item.title || item.tweet_text || '').toLowerCase();
                    return name.includes(filterQuery);
                  })
                : allReceived;
              setTickersCategoryResults({ received: { count: filtered.length, items: filtered, filtered: true } });
            }
            
            setSearchResults(null);
            setFilesSearchQuery(null);
            setIsSearching(false);
            trackEngagement('search', 'search', { search_type: `tickers_${categoryName}`, query_length: filterQuery.length });
            return;
          }
          
          // General search across all tickers categories
          const matchedSectors = sectors.filter(wl => {
            const name = (wl.name || '').toLowerCase();
            const tickers = (wl.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
            return name.includes(subQuery) || tickers.includes(subQuery);
          });
          
          const matchedCustom = customWatchlists.filter(wl => {
            const name = (wl.name || '').toLowerCase();
            const tickers = (wl.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
            return name.includes(subQuery) || tickers.includes(subQuery);
          });
          
          // X Polls - with poll options pattern
          const matchedPolls = allPolls.filter(p => {
            const isXPoll = p.source === 'x_poll' || 
              (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
            if (!isXPoll) return false;
            const title = (p.title || p.tweet_text || '').toLowerCase();
            return title.includes(subQuery);
          });
          
          // Requests - without poll options pattern
          const matchedRequests = allPolls.filter(p => {
            const isRequest = p.source === 'requests' || 
              (p.source !== 'x_poll' && p.source !== 'custom_list' && 
               !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
            if (!isRequest) return false;
            const title = (p.title || p.tweet_text || '').toLowerCase();
            return title.includes(subQuery);
          });
          
          const matchedCurations = curations.filter(c => {
            const name = (c.name || '').toLowerCase();
            const tickers = (c.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
            return name.includes(subQuery) || tickers.includes(subQuery);
          });
          
          const matchedReceived = [...allSharedWatchlists, ...allSharedPolls].filter(item => {
            const name = (item.name || item.title || item.tweet_text || '').toLowerCase();
            return name.includes(subQuery);
          });
          
          // Show combined results with counts
          setTickersCategoryResults({
            curations: { count: matchedCurations.length, items: matchedCurations, filtered: true },
            requests: { count: matchedRequests.length, items: matchedRequests, filtered: true },
            polls: { count: matchedPolls.length, items: matchedPolls, filtered: true },
            sectors: { count: matchedSectors.length, items: matchedSectors, filtered: true },
            custom: { count: matchedCustom.length, items: matchedCustom, filtered: true },
            received: { count: matchedReceived.length, items: matchedReceived, filtered: true }
          });
          setListSearchResults(null);
          setPollSearchResults(null);
          setSearchResults(null);
          setFilesSearchQuery(null);
          setIsSearching(false);
          trackEngagement('search', 'search', { search_type: 'tickers_all', query_length: subQuery.length });
          return;
        } catch (error) {
          console.error('Error fetching tickers search data:', error);
          setIsSearching(false);
          return;
        }
      }
      
      // Check for files: prefix - search existing files
      if (query.toLowerCase().startsWith('files:')) {
        const fileQuery = query.slice(6).trim().toUpperCase();
        
        // Fetch all files for this user only - STRICT DATA ISOLATION
        try {
          const userId = user?.id;
          if (!userId) {
            console.log('[DATA ISOLATION] No user ID - skipping files fetch');
            return;
          }
          const res = await fetch(`${API_BASE_URL}/api/viewers/list?user_id=${encodeURIComponent(userId)}`);
          if (res.ok) {
            const data = await res.json();
            const allFiles = data.viewers || [];
            
            // Dedupe by ticker (keep most recent)
            const tickerMap = new Map();
            allFiles.forEach(d => {
              const key = d.ticker || d.viewer_id;
              if (!tickerMap.has(key) || new Date(d.created_at) > new Date(tickerMap.get(key).created_at)) {
                tickerMap.set(key, d);
              }
            });
            
            const dedupedFiles = Array.from(tickerMap.values());
            
            // Update briefs list for dropdown (always update with all briefs)
            setBriefsList(dedupedFiles);
            
            // Filter by ticker or company name if query provided
            const filtered = fileQuery 
              ? dedupedFiles.filter(d => {
                  const tickerMatch = d.ticker && d.ticker.toUpperCase().includes(fileQuery);
                  const companyMatch = d.company_name && d.company_name.toUpperCase().includes(fileQuery);
                  return tickerMatch || companyMatch;
                })
              : dedupedFiles;
            
            setFilesSearchResults(filtered);
          }
        } catch (error) {
          console.error('Error searching files:', error);
          setFilesSearchResults([]);
          setBriefsList([]);
        }
        
        setSearchResults(null);
        setPollSearchResults(null);
        setListSearchResults(null);
        setIsSearching(false);
        trackEngagement('search', 'search', { search_type: 'file', query_length: fileQuery.length });
        return;
      }
      
      // Check for groups: prefix - fetch ALL groups from API
      if (query.toLowerCase().startsWith('groups:')) {
        const groupsQuery = query.slice(7).trim().toLowerCase();
        setIsSearching(true);
        
        try {
          const userId = user?.id || user?.user_id || user?.email;
          
          // Fetch ALL groups from API to ensure we have the latest data
          const groupsRes = await fetch(`${API_BASE_URL}/api/groups/${userId}`);
          const groupsData = groupsRes.ok ? await groupsRes.json() : { groups: [] };
          const allGroups = groupsData.groups || [];
          
          console.log('🔍 groups: search - ALL groups from API:', allGroups.length);
          
          // Also update the local userGroups state with fresh data
          setUserGroups(allGroups);
          
          // Store the filter query and fetched groups for use in the UI
          setGroupsSearchResults({ query: groupsQuery, triggered: true, groups: allGroups });
          setIdeasSearchResults(null);
          setFoldersSearchResults(null);
          setSearchResults(null);
          setPollSearchResults(null);
          setListSearchResults(null);
          setTickersCategoryResults(null);
          setIsSearching(false);
          trackEngagement('search', 'search', { search_type: 'groups', query_length: groupsQuery.length });
          return;
        } catch (error) {
          console.error('Error fetching groups search data:', error);
          setGroupsSearchResults({ query: groupsQuery, triggered: true, groups: [] });
          setIsSearching(false);
          return;
        }
      }
      
      // Check for ideas: prefix - fetch ALL ideas from API (bypass local pagination)
      if (query.toLowerCase().startsWith('ideas:')) {
        const ideasQuery = query.slice(6).trim().toLowerCase();
        setIsSearching(true);
        
        try {
          const userId = user?.id || user?.user_id || user?.email;
          
          // Fetch ALL ideas from dashboard API with large page_size to bypass pagination
          // include_no_tickers=true ensures ideas without tickers are also included in search
          // include_nested=true ensures nested ideas (from threads/quoted tweets) are also included
          const ideasRes = await fetch(`${API_BASE_URL}/api/dashboard-ideas/${userId}?page=1&page_size=1000&include_no_tickers=true&include_nested=true`);
          const ideasData = ideasRes.ok ? await ideasRes.json() : { ideas: [] };
          const allIdeas = ideasData.ideas || [];
          
          console.log('🔍 ideas: search - ALL ideas from API:', allIdeas.length, 'total_cards:', ideasData.pagination?.total_cards);
          
          const filtered = ideasQuery 
            ? allIdeas.filter(idea => {
                const title = (idea.title || idea.one_liner_thesis || '').toLowerCase();
                const summary = (idea.summary || '').toLowerCase();
                const tickers = (idea.tickers || []).map(t => typeof t === 'string' ? t : t?.ticker || '').join(' ').toLowerCase();
                const handle = (idea.handle || idea.author || idea.author_username || '').toLowerCase();
                return title.includes(ideasQuery) || summary.includes(ideasQuery) || tickers.includes(ideasQuery) || handle.includes(ideasQuery);
              })
            : allIdeas;
          
          setIdeasSearchResults(filtered);
          setGroupsSearchResults(null);
          setFoldersSearchResults(null);
          setSearchResults(null);
          setPollSearchResults(null);
          setListSearchResults(null);
          setTickersCategoryResults(null);
          setIsSearching(false);
          trackEngagement('search', 'search', { search_type: 'ideas', query_length: ideasQuery.length });
          return;
        } catch (error) {
          console.error('Error fetching ideas search data:', error);
          setIdeasSearchResults([]);
          setIsSearching(false);
          return;
        }
      }
      
      // Check for folders: prefix - fetch ALL scheduled research from API
      if (query.toLowerCase().startsWith('folders:')) {
        const foldersQuery = query.slice(8).trim().toLowerCase();
        setIsSearching(true);
        
        try {
          const userId = user?.id || user?.user_id || user?.email;
          
          // Fetch both schedule sources from API
          const [scheduleRes, manualRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/research-schedule/${userId}`),
            fetch(`${API_BASE_URL}/api/scheduled-research/${userId}`)
          ]);
          
          const autoSchedule = scheduleRes.ok ? (await scheduleRes.json()).schedule || [] : [];
          const manualSchedule = manualRes.ok ? (await manualRes.json()).reminders || [] : [];
          
          // Combine both sources
          const allScheduleItems = [
            ...autoSchedule.map(item => ({ ...item, source: 'auto' })),
            ...manualSchedule.map(item => ({
              ticker: item.ticker,
              company_name: item.company_name || '',
              scheduled_date: item.reminder_date || item.scheduled_date,
              notes: item.notes,
              id: item.id,
              source: 'manual'
            }))
          ];
          
          console.log('🔍 folders: search - ALL schedule items:', allScheduleItems.length, '(auto:', autoSchedule.length, ', manual:', manualSchedule.length, ')');
          
          // Store the fetched data along with the filter query
          setFoldersSearchResults({ query: foldersQuery, triggered: true, items: allScheduleItems });
          setGroupsSearchResults(null);
          setIdeasSearchResults(null);
          setSearchResults(null);
          setPollSearchResults(null);
          setListSearchResults(null);
          setTickersCategoryResults(null);
          setIsSearching(false);
          trackEngagement('search', 'search', { search_type: 'folders', query_length: foldersQuery.length });
          return;
        } catch (error) {
          console.error('Error fetching folders search data:', error);
          setFoldersSearchResults({ query: foldersQuery, triggered: true, items: [] });
          setIsSearching(false);
          return;
        }
      }
      
      // Regular search - clear special results
      setPollSearchResults(null);
      setListSearchResults(null);
      setFilesSearchResults(null);
      setFilesSearchQuery(null);
      setGroupsSearchResults(null);
      setIdeasSearchResults(null);
      setFoldersSearchResults(null);
      setGlobalTickerResults(null); // Reset global ticker results

      // Track search engagement with search type detection
      let searchType = 'word';
      const isTickerSearch = query.startsWith('$') || /^[A-Z]{1,5}$/.test(query);
      if (isTickerSearch) {
        searchType = 'ticker';
      } else if (query.startsWith('@')) {
        searchType = 'handle';
      } else if (['email', 'bookmark', 'twitter', 'x', 'manual'].some(s => query.toLowerCase().includes(s))) {
        searchType = 'source';
      }
      trackEngagement('search', 'search', { search_type: searchType, query_length: query.length });

      setIsSearching(true);
      try {
        // For ticker searches ($TICKER or TICKER), use global ticker search
        if (isTickerSearch) {
          const tickerQuery = query.replace('$', '').trim();
          
          // Call both APIs in parallel - global ticker search for comprehensive results
          // and regular search for backward compatibility with ideas display
          const [globalRes, searchRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/global-ticker-search/${encodeURIComponent(userId)}?ticker=${encodeURIComponent(tickerQuery)}&limit=50`),
            fetch(`${API_BASE_URL}/api/search-ideas/${encodeURIComponent(userId)}?q=${encodeURIComponent(searchQuery.trim())}&limit=50`)
          ]);
          
          // Set global ticker results for the grouped view
          if (globalRes.ok) {
            const globalData = await globalRes.json();
            setGlobalTickerResults(globalData);
            console.log(`🔍 Global ticker search for $${globalData.ticker}: ${globalData.total} total (${globalData.ideas?.length || 0} ideas, ${globalData.files?.length || 0} files, ${globalData.watchlists?.length || 0} watchlists)`);
          } else {
            setGlobalTickerResults(null);
          }
          
          // Also set regular search results for the ideas list view
          if (searchRes.ok) {
            const searchData = await searchRes.json();
            const ideas = searchData.ideas || [];
            setSearchResults(ideas);
            const tickerCount = ideas.reduce((total, idea) => {
              const tickers = idea.tickers || [];
              return total + (tickers.length || 1);
            }, 0);
            setSearchTickerCount(tickerCount);
          } else {
            setSearchResults([]);
            setSearchTickerCount(0);
          }
        } else {
          // Regular search for non-ticker queries - ALSO call global keyword search
          // This searches across ideas, watchlists, polls, and tickers by company name
          const [searchRes, keywordRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/search-ideas/${encodeURIComponent(userId)}?q=${encodeURIComponent(searchQuery.trim())}&limit=50`),
            fetch(`${API_BASE_URL}/api/global-keyword-search/${encodeURIComponent(userId)}?q=${encodeURIComponent(searchQuery.trim())}&limit=50`)
          ]);
          
          if (searchRes.ok) {
            const data = await searchRes.json();
            const ideas = data.ideas || [];
            setSearchResults(ideas);
            // Calculate total tickers across all search results
            const tickerCount = ideas.reduce((total, idea) => {
              const tickers = idea.tickers || [];
              return total + (tickers.length || 1); // Count at least 1 if no tickers array
            }, 0);
            setSearchTickerCount(tickerCount);
            console.log(`🔍 Ideas search found ${ideas.length} posts with ${tickerCount} ideas for: "${searchQuery.trim()}"`);
          } else {
            console.error('Search API error:', searchRes.status);
            setSearchResults([]);
            setSearchTickerCount(0);
          }
          
          // Set global keyword results (watchlists, polls, tickers by company)
          if (keywordRes.ok) {
            const keywordData = await keywordRes.json();
            setGlobalKeywordResults(keywordData);
            console.log(`🔍 Global keyword search found: ${keywordData.watchlists?.length || 0} lists, ${keywordData.polls?.length || 0} polls, ${keywordData.tickers_by_company?.length || 0} companies for: "${searchQuery.trim()}"`);
          } else {
            setGlobalKeywordResults(null);
          }
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        setGlobalTickerResults(null);
        setGlobalKeywordResults(null);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, user?.id, user?.user_id, user?.email, trackEngagement, fishingThreads, watchlists, bookmarks, sharedWatchlists, sharedPolls]);

  // Expand/collapse state
  const [expandedBookmarks, setExpandedBookmarks] = useState(new Set());
  const [globalExpanded, setGlobalExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Sorting state
  const [sortBy, setSortBy] = useState('newest'); // newest (default), fresh, warm, stale (age-based categories)
  const sortByRef = useRef(sortBy); // Ref to track current sortBy for use in fetchData closure
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortByInitializedRef = useRef(false); // Track if sort has been initialized
  
  // Reset first-seen indexes when sort option changes to ensure correct ordering
  useEffect(() => {
    // Always keep ref in sync
    sortByRef.current = sortBy;
    
    // Skip the effect on initial mount (when sortBy is set to its default value)
    if (!sortByInitializedRef.current) {
      sortByInitializedRef.current = true;
      return;
    }
    
    // Track sort change for engagement analytics
    trackEngagement('sort_change', 'navigation', { value: sortBy });
    
    // Reset pagination and first-seen indexes for fresh sorted data
    ideaFirstSeenIndexRef.current = new Map();
    nextFirstSeenIndexRef.current = 0;
    setCurrentPage(1);
    currentPageRef.current = 1;
    setBookmarks([]); // Clear existing bookmarks to show loading state
    console.log(`🔄 Sort changed to '${sortBy}', resetting pagination and refetching...`);
    
    // Refetch data with new sort option
    if (user?.id || user?.email) {
      fetchData(); // This will fetch with the new sortBy value from ref
    }
  }, [sortBy, trackEngagement]);
  
  // Shared ideas state
  const [showSharedByMeModal, setShowSharedByMeModal] = useState(false);
  const [sharedByMeIdeas, setSharedByMeIdeas] = useState([]);
  const [sharedByMeCount, setSharedByMeCount] = useState(0);
  const [expandedGroupShares, setExpandedGroupShares] = useState({}); // Track which group shares are expanded
  
  // Fetch shared ideas count on mount - DEFERRED for performance
  useEffect(() => {
    const fetchSharedCount = async () => {
      try {
        const userEmail = user?.email || 'urijmiller@gmail.com';
        const response = await fetch(`${API_BASE_URL}/api/shared-by-me/${encodeURIComponent(userEmail)}`, {
          headers: {
            'X-User-ID': user?.id || 'anonymous'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Count number of shares (posts/polls), not individual tickers
          setSharedByMeCount(data.total_count || 0);
        }
      } catch (error) {
        console.error('Error fetching shared count:', error);
      }
    };
    
    if (user?.email) {
      // Defer to improve initial load time
      setTimeout(() => {
        fetchSharedCount();
      }, 1500);
    }
  }, [user?.email]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't interfere with ANY modal or input interactions
      if (event.target.closest('.fixed.inset-0') || 
          event.target.tagName === 'INPUT') {
        return;
      }
      
      if (showSortDropdown && !event.target.closest('.sort-dropdown-container')) {
        setShowSortDropdown(false);
      }
      
      // Close polls dropdown when clicking outside
      if (showPollDropdown && !event.target.closest('.poll-dropdown-container')) {
        setShowPollDropdown(false);
      }
      
      // Close poll sort dropdown when clicking outside
      if (showPollSortMenu && !event.target.closest('.poll-sort-dropdown-container')) {
        setShowPollSortMenu(false);
      }
      
      // Close poll type filter dropdown when clicking outside
      if (showPollTypeFilterMenu && !event.target.closest('.poll-type-filter-container')) {
        setShowPollTypeFilterMenu(false);
      }
      
      // Close group age sort dropdown when clicking outside
      if (showGroupAgeSortMenu && !event.target.closest('.group-age-sort-container')) {
        setShowGroupAgeSortMenu(false);
      }
      
      // Close group type filter dropdown when clicking outside
      if (showGroupTypeFilterMenu && !event.target.closest('.group-type-filter-container')) {
        setShowGroupTypeFilterMenu(false);
      }
      
      // Close groups dropdown when clicking outside
      if (showGroupDropdown && !event.target.closest('.group-dropdown-container')) {
        setShowGroupDropdown(false);
      }
      
      // Close group sort dropdown when clicking outside
      if (showGroupSortMenu && !event.target.closest('.group-sort-dropdown-container')) {
        setShowGroupSortMenu(false);
      }
      
      // Close new poll dropdown when clicking outside
      if (showNewPollDropdown && !event.target.closest('.new-poll-dropdown-container')) {
        setShowNewPollDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortDropdown, showPollDropdown, showPollSortMenu, showPollTypeFilterMenu, showGroupAgeSortMenu, showGroupTypeFilterMenu, showGroupDropdown, showGroupSortMenu, showNewPollDropdown]);
  
  // Manual watchlist creation states
  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [newWatchlistTickers, setNewWatchlistTickers] = useState('');
  const [creatingWatchlist, setCreatingWatchlist] = useState(false);
  const [watchlistModalOpen, setWatchlistModalOpen] = useState(false);
  const [watchlistModalTickers, setWatchlistModalTickers] = useState([]);
  const [watchlistModalTitle, setWatchlistModalTitle] = useState('');
  const [watchlistModalIdeas, setWatchlistModalIdeas] = useState([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null); // For global search navigation
  
  // Create Collection modal ticker search states
  const [createCollectionTickerSearch, setCreateCollectionTickerSearch] = useState('');
  const [createCollectionSearchResults, setCreateCollectionSearchResults] = useState([]);
  const [createCollectionSearching, setCreateCollectionSearching] = useState(false);
  const [createCollectionSelectedTickers, setCreateCollectionSelectedTickers] = useState([]);
  
  // Watchlist creation type popup (Auto vs Manual)
  const [showWatchlistTypePopup, setShowWatchlistTypePopup] = useState(false);
  const [pendingWatchlistTicker, setPendingWatchlistTicker] = useState(null);
  const [pendingWatchlistIndustry, setPendingWatchlistIndustry] = useState(null);
  const [showManualWatchlistSelect, setShowManualWatchlistSelect] = useState(false);
  
  // Remember collection choice preference (stored in localStorage)
  const [rememberCollectionChoice, setRememberCollectionChoice] = useState(() => {
    try {
      return localStorage.getItem('tickler_remember_collection_choice') === 'true';
    } catch {
      return false;
    }
  });
  const [savedCollectionChoice, setSavedCollectionChoice] = useState(() => {
    try {
      return localStorage.getItem('tickler_collection_choice') || null; // 'auto' or 'manual'
    } catch {
      return null;
    }
  });
  
  // Create Collection modal mode - 'create' for new collection, 'existing' for add to existing
  const [collectionModalMode, setCollectionModalMode] = useState('create');
  
  // Theme toggle state - persist to localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('tickler_dark_mode');
      // Default to true (dark mode) if no preference saved
      return saved === null ? true : saved === 'true';
    } catch {
      return true; // Default to dark mode
    }
  });
  
  // Persist dark mode preference when it changes - both to localStorage and backend
  useEffect(() => {
    try {
      localStorage.setItem('tickler_dark_mode', isDarkMode.toString());
      
      // Sync theme preference to backend for email digest (fire and forget)
      if (user?.id) {
        const theme = isDarkMode ? 'dark' : 'light';
        fetch(`${API_BASE_URL}/api/email-preferences/${user.id}/theme`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme }),
          credentials: 'include'
        }).catch(() => {/* Silently fail - this is a background sync */});
      }
    } catch (e) {
      console.warn('Could not save dark mode preference:', e);
    }
  }, [isDarkMode, user?.id]);
  
  // Ref for search input to focus with keyboard shortcut
  const searchInputRef = useRef(null);

  // Load data when authentication changes - but with delay to avoid race conditions
  // Fetch data after authentication is complete
  useEffect(() => {
    if (!!user && user && !authLoading) {
      // Fetch critical data immediately
      fetchData();
      fetchAllSources(); // Fetch all unique sources for dropdown filter
      
      // Defer non-critical data to after initial render (improves perceived performance)
      setTimeout(() => {
        fetchGroups();
        fetchPendingInvitations();
      }, 1000);
      
      // No auto-refresh - user will manually refresh when needed
    }
  }, [!!user, user, authLoading]);

  // Track if this is the initial mount to avoid refetching on first render
  const isInitialMountForFilter = React.useRef(true);

  // Refetch data when source filter changes (skip initial mount)
  useEffect(() => {
    if (isInitialMountForFilter.current) {
      isInitialMountForFilter.current = false;
      return;
    }
    if (!!user && user && !authLoading) {
      // Track source filter change for engagement analytics
      trackEngagement('source_change', 'navigation', { value: sourceFilter });
      console.log(`🔄 Source filter changed to: ${sourceFilter}. Refetching data...`);
      fetchData(); // Refetch data with new filter
    }
  }, [sourceFilter, trackEngagement]);

  // Fetch groups when user is authenticated - REMOVED (now deferred in above useEffect)
  // useEffect(() => {
  //   if (!!user && user) {
  //     fetchGroups();
  //     fetchPendingInvitations();
  //   }
  // }, [!!user, user]);

  // Check subscription status when user loads
  useEffect(() => {
    if (user?.id) {
      checkSubscription();
    }
  }, [user?.id]);

  // Check for payment success/cancel in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    
    if (payment === 'success') {
      showNotification('🎉 Payment successful! Your Pro subscription is now active.', 'success', 5000);
      checkSubscription(); // Refresh subscription status
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      showNotification('Payment cancelled. You can upgrade anytime from Settings.', 'info', 4000);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check for invitation acceptance messages in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    const groupName = urlParams.get('group');
    const email = urlParams.get('email');
    
    if (message === 'joined-group') {
      showNotification(`Success! You've joined the group "${groupName}".\n\nYou'll now receive investment ideas shared with this group at ${email}`, 'success', 6000);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (message === 'already-member') {
      showNotification(`You're already a member of "${groupName}"!`, 'info');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (message === 'invitation-not-found') {
      showNotification('Invitation not found or already used.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (message === 'group-not-found') {
      showNotification('Group not found.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (message === 'error') {
      showNotification('An error occurred while accepting the invitation. Please try again.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Handle navigation from Folders tab to Ideas tab with scroll to specific idea
  useEffect(() => {
    if (!scrollToIdeaTarget || activeTab !== 'dashboard') return;
    
    const { ticker, ideaId } = scrollToIdeaTarget;
    
    // Extract base ID by removing ticker suffix (e.g., "x_bookmark_123_AAPL" -> "x_bookmark_123")
    const baseIdeaId = ideaId?.replace(/_[A-Z0-9.]+$/i, '') || ideaId;
    
    // Clear target immediately to prevent re-triggers
    setScrollToIdeaTarget(null);
    
    // Clear any existing search to enable rollup expansion (search disables rollup view)
    setSearchQuery('');
    
    // Scroll with highlight
    const scrollOnce = (targetElement) => {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetElement.style.boxShadow = '0 0 0 3px #f97316';
      targetElement.style.transition = 'box-shadow 0.3s ease';
      setTimeout(() => { targetElement.style.boxShadow = ''; }, 2500);
    };
    
    // Helper to strip suffixes for comparison
    const stripSuffixes = (id) => {
      if (!id) return id;
      // Remove _summary, _TICKER suffixes
      return id.replace(/_summary$/i, '').replace(/_[A-Z0-9.]+$/i, '');
    };
    
    // Wait for DOM to settle after tab switch
    setTimeout(() => {
      const allCards = document.querySelectorAll('[data-idea-id]');
      let targetCard = null;
      const strippedIdeaId = stripSuffixes(ideaId);
      const strippedBaseId = stripSuffixes(baseIdeaId);
      
      // Try exact ID match first, then base ID
      targetCard = document.querySelector(`[data-idea-id="${ideaId}"]`) || 
                   document.querySelector(`[data-idea-id="${baseIdeaId}"]`);
      
      // If no exact match, try matching with _summary suffix
      if (!targetCard) {
        targetCard = document.querySelector(`[data-idea-id="${baseIdeaId}_summary"]`);
      }
      
      // If no exact match, find card whose stripped ID matches
      if (!targetCard) {
        for (const card of allCards) {
          const cardId = card.getAttribute('data-idea-id');
          const strippedCardId = stripSuffixes(cardId);
          
          // Check if stripped IDs match
          if (strippedCardId === strippedIdeaId || strippedCardId === strippedBaseId) {
            targetCard = card;
            break;
          }
          
          // Check prefix match (for nested ideas within a thread)
          if (strippedIdeaId?.startsWith(strippedCardId) || strippedBaseId?.startsWith(strippedCardId)) {
            targetCard = card;
            break;
          }
        }
      }
      
      if (targetCard) {
        const parentId = targetCard.getAttribute('data-idea-id');
        
        // Expand the card (both states to cover threads and rollups)
        setExpandedCardGroups(prev => new Set([...prev, parentId]));
        setExpandedRollups(prev => new Set([...prev, parentId]));
        
        // After expansion, find and scroll to the specific ticker
        setTimeout(() => {
          // Look for nested item (rollup) or child item (thread)
          const nestedItem = document.querySelector(`[data-nested-ticker="${ticker}"]`) ||
                             document.querySelector(`[data-child-ticker="${ticker}"]`);
          
          if (nestedItem) {
            scrollOnce(nestedItem);
          } else {
            scrollOnce(targetCard);
          }
        }, 500);
      }
    }, 600);
  }, [scrollToIdeaTarget, activeTab]);

  // Handle idea action URL parameters from digest emails and spreadsheet page
  useEffect(() => {
    // Get the idea parameter from URL first, before any checks
    const urlParams = new URLSearchParams(window.location.search);
    const ideaId = urlParams.get('idea');
    const action = urlParams.get('action');
    
    // No idea parameter in URL, nothing to do
    if (!ideaId) return;
    
    // CRITICAL: Check sessionStorage FIRST (persists across component remounts and StrictMode double-runs)
    const handledKey = `url_idea_handled_${ideaId}`;
    if (sessionStorage.getItem(handledKey)) {
      return; // Already handled this specific idea
    }
    
    // Need user and bookmarks to be loaded
    if (!user?.id || bookmarks.length === 0) return;
    
    // Find the idea in bookmarks
    const idea = bookmarks.find(b => b.id === ideaId);
    
    if (idea) {
      // Mark as handled IMMEDIATELY in sessionStorage (prevents all duplicate runs)
      sessionStorage.setItem(handledKey, 'true');
      
      // Clean URL right away
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // If no action specified, just view the idea (show details/expand)
      if (!action) {
        // Scroll to ideas view and expand the idea if it's a rollup
        setActiveTab('ideas');
        setDashboardView('ideas');
        
        // Expand the idea if it's a rollup/has nested ideas
        if (idea.is_rollup || idea.ticker_ideas?.length > 0) {
          setExpandedRollups(prev => new Set([...prev, idea.id]));
        }
        
        // Also expand the card group if it's grouped
        setExpandedCardGroups(prev => new Set([...prev, idea.id]));
        
        // Show notification ONCE
        const tickerDisplay = idea.tickers?.[0] || idea.title || 'Investment Idea';
        showNotification(`Viewing: ${typeof tickerDisplay === 'string' ? tickerDisplay : tickerDisplay.ticker || 'Idea'}`, 'info', 3000);
        
        // Scroll to the idea - use polling to wait for DOM element to render
        const scrollToIdea = (attempts = 0) => {
          const ideaElement = document.querySelector(`[data-idea-id="${ideaId}"]`);
          if (ideaElement) {
            ideaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight effect with orange border
            ideaElement.style.boxShadow = '0 0 0 3px #f97316';
            ideaElement.style.transition = 'box-shadow 0.3s ease';
            setTimeout(() => {
              ideaElement.style.boxShadow = '';
            }, 2500);
          } else if (attempts < 10) {
            // Retry up to 10 times (total 2.5 seconds wait)
            setTimeout(() => scrollToIdea(attempts + 1), 250);
          }
        };
        // Start checking after initial render delay
        setTimeout(scrollToIdea, 300);
        return;
      }
      
      // Track modal button click for engagement analytics
      trackEngagement('modal_click', 'modal', { 
        button_name: action, 
        ticker: idea.tickers?.[0] || null,
        source: idea.source || 'unknown'
      });
      
      // Perform the action
      switch(action) {
        case 'original':
          // Track original document view
          trackEngagement('original_view', 'modal', { source: idea.source || 'unknown' });
          // Open original content modal - prioritize lead tweet for threads
          const twitterMeta = idea.twitterMetadata || idea.metadata?.twitter_content?.tweet_data || null;
          const hasThread = twitterMeta?.is_thread && twitterMeta?.full_thread?.length > 0;
          const leadTweetText = hasThread ? twitterMeta.full_thread[0]?.text : null;
          const fullTweetContent = leadTweetText ||
                                  idea.original_content || 
                                  idea.twitterMetadata?.text || 
                                  idea.metadata?.twitter_content?.tweets?.[0]?.text ||
                                  idea.metadata?.original_email_content || 
                                  idea.tweet_content ||
                                  '';
          const shouldUseTweet = (idea.source === 'bookmark' || idea.source?.startsWith('x_bookmark') || idea.is_twitter_content) && fullTweetContent;
          
          setSelectedOriginalContent({
            content: shouldUseTweet ? fullTweetContent : (idea.original_content || idea.content),
            title: idea.title || idea.company_name || 'Original Content',
            source: idea.source || 'Email',
            isTwitterContent: idea.is_twitter_content || idea.source?.startsWith('x_bookmark') || false,
            twitterMetadata: idea.twitterMetadata || idea.metadata?.twitter_content?.tweet_data || null,
            pdfData: idea.metadata?.pdf_data || null,
            attachments: idea.metadata?.attachments || [],
            useTwitterFormatting: shouldUseTweet
          });
          setShowOriginalContentModal(true);
          break;
          
        case 'documents':
          // Open document modal - this triggers the SEC dossier generation
          if (idea.tickers && idea.tickers[0]) {
            setSelectedTickerForScheduling({
              ticker: idea.tickers[0],
              company_name: idea.company_name || idea.title,
              ideaId: idea.id
            });
          }
          break;
          
        case 'schedule':
          // Open schedule modal
          if (idea.tickers && idea.tickers[0]) {
            setSelectedTickerForScheduling({
              ticker: idea.tickers[0],
              company_name: idea.company_name || idea.title,
              ideaId: idea.id
            });
          }
          break;
          
        case 'share':
          // Open share modal
          if (idea.tickers && idea.tickers[0]) {
            setSelectedTickerForScheduling({
              ticker: idea.tickers[0],
              company_name: idea.company_name || idea.title,
              ideaId: idea.id
            });
            setShowShareModal(true);
          }
          break;
          
        case 'watchlist':
          // Open add-to-collection functionality
          if (idea.tickers && idea.tickers.length > 0) {
            openWatchlistModal(idea.tickers, `Add ${idea.tickers[0]} to Tickers`);
          }
          break;
          
        default:
          break;
      }
    } else {
      // Idea not found - mark handled and show error
      sessionStorage.setItem('url_idea_handled', ideaId);
      window.history.replaceState({}, document.title, window.location.pathname);
      showNotification('Idea not found. It may have been removed or archived.', 'error', 4000);
    }
  }, [user?.id, bookmarks]);

  // Handle direct tab navigation from URL parameter (e.g., from email links)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    
    // Only proceed if there's a tab parameter and user is logged in
    if (tab && user?.id) {
      // Map valid tab values to their setActiveTab equivalents
      const validTabs = ['dashboard', 'ideas', 'files', 'folders', 'schedule', 'watchlists', 'threads', 'groups', 'settings'];
      
      if (validTabs.includes(tab.toLowerCase())) {
        setActiveTab(tab.toLowerCase());
        
        // Clean URL after navigation
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [user?.id]);

  // Handle open_ticker URL parameter from spreadsheet page - opens watchlist modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openTicker = urlParams.get('open_ticker');
    const tab = urlParams.get('tab');
    
    if (openTicker && user?.id) {
      // Set the watchlists tab if specified
      if (tab === 'watchlists') {
        setActiveTab('watchlists');
      }
      
      // Open the watchlist modal for this ticker
      openWatchlistModal([openTicker.toUpperCase()], `${openTicker.toUpperCase()} Ideas`);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user?.id]);


  // Auto-categorize user tickers function
  const autoCategorizeUserTickers = async () => {
    if (!user || !user?.user_id) return;
    
    try {

      const response = await fetch(`${API_BASE_URL}/api/categorize-user-tickers/${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();

        
        // Refresh dashboard data to show updated industry tags
        if (result.successful_count > 0) {
          setTimeout(() => {
            fetchData();
          }, 2000); // Give time for backend processing
        }
      } else {
        console.error('Auto-categorization failed:', response.status);
      }
    } catch (error) {
      console.error('Error auto-categorizing user tickers:', error);
    }
  };

  // Group investment ideas by source for Source View
  const groupIdeasBySource = useCallback((ideas) => {
    const grouped = {};
    
    ideas.forEach(idea => {
      let sourceKey;
      let sourceTitle;
      let sourceType;
      let sourceIcon;
      let sourceBadgeStyle;
      
      // ALWAYS extract the person/author who created the content
      let personName = 'Unknown';
      
      // Priority 1: Direct author field (most reliable for Twitter)
      if (idea.author && idea.author !== 'undefined' && idea.author !== 'Unknown') {
        personName = idea.author.replace(/^@/, ''); // Remove @ prefix, we'll add it back for Twitter
      }
      // Priority 2: Tweet author
      else if (idea.tweet_author) {
        personName = idea.tweet_author.replace(/^@/, '');
      }
      // Priority 3: Extract from source_info.source_name (e.g., "X/Twitter (@Username)")
      else if (idea.source_info?.source_name) {
        const match = idea.source_info.source_name.match(/@(\w+)/);
        if (match) {
          personName = match[1];
        } else if (!idea.source_info.source_name.includes('/')) {
          // Use the whole name if it's not a generic "Platform/Type" format
          personName = idea.source_info.source_name;
        }
      }
      // Priority 4: AI attribution
      else if (idea.source_info?.ai_attribution?.original_source_name) {
        personName = idea.source_info.ai_attribution.original_source_name;
      }
      // Priority 5: PDF filename as source
      else if (idea.metadata?.has_pdf_attachments) {
        const filename = idea.metadata?.pdf_data?.filename || idea.metadata?.original_pdf_data?.filename || 'Unknown Document';
        personName = filename.replace('.pdf', '');
      }
      // Priority 6: From address for emails
      else if (idea.from_address) {
        personName = idea.from_address.split('@')[0];
      }
      
      // Determine source type for icon/styling
      const isTwitter = idea.is_twitter_content || idea.source === 'bookmark' || idea.source === 'x_bookmark' || 
                        idea.source === 'x_bookmark_thread_summary' || idea.metadata?.twitter_data;
      const isPDF = idea.metadata?.has_pdf_attachments;
      const isEmail = idea.source === 'email';
      
      // Format the display name
      let displayName = personName;
      if (isTwitter && !personName.startsWith('@')) {
        displayName = `@${personName.replace(/\s+/g, '').toLowerCase()}`;
      }
      
      // Create unique key based on normalized person name
      sourceKey = displayName.toLowerCase().replace(/[^a-z0-9]/g, '_');
      sourceTitle = displayName;
      
      // Set source type and styling
      if (isTwitter) {
        sourceType = 'twitter';
        sourceBadgeStyle = 'bg-black text-white';
        sourceIcon = (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        );
      } else if (isPDF) {
        sourceType = 'pdf';
        sourceBadgeStyle = 'bg-red-100 text-red-700';
        sourceIcon = (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
        );
      } else if (isEmail) {
        sourceType = 'email';
        sourceBadgeStyle = 'bg-blue-100 text-blue-700';
        sourceIcon = (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
        );
      } else {
        sourceType = 'other';
        sourceBadgeStyle = 'bg-gray-100 text-gray-700';
        sourceIcon = (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
        );
      }
      
      if (!grouped[sourceKey]) {
        grouped[sourceKey] = {
          sourceKey,
          sourceTitle,
          sourceType,
          sourceIcon,
          sourceBadgeStyle,
          ideas: [],
          createdAt: idea.created_at
        };
      }
      
      grouped[sourceKey].ideas.push(idea);
    });
    
    // Convert to array and sort by idea count (most ideas first)
    return Object.values(grouped).sort((a, b) => b.ideas.length - a.ideas.length);
  }, []);


  // Fetch all sources from backend (for dropdown filter)
  const fetchAllSources = async () => {
    const currentUserId = user?.id || 'sample_user';
    if (!currentUserId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/ideas/sources/${currentUserId}`);
      if (!response.ok) {
        console.error('Failed to fetch all sources');
        return;
      }
      
      const data = await response.json();
      console.log('All sources from backend:', data);
      
      // Convert backend sources to frontend source categories format
      const sourceTypes = ['all']; // Always include "All"
      const sourceCategories = new Map();
      
      data.sources.forEach(source => {
        const sourceType = source.type;
        sourceTypes.push(sourceType);
        
        // Map source types to UI display info
        let label = sourceType;
        let icon = null;
        
        switch(sourceType) {
          case 'twitter':
            label = 'X Posts';
            icon = (
              <svg className="w-4 h-4 text-sky-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.910 11.078h-3.246z"/>
              </svg>
            );
            break;
          case 'investor_letter':
            label = 'Investment Letters';
            icon = (
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            );
            break;
          case 'pdf':
            label = 'Documents';
            icon = (
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            );
            break;
          case 'newsletter':
            label = 'Newsletters';
            icon = (
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            );
            break;
          case 'reddit':
            label = 'Reddit';
            icon = (
              <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
              </svg>
            );
            break;
          case 'substack':
            label = 'Substack';
            icon = (
              <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
              </svg>
            );
            break;
          case 'shared':
            label = 'Shared Ideas';
            icon = (
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
              </svg>
            );
            break;
          default:
            // For domain-based sources (fool, seeking_alpha, etc), use a generic news icon
            label = sourceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            icon = (
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
              </svg>
            );
        }
        
        // Create proper filter function for each source type
        let filterFunc;
        switch(sourceType) {
          case 'twitter':
            filterFunc = (item) => {
              const category = categorizByDomain(item);
              const hasTwitterData = item.metadata?.twitter_data || item.metadata?.twitter_content;
              const isExplicitlyTwitter = item.platform === 'Twitter' || item.is_twitter_content === true;
              return (category.name.includes('Twitter') || category.name.includes('X')) && 
                     (isExplicitlyTwitter || hasTwitterData);
            };
            break;
          case 'investor_letter':
            filterFunc = (item) => {
              if (!item.metadata?.has_pdf_attachments) return false;
              const aiType = item.source_info?.ai_attribution?.original_source_type;
              const actualSourceName = item.source_info?.ai_attribution?.original_source_name || 
                                     item.source_info?.source_name || 'Unknown';
              return aiType === 'investment_letter' || aiType === 'investor_letter' ||
                     actualSourceName.toLowerCase().includes('capital') || 
                     actualSourceName.toLowerCase().includes('fund') || 
                     actualSourceName.toLowerCase().includes('partners') ||
                     (item.metadata?.pdf_data?.text_content && 
                      item.metadata.pdf_data.text_content.toLowerCase().includes('dear limited partners'));
            };
            break;
          case 'pdf':
            filterFunc = (item) => {
              if (!item.metadata?.has_pdf_attachments) return false;
              const aiType = item.source_info?.ai_attribution?.original_source_type;
              const actualSourceName = item.source_info?.ai_attribution?.original_source_name || 
                                     item.source_info?.source_name || 'Unknown';
              return !(aiType === 'investment_letter' || aiType === 'investor_letter' ||
                      actualSourceName.toLowerCase().includes('capital') || 
                      actualSourceName.toLowerCase().includes('fund') || 
                      actualSourceName.toLowerCase().includes('partners') ||
                      (item.metadata?.pdf_data?.text_content && 
                       item.metadata.pdf_data.text_content.toLowerCase().includes('dear limited partners')));
            };
            break;
          case 'newsletter':
            filterFunc = (item) => item.source === 'email' && !item.metadata?.has_pdf_attachments;
            break;
          case 'reddit':
            filterFunc = (item) => item.url?.includes('reddit.com');
            break;
          case 'substack':
            filterFunc = (item) => item.url?.includes('substack.com') || 
              item.source_info?.ai_attribution?.original_source_name?.toLowerCase().includes('substack');
            break;
          case 'shared':
            filterFunc = (item) => item.is_shared_idea || item.source === 'shared_idea';
            break;
          default:
            // For domain-based sources (fool, seeking_alpha, gmail_com, etc.)
            filterFunc = (item) => {
              const category = categorizByDomain(item);
              const domain = category.domain;
              
              // Map source type to domain
              const sourceDomain = sourceType.replace(/_/g, '.');
              
              // Check if the item's domain matches this source type
              if (domain === sourceDomain) return true;
              
              // Also check by source type name
              if (sourceType === 'gmail_com' && domain === 'gmail.com') return true;
              if (sourceType === 'user_com' && domain === 'user.com') return true;
              if (sourceType === 'fool' && domain === 'fool.com') return true;
              
              return false;
            };
        }
        
        sourceCategories.set(sourceType, {
          label,
          icon,
          count: source.count,
          filter: filterFunc
        });
      });
      
      setAllSourceTypes(sourceTypes);
      setAllSourceCategories(sourceCategories);
      console.log('Set all sources:', { sourceTypes, sourceCategories: Array.from(sourceCategories.keys()) });
      
    } catch (error) {
      console.error('Error fetching all sources:', error);
    }
  };

  // Compare AI Models Handler - Analyze ONE idea with MULTIPLE models
  const handleCompareAIModels = async (idea) => {
    console.log('🧪 Compare AI Models for idea:', { 
      id: idea.id?.substring(0, 20), 
      title: idea.title?.substring(0, 50),
      ticker: idea.ticker || idea.tickers?.[0]
    });
    
    setLlmAnalyzing(true);
    try {
      const ideaData = {
        id: idea.id,
        title: idea.title || '',
        content: idea.content || idea.summary || '',
        original_summary: idea.summary || '',
        ticker: idea.ticker || idea.tickers?.[0] || ''
      };
      
      console.log('🔄 Calling compare-ai-models API:', { 
        url: `${API_BASE_URL}/api/compare-ai-models`,
        ticker: ideaData.ticker
      });
      
      // Call backend to analyze with ALL models
      const response = await fetch(`${API_BASE_URL}/api/compare-ai-models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idea_data: ideaData,
          user_id: user?.user_id || 'sample_user'
        })
      });
      
      console.log('📥 API Response:', { status: response.status, ok: response.ok });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error('Failed to compare AI models');
      }
      
      const comparisonData = await response.json();
      console.log('✅ Received comparison data:', {
        model_count: comparisonData.models?.length,
        models: comparisonData.models?.map(m => m.name)
      });
      
      // Show comparison modal
      setLlmPreviewData({
        idea: ideaData,
        models: comparisonData.models
      });
      console.log('🎨 Setting modal state for AI comparison');
      console.log('📊 Modal data:', {
        showLLMPreview: true,
        llmPreviewData: {
          idea: ideaData.title,
          modelCount: comparisonData.models?.length
        }
      });
      setShowLLMPreview(true);
      
      // Force a small delay to ensure state updates
      setTimeout(() => {
        console.log('✅ Modal should now be visible');
      }, 100);
      
    } catch (error) {
      console.error('Error comparing AI models:', error);
      alert('Failed to compare AI models. Please try again.');
    } finally {
      setLlmAnalyzing(false);
      console.log('🏁 AI comparison complete');
    }
  };

  // Check subscription status
  const checkSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription/status/${user.id}`);
      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
        
        // Show pricing modal if trial expired
        if (status.tier === 'free' && status.trial_expired) {
          setShowPricingModal(true);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Removed fetchIdeasDirect - using fetchData() for all refreshes to maintain data consistency

  const fetchData = async (isLoadingMore = false) => {
    // Only fetch real data if user is authenticated
    
    
    // Allow demo user OR authenticated user to proceed
    const currentUserId = user?.id || 'sample_user';
    
    if (!currentUserId) {

    }
    

    if (isLoadingMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(1); // Reset to page 1 on fresh load
    }
    
    try {
      // Use ref for currentPage when loading more to avoid stale closure
      const actualCurrentPage = isLoadingMore ? currentPageRef.current : currentPage;
      const pageToFetch = isLoadingMore ? actualCurrentPage + 1 : 1;
      console.log(`📄 Fetching page ${pageToFetch} (isLoadingMore: ${isLoadingMore}, currentPage=${currentPage}, refPage=${currentPageRef.current})`);
      
      // Check cache for dashboard ideas (5 minute expiry) - only for page 1
      const cacheKey = `dashboard_ideas_v22_${currentUserId}`; // v22: Append "(Investor Letter)" to source name
      
      // AUTOMATIC CLEANUP: Remove ALL old dashboard idea caches on EVERY load to prevent quota issues
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          // Remove ANY dashboard_ideas cache that isn't the current session
          if (key && key.startsWith('dashboard_ideas_v')) {
            keysToRemove.push(key);
          }
        }
        if (keysToRemove.length > 0) {
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`🧹 Cleared ${keysToRemove.length} old caches to prevent quota issues`);
        }
      } catch (e) {
        console.error('Cleanup error:', e);
      }
      // CACHE COMPLETELY DISABLED - was causing QuotaExceededError and page crashes
      // Always fetch fresh data from API - it's fast enough
      console.log(`📡 Fetching fresh data: page=${pageToFetch}, source_filter=${sourceFilter}, sort_by=${sortByRef.current}`);
      
      // Fetch all critical data in parallel
      const promises = [
        fetch(`${API_BASE_URL}/api/bookmarks/${currentUserId}`),
        fetch(`${API_BASE_URL}/api/watchlists/${currentUserId}`),
        fetch(`${API_BASE_URL}/api/tickers/${currentUserId}`),
        fetch(`${API_BASE_URL}/api/stats/${currentUserId}`),
        fetch(`${API_BASE_URL}/api/fishing-threads/${currentUserId}`),
        fetch(`${API_BASE_URL}/api/usage-stats`),
        fetch(`${API_BASE_URL}/api/dashboard-ideas/${currentUserId}?page=${pageToFetch}&page_size=20&source_filter=${sourceFilter}&sort_by=${sortByRef.current}`)
      ];
      
      const responses = await Promise.all(promises);
      console.log(`✅ Received ${responses.length} API responses`);
      
      // Destructure all 7 responses (no caching anymore)
      const [bookmarksRes, watchlistsRes, tickersRes, statsRes, threadsRes, usageRes, dashboardIdeasRes] = responses;

      // Fetch pending shared ideas (uses email instead of user_id)
      const userEmail = user?.email || 'sample_user@example.com';
      try {
        const pendingRes = await fetch(`${API_BASE_URL}/api/shared-ideas/${userEmail}`);
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          console.log('[fetchData] Pending ideas response:', pendingData);
          setPendingIdeas(pendingData.pending_ideas || []);
        } else {
          console.error('[fetchData] Pending ideas API failed:', pendingRes.status);
          // Don't clear existing pending ideas on error
        }
      } catch (error) {
        console.error('[fetchData] Error fetching pending ideas:', error);
        // Don't clear existing pending ideas on error
      }



      // Check if any requests failed
      if (!bookmarksRes.ok) console.error('Bookmarks API failed:', bookmarksRes.status);
      if (!watchlistsRes.ok) console.error('Watchlists API failed:', watchlistsRes.status);
      if (!tickersRes.ok) console.error('Tickers API failed:', tickersRes.status);
      if (!statsRes.ok) console.error('Stats API failed:', statsRes.status);
      if (!threadsRes.ok) console.error('Threads API failed:', threadsRes.status);
      if (!usageRes.ok) console.error('Usage Stats API failed:', usageRes.status);
      if (dashboardIdeasRes && !dashboardIdeasRes.ok) console.error('Dashboard Ideas API failed:', dashboardIdeasRes.status);

      const bookmarksData = await bookmarksRes.json();
      const watchlistsData = await watchlistsRes.json();
      console.log('🔍 RAW WATCHLIST DATA:', watchlistsData);
      console.log('🔍 Watchlist count:', watchlistsData.watchlists?.length);
      if (watchlistsData.watchlists?.length > 0) {
        console.log('🔍 First watchlist:', watchlistsData.watchlists[0]);
      }
      const tickersData = await tickersRes.json();
      const statsData = await statsRes.json();
      const threadsData = await threadsRes.json();
      const usageData = await usageRes.json();
      
      // Process dashboard ideas (always fresh from API now)
      let ideasData;
      if (dashboardIdeasRes && dashboardIdeasRes.ok) {
        ideasData = await dashboardIdeasRes.json();
        console.log(`📊 Fetched ${ideasData.ideas?.length || 0} ideas from API`);
      } else {
        console.error('⚠️ Dashboard ideas API failed');
        ideasData = { ideas: [] };
      }
      
      // Update pagination state from response
      if (ideasData.pagination) {
        const newPage = ideasData.pagination.page;
        setCurrentPage(newPage);
        currentPageRef.current = newPage;  // CRITICAL: Keep ref in sync for closure access
        setHasMoreIdeas(ideasData.pagination.has_more);
        setTotalIdeas(ideasData.pagination.total_ideas);  // Total investment ideas for display
        setTotalCards(ideasData.pagination.total_cards || ideasData.pagination.total_ideas);  // Actual cards for pagination
        setMyIdeasCount(ideasData.pagination.my_ideas_count || 0);  // Count from database
        setReceivedIdeasCount(ideasData.pagination.received_ideas_count || 0);  // Count from database
        setTotalHandles(ideasData.pagination.total_handles || 0);  // Count of unique handles
        setUniqueHandlesList(ideasData.pagination.handles_list || []);  // List of handles for @ dropdown
        // Track if user has ever had ideas (use my_ideas_count which is unfiltered total)
        if ((ideasData.pagination.my_ideas_count || 0) > 0 || (ideasData.pagination.received_ideas_count || 0) > 0) {
          setHasEverHadIdeas(true);
        }
        console.log(`📄 Pagination updated: page ${newPage}/${ideasData.pagination.total_pages}, has_more: ${ideasData.pagination.has_more}, cards: ${ideasData.pagination.total_cards}, ideas: ${ideasData.pagination.total_ideas}, my: ${ideasData.pagination.my_ideas_count}, received: ${ideasData.pagination.received_ideas_count}, handles: ${ideasData.pagination.total_handles}`);
      }
      
      if (ideasData && ideasData.ideas && ideasData.ideas.length > 0) {
        
        // Transform dashboard ideas to bookmark format
        const transformedIdeas = ideasData.ideas.map((idea, index) => {
          // DEBUG: Log shared ideas when they're received from API
          if (idea.source === 'shared_idea') {
            console.log('SHARED IDEA SOURCE INFO:', {
              id: idea.id.substring(0, 8) + '...',
              title: idea.title.substring(0, 30) + '...',
              has_source_info: !!idea.source_info,
              source_info_keys: idea.source_info ? Object.keys(idea.source_info) : 'null',
              source_name: idea.source_info?.source_name || 'MISSING'
            });
          }
          
          const transformed = {
            id: idea.id || idea.bookmark_id, // Use idea.id first, fallback to bookmark_id
            parent_id: idea.parent_id || null, // CRITICAL: Include parent_id for thread nesting
            is_thread_summary: idea.is_thread_summary || false, // Thread summary parent flag
            content: idea.summary || idea.content || idea.tweet_content,  // Display AI summary first, then raw content
            author: idea.tweet_author,
            created_at: idea.created_at,
            tickers: idea.tickers || [],
            all_tickers: idea.all_tickers || idea.tickers || [],  // IDEAS LIST: Include all_tickers for badge display
            is_ticker_list: idea.is_ticker_list || false,  // IDEAS LIST: Flag for consolidated ticker list cards
            categorized_tickers: idea.categorized_tickers || [],  // IDEAS LIST: Categorized ticker groups
            researched_tickers: idea.researched_tickers || [],
            url: idea.tweet_url || idea.url,
            source: idea.source, // Keep original source from API
            is_received: idea.is_received || false, // Flag for ideas received from others
            shared_from: idea.shared_from || null, // Who shared this idea to the user
            platform: idea.platform || idea.source_info?.platform_source || (idea.source === 'email' ? 'Email' : idea.source === 'twitter' ? 'Twitter' : 'Email'), // Use backend platform first, then source_info platform, then infer from source
            is_substack: idea.is_substack || false, // Use backend is_substack detection
            ticker_industries: idea.ticker_industries || {},
            primary_sector: idea.primary_sector || 'Uncategorized',
            primary_ticker: idea.primary_ticker || (idea.tickers && idea.tickers[0]) || null, // CRITICAL: Include primary_ticker for Twelve Data lookup
            ticker_metrics: idea.ticker_metrics || {},
            // CRITICAL: Ensure financial data flows through
            primary_market_cap: idea.primary_market_cap,
            primary_enterprise_value: idea.primary_enterprise_value,
            // ROLLUP FIELDS: Add rollup card support
            is_rollup: idea.is_rollup || false,
            ticker_ideas: idea.ticker_ideas || null,
            title: idea.title || idea.content?.substring(0, 50) || 'Investment Idea',
            summary: idea.summary || '',
            one_liner_thesis: idea.one_liner_thesis || '',  // Concise investment thesis
            from_name: idea.from_name || idea.author,
            // COMPANY INTELLIGENCE: Critical missing field - this was the root cause!
            company_intelligence: idea.company_intelligence || null,
            company_name: idea.company_name || '',  // Company name from AI analysis
            // ORIGINAL CONTENT: For full context display
            metadata: idea.metadata || {},
            original_content: idea.original_content || idea.metadata?.original_email_content || null,  // Use direct field first, fallback to metadata
            original_email_content: idea.original_email_content || null,  // Preserve backend field for modal display
            is_twitter_content: idea.is_twitter_content || idea.metadata?.is_twitter_content || false,  // Use direct field first
            // SOURCE INFO: For original sender attribution
            source_info: idea.source_info || null,
            // CATEGORIZATION TAGS: Investment idea categorization
            categorization_tags: idea.categorization_tags || null,
            // CLASSIFICATION FIELDS: Content type classification (idea vs research)
            classification: idea.classification || 'idea',
            classification_confidence: idea.classification_confidence || 0.5,
            classification_reasoning: idea.classification_reasoning || '',
            // TWITTER METADATA: Full thread and context data
            twitterMetadata: idea.twitterMetadata || null,
            // WWNBT ANALYSIS: Author's Thesis for collapsed card display
            wwnbt_analysis: idea.wwnbt_analysis || null,
            // NEW: Hook-based preview and investment tags
            preview: idea.preview || '',
            tags: idea.tags || []
          };
          return transformed;
        });
        
        // Handle pagination: append or replace bookmarks
        if (isLoadingMore) {
          setBookmarks(prevBookmarks => {
            // Prevent duplicates by checking IDs
            const existingIds = new Set(prevBookmarks.map(b => b.id));
            const newIdeas = transformedIdeas.filter(idea => !existingIds.has(idea.id));
            console.log(`📝 Appending ${newIdeas.length} new ideas (filtered ${transformedIdeas.length - newIdeas.length} duplicates)`);
            return [...prevBookmarks, ...newIdeas];
          });
        } else {
          console.log('SETTING BOOKMARKS - checking first idea:', transformedIdeas[0] ? {
            id: transformedIdeas[0].id,
            title: transformedIdeas[0].title?.substring(0,40),
            has_original_email_content: !!transformedIdeas[0].original_email_content,
            original_email_content_length: transformedIdeas[0].original_email_content?.length || 0
          } : 'no ideas');
          // Fresh load - reset first-seen indexes to allow proper sorting
          ideaFirstSeenIndexRef.current = new Map();
          nextFirstSeenIndexRef.current = 0;
          setBookmarks(transformedIdeas);
        }
        
      } else {
        
        // Only clear bookmarks if this is a fresh load, not loading more
        if (!isLoadingMore) {
          setBookmarks([]);
        }
      }
      console.log('📋 Setting watchlists from loadUserData:', watchlistsData.watchlists);
      setWatchlists(watchlistsData.watchlists || []); 
      setTickers(tickersData.tickers || []);
      setFishingThreads(threadsData.threads || []);
      setStats(statsData || { total_bookmarks: 0, total_tickers: 0, total_watchlists: 0 });
      setApiUsage(usageData.data || { daily_calls: 0, daily_limit: 3, remaining_calls: 3 });
      

      
      // Auto-categorize user tickers if they have investment ideas but missing industry data
      // DISABLED: This was causing slow page loads due to AI API calls
      // Categorization now happens during import instead
      /*
      if (ideasData.ideas && ideasData.ideas.length > 0) {
        // Check if any ideas are missing industry categorization
        const hasUncategorizedTickers = ideasData.ideas.some(idea => 
          idea.primary_sector === 'Uncategorized' || !idea.primary_sector
        );
        
        if (hasUncategorizedTickers) {
          console.log('Found uncategorized tickers, starting auto-categorization...');
          // Run categorization in background without blocking UI
          setTimeout(() => {
            autoCategorizeUserTickers();
          }, 1000);
        }
      }
      */
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set fallback data so UI doesn't break
      setStats({ total_bookmarks: 0, total_tickers: 0, total_watchlists: 0 });
    }
    
    // Reset loading states
    if (isLoadingMore) {
      setLoadingMore(false);
    } else {
      setLoading(false);
    }
  };
  
  // Helper function to check if a ticker has complete/valid cached data
  // Returns true if data is VALID (should skip fetching), false if needs fetch
  const isTickerCacheValid = useCallback((ticker, cache) => {
    if (!ticker) return true; // Invalid ticker, skip
    const upperTicker = ticker.toUpperCase();
    const cached = cache[upperTicker];
    
    // Not cached at all - needs fetch
    if (!cached) return false;
    
    // Check if we have meaningful data for display:
    // MUST have market_cap (the primary metric shown in UI)
    // AND at least ONE of the newer financial metrics
    const hasMarketCap = cached.market_cap !== undefined && cached.market_cap !== null;
    const hasFinancialMetrics = cached.gross_margin !== undefined || 
                                cached.operating_margin !== undefined || 
                                cached.profit_margin !== undefined || 
                                cached.shares_outstanding !== undefined ||
                                cached.forward_pe !== undefined;
    
    // Consider valid only if we have market_cap AND at least one financial metric
    return hasMarketCap && hasFinancialMetrics;
  }, []);

  // Fetch Twelve Data metrics - called when bookmarks load or change
  const fetchTickerMetricsOnce = async (tickersToFetch) => {
    if (!tickersToFetch || tickersToFetch.length === 0) return;
    
    // Normalize tickers to uppercase for consistent cache checking
    const normalizedTickers = tickersToFetch.map(t => t?.toUpperCase()).filter(Boolean);
    
    // Filter out tickers we already have cached with COMPLETE data
    const uncachedTickers = normalizedTickers.filter(t => {
      if (isTickerCacheValid(t, tickerMetricsCache)) {
        return false; // Has complete data, skip
      }
      return true;
    });
    
    if (uncachedTickers.length === 0) {
      console.log('📊 All tickers already cached, skipping API call');
      return;
    }
    
    console.log(`📊 Fetching ${uncachedTickers.length} tickers (${normalizedTickers.length - uncachedTickers.length} already cached)`);
    
    try {
      
      console.log(`📊 Fetching ${uncachedTickers.length} uncached tickers (${normalizedTickers.length - uncachedTickers.length} already cached/pending)`);
      
      // Fetch in batches of 15 to respect API limits (reduced from 50)
      const batches = [];
      for (let i = 0; i < uncachedTickers.length; i += 15) {
        batches.push(uncachedTickers.slice(i, i + 15));
      }
      
      // Fetch all batches (with small delay between to avoid rate limits)
      const allResults = {};
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const response = await fetch(`${API_BASE_URL}/api/ticker-metrics/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tickers: batch })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.tickers) {
            Object.assign(allResults, data.tickers);
            console.log(`📊 Batch ${i+1}/${batches.length} returned ${Object.keys(data.tickers).length} tickers`);
          } else {
            console.warn(`📊 Batch ${i+1}/${batches.length} response missing 'tickers' field:`, data);
          }
        } else {
          console.error(`📊 Batch ${i+1}/${batches.length} failed with status ${response.status}:`, await response.text());
        }
        
        // Longer delay between batches to avoid rate limiting (500ms instead of 200ms)
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Merge with existing cache instead of replacing
      console.log('[fetchTickerMetricsOnce] API results received:', Object.keys(allResults).length, 'tickers');
      console.log('[fetchTickerMetricsOnce] All result keys:', Object.keys(allResults));
      if (Object.keys(allResults).length > 0) {
        console.log('[fetchTickerMetricsOnce] Updating tickerMetricsCache with keys:', Object.keys(allResults).slice(0, 10));
        setTickerMetricsCache(prev => {
          const newCache = { ...prev, ...allResults };
          console.log('[fetchTickerMetricsOnce] New cache size:', Object.keys(newCache).length);
          // Save to localStorage for persistence across page loads
          try {
            localStorage.setItem('tickerMetricsCache', JSON.stringify(newCache));
            localStorage.setItem('tickerMetricsCacheTimestamp', new Date().toISOString());
            console.log(`📊 Saved ${Object.keys(newCache).length} tickers to localStorage cache`);
          } catch (e) {
            console.warn('Failed to save ticker metrics cache to localStorage:', e);
          }
          return newCache;
        });
      } else {
        console.log('[fetchTickerMetricsOnce] No results received from API');
      }
    } catch (error) {
      console.error('Error fetching ticker metrics:', error);
    }
  };

  // Simple load more handler - memoized with useCallback for stable reference
  const loadMoreIdeas = useCallback(() => {
    console.log(`🔄 loadMoreIdeas called: hasMoreIdeas=${hasMoreIdeas}, loadingMore=${loadingMore}, loading=${loading}`);
    if (!loadingMore && hasMoreIdeas && !loading) {
      console.log(`✅ Loading more ideas...`);
      fetchData(true);
    } else {
      console.log(`⏸️ Skipping load: loadingMore=${loadingMore}, hasMoreIdeas=${hasMoreIdeas}, loading=${loading}`);
    }
  }, [hasMoreIdeas, loadingMore, loading]);

  // Fetch metrics ON-DEMAND when user expands a card (not on page load)
  const fetchMetricsForTicker = useCallback(async (ticker) => {
    if (!ticker) return;
    const tickerUpper = ticker.toUpperCase();
    
    // Skip if already have COMPLETE data cached (description AND market_cap AND new metrics fields)
    const cached = tickerMetricsCache[tickerUpper];
    if (cached?.description && cached?.market_cap && 
        (cached.gross_margin !== undefined || cached.operating_margin !== undefined || cached.shares_outstanding !== undefined)) {
      console.log(`📊 Full profile + metrics for ${tickerUpper} already cached`);
      return;
    }
    
    console.log(`📊 Fetching detailed profile + metrics on-demand for ${tickerUpper}`);
    
    try {
      // Fetch both profile and metrics in parallel
      const [profileResponse, metricsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ticker-profile/${tickerUpper}`),
        fetch(`${API_BASE_URL}/api/ticker-metrics/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tickers: [tickerUpper] })
        })
      ]);
      
      let profileData = {};
      let metricsData = {};
      
      if (profileResponse.ok) {
        const data = await profileResponse.json();
        if (data && !data.error) {
          profileData = data;
        }
      }
      
      if (metricsResponse.ok) {
        const data = await metricsResponse.json();
        if (data?.tickers?.[tickerUpper]) {
          metricsData = data.tickers[tickerUpper];
        }
      }
      
      // Merge both data sources
      if (Object.keys(profileData).length > 0 || Object.keys(metricsData).length > 0) {
        setTickerMetricsCache(prev => {
          const existing = prev[tickerUpper] || {};
          const newCache = { 
            ...prev, 
            [tickerUpper]: { 
              ...existing,
              ...metricsData,  // Metrics first (market_cap, forward_pe, etc.)
              ...profileData,  // Profile overwrites with description, CEO, etc.
              // Ensure we keep financial metrics from metricsData even if profileData doesn't have them
              market_cap: metricsData.market_cap || existing.market_cap || profileData.market_cap,
              enterprise_value: metricsData.enterprise_value || existing.enterprise_value || profileData.enterprise_value,
              forward_pe: metricsData.forward_pe || existing.forward_pe || profileData.forward_pe,
              trailing_pe: metricsData.trailing_pe || existing.trailing_pe || profileData.trailing_pe,
              peg_ratio: metricsData.peg_ratio ?? existing.peg_ratio ?? profileData.peg_ratio,
              ev_to_sales: metricsData.ev_to_sales || existing.ev_to_sales || profileData.ev_to_sales,
              ev_to_revenue: metricsData.ev_to_revenue ?? existing.ev_to_revenue ?? profileData.ev_to_revenue,
              ev_to_ebitda: metricsData.ev_to_ebitda ?? existing.ev_to_ebitda ?? profileData.ev_to_ebitda,
              price_to_book: metricsData.price_to_book || existing.price_to_book || profileData.price_to_book,
              price_to_sales: metricsData.price_to_sales ?? existing.price_to_sales ?? profileData.price_to_sales,
              // Financial margins
              gross_margin: metricsData.gross_margin ?? existing.gross_margin ?? profileData.gross_margin,
              operating_margin: metricsData.operating_margin ?? existing.operating_margin ?? profileData.operating_margin,
              profit_margin: metricsData.profit_margin ?? existing.profit_margin ?? profileData.profit_margin,
              revenue_growth: metricsData.revenue_growth ?? existing.revenue_growth ?? profileData.revenue_growth,
              // Stock statistics
              shares_outstanding: metricsData.shares_outstanding ?? existing.shares_outstanding ?? profileData.shares_outstanding,
              shares_float: metricsData.shares_float ?? existing.shares_float ?? profileData.shares_float,
              short_ratio: metricsData.short_ratio || existing.short_ratio || profileData.short_ratio,
              short_percent: metricsData.short_percent ?? existing.short_percent ?? profileData.short_percent,
              short_percent_float: metricsData.short_percent_float ?? existing.short_percent_float ?? profileData.short_percent_float,
              insider_percent: metricsData.insider_percent ?? existing.insider_percent ?? profileData.insider_percent,
              institution_percent: metricsData.institution_percent ?? existing.institution_percent ?? profileData.institution_percent,
              beta: metricsData.beta ?? existing.beta ?? profileData.beta,
              dividend_yield: metricsData.dividend_yield ?? existing.dividend_yield ?? profileData.dividend_yield,
            } 
          };
          // Save to localStorage
          try {
            localStorage.setItem('tickerMetricsCache', JSON.stringify(newCache));
            localStorage.setItem('tickerMetricsCacheTimestamp', new Date().toISOString());
          } catch (e) {
            console.warn('Failed to save to localStorage:', e);
          }
          return newCache;
        });
        console.log(`✅ Fetched and cached full profile + metrics for ${tickerUpper}`);
      }
    } catch (error) {
      console.error(`Error fetching profile/metrics for ${tickerUpper}:`, error);
    }
  }, [tickerMetricsCache]);

  // Fetch structured signal for an idea (on-demand or extract if not available)
  const fetchStructuredSignal = useCallback(async (ideaId, forceExtract = false) => {
    if (!ideaId) return null;
    
    // Check cache first
    if (!forceExtract && signalCache[ideaId]) {
      console.log(`📊 Signal for ${ideaId.substring(0, 15)} already cached`);
      return signalCache[ideaId];
    }
    
    setSignalLoading(prev => ({ ...prev, [ideaId]: true }));
    
    try {
      // First try to get existing signal
      const getResponse = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/signal`);
      
      if (getResponse.ok) {
        const data = await getResponse.json();
        
        if (data.has_signal && data.structured_signal) {
          // Cache and return existing signal
          setSignalCache(prev => ({ ...prev, [ideaId]: data.structured_signal }));
          setSignalLoading(prev => ({ ...prev, [ideaId]: false }));
          console.log(`✅ Retrieved cached signal for ${ideaId.substring(0, 15)}`);
          return data.structured_signal;
        }
      }
      
      // No signal exists - extract it
      console.log(`🔄 Extracting new signal for ${ideaId.substring(0, 15)}...`);
      const extractResponse = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/extract-signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (extractResponse.ok) {
        const extractData = await extractResponse.json();
        if (extractData.success && extractData.structured_signal) {
          setSignalCache(prev => ({ ...prev, [ideaId]: extractData.structured_signal }));
          setSignalLoading(prev => ({ ...prev, [ideaId]: false }));
          console.log(`✅ Extracted and cached signal for ${ideaId.substring(0, 15)}`);
          return extractData.structured_signal;
        }
      }
      
      setSignalLoading(prev => ({ ...prev, [ideaId]: false }));
      return null;
      
    } catch (error) {
      console.error(`Error fetching signal for ${ideaId}:`, error);
      setSignalLoading(prev => ({ ...prev, [ideaId]: false }));
      return null;
    }
  }, [signalCache]);

  // Toggle between summary and signal view for an idea
  const toggleIdeaViewMode = useCallback(async (ideaId) => {
    const currentMode = ideaViewMode[ideaId] || 'summary';
    const newMode = currentMode === 'summary' ? 'signal' : 'summary';
    
    // If switching to signal view, fetch the signal
    if (newMode === 'signal') {
      await fetchStructuredSignal(ideaId);
    }
    
    setIdeaViewMode(prev => ({ ...prev, [ideaId]: newMode }));
  }, [ideaViewMode, fetchStructuredSignal]);

  // Fetch WWNBT analysis for an idea (on-demand)
  const fetchWwnbtAnalysis = useCallback(async (ideaId, forceExtract = false) => {
    if (!ideaId) return null;
    
    // Check cache first (unless forcing extraction)
    if (!forceExtract && wwnbtCache[ideaId]) {
      console.log(`📊 WWNBT for ${ideaId.substring(0, 15)} already cached`);
      return wwnbtCache[ideaId];
    }
    
    setWwnbtLoading(prev => ({ ...prev, [ideaId]: true }));
    setWwnbtError(prev => ({ ...prev, [ideaId]: null }));
    
    try {
      // If not forcing, first try to get existing WWNBT from DB
      if (!forceExtract) {
        const getResponse = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/wwnbt`);
        
        if (getResponse.ok) {
          const data = await getResponse.json();
          
          if (data.has_wwnbt && data.wwnbt_analysis) {
            // Cache and return existing WWNBT
            setWwnbtCache(prev => ({ ...prev, [ideaId]: data.wwnbt_analysis }));
            setWwnbtLoading(prev => ({ ...prev, [ideaId]: false }));
            console.log(`✅ Retrieved cached WWNBT for ${ideaId.substring(0, 15)}`);
            return data.wwnbt_analysis;
          }
        }
      }
      
      // No WWNBT exists or forceExtract - extract it now
      console.log(`🔄 Extracting WWNBT for ${ideaId.substring(0, 15)}...`);
      const extractResponse = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/extract-wwnbt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (extractResponse.ok) {
        const extractData = await extractResponse.json();
        console.log(`📊 Extract response for ${ideaId.substring(0, 15)}:`, extractData);
        if (extractData.success && extractData.wwnbt_analysis) {
          setWwnbtCache(prev => ({ ...prev, [ideaId]: extractData.wwnbt_analysis }));
          setWwnbtLoading(prev => ({ ...prev, [ideaId]: false }));
          console.log(`✅ Extracted and cached WWNBT for ${ideaId.substring(0, 15)}`);
          return extractData.wwnbt_analysis;
        } else {
          console.error(`❌ WWNBT extraction failed for ${ideaId}:`, extractData);
          setWwnbtError(prev => ({ ...prev, [ideaId]: extractData.error || 'Grok analysis failed. Please retry.' }));
        }
      } else {
        const errText = await extractResponse.text();
        console.error(`❌ WWNBT extraction HTTP error for ${ideaId}:`, extractResponse.status, errText);
        let detail = '';
        try { detail = JSON.parse(errText).detail || ''; } catch (e) { /* not json */ }
        setWwnbtError(prev => ({ ...prev, [ideaId]: detail || `Analysis failed (HTTP ${extractResponse.status}). Please retry.` }));
      }
      
      setWwnbtLoading(prev => ({ ...prev, [ideaId]: false }));
      return null;
      
    } catch (error) {
      console.error(`Error fetching WWNBT for ${ideaId}:`, error);
      setWwnbtError(prev => ({ ...prev, [ideaId]: 'Network error during analysis. Please retry.' }));
      setWwnbtLoading(prev => ({ ...prev, [ideaId]: false }));
      return null;
    }
  }, [wwnbtCache]);

  // Fetch IR contacts from Perplexity API
  const fetchIrContacts = useCallback(async (ticker, forceRefresh = false) => {
    if (!ticker) return null;
    
    const tickerUpper = ticker.toUpperCase();
    
    // Check cache first (unless force refresh)
    if (!forceRefresh && irContactsCache[tickerUpper]) {
      console.log(`📞 IR contacts for ${tickerUpper} already cached`);
      return irContactsCache[tickerUpper];
    }
    
    setIrContactsLoading(prev => ({ ...prev, [tickerUpper]: true }));
    
    try {
      // Add refresh param to bypass backend cache too
      const url = forceRefresh 
        ? `${API_BASE_URL}/api/ticker-ir-contacts/${tickerUpper}?refresh=true`
        : `${API_BASE_URL}/api/ticker-ir-contacts/${tickerUpper}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        
        if (!data.error) {
          setIrContactsCache(prev => ({ ...prev, [tickerUpper]: data }));
          console.log(`✅ ${forceRefresh ? 'Refreshed' : 'Fetched'} IR contacts for ${tickerUpper}`);
        }
        
        setIrContactsLoading(prev => ({ ...prev, [tickerUpper]: false }));
        return data;
      }
      
      setIrContactsLoading(prev => ({ ...prev, [tickerUpper]: false }));
      return null;
      
    } catch (error) {
      console.error(`Error fetching IR contacts for ${ticker}:`, error);
      setIrContactsLoading(prev => ({ ...prev, [tickerUpper]: false }));
      return null;
    }
  }, [irContactsCache]);

  // WWNBT Action Agent - chat function
  const sendWwnbtAgentMessage = useCallback(async (ideaId, userMessage = '') => {
    try {
      setWwnbtAgentLoading(prev => ({ ...prev, [ideaId]: true }));
      
      const currentHistory = wwnbtAgentChat[ideaId] || [];
      
      const response = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/wwnbt-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: currentHistory
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.response) {
          // Update chat history
          const newHistory = [...currentHistory];
          if (userMessage) {
            newHistory.push({ role: 'user', content: userMessage });
          }
          newHistory.push({ role: 'assistant', content: data.response });
          
          setWwnbtAgentChat(prev => ({ ...prev, [ideaId]: newHistory }));
          setWwnbtAgentInput(prev => ({ ...prev, [ideaId]: '' }));
        }
      } else {
        console.error('WWNBT Agent error:', await response.text());
      }
    } catch (error) {
      console.error('Error calling WWNBT agent:', error);
    } finally {
      setWwnbtAgentLoading(prev => ({ ...prev, [ideaId]: false }));
    }
  }, [wwnbtAgentChat]);

  // Open IR Email Composer with pre-built template
  const openIrEmailComposer = useCallback((ticker, email, companyName, irContactName) => {
    const contactFirstName = irContactName && irContactName !== 'Not found' 
      ? irContactName.split(' ')[0] 
      : 'Investor Relations Team';
    
    const subject = `Request for Introductory IR Call - ${companyName} (${ticker})`;
    
    const body = `Dear ${contactFirstName},

I hope this message finds you well. I am an investor researching ${companyName} and would appreciate the opportunity to schedule a brief introductory call with your Investor Relations team.

I am particularly interested in:
• Understanding the company's strategic priorities and growth initiatives
• Gaining insight into the competitive landscape and market positioning
• Discussing the company's capital allocation philosophy

I am flexible on timing and happy to work around your schedule. A 20-30 minute call would be greatly appreciated.

Thank you for your time, and I look forward to hearing from you.

Best regards,
[Your Name]
[Your Title/Firm]
[Your Phone Number]`;

    setIrEmailComposer({
      ticker,
      email,
      companyName,
      irContactName: contactFirstName,
      subject,
      body
    });
  }, []);

  // WWNBT Action Agent - execute specific action
  const executeWwnbtAction = useCallback(async (ideaId, actionType) => {
    try {
      setWwnbtAgentLoading(prev => ({ ...prev, [ideaId]: true }));
      
      const response = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/wwnbt-agent/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: actionType })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Format the result as a chat message
        let resultMessage = '';
        if (result.success && result.data) {
          const actionLabels = {
            'sec_filings': '📄 SEC Filings',
            'financial_metrics': '📊 Financial Metrics',
            'insider_trading': '👤 Insider Activity',
            'news': '📰 Recent News',
            'competitors': '🏢 Competitor Analysis'
          };
          
          resultMessage = `**${actionLabels[actionType] || actionType} for ${result.ticker}:**\n\n`;
          
          if (actionType === 'sec_filings' && result.data.filings) {
            resultMessage += result.data.summary + '\n\n';
            result.data.filings.forEach(f => {
              resultMessage += `• **${f.type}** (${f.date})\n`;
            });
          } else if (actionType === 'financial_metrics') {
            const d = result.data;
            resultMessage += `• Market Cap: ${d.market_cap || 'N/A'}\n`;
            resultMessage += `• Forward P/E: ${d.forward_pe?.toFixed(1) || 'N/A'}\n`;
            resultMessage += `• Profit Margin: ${d.profit_margin || 'N/A'}\n`;
            resultMessage += `• Gross Margin: ${d.gross_margin || 'N/A'}\n`;
            resultMessage += `• EV/EBITDA: ${d.ev_to_ebitda?.toFixed(1) || 'N/A'}\n`;
            resultMessage += `• Dividend Yield: ${d.dividend_yield || 'N/A'}`;
          } else if (actionType === 'insider_trading') {
            const d = result.data;
            resultMessage += `• Insider Ownership: ${d.insider_ownership}\n`;
            resultMessage += `• Institutional Ownership: ${d.institutional_ownership}\n`;
            if (d.short_ratio) resultMessage += `• Short Ratio: ${d.short_ratio}\n`;
            if (d.note) resultMessage += `\n_${d.note}_`;
          } else if (actionType === 'news') {
            resultMessage += result.data.summary || 'No news available';
          } else if (actionType === 'competitors' && result.data.comparison) {
            resultMessage += '| Ticker | Name | MCap | P/E | Margin |\n';
            resultMessage += '|--------|------|------|-----|--------|\n';
            result.data.comparison.forEach(c => {
              resultMessage += `| ${c.ticker} | ${c.name?.substring(0, 15) || '-'} | ${c.market_cap || '-'} | ${c.pe || '-'} | ${c.margin || '-'} |\n`;
            });
          } else if (result.data.error) {
            resultMessage = `⚠️ ${result.data.error}`;
          }
        } else {
          resultMessage = `⚠️ ${result.data?.error || 'Action failed'}`;
        }
        
        // Add to chat history
        setWwnbtAgentChat(prev => ({
          ...prev,
          [ideaId]: [...(prev[ideaId] || []), { role: 'assistant', content: resultMessage, isActionResult: true }]
        }));
      }
    } catch (error) {
      console.error('Error executing WWNBT action:', error);
      setWwnbtAgentChat(prev => ({
        ...prev,
        [ideaId]: [...(prev[ideaId] || []), { role: 'assistant', content: `⚠️ Error: ${error.message}`, isActionResult: true }]
      }));
    } finally {
      setWwnbtAgentLoading(prev => ({ ...prev, [ideaId]: false }));
    }
  }, []);

  // Handler for expanding idea cards - fetches metrics on-demand
  const handleExpandIdeaCard = useCallback((bookmarkId, primaryTicker, bookmark) => {
    setExpandedIdeaCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookmarkId)) {
        newSet.delete(bookmarkId);
        // Sync toggle state - collapse means back to summary
        setFullPostModeCards(prevToggle => {
          const newToggle = new Set(prevToggle);
          newToggle.delete(bookmarkId);
          return newToggle;
        });
      } else {
        newSet.add(bookmarkId);
        // Sync toggle state - expand means full post
        setFullPostModeCards(prevToggle => {
          const newToggle = new Set(prevToggle);
          newToggle.add(bookmarkId);
          return newToggle;
        });
        // Fetch metrics when expanding (not collapsing)
        if (primaryTicker) {
          fetchMetricsForTicker(primaryTicker);
        }
        // REMOVED: Auto-fetch WWNBT on expand - now user must click Grok button
      }
      return newSet;
    });
  }, [fetchMetricsForTicker]);

  // Handler for expanding group idea cards - fetches metrics on-demand
  const handleExpandGroupIdeaCard = useCallback((ideaId, primaryTicker) => {
    setExpandedGroupIdeaCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
        // Fetch metrics when expanding (not collapsing)
        if (primaryTicker) {
          fetchMetricsForTicker(primaryTicker);
        }
      }
      return newSet;
    });
  }, [fetchMetricsForTicker]);

  // Infinite scroll detector - with proper dependency array to prevent re-renders
  useEffect(() => {
    const handleScroll = () => {
      // Check if user has scrolled near bottom (within 500px)
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.documentElement.scrollHeight - 500;
      
      if (scrollPosition >= bottomPosition) {
        loadMoreIdeas();
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreIdeas]);

  // DISABLED: No longer fetch metrics on page load - now fetch on-demand when user expands a card
  // This dramatically reduces API calls since most users don't expand every card
  /*
  const lastFetchedBookmarkCount = React.useRef(0);
  useEffect(() => {
    // Only fetch when bookmark count actually changes (new data loaded)
    if (bookmarks.length === 0 || bookmarks.length === lastFetchedBookmarkCount.current) return;
    lastFetchedBookmarkCount.current = bookmarks.length;
    
    // Get unique tickers - including from Ideas List cards
    const allTickers = new Set();
    bookmarks.forEach(b => {
      if (b.primary_ticker) allTickers.add(b.primary_ticker.toUpperCase());
      // Include tickers from Ideas List cards
      if (b.is_ticker_list || b.title === "Ideas List") {
        const tickersList = b.all_tickers || b.tickers || [];
        tickersList.forEach(t => {
          const tickerName = typeof t === 'string' ? t : t.ticker;
          if (tickerName) allTickers.add(tickerName.toUpperCase());
        });
      }
      // Also include any tickers array
      if (b.tickers && Array.isArray(b.tickers)) {
        b.tickers.forEach(t => {
          if (t) allTickers.add(t.toUpperCase());
        });
      }
    });
    
    if (allTickers.size > 0) {
      fetchTickerMetricsOnce(Array.from(allTickers));
    }
  }, [bookmarks]);
  */


  const deleteInvestmentIdea = async (ideaId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${ideaId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        // Remove the idea from local state
        setBookmarks(prevBookmarks => prevBookmarks.filter(bookmark => bookmark.id !== ideaId));
        console.log(`Successfully deleted investment idea: ${ideaId}`);
        
        // Refetch data from server to ensure consistency
        fetchData();
      } else {
        console.error('Failed to delete investment idea:', await response.text());
      }
    } catch (error) {
      console.error('Error deleting investment idea:', error);
    }
  };


  // Generate AI Reaction for an investment idea
  const handleGenerateAIReaction = async (ideaId) => {
    try {
      // Track AI reaction modal click
      trackEngagement('modal_click', 'modal', { button_name: 'ai_reaction' });
      
      setAIReactionLoading(true);
      setShowAIReactionModal(true);
      setAIReaction(null);
      
      const response = await fetch(`${API_BASE_URL}/api/ideas/${ideaId}/generate-reaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAIReaction(data.reaction);
        } else {
          alert('Failed to generate AI reaction');
          setShowAIReactionModal(false);
        }
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.detail || 'Failed to generate AI reaction'}`);
        setShowAIReactionModal(false);
      }
    } catch (error) {
      console.error('Error generating AI reaction:', error);
      alert('Error generating AI reaction. Please try again.');
      setShowAIReactionModal(false);
    } finally {
      setAIReactionLoading(false);
    }
  };


  // Fetch pending shared ideas for current user
  const fetchPendingIdeas = async () => {
    try {
      const userEmail = user?.email || 'sample_user@example.com'; // Default for testing
      const response = await fetch(`${API_BASE_URL}/api/shared-ideas/${userEmail}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[fetchPendingIdeas] Pending ideas response:', data);
        const newPendingIdeas = data.pending_ideas || [];
        
        // Check if there are NEW ideas (IDs not in seenPendingIds)
        const unseenIdeas = newPendingIdeas.filter(idea => !seenPendingIds.includes(idea.id));
        if (unseenIdeas.length > 0) {
          setHasNewSharedIdeas(true);
        }
        
        setPendingIdeas(newPendingIdeas);
      } else {
        console.error('[fetchPendingIdeas] Pending ideas API failed:', response.status);
        // Don't clear existing pending ideas on error
      }
    } catch (error) {
      console.error('[fetchPendingIdeas] Error fetching pending ideas:', error);
      // Don't clear existing pending ideas on error
    }
  };
  
  // Fetch shared polls for this user
  const fetchSharedPolls = async () => {
    try {
      const userEmail = user?.email;
      if (!userEmail) return;
      
      const response = await fetch(`${API_BASE_URL}/api/shared-polls/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        const newPolls = data.shared_polls || [];
        
        // Check if there are new shared polls since last check
        const lastChecked = localStorage.getItem('tickler_polls_last_checked');
        if (lastChecked && newPolls.length > 0) {
          const hasNewItems = newPolls.some(poll => new Date(poll.shared_at || poll.created_at) > new Date(lastChecked));
          if (hasNewItems) {
            setHasNewPolls(true);
          }
        } else if (!lastChecked) {
          // First time checking - initialize lastChecked to now, don't show blue dot for existing items
          localStorage.setItem('tickler_polls_last_checked', new Date().toISOString());
        }
        
        setSharedPolls(newPolls);
      }
    } catch (error) {
      console.error('[fetchSharedPolls] Error fetching shared polls:', error);
    }
  };
  
  // Fetch shared watchlists for this user
  const fetchSharedWatchlists = async () => {
    try {
      const userEmail = user?.email;
      if (!userEmail) return;
      
      const response = await fetch(`${API_BASE_URL}/api/shared-watchlists/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        const newWatchlists = data.shared_watchlists || [];
        
        // Check if there are new shared watchlists since last check
        const lastChecked = localStorage.getItem('tickler_watchlists_last_checked');
        if (lastChecked && newWatchlists.length > 0) {
          const hasNewItems = newWatchlists.some(wl => new Date(wl.shared_at || wl.created_at) > new Date(lastChecked));
          if (hasNewItems) {
            setHasNewWatchlists(true);
          }
        } else if (!lastChecked) {
          // First time checking - initialize lastChecked to now, don't show blue dot for existing items
          localStorage.setItem('tickler_watchlists_last_checked', new Date().toISOString());
        }
        
        setSharedWatchlists(newWatchlists);
      }
    } catch (error) {
      console.error('[fetchSharedWatchlists] Error fetching shared watchlists:', error);
    }
  };
  
  // Poll for new shared ideas every 30 seconds
  useEffect(() => {
    if (!user?.email) return;
    
    // Initial fetch
    fetchPendingIdeas();
    fetchSharedPolls();
    fetchSharedWatchlists();
    
    // Set up polling interval
    const pollInterval = setInterval(() => {
      fetchPendingIdeas();
      fetchSharedPolls();
      fetchSharedWatchlists();
    }, 30000); // 30 seconds
    
    return () => clearInterval(pollInterval);
  }, [user?.email, seenPendingIds]);

  const openWatchlistModal = async (tickers, title) => {
    setWatchlistModalTitle(title);
    setWatchlistModalTickers(tickers);
    setWatchlistModalOpen(true);
    
    // Fetch ideas for these tickers
    try {
      const tickerFilter = tickers.join(',');
      const response = await fetch(`${API_BASE_URL}/api/dashboard-ideas/${user.id}?tickers=${tickerFilter}&page_size=50`);
      if (response.ok) {
        const data = await response.json();
        setWatchlistModalIdeas(data.ideas || []);
      }
    } catch (error) {
      console.error('Error fetching watchlist ideas:', error);
    }
  };

  const removeFromWatchlist = async (watchlistId, ticker) => {
    try {
      // Find the collection
      const watchlist = watchlists.find(w => w.id === watchlistId);
      if (!watchlist) {
        showNotification('Ticker list not found', 'error');
        return;
      }

      // Remove ticker from array
      const updatedTickers = watchlist.tickers.filter(t => t !== ticker);

      // Update backend
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${watchlistId}/tickers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tickers: updatedTickers,
          user_id: user?.id || user?.user_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update watchlist');
      }

      // Update local state
      setWatchlists(prev => 
        prev.map(w => w.id === watchlistId ? { ...w, tickers: updatedTickers } : w)
      );

      showNotification(`Removed ${ticker} from ticker list`, 'success');
    } catch (error) {
      console.error('Error removing ticker:', error);
      showNotification('Failed to remove ticker', 'error');
    }
  };

  const deleteWatchlist = async (watchlistId) => {
    try {
      const watchlist = watchlists.find(w => w.id === watchlistId);
      if (!watchlist) {
        showNotification('Ticker list not found', 'error');
        return;
      }

      // Confirm deletion with extra context for auto-generated collections
      const confirmMessage = watchlist.auto_generated 
        ? `Delete auto-generated ticker list "${watchlist.name}"? This will remove all tickers. This cannot be undone.`
        : `Delete ticker list "${watchlist.name}"? This cannot be undone.`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Get user ID - match the user_id stored in the watchlist
      // Use watchlist.user_id if available, otherwise try user object
      const userId = watchlist.user_id || user?.id || user?.user_id || user?.email;
      
      console.log('Delete watchlist:', { watchlistId, userId, watchlistUserId: watchlist.user_id });
      
      if (!userId) {
        showNotification('User not found. Please refresh the page.', 'error');
        return;
      }

      // Delete from backend
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${watchlistId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete failed:', errorData);
        throw new Error(errorData.detail || 'Failed to delete ticker list');
      }

      // Update local state
      setWatchlists(prev => prev.filter(w => w.id !== watchlistId));

      showNotification(`Deleted ticker list "${watchlist.name}"`, 'success');
    } catch (error) {
      console.error('Error deleting ticker list:', error);
      showNotification(error.message || 'Failed to delete ticker list', 'error');
    }
  };

  const deletePoll = async (pollId, pollTitle) => {
    const doDelete = async () => {
      try {
        const userId = user?.id || user?.user_id || user?.email;
        
        // Delete from backend
        const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${pollId}?user_id=${encodeURIComponent(userId)}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Delete poll failed:', errorData);
          throw new Error(errorData.detail || 'Failed to delete poll');
        }

        // Update local state
        setFishingThreads(prev => prev.filter(p => p.id !== pollId));

        // Clear selected poll if it was deleted
        if (selectedPoll?.id === pollId) {
          setSelectedPoll(null);
          setViewAllPollsMode(true);
        }

        showNotification(`Deleted poll "${pollTitle}"`, 'success');
      } catch (error) {
        console.error('Error deleting poll:', error);
        showNotification(error.message || 'Failed to delete poll', 'error');
      }
    };

    // Show custom confirm modal
    setConfirmModal({
      isOpen: true,
      title: 'Delete Poll',
      message: `Are you sure you want to delete "${pollTitle}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: doDelete
    });
  };

  // Auto add to watchlist (original behavior)
  const autoAddToWatchlist = async (ticker, industryInfo = null) => {
    try {
      // Get industry information for the ticker if not provided
      let tickerIndustry = industryInfo;
      if (!tickerIndustry) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/ticker-industry/${ticker}`);
          if (response.ok) {
            tickerIndustry = await response.json();
          }
        } catch (error) {
          console.log('Could not fetch industry info, using default watchlist');
        }
      }
      
      // Determine watchlist name based on industry or default
      const watchlistName = tickerIndustry?.sector ? 
        `${tickerIndustry.sector}` : 
        "General";
      
      let existingWatchlist = watchlists.find(w => w.name === watchlistName);
      
      if (existingWatchlist) {
        // Check if ticker already exists in the watchlist
        if (existingWatchlist.tickers.includes(ticker)) {
          showNotification(`${ticker} is already in your "${watchlistName}" watchlist!`, 'info');
          return;
        }
        
        // Add ticker to existing watchlist
        const updatedWatchlist = {
          ...existingWatchlist,
          tickers: [...existingWatchlist.tickers, ticker]
        };
        
        // Update local state
        console.log('📝 Updating watchlist locally:', updatedWatchlist);
        setWatchlists(prev => 
          prev.map(w => w.name === watchlistName ? updatedWatchlist : w)
        );
        
        // Save to backend
        try {
          const response = await fetch(`${API_BASE_URL}/api/watchlists/${existingWatchlist.id}/tickers`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              tickers: updatedWatchlist.tickers,
              user_id: user?.id || user?.user_id
            })
          });
          
          if (!response.ok) {
            throw new Error('Failed to update watchlist');
          }
          
          showNotification(`Added ${ticker} to "${watchlistName}" watchlist!`, 'success');
        } catch (error) {
          console.error('Error saving watchlist:', error);
          showNotification(`Added ${ticker} locally, but failed to save to server`, 'warning');
        }
        
      } else {
        // Create new industry-specific watchlist with this ticker
        const newWatchlist = {
          name: watchlistName,
          description: tickerIndustry?.sector ? 
            `Industry watchlist` : 
            "Custom watchlist",
          tickers: [ticker],
          industry: tickerIndustry?.sector || null,
          user_id: user?.user_id || 'sample_user',
          created_at: new Date().toISOString()
        };
        
        // Create in backend first
        try {
          const response = await fetch(`${API_BASE_URL}/api/watchlists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: newWatchlist.name,
              description: newWatchlist.description,
              user_id: user?.id || user?.user_id
            })
          });
          
          if (!response.ok) {
            throw new Error('Failed to create watchlist');
          }
          
          const result = await response.json();
          newWatchlist.id = result.watchlist_id;
          
          // Add to local state
          console.log('📝 Adding new watchlist locally:', newWatchlist);
          setWatchlists(prev => [...prev, newWatchlist]);
          
          // Now add the ticker to it
          await fetch(`${API_BASE_URL}/api/watchlists/${newWatchlist.id}/tickers`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              tickers: newWatchlist.tickers,
              user_id: user?.id || user?.user_id
            })
          });
          
          showNotification(`Created "${watchlistName}" ticker list and added ${ticker}!`, 'success');
        } catch (error) {
          console.error('Error creating ticker list:', error);
          showNotification('Failed to create ticker list', 'error');
        }
      }
      
    } catch (error) {
      console.error('Error adding to ticker list:', error);
      showNotification('Failed to add ticker to list', 'error');
    }
  };

  // Manual add to specific watchlist
  const manualAddToWatchlist = async (ticker, watchlistId) => {
    try {
      const existingWatchlist = watchlists.find(w => w.id === watchlistId);
      if (!existingWatchlist) {
        showNotification('Watchlist not found', 'error');
        return;
      }
      
      // Check if ticker already exists
      if (existingWatchlist.tickers?.includes(ticker)) {
        showNotification(`${ticker} is already in "${existingWatchlist.name}"!`, 'info');
        return;
      }
      
      const updatedTickers = [...(existingWatchlist.tickers || []), ticker];
      
      // Update local state
      setWatchlists(prev => 
        prev.map(w => w.id === watchlistId ? { ...w, tickers: updatedTickers } : w)
      );
      
      // Save to backend
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${watchlistId}/tickers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tickers: updatedTickers,
          user_id: user?.id || user?.user_id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update watchlist');
      }
      
      showNotification(`Added ${ticker} to "${existingWatchlist.name}"!`, 'success');
      setShowManualWatchlistSelect(false);
      setPendingWatchlistTicker(null);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      showNotification('Failed to add ticker to watchlist', 'error');
    }
  };

  // Show popup to choose Auto or Manual (or use saved preference)
  const addToWatchlist = async (ticker, industryInfo = null) => {
    setPendingWatchlistTicker(ticker);
    setPendingWatchlistIndustry(industryInfo);
    
    // Check if user has a saved preference
    if (rememberCollectionChoice && savedCollectionChoice) {
      if (savedCollectionChoice === 'auto') {
        // Auto add to sector-based collection
        autoAddToWatchlist(ticker, industryInfo);
        setPendingWatchlistTicker(null);
        setPendingWatchlistIndustry(null);
      } else if (savedCollectionChoice === 'manual') {
        // Open the Create Custom Collection modal
        setCollectionModalMode('create');
        setShowCreateWatchlist(true);
      }
    } else {
      // Show the choice popup
      setShowWatchlistTypePopup(true);
    }
  };

  // Get SEC filings URL - Updated to working 2025 format
  const getSECFilingsUrl = async (ticker) => {
    // Use the main SEC EDGAR filing search - this will work in browser
    return `https://www.sec.gov/search-filings?query=${ticker}&category=filing&startdt=2020-01-01&enddt=2025-12-31`;
  };

  // IR FUNCTIONALITY TEMPORARILY DISABLED (SAVED IN ir_functionality_backup.js)
  // Uncomment these functions to restore IR button functionality
  
  /*
  // Get IR URL for a ticker (IR pages only, not SEC)
  const getIRUrl = async (ticker) => {
    if (!ticker) return null;
    
    try {
      // Use the backend IR discovery API
      const response = await fetch(`${API_BASE_URL}/api/ir-url/${ticker}`);
      
      if (response.ok) {
        const data = await response.json();
        return data.ir_url;
      } else {
        // Fallback to static patterns if API fails
        return generateStaticIRUrl(ticker);
      }
    } catch (error) {
      console.error('Error fetching IR URL:', error);
      // Fallback to static patterns
      return generateStaticIRUrl(ticker);
    }
  };
  */

  /*
  // Static IR URL generation (fallback and for known companies) - COMMENTED OUT
  const generateStaticIRUrl = (ticker) => {
    if (!ticker) return null;
    
    // Keep our existing comprehensive mapping for instant results
    const staticMapping = {
      // Mega Cap Tech (FAANG+)
      'AAPL': 'https://investor.apple.com',
      'MSFT': 'https://www.microsoft.com/en-us/Investor',
      'GOOGL': 'https://abc.xyz/investor',
      'GOOG': 'https://abc.xyz/investor',
      'AMZN': 'https://ir.aboutamazon.com',
      'META': 'https://investor.fb.com',
      'NVDA': 'https://investor.nvidia.com',
      'TSLA': 'https://ir.tesla.com',
      'NFLX': 'https://ir.netflix.net',
      
      // Other Major Tech
      'CRM': 'https://investor.salesforce.com',
      'ORCL': 'https://investor.oracle.com',
      'AMD': 'https://ir.amd.com',
      'INTC': 'https://www.intc.com/investor-relations',
      'PYPL': 'https://investor.pypl.com',
      'ADBE': 'https://www.adobe.com/investor-relations.html',
      'AVGO': 'https://investors.broadcom.com',
      'TXN': 'https://investor.ti.com',
      'QCOM': 'https://investor.qualcomm.com',
      'MU': 'https://investors.micron.com',
      
      // Financial Services  
      'JPM': 'https://www.jpmorganchase.com/ir',
      'BAC': 'https://investor.bankofamerica.com',
      'WFC': 'https://www.wellsfargo.com/about/investor-relations',
      'GS': 'https://www.goldmansachs.com/investor-relations',
      'MS': 'https://www.morganstanley.com/about-us-ir',
      'C': 'https://www.citigroup.com/citi/investor',
      'BLK': 'https://ir.blackrock.com',
      'AXP': 'https://ir.americanexpress.com',
      
      // Healthcare & Biotech
      'JNJ': 'https://www.investor.jnj.com',
      'UNH': 'https://www.unitedhealthgroup.com/investors.html',
      'PFE': 'https://investors.pfizer.com',
      'ABT': 'https://www.abbott.com/investors.html',
      'TMO': 'https://ir.thermofisher.com',
      'ABBV': 'https://investors.abbvie.com',
      'MRK': 'https://investors.merck.com',
      'LLY': 'https://investor.lilly.com',
      'GILD': 'https://investors.gilead.com',
      'AMGN': 'https://investors.amgen.com',
      'MRNA': 'https://investors.modernatx.com',
      
      // Consumer & Retail
      'WMT': 'https://stock.walmart.com',
      'HD': 'https://ir.homedepot.com',
      'PG': 'https://www.pginvestor.com',
      'KO': 'https://investors.coca-colacompany.com',
      'PEP': 'https://www.pepsico.com/investors',
      'COST': 'https://investor.costco.com',
      'NKE': 'https://investors.nike.com',
      'SBUX': 'https://investor.starbucks.com',
      'MCD': 'https://corporate.mcdonalds.com/corpmcd/investors.html',
      
      // Energy
      'XOM': 'https://corporate.exxonmobil.com/Investors',
      'CVX': 'https://www.chevron.com/investors',
      'COP': 'https://www.conocophillips.com/investors',
      'EOG': 'https://investors.eogresources.com',
    };
    
    const upperTicker = ticker.toUpperCase();
    
    // Check static mapping first for instant results
    if (staticMapping[upperTicker]) {
      return staticMapping[upperTicker];
    }
    
    // Fallback to most common pattern
    return `https://investor.${ticker.toLowerCase()}.com`;
  };
  */

  /*
  // Initialize IR discovery for user's tickers (called once after login) - COMMENTED OUT
  const initializeIRDiscovery = async () => {
    if (!user) return;
    
    try {
      await fetch(`${API_BASE_URL}/api/bookmarks/${user}/discover-ir`, {
        method: 'POST'
      });
      console.log('IR URL discovery initiated for user tickers');
    } catch (error) {
      console.error('Failed to initialize IR discovery:', error);
    }
  };

  // Trigger IR discovery when user data loads - COMMENTED OUT
  React.useEffect(() => {
    if (user && bookmarks.length > 0) {
      // Delay to avoid blocking main UI
      setTimeout(initializeIRDiscovery, 2000);
    }
  }, [user, bookmarks]);
  */

  // Check SEC availability for ticker
  const checkSECAvailability = async (ticker) => {
    console.log('📊 checkSECAvailability called with ticker:', ticker);
    
    if (!ticker) {
      console.log('📊 No ticker provided, setting unavailable');
      setSecAvailability({ loading: false, hasFilings: false, hasForeignFilings: false, message: 'No ticker provided' });
      return;
    }
    
    try {
      setSecAvailability({ loading: true, hasFilings: false, hasForeignFilings: false, message: 'Checking...' });
      
      const response = await fetch(`${API_BASE_URL}/api/check-sec-availability/${ticker}`);
      const data = await response.json();
      
      console.log('📊 SEC Check API Response for', ticker, ':', data);
      
      // API returns has_filings (US) and has_foreign_filings (20-F/6-K)
      const isUsCompany = data.has_filings === true;
      const isForeignFiler = data.has_foreign_filings === true && !isUsCompany;
      
      console.log('📊 SEC Check Computed:', { ticker, isUsCompany, isForeignFiler });
      
      setSecAvailability({
        loading: false,
        hasFilings: isUsCompany,
        hasForeignFilings: isForeignFiler,
        message: data.message
      });
      
      // Set filing preferences based on company type
      if (isUsCompany) {
        // US company - enable US filings, disable foreign
        setSecFilingPreferences(prev => ({
          ...prev,
          include10K: true,
          include10Q: true,
          include8K: true,
          include20F: false,
          include6K: false,
          includeProxy: false,
          includeStatements: false
        }));
      } else if (isForeignFiler) {
        // Foreign company with SEC filings - enable foreign, disable US
        setSecFilingPreferences(prev => ({
          ...prev,
          include10K: false,
          include10Q: false,
          include8K: false,
          include20F: true,
          include6K: true,
          includeProxy: false,
          includeStatements: false
        }));
      } else {
        // No SEC filings - disable all
        setSecFilingPreferences(prev => ({
          ...prev,
          include10K: false,
          include10Q: false,
          include8K: false,
          include20F: false,
          include6K: false,
          includeProxy: false,
          includeStatements: false
        }));
      }
      
    } catch (error) {
      console.error('Error checking SEC availability:', error);
      setSecAvailability({ 
        loading: false, 
        hasFilings: false, 
        hasForeignFilings: false,
        message: 'Unable to check SEC filing availability' 
      });
    }
  };

  // Validate tweet existence (X Content Compliance)
  // Called in background when user opens original content modal
  const validateTweetContent = async (globalTweetId, twitterId) => {
    if (!globalTweetId || !twitterId) {
      console.log('Tweet validation skipped - missing IDs');
      return;
    }
    
    try {
      setTweetValidationStatus(prev => ({ ...prev, validating: true, error: null }));
      
      const response = await fetch(`${API_BASE_URL}/api/tweets/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          global_tweet_id: globalTweetId,
          twitter_id: twitterId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Validation failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      setTweetValidationStatus({
        validating: false,
        is_deleted: result.is_deleted || false,
        is_protected: result.is_protected || false,
        error: null,
        last_validated: result.last_validated
      });
      
      // If tweet was deleted, update the selectedOriginalContent to reflect this
      if (result.is_deleted) {
        setSelectedOriginalContent(prev => prev ? {
          ...prev,
          contentDeleted: true,
          deletionMessage: result.message || 'This post has been removed from X'
        } : null);
      }
      
      console.log('Tweet validation result:', result);
      
    } catch (error) {
      console.error('Tweet validation error:', error);
      setTweetValidationStatus(prev => ({
        ...prev,
        validating: false,
        error: error.message
      }));
    }
  };

  // SEC Filings integration
  const getSECFilings = async (ticker) => {
    if (!ticker) return null;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sec-filings/${ticker}`);
      
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to fetch SEC filings:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching SEC filings:', error);
      return null;
    }
  };

  // Enhanced SEC filings modal - navigate to SEC company page
  const showSECFilings = async (ticker) => {
    const filings = await getSECFilings(ticker);
    
    if (!filings) {
      // Check if this might be an international company
      const isLikelyInternational = await checkIfInternationalTicker(ticker);
      if (isLikelyInternational) {
        alert(`${ticker} appears to be an international company.\n\nInternational companies may not file with the US SEC. Instead, try:\n• Company's Investor Relations page (green IR button)\n• Local securities regulator filings\n• Company's annual/interim reports on their website`);
      } else {
        alert(`SEC filings for ${ticker} could not be retrieved. The company may not be a US public company or may not be required to file with the SEC.`);
      }
      return;
    }
    
    if (filings.status === 'fetching') {
      alert(`SEC filings for ${ticker} are being retrieved. Please try again in a few moments.`);
      return;
    }
    
    // Build comprehensive filings modal
    const availableFilings = filings.filings || {};
    const filingCount = Object.keys(availableFilings).length;
    
    if (filingCount === 0) {
      alert(`No recent SEC filings found for ${ticker}.\n\nThis may indicate:\n• Company is not a US public company\n• Company is dormant or recently delisted\n• Filings are under a different ticker symbol\n\nTry the IR button for company's official investor page.`);
      return;
    }
    
    // Create modal content showing recent filings
    let modalContent = `SEC Filings for ${ticker}${filings.company_name ? ` (${filings.company_name})` : ''}\n\n`;
    
    if (filings.is_foreign) {
      modalContent += `🌍 Foreign Company - Uses international filing forms\n\n`;
    }
    
    modalContent += `📄 Recent Filings Available (${filingCount} documents):\n\n`;
    
    // Define filing descriptions
    const filingDescriptions = {
      '10-K': '📋 Annual Report',
      '10-Q': '📊 Quarterly Report', 
      '8-K': '📢 Current Report',
      '10-K/A': '📋 Amended Annual Report',
      '10-Q/A': '📊 Amended Quarterly Report',
      'DEF 14A': '🗳️ Proxy Statement',
      '20-F': '🌍 Foreign Annual Report',
      '6-K': '🌍 Foreign Interim Report',
      'S-1': '🚀 Registration Statement',
      '424B4': '📈 Prospectus'
    };
    
    // Show recent filings (up to 5 for preview)
    const filingOrder = ['10-K', '10-Q', '8-K', '20-F', '6-K', 'DEF 14A', '10-K/A', '10-Q/A', 'S-1', '424B4'];
    let shown = 0;
    
    for (const filingType of filingOrder) {
      if (availableFilings[filingType] && shown < 5) {
        const filing = availableFilings[filingType];
        const description = filingDescriptions[filingType] || `📄 ${filingType}`;
        modalContent += `${description} (${filing.date})\n`;
        shown++;
      }
    }
    
    if (filingCount > 5) {
      modalContent += `...and ${filingCount - 5} more documents\n`;
    }
    
    modalContent += `\n📋 Click OK to view all filings on SEC.gov where you can choose which documents to open.`;
    
    if (confirm(modalContent)) {
      // Open SEC's main company filings page (much better UX!)
      const cik = filings.cik;
      const secCompanyUrl = `https://www.sec.gov/edgar/browse/?CIK=${cik}`;
      window.open(secCompanyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if ticker is likely international (heuristic)
  const checkIfInternationalTicker = async (ticker) => {
    // Simple heuristics for international companies
    const internationalIndicators = [
      ticker.includes('.'), // ADRs often have dots (e.g., BABA.L)
      ticker.length > 4,    // Many international tickers are longer
      ticker.endsWith('Y'),  // Many ADRs end in Y
      ticker.includes('-'),  // Some international formats use dashes
    ];
    
    // Additional check: see if ticker has common international patterns
    const internationalPatterns = [
      /^[A-Z]{1,3}\d+$/,    // Numbers in ticker (common internationally)
      /^[A-Z]+[.]L$/,       // London Stock Exchange format
      /^[A-Z]+[.]TO$/,      // Toronto Stock Exchange format
      /^[A-Z]+[.]HK$/,      // Hong Kong Stock Exchange format
    ];
    
    const hasInternationalPattern = internationalPatterns.some(pattern => 
      pattern.test(ticker.toUpperCase())
    );
    
    return internationalIndicators.some(indicator => indicator) || hasInternationalPattern;
  };

  // Manual watchlist creation function
  const createManualWatchlist = async () => {
    if (!newWatchlistName.trim() || !newWatchlistTickers.trim()) {
      alert('Please enter both watchlist name and tickers');
      return;
    }

    setCreatingWatchlist(true);
    
    try {
      // Parse tickers from comma-separated input
      const tickersArray = newWatchlistTickers
        .split(',')
        .map(ticker => ticker.trim().toUpperCase().replace('$', ''))
        .filter(ticker => ticker.length > 0);

      const watchlistData = {
        name: newWatchlistName.trim(),
        tickers: tickersArray,
        description: `Manual watchlist with ${tickersArray.length} tickers`,
        user_id: user?.user_id || 'sample_user',
        auto_generated: false,
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${API_BASE_URL}/api/watchlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(watchlistData),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Add the watchlist returned from backend to local state
        if (result.watchlist) {
          setWatchlists(prev => [...prev, result.watchlist]);
        }
        
        // Reset form
        setNewWatchlistName('');
        setNewWatchlistTickers('');
        setShowCreateWatchlist(false);
        
        // Trigger blue dot notification for collections
        setHasNewWatchlists(true);
        
        showNotification(`Ticker list "${watchlistData.name}" created with ${tickersArray.length} tickers!`, 'success');
      } else {
        throw new Error('Failed to create ticker list');
      }
    } catch (error) {
      console.error('Error creating ticker list:', error);
      showNotification('Failed to create ticker list. Please try again.', 'error');
    } finally {
      setCreatingWatchlist(false);
    }
  };

  // Create collection with selected tickers (from modal)
  const handleCreateCollectionFromModal = async () => {
    if (!newWatchlistName.trim()) {
      showNotification('Please enter a ticker list name', 'error');
      return;
    }

    setCreatingWatchlist(true);
    
    try {
      // Get tickers from selected tickers array
      const tickersArray = createCollectionSelectedTickers.map(t => t.ticker);

      const watchlistData = {
        name: newWatchlistName.trim(),
        tickers: tickersArray,
        description: `Custom ticker list with ${tickersArray.length} tickers`,
        user_id: user?.user_id || 'sample_user',
        auto_generated: false,
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${API_BASE_URL}/api/watchlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(watchlistData),
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.watchlist) {
          setWatchlists(prev => [...prev, result.watchlist]);
        }
        
        // Reset all form states
        setNewWatchlistName('');
        setCreateCollectionSelectedTickers([]);
        setCreateCollectionTickerSearch('');
        setCreateCollectionSearchResults([]);
        setShowCreateWatchlist(false);
        setPendingWatchlistTicker(null);
        setPendingWatchlistIndustry(null);
        
        setHasNewWatchlists(true);
        showNotification(`Ticker list "${watchlistData.name}" created with ${tickersArray.length} tickers!`, 'success');
      } else {
        throw new Error('Failed to create ticker list');
      }
    } catch (error) {
      console.error('Error creating ticker list:', error);
      showNotification('Failed to create ticker list. Please try again.', 'error');
    } finally {
      setCreatingWatchlist(false);
    }
  };

  // Search tickers for create collection modal
  const searchTickersForCollection = async (query) => {
    if (!query || query.length < 1) {
      setCreateCollectionSearchResults([]);
      return;
    }

    setCreateCollectionSearching(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/search?q=${encodeURIComponent(query)}&user_id=${user?.user_id || 'sample_user'}`);
      if (response.ok) {
        const data = await response.json();
        setCreateCollectionSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Error searching tickers:', error);
    } finally {
      setCreateCollectionSearching(false);
    }
  };

  // Debounced ticker search for collection modal
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (createCollectionTickerSearch) {
        searchTickersForCollection(createCollectionTickerSearch);
      } else {
        setCreateCollectionSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [createCollectionTickerSearch]);

  // Pre-populate selected ticker when opening Create Collection modal from idea card
  useEffect(() => {
    if (showCreateWatchlist && pendingWatchlistTicker && createCollectionSelectedTickers.length === 0) {
      setCreateCollectionSelectedTickers([{
        ticker: pendingWatchlistTicker,
        company_name: pendingWatchlistTicker
      }]);
    }
  }, [showCreateWatchlist, pendingWatchlistTicker]);

  // Add ticker to selection in create collection modal
  const addTickerToCollectionSelection = (idea) => {
    if (!createCollectionSelectedTickers.find(t => t.ticker === idea.ticker)) {
      setCreateCollectionSelectedTickers(prev => [...prev, {
        ticker: idea.ticker,
        company_name: idea.company_name || idea.ticker
      }]);
    }
  };

  // Remove ticker from selection in create collection modal
  const removeTickerFromCollectionSelection = (ticker) => {
    setCreateCollectionSelectedTickers(prev => prev.filter(t => t.ticker !== ticker));
  };



  const fetchSyncStatus = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sync-status/${user.id}`);
      if (response.ok) {
        const status = await response.json();
        setLastSyncStatus(status);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchXAuthStatus = async () => {
    if (!user?.email) return;
    
    setIsCheckingAuth(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/twitter/account-status?email=${encodeURIComponent(user.email)}`);
      if (response.ok) {
        const status = await response.json();
        // Transform to match expected format
        setXAuthStatus({
          authenticated: status.linked,
          oauth_type: status.oauth_version === '2.0' ? 'oauth2' : 'oauth1',
          needs_oauth2: status.needs_oauth2 || false,
          twitter_user: status.twitter_user
        });
      }
    } catch (error) {
      console.error('Error fetching X auth status:', error);
      setXAuthStatus({ authenticated: false, message: 'Error checking authentication' });
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const authenticateWithX = async () => {
    setIsAuthenticating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/twitter/login?user_id=${user}`);
      if (response.ok) {
        const data = await response.json();
        // Redirect to X authorization page
        window.location.href = data.auth_url;
      } else {
        alert('Failed to start X authentication. Please try again.');
      }
    } catch (error) {
      console.error('Error starting X authentication:', error);
      alert('Error connecting to X. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleBookmarkSync = async () => {
    if (isBookmarkSyncing) return;

    setIsBookmarkSyncing(true);
    setSyncResults(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/sync-bookmarks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user,
          max_results: 50  // Import last 50 bookmarks
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSyncResults(result);
        
        // Check if monthly limit was exceeded
        if (result.limit_exceeded) {
          showNotification(
            'Monthly limit reached (1,000 posts). Contact us for Enterprise with unlimited access.',
            'warning'
          );
          return;
        }
        
        if (result.success) {
          fetchData(); // Refresh dashboard data
          fetchSyncStatus(); // Refresh sync status
          
          // Show success with haptic feedback
          if ('vibrate' in navigator) {
            navigator.vibrate([100, 50, 100]);
          }
        }
      } else {
        setSyncResults({
          success: false,
          message: 'Sync request failed'
        });
      }
    } catch (error) {
      console.error('Error syncing bookmarks:', error);
      setSyncResults({
        success: false,
        message: `Sync error: ${error.message}`
      });
    } finally {
      setIsBookmarkSyncing(false);
    }
  };

  const showSuccessMessage = (tickerCount) => {
    // Create and show a temporary success message
    const message = document.createElement('div');
    message.innerHTML = `
      <div style="position: fixed; top: 20px; left: 20px; right: 20px; background: #10b981; color: white; padding: 16px; border-radius: 12px; z-index: 10000; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.2);">
        <div style="font-weight: 600; margin-bottom: 4px;">✅ Saved Successfully!</div>
        <div style="font-size: 14px; opacity: 0.9;">Found ${tickerCount} tickers • Added to your dashboard</div>
      </div>
    `;
    document.body.appendChild(message);
    
    setTimeout(() => {
      document.body.removeChild(message);
    }, 4000);
  };


  // Helper function to decode HTML entities (e.g., &amp; -> &, &lt; -> <)
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const formatDate = (dateString) => {
    // Only show relative time (e.g., "2 days ago")
    return formatRelativeTime(dateString);
  };

  const getPlatformIcon = (platform) => {
    // Use text-based icons for better compatibility and visual appeal
    const platformDisplay = getPlatformDisplay(platform);
    return <span className="text-sm">{platformDisplay.icon}</span>;
  };

  const calculateDaysOld = (timestamp) => {
    // Handle both string and Date timestamps, and convert to local time
    const savedDate = new Date(timestamp);
    const today = new Date();
    const diffTime = Math.abs(today - savedDate);
    // Use Math.floor to get complete days (e.g., 2.5 days = 2 days old)
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const formatLocalTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString(); // This will show in user's local timezone
  };

  // Helper function to get age category
  // Based on when user saved/imported the idea
  const getAgeCategory = (timestamp) => {
    const days = calculateDaysOld(timestamp);
    if (days < 10) return 'New';          // < 10 days since you saved it
    if (days <= 30) return 'Recent';      // 10-30 days since you saved it
    return 'Older';                       // 30+ days since you saved it
  };
  
  // Get age category from bookmark, using display_age_date if available (for custom coloring)
  const getBookmarkAgeCategory = (bookmark) => {
    // If display_age_date is set, use it for coloring (allows decoupling color from sort order)
    const ageDate = bookmark.display_age_date || bookmark.created_at;
    return getAgeCategory(ageDate);
  };

  // Helper function to highlight tickers in content
  const highlightTickers = (content) => {
    if (!content) return content;
    // For now, just return the content as-is. We can enhance this later.
    return content;
  };

  // Helper function to get category from tickers
  const getCategoryFromTickers = (tickers) => {
    if (!tickers || tickers.length === 0) return 'General';
    // Simple categorization based on common tickers
    const ticker = tickers[0].toUpperCase();
    if (['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'TSLA', 'NVDA'].includes(ticker)) {
      return 'Tech';
    }
    if (['JPM', 'BAC', 'WFC', 'GS', 'MS'].includes(ticker)) {
      return 'Finance';
    }
    return 'General';
  };

  // Helper function to format relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    
    const days = Math.floor(diffInSeconds / 86400);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  // Short format for compact card display (e.g., "18h", "2d", "1w")
  const formatDateShort = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) return `${days}d`;
    if (days < 30) return `${Math.floor(days / 7)}w`;
    if (days < 365) return `${Math.floor(days / 30)}mo`;
    return `${Math.floor(days / 365)}y`;
  };

  // Helper function to render text with clickable URLs (React-friendly)
  const renderTextWithLinks = (text, isDark = false) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Reset regex lastIndex
        urlRegex.lastIndex = 0;
        // Clean trailing punctuation
        let cleanUrl = part.replace(/[.,;:!?)\]]+$/, '');
        const punctuation = part.substring(cleanUrl.length);
        return (
          <span key={index}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`underline ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              {cleanUrl}
            </a>
            {punctuation}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Helper function to map a group shared idea to bookmark format for consistent card rendering
  const mapGroupIdeaToBookmark = (groupIdea, isDemoIdea = false) => {
    if (isDemoIdea) {
      // Demo ideas have a different structure
      const daysAgo = groupIdea.daysAgo || 0;
      const createdAt = new Date();
      createdAt.setDate(createdAt.getDate() - daysAgo);
      
      return {
        id: groupIdea.id,
        tickers: [groupIdea.ticker],
        primary_ticker: groupIdea.ticker,
        company_name: groupIdea.company,
        company_intelligence: { company_name: groupIdea.company },
        one_liner_thesis: groupIdea.thesis,
        content: groupIdea.thesis,
        summary: groupIdea.thesis,
        author: groupIdea.author,
        from_name: groupIdea.author,
        created_at: createdAt.toISOString(),
        source: 'shared_idea',
        is_shared: true,
        is_received: true,
        shared_from: groupIdea.author
      };
    }
    
    // Real shared ideas from groupSharedIdeas
    return {
      id: groupIdea.id,
      tickers: groupIdea.ticker ? [groupIdea.ticker] : [],
      primary_ticker: groupIdea.ticker,
      company_name: groupIdea.company_name,
      company_intelligence: { company_name: groupIdea.company_name },
      one_liner_thesis: groupIdea.one_liner || groupIdea.content,
      content: groupIdea.content || groupIdea.one_liner,
      summary: groupIdea.content || groupIdea.one_liner,
      author: groupIdea.sender_name || groupIdea.sender_email?.split('@')[0],
      from_name: groupIdea.sender_name || groupIdea.sender_email?.split('@')[0],
      created_at: groupIdea.shared_at || groupIdea.created_at,
      source: 'shared_idea',
      is_shared: true,
      is_received: true,
      shared_from: groupIdea.sender_name || groupIdea.sender_email?.split('@')[0],
      // Preserve original group idea data for reference
      _originalGroupIdea: groupIdea
    };
  };

  // Helper function to extract base ID from a bookmark ID (removes ticker suffix like _AAPL, _MSFT, _list)
  const extractBaseBookmarkId = (id) => {
    if (!id) return id;
    // Pattern: x_bookmark_1764693718.319112_TICKER or email_uuid_TICKER or _QUOTED
    // We want to extract: x_bookmark_1764693718.319112 or email_uuid
    // Look for pattern ending in _TICKER (2-5 uppercase letters), _TICKER.EXCHANGE (e.g., OCI.AS, ULVR.LN), _list, or _QUOTED
    // International tickers have format: TICKER.EXCHANGE where EXCHANGE is 1-3 uppercase letters
    const tickerSuffixPattern = /(_[A-Z]{1,5}(\.[A-Z]{1,3})?|_list|_QUOTED)$/;
    if (tickerSuffixPattern.test(id)) {
      return id.replace(tickerSuffixPattern, '');
    }
    return id;
  };

  // Helper function to group ideas by their base bookmark ID for stacked card display
  // IMPORTANT: This function preserves the input order to prevent card shuffling during pagination
  const groupIdeasForStackedCards = useCallback((ideas, allBookmarksForGrouping) => {
    const groups = {};
    const groupFirstIndex = {}; // Track first occurrence index for each group
    const resultItems = []; // Track items in order of first appearance
    const processedIds = new Set(); // Track all IDs that have been processed
    
    // Build parent-child map from ALL bookmarks (including nested ones)
    const childrenByParentId = {};
    const cardsByBaseId = {}; // For legacy grouping
    
    console.log('🔍 allBookmarksForGrouping count:', allBookmarksForGrouping?.length);
    const bourbon = (allBookmarksForGrouping || []).filter(b => b.author && b.author.includes('BourbonCap'));
    console.log('🔍 BourbonCap cards in allBookmarksForGrouping:', bourbon.map(b => ({id: b.id, ticker: b.tickers?.[0], parent_id: b.parent_id})));
    
    (allBookmarksForGrouping || []).forEach(bookmark => {
      // Skip quoted tweets - DISABLED for now
      if (bookmark.source === 'x_bookmark_quoted') return;
      
      // Build parent_id relationships
      if (bookmark.parent_id) {
        if (!childrenByParentId[bookmark.parent_id]) {
          childrenByParentId[bookmark.parent_id] = [];
        }
        childrenByParentId[bookmark.parent_id].push(bookmark);
      }
      
      // Build legacy ID-based relationships
      const baseId = extractBaseBookmarkId(bookmark.id);
      if (baseId !== bookmark.id) { // Has ticker suffix
        if (!cardsByBaseId[baseId]) {
          cardsByBaseId[baseId] = [];
        }
        cardsByBaseId[baseId].push(bookmark);
      }
    });
    
    // Sort all children arrays by created_at (oldest first) to ensure consistent thread order
    Object.keys(childrenByParentId).forEach(parentId => {
      childrenByParentId[parentId].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateA - dateB;
      });
    });
    Object.keys(cardsByBaseId).forEach(baseId => {
      cardsByBaseId[baseId].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateA - dateB;
      });
    });
    
    // CRITICAL: Determine which base IDs should use parent_id grouping vs legacy grouping
    // Only use parent_id grouping if the parent actually exists in the ideas list
    const baseIdsWithParentChild = new Set();
    const ideaIds = new Set(ideas.map(i => i.id));
    
    Object.keys(childrenByParentId).forEach(parentId => {
      // Only mark as parent-child if the parent actually exists
      if (ideaIds.has(parentId)) {
        const baseId = extractBaseBookmarkId(parentId);
        baseIdsWithParentChild.add(baseId);
      }
    });
    
    // First pass: Process parent-child relationships
    ideas.forEach((idea, index) => {
      if (processedIds.has(idea.id)) return;
      // Skip quoted tweets
      if (idea.source === 'x_bookmark_quoted') {
        processedIds.add(idea.id);
        return;
      }
      
      const nestedChildren = childrenByParentId[idea.id] || [];
      
      // Only create a thread group if there are 2+ different tickers involved
      // A single idea about one ticker should NOT be a thread, even if it has parent_id children
      if (nestedChildren.length > 0) {
        // Get all unique tickers from parent + children
        const parentTicker = idea.tickers?.[0]?.toUpperCase();
        const allTickers = new Set([parentTicker]);
        nestedChildren.forEach(child => {
          const childTicker = child.tickers?.[0]?.toUpperCase();
          if (childTicker) allTickers.add(childTicker);
        });
        
        // Only create a thread if there are multiple different tickers
        if (allTickers.size > 1) {
          // Determine color based on child type:
          // - Purple for quoted tweets (source === 'x_bookmark_quoted')
          // - Green for thread children (source === 'x_bookmark_thread_child' or parent is thread_summary)
          // - Yellow for other cases
          const hasQuotedTweet = nestedChildren.some(c => c.source === 'x_bookmark_quoted');
          const hasThreadChild = nestedChildren.some(c => c.source === 'x_bookmark_thread_child');
          const isThreadSummary = idea.source === 'x_bookmark_thread_summary';
          
          // Debug for investspecial
          if (idea.id?.includes('1768708667')) {
            console.warn('FIRST PASS - InvestSpecial:', {
              parentId: idea.id,
              parentSource: idea.source,
              childCount: nestedChildren.length,
              hasQuotedTweet,
              hasThreadChild,
              isThreadSummary,
              childSources: nestedChildren.map(c => c.source)
            });
          }
          
          let childColor = 'yellow'; // default
          if (hasQuotedTweet) {
            childColor = 'purple';
          } else if (hasThreadChild || isThreadSummary) {
            childColor = 'green';
          }
          
          resultItems.push({
            type: 'thread',
            parentIdea: idea,
            childIdeas: [...nestedChildren].sort((a, b) => {
              // Sort by created_at ascending (oldest first)
              const dateA = new Date(a.created_at || 0);
              const dateB = new Date(b.created_at || 0);
              return dateA - dateB;
            }),
            childColor: childColor,
            originalIndex: index,
            sortDate: idea.created_at || idea.processed_at || '',
            sortId: idea.id || ''
          });
          
          processedIds.add(idea.id);
          nestedChildren.forEach(child => processedIds.add(child.id));
        }
      }
      
      // Handle ticker_ideas - MULTI-IDEA POST
      if (!processedIds.has(idea.id) && idea.ticker_ideas && idea.ticker_ideas.length > 1 && !(idea.is_ticker_list || idea.title === "Ideas List")) {
        // MULTI-IDEA POST: Single post with multiple ticker ideas (no parent_id children)
        // Only treat as multi-idea if there are actually 2+ ideas
        // Skip Ideas List cards - they have their own expansion logic
        // Check if these are thread children (green) or regular multi-idea (yellow)
        const hasThreadChildren = idea.ticker_ideas.some(ti => 
          ti.source === 'x_bookmark_thread_child' || ti.source === 'x_bookmark_thread_summary'
        );
        const isThreadParent = idea.source === 'x_bookmark_thread_summary';
        const threadChildColor = (hasThreadChildren || isThreadParent) ? 'green' : 'yellow';
        
        console.log(`💡 ${threadChildColor === 'green' ? 'THREAD' : 'MULTI-IDEA POST'}: ${idea.tickers?.[0]} with ${idea.ticker_ideas.length} nested ideas (${threadChildColor})`);
        // Debug: Log wwnbt_analysis presence in ticker_ideas
        idea.ticker_ideas.forEach((ti, idx) => {
          console.log(`  📊 ticker_idea[${idx}]: id=${ti.id?.substring(0, 15)}, ticker=${ti.ticker}, has_wwnbt=${!!ti.wwnbt_analysis}, thesis=${ti.wwnbt_analysis?.thesis_summary?.substring(0, 40) || 'none'}`);
        });
        
        // Map ticker_ideas to childIdeas and sort by created_at (oldest first)
        const mappedChildren = idea.ticker_ideas.map((ti, idx) => ({
          id: ti.id || `${idea.id}_ticker_${idx}`,
          tickers: ti.tickers || [ti.ticker],
          primary_ticker: ti.ticker,
          title: ti.title || `$${ti.ticker}`,
          content: ti.context || ti.summary || ti.content || '',
          summary: ti.summary || ti.context || '',
          preview: ti.preview || '',  // 8-14 word hook preview
          one_liner_thesis: ti.one_liner_thesis,
          company_name: ti.company_name,
          created_at: ti.created_at || idea.created_at,
          source: ti.source || idea.source,  // Use child's source if available, fallback to parent
          author: idea.author,
          from_name: idea.from_name,
          url: idea.url,
          // Use child's twitterMetadata if available, fallback to parent's
          twitterMetadata: ti.twitterMetadata || idea.twitterMetadata,
          metadata: idea.metadata,
          is_twitter_content: ti.is_twitter_content || idea.is_twitter_content,
          original_content: ti.original_content || ti.content || '',
          // WWNBT Analysis: Author's Thesis for collapsed card display
          wwnbt_analysis: ti.wwnbt_analysis || null
        })).sort((a, b) => {
          // Sort by created_at ascending (oldest first)
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateA - dateB;
        });
        
        resultItems.push({
          type: 'thread',
          parentIdea: idea,
          childIdeas: mappedChildren,
          childColor: threadChildColor, // Green for threads, yellow for multi-idea posts
          originalIndex: index,
          sortDate: idea.created_at || idea.processed_at || '',
          sortId: idea.id || ''
        });
        
        processedIds.add(idea.id);
      }
    });
    
    // Second pass: Process remaining cards (legacy grouping or standalone)
    ideas.forEach((idea, index) => {
      if (processedIds.has(idea.id)) return;
      // Skip quoted tweets
      if (idea.source === 'x_bookmark_quoted') {
        processedIds.add(idea.id);
        return;
      }
      
      const baseId = extractBaseBookmarkId(idea.id);
      const hasTickerSuffix = idea.id !== baseId;
      
      // Handle Ideas List cards as standalone (they have _list suffix but should not be grouped)
      if (idea.is_ticker_list || idea.title === "Ideas List") {
        resultItems.push({
          type: 'standalone',
          idea,
          originalIndex: index,
          sortDate: idea.created_at || idea.processed_at || '',
          sortId: idea.id || ''
        });
        processedIds.add(idea.id);
        return;
      }
      
      // Skip legacy grouping if this base ID uses parent_id relationships
      if (hasTickerSuffix && !baseIdsWithParentChild.has(baseId)) {
        // Legacy ID-based grouping - these are MULTI-IDEA posts
        const cardsInGroup = cardsByBaseId[baseId] || [];
        if (cardsInGroup.length > 1 && !groups[baseId]) {
          // Check if any card is a quoted tweet or thread child - determine color
          const hasQuotedTweet = cardsInGroup.some(c => c.source === 'x_bookmark_quoted');
          const hasThreadChild = cardsInGroup.some(c => c.source === 'x_bookmark_thread_child');
          
          // Also check if there's a parent in bookmarks that's a thread summary
          const parentInBookmarks = allBookmarksForGrouping?.find(b => b.id === baseId);
          const parentIsThreadSummary = parentInBookmarks?.source === 'x_bookmark_thread_summary';
          
          // Debug for investspecial
          if (baseId?.includes('1768708667')) {
            console.warn('LEGACY GROUPING - InvestSpecial:', {
              baseId,
              cardCount: cardsInGroup.length,
              hasQuotedTweet,
              hasThreadChild,
              parentInBookmarks: !!parentInBookmarks,
              parentIsThreadSummary,
              cardSources: cardsInGroup.map(c => c.source)
            });
          }
          
          let childColor = 'yellow'; // default for multi-idea
          if (hasQuotedTweet) {
            childColor = 'purple';
          } else if (hasThreadChild || parentIsThreadSummary) {
            childColor = 'green';
          }
          
          // Create a summary parent from the original post's data
          const firstCard = cardsInGroup[0];
          const allTickers = [...new Set(cardsInGroup.flatMap(c => c.tickers || []))];
          const originalTweetContent = firstCard.content || '';
          
          const summaryParent = {
            id: baseId + '_summary',
            title: `${allTickers.length} Investment Ideas`,
            tickers: allTickers,
            content: originalTweetContent,
            summary: originalTweetContent,
            created_at: firstCard.created_at,
            source: firstCard.source,
            author: firstCard.author,
            from_name: firstCard.from_name,
            url: firstCard.url
          };
          
          // Sort children by created_at ascending (oldest first)
          const sortedChildren = [...cardsInGroup].sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateA - dateB;
          });
          
          resultItems.push({
            type: 'thread',
            parentIdea: summaryParent,
            childIdeas: sortedChildren,
            childColor: childColor,
            originalIndex: index,
            sortDate: firstCard.created_at || firstCard.processed_at || '',
            sortId: baseId
          });
          
          cardsInGroup.forEach(card => processedIds.add(card.id));
        }
      } else if (!idea.parent_id && !hasTickerSuffix) {
        // Standalone card (no ticker suffix and no parent_id)
        resultItems.push({
          type: 'standalone',
          idea,
          originalIndex: index,
          sortDate: idea.created_at || idea.processed_at || '',
          sortId: idea.id || ''
        });
        processedIds.add(idea.id);
      }
    });
    
    // Third pass: process any remaining groups (parent-child relationships not caught in first pass)
    Object.entries(groups).forEach(([baseId, groupIdeas]) => {
      if (groupIdeas.length >= 2) {
        let sortedGroupIdeas;
        
        // Check if this is a parent-child relationship (first item has no parent_id, others do)
        const isParentChildGroup = !groupIdeas[0].parent_id && groupIdeas.slice(1).every(idea => idea.parent_id === groupIdeas[0].id);
        
        if (isParentChildGroup) {
          // Parent-child relationship: keep parent first, sort children by created_at (oldest first)
          const parent = groupIdeas[0];
          const children = groupIdeas.slice(1).sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateA - dateB;
          });
          sortedGroupIdeas = [parent, ...children];
        } else {
          // Legacy ID-based grouping: sort all by created_at (oldest first)
          sortedGroupIdeas = [...groupIdeas].sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateA - dateB;
          });
        }
        
        resultItems.push({
          type: 'group',
          baseId,
          ideas: sortedGroupIdeas,
          primaryIdea: sortedGroupIdeas[0],
          originalIndex: groupFirstIndex[baseId], // Use first occurrence position
          sortDate: sortedGroupIdeas.reduce((earliest, idea) => {
            const ideaDate = idea.created_at || idea.processed_at || '';
            return !earliest || ideaDate < earliest ? ideaDate : earliest;
          }, null),
          sortId: baseId || ''
        });
      } else {
        // Single-item groups become standalone
        groupIdeas.forEach(idea => {
          resultItems.push({
            type: 'standalone',
            idea,
            originalIndex: groupFirstIndex[baseId],
            sortDate: idea.created_at || idea.processed_at || '',
            sortId: idea.id || ''
          });
        });
      }
    });
    
    // Sort by original index to preserve input order (which comes pre-sorted from sortBookmarks)
    return resultItems.sort((a, b) => a.originalIndex - b.originalIndex);
  }, [extractBaseBookmarkId]);

  // Toggle expanded state for a card group (including Ideas Lists)
  const toggleCardGroup = (baseId, tickers = []) => {
    console.log('[toggleCardGroup] Called with baseId:', baseId, 'tickers count:', tickers?.length);
    setExpandedCardGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(baseId)) {
        console.log('[toggleCardGroup] Collapsing:', baseId);
        newSet.delete(baseId);
        // Also collapse all individual idea cards in this group
        setExpandedIdeaCards(prevIdeas => {
          const newIdeasSet = new Set(prevIdeas);
          // Remove all IDs that start with this baseId
          Array.from(newIdeasSet).forEach(id => {
            if (id.startsWith(baseId) || id === baseId) {
              newIdeasSet.delete(id);
            }
          });
          return newIdeasSet;
        });
        // Sync toggle state - collapse means back to summary
        setFullPostModeCards(prevToggle => {
          const newToggle = new Set(prevToggle);
          newToggle.delete(baseId);
          return newToggle;
        });
      } else {
        console.log('[toggleCardGroup] Expanding:', baseId);
        newSet.add(baseId);
        // When expanding, also expand all individual idea cards in this group
        const groupIdeas = bookmarks.filter(b => {
          const bBaseId = b.id?.includes('_') ? b.id.substring(0, b.id.lastIndexOf('_')) : b.id;
          return bBaseId === baseId || b.id === baseId;
        });
        setExpandedIdeaCards(prevIdeas => {
          const newIdeasSet = new Set(prevIdeas);
          groupIdeas.forEach(idea => {
            if (idea.id) {
              newIdeasSet.add(idea.id);
            }
          });
          return newIdeasSet;
        });
        // Sync toggle state - expand means full post
        setFullPostModeCards(prevToggle => {
          const newToggle = new Set(prevToggle);
          newToggle.add(baseId);
          return newToggle;
        });
        // When expanding, fetch metrics for all tickers in this group
        if (tickers && tickers.length > 0) {
          const tickerNames = tickers.map(t => typeof t === 'string' ? t : t.ticker).filter(Boolean);
          console.log(`[Ideas List] Expanding ${baseId}, fetching metrics for ${tickerNames.length} tickers:`, tickerNames.slice(0, 5));
          fetchTickerMetricsOnce(tickerNames);
        } else {
          console.log('[toggleCardGroup] WARNING: No tickers to fetch for', baseId);
        }
      }
      return newSet;
    });
  };

  // Also fetch metrics when any expanded Ideas List needs data
  useEffect(() => {
    console.log('[Ideas List DEBUG] expandedCardGroups:', Array.from(expandedCardGroups), 'bookmarks:', bookmarks.length);
    
    if (expandedCardGroups.size === 0 || bookmarks.length === 0) return;
    
    // Find expanded Ideas List cards that need metrics
    const expandedIdeasLists = bookmarks.filter(b => {
      const isIdeasList = b.is_ticker_list || b.title === "Ideas List";
      const isExpanded = expandedCardGroups.has(b.id);
      if (isIdeasList) {
        console.log('[Ideas List DEBUG] Found Ideas List:', b.id, 'expanded:', isExpanded, 'tickers:', b.tickers?.length, b.all_tickers?.length, b.categorized_tickers?.length);
      }
      return isIdeasList && isExpanded;
    });
    
    console.log('[Ideas List DEBUG] expandedIdeasLists:', expandedIdeasLists.length);
    
    if (expandedIdeasLists.length === 0) return;
    
    // Collect all tickers that need metrics - use consistent validation
    const tickersToFetch = [];
    expandedIdeasLists.forEach(bookmark => {
      const allTickers = [
        ...(bookmark.tickers || []),
        ...(bookmark.all_tickers || []),
        ...((bookmark.categorized_tickers || []).flatMap(cat => cat.tickers || []))
      ];
      console.log('[Ideas List DEBUG] bookmark', bookmark.id, 'allTickers:', allTickers);
      allTickers.forEach(t => {
        const ticker = typeof t === 'string' ? t : t.ticker;
        // Use isTickerCacheValid for consistent validation across all fetch paths
        if (ticker && !isTickerCacheValid(ticker, tickerMetricsCache)) {
          tickersToFetch.push(ticker);
        }
      });
    });
    
    if (tickersToFetch.length > 0) {
      console.log(`[Ideas List] Auto-fetching metrics for ${tickersToFetch.length} tickers needing data:`, tickersToFetch);
      fetchTickerMetricsOnce([...new Set(tickersToFetch)]);
    }
  }, [expandedCardGroups, bookmarks, isTickerCacheValid, tickerMetricsCache]);

  // Extract dynamic source types from user's actual content - MATCH AI ATTRIBUTION EXACTLY
  const extractDynamicSources = (ideas) => {
    // SIMPLIFIED: Only show "All" and "X Posts" in the filter
    // Other sources (Investor Letters, Reddit, etc.) will be added later
    // "Shared" is a separate tab, not part of this filter
    
    const sourceTypes = ['all', 'twitter'];
    const sourceCategories = new Map();
    
    // X Posts filter
    sourceCategories.set('twitter', { 
      label: 'X Posts', 
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.910 11.078h-3.246z"/>
        </svg>
      ),
      filter: (item) => {
        const hasTwitterData = item.metadata?.twitter_data || item.metadata?.twitter_content;
        const isExplicitlyTwitter = item.platform === 'Twitter' || item.is_twitter_content === true;
        const urlIsTwitter = item.url?.includes('twitter.com') || item.url?.includes('x.com');
        return isExplicitlyTwitter || hasTwitterData || urlIsTwitter;
      }
    });
    
    return { sourceTypes, sourceCategories };
  };

  // Helper function to detect if content is an AI summary
  const isAISummaryContent = (bookmark) => {
    // If there's both content and original_content, then content is likely the AI summary
    if (bookmark.content && bookmark.original_content && bookmark.content !== bookmark.original_content) {
      return true;
    }
    
    // If there's both content and tweet_content, then content is likely the AI summary
    if (bookmark.content && bookmark.metadata?.tweet_content && bookmark.content !== bookmark.metadata.tweet_content) {
      return true;
    }
    
    // If the source info indicates AI attribution, it's likely an AI summary
    if (bookmark.source_info?.ai_attribution?.original_source_name) {
      return true;
    }
    
    return false;
  };

  const cleanContentForDisplay = (content, maxLength = 200, bookmarkId = null, forceExpand = false, isAISummary = false) => {
    if (!content) return '';
    
    // Remove age-related prefixes from tweet content
    const agePatterns = [
      /^Going stale:\s*/i,
      /^Getting warm:\s*/i, 
      /^Warm idea:\s*/i,
      /^Fresh:\s*/i,
      /^Stale:\s*/i,
      /^🆕\s*Fresh:\s*/i,
      /^🔥\s*Warm:\s*/i,
      /^⚠️\s*Stale:\s*/i,
      /^🆕\s*/i,
      /^🔥\s*/i,
      /^⚠️\s*/i
    ];
    
    let cleanedContent = content;
    for (const pattern of agePatterns) {
      cleanedContent = cleanedContent.replace(pattern, '');
    }
    
    const trimmedContent = cleanedContent.trim();
    
    // AI summaries should ALWAYS be shown in full - never truncated
    if (isAISummary) {
      return trimmedContent;
    }
    
    // Individual expansion should work independently of global state
    // Priority: forceExpand > individual expansion > global expansion
    if (forceExpand) {
      return trimmedContent;
    }
    
    // If this specific bookmark is individually expanded, show full content
    if (bookmarkId && expandedBookmarks.has(bookmarkId)) {
      return trimmedContent;
    }
    
    // If globally expanded and not individually collapsed, show full content
    if (globalExpanded) {
      return trimmedContent;
    }
    
    // If content is short enough, show it all
    if (trimmedContent.length <= maxLength) {
      return trimmedContent;
    }
    
    // Otherwise truncate and add ellipsis
    return trimmedContent.substring(0, maxLength) + '...';
  };


  // Make citation numbers clickable (for Perplexity citations)
  const makeCitationsClickable = (text, citations = []) => {
    if (!text || !citations || citations.length === 0) {
      return text;
    }

    // Replace [1], [2], etc. with clickable links
    const citationPattern = /\[(\d+)\]/g;
    
    return text.split(citationPattern).map((part, index) => {
      // Even indices are text, odd indices are citation numbers
      if (index % 2 === 0) {
        return part;
      }
      
      const citationNumber = parseInt(part);
      const citationUrl = citations[citationNumber - 1]; // Arrays are 0-indexed
      
      if (citationUrl) {
        return (
          <a
            key={`citation-${index}`}
            href={citationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium mx-0.5"
            title={`Source: ${citationUrl}`}
            onClick={(e) => e.stopPropagation()}
          >
            [{part}]
          </a>
        );
      }
      
      return `[${part}]`;
    });
  };


  const toggleBookmarkExpansion = useCallback((bookmarkId) => {
    const newExpanded = new Set(expandedBookmarks);
    if (newExpanded.has(bookmarkId)) {
      newExpanded.delete(bookmarkId);
    } else {
      newExpanded.add(bookmarkId);
    }
    setExpandedBookmarks(newExpanded);
  }, [expandedBookmarks]);

  const toggleGlobalExpansion = useCallback(() => {
    setGlobalExpanded(!globalExpanded);
    
    if (!globalExpanded) {
      // If expanding globally
      if (dashboardView === 'sources') {
        // For sources view: expand all source categories
        const allSourceKeys = new Set();
        const groupedSources = groupIdeasBySource(bookmarks);
        groupedSources.forEach(source => {
          allSourceKeys.add(source.sourceKey);
        });
        setExpandedSources(allSourceKeys);
      } else {
        // For ideas view: clear individual expansions (existing behavior)
        setExpandedBookmarks(new Set());
      }
    } else {
      // If collapsing globally
      if (dashboardView === 'sources') {
        // For sources view: collapse all source categories
        setExpandedSources(new Set());
      } else {
        // For ideas view: expand all bookmarks (existing behavior)
        const allIds = new Set(bookmarks.map(b => b.id));
        setExpandedBookmarks(allIds);
      }
    }
  }, [globalExpanded, dashboardView, bookmarks]);

  // Fetch ideas that the user has shared with others
  const fetchSharedByMeIdeas = async () => {
    try {
      const userEmail = user?.email || 'urijmiller@gmail.com';
      const response = await fetch(`${API_BASE_URL}/api/shared-by-me/${encodeURIComponent(userEmail)}`, {
        headers: {
          'X-User-ID': user?.id || 'anonymous'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSharedByMeIdeas(data.shared_ideas || []);
        // Count number of shares (posts/polls), not individual tickers
        setSharedByMeCount(data.total_count || 0);
        setShowSharedByMeModal(true);
      } else {
        console.error('Failed to fetch shared ideas');
        alert('Failed to load shared ideas');
      }
    } catch (error) {
      console.error('Error fetching shared ideas:', error);
      alert('Error loading shared ideas');
    }
  };

  const clearAllReceivedIdeas = async () => {
    if (!window.confirm('Are you sure you want to clear all received ideas? This will reject all pending shared ideas.')) {
      return;
    }

    try {
      const userEmail = user?.email || 'urijmiller@gmail.com';
      const response = await fetch(`${API_BASE_URL}/api/shared-ideas/clear-received/${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: {
          'X-User-ID': user?.id || 'anonymous'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setPendingIdeas([]);
        setShowReceiveModal(false);
        fetchData(); // Refresh
      } else {
        alert('Failed to clear received ideas');
      }
    } catch (error) {
      console.error('Error clearing received ideas:', error);
      alert('Error clearing received ideas');
    }
  };

  const clearAllSentIdeas = async () => {
    if (!window.confirm('Are you sure you want to clear all sent ideas? This will permanently delete all ideas you\'ve shared.')) {
      return;
    }

    try {
      const userEmail = user?.email || 'urijmiller@gmail.com';
      const response = await fetch(`${API_BASE_URL}/api/shared-ideas/clear-sent/${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: {
          'X-User-ID': user?.id || 'anonymous'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setSharedByMeIdeas([]);
        setSharedByMeCount(0);
        setShowSharedByMeModal(false);
      } else {
        alert('Failed to clear sent ideas');
      }
    } catch (error) {
      console.error('Error clearing sent ideas:', error);
      alert('Error clearing sent ideas');
    }
  };

  // Sort bookmarks - preserves existing item positions during pagination
  // This prevents cards from shuffling when new ideas are loaded via infinite scroll
  // Key insight: The backend returns items in the correct order for each page.
  // We just need to maintain that order - existing items stay in place, new items append.
  const sortBookmarks = useCallback((bookmarksToSort) => {
    if (!bookmarksToSort || bookmarksToSort.length === 0) return bookmarksToSort;
    
    // Backend already handles sorting via sort_by parameter
    // We only need to maintain order for pagination (new items appended)
    // Return items in their API response order - DO NOT re-sort
    return bookmarksToSort;
  }, []);

  // Memoized filtered bookmarks for Ideas view - only recalculates when bookmarks change
  // NOTE: Search filtering is now done via the GLOBAL SEARCH API endpoint
  // This only filters by classification (idea vs non-idea)
  const filteredIdeasForView = useMemo(() => {
    const filtered = bookmarks.filter(b => {
      // Filter out non-ideas
      if (b.classification && b.classification !== 'idea') return false;
      
      // Filter out Ideas Lists - they now live in Collections tab
      if (b.is_ticker_list || b.title === "Ideas List") return false;
      
      // Filter by ideaView (My Ideas vs Received)
      // "Received" = ideas shared TO this user by others (not self-shares)
      // Check: is_received flag AND shared_from is different from current user's email
      const isReceivedFromOthers = (b.is_received === true || !!b.shared_from || (b.source === 'shared_idea')) && 
                                   (!b.shared_from || b.shared_from !== userEmail);
      if (ideaView === 'my' && isReceivedFromOthers) return false;
      if (ideaView === 'received' && !isReceivedFromOthers) return false;
      
      // NOTE: Do NOT filter out nested ideas here - the grouping function needs them
      // to build parent-child relationships. They will be hidden during rendering.
      
      // Search filtering is now handled globally by the backend API
      // The searchResults state will contain results from the entire database
      
      return true;
    });
    
    // Debug: Log filter results
    const receivedCount = bookmarks.filter(b => 
      (b.is_received === true || !!b.shared_from || b.source === 'shared_idea') && 
      (!b.shared_from || b.shared_from !== userEmail)
    ).length;
    console.log(`🔍 ideaView filter: ${ideaView}, total bookmarks: ${bookmarks.length}, received: ${receivedCount}, filtered result: ${filtered.length}`);
    
    return filtered;
  }, [bookmarks, ideaView]);

  // Memoized Ideas Lists - items with is_ticker_list flag, for Collections tab
  const ideasListsForCollections = useMemo(() => {
    return bookmarks.filter(b => b.is_ticker_list || b.title === "Ideas List");
  }, [bookmarks]);

  // Memoized sorted bookmarks - only recalculates when filtered list or sort changes
  const sortedIdeasForView = useMemo(() => {
    return sortBookmarks(filteredIdeasForView);
  }, [filteredIdeasForView, sortBookmarks]);

  // Memoized grouped items for stacked card display - only recalculates when sorted list changes
  const groupedIdeasForView = useMemo(() => {
    const result = groupIdeasForStackedCards(sortedIdeasForView, bookmarks);
    
    // Debug: Find any thread items and log their color
    const threadItems = result.filter(r => r.type === 'thread');
    threadItems.forEach(t => {
      if (t.parentIdea?.id?.includes('1768708667') || t.childIdeas?.some(c => c.id?.includes('1768708667'))) {
        console.warn('INVESTSPECIAL THREAD:', t.childColor, 'parent:', t.parentIdea?.id, 'children:', t.childIdeas?.length);
      }
    });
    
    return result;
  }, [sortedIdeasForView, bookmarks, groupIdeasForStackedCards]);

  // GLOBAL SEARCH: Memoized grouped search results for display
  // Uses searchResults from the backend API when a search query is active
  const groupedSearchResults = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];
    
    // For search results, show all posts individually (no grouping)
    // This matches the watchlist search behavior
    const sortedResults = sortBookmarks(searchResults);
    return sortedResults.map(idea => ({
      // Use 'standalone' type so it goes through the same rendering logic as main dashboard
      type: 'standalone',
      idea: idea,
      bookmark: idea,
      parentIdea: idea
    }));
  }, [searchResults, sortBookmarks]);

  // FINAL DISPLAY DATA: Use search results when searching, otherwise use filtered/sorted ideas
  const displayGroupedIdeas = useMemo(() => {
    // If there's a search idea filter set (from clicking an idea in search), show those ideas
    if (searchIdeaFilter && searchIdeaFilter.length > 0) {
      // Convert filtered ideas to the grouped format used by the view
      return searchIdeaFilter.map(idea => ({
        type: 'standalone',
        idea: idea,
        displayIndex: 0
      }));
    }
    // If there's an active search query and we have results (or an empty array meaning no results)
    if (searchQuery && searchQuery.trim() && searchResults !== null) {
      return groupedSearchResults;
    }
    // Otherwise, show the normal filtered/sorted ideas
    return groupedIdeasForView;
  }, [searchQuery, searchResults, groupedSearchResults, groupedIdeasForView, searchIdeaFilter]);

  // Memoized grouped sources for Source View - only recalculates when filtered ideas change
  const groupedSourcesForView = useMemo(() => {
    return groupIdeasBySource(filteredIdeasForView);
  }, [filteredIdeasForView, groupIdeasBySource]);

  // Memoized sorted research items for Library tab - only recalculates when research items or sort changes
  const sortedResearchItems = useMemo(() => {
    if (!researchItems || researchItems.length === 0) return researchItems;
    const cloned = [...researchItems];
    return cloned.sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime() || 0;
      const dateB = new Date(b.created_at || 0).getTime() || 0;
      const dateCompare = librarySortBy === 'oldest' ? dateA - dateB : dateB - dateA;
      if (dateCompare !== 0) return dateCompare;
      return (a.id || '').localeCompare(b.id || '');
    });
  }, [researchItems, librarySortBy]);

  const isContentTruncated = (content, maxLength = 200, bookmarkId = null, bookmark = null) => {
    if (!content) return false;
    
    // AI summaries should never show "Show More" button
    if (bookmark && isAISummaryContent(bookmark)) return false;
    
    // Remove age-related prefixes to get clean content for length check
    const agePatterns = [
      /^Going stale:\s*/i,
      /^Getting warm:\s*/i, 
      /^Warm idea:\s*/i,
      /^Fresh:\s*/i,
      /^Stale:\s*/i,
      /^🆕\s*Fresh:\s*/i,
      /^🔥\s*Warm:\s*/i,
      /^⚠️\s*Stale:\s*/i,
      /^🆕\s*/i,
      /^🔥\s*/i,
      /^⚠️\s*/i
    ];
    
    let cleanedContent = content;
    for (const pattern of agePatterns) {
      cleanedContent = cleanedContent.replace(pattern, '');
    }
    
    const trimmedContent = cleanedContent.trim();
    
    // Content is truncated if:
    // 1. It's longer than maxLength AND
    // 2. NOT individually expanded AND
    // 3. NOT globally expanded
    const isIndividuallyExpanded = bookmarkId && expandedBookmarks.has(bookmarkId);
    
    return trimmedContent.length > maxLength && !isIndividuallyExpanded && !globalExpanded;
  };

  const handleSyncBookmarks = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sync-bookmarks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user,
          max_results: 50  // Import last 50 bookmarks
        })
      });

      const result = await response.json();
      
      // Check if monthly limit was exceeded
      if (result.limit_exceeded) {
        showNotification(
          'Monthly limit reached (1,000 posts). Contact us for Enterprise with unlimited access.',
          'warning'
        );
        return;
      }
      
      if (result.success) {
        // Refresh data after successful sync
        await fetchData();
        showNotification(result.message || 'Bookmarks synchronized.', 'success');
      } else {
        showNotification(result.message || 'Sync temporarily unavailable.', 'warning');
      }
    } catch (error) {
      console.error('Sync error:', error);
      showNotification(`Sync failed: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getAgeColor = (days) => {
    if (days === 1) return "text-green-600 font-semibold"; // New
    if (days <= 5) return "text-orange-600 font-semibold"; // Recent  
    return "text-red-600 font-bold"; // Older
  };

  const getAgeLabel = (days) => {
    if (days === 1) return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
        New
      </span>
    );
    if (days <= 5) return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <div className="w-2 h-2 bg-orange-400 rounded-full mr-1"></div>
        Recent
      </span>
    );
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <div className="w-2 h-2 bg-red-400 rounded-full mr-1"></div>
        Older
      </span>
    );
  };

  const markTickerResearched = async (bookmarkId, ticker) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickers/researched`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          user_id: user
        }),
      });

      if (response.ok) {
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error marking ticker as researched:', error);
    }
  };

  const deleteTickerIdea = async (bookmarkId, ticker) => {
    if (!confirm(`Delete ${ticker} from your research list?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickers/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          user_id: user
        }),
      });

      if (response.ok) {
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error deleting ticker idea:', error);
    }
  };

  const fetchTweetReplies = async (bookmark) => {
    try {
      const userId = user?.id || user?.user_id || user?.email;
      if (!userId) {
        showNotification('Please log in to fetch tweet replies', 'error', 4000);
        return;
      }
      
      // Extract conversation_id from the bookmark (if available) or use twitter_id
      const conversationId = bookmark.conversation_id || bookmark.twitter_id;
      
      const response = await fetch(`${API_BASE_URL}/api/fetch-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          conversation_id: conversationId,
          max_results: 50
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const tickerList = result.tickers_found?.length > 0 ? result.tickers_found.join(', ') : 'none';
          showNotification(`Found ${result.investment_replies || 0} replies with tickers: ${tickerList}`, 'success', 5000);
          fetchData(); // Refresh data to show new replies
        } else {
          showNotification(result.error || 'Failed to fetch replies', 'error', 5000);
        }
      } else {
        showNotification('Failed to fetch tweet replies. Please try again.', 'error', 5000);
      }
    } catch (error) {
      console.error('Error fetching tweet replies:', error);
      showNotification('Error fetching tweet replies. Please try again.', 'error', 5000);
    }
  };

  const fetchThreadReplies = async (threadId) => {
    try {
      const userId = user?.id || user?.user_id || user?.email;
      if (!userId) {
        showNotification('Please log in to refresh poll replies', 'error', 4000);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${threadId}/fetch-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const tickerList = result.tickers_found?.length > 0 ? result.tickers_found.join(', ') : 'none';
          showNotification(`Found ${result.investment_replies || 0} new replies with tickers: ${tickerList}`, 'success', 5000);
          fetchData(); // Refresh data to show updated thread and new replies
        } else {
          showNotification(result.error || 'Failed to refresh replies', 'error', 5000);
        }
      } else {
        const error = await response.json().catch(() => ({}));
        if (response.status === 401 && (error.detail?.includes('authorization expired') || error.detail?.includes('reconnect'))) {
          // Token expired - show reauthorize modal
          setShowReauthorizeModal(true);
        } else {
          showNotification('Failed to fetch thread replies. Please try again.', 'error', 5000);
        }
      }
    } catch (error) {
      console.error('Error fetching thread replies:', error);
      showNotification('Error fetching thread replies. Please try again.', 'error', 5000);
    }
  };

  const categorizeTickerForDisplay = (ticker, content) => {
    const contentLower = content.toLowerCase();
    
    // Financial Services
    if (/\b(bank|banking|credit|loan|financial services|insurance|asset management|wealth management|reit|mortgage)\b/i.test(content)) {
      return "Financial Services";
    }
    
    // Technology - More specific subcategories
    if (/\b(semiconductor|chip|nvidia|amd|intel|qualcomm|broadcom)\b/i.test(content)) {
      return "Semiconductors";
    }
    if (/\b(software|saas|cloud|enterprise software|microsoft|salesforce|adobe)\b/i.test(content)) {
      return "Software";
    }
    if (/\b(ai|artificial intelligence|machine learning|openai|meta|google)\b/i.test(content)) {
      return "Artificial Intelligence";
    }
    if (/\b(tech|technology|apple|amazon|tesla)\b/i.test(content)) {
      return "Technology";
    }
    
    // Healthcare/Biotech
    if (/\b(biotech|pharmaceutical|drug|medicine|healthcare|medical|hospital|pfizer|johnson|merck)\b/i.test(content)) {
      return "Healthcare";
    }
    
    // Energy
    if (/\b(energy|oil|gas|renewable|solar|wind|exxon|chevron|bp)\b/i.test(content)) {
      return "Energy";
    }
    
    // Consumer
    if (/\b(retail|consumer|restaurant|food|beverage|walmart|target|coca-cola|pepsi)\b/i.test(content)) {
      return "Consumer";
    }
    
    // Industrial
    if (/\b(industrial|manufacturing|aerospace|defense|construction|boeing|caterpillar|ge)\b/i.test(content)) {
      return "Industrial";
    }
    
    // Utilities
    if (/\b(utility|utilities|electric|power|water|telecom|telecommunications|verizon|att)\b/i.test(content)) {
      return "Utilities";
    }
    
    // Real Estate
    if (/\b(real estate|reit|property|housing|commercial real estate)\b/i.test(content)) {
      return "Real Estate";
    }
    
    // Default based on common investment themes
    if (/\b(growth|value|dividend|income)\b/i.test(content)) {
      return "Investment Strategy";
    }
    
    return "General";
  };



  const getUpdateType = (content) => {
    const contentLower = content.toLowerCase();
    
    if (/\b(earnings|revenue|q[1-4]|quarter|ebitda|eps)\b/i.test(content)) {
      return { label: "EARNINGS REPORT", variant: "default" };
    }
    
    if (/\b(acquisition|merger|buyout|deal|purchase)\b/i.test(content)) {
      return { label: "BUSINESS ACQUISITION", variant: "secondary" };
    }
    
    if (/\b(lawsuit|legal|court|settlement)\b/i.test(content)) {
      return { label: "LEGAL UPDATE", variant: "destructive" };
    }
    
    if (/\b(partnership|collaboration|agreement|alliance)\b/i.test(content)) {
      return { label: "PARTNERSHIP", variant: "outline" };
    }
    
    if (/\b(ipo|listing|public|offering)\b/i.test(content)) {
      return { label: "PUBLIC OFFERING", variant: "secondary" };
    }
    
    return { label: "BUSINESS UPDATE", variant: "outline" };
  };

  const [emailSettings, setEmailSettings] = useState({
    email: '',
    email_address: '',
    // Daily Digest settings
    daily_digest_enabled: true,
    daily_digest_time: '08:00',
    weekly_digest_enabled: false,
    weekly_digest_time: '09:00',
    weekly_digest_day: 'Sunday',
    timezone: 'America/New_York',
    max_ideas_in_digest: 10,
    include_research_dossier_links: true,
    include_document_links: true,
    default_document_preferences: {
      include10K: true,
      include10Q: true,
      include8K: true,
      includeProxy: false,
      includeStatements: true
    },
    // Folder Email settings (unified with daily digest pattern)
    folder_email_enabled: true,
    folder_email_time: '08:00',
    folder_email_frequency: 'daily', // 'daily', 'weekly', 'off'
    folder_email_docs: ['10-K', '10-Q', '8-K', '20-F', '6-K']
  });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [schedulingDashboardIdeas, setSchedulingDashboardIdeas] = useState([]);
  const [scheduledResearch, setScheduledResearch] = useState([]);
  const [scheduledResearchLoading, setScheduledResearchLoading] = useState(true); // Start as loading to avoid flash
  const [scheduleDataInitialized, setScheduleDataInitialized] = useState(false); // Track if schedule data has been fetched
  const [scheduleTabFirstRender, setScheduleTabFirstRender] = useState(true); // Force loading on first render
  const scheduleTabHasRendered = React.useRef(false); // Track if schedule tab has ever rendered content
  const [scheduleTabReady, setScheduleTabReady] = useState(false); // Delay content until after first paint
  
  // Auto Research Dossier settings
  const [dossierSettings, setDossierSettings] = useState({
    enabled: true,
    frequency: 'daily',
    time: '08:00',
    timezone: 'America/New_York',
    docs: ['10-K', '10-Q', '8-K', '20-F', '6-K'],
    ideas_per_day: 3,
    include_ai: false,
    complete_in_30_days: false
  });
  const [dossierSettingsLoading, setDossierSettingsLoading] = useState(false);
  const [dossierSettingsSaving, setDossierSettingsSaving] = useState(false);
  const [dossierSettingsLastSaved, setDossierSettingsLastSaved] = useState(0); // Track when settings were last saved (timestamp)
  const [researchSchedule, setResearchSchedule] = useState([]);
  const [scheduleGenerating, setScheduleGenerating] = useState(false);
  const [showSchedulePreview, setShowSchedulePreview] = useState(false);
  const [schedulePreviewData, setSchedulePreviewData] = useState(null);
  const [totalPendingIdeas, setTotalPendingIdeas] = useState(0);
  const [showDossierConfigModal, setShowDossierConfigModal] = useState(false);
  const [showScheduleCalendarModal, setShowScheduleCalendarModal] = useState(false);
  const [showAddToScheduleModal, setShowAddToScheduleModal] = useState(false);
  const [completedScheduleItems, setCompletedScheduleItems] = useState(() => {
    // Load from localStorage, reset if it's a new day
    try {
      const saved = localStorage.getItem('completedScheduleItems');
      if (saved) {
        const { date, items } = JSON.parse(saved);
        // Use local time for today comparison
        const now = new Date();
        const today = now.getFullYear() + '-' + 
          String(now.getMonth() + 1).padStart(2, '0') + '-' + 
          String(now.getDate()).padStart(2, '0');
        if (date === today) {
          return new Set(items);
        }
      }
    } catch (e) {}
    return new Set();
  });
  
  // Helper function to check if a schedule item is completed
  // Uses standardized key format and checks both localStorage and item status
  const isScheduleItemCompleted = useCallback((item) => {
    // First check the database status - this is the source of truth
    if (item.status === 'completed') return true;
    
    // Then check localStorage with standardized key format (just ticker-date)
    const standardKey = `${item.ticker}-${item.scheduled_date}`;
    if (completedScheduleItems.has(standardKey)) return true;
    
    // Also check legacy key formats for backwards compatibility
    const legacyKeys = [
      `12m-${item.ticker}-${item.scheduled_date}`,
      `list-${item.ticker}-${item.scheduled_date}`,
      `folder-${item.ticker}-${item.scheduled_date}`,
      `mobile-${item.ticker}-${item.scheduled_date}`,
      `cal-${item.ticker}-${item.scheduled_date}`,
      `az-${item.ticker}-${item.scheduled_date}`
    ];
    return legacyKeys.some(key => completedScheduleItems.has(key));
  }, [completedScheduleItems]);
  
  // Helper to get standardized key for a schedule item
  const getScheduleItemKey = (ticker, scheduledDate) => `${ticker}-${scheduledDate}`;
  
  // Schedule tab sort and filter state
  const [scheduleSortBy, setScheduleSortBy] = useState('date-asc'); // 'date-asc', 'date-desc', 'ticker-asc'
  const [scheduleFilterBy, setScheduleFilterBy] = useState('31days'); // '31days', 'all', 'auto', 'manual', 'past'
  const [scheduleCompletionFilter, setScheduleCompletionFilter] = useState('all'); // 'all', 'completed', 'incomplete'
  const [showScheduleSortDropdown, setShowScheduleSortDropdown] = useState(false);
  const [showScheduleFilterDropdown, setShowScheduleFilterDropdown] = useState(false);
  const [expandedScheduleItem, setExpandedScheduleItem] = useState(null); // Track expanded schedule item for dropdown
  const [scheduleIdeaIndex, setScheduleIdeaIndex] = useState({}); // Track current idea index for multi-idea items { itemKey: index }
  const [scheduleContentExpanded, setScheduleContentExpanded] = useState({}); // Track expanded content for schedule ideas
  const [scheduleCardViewMode, setScheduleCardViewMode] = useState({}); // Track view mode per schedule item: 'ai' (default) or 'tweet'
  const [scheduleViewMode, setScheduleViewMode] = useState('list'); // 'list', 'calendar', '12month'
  const [scheduleTickerSearch, setScheduleTickerSearch] = useState(''); // Ticker search filter for calendar
  const [expanded12MonthMonth, setExpanded12MonthMonth] = useState(() => {
    // Default to current month so it's expanded on load
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  }); // Track which month folder is expanded
  const [expandedFolderDate, setExpandedFolderDate] = useState(() => {
    // Default to today's date so today's folder is expanded on load
    // Use local time, not UTC (toISOString uses UTC which can show wrong day)
    const today = new Date();
    return today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
  }); // Track which daily folder is expanded in list view
  const [showCalendarView, setShowCalendarView] = useState(false); // Toggle between list and calendar view on desktop
  
  // Cross-panel linking effect - when an idea is selected, scroll to and highlight in other panels
  // NOTE: This effect must be placed after all state it depends on (dossierSettings, researchSchedule, etc.)
  useEffect(() => {
    if (!showWorkspace || !workspaceSelectedItem) return;
    
    const { ticker, bookmarkId, source } = workspaceSelectedItem;
    if (!ticker) return;
    
    console.log('[Workspace Cross-Link] Selected ticker:', ticker, 'from', source);
    
    // Set highlighted ticker (will be used for visual highlighting)
    // Highlight persists until a NEW idea is selected (no auto-clear timeout)
    const tickerUpper = ticker.toUpperCase();
    setWorkspaceHighlightedTicker(tickerUpper);
    
    // Check which panels have calendar or files open
    const hasCalendarPanel = workspacePanels.left === 'calendar' || 
                             workspacePanels.middle === 'calendar' || 
                             workspacePanels.right === 'calendar';
    const hasFilesPanel = workspacePanels.left === 'files' || 
                          workspacePanels.middle === 'files' || 
                          workspacePanels.right === 'files';
    
    console.log('[Cross-Link] Panel state - hasCalendarPanel:', hasCalendarPanel, 'hasFilesPanel:', hasFilesPanel);
    
    // Track if we need to expand a calendar folder (affects scroll timing)
    let needsFolderExpansion = false;
    
    // If calendar panel is open, find the ticker in schedule data and expand its folder
    if (hasCalendarPanel) {
      // Get schedule items from state (using dossierSettings.enabled to determine source)
      const scheduleItems = dossierSettings?.enabled ? researchSchedule : scheduledResearch;
      
      console.log('[Cross-Link] Schedule items count:', scheduleItems?.length, 'dossierSettings.enabled:', dossierSettings?.enabled);
      
      // Find the ticker in schedule data
      const matchingItem = scheduleItems?.find(item => 
        item.ticker?.toUpperCase() === tickerUpper
      );
      
      // Debug: log first few tickers for comparison
      console.log('[Cross-Link] Looking for ticker:', tickerUpper, 'Sample tickers in schedule:', 
        scheduleItems?.slice(0, 10).map(i => i.ticker?.toUpperCase()).join(', '));
      
      if (matchingItem) {
        const scheduledDate = matchingItem.scheduled_date || matchingItem.reminder_date;
        console.log('[Cross-Link] Found ticker in schedule data, date:', scheduledDate);
        
        // Expand the folder for this date
        if (scheduledDate) {
          needsFolderExpansion = true;
          
          // Just expand the folder - don't change the filter automatically
          // The user can manually switch filters if needed
          setExpandedFolderDate(scheduledDate);
          
          // Also expand the schedule item itself
          const itemKey = `list-${matchingItem.ticker}-${scheduledDate}`;
          setExpandedScheduleItem(itemKey);
          
          // After React renders the expanded folder, scroll to make it visible
          setTimeout(() => {
            // Find all visible calendar scroll containers
            const calendarContainers = document.querySelectorAll('[data-calendar-scroll-container]');
            console.log('[Cross-Link] Found calendar containers:', calendarContainers.length);
            
            calendarContainers.forEach(container => {
              // Check if container is visible (not in hidden parent)
              const hiddenParent = container.closest('.hidden');
              if (hiddenParent) {
                console.log('[Cross-Link] Skipping hidden container:', container.getAttribute('data-calendar-scroll-container'));
                return;
              }
              
              // Find the folder element inside this container
              const folderElement = container.querySelector(`[data-folder-date="${scheduledDate}"]`);
              if (folderElement) {
                console.log('[Cross-Link] Found folder in container:', container.getAttribute('data-calendar-scroll-container'));
                const containerRect = container.getBoundingClientRect();
                const folderRect = folderElement.getBoundingClientRect();
                // Scroll to put the folder near the top with some padding
                const scrollTop = container.scrollTop + (folderRect.top - containerRect.top) - 20;
                console.log('[Cross-Link] Scrolling to:', scrollTop, 'container scrollTop:', container.scrollTop);
                container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
              } else {
                console.log('[Cross-Link] Folder not found in container - date may be outside current filter view');
              }
            });
          }, 500); // Wait for folder expansion to render
        }
      } else {
        console.warn('⚠️ [Cross-Link] TICKER NOT IN SCHEDULE:', tickerUpper, 
          '| First 10 scheduled tickers:', scheduleItems?.slice(0, 10).map(i => i.ticker).join(', '));
      }
    }
    
    // Function to scroll to elements with retry logic for timing issues
    const scrollToElements = (attempt = 1) => {
      console.log(`[Cross-Link] Attempt ${attempt} to find elements for ticker: ${tickerUpper}`);
      
      let calendarFound = false;
      let filesFound = false;
      
      // Only look for calendar items if a calendar panel is open
      if (hasCalendarPanel) {
        // Direct query for data-schedule-ticker attribute
        const allCalendarItems = document.querySelectorAll(`[data-schedule-ticker="${tickerUpper}"]`);
        console.log('[Cross-Link] Found all calendar items with ticker:', allCalendarItems.length);
        
        // Filter to only visible ones (not inside hidden parents)
        let calendarItems = [];
        allCalendarItems.forEach(item => {
          const hiddenParent = item.closest('.hidden');
          if (!hiddenParent) {
            calendarItems.push(item);
          }
        });
        
        console.log('[Cross-Link] Found visible calendar items:', calendarItems.length);
        
        // Scroll to calendar item if found
        if (calendarItems.length > 0) {
          calendarFound = true;
          const calendarItem = calendarItems[0];
          // Find the scrollable parent container for the calendar
          const scrollableParent = calendarItem.closest('.overflow-y-auto');
          if (scrollableParent) {
            // Calculate scroll position to center the item
            const containerRect = scrollableParent.getBoundingClientRect();
            const itemRect = calendarItem.getBoundingClientRect();
            const scrollTop = scrollableParent.scrollTop + (itemRect.top - containerRect.top) - (containerRect.height / 2) + (itemRect.height / 2);
            console.log('[Cross-Link] Scrolling calendar to:', scrollTop);
            scrollableParent.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          } else {
            console.log('[Cross-Link] Using scrollIntoView for calendar item');
            calendarItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      
      // Only look for file items if a files panel is open
      if (hasFilesPanel) {
        const allFileItems = document.querySelectorAll(`[data-file-ticker="${tickerUpper}"]`);
        let fileItems = [];
        
        // Filter to only visible ones
        allFileItems.forEach(item => {
          const hiddenParent = item.closest('.hidden');
          if (!hiddenParent) {
            fileItems.push(item);
          }
        });
        
        console.log('[Cross-Link] Found visible file items:', fileItems.length);
        
        // Scroll to file item if found  
        if (fileItems.length > 0) {
          filesFound = true;
          const fileItem = fileItems[0];
          const scrollableParent = fileItem.closest('.overflow-y-auto');
          if (scrollableParent) {
            const containerRect = scrollableParent.getBoundingClientRect();
            const itemRect = fileItem.getBoundingClientRect();
            const scrollTop = scrollableParent.scrollTop + (itemRect.top - containerRect.top) - (containerRect.height / 2) + (itemRect.height / 2);
            console.log('[Cross-Link] Scrolling files to:', scrollTop);
            scrollableParent.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          } else {
            console.log('[Cross-Link] Using scrollIntoView for file item');
            fileItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      
      // Retry only if we expected to find something but didn't
      const shouldRetryCalendar = hasCalendarPanel && !calendarFound;
      const shouldRetryFiles = hasFilesPanel && !filesFound;
      
      if ((shouldRetryCalendar || shouldRetryFiles) && attempt < 8) {
        console.log(`[Cross-Link] Retrying... (calendar: ${shouldRetryCalendar}, files: ${shouldRetryFiles})`);
        // Use longer delay for calendar since folder expansion needs time to render
        const retryDelay = shouldRetryCalendar ? 300 : 200;
        setTimeout(() => scrollToElements(attempt + 1), retryDelay);
      } else if (attempt >= 8) {
        console.log('[Cross-Link] Max attempts reached. Calendar found:', calendarFound, 'Files found:', filesFound);
        if (hasCalendarPanel && !calendarFound) {
          console.log('[Cross-Link] Note: Ticker may not have a scheduled research date in the current calendar view');
        }
      }
    };
    
    // Start scrolling after a delay - use longer delay if we're expanding a folder
    // to give React time to re-render the expanded content
    const initialDelay = needsFolderExpansion ? 500 : 200;
    console.log('[Cross-Link] Starting scroll with initial delay:', initialDelay, 'needsFolderExpansion:', needsFolderExpansion);
    setTimeout(() => scrollToElements(1), initialDelay);
    
  }, [workspaceSelectedItem, showWorkspace, workspacePanels, dossierSettings?.enabled, researchSchedule, scheduledResearch]);
  
  // Generate company name suggestions from user's data (bookmarks, watchlists, schedule)
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    console.log('🔍 Company suggestions useEffect - query:', query, 'tickerMetricsCache keys:', Object.keys(tickerMetricsCache).length);
    
    // Only show suggestions if query is 2+ chars and doesn't start with special prefixes
    if (!query || query.length < 2 || query.startsWith('$') || query.startsWith('@') || 
        query.includes(':') || query === '') {
      setCompanySuggestions([]);
      return;
    }
    
    // Build company name → ticker map from all user data sources
    const companyMap = new Map(); // Map<company_name, { ticker, source }>
    
    // From bookmarks/ideas
    if (bookmarks && bookmarks.length > 0) {
      console.log('🔍 Processing', bookmarks.length, 'bookmarks for company suggestions');
      
      bookmarks.forEach(b => {
        // bookmarks use 'tickers' array, not 'ticker' field
        const ticker = b.tickers?.[0] || b.primary_ticker || b.tickers_discovered?.[0];
        
        // Must have a ticker
        if (!ticker) return;
        
        // Get company name from multiple sources - prioritize FREE internal data first:
        // 1. company_intelligence.company_name (internal, free)
        // 2. company_name field (internal, free)
        // 3. Twelve Data cache (tickerMetricsCache) - only if internal data not available
        const tdMetrics = tickerMetricsCache[ticker.toUpperCase()];
        const actualCompanyName = b.company_intelligence?.company_name || b.company_name || tdMetrics?.company_name;
        
        if (actualCompanyName && typeof actualCompanyName === 'string' && actualCompanyName.trim()) {
          // Has a real company name - index by it
          const companyKey = actualCompanyName.toLowerCase();
          if (!companyMap.has(companyKey)) {
            companyMap.set(companyKey, { name: actualCompanyName, ticker: ticker.toUpperCase(), source: 'idea' });
          }
        }
        
        // Always index by ticker symbol so searching "mcd" finds "MCDIF"
        const tickerKey = ticker.toLowerCase();
        if (!companyMap.has(tickerKey)) {
          // Use company name as display if available, otherwise ticker
          const displayName = actualCompanyName || ticker.toUpperCase();
          companyMap.set(tickerKey, { name: displayName, ticker: ticker.toUpperCase(), source: 'idea' });
        }
      });
      console.log('🔍 After processing bookmarks, companyMap size:', companyMap.size);
    }
    
    // From watchlists
    if (watchlists && watchlists.length > 0) {
      console.log('🔍 Processing', watchlists.length, 'watchlists for company suggestions');
      watchlists.forEach(w => {
        if (w.tickers && Array.isArray(w.tickers)) {
          w.tickers.forEach(t => {
            let ticker, companyName;
            
            // Handle both string and object tickers
            if (typeof t === 'string') {
              ticker = t;
              companyName = null;
            } else {
              ticker = t.ticker || t.symbol;
              companyName = t.company_name || t.name;
            }
            
            if (!ticker) return;
            
            // Index by ticker - display company name if available
            const tickerKey = ticker.toLowerCase();
            if (!companyMap.has(tickerKey)) {
              const displayName = companyName && typeof companyName === 'string' && companyName.trim()
                ? companyName
                : ticker.toUpperCase();
              companyMap.set(tickerKey, { name: displayName, ticker: ticker.toUpperCase(), source: 'watchlist' });
            }
            
            // Also index by company name if available
            if (companyName && typeof companyName === 'string' && companyName.trim()) {
              const companyKey = companyName.toLowerCase();
              if (!companyMap.has(companyKey)) {
                companyMap.set(companyKey, { name: companyName, ticker: ticker.toUpperCase(), source: 'watchlist' });
              }
            }
          });
        }
      });
      console.log('🔍 After processing watchlists, companyMap size:', companyMap.size);
    }
    
    // From research schedule
    if (researchSchedule && researchSchedule.length > 0) {
      console.log('🔍 Processing', researchSchedule.length, 'schedule items for company suggestions');
      researchSchedule.forEach(s => {
        const companyName = s.company_name;
        const ticker = s.ticker;
        
        // Must have a ticker
        if (!ticker) return;
        
        // Index by ticker
        const tickerKey = ticker.toLowerCase();
        if (!companyMap.has(tickerKey)) {
          const displayName = companyName && typeof companyName === 'string' && companyName.trim()
            ? companyName
            : ticker.toUpperCase();
          companyMap.set(tickerKey, { name: displayName, ticker: ticker.toUpperCase(), source: 'schedule' });
        }
        
        // Also index by company name if available
        if (companyName && typeof companyName === 'string' && companyName.trim()) {
          const companyKey = companyName.toLowerCase();
          if (!companyMap.has(companyKey)) {
            companyMap.set(companyKey, { name: companyName, ticker: ticker.toUpperCase(), source: 'schedule' });
          }
        }
      });
      console.log('🔍 After processing schedule, companyMap size:', companyMap.size);
    }
    
    // Filter matches
    const matches = [];
    companyMap.forEach((value, key) => {
      if (key.includes(query) || value.ticker.toLowerCase().includes(query)) {
        matches.push(value);
      }
    });
    
    console.log('🔍 Found', matches.length, 'matches for query:', query, 'Total companies in map:', companyMap.size);
    if (matches.length > 0) {
      console.log('🔍 Sample matches:', matches.slice(0, 3));
    }
    
    // Sort by relevance (starts with > contains)
    matches.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(query) ? 0 : 1;
      const bStarts = b.name.toLowerCase().startsWith(query) ? 0 : 1;
      return aStarts - bStarts || a.name.localeCompare(b.name);
    });
    
    const finalSuggestions = matches.slice(0, 6);
    console.log('🔍 Setting companySuggestions to:', finalSuggestions);
    setCompanySuggestions(finalSuggestions);
  }, [searchQuery, bookmarks, watchlists, researchSchedule, tickerMetricsCache]);
  
  // Toggle completion status for a schedule item
  const toggleScheduleItemComplete = async (itemKey, ticker, scheduledDate) => {
    console.log('toggleScheduleItemComplete called:', { itemKey, ticker, scheduledDate });
    
    // Use standardized key format for consistency
    const standardKey = getScheduleItemKey(ticker, scheduledDate);
    
    // Optimistically update local state - check if currently completed
    const isCompleting = !completedScheduleItems.has(standardKey) && 
                         !completedScheduleItems.has(itemKey);  // Also check legacy key
    
    setCompletedScheduleItems(prev => {
      const newSet = new Set(prev);
      // Remove any legacy keys for this item
      const legacyKeys = [
        itemKey,
        `12m-${ticker}-${scheduledDate}`,
        `list-${ticker}-${scheduledDate}`,
        `folder-${ticker}-${scheduledDate}`,
        `mobile-${ticker}-${scheduledDate}`,
        `cal-${ticker}-${scheduledDate}`,
        `az-${ticker}-${scheduledDate}`
      ];
      legacyKeys.forEach(key => newSet.delete(key));
      
      // Add/remove the standard key
      if (isCompleting) {
        newSet.add(standardKey);
      }
      // If uncompleting, we already deleted all keys above
      
      // Save to localStorage with today's date (use local time, not UTC)
      const now = new Date();
      const today = now.getFullYear() + '-' + 
        String(now.getMonth() + 1).padStart(2, '0') + '-' + 
        String(now.getDate()).padStart(2, '0');
      localStorage.setItem('completedScheduleItems', JSON.stringify({
        date: today,
        items: Array.from(newSet)
      }));
      return newSet;
    });
    
    // Optimistically update the pending count
    setTotalPendingIdeas(prev => isCompleting ? prev - 1 : prev + 1);
    
    // Persist to database
    try {
      const userId = user?.user_id || user?.id || user?.email || user;
      console.log('Persisting to DB:', { userId, ticker, scheduledDate, isCompleting });
      if (userId && ticker && scheduledDate) {
        const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}/mark-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticker: ticker,
            scheduled_date: scheduledDate,
            completed: isCompleting
          })
        });
        const data = await response.json();
        console.log('Mark complete response:', data);
        
        // Refresh schedule data but skip count update to preserve optimistic update
        if (data.success) {
          fetchResearchSchedule(true);  // Skip count update - trust our optimistic update
        }
      } else {
        console.warn('Missing data for mark-complete:', { userId, ticker, scheduledDate });
      }
    } catch (error) {
      console.error('Failed to persist completion status:', error);
      // Revert optimistic update on error
      setTotalPendingIdeas(prev => isCompleting ? prev + 1 : prev - 1);
    }
  };
  
  // Add to Schedule modal state
  const [scheduleModalTicker, setScheduleModalTicker] = useState('');
  const [scheduleModalTickerSuggestions, setScheduleModalTickerSuggestions] = useState([]);
  const [scheduleModalDate, setScheduleModalDate] = useState('');
  const [scheduleModalTime, setScheduleModalTime] = useState('08:00');
  const [scheduleModalLoading, setScheduleModalLoading] = useState(false);

  // Close schedule item dropdown when clicking outside
  useEffect(() => {
    const handleScheduleDropdownClick = (event) => {
      // Don't close if clicking on schedule item rows (they handle their own toggle)
      if (event.target.closest('[data-schedule-item]')) {
        return;
      }
      // Don't close if clicking inside the schedule dropdown content
      if (event.target.closest('.schedule-item-dropdown')) {
        return;
      }
      // Don't close if clicking inside the original content modal or any modal overlay
      if (event.target.closest('[data-original-content-modal]') || event.target.closest('.fixed.inset-0')) {
        return;
      }
      if (expandedScheduleItem) {
        setExpandedScheduleItem(null);
      }
    };
    
    document.addEventListener('mousedown', handleScheduleDropdownClick);
    return () => document.removeEventListener('mousedown', handleScheduleDropdownClick);
  }, [expandedScheduleItem]);

  // Groups management state
  const [userGroups, setUserGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupView, setGroupView] = useState('my'); // 'my' or 'invited' - for tab toggle
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupMembers, setNewGroupMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});  // Track which groups are expanded
  const [groupMembers, setGroupMembers] = useState({});  // Store members for each group
  const [groupSharedIdeas, setGroupSharedIdeas] = useState({});  // Store shared ideas for each group
  const [expandedGroupIdeas, setExpandedGroupIdeas] = useState({});  // Track which groups' ideas are expanded
  const [selectedGroupIdeas, setSelectedGroupIdeas] = useState(null);  // { id, name, member: { id, name } | null } - for viewing group/member ideas
  const [pendingInvitations, setPendingInvitations] = useState([]); // Invitations I received
  const [sentInvitations, setSentInvitations] = useState([]); // Invitations I sent (for groups I admin)
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [shareToGroup, setShareToGroup] = useState(false); // Toggle between individual and group
  const [shareRecipientStatus, setShareRecipientStatus] = useState(null); // { exists: bool, message: string }
  const [checkingRecipient, setCheckingRecipient] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteGroupId, setInviteGroupId] = useState('');
  const [showInviteGroupDropdown, setShowInviteGroupDropdown] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null); // Track which group is being edited
  const [editGroupName, setEditGroupName] = useState(''); // Store the edited group name
  const [groupSortBy, setGroupSortBy] = useState('name'); // Sort groups by: name, members, newest

  // Swipe handlers for tab navigation - DISABLED due to unintended activations
  const dashboardSwipeHandlers = useSwipe(
    () => {}, // Disabled
    () => {}  // Disabled
  );
  
  const pollSwipeHandlers = useSwipe(
    () => {}, // Disabled
    () => {}  // Disabled
  );
  
  const groupSwipeHandlers = useSwipe(
    () => {}, // Disabled
    () => {}  // Disabled
  );

  const settingsSwipeHandlers = useSwipe(
    () => {}, // Disabled
    () => {}  // Disabled
  );

  const [filesView, setFilesView] = useState('companies');
  const filesSwipeHandlers = useSwipe(
    () => {}, // Disabled
    () => {}  // Disabled
  );

// Twitter Account Section Component
function TwitterAccountSection({ user, isDarkMode, API_BASE_URL, onClose, showNotification, setShowPricingModal }) {
  const [hasImportedBefore, setHasImportedBefore] = useState(false);
  const [dailyQuota, setDailyQuota] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [importEligibility, setImportEligibility] = useState(null);
  const importInProgressRef = useRef(false); // Guard against double-clicks
  
  // Fetch import eligibility on mount
  useEffect(() => {
    const fetchEligibility = async () => {
      // Prefer id (canonical identifier for posts); fall back to email
      const userIdentifier = user?.id || user?.user_id || user?.email;
      if (!userIdentifier) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/import/eligibility/${encodeURIComponent(userIdentifier)}`);
        if (response.ok) {
          const data = await response.json();
          setImportEligibility(data);
          setHasImportedBefore(data.has_used_free_import);
        }
      } catch (error) {
        console.error('Error fetching import eligibility:', error);
      }
    };
    fetchEligibility();
  }, [user?.email, user?.id, API_BASE_URL]);
  
  // DEV: Reset database handler
  const handleResetDatabase = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE ALL investment ideas and reset the import counter. Are you sure?')) {
      return;
    }
    
    setResetting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dev/reset-database`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Reset complete!\n\n• Deleted ${data.deleted_ideas} ideas\n• Reset counter for ${data.users_reset} users`);
        // Clear localStorage
        localStorage.removeItem('tickler_import_progress');
        localStorage.removeItem('tickler_importing');
        // Reload page to refresh dashboard
        window.location.reload();
      } else {
        alert('❌ Reset failed. Check console for details.');
      }
    } catch (error) {
      console.error('Reset error:', error);
      alert('❌ Reset failed: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  // User is already authenticated with OAuth 2.0 from login
  // Check if user has X account linked
  const isXLinked = user?.x_username;

  if (!isXLinked) {
    // User logged in via Google or demo - show info about X login
    return (
      <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-[#1a2235] border-slate-700 text-white' : 'bg-white border-slate-200'} border`}>
        <div className="text-center">
          <svg className={`w-14 h-14 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          <h5 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            X Account Not Connected
          </h5>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            To import bookmarks, please log in with your X account
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Note: You'll need to logout and "Continue with X" to access bookmarks import
          </p>
        </div>
      </div>
    );
  }

  // User logged in with X - show connected state with bookmark import
  // X Identity display per X Brand Guidelines: Avatar + @handle + X Logo
  return (
    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-[#1a2235] border-slate-700 text-white' : 'bg-white border-slate-200'} border`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* X Profile Avatar */}
          {user.x_profile_image_url && (
            <img 
              src={user.x_profile_image_url} 
              alt={`@${user.x_username} on X`}
              className="w-12 h-12 rounded-full"
            />
          )}
          <div>
            {/* X Logo + Display Name */}
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-label="X logo">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {user.x_name || user.x_username}
              </p>
            </div>
            {/* X @handle */}
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              @{user.x_username}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
            </svg>
            Connected to X
          </span>
        </div>
      </div>
      
      {/* Import X Bookmarks Button */}
      <div className="space-y-2">
        {/* Freemium Status Banner */}
        {importEligibility && (
          <div className={`text-xs text-center pb-2 rounded-lg px-3 py-2 ${
            importEligibility.is_subscriber 
              ? (isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700')
              : importEligibility.has_used_free_import
                ? (isDarkMode ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700')
                : (isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-50 text-blue-700')
          }`}>
            {importEligibility.is_subscriber ? (
              <>
                <span className="font-medium">Pro Subscriber</span>
                <span className="mx-1">•</span>
                <span>{importEligibility.total_posts?.toLocaleString() || 0} / {importEligibility.posts_limit?.toLocaleString() || '1,000'} posts</span>
                {importEligibility.posts_remaining && (
                  <span className="opacity-70 ml-1">({importEligibility.posts_remaining} remaining)</span>
                )}
              </>
            ) : importEligibility.has_used_free_import ? (
              <>
                <span className="font-medium">Free import used</span>
                <span className="mx-1">•</span>
                <span>Subscribe to sync new bookmarks</span>
              </>
            ) : (
              <>
                <span className="font-medium">Free Trial</span>
                <span className="mx-1">•</span>
                <span>Import your first 50 bookmarks free</span>
              </>
            )}
          </div>
        )}
        {dailyQuota && (
          <div className={`text-xs text-center pb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Daily quota: {dailyQuota.used}/{dailyQuota.limit} bookmarks
            {dailyQuota.remaining > 0 && (
              <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}> ({dailyQuota.remaining} remaining)</span>
            )}
          </div>
        )}
        <button
        onClick={async () => {
          // Prevent double-clicks using both ref AND localStorage
          const importLockKey = 'tickler_import_lock';
          const lastImportTime = localStorage.getItem(importLockKey);
          const now = Date.now();
          
          // Block if import was triggered in last 30 seconds
          if (lastImportTime && (now - parseInt(lastImportTime)) < 30000) {
            console.log('Import recently triggered, ignoring click');
            showNotification('Import already in progress. Please wait...', 'info', 3000);
            return;
          }
          
          if (importInProgressRef.current) {
            console.log('Import already in progress, ignoring click');
            showNotification('Import already in progress. Please wait...', 'info', 3000);
            return;
          }
          
          // FREEMIUM CHECK: If user has used free import and is not a subscriber, show paywall
          if (importEligibility && !importEligibility.can_import && importEligibility.show_paywall) {
            // Close the X account modal first
            if (onClose) onClose();
            // Show pricing modal for subscription (no notification, the modal is enough)
            if (setShowPricingModal) {
              setShowPricingModal(true);
            }
            return;
          }
          
          // Set both guards immediately
          importInProgressRef.current = true;
          localStorage.setItem(importLockKey, now.toString());
          
          const isFirstTime = !hasImportedBefore;
          
          // Close the modal immediately
          if (onClose) onClose();
          
          // Show immediate notification (no emoji)
          showNotification('Importing bookmarks in the background. You\'ll receive an email when complete!', 'success', 5000);
          
          // Start WebSocket connection for real-time updates
          setImporting(true);
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/x/import-bookmarks`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                email: user.email || user.id,
                max_results: 50,
                is_initial_import: isFirstTime,
                send_completion_email: true
              })
            });
            
            if (response.ok) {
              setHasImportedBefore(true);
              // Refresh eligibility after successful import - user has now used their free import
              const userIdentifier = user.id || user.user_id || user.email;
              try {
                const eligibilityResponse = await fetch(`${API_BASE_URL}/api/import/eligibility/${encodeURIComponent(userIdentifier)}`);
                if (eligibilityResponse.ok) {
                  const newEligibility = await eligibilityResponse.json();
                  console.log('📊 Post-import eligibility:', newEligibility);
                  setImportEligibility(newEligibility);
                  
                  // If this was their free import and they're not a subscriber, show the post-import modal
                  if (newEligibility.has_used_free_import && !newEligibility.is_subscriber) {
                    console.log('🎉 Showing post-import modal and subscribe banner');
                    // Delay slightly so they see the success notification first
                    setTimeout(() => {
                      setShowPostImportModal(true);
                      setShowSubscribeBanner(true); // Persist the banner
                    }, 2000);
                  } else {
                    console.log('❌ Not showing modal - has_used_free_import:', newEligibility.has_used_free_import, 'is_subscriber:', newEligibility.is_subscriber);
                  }
                }
              } catch (e) {
                console.error('Failed to refresh eligibility:', e);
              }
            } else {
              // Import didn't start - hide the progress modal and release the lock
              setImporting(false);
              localStorage.removeItem('tickler_importing');
              localStorage.removeItem('tickler_import_progress');
              importInProgressRef.current = false;
              localStorage.removeItem(importLockKey);
              const error = await response.json();
              if (response.status === 402) {
                // Payment required - close import modal and show subscription modal
                setImporting(false);  // Stop the importing state
                if (onClose) onClose();  // Close the X account modal
                // Show pricing modal for subscription (no notification spam)
                if (setShowPricingModal) {
                  setShowPricingModal(true);
                }
                // Refresh eligibility to update button state
                const userIdentifier = user.id || user.user_id || user.email;
                try {
                  const eligibilityResponse = await fetch(`${API_BASE_URL}/api/import/eligibility/${encodeURIComponent(userIdentifier)}`);
                  if (eligibilityResponse.ok) {
                    const newEligibility = await eligibilityResponse.json();
                    setImportEligibility(newEligibility);
                  }
                } catch (e) {
                  console.error('Failed to refresh eligibility:', e);
                }
              } else if (response.status === 429) {
                const errorDetail = typeof error.detail === 'object' ? error.detail : { message: error.detail };
                if (errorDetail.error === 'post_limit_reached') {
                  showNotification(errorDetail.message || `You've reached the maximum of ${errorDetail.posts_limit || 1000} saved posts.`, 'error', 5000);
                } else {
                  showNotification(`Daily limit reached: ${errorDetail.message || String(error.detail)}`, 'error', 5000);
                }
              } else {
                // Handle any other error - make sure to extract message properly
                let errorMessage = 'Failed to import';
                if (error.detail) {
                  if (typeof error.detail === 'string') {
                    errorMessage = error.detail;
                  } else if (typeof error.detail === 'object' && error.detail.message) {
                    errorMessage = error.detail.message;
                  } else if (typeof error.detail === 'object') {
                    errorMessage = JSON.stringify(error.detail);
                  }
                } else if (error.message) {
                  errorMessage = error.message;
                }
                showNotification(`Error: ${errorMessage}`, 'error', 5000);
              }
            }
          } catch (error) {
            console.error('Error importing bookmarks:', error);
            setImporting(false);
            localStorage.removeItem('tickler_importing');
            localStorage.removeItem('tickler_import_progress');
            showNotification('Failed to start import. Please try again.', 'error', 5000);
          } finally {
            // Reset guard after a delay to allow re-import later
            setTimeout(() => {
              importInProgressRef.current = false;
              localStorage.removeItem(importLockKey);
            }, 30000); // 30 second cooldown
          }
        }}
        className={`w-full px-4 py-4 rounded-lg font-medium transition-all flex flex-col items-center justify-center gap-2 ${
          importEligibility && !importEligibility.can_import && importEligibility.show_paywall
            ? (isDarkMode 
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white')
            : (isDarkMode 
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white')
        }`}
      >
        {importEligibility && !importEligibility.can_import && importEligibility.show_paywall ? (
          <>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="font-medium">Subscribe to Sync</span>
            <span className="text-xs opacity-70 text-center">Get automatic nightly imports</span>
          </>
        ) : (
          <>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span className="font-medium">{hasImportedBefore ? 'Import Bookmarks' : 'Import Your First 50 Bookmarks'}</span>
            <span className="text-xs opacity-70 text-center">
              {hasImportedBefore ? 'Processes in background • Email when done' : 'Free • See what Tickler can do'}
            </span>
          </>
        )}
      </button>
      
      {/* DEV: Reset Database Button */}
      <button
        onClick={handleResetDatabase}
        disabled={resetting}
        className={`w-full mt-4 py-1 px-2 rounded text-xs transition-all ${
          isDarkMode 
            ? 'bg-red-900/20 hover:bg-red-900/40 text-red-400 border border-red-800/50'
            : 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'
        } ${resetting ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="DEV ONLY: Delete all ideas and reset import counter"
      >
        {resetting ? '⏳ Resetting...' : '🗑️ DEV: Reset DB & Counter'}
      </button>
      </div>
    </div>
  );
}

// Back to MainDashboard component - state declarations below
  const [inviteEmail, setInviteEmail] = useState('');

  // Subscription management state
  const [accountSubscription, setAccountSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  // showPricingModal moved to top of MainDashboard component
  const [selectedAction, setSelectedAction] = useState(null); // 'upgrade' or 'downgrade'
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pricingViewType, setPricingViewType] = useState('monthly'); // 'monthly' or 'annual'

  // Settings page modal states
  const [showBookmarkletModal, setShowBookmarkletModal] = useState(false);
  const [showPublisherButtonModal, setShowPublisherButtonModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [showEmailInstructionsModal, setShowEmailInstructionsModal] = useState(false);
  const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
  const [showBillingInfoModal, setShowBillingInfoModal] = useState(false);
  const [showKeyboardShortcutsModal, setShowKeyboardShortcutsModal] = useState(false);
  
  // Ref for bookmarklet button (vanilla JS creation to bypass React security)
  const bookmarkletButtonRef = useRef(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showXAccountModal, setShowXAccountModal] = useState(false);
  const [showReauthorizeModal, setShowReauthorizeModal] = useState(false); // Show when X token expires

  // Keyboard shortcuts handler - placed after all modal states are defined
  useEffect(() => {
    console.log('🎹 Keyboard shortcuts handler mounted');
    
    const handleKeyDown = (event) => {
      // Log ALL key presses first
      console.log('🎹 Key pressed:', event.key, '| code:', event.code, '| Shift:', event.shiftKey, '| Ctrl:', event.ctrlKey, '| Target:', event.target.tagName);
      
      // Don't trigger shortcuts when typing in input fields
      const activeElement = document.activeElement;
      const targetTag = event.target.tagName;
      const isTyping = targetTag === 'INPUT' || targetTag === 'TEXTAREA' || event.target.isContentEditable;
      
      // Allow Escape to work even when typing (to close modals/blur inputs)
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        console.log('🎹 Escape pressed - closing modals');
        
        // Close modals that are defined at this point in the code
        let modalClosed = false;
        
        // Search/Mobile search
        if (showMobileSearch) { setShowMobileSearch(false); setSearchQuery(''); setSearchIdeaFilter(null); modalClosed = true; }
        
        // Content modals (defined early)
        else if (showOriginalContentModal) { setShowOriginalContentModal(false); modalClosed = true; }
        else if (showDocumentModal) { setShowDocumentModal(false); modalClosed = true; }
        else if (showShareModal) { setShowShareModal(false); modalClosed = true; }
        else if (showAIReactionModal) { setShowAIReactionModal(false); modalClosed = true; }
        
        // Pricing/billing (defined early)
        else if (showPricingModal) { setShowPricingModal(false); modalClosed = true; }
        else if (showCancelModal) { setShowCancelModal(false); modalClosed = true; }
        else if (showBillingInfoModal) { setShowBillingInfoModal(false); modalClosed = true; }
        
        // Settings modals
        else if (showEmailConfigModal) { setShowEmailConfigModal(false); modalClosed = true; }
        else if (showKeyboardShortcutsModal) { setShowKeyboardShortcutsModal(false); modalClosed = true; }
        else if (showBookmarkletModal) { setShowBookmarkletModal(false); modalClosed = true; }
        else if (showPublisherButtonModal) { setShowPublisherButtonModal(false); modalClosed = true; }
        else if (showExtensionModal) { setShowExtensionModal(false); modalClosed = true; }
        else if (showEmailInstructionsModal) { setShowEmailInstructionsModal(false); modalClosed = true; }
        else if (showXAccountModal) { setShowXAccountModal(false); modalClosed = true; }
        
        // Help modals
        else if (showSuggestionsModal) { setShowSuggestionsModal(false); modalClosed = true; }
        else if (showHelpModal) { setShowHelpModal(false); modalClosed = true; }
        else if (showFAQModal) { setShowFAQModal(false); modalClosed = true; }
        
        // Group/sharing modals
        else if (showCreateGroupModal) { setShowCreateGroupModal(false); modalClosed = true; }
        else if (showInviteModal) { setShowInviteModal(false); modalClosed = true; }
        else if (showSharedByMeModal) { setShowSharedByMeModal(false); modalClosed = true; }
        else if (showReceiveModal) { setShowReceiveModal(false); modalClosed = true; }
        
        // Landing modal
        else if (showLandingModal) { setShowLandingModal(false); modalClosed = true; }
        
        // Watchlist modal
        else if (watchlistModalOpen) { setWatchlistModalOpen(false); modalClosed = true; }
        
        // If no modal was closed, try to close any visible modal
        if (!modalClosed) {
          // Try different backdrop patterns used in the app
          const backdrop = document.querySelector('.fixed.inset-0');
          if (backdrop && backdrop.classList.contains('bg-black') || 
              (backdrop && getComputedStyle(backdrop).backgroundColor.includes('0, 0, 0'))) {
            console.log('🎹 Found modal backdrop, trying to close');
            
            // Find close button - look for × button or buttons with close-like text
            const allButtons = backdrop.querySelectorAll('button');
            let closeBtn = null;
            for (const btn of allButtons) {
              const text = btn.textContent?.trim();
              if (text === '×' || text === '✕' || text === 'Close' || text === 'Cancel' || 
                  btn.getAttribute('aria-label')?.toLowerCase().includes('close')) {
                closeBtn = btn;
                break;
              }
            }
            
            if (closeBtn) {
              console.log('🎹 Clicking close button:', closeBtn.textContent);
              closeBtn.click();
              modalClosed = true;
            } else {
              // Fallback: Dispatch mousedown on backdrop itself
              console.log('🎹 No close button found, dispatching mousedown on backdrop');
              const mousedownEvent = new MouseEvent('mousedown', {
                bubbles: false,
                cancelable: true,
                view: window
              });
              Object.defineProperty(mousedownEvent, 'target', { value: backdrop });
              backdrop.dispatchEvent(mousedownEvent);
              modalClosed = true;
            }
          }
        }
        
        // If still no modal closed, blur active input
        if (!modalClosed && isTyping) {
          activeElement.blur();
        }
        
        return;
      }
      
      // Don't trigger other shortcuts when typing
      if (isTyping) {
        console.log('🎹 Ignoring - user is typing in:', targetTag);
        return;
      }
      
      // ? - Show keyboard shortcuts help (check for both ? and Shift+/)
      if (event.key === '?' || (event.code === 'Slash' && event.shiftKey)) {
        console.log('🎹 ? detected - opening shortcuts modal');
        event.preventDefault();
        event.stopPropagation();
        setShowKeyboardShortcutsModal(true);
        return;
      }
      
      // / - Focus search (Slash key without shift)
      if ((event.key === '/' || event.code === 'Slash') && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        console.log('🎹 / detected - opening search');
        event.preventDefault();
        event.stopPropagation();
        setShowMobileSearch(true);
        return;
      }
      
      // Cmd/Ctrl + K - Focus search (alternative)
      if ((event.key === 'k' || event.code === 'KeyK') && (event.ctrlKey || event.metaKey)) {
        console.log('🎹 Cmd+K detected - opening search');
        event.preventDefault();
        event.stopPropagation();
        setShowMobileSearch(true);
        return;
      }
      
      // D - Toggle dark mode (lowercase d only)
      if ((event.key === 'd' || event.code === 'KeyD') && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        console.log('🎹 D detected - toggling dark mode');
        event.preventDefault();
        event.stopPropagation();
        setIsDarkMode(prev => !prev);
        return;
      }
    };
    
    // Use capture phase to catch events before they reach other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      console.log('🎹 Keyboard shortcuts handler unmounted');
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    // Modal states for Escape key (only those defined before this useEffect)
    showMobileSearch,
    showOriginalContentModal,
    showDocumentModal,
    showShareModal,
    showAIReactionModal,
    showPricingModal,
    showCancelModal,
    showBillingInfoModal,
    showEmailConfigModal,
    showKeyboardShortcutsModal,
    showBookmarkletModal,
    showPublisherButtonModal,
    showExtensionModal,
    showEmailInstructionsModal,
    showXAccountModal,
    showSuggestionsModal,
    showHelpModal,
    showFAQModal,
    showCreateGroupModal,
    showInviteModal,
    showSharedByMeModal,
    showReceiveModal,
    showLandingModal,
    watchlistModalOpen
  ]);

  const fetchEmailSettings = async () => {
    try {
      // Extract userId properly - user might be an object or string
      const userId = user?.id || user?.email || user;
      if (!userId) return;
      
      // Try new email preferences first
      let response = await fetch(`${API_BASE_URL}/api/email-preferences/${userId}`);
      if (response.ok) {
        const settings = await response.json();
        setEmailSettings(prev => ({...prev, ...settings}));
        return;
      }
      
      // Fallback to old settings
      response = await fetch(`${API_BASE_URL}/api/user/email-settings/${userId}`);
      if (response.ok) {
        const settings = await response.json();
        setEmailSettings(prev => ({...prev, ...settings}));
      }
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const fetchScheduledResearch = async () => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) {
        // Don't mark as initialized if we can't fetch
        return;
      }
      
      setScheduledResearchLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/scheduled-research/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setScheduledResearch(data.scheduled_research || []);
      }
      // Mark as initialized only after successful fetch attempt
      setScheduleDataInitialized(true);
      scheduleTabHasRendered.current = true; // Mark that we can now show content
      // Delay showing content to ensure loading spinner is visible
      setTimeout(() => setScheduleTabReady(true), 300);
    } catch (error) {
      console.error('Error fetching scheduled research:', error);
      // Still mark as initialized on error so we don't show spinner forever
      setScheduleDataInitialized(true);
      scheduleTabHasRendered.current = true;
      setTimeout(() => setScheduleTabReady(true), 300);
    } finally {
      setScheduledResearchLoading(false);
    }
  };

  const cancelScheduledResearch = async (reminderId, ticker) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/scheduled-research/${reminderId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showNotification(`Cancelled research reminder for ${ticker}`, 'success');
        fetchScheduledResearch(); // Refresh list
      } else {
        showNotification('Failed to cancel reminder', 'error');
      }
    } catch (error) {
      console.error('Error cancelling scheduled research:', error);
      showNotification('Error cancelling reminder', 'error');
    }
  };

  const fetchDossierSettings = async () => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) {
        console.log('⚠️ fetchDossierSettings: No userId available');
        return;
      }
      
      // Don't fetch if currently saving - prevents race condition
      if (dossierSettingsSaving) {
        console.log('⚠️ fetchDossierSettings: Skipping fetch - save in progress');
        return;
      }
      
      // Don't fetch if settings were saved recently (within 5 seconds) - prevents overwriting user changes
      const timeSinceLastSave = Date.now() - dossierSettingsLastSaved;
      if (timeSinceLastSave < 5000) {
        console.log(`⚠️ fetchDossierSettings: Skipping fetch - settings saved ${timeSinceLastSave}ms ago`);
        return;
      }
      
      console.log('🔧 fetchDossierSettings: Fetching for userId:', userId);
      setDossierSettingsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/dossier-settings/${userId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('🔧 fetchDossierSettings: Received:', data);
        setDossierSettings(data);
      } else {
        console.log('❌ fetchDossierSettings: API error:', response.status);
      }
    } catch (error) {
      console.error('Error fetching dossier settings:', error);
    } finally {
      setDossierSettingsLoading(false);
    }
  };

  const saveDossierSettings = async (settingsOverride = null, showNotificationBubble = true) => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) return;
      
      // Use override if provided (for immediate saves after state changes)
      const settingsToSave = settingsOverride || dossierSettings;
      
      setDossierSettingsSaving(true);
      setDossierSettingsLastSaved(Date.now()); // Mark save time to prevent fetch overwriting
      
      console.log('💾 saveDossierSettings: Saving settings:', settingsToSave);
      
      const response = await fetch(`${API_BASE_URL}/api/dossier-settings/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsToSave)
      });
      
      if (response.ok) {
        console.log('✅ saveDossierSettings: Save successful');
        if (showNotificationBubble) {
          const data = await response.json();
          showNotification(data.message || 'Auto-schedule settings saved', 'success');
        }
      } else {
        console.log('❌ saveDossierSettings: Save failed:', response.status);
        if (showNotificationBubble) {
          showNotification('Failed to save settings', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving auto-schedule settings:', error);
      if (showNotificationBubble) {
        showNotification('Error saving settings', 'error');
      }
    } finally {
      setDossierSettingsSaving(false);
    }
  };

  const testDailyDossier = async () => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) {
        showNotification('Please log in to test auto-schedule', 'error');
        return;
      }
      
      showNotification('Sending test auto-schedule email...', 'info');
      
      const response = await fetch(`${API_BASE_URL}/api/test-auto-schedule-email/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification(data.message || 'Test email sent!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to send test email', 'error');
      }
    } catch (error) {
      console.error('Error sending test auto-schedule:', error);
      showNotification('Error sending test email', 'error');
    }
  };

  const toggleDossierDoc = (doc) => {
    setDossierSettings(prev => {
      const docs = prev.docs.includes(doc)
        ? prev.docs.filter(d => d !== doc)
        : [...prev.docs, doc];
      return { ...prev, docs };
    });
  };

  const fetchResearchSchedule = async (skipCountUpdate = false) => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) return;
      
      const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setResearchSchedule(data.schedule || []);
        // Update pending count from API (only counts status: 'scheduled' or null, excludes 'completed' and 'sent')
        // Skip count update when called right after mark-complete to preserve optimistic update
        if (!skipCountUpdate) {
          // Count items that are actually pending (not completed, not sent)
          const schedule = data.schedule || [];
          const pendingCount = schedule.filter(item => 
            item.status === 'scheduled' || item.status === null || item.status === undefined
          ).length;
          setTotalPendingIdeas(pendingCount);
        }
      }
    } catch (error) {
      console.error('Error fetching research schedule:', error);
    }
  };

  const generateResearchSchedule = async () => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) return;
      
      setScheduleGenerating(true);
      
      // First, get total ideas count to calculate ideas per day for 31-day schedule
      const countResponse = await fetch(`${API_BASE_URL}/api/dashboard-ideas/${userId}?page=1&page_size=1`);
      let totalIdeas = 0;
      if (countResponse.ok) {
        const countData = await countResponse.json();
        totalIdeas = countData.total_cards || 0;
      }
      
      // Always schedule across 31 days
      const ideasPerDay = Math.max(1, Math.ceil(totalIdeas / 31));
      
      const response = await fetch(`${API_BASE_URL}/api/research-schedule/generate/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ideas_per_day: ideasPerDay })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchedulePreviewData(data);
        setTotalPendingIdeas(data.total_ideas || 0);
        fetchResearchSchedule();
      } else {
        showNotification('Failed to generate schedule', 'error');
      }
    } catch (error) {
      console.error('Error generating schedule:', error);
      showNotification('Error generating schedule', 'error');
    } finally {
      setScheduleGenerating(false);
    }
  };

  const getIdeaScheduleDate = async (ideaId) => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) return null;
      
      const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}/idea/${ideaId}`);
      if (response.ok) {
        const data = await response.json();
        return data.scheduled ? data : null;
      }
      return null;
    } catch (error) {
      console.error('Error getting idea schedule:', error);
      return null;
    }
  };

  const rescheduleIdea = async (ideaId, newDate, newTime = '08:00') => {
    try {
      const userId = user?.id || user?.email || user;
      if (!userId) return false;
      
      const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}/idea/${ideaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_date: newDate, new_time: newTime })
      });
      
      if (response.ok) {
        showNotification(`Recycled to ${newDate}`, 'success');
        fetchResearchSchedule();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error rescheduling idea:', error);
      return false;
    }
  };

  const fetchDashboardIdeas = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/dashboard-ideas/${user?.id || 'sample_user'}`);
      if (response.ok) {
        const data = await response.json();
        setSchedulingDashboardIdeas(data.ideas || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard ideas:', error);
    }
  };

  const fetchResearchItems = async (page = 1, append = false) => {
    // Fetch Library items from dedicated research endpoint with pagination
    if (!user || !user.id) return;
    
    try {
      if (append) {
        setLibraryLoadingMore(true);
      }
      
      console.log(`🔄 Fetching library items page ${page} with filter: ${librarySourceFilter}`);
      const response = await fetch(`${API_BASE_URL}/api/research/${user.id}?page=${page}&limit=20&source_filter=${encodeURIComponent(librarySourceFilter)}`, {
        headers: {
          'X-User-ID': user.id || 'anonymous'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (append) {
          // Append to existing items for "Load More"
          setResearchItems(prev => [...prev, ...(data.research || [])]);
        } else {
          // Replace items for initial load or filter change
          setResearchItems(data.research || []);
        }
        
        setLibraryAvailableSources(data.available_sources || []);
        setLibraryPage(data.page || page);
        setLibraryHasMore(data.has_more || false);
        setLibraryTotalCount(data.total_count || 0);
        
        console.log(`✅ Fetched ${data.research?.length || 0} library items (page ${page}/${data.total_pages}, total: ${data.total_count})`);
      } else {
        console.error('Failed to fetch research items');
        if (!append) {
          setResearchItems([]);
          setLibraryAvailableSources([]);
        }
      }
    } catch (error) {
      console.error('Error fetching research items:', error);
      if (!append) {
        setResearchItems([]);
        setLibraryAvailableSources([]);
      }
    } finally {
      setLibraryLoadingMore(false);
    }
  };
  
  // Load more library items
  const loadMoreLibraryItems = () => {
    if (libraryHasMore && !libraryLoadingMore) {
      fetchResearchItems(libraryPage + 1, true);
    }
  };
  
  // Set up bookmarklet modal button with vanilla JS (bypass React security for javascript: URLs)
  // This runs whenever the modal becomes visible
  useEffect(() => {
    // Wait a bit for the modal to render and ref to be populated
    if (!showBookmarkletModal || !bookmarkletCode || !user) return;
    
    const timer = setTimeout(() => {
      if (bookmarkletButtonRef.current) {
        const container = bookmarkletButtonRef.current;
        container.innerHTML = ''; // Clear any existing content
        
        // Create the draggable bookmarklet button with inline styles (WORKING VERSION)
        const button = document.createElement('a');
        button.href = bookmarkletCode;
        button.textContent = '📁 Save to Tickler';
        button.draggable = true;
        button.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
          transition: all 0.2s ease;
          cursor: grab;
          border: 2px solid #fed7aa;
        `;
        
        // Add hover effects
        button.addEventListener('mouseenter', () => {
          button.style.transform = 'translateY(-2px)';
          button.style.boxShadow = '0 6px 20px rgba(249, 115, 22, 0.4)';
        });
        
        button.addEventListener('mouseleave', () => {
          button.style.transform = 'translateY(0)';
          button.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
        });
        
        // Add drag start effect
        button.addEventListener('dragstart', () => {
          button.style.cursor = 'grabbing';
          button.style.opacity = '0.8';
        });
        
        button.addEventListener('dragend', () => {
          button.style.cursor = 'grab';
          button.style.opacity = '1';
        });
        
        container.appendChild(button);
      }
    }, 100); // Small delay to ensure DOM is ready
    
    return () => clearTimeout(timer);
  }, [showBookmarkletModal, bookmarkletCode, user]);

  // Fetch research items when user changes or library source filter changes
  useEffect(() => {
    if (user) {
      // Reset pagination when filter changes
      setLibraryPage(1);
      setLibraryHasMore(true);
      fetchResearchItems(1, false);
    }
  }, [user, librarySourceFilter]);


  const reclassifyItem = async (bookmarkId, newClassification) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookmarks/${bookmarkId}/reclassify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ classification: newClassification })
      });
      
      if (response.ok) {
        // Update local state immediately for smooth UX
        setBookmarks(prev => prev.map(b => 
          b.id === bookmarkId ? { ...b, classification: newClassification, classification_confidence: 1.0 } : b
        ));
        
        // Handle both 'library' and 'research' (backward compatibility)
        if (newClassification === 'library' || newClassification === 'research') {
          // Add to research items
          const movedItem = bookmarks.find(b => b.id === bookmarkId);
          if (movedItem) {
            setResearchItems(prev => [{...movedItem, classification: newClassification, classification_confidence: 1.0}, ...prev]);
          }
        } else {
          // Remove from research items
          setResearchItems(prev => prev.filter(r => r.id !== bookmarkId));
        }
        
        // Show success message
        const targetTab = newClassification === 'idea' ? 'Ideas' : 'Library';
        console.log(`✅ Item moved to ${targetTab}`);
      }
    } catch (error) {
      console.error('Error reclassifying item:', error);
      alert('Failed to reclassify item');
    }
  };

  const saveEmailSettings = async (settingsOverride = null) => {
    setEmailSettingsLoading(true);
    try {
      const userId = user?.id || user?.email;
      const settingsToSave = settingsOverride || emailSettings;
      const response = await fetch(`${API_BASE_URL}/api/email-preferences/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsToSave)
      });
      
      if (response.ok) {
        // Success - no notification pop-up (user requested removal)
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      showNotification('Failed to save email settings', 'error');
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  const testWeeklyDigest = async () => {
    if (!emailSettings.email_address) {
      showNotification('Please enter an email address first and save your settings.', 'error');
      return;
    }
    
    try {
      const userId = user?.id || user?.email;
      const response = await fetch(`${API_BASE_URL}/api/send-weekly-digest/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        showNotification(`Test weekly digest sent! Check your inbox at ${emailSettings.email_address}`, 'success', 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Test digest error:', errorData);
        showNotification(`Failed to send test digest: ${errorData.detail || 'Please try again.'}`, 'error');
      }
    } catch (error) {
      console.error('Error sending test digest:', error);
      showNotification('Error sending test digest', 'error');
    }
  };

  const testDailyDigest = async () => {
    // Validate email is set
    if (!emailSettings.email_address) {
      showNotification('Please enter an email address first and save your settings.', 'error');
      return;
    }
    
    // Get user ID
    const userId = user?.id || user?.email;
    if (!userId) {
      showNotification('User not found. Please refresh the page.', 'error');
      return;
    }

    // Build API URL
    const url = `${API_BASE_URL}/api/send-daily-digest/${encodeURIComponent(userId)}`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json().catch(() => ({ detail: 'Unknown error' }));
      
      if (response.ok && data.success) {
        showNotification(`Test daily digest sent to ${emailSettings.email_address}!`, 'success', 5000);
      } else {
        showNotification(`Failed: ${data.detail || data.message || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      showNotification(`Network error: ${err.message}`, 'error');
    }
  };

  // Test digest email - tests daily if enabled, otherwise weekly
  const testDigestEmail = async () => {
    if (!emailSettings.email_address) {
      showNotification('Please enter an email address first and save your settings.', 'error');
      return;
    }
    
    // Prefer daily digest if enabled, otherwise test weekly
    if (emailSettings.daily_digest_enabled) {
      await testDailyDigest();
    } else if (emailSettings.weekly_digest_enabled) {
      await testWeeklyDigest();
    } else {
      showNotification('Please enable either daily or weekly digest first.', 'error');
    }
  };


  // Subscription management functions
  const fetchSubscriptionDetails = async () => {
    if (!user) return;
    
    try {
      setSubscriptionLoading(true);
      const userId = user.id || user; // Handle both object and string
      const response = await fetch(`${API_BASE_URL}/api/subscription/details/${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        setAccountSubscription(data);
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleUpgradeDowngrade = async () => {
    if (!selectedTier || !user) return;
    
    try {
      setSubscriptionLoading(true);
      const userId = user.id || user; // Handle both object and string
      const endpoint = selectedAction === 'upgrade' 
        ? `${API_BASE_URL}/api/subscription/upgrade`
        : `${API_BASE_URL}/api/subscription/downgrade`;
      
      const body = selectedAction === 'upgrade'
        ? { user_id: userId, new_tier: selectedTier, billing_cycle: selectedBillingCycle }
        : { user_id: userId, new_tier: selectedTier };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        await fetchSubscriptionDetails(); // Refresh subscription data
        setShowPricingModal(false);
        setSelectedTier(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to process subscription change');
      }
    } catch (error) {
      console.error('Error processing subscription change:', error);
      alert('Error processing subscription change');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    
    try {
      setSubscriptionLoading(true);
      const userId = user.id || user; // Handle both object and string
      const response = await fetch(`${API_BASE_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        await fetchSubscriptionDetails(); // Refresh subscription data
        setShowCancelModal(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Error cancelling subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const openPricingModal = (action, tier = null) => {
    setSelectedAction(action);
    setSelectedTier(tier);
    setShowPricingModal(true);
  };

  // Groups management functions
  const fetchGroups = async () => {
    console.log('🔍 FETCH GROUPS CALLED', { user, API_BASE_URL });
    
    if (!user) {
      console.error('❌ Cannot fetch groups: user is null/undefined');
      return;
    }
    
    try {
      setGroupsLoading(true);
      const url = `${API_BASE_URL}/api/groups/${user.id}`;
      console.log('📡 Making API call to:', url);
      
      const response = await fetch(url);
      console.log('📥 Fetch groups response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Groups fetched successfully:', data);
        console.log('📊 Setting userGroups to:', data.groups || []);
        setUserGroups(data.groups || []);
        console.log('✅ userGroups state updated');
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch groups. Status:', response.status, 'Error:', errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching groups:', error);
    } finally {
      setGroupsLoading(false);
      console.log('🏁 fetchGroups completed');
    }
  };

  const createGroup = async () => {
    console.log('CREATE GROUP CALLED', { newGroupName, newGroupDescription, userEmail: user?.email });
    
    if (!newGroupName.trim()) {
      showNotification('Please enter a group name', 'error');
      return;
    }

    try {
      setGroupsLoading(true);
      console.log('Making API request to create group...');
      
      const response = await fetch(`${API_BASE_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user?.id || 'anonymous'
        },
        body: JSON.stringify({
          name: newGroupName.trim(),
          description: newGroupDescription.trim(),
          creator_email: user?.email || null  // Send real email
        })
      });

      console.log('API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Group created successfully:', data);
        
        // Send invitations to all added members
        if (newGroupMembers.length > 0) {
          console.log(`Sending invitations to ${newGroupMembers.length} members...`);
          for (const email of newGroupMembers) {
            try {
              const inviteResponse = await fetch(`${API_BASE_URL}/api/groups/${data.group_id}/invite`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-User-ID': user?.id || 'anonymous'
                },
                body: JSON.stringify({ email })
              });
              
              if (inviteResponse.ok) {
                console.log(`✅ Invitation sent to ${email}`);
              } else {
                console.error(`❌ Failed to send invitation to ${email}`);
              }
            } catch (inviteError) {
              console.error(`Error sending invitation to ${email}:`, inviteError);
            }
          }
        }
        
        const memberText = newGroupMembers.length > 0 
          ? ` and invited ${newGroupMembers.length} member${newGroupMembers.length > 1 ? 's' : ''}`
          : '';
        showNotification(`Group "${data.name}" created successfully${memberText}!`, 'success', 5000);
        
        setNewGroupName('');
        setNewGroupDescription('');
        setNewGroupMembers([]);
        setNewMemberEmail('');
        setShowCreateGroupModal(false); // Close the modal
        await fetchGroups(); // Refresh the groups list
      } else {
        // Try to parse as JSON, fallback to text if that fails
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || 'Unknown error';
        } catch {
          const errorText = await response.text();
          errorMessage = `Server error (${response.status}): ${errorText.substring(0, 100)}`;
        }
        console.error('Failed to create group:', errorMessage);
        showNotification(`Failed to create group: ${errorMessage}`, 'error');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showNotification(`Error creating group: ${error.message}`, 'error');
    } finally {
      setGroupsLoading(false);
    }
  };


  const handleGenerateOnePageSummary = async (ticker) => {
    try {
      setSummaryLoading(true);
      setShowOnePageSummary(true);
      setOnePageSummaryData(null);
      
      const response = await fetch(`${API_BASE_URL}/api/one-page-summary/${ticker}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnePageSummaryData(data);
      } else {
        const error = await response.json();
        console.error('Failed to generate summary:', error);
        setOnePageSummaryData({
          success: false,
          error: error.detail || 'Failed to generate summary',
          ticker: ticker
        });
      }
    } catch (error) {
      console.error('Error generating one-page summary:', error);
      setOnePageSummaryData({
        success: false,
        error: error.message,
        ticker: ticker
      });
    } finally {
      setSummaryLoading(false);
    }
  };


  const inviteToGroup = async (groupId) => {
    // Open modal instead of prompt
    setInviteGroupId(groupId);
    setShowInviteModal(true);
  };

  const sendGroupInvitation = async () => {
    if (!inviteEmail || !inviteEmail.trim()) {
      showNotification('Please enter an email address', 'error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${inviteGroupId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user?.id || 'anonymous'
        },
        body: JSON.stringify({
          group_id: inviteGroupId,
          email: inviteEmail.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success', 5000);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteGroupId('');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to send invitation', 'error');
      }
    } catch (error) {
      console.error('Error inviting to group:', error);
      showNotification('Error sending invitation', 'error');
    }
  };

  const leaveGroup = async (groupId) => {
    const group = userGroups.find(g => g.group_id === groupId);
    if (!group) return;

    const isAdmin = group.user_role === 'admin';
    
    setConfirmModal({
      isOpen: true,
      title: 'Leave Group',
      message: `Are you sure you want to leave "${group.name}"?`,
      warningMessage: isAdmin ? 'You are an admin of this group. If you are the only admin and there are other members, you will need to transfer admin rights first.' : null,
      confirmText: 'Leave Group',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/leave`, {
            method: 'DELETE',
          });

          if (response.ok) {
            const data = await response.json();
            showNotification(data.message, 'success', 5000);
            await fetchGroups(); // Refresh the groups list
          } else {
            const error = await response.json();
            showNotification(error.detail || 'Failed to leave group', 'error');
            // DO NOT refresh groups list on error - group should still be there
            console.log('Leave group failed - group remains:', groupId);
          }
        } catch (error) {
          console.error('Error leaving group:', error);
          showNotification('Error leaving group', 'error');
          // DO NOT refresh on exception either
        }
      }
    });
  };

  const fetchPendingInvitations = async () => {
    if (!user) {
      console.log('❌ Cannot fetch invitations: user is null/undefined');
      return;
    }
    
    try {
      setInvitationsLoading(true);
      // Use the user's actual email if available, otherwise construct from user ID
      const userEmail = user.email || `${user.id}@example.com`;
      console.log('📨 Fetching pending invitations for email:', userEmail);
      
      // Fetch invitations I received
      const response = await fetch(`${API_BASE_URL}/api/group-invitations/${userEmail}`);
      console.log('📥 Fetch invitations response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received invitations fetched:', data);
        setPendingInvitations(data.invitations || []);
        console.log('📊 Pending invitations count:', data.invitations?.length || 0);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch invitations. Status:', response.status, 'Error:', errorText);
      }
      
      // Fetch invitations I sent (for groups I admin)
      const sentResponse = await fetch(`${API_BASE_URL}/api/group-invitations-sent/${user.id}`);
      if (sentResponse.ok) {
        const sentData = await sentResponse.json();
        console.log('✅ Sent invitations fetched:', sentData);
        setSentInvitations(sentData.invitations || []);
      }
    } catch (error) {
      console.error('❌ Error fetching invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const acceptInvitation = async (invitationId, groupName) => {
    const confirmed = window.confirm(
      `Do you want to join the group "${groupName}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/join/${invitationId}`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success', 5000);
        await fetchGroups(); // Refresh groups list
        await fetchPendingInvitations(); // Refresh invitations
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to accept invitation', 'error');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showNotification('Error accepting invitation', 'error');
    }
  };


  const rejectInvitation = async (invitationId, groupName) => {
    const confirmed = window.confirm(
      `Are you sure you want to reject the invitation to "${groupName}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/reject/${invitationId}`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success', 5000);
        await fetchPendingInvitations(); // Refresh invitations
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to reject invitation', 'error');
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      showNotification('Error rejecting invitation', 'error');
    }
  };



  // Toggle group expansion and fetch members if needed
  const toggleGroupExpansion = async (groupId) => {
    const isExpanded = expandedGroups[groupId];
    
    // Toggle the expansion state
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !isExpanded
    }));
    
    // Fetch members if expanding and not already loaded
    if (!isExpanded && !groupMembers[groupId]) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, {
          headers: { 'X-User-ID': user?.id || '' }
        });
        if (response.ok) {
          const data = await response.json();
          setGroupMembers(prev => ({
            ...prev,
            [groupId]: data.members || []
          }));
        } else {
          console.error('Failed to fetch group members');
        }
      } catch (error) {
        console.error('Error fetching group members:', error);
      }
    }
  };

  // Toggle shared ideas expansion for a group
  const toggleGroupIdeasExpansion = async (groupId) => {
    console.log('📋 toggleGroupIdeasExpansion called for:', groupId);
    const isExpanded = expandedGroupIdeas[groupId];
    console.log('📋 Current expanded state:', isExpanded, 'Will toggle to:', !isExpanded);
    
    // Toggle the expansion state
    setExpandedGroupIdeas(prev => {
      const newState = {
        ...prev,
        [groupId]: !isExpanded
      };
      console.log('📋 New expandedGroupIdeas state:', newState);
      return newState;
    });
    
    // Fetch shared ideas if expanding and not already loaded
    if (!isExpanded && !groupSharedIdeas[groupId]) {
      console.log('📋 Fetching shared ideas for group:', groupId);
      try {
        const userEmail = user?.email || '';
        const url = `${API_BASE_URL}/api/groups/${groupId}/shared-ideas${userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : ''}`;
        console.log('📋 Fetching from URL:', url);
        const response = await fetch(url);
        console.log('📋 Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('📋 Received shared ideas:', data);
          setGroupSharedIdeas(prev => ({
            ...prev,
            [groupId]: data.shared_ideas || []
          }));
        } else {
          console.error('Failed to fetch group shared ideas:', response.status);
          // Set empty array on error so loading state goes away
          setGroupSharedIdeas(prev => ({
            ...prev,
            [groupId]: []
          }));
        }
      } catch (error) {
        console.error('Error fetching group shared ideas:', error);
        // Set empty array on error so loading state goes away
        setGroupSharedIdeas(prev => ({
          ...prev,
          [groupId]: []
        }));
      }
    }
  };

  // Refresh shared ideas for a group (call after sharing to a group)
  const refreshGroupSharedIdeas = async (groupId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/shared-ideas?user_email=${encodeURIComponent(user?.email || '')}`);
      if (response.ok) {
        const data = await response.json();
        setGroupSharedIdeas(prev => ({
          ...prev,
          [groupId]: data.shared_ideas || []
        }));
      }
    } catch (error) {
      console.error('Error refreshing group shared ideas:', error);
    }
  };

  // Remove member from group
  const removeMember = async (groupId, memberUserId, memberEmail, groupName) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove ${memberEmail} from "${groupName}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members/${memberUserId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success', 5000);
        // Refresh members list
        const membersResponse = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, { headers: { 'X-User-ID': user?.id || '' } });
        if (membersResponse.ok) {
          const membersData = await membersResponse.json();
          setGroupMembers(prev => ({
            ...prev,
            [groupId]: membersData.members || []
          }));
        }
        await fetchGroups(); // Refresh groups to update member count
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to remove member', 'error');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showNotification('Error removing member', 'error');
    }
  };

  // Change member role
  const changeMemberRole = async (groupId, memberUserId, currentRole, memberEmail, groupName) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    const action = newRole === 'admin' ? 'promote to admin' : 'demote to member';
    
    const confirmed = window.confirm(
      `Are you sure you want to ${action}: ${memberEmail} in "${groupName}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members/${memberUserId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message, 'success', 5000);
        // Refresh members list
        const membersResponse = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, { headers: { 'X-User-ID': user?.id || '' } });
        if (membersResponse.ok) {
          const membersData = await membersResponse.json();
          setGroupMembers(prev => ({
            ...prev,
            [groupId]: membersData.members || []
          }));
        }
        await fetchGroups(); // Refresh groups to update user role
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to change member role', 'error');
      }
    } catch (error) {
      console.error('Error changing member role:', error);
      showNotification('Error changing member role', 'error');
    }
  };

  // Delete group
  const deleteGroup = async (groupId, groupName) => {
    showConfirmModal({
      title: 'Delete Group',
      message: `Are you sure you want to delete "${groupName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700'
    }).then(async (confirmed) => {
      if (confirmed) {
        try {
          const userId = user?.id || user?.email;
          const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}?user_id=${encodeURIComponent(userId)}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            const data = await response.json();
            showNotification(data.message, 'success', 5000);
            await fetchGroups(); // Refresh groups list
          } else {
            const error = await response.json();
            showNotification(error.detail || 'Failed to delete group', 'error');
          }
        } catch (error) {
          console.error('Error deleting group:', error);
          showNotification('Error deleting group', 'error');
        }
      }
      closeConfirmModal();
    });
  };

  // Start editing group name
  const startEditingGroup = (groupId, currentName) => {
    setEditingGroupId(groupId);
    setEditGroupName(currentName);
  };

  // Cancel editing group name
  const cancelEditingGroup = () => {
    setEditingGroupId(null);
    setEditGroupName('');
  };

  // Save edited group name
  const saveGroupName = async (groupId) => {
    if (!editGroupName.trim()) {
      showNotification('Group name cannot be empty', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editGroupName.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('Group name updated successfully', 'success', 5000);
        setEditingGroupId(null);
        setEditGroupName('');
        await fetchGroups(); // Refresh groups list
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to update group name', 'error');
      }
    } catch (error) {
      console.error('Error updating group name:', error);
      showNotification('Error updating group name', 'error');
    }
  };


  const previewEmail = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/preview-digest/${user}`);
      if (response.ok) {
        const data = await response.json();
        // Open preview in new window
        const previewWindow = window.open('', '_blank');
        previewWindow.document.write(data.html_content);
        previewWindow.document.close();
      } else {
        const errorData = await response.json();
        alert(`Failed to generate email preview: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error previewing email:', error);
      alert('Failed to preview email. Please check your connection.');
    }
  };

  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [ideaScheduleInfo, setIdeaScheduleInfo] = useState(null); // Stores scheduled date/time for current idea
  const [ideaScheduleLoading, setIdeaScheduleLoading] = useState(false);
  const [useManualScheduleFlow, setUseManualScheduleFlow] = useState(false); // Bypass auto-schedule flow
  
  // Helper to open calendar modal with proper loading state
  const openCalendarModal = (tickerData) => {
    console.log('🗓️ openCalendarModal called with:', tickerData);
    setSelectedTickerForScheduling(tickerData);
    setUseManualScheduleFlow(false); // Reset manual flow flag
    // Always reset SEC availability to loading state for fresh check
    setSecAvailability({ loading: true, hasFilings: false, hasForeignFilings: false, message: '' });
    if (dossierSettings.enabled) {
      setIdeaScheduleLoading(true);
      setIdeaScheduleInfo(null);
    }
    setShowCalendarModal(true);
    setCurrentCalendarStep(1);
    // Fetch earnings data and check SEC availability for the ticker
    if (tickerData.ticker) {
      console.log('🗓️ Fetching earnings for:', tickerData.ticker);
      fetchNextEarnings(tickerData.ticker);
      checkSECAvailability(tickerData.ticker);
    } else {
      console.warn('🗓️ No ticker provided to openCalendarModal');
    }
  };
  
  // Helper to open manual schedule modal (bypasses auto-schedule flow)
  const openManualScheduleModal = (tickerData) => {
    setSelectedTickerForScheduling(tickerData);
    setUseManualScheduleFlow(true); // Use manual flow
    setIdeaScheduleLoading(false);
    setIdeaScheduleInfo(null);
    // Reset SEC availability to loading state for fresh check
    setSecAvailability({ loading: true, hasFilings: false, hasForeignFilings: false, message: '' });
    setShowCalendarModal(true);
    setCurrentCalendarStep(1);
    // Initialize with tomorrow's date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setCalendarScheduleData(prev => ({
      ...prev,
      date: tomorrow.toISOString().split('T')[0],
      time: '09:00'
    }));
    // Check SEC availability and fetch earnings for this ticker
    if (tickerData.ticker) {
      checkSECAvailability(tickerData.ticker);
      fetchNextEarnings(tickerData.ticker);
    }
  };
  
  // Helper to close calendar modal and reset state
  const closeCalendarModal = () => {
    setShowCalendarModal(false);
    setUseManualScheduleFlow(false);
    setIsEditingSchedule(false);
  };
  
  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [rescheduleEarningsData, setRescheduleEarningsData] = useState(null);
  const [rescheduleEarningsLoading, setRescheduleEarningsLoading] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [isScheduleMode, setIsScheduleMode] = useState(false); // true = "Schedule", false = "Reschedule"
  
  // Open reschedule modal (isNewSchedule: true for Collections, false for Files)
  const openRescheduleModal = async (item, isNewSchedule = false) => {
    setRescheduleItem(item);
    setIsScheduleMode(isNewSchedule);
    setShowRescheduleModal(true);
    setRescheduleEarningsLoading(true);
    setRescheduleEarningsData(null);
    
    // Fetch earnings data
    try {
      const response = await fetch(`${API_BASE_URL}/api/ticker-earnings/${item.ticker}`);
      const data = await response.json();
      if (data.success) {
        setRescheduleEarningsData(data);
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
    } finally {
      setRescheduleEarningsLoading(false);
    }
  };
  
  // Handle reschedule actions
  const handleReschedule = async (type) => {
    if (!rescheduleItem) return;
    
    setRescheduling(true);
    let newDate;
    
    if (type === 'auto') {
      // Use auto-schedule logic - schedule for tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      newDate = tomorrow.toISOString().split('T')[0];
    } else if (type === 'earnings' && rescheduleEarningsData) {
      // Schedule for day after next earnings
      newDate = rescheduleEarningsData.day_after_earnings;
    } else if (type === 'monthly') {
      // Schedule for one month from today
      const oneMonth = new Date();
      oneMonth.setMonth(oneMonth.getMonth() + 1);
      newDate = oneMonth.toISOString().split('T')[0];
    } else if (type === 'manual') {
      // Close this modal and open the manual schedule modal
      setShowRescheduleModal(false);
      setRescheduling(false);
      openManualScheduleModal({
        ticker: rescheduleItem.ticker,
        content: `Review $${rescheduleItem.ticker}`,
        company_name: rescheduleItem.company_name
      });
      return;
    }
    
    if (newDate) {
      try {
        // Save the new schedule using the ticker endpoint
        const userId = user?.id || user?.email || 'demo_user';
        const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}/ticker`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticker: rescheduleItem.ticker,
            scheduled_date: newDate,
            scheduled_time: '09:00',
            company_name: rescheduleItem.company_name,
            source: 'manual'
          })
        });
        
        if (response.ok) {
          showNotification(`${isScheduleMode ? 'Scheduled' : 'Recycled'} ${rescheduleItem.ticker} to ${new Date(newDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, 'success');
          // Refresh schedule
          fetchResearchSchedule();
        } else {
          const error = await response.json();
          showNotification(`Failed to ${isScheduleMode ? 'schedule' : 'recycle'}: ${error.detail || 'Unknown error'}`, 'error');
        }
      } catch (error) {
        console.error('Error scheduling:', error);
        showNotification(`Error ${isScheduleMode ? 'scheduling' : 'rescheduling'}`, 'error');
      }
    }
    
    setRescheduling(false);
    setShowRescheduleModal(false);
  };
  
  // No need for aggressive keyboard blocking - let inputs work naturally
  const [showCalendarProviders, setShowCalendarProviders] = useState(false);
  const [selectedTickerForScheduling, setSelectedTickerForScheduling] = useState(null);
  const [currentCalendarStep, setCurrentCalendarStep] = useState(1);

  // Research Preferences State
  const [secFilingPreferences, setSecFilingPreferences] = useState({
    includeOnePageSummary: false,
    includeManagementQA: false,
    include10K: true,
    include10Q: true,
    include8K: true,
    include20F: false,  // Foreign issuer annual report - disabled by default
    include6K: false,   // Foreign issuer current report - disabled by default
    includeProxy: false,
    includeDossier: false,
    includeStatements: false
    // Temporarily removed:
    // includeAIAnalysis: false,  // Pro feature: AI-powered investment analysis
    // includeEarningsCallSummary: false  // Pro feature: 750-word earnings call summary
  });

  // AI Analysis states
  const [aiAnalysisResult, setAIAnalysisResult] = useState(null);
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [isGeneratingAIAnalysis, setIsGeneratingAIAnalysis] = useState(false);

  // Function to call the Pro AI Investment Analysis API
  const generateAIInvestmentAnalysis = async (userId, documentFilters = null) => {
    try {
      setIsGeneratingAIAnalysis(true);
      
      const response = await fetch(`${API_BASE_URL}/api/ai-investment-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          document_filters: documentFilters,
          analysis_type: 'comprehensive'
        })
      });

      if (response.ok) {
        const analysisResult = await response.json();
        if (analysisResult.success) {
          return analysisResult.analysis;
        } else {
          throw new Error(analysisResult.error || 'Analysis generation failed');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate AI investment analysis');
      }
    } catch (error) {
      console.error('Error generating AI investment analysis:', error);
      throw error;
    } finally {
      setIsGeneratingAIAnalysis(false);
    }
  };

  // Function to handle Pro AI Analysis when user requests it
  const handleProAIAnalysis = async () => {
    if (!user || !user?.user_id) {
      alert('Please log in to use Pro AI Investment Analysis');
      return;
    }

    // Temporarily removed Pro AI Analysis check
    /*
    if (!secFilingPreferences.includeAIAnalysis) {
      return; // Not selected
    }
    */

    try {
      // Prepare document filters based on user's preferences
      const documentFilters = {
        document_types: []
      };
      
      // Add selected filing types if any are selected
      if (secFilingPreferences.include10K) documentFilters.document_types.push('10-K');
      if (secFilingPreferences.include10Q) documentFilters.document_types.push('10-Q');
      if (secFilingPreferences.include8K) documentFilters.document_types.push('8-K');
      if (secFilingPreferences.include20F) documentFilters.document_types.push('20-F');
      if (secFilingPreferences.include6K) documentFilters.document_types.push('6-K');
      if (secFilingPreferences.includeProxy) documentFilters.document_types.push('Proxy');
      
      // If no specific SEC filings selected, include all user documents
      if (documentFilters.document_types.length === 0) {
        documentFilters.document_types = null; // This will include all user documents
      }

      // Generate AI investment analysis
      const analysisResult = await generateAIInvestmentAnalysis(user.id, documentFilters);
      
      // Display the analysis result
      if (analysisResult) {
        setAIAnalysisResult(analysisResult);
        setShowAIAnalysisModal(true);
        setShowDocumentModal(false); // Close document modal if open
      }
      
    } catch (error) {
      alert(`Failed to generate AI investment analysis: ${error.message}`);
    }
  };

  // Function to format analysis sections for display
  const formatAnalysisSection = (sectionContent) => {
    if (!sectionContent) return '';
    
    return sectionContent.split('\n').map((paragraph, index) => {
      if (paragraph.trim()) {
        return <p key={index} className="mb-3 text-gray-700 leading-relaxed">{paragraph.trim()}</p>;
      }
      return null;
    }).filter(Boolean);
  };

  // Download preferences
  const [downloadPreferences, setDownloadPreferences] = useState({
    immediateDownload: false,
    includeInEmail: false
  });
  const [calendarScheduleData, setCalendarScheduleData] = useState({
    date: '',
    time: '',
    reminderType: 'email',
    reminderTime: '1hour',
    researchPackage: 'basic',
    calendarProvider: 'default',
    notes: '',
    includeOriginalContent: true,
    includeValuation: true,
    includeNews: false,
    includeSentiment: false
  });
  
  // Earnings data for schedule modal
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  
  // Fetch next earnings date for a ticker
  const fetchNextEarnings = async (ticker) => {
    console.log('📊 fetchNextEarnings called for:', ticker);
    if (!ticker) {
      console.warn('📊 No ticker provided to fetchNextEarnings');
      return;
    }
    setEarningsLoading(true);
    setEarningsData(null);
    try {
      const url = `${API_BASE_URL}/api/ticker-earnings/${ticker}`;
      console.log('📊 Fetching from:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('📊 Earnings response:', data);
      if (data.success) {
        setEarningsData(data);
        console.log('📊 Earnings data set successfully');
      } else {
        console.warn('📊 Earnings API returned success: false');
      }
    } catch (error) {
      console.error('📊 Error fetching earnings:', error);
    } finally {
      setEarningsLoading(false);
      console.log('📊 Earnings loading set to false');
    }
  };

  const calendarSteps = [
    { id: 1, title: 'Choose Date & Time', icon: '📅' },
    { id: 2, title: 'Research Preferences', icon: '📋' },
    { id: 3, title: 'Reminder Settings', icon: '🔔' },
    { id: 4, title: 'Review & Schedule', icon: '✅' }
  ];

  const handleCalendarNext = () => {
    if (currentCalendarStep < calendarSteps.length) {
      setCurrentCalendarStep(currentCalendarStep + 1);
    }
  };

  const handleCalendarPrevious = () => {
    if (currentCalendarStep > 1) {
      setCurrentCalendarStep(currentCalendarStep - 1);
    }
  };

  const generateCalendarUrls = (title, description, startDate, endDate, location = '') => {
    const formatDateForGoogle = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };
    
    const encodedTitle = encodeURIComponent(title);
    const encodedDescription = encodeURIComponent(description);
    const encodedLocation = encodeURIComponent(location);
    
    const googleStartDate = formatDateForGoogle(startDate);
    const googleEndDate = formatDateForGoogle(endDate);
    
    return {
      google: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodedTitle}&dates=${googleStartDate}/${googleEndDate}&details=${encodedDescription}&location=${encodedLocation}`,
      outlook: `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodedTitle}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}&body=${encodedDescription}&location=${encodedLocation}`,
      yahoo: `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodedTitle}&st=${googleStartDate}&et=${googleEndDate}&desc=${encodedDescription}&in_loc=${encodedLocation}`,
    };
  };

  const openCalendarEvent = (calendarProvider = 'google') => {
    const scheduleDateTime = new Date(`${calendarScheduleData.date}T${calendarScheduleData.time}:00`);
    const endDateTime = new Date(scheduleDateTime.getTime() + 30 * 60000); // 30 minutes later
    
    const title = `Research ${selectedTickerForScheduling.ticker}`;
    
    // Build enhanced description with document links (same logic as handleEnhancedSchedule)
    const ticker = selectedTickerForScheduling.ticker;
    const selectedDocs = [];
    if (secFilingPreferences.include10K) selectedDocs.push('10-K Annual Reports');
    if (secFilingPreferences.include10Q) selectedDocs.push('10-Q Quarterly Reports');
    if (secFilingPreferences.include8K) selectedDocs.push('8-K Current Reports');
    if (secFilingPreferences.includeProxy) selectedDocs.push('Proxy Statements');
    
    let reminderContent = `Research reminder for ${ticker.toUpperCase()}`;
    
    if (selectedDocs.length > 0) {
      reminderContent += `\n\nFocus on: ${selectedDocs.join(', ')}`;
    }
    
    if (secFilingPreferences.includeStatements) {
      reminderContent += '\nInclude Financial Statements (Income Statement, Balance Sheet, Cash Flow)';
    }
    
    // Temporarily removed Pro features
    /*
    if (secFilingPreferences.includeAIAnalysis) {
      reminderContent += '\n🤖 Include AI Investment Analysis (Pro Feature): AI-powered critique and merit assessment based on documents';
    }
    */
    
    if (calendarScheduleData.notes) {
      reminderContent += `\n\nNotes: ${calendarScheduleData.notes}`;
    }
    
    reminderContent += `\n\nOriginal Investment Idea: ${selectedTickerForScheduling.content || 'View in Tickler dashboard'}`;
    
    // Create document generation link
    const documentPrefs = {
      filing_types: [],
      include_statements: secFilingPreferences.includeStatements || false,
      include_sec_filings: true
    };
    
    // Add selected filing types
    if (secFilingPreferences.include10K) documentPrefs.filing_types.push('10-K');
    if (secFilingPreferences.include10Q) documentPrefs.filing_types.push('10-Q');
    if (secFilingPreferences.include8K) documentPrefs.filing_types.push('8-K');
    if (secFilingPreferences.include20F) documentPrefs.filing_types.push('20-F');
    if (secFilingPreferences.include6K) documentPrefs.filing_types.push('6-K');
    if (secFilingPreferences.includeProxy) documentPrefs.filing_types.push('Proxy');
    
    // Add user_id for data isolation
    documentPrefs.user_id = user?.id;
    
    // Create encoded preferences for URL
    const encodedPrefs = encodeURIComponent(JSON.stringify(documentPrefs));
    const documentLink = `${API_BASE_URL}/api/generate-web-viewer/${ticker}?preferences=${encodedPrefs}&redirect=true`;
    
    // Enhanced description with direct document link
    const description = `Research reminder for ${ticker.toUpperCase()}

📋 QUICK ACCESS LINK:
${documentLink}

Documents: ${selectedDocs.length > 0 ? selectedDocs.join(', ') : 'SEC Filings'}${secFilingPreferences.includeStatements ? ' + Financial Statements' : ''}

Investment Idea: ${selectedTickerForScheduling.content || 'View in Tickler dashboard'}`;
    
    const calendarUrls = generateCalendarUrls(title, description, scheduleDateTime, endDateTime, 'Investment Research');
    
    console.log('Calendar provider:', calendarProvider);
    console.log('Calendar URL:', calendarUrls[calendarProvider]);
    
    // Open the calendar URL directly
    const url = calendarUrls[calendarProvider];
    if (url) {
      window.open(url, '_blank');
    } else {
      alert('Calendar provider not supported');
    }
  };

  const handleEnhancedSchedule = async () => {
    console.log('handleEnhancedSchedule called');
    
    try {
      const ticker = selectedTickerForScheduling?.ticker;
      if (!ticker) {
        alert('No ticker selected for scheduling');
        return;
      }

      // Build reminder content based on selected document preferences
      let reminderContent = `Research reminder for ${ticker.toUpperCase()}`;
      
      const selectedDocs = [];
      if (secFilingPreferences.include10K) selectedDocs.push('10-K Annual Reports');
      if (secFilingPreferences.include10Q) selectedDocs.push('10-Q Quarterly Reports');
      if (secFilingPreferences.include8K) selectedDocs.push('8-K Current Reports');
      if (secFilingPreferences.includeProxy) selectedDocs.push('Proxy Statements');
      
      if (secFilingPreferences.includeDossier || secFilingPreferences.includeStatements) {
        
        if (selectedDocs.length > 0) {
          reminderContent += `\n\nFocus on: ${selectedDocs.join(', ')}`;
        }
        
        if (secFilingPreferences.includeStatements) {
          reminderContent += '\nInclude Financial Statements (Income Statement, Balance Sheet, Cash Flow)';
        }
        
        // Temporarily removed Pro features
        /*
        if (secFilingPreferences.includeAIAnalysis) {
          reminderContent += '\n🤖 Include AI Investment Analysis (Pro Feature): AI-powered critique and merit assessment based on documents';
        }
        */
      }
      
      if (calendarScheduleData.notes) {
        reminderContent += `\n\nNotes: ${calendarScheduleData.notes}`;
      }
      
      reminderContent += `\n\nOriginal Investment Idea: ${selectedTickerForScheduling.content || 'View in Tickler dashboard'}`;

      // Handle email reminders if selected
      if (calendarScheduleData.reminderType === 'email' || calendarScheduleData.reminderType === 'both') {
        try {
          // Convert local time to UTC for the backend
          // Create a date object from the selected date/time (this will be interpreted as local time)
          const localDateTime = new Date(`${calendarScheduleData.date}T${calendarScheduleData.time}:00`);
          
          // Convert to ISO string which automatically converts to UTC
          const utcISOString = localDateTime.toISOString();
          const utcDateStr = utcISOString.split('T')[0];
          const utcTimeStr = utcISOString.split('T')[1].substring(0, 5);
          
          console.log('Selected local time:', calendarScheduleData.date, calendarScheduleData.time);
          console.log('Converted to UTC:', utcDateStr, utcTimeStr);
          console.log('Local timezone offset (minutes):', localDateTime.getTimezoneOffset());
          
          const scheduleEmailResponse = await fetch(`${API_BASE_URL}/api/email/schedule-reminder`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-ID': user?.id || 'anonymous'
            },
            body: JSON.stringify({
              user_id: user?.id || 'sample_user',
              ticker: ticker,
              company_name: selectedTickerForScheduling?.company_name || '',
              reminder_date: utcDateStr,
              reminder_time: utcTimeStr,
              content: reminderContent,
              preferences: secFilingPreferences
            })
          });
          
          if (scheduleEmailResponse.ok) {
            console.log('Email reminder scheduled successfully');
          } else {
            console.log('Email reminder scheduling failed, will use calendar only');
          }
        } catch (error) {
          console.log('Email reminder error, continuing with calendar:', error);
        }
      }

      // Handle calendar events if selected
      if (calendarScheduleData.reminderType === 'calendar' || calendarScheduleData.reminderType === 'both') {
        const scheduleDateTime = new Date(`${calendarScheduleData.date}T${calendarScheduleData.time}`);
        const endDateTime = new Date(scheduleDateTime.getTime() + 30 * 60000); // 30 minutes later
        
        const title = `Research ${selectedTickerForScheduling.ticker}`;
        
        // Create a document generation link with the selected preferences
        const documentPrefs = {
          filing_types: [],
          include_statements: secFilingPreferences.includeStatements || false,
          include_sec_filings: true
        };
        
        // Add selected filing types
        if (secFilingPreferences.include10K) documentPrefs.filing_types.push('10-K');
        if (secFilingPreferences.include10Q) documentPrefs.filing_types.push('10-Q');
        if (secFilingPreferences.include8K) documentPrefs.filing_types.push('8-K');
        if (secFilingPreferences.include20F) documentPrefs.filing_types.push('20-F');
        if (secFilingPreferences.include6K) documentPrefs.filing_types.push('6-K');
        if (secFilingPreferences.includeProxy) documentPrefs.filing_types.push('Proxy');
        
        // Add user_id for data isolation
        documentPrefs.user_id = user?.id;
        
        // Create encoded preferences for URL
        const encodedPrefs = encodeURIComponent(JSON.stringify(documentPrefs));
        const documentLink = `${API_BASE_URL}/api/generate-web-viewer/${ticker}?preferences=${encodedPrefs}&redirect=true`;
        
        // Enhanced description with direct document link
        const description = `Research reminder for ${ticker.toUpperCase()}

📋 QUICK ACCESS LINK:
${documentLink}

Documents: ${selectedDocs.length > 0 ? selectedDocs.join(', ') : 'SEC Filings'}${secFilingPreferences.includeStatements ? ' + Financial Statements' : ''}

Investment Idea: ${selectedTickerForScheduling.content || 'View in Tickler dashboard'}`;
        
        const calendarUrls = generateCalendarUrls(title, description, scheduleDateTime, endDateTime, 'Investment Research');
        
        // Show calendar providers for scheduling
        setShowCalendarProviders(true);
        
        // Store calendar URLs and success message for the calendar provider modal
        window.tempCalendarUrls = calendarUrls;
        window.tempSuccessMessage = `📅 Research reminder scheduled for ${ticker.toUpperCase()} on ${calendarScheduleData.date} at ${calendarScheduleData.time} with direct document access link`;
        window.tempWebViewerUrl = null; // No immediate web viewer for scheduling
      } else {
        // Email only - close modal and show success
        setShowCalendarModal(false);
        setCurrentCalendarStep(1);
        showSchedulingSuccessBadge(ticker, calendarScheduleData.date, calendarScheduleData.time, 'email');
      }

    } catch (error) {
      console.error('Error in handleEnhancedSchedule:', error);
      alert(`Error scheduling reminder for ${selectedTickerForScheduling?.ticker}: ${error.message}`);
    }
  };
  const handleDocumentGeneration = async () => {
    try {
      const ticker = selectedTickerForScheduling?.ticker;
      if (!ticker) {
        alert('No ticker selected for document generation');
        return;
      }

      // Check if company has SEC filings - don't proceed if not an SEC filer
      if (!secAvailability.hasFilings && !secAvailability.hasForeignFilings) {
        // Show user-friendly message
        const noFilingsMessage = document.createElement('div');
        noFilingsMessage.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          animation: slideIn 0.3s ease-out;
          max-width: 320px;
        `;
        noFilingsMessage.innerHTML = `
          <div style="display: flex; align-items: flex-start; gap: 12px;">
            <svg style="width: 24px; height: 24px; flex-shrink: 0; margin-top: 2px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div>
              <div style="font-weight: 600; margin-bottom: 4px;">File Not Available</div>
              <div style="opacity: 0.9; font-size: 13px;">${ticker.toUpperCase()} doesn't have SEC filings. We're working on expanding coverage — stay tuned!</div>
            </div>
          </div>
          <style>
            @keyframes slideIn {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          </style>
        `;
        document.body.appendChild(noFilingsMessage);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
          if (document.body.contains(noFilingsMessage)) {
            noFilingsMessage.style.animation = 'slideIn 0.3s ease-out reverse';
            setTimeout(() => {
              if (document.body.contains(noFilingsMessage)) {
                document.body.removeChild(noFilingsMessage);
              }
            }, 300);
          }
        }, 5000);
        
        setShowDocumentModal(false);
        return;
      }

      // Track dossier order for engagement analytics
      const selectedDocs = [];
      const selectedSources = [];
      if (secFilingPreferences.include10K) selectedDocs.push('10-K');
      if (secFilingPreferences.include10Q) selectedDocs.push('10-Q');
      if (secFilingPreferences.include8K) selectedDocs.push('8-K');
      if (secFilingPreferences.includeProxy) selectedDocs.push('Proxy');
      if (secFilingPreferences.includeStatements) selectedDocs.push('Statements');
      if (secFilingPreferences.includeOnePageSummary) selectedDocs.push('One-Page Summary');
      if (secFilingPreferences.includeManagementQA) selectedDocs.push('Management Q&A');
      
      trackEngagement('dossier_order', 'dossier', { 
        ticker: ticker,
        document_types: selectedDocs,
        sources: selectedSources
      });

      // Get user email (backend will look up the correct email from database)
      const userEmail = user?.email || 'urijmiller@gmail.com';

      // Close the modal
      setShowDocumentModal(false);

      // Check if only One-Page Summary is selected (no SEC docs)
      const hasSecDocs = secFilingPreferences.include10K || 
                         secFilingPreferences.include10Q || 
                         secFilingPreferences.include8K || 
                         secFilingPreferences.includeProxy ||
                         secFilingPreferences.includeStatements;
      
      if (secFilingPreferences.includeOnePageSummary && !hasSecDocs) {
        // Show only one-page summary modal
        await handleGenerateOnePageSummary(ticker);
        return;
      }

      // Show "Generating dossier" badge immediately
      const emailBadge = document.createElement('div');
      emailBadge.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: white;
        padding: 12px 20px;
        border-radius: 25px;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-weight: 500;
        font-size: 14px;
        animation: slideIn 0.3s ease-out;
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      
      emailBadge.innerHTML = `
        <svg style="width: 16px; height: 16px; animation: spin 1s linear infinite;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        <span id="email-status">Generating ${ticker.toUpperCase()} file...</span>
        <style>
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      `;
      
      document.body.appendChild(emailBadge);

      // Continue with SEC dossier generation - use email endpoint
      // Generate web viewer with selected preferences - always include SEC filings
      const viewerPrefs = {
        filing_types: [],
        include_statements: secFilingPreferences.includeStatements || false,
        include_one_page_summary: secFilingPreferences.includeOnePageSummary || false,
        include_management_qa: secFilingPreferences.includeManagementQA || false,
        include_sec_filings: true,  // Always include SEC filings in Documents workflow
        user_email: userEmail  // Include user email for emailing
      };
      
      // Add selected filing types
      if (secFilingPreferences.include10K) viewerPrefs.filing_types.push('10-K');
      if (secFilingPreferences.include10Q) viewerPrefs.filing_types.push('10-Q');
      if (secFilingPreferences.include8K) viewerPrefs.filing_types.push('8-K');
      if (secFilingPreferences.include20F) viewerPrefs.filing_types.push('20-F');
      if (secFilingPreferences.include6K) viewerPrefs.filing_types.push('6-K');
      if (secFilingPreferences.includeProxy) viewerPrefs.filing_types.push('Proxy');
      
      // Call the email endpoint instead of the regular endpoint
      const viewerResponse = await fetch(`${API_BASE_URL}/api/email-web-viewer/${ticker}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user?.id || 'anonymous'
        },
        body: JSON.stringify(viewerPrefs)
      });

      if (viewerResponse.ok) {
        const data = await viewerResponse.json();
        
        // Trigger blue dot notification for Files tab AFTER a delay
        // This gives the backend time to create the file entry
        console.log('🔵 App.js handleDocumentGeneration: File created, will show blue dot in 4 seconds');
        setTimeout(() => {
          console.log('🔵 App.js handleDocumentGeneration: Setting hasNewFiles to true now');
          setHasNewFiles(true);
          // Trigger workspace FilesTab panels to refresh
          setFilesRefreshTrigger(prev => prev + 1);
          console.log('🔵 App.js handleDocumentGeneration: Incremented filesRefreshTrigger for workspace sync');
        }, 4000); // 4 second delay (optimized from 12s due to faster backend)
        
        // Update the message when ready
        const statusElement = document.getElementById('email-status');
        if (statusElement) {
          statusElement.textContent = `${ticker.toUpperCase()} file ready!`;
        }
      
        // Remove generating badge after showing updated message
        setTimeout(() => {
          if (document.body.contains(emailBadge)) {
            emailBadge.style.animation = 'slideIn 0.3s ease-out reverse';
            setTimeout(() => {
              if (document.body.contains(emailBadge)) {
                document.body.removeChild(emailBadge);
              }
            }, 300);
          }
        }, 2000);
        
        // Show success notification
        const successBadge = document.createElement('div');
        successBadge.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 25px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 500;
          font-size: 14px;
          animation: slideIn 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 8px;
        `;
        
        successBadge.innerHTML = `
          <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>${ticker.toUpperCase()} file added to Files tab</span>
        `;
        
        document.body.appendChild(successBadge);
        
        // Auto-remove success badge after 5 seconds
        setTimeout(() => {
          if (document.body.contains(successBadge)) {
            successBadge.style.animation = 'slideIn 0.3s ease-out reverse';
            setTimeout(() => {
              if (document.body.contains(successBadge)) {
                document.body.removeChild(successBadge);
              }
            }, 300);
          }
        }, 5000);
        
      } else {
        // Remove email badge on error
        if (document.body.contains(emailBadge)) {
          emailBadge.style.animation = 'slideIn 0.3s ease-out reverse';
          setTimeout(() => {
            if (document.body.contains(emailBadge)) {
              document.body.removeChild(emailBadge);
            }
          }, 300);
        }
        
        const errorText = await viewerResponse.text();
        console.error('Failed to queue dossier email:', viewerResponse.status, errorText);
        alert(`Failed to queue SEC Documents for ${ticker}: ${errorText}`);
      }
    } catch (error) {
      // Remove email badge on exception
      const existingBadge = document.querySelector('[style*="E-mailing"]')?.parentElement;
      if (existingBadge && document.body.contains(existingBadge)) {
        existingBadge.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => {
          if (document.body.contains(existingBadge)) {
            document.body.removeChild(existingBadge);
          }
        }, 300);
      }
      
      console.error('Error queuing SEC Documents:', error);
      alert(`Error queuing SEC Documents for ${selectedTickerForScheduling?.ticker}: ${error.message}`);
    }
  };

  const showSchedulingSuccessBadge = (ticker, date, time, type = 'calendar') => {
    // Show sleek scheduling success badge
    const successBadge = document.createElement('div');
    successBadge.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      padding: 12px 20px;
      border-radius: 25px;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-weight: 500;
      font-size: 14px;
      animation: slideIn 0.3s ease-out;
      display: flex;
      align-items: center;
      gap: 8px;
      max-width: 320px;
    `;
    
    const icon = type === 'email' ? 
      `<svg style="width: 16px; height: 16px;" fill="currentColor" viewBox="0 0 24 24">
         <path d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
       </svg>` :
      `<svg style="width: 16px; height: 16px;" fill="currentColor" viewBox="0 0 24 24">
         <path d="M8 7V3a1 1 0 012 0v4h4a1 1 0 010 2h-4v4a1 1 0 01-2 0V9H4a1 1 0 010-2h4zM3 19h18a1 1 0 000-2H3a1 1 0 000 2z"/>
       </svg>`;
    
    const message = type === 'email' ? 
      `${ticker} email reminder set for ${date}` :
      `${ticker} calendar reminder set for ${date} at ${time}`;
    
    successBadge.innerHTML = `
      ${icon}
      ${message}
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;
    
    document.body.appendChild(successBadge);
    
    // Auto-remove success badge after 4 seconds
    setTimeout(() => {
      if (document.body.contains(successBadge)) {
        successBadge.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => {
          if (document.body.contains(successBadge)) {
            document.body.removeChild(successBadge);
          }
        }, 300);
      }
    }, 4000);
  };

  // Handle responding to shared investment ideas
  const handleRespondToIdea = async (ideaId, action) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shared-ideas/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shared_idea_id: ideaId,
          action: action // 'accept' or 'reject'
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (action === 'accept') {
          // Remove from pending ideas
          setPendingIdeas(prev => prev.filter(idea => idea.id !== ideaId));
          
          // Show modern success notification with appropriate message
          const notification = document.createElement('div');
          notification.style.cssText = `
            position: fixed; 
            top: 20px; 
            right: 20px; 
            background: #10b981; 
            color: white; 
            padding: 16px 20px; 
            border-radius: 12px; 
            z-index: 10000; 
            box-shadow: 0 4px 20px rgba(0,0,0,0.2);
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 500;
            max-width: 400px;
          `;
          
          // Customize message based on share type
          let message = 'Importing idea from X... It will appear in Received shortly.';
          let iconPath = 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'; // download icon
          let bgColor = '#10b981'; // green
          
          notification.style.background = bgColor;
          notification.innerHTML = `
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"/>
            </svg>
            ${message}
          `;
          document.body.appendChild(notification);
          setTimeout(() => notification.remove(), 4000);
          
          // Handle redirect based on share type
          if (result.share_type === 'poll') {
            // Refresh shared polls and switch to Polls tab with Received view
            fetchSharedPolls();
            setTimeout(() => {
              setPollView('received');
              setActiveTab('threads');
            }, 500);
          } else if (result.share_type === 'watchlist') {
            // Refresh shared watchlists and switch to Watchlists tab with Received view
            fetchSharedWatchlists();
            setTimeout(() => {
              setWatchlistView('received');
              setActiveTab('watchlists');
            }, 500);
          } else {
            // Regular idea - refresh and switch to Received view after import completes
            fetchDashboardIdeas();
            setTimeout(() => {
              setIdeaView('received');
              fetchData();
            }, 500);
          }
          
        } else {
          // Just remove from pending ideas
          setPendingIdeas(prev => prev.filter(idea => idea.id !== ideaId));
          showNotification('Item declined.', 'info');
        }
        
        // Close modal if no more pending ideas
        if (pendingIdeas.length <= 1) {
          setShowReceiveModal(false);
        }
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to respond to shared idea');
      }
    } catch (error) {
      console.error('Error responding to shared idea:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleShareInvestmentIdea = async () => {
    try {
      // Validation based on share type
      if (!selectedTickerForScheduling?.ticker) {
        showNotification('No ticker selected', 'error');
        return;
      }

      if (shareToGroup) {
        if (!selectedGroupId) {
          showNotification('Please select a group', 'error');
          return;
        }
      } else {
        if (!shareData.friendEmail) {
          showNotification('Please provide an email address', 'error');
          return;
        }
      }

      const ticker = selectedTickerForScheduling.ticker;
      const isIdeasList = selectedTickerForScheduling.isIdeasList;
      const shareType = selectedTickerForScheduling.shareType || 'idea';
      const tickerCount = selectedTickerForScheduling.tickerCount || 1;
      
      // Get tweet ID for pointer mode
      const tweetId = selectedTickerForScheduling.tweetId || selectedTickerForScheduling.tweet_id;
      
      // Build content based on compliance mode
      let sharedContent;
      if (shareComplianceMode === 'pointer' && shareType !== 'poll' && shareType !== 'watchlist' && shareType !== 'file') {
        // Pointer mode: Only share ticker + tweet_id - recipient fetches their own content
        sharedContent = {
          mode: 'pointer',
          ticker: ticker.toUpperCase(),
          tweet_id: tweetId,
          // No content, no summary, no signal - just a pointer
        };
      } else {
        // Full content mode
        sharedContent = {
          mode: 'full',
          content: selectedTickerForScheduling.content
        };
      }
      
      // Create appropriate message based on share type
      let defaultMessage;
      if (shareType === 'poll') {
        defaultMessage = `I wanted to share this poll "${ticker}" with you. It contains ${tickerCount} tickers worth checking out!`;
      } else if (shareType === 'watchlist') {
        defaultMessage = `I wanted to share this watchlist "${ticker}" with you. It contains ${tickerCount} tickers I think you'll find interesting!`;
      } else if (shareType === 'file') {
        defaultMessage = `I wanted to share these ${ticker} SEC filings with you. Check out the documents and financial data!`;
      } else if (isIdeasList) {
        defaultMessage = `I wanted to share this curated list of investment ideas with you. Take a look and let me know what you think!`;
      } else {
        defaultMessage = `Check out $${ticker.toUpperCase()} - thought you might find this interesting.`;
      }
      
      const shareEmailResponse = await fetch(`${API_BASE_URL}/api/email/share-investment-idea`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user?.id || 'anonymous'
        },
        body: JSON.stringify({
          to_email: shareToGroup ? null : shareData.friendEmail,
          ticker: ticker,
          investment_idea: shareComplianceMode === 'pointer' ? null : selectedTickerForScheduling.content,
          shared_content: sharedContent,
          message: shareData.shareMessage || defaultMessage,
          shared_by: user?.email || 'anonymous@trytickler.com',
          sender_name: user?.display_name || user?.username || user?.email?.split('@')[0] || 'Anonymous',
          group_id: shareToGroup ? selectedGroupId : null,
          document_preferences: null,
          is_ideas_list: isIdeasList || false,
          all_tickers: selectedTickerForScheduling.allTickers || null,
          share_type: shareType,
          poll_id: selectedTickerForScheduling.pollId || null,
          watchlist_id: selectedTickerForScheduling.watchlistId || null,
          viewer_id: selectedTickerForScheduling.viewerId || null,
          ticker_count: tickerCount,
          compliance_mode: shareComplianceMode,
          tweet_id: tweetId
        })
      });
      
      // Close modal and reset state
      setShowShareModal(false);
      setShareData({ friendEmail: '', shareMessage: '' });
      setShareToGroup(false);
      setSelectedGroupId('');
      setShareRecipientStatus(null);

      if (shareEmailResponse.ok) {
        // Show success notification
        const successNotification = document.createElement('div');
        successNotification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          animation: slideIn 0.3s ease-out;
          max-width: 350px;
        `;
        
        const groupInfo = shareToGroup ? userGroups.find(g => g.group_id === selectedGroupId) : null;
        const recipient = shareToGroup 
          ? `${groupInfo?.name} (${groupInfo?.member_count} members)`
          : shareData.friendEmail;
        
        successNotification.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <svg style="width: 20px; height: 20px; flex-shrink: 0;" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <div style="font-weight: 600; font-size: 16px;">Investment Idea Shared</div>
          </div>
          <div style="font-size: 14px; opacity: 0.95; line-height: 1.4; margin-top: 4px;">
            ${shareToGroup ? 'Shared with group' : 'Email sent to'} <strong>${recipient}</strong><br>
            ${ticker.toUpperCase()} idea shared from Tickler
          </div>
          <style>
            @keyframes slideIn {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          </style>
        `;
        
        document.body.appendChild(successNotification);
        
        // Auto-remove notification after 4 seconds
        setTimeout(() => {
          successNotification.style.animation = 'slideIn 0.3s ease-out reverse';
          setTimeout(() => {
            if (document.body.contains(successNotification)) {
              document.body.removeChild(successNotification);
            }
          }, 300);
        }, 4000);
      } else {
        const errorText = await shareEmailResponse.text();
        alert(`Failed to share investment idea: ${errorText}`);
      }
    } catch (error) {
      console.error('Error sharing investment idea:', error);
      alert(`Error sharing investment idea: ${error.message}`);
    }
  };

  const handleImmediateResearch = async (ticker, bookmarkContent) => {
    try {
      if (!ticker) {
        alert('No ticker available for research');
        return;
      }

      // Show loading message
      const loadingMessage = `Generating SEC Documents for ${ticker.toUpperCase()}...`;
      const loadingAlert = setTimeout(() => {
        alert(loadingMessage);
      }, 2000);

      // Generate comprehensive web viewer with default settings
      // STRICT DATA ISOLATION: Include user_id
      const viewerPrefs = {
        filing_types: ['10-K', '10-Q', '8-K'],
        include_statements: true,
        include_sec_filings: true,
        user_id: user?.id  // REQUIRED for data isolation
      };
      
      if (!viewerPrefs.user_id) {
        clearTimeout(loadingAlert);
        alert('Error: You must be logged in to generate documents.');
        return;
      }
      
      const viewerResponse = await fetch(`${API_BASE_URL}/api/generate-web-viewer/${ticker}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user?.id || 'anonymous'
        },
        body: JSON.stringify(viewerPrefs)
      });

      if (viewerResponse.ok) {
        const viewerData = await viewerResponse.json();
        console.log('Viewer data received:', viewerData); // Debug log
        const webViewerUrl = `${API_BASE_URL}${viewerData.shareable_url}`;
        console.log('Opening viewer URL:', webViewerUrl); // Debug log
        
        // Track file view
        trackEngagement('file_view', 'files', {
          ticker: ticker,
          viewer_id: viewerData.viewer_id,
          file_created_at: viewerData.created_at || new Date().toISOString(),
          source: 'immediate_research'
        });
        
        // Open the web viewer immediately
        window.open(webViewerUrl, '_blank', 'noopener,noreferrer');
        
        // Show success message
        alert(`📊 SEC Documents opened for ${ticker.toUpperCase()}!\n\n` +
              `🔹 ${viewerData.sections_count} sections including SEC filings and financial statements\n` +
              `Your complete investment research is ready.\n` +
              `🔹 Shareable link: ${webViewerUrl}`);
      } else {
        const errorText = await viewerResponse.text();
        console.error('Failed to generate web viewer:', viewerResponse.status, errorText);
        alert(`Failed to generate SEC Documents for ${ticker}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error generating immediate research:', error);
      alert(`Error generating research viewer for ${ticker}: ${error.message}`);
    }
  };

  const fetchScheduleOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/schedule-options`);
      if (response.ok) {
        const data = await response.json();
        setScheduleOptions(data.schedule_options);
      }
    } catch (error) {
      console.error('Error fetching schedule options:', error);
    }
  };

  const scheduleTickerResearch = async (bookmarkId, ticker, scheduleOption) => {
    try {
      // Calculate days out from schedule option
      let daysOut = 0;
      if (scheduleOption === 'in_1_hour' || scheduleOption === 'in_2_hours' || scheduleOption === 'in_3_hours') {
        daysOut = 0;
      } else if (scheduleOption === 'tomorrow_9am' || scheduleOption === 'tomorrow_afternoon') {
        daysOut = 1;
      } else if (scheduleOption === 'next_week_monday') {
        daysOut = 7;
      } else if (scheduleOption === 'in_2_weeks') {
        daysOut = 14;
      }
      
      // Track schedule creation for engagement analytics
      trackEngagement('schedule_create', 'schedule', { 
        ticker: ticker,
        schedule_type: scheduleOption,
        days_out: daysOut
      });

      const response = await fetch(`${API_BASE_URL}/api/calendar/schedule-ticker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          schedule_option: scheduleOption,
          user_id: user
        }),
      });

      if (response.ok) {
        // Download the .ics file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `research_${ticker}_${scheduleOption}.ics`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        alert(`📅 Calendar event created for ${ticker} research! Check your downloads.`);
      }
    } catch (error) {
      console.error('Error scheduling ticker research:', error);
      alert('Failed to create calendar event');
    }
  };

  const scheduleBulkResearchSession = async (scheduleOption, tickerFilter = 'stale') => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/bulk-research-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user,
          schedule_option: scheduleOption,
          ticker_filter: tickerFilter
        }),
      });

      if (response.ok) {
        // Download the .ics file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_research_session_${scheduleOption}.ics`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Show sleek success badge for bulk scheduling
        const bulkSuccessBadge = document.createElement('div');
        bulkSuccessBadge.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 25px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          z-index: 10000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 500;
          font-size: 14px;
          animation: slideIn 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 8px;
        `;
        
        bulkSuccessBadge.innerHTML = `
          <svg style="width: 16px; height: 16px;" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 7V3a1 1 0 012 0v4h4a1 1 0 010 2h-4v4a1 1 0 01-2 0V9H4a1 1 0 010-2h4zM3 19h18a1 1 0 000-2H3a1 1 0 000 2z"/>
          </svg>
          Bulk research session scheduled - Check downloads
        `;
        
        document.body.appendChild(bulkSuccessBadge);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
          if (document.body.contains(bulkSuccessBadge)) {
            bulkSuccessBadge.style.animation = 'slideIn 0.3s ease-out reverse';
            setTimeout(() => {
              if (document.body.contains(bulkSuccessBadge)) {
                document.body.removeChild(bulkSuccessBadge);
              }
            }, 300);
          }
        }, 5000);
      }
    } catch (error) {
      console.error('Error scheduling bulk research session:', error);
      alert('Failed to create bulk calendar event');
    }
  };

  // Fetch schedule options on load - DEFERRED for performance
  React.useEffect(() => {
    // Defer to improve initial load
    setTimeout(() => {
      fetchScheduleOptions();
    }, 2000);
  }, []);

  const [calendarProviders, setCalendarProviders] = useState([]);
  const [calendarLinks, setCalendarLinks] = useState({});

  const fetchCalendarProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/providers`);
      if (response.ok) {
        const data = await response.json();
        setCalendarProviders(data.providers);
      }
    } catch (error) {
      console.error('Error fetching calendar providers:', error);
    }
  };

  const generateCalendarLinks = async (bookmarkId, ticker, scheduleOption) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/direct-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          schedule_option: scheduleOption,
          user_id: user
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCalendarLinks(data.calendar_links);
        setShowCalendarProviders(true);
      } else {
        throw new Error('Failed to generate calendar links');
      }
    } catch (error) {
      console.error('Error generating calendar links:', error);
      alert('Failed to generate calendar links');
    }
  };

  const openCalendarLink = (provider, link) => {
    if (provider === 'apple') {
      // For Apple Calendar, create a downloadable .ics file
      const blob = new Blob([atob(link.split(',')[1])], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `research_${selectedTickerForScheduling?.ticker}_apple.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      // For web calendar providers, open directly
      window.open(link, '_blank');
    }
    setShowCalendarProviders(false);
    setShowCalendarModal(false);
  };

  const [selectedResearchMaterials, setSelectedResearchMaterials] = useState([]);
  const [showResearchMaterials, setShowResearchMaterials] = useState(false);
  const [selectedScheduleOption, setSelectedScheduleOption] = useState(null);

  const researchMaterialOptions = [
    {
      id: 'company_presentation',
      name: 'Company Presentation',
      description: 'Latest investor presentation or pitch deck',
      icon: '📊',
      default: true,
      source: 'company_ir'
    },
    {
      id: 'conference_call',
      name: 'Latest Conference Call',
      description: 'Most recent earnings call transcript',
      icon: '🎤',
      default: true,
      source: 'earnings_call'
    },
    {
      id: 'form_10q',
      name: 'Last 10-Q',
      description: 'Most recent quarterly filing',
      icon: '📄',
      default: true,
      source: 'sec_edgar'
    },
    {
      id: 'form_10k',
      name: 'Last 10-K',
      description: 'Annual report and filing',
      icon: '📋',
      default: false,
      source: 'sec_edgar'
    },
    {
      id: 'financial_model',
      name: 'Financial Model Template',
      description: 'DCF and valuation model with current data',
      icon: '📈',
      default: true,
      source: 'generated'
    },
    {
      id: 'ai_bull_bear',
      name: 'AI Bull/Bear Analysis',
      description: 'AI-generated investment thesis summary',
      icon: '🤖',
      default: true,
      source: 'ai_generated'
    },
    {
      id: 'analyst_reports',
      name: 'Analyst Reports Summary',
      description: 'Aggregated analyst opinions and targets',
      icon: '🏦',
      default: false,
      source: 'financial_apis'
    },
    {
      id: 'competitor_analysis',
      name: 'Competitor Analysis',
      description: 'Competitive landscape and peer comparison',
      icon: '⚔️',
      default: false,
      source: 'generated'
    },
    {
      id: 'news_sentiment',
      name: 'Recent News & Sentiment',
      description: 'Latest news articles and sentiment analysis',
      icon: '📰',
      default: false,
      source: 'news_apis'
    }
  ];

  const initializeSelectedMaterials = () => {
    const defaultMaterials = researchMaterialOptions
      .filter(option => option.default)
      .map(option => option.id);
    setSelectedResearchMaterials(defaultMaterials);
  };

  const toggleResearchMaterial = (materialId) => {
    setSelectedResearchMaterials(prev => 
      prev.includes(materialId) 
        ? prev.filter(id => id !== materialId)
        : [...prev, materialId]
    );
  };

  const generateResearchPackage = async (bookmarkId, ticker, scheduleOption, materials) => {
    try {
      // Show loading state
      const loadingToast = alert('🔄 Generating comprehensive research package... This will take 2-3 minutes.');
      
      const response = await fetch(`${API_BASE_URL}/api/research/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          schedule_option: scheduleOption,
          research_materials: materials,
          user_id: user
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store calendar links for later use
        setCalendarLinks(data.calendar_links);
        setShowCalendarProviders(true);
        
        // Show success message with research info
        alert(`🎉 Research package generation started for ${ticker}!\n\n` +
              `📊 Materials: ${data.materials_count} items\n` +
              `⏱️ Estimated completion: ${data.estimated_completion}\n` +
              `🔗 Dashboard will be available at: ${data.research_dashboard_url}\n\n` +
              `Your calendar event will include a direct link to the research dashboard.`);
        
        // Optional: Poll for completion status
        setTimeout(() => {
          checkResearchStatus(ticker);
        }, 180000); // Check after 3 minutes
        
      } else {
        throw new Error('Failed to generate research package');
      }
    } catch (error) {
      console.error('Error generating research package:', error);
      alert('❌ Failed to generate research package. Please try again.');
    }
  };

  const checkResearchStatus = async (ticker) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/research/${ticker}/status?user_id=${user}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'completed') {
          alert(`✅ Research package for ${ticker} is ready!\n` +
                `🔗 View dashboard: ${window.location.origin}${data.dashboard_url}`);
        } else if (data.status === 'generating') {
          alert(`🔄 Research package for ${ticker} is still generating...\n` +
                `Check back in a few minutes or look for the calendar reminder.`);
        }
      }
    } catch (error) {
      console.error('Error checking research status:', error);
    }
  };

  const openResearchDashboard = (ticker) => {
    window.location.href = `/api/research/${ticker.toLowerCase()}/dashboard`;
  };

  // Tweet generator functions
  const generateTweet = () => {
    let tweetText = '';
    
    if (newPollMode === 'x_poll') {
      const question = pollQuestion.trim();
      const option1 = pollOptions[0].trim();
      const option2 = pollOptions[1].trim();
      const option3 = pollOptions[2].trim();
      const option4 = pollOptions[3].trim();
      
      if (!question) {
        alert('Please enter a poll question');
        return;
      }
      
      if (!option1 || !option2) {
        alert('Please enter at least 2 poll options');
        return;
      }
      
      // For X polls, we create the tweet text with options
      tweetText = `${question}\n\nA) ${option1}\nB) ${option2}`;
      if (option3) tweetText += `\nC) ${option3}`;
      if (option4) tweetText += `\nD) ${option4}`;
      tweetText += '\n\n@savetotickler';
      
      // Open X with the poll text (user will need to enable poll manually)
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
      window.open(twitterUrl, '_blank');
      
    } else if (newPollMode === 'requests') {
      const ideasTextValue = ideasText.trim();
      
      if (!ideasTextValue) {
        alert('Please enter your request for ideas');
        return;
      }
      
      // Add @savetotickler mention if not present
      tweetText = ideasTextValue;
      if (!tweetText.includes('@savetotickler')) {
        tweetText += ' @savetotickler';
      }
      
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
      window.open(twitterUrl, '_blank');
    }
    
    // Close modal and reset form
    setShowTweetGenerator(false);
    setPollQuestion('');
    setPollOptions(['', '', '', '']);
    setIdeasText('');
  };


  const checkPollMentionsManually = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/twitter/check-mentions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('✅ Poll replies checked! Any new @savetotickler mentions have been imported to your account.');
          fetchData(); // Refresh the dashboard
        } else {
          alert(`Error: ${data.error || 'Failed to check mentions'}`);
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to check poll replies'}`);
      }
    } catch (error) {
      console.error('Error checking mentions:', error);
      alert('Failed to check poll replies. Please try again.');
    }
  };


  const generateCalendarLinksWithMaterials = async (bookmarkId, ticker, scheduleOption, materials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/calendar/direct-links-with-materials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookmark_id: bookmarkId,
          ticker: ticker,
          schedule_option: scheduleOption,
          research_materials: materials,
          user_id: user
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCalendarLinks(data.calendar_links);
        setShowCalendarProviders(true);
      } else {
        throw new Error('Failed to generate calendar links');
      }
    } catch (error) {
      console.error('Error generating calendar links:', error);
      alert('Failed to generate calendar links');
    }
  };

  // Fetch calendar providers on load - DEFERRED for performance
  React.useEffect(() => {
    // Defer to improve initial load
    setTimeout(() => {
      fetchCalendarProviders();
    }, 2000);
  }, []);

  // Fetch scheduled date when opening calendar modal with auto-schedule enabled
  React.useEffect(() => {
    const fetchIdeaSchedule = async () => {
      // Skip fetching if using manual schedule flow
      if (useManualScheduleFlow) {
        setIdeaScheduleLoading(false);
        return;
      }
      
      // Only fetch when modal opens with a selected ticker
      if (!showCalendarModal || !selectedTickerForScheduling) {
        // Modal closed - reset state
        if (!showCalendarModal) {
          setIdeaScheduleLoading(false);
          setIdeaScheduleInfo(null);
        }
        return;
      }
      
      console.log('📋 Calendar Modal opened:', {
        bookmarkId: selectedTickerForScheduling?.bookmarkId,
        tickerId: selectedTickerForScheduling?.id,
        ticker: selectedTickerForScheduling?.ticker,
        dossierEnabled: dossierSettings.enabled
      });
      
      if (dossierSettings.enabled) {
        setIdeaScheduleLoading(true);
        try {
          const userId = user?.id || user?.email || user;
          let ideaId = selectedTickerForScheduling.bookmarkId || selectedTickerForScheduling.id;
          const ticker = selectedTickerForScheduling.ticker;
          
          // Strip _ticker_N suffix if present (display IDs for multi-ticker ideas)
          if (ideaId && ideaId.includes('_ticker_')) {
            ideaId = ideaId.replace(/_ticker_\d+$/, '');
          }
          
          // If we have an ideaId, check by idea first
          if (userId && ideaId) {
            const url = `${API_BASE_URL}/api/research-schedule/${userId}/idea/${ideaId}`;
            const response = await fetch(url);
            if (response.ok) {
              const data = await response.json();
              console.log('📅 Schedule response (by idea):', data);
              if (data.scheduled) {
                setIdeaScheduleInfo(data);
                setIdeaScheduleLoading(false);
                return;
              }
            }
          }
          
          // If no ideaId or not found by idea, check by ticker symbol
          if (userId && ticker) {
            const tickerUrl = `${API_BASE_URL}/api/research-schedule/${userId}/ticker/${ticker}`;
            const tickerResponse = await fetch(tickerUrl);
            if (tickerResponse.ok) {
              const tickerData = await tickerResponse.json();
              console.log('📅 Schedule response (by ticker):', tickerData);
              // Mark that this is from Collections (no bookmarkId)
              setIdeaScheduleInfo({
                ...tickerData,
                isFromCollections: !ideaId
              });
              setIdeaScheduleLoading(false);
              return;
            }
          }
          
          // Not scheduled anywhere - mark as not scheduled, and note if from Collections
          setIdeaScheduleInfo({ 
            scheduled: false,
            isFromCollections: !ideaId 
          });
        } catch (error) {
          console.error('Error fetching idea schedule:', error);
          setIdeaScheduleInfo({ scheduled: false });
        } finally {
          setIdeaScheduleLoading(false);
        }
      } else {
        setIdeaScheduleLoading(false);
        setIdeaScheduleInfo(null);
      }
    };
    fetchIdeaSchedule();
  }, [showCalendarModal, selectedTickerForScheduling, dossierSettings.enabled, user, useManualScheduleFlow]);

  // Enhanced Calendar Modal Component (Restored from Backup)
  const CalendarModal = () => {
    const [isEditingSchedule, setIsEditingSchedule] = useState(false);
    
    // Debug logging
    console.log('🎯 CalendarModal render:', {
      showCalendarModal,
      ticker: selectedTickerForScheduling?.ticker,
      earningsLoading,
      earningsData: earningsData ? 'has data' : 'null',
      ideaScheduleLoading,
      ideaScheduleInfo
    });
    
    if (!showCalendarModal || !selectedTickerForScheduling) return null;
    
    // Define ticker early so it's available throughout the component
    const ticker = selectedTickerForScheduling.ticker;

    // If using manual schedule flow (from Collections), skip auto-schedule logic entirely
    // Go directly to the step-based flow below
    if (useManualScheduleFlow) {
      // Skip all auto-schedule logic and fall through to step-based UI
    }
    // If auto-schedule is enabled and not in manual flow, check schedule status
    else if (dossierSettings.enabled) {
      // Quick check for foreign tickers:
      // 1. Contain . like CPH.TO, SHOP.TO, etc. (Canadian/international exchanges)
      // 2. ADRs ending in F or Y with 5+ chars (like MCDIF, BYDDY, TOYOF)
      const isForeignTicker = ticker && (
        ticker.includes('.') || 
        (ticker.length >= 5 && /[FY]$/i.test(ticker))
      );
      const noFilings = isForeignTicker || (!secAvailability.loading && !secAvailability.hasFilings && !secAvailability.hasForeignFilings);
      
      // No SEC filings - show unavailable message immediately
      if (noFilings) {
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCalendarModal(false)}
          >
            <div 
              className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg w-full max-w-sm`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    ${ticker}
                  </h2>
                  <button 
                    onClick={() => setShowCalendarModal(false)}
                    className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                  >×</button>
                </div>
                
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Scheduling Not Available
                      </p>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {ticker} doesn't have SEC filings. Research scheduling currently only works for US-listed companies with SEC filings.
                      </p>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        We're working to expand coverage to more markets — stay tuned!
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className={`w-full mt-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      }
      
      // Show loading spinner while fetching schedule info OR SEC availability
      // This prevents showing stale state while async operations complete
      if (ideaScheduleLoading || ideaScheduleInfo === null || secAvailability.loading) {
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCalendarModal(false)}
          >
            <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg p-6`}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className={`mt-3 text-sm text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading schedule...</p>
            </div>
          </div>
        );
      }
      
      // Idea IS in schedule - show view/edit mode
      if (ideaScheduleInfo?.scheduled) {
        // Format the date nicely
        const scheduledDate = new Date(ideaScheduleInfo.scheduled_date + 'T00:00:00');
        const formattedDate = scheduledDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric',
          year: 'numeric'
        });
        
        // Format time
        const timeStr = ideaScheduleInfo.scheduled_time || '09:00';
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const formattedTime = `${hour > 12 ? hour - 12 : hour}:${minutes || '00'} ${hour >= 12 ? 'PM' : 'AM'}`;
        
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowCalendarModal(false); setIsEditingSchedule(false); }}
          >
            <div 
              className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg w-full max-w-sm`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    ${selectedTickerForScheduling.ticker}
                  </h2>
                  <button 
                    onClick={() => { setShowCalendarModal(false); setIsEditingSchedule(false); }}
                    className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                  >×</button>
                </div>
                
                {/* View Mode - Show scheduled date/time */}
                <div>
                  <p className={`text-xs uppercase tracking-wide mb-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Scheduled Folder
                  </p>
                  
                  <div className={`text-center py-4 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                    <p className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formattedTime}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      {formattedDate}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 mt-6">
                    <button
                      onClick={() => { setShowCalendarModal(false); setIsEditingSchedule(false); }}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border ${
                        isDarkMode 
                          ? 'border-slate-600 text-slate-300 hover:bg-slate-800' 
                          : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        // Open the 3-option reschedule modal
                        openRescheduleModal({
                          ticker: selectedTickerForScheduling.ticker,
                          company_name: selectedTickerForScheduling.company_name,
                          scheduled_date: ideaScheduleInfo.scheduled_date,
                          source: ideaScheduleInfo.source || 'manual'
                        });
                        setShowCalendarModal(false);
                      }}
                      style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                      className="flex-1 px-4 py-2 text-sm rounded-lg text-white font-medium hover:opacity-90"
                    >
                      Recycle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // Idea NOT in schedule - show "not scheduled" view with 3 options (Auto, After Earnings, Pick a Date)
      // This applies to both Collections items (no bookmarkId) and Ideas tab items (with bookmarkId)
      
      // noFilings already defined above in line 9383
      
      // No SEC filings - show unavailable message
      if (noFilings) {
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCalendarModal(false)}
          >
            <div 
              className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg w-full max-w-sm`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    ${ticker}
                  </h2>
                  <button 
                    onClick={() => setShowCalendarModal(false)}
                    className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                  >×</button>
                </div>
                
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Scheduling Not Available
                      </p>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {ticker} doesn't have SEC filings. Research scheduling currently only works for US-listed companies with SEC filings.
                      </p>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        We're working to expand coverage to more markets — stay tuned!
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className={`w-full mt-4 px-4 py-2 text-sm rounded-lg border ${
                    isDarkMode 
                      ? 'border-slate-600 text-slate-300 hover:bg-slate-800' 
                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      }
      
      // Has SEC filings - show schedule options (3 options: Auto, After Earnings, Pick a Date)
      return (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowCalendarModal(false)}
        >
          <div 
            className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg w-full max-w-sm shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  Schedule ${ticker}
                </h3>
                <button 
                  onClick={() => setShowCalendarModal(false)}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                >×</button>
              </div>
              
              <div className="space-y-3">
                {/* Auto Schedule - Orange */}
                <button
                  onClick={async () => {
                    try {
                      const userId = user?.id || user?.email || 'demo_user';
                      const response = await fetch(`${API_BASE_URL}/api/watchlists/add-ticker`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: userId,
                          ticker: ticker
                        })
                      });
                      if (response.ok) {
                        showNotification(`${ticker} added to auto-schedule`, 'success');
                        setShowCalendarModal(false);
                      } else {
                        showNotification('Failed to add to auto-schedule', 'error');
                      }
                    } catch (error) {
                      console.error('Error adding to auto-schedule:', error);
                      showNotification('Error adding to auto-schedule', 'error');
                    }
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-orange-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Auto Schedule</p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Let the system pick the best time
                      </p>
                    </div>
                  </div>
                </button>
                
                {/* After Next Earnings - Green */}
                <button
                  onClick={async () => {
                    if (earningsData && earningsData.day_after_earnings) {
                      try {
                        const userId = user?.id || user?.email || 'demo_user';
                        const response = await fetch(`${API_BASE_URL}/api/research-schedule/${userId}/ticker`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            ticker: ticker,
                            scheduled_date: earningsData.day_after_earnings,
                            scheduled_time: '09:00',
                            company_name: selectedTickerForScheduling?.company_name,
                            source: 'manual'
                          })
                        });
                        if (response.ok) {
                          showNotification(`${ticker} scheduled for after earnings (Est. ${new Date(earningsData.day_after_earnings).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`, 'success');
                          fetchResearchSchedule();
                          setShowCalendarModal(false);
                        } else {
                          const error = await response.json();
                          showNotification(`Failed to schedule: ${error.detail || 'Unknown error'}`, 'error');
                        }
                      } catch (error) {
                        console.error('Error scheduling:', error);
                        showNotification('Error scheduling', 'error');
                      }
                    }
                  }}
                  disabled={earningsLoading || !earningsData}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-green-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-green-500 hover:bg-green-50'
                  } ${(earningsLoading || !earningsData) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>After Next Earnings</p>
                      {earningsLoading ? (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading earnings date...</p>
                      ) : earningsData ? (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Est. {new Date(earningsData.day_after_earnings).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          <span className={`ml-1 text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            (Earnings est. {new Date(earningsData.next_earnings_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                          </span>
                        </p>
                      ) : (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No earnings data available</p>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Pick a Date - Purple */}
                <button
                  onClick={() => {
                    setUseManualScheduleFlow(true);
                    setIdeaScheduleLoading(false);
                    setIdeaScheduleInfo({ scheduled: false, isFromCollections: true });
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setCalendarScheduleData(prev => ({
                      ...prev,
                      date: tomorrow.toISOString().split('T')[0],
                      time: '09:00'
                    }));
                    setCurrentCalendarStep(1);
                    checkSECAvailability(ticker);
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-purple-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-purple-500 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Pick a Date</p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Choose your own schedule
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    // If showing calendar providers, show provider selection screen
    if (showCalendarProviders) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowCalendarModal(false)} onKeyDown={(e) => e.key === 'Escape' && setShowCalendarModal(false)}>
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-md w-full`} onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Choose Your Calendar</h2>
                <button 
                  onClick={() => setShowCalendarProviders(false)}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                >✕</button>
              </div>
              
              <p className={`${isDarkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>Select your calendar app to add this reminder:</p>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    openCalendarEvent('google');
                    setShowCalendarProviders(false);
                    setShowCalendarModal(false);
                    showSchedulingSuccessBadge(selectedTickerForScheduling.ticker, calendarScheduleData.date, calendarScheduleData.time, 'calendar');
                  }}
                  className={`w-full flex items-center justify-start p-3 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white font-bold text-sm mr-3">G</div>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Google Calendar</span>
                </button>
                
                <button
                  onClick={() => {
                    openCalendarEvent('outlook');
                    setShowCalendarProviders(false);
                    setShowCalendarModal(false);
                    showSchedulingSuccessBadge(selectedTickerForScheduling.ticker, calendarScheduleData.date, calendarScheduleData.time, 'calendar');
                  }}
                  className={`w-full flex items-center justify-start p-3 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-sm mr-3">O</div>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Outlook Calendar</span>
                </button>
                
                <button
                  onClick={() => {
                    openCalendarEvent('yahoo');
                    setShowCalendarProviders(false);
                    setShowCalendarModal(false);
                    showSchedulingSuccessBadge(selectedTickerForScheduling.ticker, calendarScheduleData.date, calendarScheduleData.time, 'calendar');
                  }}
                  className={`w-full flex items-center justify-start p-3 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <div className="w-8 h-8 bg-purple-600 rounded flex items-center justify-center text-white font-bold text-sm mr-3">Y</div>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Yahoo Calendar</span>
                </button>
              </div>
              
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => {
                    setShowCalendarProviders(false);
                    setShowCalendarModal(false);
                  }}
                  className={`w-full p-2 text-sm ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2"
        onMouseDown={(e) => {
          // Only close if clicking directly on backdrop
          if (e.target === e.currentTarget) {
            setShowCalendarModal(false);
            setCurrentCalendarStep(1);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setShowCalendarModal(false);
            setCurrentCalendarStep(1);
          }
        }}
        tabIndex={-1}
      >
        <div 
          className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg w-full max-w-2xl max-h-[95vh] overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Schedule Research</h2>
              <Button variant="outline" size="sm" className={isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''} onClick={() => {
                setShowCalendarModal(false);
                setCurrentCalendarStep(1);
              }}>×</Button>
            </div>

            {/* Progress Steps - Simplified for Mobile */}
            <div className="mb-6">
              <div className="text-center mb-4">
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Step {currentCalendarStep} of {calendarSteps.length}</span>
                <h3 className="text-lg font-semibold text-orange-600">{calendarSteps[currentCalendarStep - 1]?.title}</h3>
              </div>
              <div className={`w-full ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'} rounded-full h-2`}>
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentCalendarStep / calendarSteps.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Step Content */}
            {currentCalendarStep === 1 && (
              <Card className={isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>When should we remind you to research this idea?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <form onSubmit={(e) => { e.preventDefault(); return false; }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="w-full">
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Date</label>
                        <input
                          type="date"
                          className={`w-full h-[42px] px-3 py-2.5 border rounded-lg font-medium text-base transition-colors ${
                            isDarkMode 
                              ? 'bg-slate-700 border-slate-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          value={calendarScheduleData.date}
                          onChange={(e) => setCalendarScheduleData({...calendarScheduleData, date: e.target.value})}
                        />
                      </div>
                      <div className="w-full">
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Time</label>
                        <select
                          className={`w-full h-[42px] px-3 py-2.5 border rounded-lg font-medium text-base transition-colors appearance-none ${
                            isDarkMode 
                              ? 'bg-slate-700 border-slate-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='${isDarkMode ? '%2394a3b8' : '%236b7280'}' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                          }}
                          value={calendarScheduleData.time || '09:00'}
                          onChange={(e) => setCalendarScheduleData({...calendarScheduleData, time: e.target.value})}
                        >
                          {Array.from({length: 96}, (_, i) => {
                            const hour = Math.floor(i / 4);
                            const minute = (i % 4) * 15;
                            const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            const period = hour >= 12 ? 'PM' : 'AM';
                            return (
                              <option key={value} value={value}>
                                {displayHour}:{String(minute).padStart(2, '0')} {period}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                  </form>
                  
                  {/* Quick Options */}
                  <div className="space-y-2">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Quick Options:</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button type="button" variant="outline" size="sm" className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-600' : ''} onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setCalendarScheduleData({
                          ...calendarScheduleData, 
                          date: tomorrow.toISOString().split('T')[0],
                          time: '09:00'
                        });
                      }}>
                        Tomorrow 9 AM
                      </Button>
                      <Button type="button" variant="outline" size="sm" className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-600' : ''} onClick={() => {
                        const nextWeek = new Date();
                        nextWeek.setDate(nextWeek.getDate() + 7);
                        setCalendarScheduleData({
                          ...calendarScheduleData, 
                          date: nextWeek.toISOString().split('T')[0],
                          time: '10:00'
                        });
                      }}>
                        Next Week
                      </Button>
                      <Button type="button" variant="outline" size="sm" className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-600' : ''} onClick={() => {
                        const nextMonth = new Date();
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        setCalendarScheduleData({
                          ...calendarScheduleData, 
                          date: nextMonth.toISOString().split('T')[0],
                          time: '10:00'
                        });
                      }}>
                        Next Month
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

                        {currentCalendarStep === 2 && (() => {
              // Compute filing status variables (same as dossier modal)
              const noFilingsAtAll = !secAvailability.loading && !secAvailability.hasFilings && !secAvailability.hasForeignFilings;
              const isUSFiler = secAvailability.hasFilings;
              const isForeignFiler = secAvailability.hasForeignFilings && !secAvailability.hasFilings;
              
              return (
              <Card className={isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Select Documents</CardTitle>
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Choose which SEC filings to include for {selectedTickerForScheduling?.ticker?.toUpperCase()}
                  </p>
                  
                  {/* SEC Availability Status */}
                  <div className={`mt-3 p-3 rounded-lg ${
                    secAvailability.loading 
                      ? (isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200')
                      : secAvailability.hasFilings 
                        ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200')
                        : secAvailability.hasForeignFilings
                          ? (isDarkMode ? 'bg-purple-900/30 border border-purple-700' : 'bg-purple-50 border border-purple-200')
                          : (isDarkMode ? 'bg-[#141c2e]/50 border border-slate-600' : 'bg-slate-100 border border-slate-300')
                  }`}>
                    <div className="flex items-center gap-2">
                      {secAvailability.loading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Checking SEC availability...</span>
                        </>
                      ) : secAvailability.hasFilings ? (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}><strong>US Company</strong> — SEC filings available</span>
                        </>
                      ) : secAvailability.hasForeignFilings ? (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}><strong>Foreign Company</strong> — 20-F/6-K available</span>
                        </>
                      ) : (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>No SEC filings found</span>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {/* 10-K Annual Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-blue-400' : 'text-blue-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>10-K Annual Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Comprehensive annual business disclosure
                          </p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.include10K} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include10K: e.target.checked})} disabled={noFilingsAtAll || !isUSFiler} className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'} focus:ring-blue-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* 10-Q Quarterly Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-green-900/50' : 'bg-green-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-green-400' : 'text-green-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>10-Q Quarterly Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Quarterly financial reports
                          </p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.include10Q} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include10Q: e.target.checked})} disabled={noFilingsAtAll || !isUSFiler} className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-green-600'} focus:ring-green-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* 8-K Current Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-orange-400' : 'text-orange-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>8-K Current Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Material events and changes
                          </p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.include8K} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include8K: e.target.checked})} disabled={noFilingsAtAll || !isUSFiler} className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600'} focus:ring-orange-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* Proxy Statements */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-pink-900/50' : 'bg-pink-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-pink-400' : 'text-pink-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>Proxy Statements</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Executive compensation & governance
                          </p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.includeProxy} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeProxy: e.target.checked})} disabled={noFilingsAtAll || !isUSFiler} className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-pink-600'} focus:ring-pink-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* Foreign Filer Section - Always show, disable if not foreign filer */}
                    <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>FOREIGN ISSUER FILINGS</p>
                    </div>
                    
                    {/* 20-F Annual Report */}
                    <label className={`flex items-center justify-between ${(!isForeignFiler || noFilingsAtAll) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-purple-400' : 'text-purple-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>20-F Annual Reports</span>
                          <p className={`text-sm ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>Foreign company annual reports</p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.include20F} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include20F: e.target.checked})} disabled={!isForeignFiler || noFilingsAtAll} className={`h-4 w-4 ${(!isForeignFiler || noFilingsAtAll) ? 'text-gray-400 cursor-not-allowed' : 'text-purple-600'} focus:ring-purple-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* 6-K Reports */}
                    <label className={`flex items-center justify-between ${(!isForeignFiler || noFilingsAtAll) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-indigo-400' : 'text-indigo-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>6-K Reports</span>
                          <p className={`text-sm ${(!isForeignFiler || noFilingsAtAll) ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>Foreign company interim reports</p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.include6K} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include6K: e.target.checked})} disabled={!isForeignFiler || noFilingsAtAll} className={`h-4 w-4 ${(!isForeignFiler || noFilingsAtAll) ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600'} focus:ring-indigo-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                    
                    {/* Financial Statements */}
                    <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>ADDITIONAL DATA</p>
                    </div>
                    <label className={`flex items-center justify-between ${noFilingsAtAll ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${noFilingsAtAll ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${noFilingsAtAll ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-emerald-300' : 'text-emerald-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${noFilingsAtAll ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-emerald-400' : 'text-emerald-700')}`}>Financial Statements</span>
                          <p className={`text-sm ${noFilingsAtAll ? (isDarkMode ? 'text-slate-600' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>Income, Balance Sheet, Cash Flow</p>
                        </div>
                      </div>
                      <input type="checkbox" checked={secFilingPreferences.includeFinancialStatements} onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeFinancialStatements: e.target.checked})} disabled={noFilingsAtAll} className={`h-4 w-4 ${noFilingsAtAll ? 'text-gray-400 cursor-not-allowed' : 'text-emerald-600'} focus:ring-emerald-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`} />
                    </label>
                  </div>
                </CardContent>
              </Card>
              );
            })()}

            {currentCalendarStep === 3 && (
              <Card className={isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>How would you like to be reminded?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Reminder Method</label>
                    <div className="space-y-2">
                      <label className={`flex items-center ${isDarkMode ? 'text-slate-200' : ''}`}>
                        <input
                          type="radio"
                          name="reminderType"
                          value="email"
                          checked={calendarScheduleData.reminderType === 'email'}
                          onChange={(e) => setCalendarScheduleData({...calendarScheduleData, reminderType: e.target.value})}
                          className="mr-2"
                        />
                        Email Reminder
                      </label>
                      <label className={`flex items-center ${isDarkMode ? 'text-slate-200' : ''}`}>
                        <input
                          type="radio"
                          name="reminderType"
                          value="calendar"
                          checked={calendarScheduleData.reminderType === 'calendar'}
                          onChange={(e) => setCalendarScheduleData({...calendarScheduleData, reminderType: e.target.value})}
                          className="mr-2"
                        />
                        Calendar Event Only
                      </label>
                      <label className={`flex items-center ${isDarkMode ? 'text-slate-200' : ''}`}>
                        <input
                          type="radio"
                          name="reminderType"
                          value="both"
                          checked={calendarScheduleData.reminderType === 'both'}
                          onChange={(e) => setCalendarScheduleData({...calendarScheduleData, reminderType: e.target.value})}
                          className="mr-2"
                        />
                        Both Email & Calendar
                      </label>
                    </div>
                  </div>

                  {/* SEC Documents Download Options */}
                  {secFilingPreferences.includeDossier && (
                    <div className={`${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'} p-4 rounded-lg`}>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>📊 SEC Documents Options</label>
                      <div className="space-y-2">
                        <label className={`flex items-center ${isDarkMode ? 'text-slate-200' : ''}`}>
                          <input
                            type="checkbox"
                            checked={downloadPreferences.immediateDownload}
                            onChange={(e) => setDownloadPreferences({...downloadPreferences, immediateDownload: e.target.checked})}
                            className={`mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 ${isDarkMode ? 'border-slate-500 bg-slate-600' : 'border-gray-300'} rounded`}
                          />
                          <span className="text-sm">Download PDF immediately after scheduling</span>
                        </label>
                        <label className={`flex items-center ${isDarkMode ? 'text-slate-200' : ''}`}>
                          <input
                            type="checkbox"
                            checked={downloadPreferences.includeInEmail}
                            onChange={(e) => setDownloadPreferences({...downloadPreferences, includeInEmail: e.target.checked})}
                            className={`mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 ${isDarkMode ? 'border-slate-500 bg-slate-600' : 'border-gray-300'} rounded`}
                          />
                          <span className="text-sm">Include PDF as email attachment (when available)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Reminder Time</label>
                    <select
                      className={`w-full p-2 border rounded-md ${isDarkMode ? 'bg-slate-600 border-slate-500 text-white' : 'border-gray-300'}`}
                      value={calendarScheduleData.reminderTime}
                      onChange={(e) => setCalendarScheduleData({...calendarScheduleData, reminderTime: e.target.value})}
                    >
                      <option value="5min">5 minutes before</option>
                      <option value="15min">15 minutes before</option>
                      <option value="30min">30 minutes before</option>
                      <option value="1hour">1 hour before</option>
                      <option value="1day">1 day before</option>
                    </select>
                  </div>

                  {calendarScheduleData.reminderType !== 'email' && (
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Calendar Provider</label>
                      <select
                        className={`w-full p-2 border rounded-md ${isDarkMode ? 'bg-slate-600 border-slate-500 text-white' : 'border-gray-300'}`}
                        value={calendarScheduleData.calendarProvider}
                        onChange={(e) => setCalendarScheduleData({...calendarScheduleData, calendarProvider: e.target.value})}
                      >
                        <option value="default">Default Calendar</option>
                        <option value="google">Google Calendar</option>
                        <option value="outlook">Outlook Calendar</option>
                        <option value="apple">Apple Calendar</option>
                      </select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {currentCalendarStep === 4 && (
              <Card className={isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : ''}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Review Your Reminder Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`${isDarkMode ? 'bg-slate-600' : 'bg-gray-50'} p-4 rounded-lg`}>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Date & Time:</div>
                        <div className={isDarkMode ? 'text-slate-200' : ''}>{calendarScheduleData.date} at {calendarScheduleData.time}</div>
                      </div>
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Reminder Method:</div>
                        <div className={`capitalize ${isDarkMode ? 'text-slate-200' : ''}`}>{calendarScheduleData.reminderType}</div>
                      </div>
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Reminder Time:</div>
                        <div className={isDarkMode ? 'text-slate-200' : ''}>{calendarScheduleData.reminderTime}</div>
                      </div>
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Ticker:</div>
                        <div className={isDarkMode ? 'text-slate-200' : ''}>{selectedTickerForScheduling?.ticker}</div>
                      </div>
                    </div>
                  </div>

                  {/* Research Preferences Summary */}
                  <div className={`${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} p-4 rounded-lg`}>
                    <div className="text-sm">
                      <div className={`font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        Research Sources:
                      </div>
                      <div className={`grid grid-cols-2 gap-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        <label className={`flex items-center ${secFilingPreferences.include10K ? 'font-medium' : 'opacity-50'}`}>
                          <span className={`w-4 h-4 mr-2 ${secFilingPreferences.include10K ? 'text-green-600' : 'text-gray-400'}`}>
                            {secFilingPreferences.include10K ? '✓' : '○'}
                          </span>
                          10-K Annual Reports
                        </label>
                        <label className={`flex items-center ${secFilingPreferences.include10Q ? 'font-medium' : 'opacity-50'}`}>
                          <span className={`w-4 h-4 mr-2 ${secFilingPreferences.include10Q ? 'text-green-600' : 'text-gray-400'}`}>
                            {secFilingPreferences.include10Q ? '✓' : '○'}
                          </span>
                          10-Q Quarterly Reports
                        </label>
                        <label className={`flex items-center ${secFilingPreferences.include8K ? 'font-medium' : 'opacity-50'}`}>
                          <span className={`w-4 h-4 mr-2 ${secFilingPreferences.include8K ? 'text-green-600' : 'text-gray-400'}`}>
                            {secFilingPreferences.include8K ? '✓' : '○'}
                          </span>
                          8-K Current Reports
                        </label>
                        <label className={`flex items-center ${secFilingPreferences.includeProxy ? 'font-medium' : 'opacity-50'}`}>
                          <span className={`w-4 h-4 mr-2 ${secFilingPreferences.includeProxy ? 'text-green-600' : 'text-gray-400'}`}>
                            {secFilingPreferences.includeProxy ? '✓' : '○'}
                          </span>
                          Proxy Statements
                        </label>
                      </div>
                      {secFilingPreferences.includeDossier && (
                        <div className={`mt-2 p-2 rounded border ${isDarkMode ? 'bg-blue-900/50 border-blue-700' : 'bg-blue-100'}`}>
                          <span className={`font-medium flex items-center gap-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            SEC Documents will be generated and shared
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Additional Notes (Optional)</label>
                    <textarea
                      className={`w-full p-2 border rounded-md ${isDarkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-gray-300'}`}
                      rows="3"
                      placeholder="Add any additional notes for your future self..."
                      value={calendarScheduleData.notes}
                      onChange={(e) => setCalendarScheduleData({...calendarScheduleData, notes: e.target.value})}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Preview of what will be included */}
                  <div className={`${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'} p-4 rounded-lg`}>
                    <div className="text-sm">
                      <div className={`font-medium mb-2 ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Your reminder will include:</div>
                      <ul className={`space-y-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        <li>• Original investment idea content</li>
                        <li>• Ticker: {selectedTickerForScheduling?.ticker}</li>
                        <li>• Reminder at: {calendarScheduleData.date} {calendarScheduleData.time}</li>
                        <li>• Financial data and statements from latest filings</li>
                        {/* Temporarily removed Pro features
                        {secFilingPreferences.includeAIAnalysis && (
                          <li className="flex items-center gap-2">
                            <svg className="h-4 w-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                            </svg>
                            • AI Investment Analysis (Pro Feature)
                          </li>
                        )}
                        */}
                        {secFilingPreferences.includeDossier && (
                          <li className="flex items-center gap-2">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            SEC Documents with combined research documents
                          </li>
                        )}
                        {calendarScheduleData.notes && <li>• Your notes: {calendarScheduleData.notes}</li>}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <Button 
                type="button"
                variant="outline" 
                onClick={handleCalendarPrevious}
                disabled={currentCalendarStep === 1}
                className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-600' : ''}
              >
                Previous
              </Button>
              
              {currentCalendarStep < calendarSteps.length ? (
                <Button 
                  type="button"
                  onClick={handleCalendarNext}
                  disabled={!calendarScheduleData.date || !calendarScheduleData.time}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Next
                </Button>
              ) : (
                <Button 
                  type="button"
                  onClick={handleEnhancedSchedule}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Schedule Reminder
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fetch email settings, dashboard ideas, and subscription on load
  React.useEffect(() => {
    if (user) {
      fetchEmailSettings();
      fetchDashboardIdeas();
      fetchSubscriptionDetails();
      fetchDossierSettings(); // Also fetch dossier settings for auto-schedule modal
    }
  }, [user]);

  // Fetch scheduled research and dossier settings when switching to settings tab (schedules view)
  React.useEffect(() => {
    if (activeTab === 'settings' && settingsView === 'schedules' && user) {
      fetchScheduledResearch();
      fetchDossierSettings();
      fetchResearchSchedule();
    }
  }, [activeTab, settingsView, user]);

  // Fetch schedule data when switching to schedule tab
  React.useEffect(() => {
    if (activeTab === 'schedule' && user) {
      // Reset initialized state to force loading spinner before fetch
      setScheduleDataInitialized(false);
      // Set loading immediately to avoid flash of "No scheduled research" message
      setScheduledResearchLoading(true);
      // Clear first render flag after a delay to ensure loading shows
      if (scheduleTabFirstRender) {
        setTimeout(() => setScheduleTabFirstRender(false), 100);
      }
      fetchScheduledResearch();
      fetchDossierSettings();
      fetchResearchSchedule();
    }
  }, [activeTab, user]);

  // Fetch schedule data when workspace mode is active and a calendar panel is selected
  React.useEffect(() => {
    if (showWorkspace && user) {
      const hasCalendarPanel = workspacePanels.left === 'calendar' || 
                               workspacePanels.middle === 'calendar' || 
                               workspacePanels.right === 'calendar';
      if (hasCalendarPanel && !scheduleDataInitialized) {
        fetchScheduledResearch();
        fetchDossierSettings();
        fetchResearchSchedule();
      }
    }
  }, [showWorkspace, workspacePanels, user, scheduleDataInitialized]);

  // Fetch research items when switching to research tab
  React.useEffect(() => {
    if (activeTab === 'research' && user) {
      fetchResearchItems();
    }
  }, [activeTab, user]);

  // Initialize last checked timestamps from localStorage and start background checker
  React.useEffect(() => {
    if (!user) return;
    
    // Load timestamps from localStorage or use current time as default
    const storedIdeasCheck = localStorage.getItem('tickler_ideas_last_checked');
    const storedLibraryCheck = localStorage.getItem('tickler_library_last_checked');
    
    const now = new Date().toISOString();
    const ideasCheck = storedIdeasCheck || now;
    const libraryCheck = storedLibraryCheck || now;
    
    setIdeasLastChecked(ideasCheck);
    setLibraryLastChecked(libraryCheck);
    
    // Save to localStorage if not exists
    if (!storedIdeasCheck) localStorage.setItem('tickler_ideas_last_checked', ideasCheck);
    if (!storedLibraryCheck) localStorage.setItem('tickler_library_last_checked', libraryCheck);
    
    // Background checker function
    const checkForNewItems = async () => {
      try {
        // CRITICAL: Always read fresh timestamps from localStorage, not closure variables
        // This ensures we use the updated timestamp after user clicks the tab
        const currentIdeasCheck = localStorage.getItem('tickler_ideas_last_checked') || new Date().toISOString();
        const currentLibraryCheck = localStorage.getItem('tickler_library_last_checked') || new Date().toISOString();
        
        const response = await fetch(
          `${API_BASE_URL}/api/new-items-check/${user.id}?ideas_last_check=${encodeURIComponent(currentIdeasCheck)}&library_last_check=${encodeURIComponent(currentLibraryCheck)}`,
          {
            credentials: 'include' // Use session cookies for auth
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHasNewIdeas(data.has_new_ideas);
          setHasNewLibrary(data.has_new_library);
        }
      } catch (error) {
        console.error('Error checking for new items:', error);
      }
    };
    
    // Start checking after 30 seconds, then every 60 seconds
    const initialTimeout = setTimeout(() => {
      checkForNewItems();
      
      // Set up interval for subsequent checks
      const intervalId = setInterval(checkForNewItems, 60000); // 60 seconds
      
      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }, 30000); // 30 seconds initial delay
    
    // Cleanup timeout on unmount
    return () => clearTimeout(initialTimeout);
  }, [user]);
  
  // Modified tab change handler to refresh data and clear blue dot
  const handleTabChange = (newTab) => {
    const now = new Date().toISOString();
    
    // Track tab change for engagement analytics
    trackTabChange(newTab);
    
    if (newTab === 'dashboard' && hasNewIdeas) {
      // Refresh Ideas data
      fetchData();
      // Update timestamp and clear blue dot
      setIdeasLastChecked(now);
      localStorage.setItem('tickler_ideas_last_checked', now);
      setHasNewIdeas(false);
    } else if (newTab === 'research' && hasNewLibrary) {
      // Refresh Library data
      fetchResearchItems();
      // Update timestamp and clear blue dot
      setLibraryLastChecked(now);
      localStorage.setItem('tickler_library_last_checked', now);
      setHasNewLibrary(false);
    } else if (newTab === 'threads' && hasNewPolls) {
      // Clear blue dot for Polls
      localStorage.setItem('tickler_polls_last_checked', now);
      setHasNewPolls(false);
    } else if (newTab === 'watchlists' && hasNewWatchlists) {
      // Clear blue dot for Watchlists
      localStorage.setItem('tickler_watchlists_last_checked', now);
      setHasNewWatchlists(false);
    } else if (newTab === 'files' && hasNewFiles) {
      // Clear blue dot for Files
      localStorage.setItem('tickler_files_last_checked', now);
      setHasNewFiles(false);
    }
    
    // Set the active tab
    setActiveTab(newTab);
  };



  // Custom Notification Component (matches bookmarklet design)
  const NotificationBox = () => {
    if (!notification) return null;
    
    const { message, type, id } = notification;
    
    const typeStyles = {
      success: {
        bg: 'bg-white',
        border: 'border-green-200',
        icon: '✓',
        iconBg: 'bg-green-500',
        iconColor: 'text-white',
        textColor: 'text-slate-700'
      },
      error: {
        bg: 'bg-white',
        border: 'border-red-200',
        icon: '✗',
        iconBg: 'bg-red-500',
        iconColor: 'text-white',
        textColor: 'text-slate-700'
      },
      info: {
        bg: 'bg-white',
        border: 'border-blue-200',
        icon: 'ℹ',
        iconBg: 'bg-blue-500',
        iconColor: 'text-white',
        textColor: 'text-slate-700'
      },
      warning: {
        bg: 'bg-white',
        border: 'border-orange-200',
        icon: '⚠',
        iconBg: 'bg-orange-500',
        iconColor: 'text-white',
        textColor: 'text-slate-700'
      }
    };
    
    const style = typeStyles[type] || typeStyles.info;
    
    return (
      <div 
        key={id}
        className="fixed top-6 right-6 z-[100000] animate-in slide-in-from-top-4 fade-in duration-300"
        style={{ maxWidth: '420px', width: '90%' }}
      >
        <div className={`${style.bg} ${style.border} border-2 rounded-xl shadow-lg p-4 flex items-start gap-3`}>
          <div className={`${style.iconBg} ${style.iconColor} w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0`}>
            {style.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`${style.textColor} text-sm font-medium leading-relaxed whitespace-pre-wrap`}>
              {message}
            </p>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // If still checking authentication, show loading
  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0f1419]' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading Tickler...</p>
        </div>
      </div>
    );
  }

  // BETA ACCESS CHECK: If user is logged in but not approved, show waitlist page
  if (user && user.is_beta_approved === false) {
    return (
      <WaitlistPage 
        user={user} 
        isDarkMode={isDarkMode} 
        onLogout={logout}
      />
    );
  }

  // AUTH HANDLED BY AuthContext and ROUTING - No need for inline auth screen

  // Helper function to render a single idea card - moved to component level for reuse in workspace
  function renderIdeaCard(bookmark, index, isGrouped, groupBaseId, isFirstInGroup, childColor = false, disableRollup = false, stackCount = null) {
    console.log('[renderIdeaCard] Called for:', bookmark.id?.substring(0, 15), 'isIdeasList:', bookmark.is_ticker_list || bookmark.title === "Ideas List", 'stackCount:', stackCount);
    const daysOld = calculateDaysOld(bookmark.created_at);
    // Use display_age_date for coloring if available, otherwise fallback to created_at
    const ageCategory = getBookmarkAgeCategory(bookmark);
    const contentBasedId = `content-${bookmark.content?.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '') || 'empty'}`;
    // Only treat as rollup if there are 2+ different tickers in ticker_ideas
    const hasMultipleTickerIdeas = bookmark.ticker_ideas && bookmark.ticker_ideas.length > 1;
    // Treat cards with multiple ticker_ideas (nested ideas) as rollups for expand/collapse
    // BUT disable rollup view for search results (disableRollup = true)
    const isRollup = !disableRollup && (bookmark.is_rollup || hasMultipleTickerIdeas);
    const isExpanded = expandedRollups.has(bookmark.id);
    
    // Use stackCount if provided, otherwise fall back to ticker_ideas length
    const displayStackCount = stackCount || bookmark.ticker_ideas?.length || 1;
    
    const toggleRollup = () => {
      const newExpanded = new Set(expandedRollups);
      if (isExpanded) {
        newExpanded.delete(bookmark.id);
        // Sync toggle state - collapse means back to summary
        setFullPostModeCards(prev => {
          const newToggle = new Set(prev);
          newToggle.delete(bookmark.id);
          return newToggle;
        });
      } else {
        newExpanded.add(bookmark.id);
        // Sync toggle state - expand means full post
        setFullPostModeCards(prev => {
          const newToggle = new Set(prev);
          newToggle.add(bookmark.id);
          return newToggle;
        });
      }
      setExpandedRollups(newExpanded);
    };
    let ageBadgeColor = 'bg-gray-100 text-gray-800';
    let statusBarColor = 'bg-zinc-600'; // Default status bar
    
    // STATUS BAR = AGE (green/orange/red based on freshness) - replaces full border
    if (ageCategory === 'New') {
      ageBadgeColor = 'bg-emerald-400/10 text-emerald-400 ring-1 ring-inset ring-emerald-400/20';
      statusBarColor = 'bg-emerald-500';
    } else if (ageCategory === 'Recent') {
      ageBadgeColor = 'bg-amber-400/10 text-amber-400 ring-1 ring-inset ring-amber-400/20';
      statusBarColor = 'bg-amber-500';
    } else if (ageCategory === 'Older') {
      ageBadgeColor = 'bg-rose-400/10 text-rose-400 ring-1 ring-inset ring-rose-400/20';
      statusBarColor = 'bg-rose-500';
    }
    
    const isQuotedTweet = false; // DISABLED: bookmark.source === 'x_bookmark_quoted' - keeping logic for future use
    // Only apply colored backgrounds if childColor is explicitly set
    const isThreadChild = childColor === 'green';
    const isMultiIdeaChild = childColor === 'yellow';
    const isPurpleChild = childColor === 'purple';
    
    // BACKGROUND = Only apply colored background if childColor is set
    // Border color is ALWAYS based on age above
    let bgColor = isDarkMode ? 'bg-[#1a2235]' : 'bg-white';
    if (isPurpleChild || isQuotedTweet) bgColor = isDarkMode ? 'bg-purple-900/40' : 'bg-purple-50';
    else if (isThreadChild) bgColor = isDarkMode ? 'bg-green-900/40' : 'bg-green-50';
    else if (isMultiIdeaChild) bgColor = isDarkMode ? 'bg-yellow-900/40' : 'bg-yellow-50';
    
    // Check if this is an Ideas List card
    const isIdeasListCard = bookmark.is_ticker_list || bookmark.title === "Ideas List";
    const ideasListTickerCount = isIdeasListCard ? (bookmark.all_tickers || bookmark.tickers || []).length : 0;
    // Ideas List can be expanded via either expandedRollups OR expandedCardGroups
    const isIdeasListExpanded = isIdeasListCard && (expandedRollups.has(bookmark.id) || expandedCardGroups.has(bookmark.id));
    const labelTextColor = isDarkMode ? 'text-blue-300' : 'text-blue-700';
    
    // Source-based border color for expanded/quoted states
    const isXSourceCard = bookmark.source?.startsWith('x_') || bookmark.platform === 'x';
    const isSubstackSourceCard = bookmark.source?.includes('substack') || bookmark.platform === 'substack';
    const isRedditSourceCard = bookmark.source?.includes('reddit') || bookmark.platform === 'reddit';
    const isInvestorLetterCard = bookmark.source?.includes('investor_letter') || bookmark.classification === 'investor_letter';
    
    const sourceBorderColor = isXSourceCard ? 'border-black' 
      : isSubstackSourceCard ? 'border-orange-500' 
      : isRedditSourceCard ? 'border-orange-600' 
      : isInvestorLetterCard ? 'border-green-500' 
      : 'border-purple-400';
    
    // For Ideas List expanded, we DON'T do an early return
    // Instead, we let the normal card render and wrap it with the source-branded bar + ticker rows below
    // This is handled after the normal card rendering
    
    return (
      <>
      {/* Wrapper - adds source-branded bar when quoted tweet (NOT for Ideas List - that's handled by parent) */}
      <div className={`${isQuotedTweet ? `ml-8 pl-4 border-l-4 ${sourceBorderColor}` : ''}`}>
        <div className="relative group">
        {/* Modern Card with Status Bar */}
        <Card 
          key={`bookmark-${bookmark.id || bookmark._id || bookmark.created_at}`} 
          className={`relative overflow-hidden ${bgColor} border border-white/5 backdrop-blur-sm hover:border-white/10 hover:shadow-card-hover hover:-translate-y-[1px] transition-all duration-200 cursor-pointer`}
          onClick={(e) => {
            // Don't expand if clicking on buttons, links, or interactive elements
            if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[role="button"]')) {
              return;
            }
            
            // Cross-panel linking - notify other panels about the selection
            if (showWorkspace) {
              const primaryTicker = bookmark.primary_ticker || bookmark.tickers?.[0];
              console.log('[Cross-Link Click] Idea clicked, ticker:', primaryTicker, 'showWorkspace:', showWorkspace);
              if (primaryTicker) {
                setWorkspaceSelectedItem({
                  ticker: primaryTicker,
                  bookmarkId: bookmark.id,
                  tickers: bookmark.tickers || [],
                  source: 'ideas',
                  timestamp: Date.now()  // Ensure React detects this as a new value
                });
              }
            }
            
            // For Ideas List cards, expand with all tickers
            if (isIdeasListCard) {
              const allTickers = [
                ...(bookmark.tickers || []),
                ...(bookmark.all_tickers || []),
                ...((bookmark.categorized_tickers || []).flatMap(cat => cat.tickers || []))
              ];
              console.log('[IDEAS LIST CLICK] Card clicked, id:', bookmark.id, 'allTickers:', allTickers.length, allTickers.slice(0, 5));
              toggleCardGroup(bookmark.id, allTickers);
            } else if (isGrouped && groupBaseId) {
              // For grouped cards (multi-idea), expand the entire group
              const groupIdeas = bookmarks.filter(b => {
                const bBaseId = b.id?.includes('_') ? b.id.substring(0, b.id.lastIndexOf('_')) : b.id;
                return bBaseId === groupBaseId || b.id === groupBaseId;
              });
              const allTickers = groupIdeas.flatMap(idea => idea.tickers || []);
              toggleCardGroup(groupBaseId, allTickers);
            } else if (isRollup) {
              // For rollup cards (multi-idea posts), toggle to show/hide nested cards
              toggleRollup();
            } else {
              const primaryTicker = bookmark.primary_ticker || bookmark.tickers?.[0];
              handleExpandIdeaCard(bookmark.id, primaryTicker, bookmark);
            }
          }}
        >
          {/* Age Status Bar - Left Edge Indicator */}
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
          <CardContent className="p-3 sm:p-4 pl-4 sm:pl-5">
            {/* DELETED TWEET - Show deletion message instead of content */}
            {bookmark.is_deleted && (bookmark.source?.startsWith('x_') || bookmark.source === 'x_post') ? (
              <div className={`flex flex-col items-center justify-center py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <svg className="w-12 h-12 mb-3 opacity-50" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <p className="font-medium mb-1">This post has been deleted from X</p>
                <p className="text-sm opacity-75">The original author removed this content</p>
                {/* Delete button for removed content */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    showNotification('Remove this deleted idea from your dashboard?', 'confirm', 0, {
                      onConfirm: async () => {
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${bookmark.id}`, { method: 'DELETE' });
                          if (response.ok) {
                            setBookmarks(bookmarks.filter(b => b.id !== bookmark.id));
                            showNotification('Removed deleted idea', 'success');
                          }
                        } catch (error) {
                          showNotification('Error removing idea', 'error');
                        }
                      }
                    });
                  }}
                  className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
                >
                  Remove from Dashboard
                </button>
              </div>
            ) : (
            <>
            {/* Quoted Tweet Arrow Indicator */}
            {isQuotedTweet && (
              <div className={`flex items-center gap-2 mb-3 font-semibold text-sm ${isXSourceCard ? 'text-gray-700' : (isDarkMode ? 'text-purple-300' : 'text-purple-700')}`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <span>Quoted Tweet</span>
              </div>
            )}
          {/* Header with Ticker and Age - Mobile Responsive */}
          <div className={`flex items-start justify-between ${isRollup || isIdeasListCard ? 'mb-4' : ''}`}>
            <div className="flex-1 min-w-0">
              <div className={`flex items-center gap-3 ${isRollup || isIdeasListCard ? 'mb-2' : ''}`}>
                
                {/* Ticker Display - Enhanced for Rollup Cards */}
                {isRollup ? (
                  <div className={`flex-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {/* Profile Image + Author at TOP of card */}
                    <div className="flex items-center gap-2 mb-2">
                      {bookmark.twitterMetadata?.profileImageUrl ? (
                        <img 
                          src={bookmark.twitterMetadata.profileImageUrl}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : bookmark.profile_image ? (
                        <img 
                          src={bookmark.profile_image}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : null}
                      {(bookmark.twitterMetadata?.username || bookmark.from_name || bookmark.tweet_author) && (
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          @{(bookmark.twitterMetadata?.username || bookmark.from_name || bookmark.tweet_author || '').replace('@', '')}
                        </span>
                      )}
                      {/* X Logo next to author */}
                      {isXSourceCard && (
                        <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      )}
                    </div>
                    
                    <div className="font-bold text-lg sm:text-xl mb-1">
                      {(() => {
                        const ticker = bookmark.tickers?.[0];
                        const tdMetrics = ticker ? tickerMetricsCache[ticker.toUpperCase()] : null;
                        // Prefer Twelve Data company name, then company_intelligence, then company_name
                        let companyName = tdMetrics?.company_name || bookmark.company_intelligence?.company_name || bookmark.company_name;
                        
                        // Filter out AI-generated placeholder names
                        const isValidCompanyName = companyName && 
                          !companyName.startsWith('Company with ticker') &&
                          !companyName.includes('(assuming') &&
                          !companyName.includes('exact name may vary');
                        
                        const displayName = isValidCompanyName ? companyName : bookmark.title;
                        
                        return (
                          <>
                            {displayName}
                            {ticker && (
                              <span className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                {' '}({ticker})
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {(() => {
                      const oneLinerThesis = bookmark.one_liner_thesis;
                      const signal = signalCache[bookmark.id] || bookmark.structured_signal;
                      
                      return (
                        <>
                          {/* One-liner thesis row */}
                          {oneLinerThesis && (
                            <div className="mb-2">
                              <span className={`text-sm italic ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                {oneLinerThesis}
                              </span>
                            </div>
                          )}
                          
                          {/* Signal Tags */}
                          {signal && (signal.position_type || signal.thesis_type || signal.time_horizon || signal.geographic_focus || (signal.tags && signal.tags.length > 0)) && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {signal.position_type && (
                                <span className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                  {signal.position_type}
                                </span>
                              )}
                              {signal.thesis_type && (
                                <span className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                  {signal.thesis_type.replace(/_/g, ' ')}
                                </span>
                              )}
                              {signal.time_horizon && (
                                <span className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                  {signal.time_horizon.replace(/_/g, ' ')}
                                </span>
                              )}
                              {signal.geographic_focus && (
                                <span className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                  {signal.geographic_focus}
                                </span>
                              )}
                              {signal.tags && signal.tags.slice(0, 3).map((tag, i) => (
                                <span key={i} className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <span className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Source: {bookmark.from_name || 'Email'} | {formatDate(bookmark.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (bookmark.is_ticker_list || bookmark.title === "Ideas List") ? (
                  /* CURATION CARD - Just title + summary, expansion happens outside */
                  <div className={`${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    <div className={`font-bold text-lg sm:text-xl leading-tight mb-1`}>
                      Curation
                      <span className={`ml-2 text-sm font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        ({(bookmark.all_tickers || bookmark.tickers || []).length} ideas)
                      </span>
                    </div>
                    
                    {/* Summary - what type of ideas are in the list */}
                    {(bookmark.one_liner_thesis || bookmark.summary) && (
                      <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {bookmark.one_liner_thesis || bookmark.summary}
                      </div>
                    )}
                  </div>
                ) : (
                  /* REGULAR IDEA CARD - Matching Tickers/Curations page layout */
                  <div className={`flex-1 min-w-0 ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {(() => {
                      const ticker = bookmark.tickers?.[0];
                      const tdMetrics = ticker ? tickerMetricsCache[ticker.toUpperCase()] : null;
                      let companyName = decodeHtmlEntities(bookmark.company_intelligence?.company_name || bookmark.company_name || tdMetrics?.company_name);
                      const oneLinerThesis = decodeHtmlEntities(bookmark.one_liner_thesis);
                      
                      const isValidCompanyName = companyName && 
                        !companyName.startsWith('Company with ticker') &&
                        !companyName.includes('(assuming') &&
                        !companyName.includes('exact name may vary');
                      
                      const displayName = isValidCompanyName
                        ? companyName 
                        : (ticker || 'Unknown Company');
                      
                      const oneLiner = ''; // Deprecated - using preview instead
                      // Use full summary only for expanded view, not card display
                      const fullSummary = decodeHtmlEntities(bookmark.summary || '');
                      
                      // WWNBT thesis - only for Grok button click, not automatic display
                      const wwnbtThesis = wwnbtCache[bookmark.id]?.thesis_summary || bookmark.wwnbt_analysis?.thesis_summary;
                      
                      // Show Grok toggle only when we have a preview
                      const isGrokSummary = !!bookmark.preview;
                      
                      // Check if this card is in "Full post" mode
                      const isFullPostMode = fullPostModeCards.has(bookmark.id);
                      
                      // Get original tweet content for "Full post" mode
                      const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || null;
                      const originalTweetText = twitterData?.text || twitterData?.full_text || bookmark.content || bookmark.summary || '';
                      
                      // Display text: ONLY use preview (8-14 word hook) - no fallbacks to long summaries
                      const previewHook = decodeHtmlEntities(bookmark.preview || '');
                      const grokSummaryText = previewHook || '';  // Just preview, no fallback chain
                      const displayText = isFullPostMode ? decodeHtmlEntities(originalTweetText) : grokSummaryText;
                      
                      const authorUsername = bookmark.twitterMetadata?.username || bookmark.from_name || bookmark.tweet_author || '';
                      
                      // Toggle function for Grok summary / Full post
                      const toggleFullPostMode = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Toggle full post mode AND expand/collapse the card
                        const willBeFullPost = !fullPostModeCards.has(bookmark.id);
                        setFullPostModeCards(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(bookmark.id)) {
                            newSet.delete(bookmark.id);
                          } else {
                            newSet.add(bookmark.id);
                          }
                          return newSet;
                        });
                        // Expand card when switching to full post, collapse when switching back
                        if (willBeFullPost) {
                          // Expand the card
                          if (isGrouped && groupBaseId) {
                            if (!expandedCardGroups.has(groupBaseId)) {
                              toggleCardGroup(groupBaseId);
                            }
                          } else if (isRollup) {
                            if (!expandedRollups.has(bookmark.id)) {
                              setExpandedRollups(prev => new Set([...prev, bookmark.id]));
                            }
                          } else {
                            if (!expandedIdeaCards.has(bookmark.id)) {
                              handleExpandIdeaCard(bookmark.id, bookmark.primary_ticker, bookmark);
                            }
                          }
                        } else {
                          // Collapse the card
                          if (isGrouped && groupBaseId) {
                            if (expandedCardGroups.has(groupBaseId)) {
                              toggleCardGroup(groupBaseId);
                            }
                          } else if (isRollup) {
                            if (expandedRollups.has(bookmark.id)) {
                              setExpandedRollups(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(bookmark.id);
                                return newSet;
                              });
                            }
                          } else {
                            if (expandedIdeaCards.has(bookmark.id)) {
                              setExpandedIdeaCards(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(bookmark.id);
                                return newSet;
                              });
                            }
                          }
                        }
                      };
                      
                      return (
                        <div className="space-y-0.5">
                          {/* Row 1: X Logo + Profile pic + @author | Toggle pill | Ideas count */}
                          <div className="flex items-center gap-2">
                            {/* Left: Author info - takes available space */}
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              {isXSourceCard && (
                                <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                              )}
                              {(bookmark.twitterMetadata?.profileImageUrl || bookmark.profile_image) && (
                                <img 
                                  src={bookmark.twitterMetadata?.profileImageUrl || bookmark.profile_image}
                                  alt=""
                                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              {authorUsername && (
                                <span className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  @{authorUsername.replace('@', '')}
                                </span>
                              )}
                            </div>
                            {/* Center: Toggle switch - fixed width */}
                            <div className="flex-shrink-0" style={{ width: '85px' }}>
                              {isGrokSummary && (
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={toggleFullPostMode}
                                    className={`relative w-8 h-4 rounded-full cursor-pointer transition-colors duration-200 bg-black`}
                                    data-testid="grok-toggle-pill"
                                  >
                                    {/* Sliding Grok logo - always black/white per official Grok branding */}
                                    <div 
                                      className={`absolute top-0.5 w-3 h-3 transition-all duration-200 ease-in-out ${
                                        isFullPostMode ? 'left-[18px]' : 'left-0.5'
                                      }`}
                                    >
                                      <svg className="w-3 h-3" viewBox="0 0 512 509.641" fill="#000000">
                                        <path d="M115.612 0h280.776C459.975 0 512 52.026 512 115.612v278.416c0 63.587-52.025 115.613-115.612 115.613H115.612C52.026 509.641 0 457.615 0 394.028V115.612C0 52.026 52.026 0 115.612 0z"/>
                                        <path fill="#ffffff" d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
                                      </svg>
                                    </div>
                                  </button>
                                  {/* Text label */}
                                  <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                    {isFullPostMode ? 'Original' : 'Preview'}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Right: Ideas count - fixed width */}
                            <span className={`text-xs flex-shrink-0 text-right ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} style={{ width: '50px' }}>
                              {displayStackCount > 1 
                                ? `${displayStackCount} ideas`
                                : '1 idea'
                              }
                            </span>
                          </div>
                          
                          {/* Row 2: Company Name */}
                          <div className={`flex items-center gap-2 font-medium min-w-0 ${isXSourceCard ? 'ml-6' : ''}`}>
                            <span className="truncate">
                              {displayName}
                              {ticker && (
                                <span className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                  {' '}({ticker})
                                </span>
                              )}
                            </span>
                          </div>
                          
                          {/* Row 3: Content - show Grok summary only when collapsed, hide when full post is expanded */}
                          {!isFullPostMode && (
                            <div className={`text-sm ${isXSourceCard ? 'ml-6' : ''} ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                              <span>{grokSummaryText}</span>
                              
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Expand/collapse - let the toggle functions handle fullPostModeCards sync
                                  if (isGrouped && groupBaseId) {
                                  toggleCardGroup(groupBaseId);
                                } else if (isRollup) {
                                  toggleRollup();
                                } else {
                                  handleExpandIdeaCard(bookmark.id, bookmark.primary_ticker, bookmark);
                                }
                              }}
                              className={`inline-flex items-center ml-1 p-0.5 rounded-full transition-colors align-middle float-right ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                              data-testid="card-expand-btn"
                            >
                              <svg 
                                className={`w-5 h-5 transition-transform ${(isGrouped && groupBaseId) ? (expandedCardGroups.has(groupBaseId) ? 'rotate-180' : '') : (isRollup ? (isExpanded ? 'rotate-180' : '') : (expandedIdeaCards.has(bookmark.id) ? 'rotate-180' : ''))} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                            </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Ticker Cards - Compact Collapsed View with Individual Expand */}
          {isRollup && isExpanded && bookmark.ticker_ideas && (
            <div className={`mt-6 space-y-2`} onClick={(e) => e.stopPropagation()}>
              {bookmark.ticker_ideas.map((tickerIdea, tickerIndex) => {
                // Create nested bookmark with market cap/EV from parent
                const nestedId = `${bookmark.id}_nested_${tickerIndex}`;
                const nestedBookmark = {
                  id: nestedId,
                  tickers: [tickerIdea.ticker],
                  content: tickerIdea.context || tickerIdea.summary,
                  author: bookmark.author,
                  created_at: bookmark.created_at,
                  url: bookmark.url,
                  source: bookmark.source,
                  researched_tickers: [tickerIdea.ticker],
                  primary_market_cap: bookmark.ticker_metrics?.[tickerIdea.ticker]?.market_cap || bookmark.primary_market_cap,
                  primary_enterprise_value: bookmark.ticker_metrics?.[tickerIdea.ticker]?.enterprise_value || bookmark.primary_enterprise_value
                };
                
                // Check if this nested card is expanded
                const isNestedExpanded = expandedNestedIdeas.has(nestedId);
                
                // Toggle function for individual nested cards
                const toggleNestedExpand = (e) => {
                  e.stopPropagation();
                  setExpandedNestedIdeas(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(nestedId)) {
                      newSet.delete(nestedId);
                    } else {
                      newSet.add(nestedId);
                    }
                    return newSet;
                  });
                };
                
                // Display text for collapsed view - use preview (8-14 word hook)
                const displayOneLiner = tickerIdea.preview || 
                  (nestedBookmark.content?.length > 100 
                    ? nestedBookmark.content.substring(0, 100) + '...' 
                    : nestedBookmark.content) || 
                  `Investment idea for $${tickerIdea.ticker}`;

                return (
                  <div key={tickerIndex} className={`ml-4 pl-3 border-l-2 ${isDarkMode ? 'border-slate-600' : 'border-slate-300'}`} data-nested-ticker={tickerIdea.ticker} onClick={(e) => e.stopPropagation()}>
                    {/* Compact Collapsed Card */}
                    <div 
                      className={`w-full ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#1e2940]' : 'bg-white hover:bg-slate-50'} rounded-lg border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'} shadow-sm transition-all duration-200 cursor-pointer`}
                      onClick={toggleNestedExpand}
                      data-testid={`nested-card-${tickerIndex}`}
                    >
                      {/* Compact Header Row */}
                      <div className="p-3 flex items-center gap-3">
                        {/* Number Badge */}
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-[10px] flex items-center justify-center flex-shrink-0">
                          {tickerIndex + 1}
                        </div>
                        
                        {/* Ticker */}
                        <span className={`font-mono font-bold text-base ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} flex-shrink-0`}>
                          ${tickerIdea.ticker}
                        </span>
                        
                        {/* One-liner thesis (truncated) */}
                        <span className={`text-sm flex-1 truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          {displayOneLiner}
                        </span>
                        
                        {/* Expand/Collapse Arrow */}
                        <button
                          onClick={toggleNestedExpand}
                          className={`p-1 rounded-full transition-colors flex-shrink-0 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                          data-testid={`nested-expand-btn-${tickerIndex}`}
                        >
                          <svg 
                            className={`w-4 h-4 transition-transform ${isNestedExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                          </svg>
                        </button>
                      </div>
                      
                      {/* Expanded Content */}
                      {isNestedExpanded && (
                        <div className={`px-3 pb-3 pt-0 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          {/* Author & Date Row */}
                          <div className="flex items-center justify-between mt-3 mb-2">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const isXBookmark = nestedBookmark.source?.startsWith('x_bookmark') || nestedBookmark.source === 'x_post';
                                if (isXBookmark) {
                                  return (
                                    <svg className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  );
                                }
                                return null;
                              })()}
                              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                {nestedBookmark.source === 'email' ? 
                                  nestedBookmark.author : 
                                  `@${(nestedBookmark.author || '').replace(/^@/, '')}`
                                }
                              </span>
                            </div>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {formatDate(nestedBookmark.created_at)}
                            </span>
                          </div>
                          
                          {/* Financial Metrics */}
                          {(nestedBookmark.primary_market_cap || nestedBookmark.primary_enterprise_value) && (
                            <div className={`flex items-center gap-2 text-xs mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {nestedBookmark.primary_market_cap && (
                                <span>MCap: <span className="font-mono">{formatFinancialValue(nestedBookmark.primary_market_cap)}</span></span>
                              )}
                              {nestedBookmark.primary_enterprise_value && (
                                <>
                                  {nestedBookmark.primary_market_cap && <span>|</span>}
                                  <span>EV: <span className="font-mono">{formatFinancialValue(nestedBookmark.primary_enterprise_value)}</span></span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* Full Content */}
                          <div className={`text-sm leading-relaxed mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'} whitespace-pre-wrap break-words`}>
                            {nestedBookmark.content}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {/* File Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTickerForScheduling({
                                  bookmarkId: nestedBookmark.id,
                                  ticker: tickerIdea.ticker,
                                  content: nestedBookmark.content
                                });
                                setShowDocumentModal(true);
                                checkSECAvailability(tickerIdea.ticker);
                              }}
                              className={`flex-1 px-1 py-1.5 ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-800'} text-xs rounded-md font-medium transition-colors flex items-center justify-center gap-1`}
                              title={`Generate Research for ${tickerIdea.ticker}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                              </svg>
                              <span className="text-[10px] sm:text-xs">File</span>
                            </button>
                            
                            {/* Folder Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openCalendarModal({
                                  bookmarkId: nestedBookmark.id,
                                  ticker: tickerIdea.ticker,
                                  content: nestedBookmark.content
                                });
                              }}
                              className={`flex-1 px-1 py-1.5 ${isDarkMode ? 'bg-purple-900/50 hover:bg-purple-800/50 text-purple-300' : 'bg-purple-100 hover:bg-purple-200 text-purple-800'} text-xs rounded-md font-medium transition-colors flex items-center justify-center gap-1`}
                              title={`Add to folder for ${tickerIdea.ticker}`}
                            >
                              <FolderIcon className="w-5 h-5" />
                              <span className="text-[10px] sm:text-xs">Folder</span>
                            </button>
                            
                            {/* Share Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTickerForScheduling({
                                  bookmarkId: nestedBookmark.id,
                                  ticker: tickerIdea.ticker,
                                  content: nestedBookmark.content,
                                  tweetId: nestedBookmark.twitterMetadata?.tweet_id || nestedBookmark.global_tweet_id,
                                  shareType: 'idea'
                                });
                                setShowShareModal(true);
                              }}
                              className={`flex-1 px-1 py-1.5 ${isDarkMode ? 'bg-yellow-900/50 hover:bg-yellow-800/50 text-yellow-300' : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800'} text-xs rounded-md font-medium transition-colors flex items-center justify-center gap-1`}
                              title={`Share ${tickerIdea.ticker} investment idea`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                              </svg>
                              <span className="text-[10px] sm:text-xs">Share</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Action Buttons - Only show when card is expanded */}
          {expandedIdeaCards.has(bookmark.id) && (
          <div data-expanded-content onClick={(e) => e.stopPropagation()} className={`mt-3 px-2 py-2 border-t ${isDarkMode ? 'border-slate-700 bg-[#1a2235]/50' : 'border-slate-200 bg-white'}`}>
            {/* Container query wrapper - switches between compact and wide layouts */}
            <div className="idea-buttons-container">
              <div className="idea-buttons-row">
              {/* Folder Button */}
              {!isIdeasListCard && (
              <button
                onClick={() => {
                  trackEngagement('modal_click', 'modal', { button_name: 'schedule', ticker: bookmark.tickers?.[0] || null, source: bookmark.source || 'unknown' });
                  openCalendarModal({ bookmarkId: bookmark.id, ticker: bookmark.tickers?.[0], content: bookmark.content });
                }}
                className={`idea-action-btn ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
              >
                <FolderIcon className="idea-btn-icon" />
                <span className="idea-btn-text">Folder</span>
              </button>
              )}

              {/* Files Button */}
              {!isIdeasListCard && (
              <button
                onClick={() => {
                  trackEngagement('modal_click', 'modal', { button_name: 'brief', ticker: bookmark.tickers?.[0] || null, source: bookmark.source || 'unknown' });
                  setSelectedTickerForScheduling({ bookmarkId: bookmark.id, ticker: bookmark.tickers?.[0], content: bookmark.content });
                  setShowDocumentModal(true);
                  checkSECAvailability(bookmark.tickers?.[0]);
                }}
                className={`idea-action-btn ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
              >
                <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span className="idea-btn-text">File</span>
              </button>
              )}

              {/* Tickers Button */}
              <button
                onClick={async () => {
                  trackEngagement('modal_click', 'modal', { button_name: 'watchlist', ticker: bookmark.tickers?.[0] || null, source: bookmark.source || 'unknown', is_ideas_list: bookmark.is_ticker_list || bookmark.title === "Ideas List" });
                  if (bookmark.is_ticker_list || bookmark.title === "Ideas List") {
                    const tickersList = bookmark.all_tickers || bookmark.tickers || [];
                    if (tickersList.length > 0) {
                      const summaryText = bookmark.one_liner_thesis || bookmark.summary || "Curation";
                      let watchlistName = summaryText.split(/[.!?]/)[0].trim();
                      if (watchlistName.length > 50) watchlistName = watchlistName.substring(0, 50) + "...";
                      try {
                        const tickerNames = tickersList.map(t => typeof t === 'string' ? t : t.ticker);
                        const sourceInfo = { author: bookmark.twitter_metadata?.username || bookmark.tweet_author?.replace('@', '') || null, source_url: bookmark.url || null, source_type: bookmark.source || 'x_bookmark', original_summary: summaryText, bookmark_id: bookmark.id, ticker_count: tickerNames.length };
                        const response = await fetch(`${API_BASE_URL}/api/watchlists`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: watchlistName, tickers: tickerNames, source_info: sourceInfo, list_type: 'custom_list' }) });
                        if (response.ok) showNotification(`Created list "${watchlistName}" with ${tickerNames.length} tickers`, 'success');
                        else showNotification('Failed to create list', 'error');
                      } catch (error) { showNotification('Error creating list', 'error'); }
                    }
                  } else {
                    const ticker = bookmark.tickers?.[0];
                    if (ticker) {
                      const tdMetrics = ticker ? tickerMetricsCache[ticker.toUpperCase()] : null;
                      const sectorToUse = tdMetrics?.sector || (bookmark.primary_sector !== 'X/Twitter' ? bookmark.primary_sector : null);
                      addToWatchlist(ticker, sectorToUse ? { sector: sectorToUse } : null);
                    }
                  }
                }}
                disabled={!(bookmark.is_ticker_list || bookmark.title === "Ideas List") && !bookmark.tickers?.[0]}
                className={`idea-action-btn ${(bookmark.is_ticker_list || bookmark.title === "Ideas List" || bookmark.tickers?.[0]) ? (isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50') : 'border-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="idea-btn-text">{(bookmark.is_ticker_list || bookmark.title === "Ideas List") ? 'Save' : 'Ticker'}</span>
              </button>

              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const tickerName = bookmark.tickers?.[0] || bookmark.primary_ticker || 'this idea';
                  showNotification(`Are you sure you want to delete the idea for $${tickerName}?`, 'confirm', 0, {
                    onConfirm: async () => {
                      try {
                        const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${bookmark.id}`, {
                          method: 'DELETE'
                        });
                        const responseData = await response.json();
                        if (response.ok && responseData.success !== false) {
                          setBookmarks(bookmarks.filter(b => b.id !== bookmark.id));
                          showNotification(`Deleted idea for $${tickerName}`, 'success');
                        } else {
                          showNotification('Delete failed: ' + (responseData.error || 'Unknown error'), 'error');
                        }
                      } catch (error) {
                        showNotification('Error: ' + error.message, 'error');
                      }
                    }
                  });
                }}
                className={`idea-action-btn ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                title="Delete this investment idea"
              >
                <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                <span className="idea-btn-text">Delete</span>
              </button>

              {/* Share Button */}
              <button
                onClick={() => {
                  trackEngagement('modal_click', 'modal', { button_name: 'share', ticker: bookmark.tickers?.[0] || null, source: bookmark.source || 'unknown' });
                  setSelectedTickerForScheduling({
                    bookmarkId: bookmark.id,
                    ticker: bookmark.tickers?.[0],
                    content: bookmark.content || bookmark.one_liner_thesis || bookmark.summary || '',
                    tweetId: bookmark.twitterMetadata?.tweet_id || bookmark.global_tweet_id,
                    shareType: 'idea'
                  });
                  setShowShareModal(true);
                }}
                className={`idea-action-btn ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
              >
                <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                </svg>
                <span className="idea-btn-text">Share</span>
              </button>

              {/* Open Button */}
              {(bookmark.content || bookmark.tweet_content || bookmark.original_content) ? (
                <button
                  onClick={async () => {
                    trackEngagement('original_view', 'modal', { source: bookmark.source || 'unknown', ticker: bookmark.tickers?.[0] || null });
                    // For threads, prioritize the lead tweet (first in full_thread) over bookmark.content
                    const twitterMeta = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || bookmark.metadata?.twitter_data?.[0] || null;
                    const hasThread = twitterMeta?.is_thread && twitterMeta?.full_thread?.length > 0;
                    const leadTweetText = hasThread ? twitterMeta.full_thread[0]?.text : null;
                    const fullTweetContent = leadTweetText || bookmark.original_content || bookmark.twitterMetadata?.text || bookmark.metadata?.twitter_content?.tweets?.[0]?.text || bookmark.metadata?.original_email_content || bookmark.metadata?.twitter_data?.[0]?.text || bookmark.tweet_content || '';
                    const shouldUseTweet = (bookmark.source === 'bookmark' || bookmark.source?.startsWith('x_bookmark') || bookmark.is_twitter_content) && fullTweetContent && !bookmark.is_substack;
                    setSelectedOriginalContent(null);
                    let fullPdfData = bookmark.metadata?.pdf_data || null;
                    if (bookmark.metadata?.has_pdf_attachments && bookmark.id) {
                      try { const response = await fetch(`${API_BASE_URL}/api/pdf-content/${bookmark.id}`); if (response.ok) { const pdfResponse = await response.json(); fullPdfData = pdfResponse.pdf_data; } } catch (error) {}
                    }
                    setTimeout(() => {
                      const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || bookmark.metadata?.twitter_data?.[0] || null;
                      setSelectedOriginalContent({
                        id: bookmark.id, title: bookmark.title,
                        content: shouldUseTweet ? fullTweetContent : (bookmark.html_content || bookmark.metadata?.email_html_content || bookmark.metadata?.pdf_data?.html_content || bookmark.metadata?.pdf_data?.text_content || bookmark.metadata?.email_content || bookmark.metadata?.original_email_content || bookmark.original_content || bookmark.content),
                        htmlContent: shouldUseTweet ? null : (() => { const emailHtml = bookmark.html_content || bookmark.metadata?.email_html_content || ''; const originalEmailContent = bookmark.original_email_content || bookmark.metadata?.original_email_content || ''; const originalContent = bookmark.original_content || ''; const isHTML = (content) => content && (content.includes('<div') || content.includes('<p>') || content.includes('<span') || content.includes('<html>') || content.includes('<!DOCTYPE') || content.includes('<body') || content.includes('<article') || content.includes('<section') || (content.includes('<') && content.includes('>') && content.length > 100)); if (emailHtml && isHTML(emailHtml)) return emailHtml; if (originalEmailContent && isHTML(originalEmailContent)) return originalEmailContent; if (originalContent && isHTML(originalContent)) return originalContent; return null; })(),
                        subject: bookmark.metadata?.email_subject || bookmark.title || 'Email Content',
                        from: bookmark.source_info?.source_name || bookmark.from_name || bookmark.from_address,
                        isTwitterContent: bookmark.is_twitter_content || bookmark.source?.startsWith('x_bookmark') || false,
                        twitterMetadata: twitterData ? { authorName: twitterData.name || twitterData.author?.name, username: twitterData.username || twitterData.author?.username, profileImageUrl: twitterData.profileImageUrl || twitterData.profile_image_url || twitterData.author?.profile_image_url, tweetUrl: twitterData.url || twitterData.tweet_url || bookmark.metadata?.twitter_content?.tweet_url, createdAt: twitterData.created_at || twitterData.createdAt, metrics: twitterData.metrics, is_thread: twitterData.is_thread || false, thread_length: twitterData.thread_length || 1, full_thread: twitterData.full_thread || null, thread_info: twitterData.thread_info || null, conversation_context: twitterData.conversation_context || null, has_context: twitterData.has_context || false, media: twitterData.media || null, entities: twitterData.entities || null, url_card: twitterData.url_card || null, quoted_tweet: twitterData.quoted_tweet || null } : null,
                        hasPdfAttachments: bookmark.metadata?.has_pdf_attachments || false, pdfData: fullPdfData,
                        platformSource: bookmark.is_twitter_content || shouldUseTweet ? 'Twitter' : bookmark.is_substack ? 'Substack' : bookmark.platform === 'Substack' ? 'Substack' : bookmark.metadata?.platform_source || 'Email'
                      });
                      if (bookmark.is_twitter_content || shouldUseTweet) {
                        const globalTweetId = bookmark.global_tweet_id || bookmark.metadata?.global_tweet_id;
                        const twitterId = bookmark.twitter_id || bookmark.metadata?.twitter_id || twitterData?.id || (twitterData?.url || twitterData?.tweet_url || '')?.match(/status\/(\d+)/)?.[1];
                        if (globalTweetId && twitterId) { setTweetValidationStatus({ validating: true, is_deleted: false, is_protected: false, error: null, last_validated: null }); validateTweetContent(globalTweetId, twitterId); }
                      }
                    }, 10);
                    setShowOriginalContentModal(true);
                  }}
                  className={`idea-action-btn ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                >
                  <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  <span className="idea-btn-text">Open</span>
                </button>
              ) : (
                <button
                  onClick={() => showNotification('No content available', 'info')}
                  className={`idea-action-btn ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                >
                  <svg className="idea-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  <span className="idea-btn-text">Open</span>
                </button>
              )}
            </div>
            </div>
          </div>
          )}
          
          {/* Full Tweet Content + Grok Analysis - Only show when expanded */}
          {expandedIdeaCards.has(bookmark.id) && (
            <div data-expanded-content onClick={(e) => e.stopPropagation()} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              {/* Tweet Content Area */}
              <div className={`px-3 sm:px-4 py-3 ${isDarkMode ? 'bg-[#141c2e]/20' : 'bg-slate-50'}`}>
                {/* Author Profile Header - X Display Requirements compliant */}
                {(() => {
                  const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || bookmark.metadata?.twitter_data?.[0] || null;
                  const authorName = twitterData?.name || twitterData?.author?.name || bookmark.tweet_author_name;
                  const username = twitterData?.username || twitterData?.author?.username || bookmark.tweet_author?.replace('@', '') || bookmark.author;
                  const profileImage = twitterData?.profileImageUrl || twitterData?.profile_image_url || twitterData?.author?.profile_image_url;
                  const tweetId = bookmark.twitterMetadata?.tweet_id || bookmark.metadata?.tweet_id || bookmark.tweet_id;
                  const tweetUrl = bookmark.twitterMetadata?.tweet_url || bookmark.twitterMetadata?.url || bookmark.metadata?.tweet_url || bookmark.source_url;
                  const linkUrl = tweetUrl || (tweetId ? `https://x.com/i/status/${tweetId}` : null);
                  const tweetDate = twitterData?.created_at || twitterData?.createdAt;
                  const metrics = twitterData?.metrics || twitterData?.public_metrics;
                  
                  if (authorName || username) {
                    // Build X profile URL for linking
                    const xProfileUrl = username ? `https://x.com/${username}` : null;
                    
                    return (
                      <div className="mb-4">
                        {/* Entire post area is clickable to permalink per Display Requirements (white space rule) */}
                        <a 
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="block cursor-pointer"
                        >
                          <div className="flex items-start gap-3">
                            {/* Profile Image - MUST link to X profile per Display Requirements */}
                            {profileImage && (
                              <span 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(xProfileUrl, '_blank');
                                }}
                                className="flex-shrink-0"
                              >
                                <img 
                                  src={profileImage}
                                  alt={`${authorName} profile`}
                                  className="w-12 h-12 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </span>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                {/* Author info stacked vertically like X */}
                                <div className="min-w-0">
                                  {/* Author Name - MUST link to X profile per Display Requirements */}
                                  <span 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      window.open(xProfileUrl, '_blank');
                                    }}
                                    className={`font-bold block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                  >
                                    {authorName}
                                  </span>
                                  
                                  {/* @username below name - MUST link to X profile per Display Requirements */}
                                  <span 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      window.open(xProfileUrl, '_blank');
                                    }}
                                    className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                  >
                                    @{username}
                                  </span>
                                </div>
                                
                                {/* X Logo - upper right per X Display Requirements */}
                                <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </a>
                        
                        {/* Timestamp + View on X - MUST link to permalink per Display Requirements */}
                        <div className="flex items-center gap-2 mt-2 ml-15 pl-15" style={{marginLeft: '60px'}}>
                          {tweetDate && (
                            <a
                              href={linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                            >
                              {new Date(tweetDate).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </a>
                          )}
                          {/* "View on X" next to timestamp per Display Requirements */}
                          {linkUrl && (
                            <>
                              <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                              <a
                                href={linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm text-blue-500 hover:underline"
                              >
                                View on X
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Full tweet text - HIDE if we have full_thread with 2+ tweets */}
                {(() => {
                  const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || null;
                  const fullThread = twitterData?.full_thread;
                  const hasFullThread = twitterData?.is_thread && fullThread?.length > 1;
                  
                  // For threads, show the specific tweet related to THIS idea
                  // Not skipping anymore - we always show the idea's specific content
                  
                  return (
                    <>
                <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {(() => {
                    // For thread children, prioritize content (the specific tweet for this idea)
                    // For regular tweets, prioritize twitterMetadata.text
                    const isThreadChild = bookmark.source === 'x_bookmark_thread_child' || bookmark.parent_id;
                    
                    let tweetContent;
                    if (isThreadChild) {
                      // Thread child: use content first (the specific reply tweet for this idea)
                      tweetContent = bookmark.content || 
                                    bookmark.twitterMetadata?.text ||
                                    bookmark.summary || '';
                    } else if (hasFullThread) {
                      // Part of a thread but not a child - show the first tweet (main tweet for this idea)
                      tweetContent = fullThread[0]?.text ||
                                    bookmark.twitterMetadata?.text || 
                                    bookmark.content ||
                                    bookmark.summary || '';
                    } else {
                      // Regular tweet: prioritize original tweet text from metadata
                      tweetContent = bookmark.twitterMetadata?.text || 
                                    bookmark.twitterMetadata?.full_text ||
                                    bookmark.metadata?.twitter_content?.tweet_data?.text ||
                                    bookmark.content ||
                                    bookmark.summary || '';
                    }
                    
                    const isLong = tweetContent.length > 500;
                    const displayContent = sourceExpanded[bookmark.id] ? tweetContent : tweetContent.substring(0, 500);
                    
                    // Process URLs to make them clickable
                    const processedContent = (() => {
                      let text = decodeHtmlEntities(displayContent)
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;');
                      
                      // Get entities for URL expansion
                      const entities = bookmark.twitterMetadata?.entities;
                      const urlMap = {};
                      const mediaUrls = new Set();
                      const quotedTweetUrls = new Set();
                      
                      // If there's a quoted tweet being displayed, we should hide its URL from the text
                      const hasQuotedTweet = bookmark.twitterMetadata?.quoted_tweet;
                      
                      if (entities?.urls) {
                        entities.urls.forEach(urlEntity => {
                          if (urlEntity.url) {
                            // Check if this is a media URL
                            const isMediaUrl = urlEntity.display_url?.startsWith('pic.') || 
                                              urlEntity.expanded_url?.includes('/photo/') ||
                                              urlEntity.expanded_url?.includes('/video/');
                            // Check if this URL points to a Twitter/X status (quoted tweet)
                            const isQuotedTweetUrl = hasQuotedTweet && (
                              urlEntity.expanded_url?.includes('/status/') ||
                              urlEntity.expanded_url?.includes('twitter.com/') ||
                              urlEntity.expanded_url?.includes('x.com/')
                            );
                            
                            if (isMediaUrl) {
                              mediaUrls.add(urlEntity.url);
                            } else if (isQuotedTweetUrl) {
                              quotedTweetUrls.add(urlEntity.url);
                            } else if (urlEntity.expanded_url) {
                              urlMap[urlEntity.url] = {
                                href: urlEntity.expanded_url,
                                display: urlEntity.display_url || urlEntity.expanded_url
                              };
                            }
                          }
                        });
                      }
                      
                      // Replace URLs
                      text = text.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                        let cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
                        const punctuation = url.substring(cleanUrl.length);
                        
                        // Skip media URLs (they're shown as images)
                        if (mediaUrls.has(cleanUrl)) {
                          return punctuation;
                        }
                        
                        // Skip quoted tweet URLs (they're shown as embedded quote)
                        if (quotedTweetUrls.has(cleanUrl)) {
                          return punctuation;
                        }
                        
                        // Also skip any t.co URL if we have a quoted tweet (Twitter doesn't expand these in entities sometimes)
                        if (hasQuotedTweet && cleanUrl.includes('t.co/')) {
                          return punctuation;
                        }
                        
                        // Skip any Twitter/X status URLs - they're usually self-referential or quoted tweet links
                        if (cleanUrl.match(/(?:twitter\.com|x\.com)\/\w+\/status\/\d+/)) {
                          return punctuation;
                        }
                        
                        // Use expanded URL if available
                        if (urlMap[cleanUrl]) {
                          const displayUrl = urlMap[cleanUrl].display.length > 35 
                            ? urlMap[cleanUrl].display.substring(0, 32) + '...' 
                            : urlMap[cleanUrl].display;
                          return `<a href="${urlMap[cleanUrl].href}" target="_blank" rel="noopener noreferrer" class="${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}" onclick="event.stopPropagation()">${displayUrl}</a>${punctuation}`;
                        }
                        
                        // Regular URL
                        const displayUrl = cleanUrl.length > 35 ? cleanUrl.substring(0, 32) + '...' : cleanUrl;
                        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}" onclick="event.stopPropagation()">${displayUrl}</a>${punctuation}`;
                      });
                      
                      return text.replace(/\n/g, '<br>');
                    })();
                    
                    return (
                      <>
                        <div 
                          className="whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedContent) }}
                        />
                        {isLong && !sourceExpanded[bookmark.id] && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSourceExpanded(prev => ({ ...prev, [bookmark.id]: true }));
                            }}
                            className={`mt-2 flex items-center gap-1 text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                          >
                            <span>...</span>
                            <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                              Show more
                            </span>
                          </button>
                        )}
                        {isLong && sourceExpanded[bookmark.id] && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSourceExpanded(prev => ({ ...prev, [bookmark.id]: false }));
                            }}
                            className={`mt-2 text-xs font-medium ${isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-600'}`}
                          >
                            ▲ Show less
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Tweet images if present - also hide if showing full thread */}
                {bookmark.twitterMetadata?.media && bookmark.twitterMetadata.media.length > 0 && (
                  <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: bookmark.twitterMetadata.media.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
                    {bookmark.twitterMetadata.media.map((media, idx) => (
                      <img 
                        key={media.media_key || media.url || `media-${bookmark.id}-${idx}`}
                        src={media.url || media.preview_image_url} 
                        alt="Tweet media"
                        className="rounded-lg w-full object-cover max-h-64"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Main Tweet URL Card / Link Preview - show image with title/domain */}
                {bookmark.twitterMetadata?.url_card?.images?.[0]?.url && (
                  <div className={`mt-3 rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-600' : 'border-slate-300'}`}>
                    <a 
                      href={bookmark.twitterMetadata.url_card.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={bookmark.twitterMetadata.url_card.images[0].url}
                        alt={bookmark.twitterMetadata.url_card.title || ''}
                        className="w-full object-cover max-h-64"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {(bookmark.twitterMetadata.url_card.title || bookmark.twitterMetadata.url_card.display_url) && (
                        <div className={`px-3 py-2 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                          {bookmark.twitterMetadata.url_card.title && (
                            <p className={`text-sm font-medium line-clamp-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {bookmark.twitterMetadata.url_card.title}
                            </p>
                          )}
                          {bookmark.twitterMetadata.url_card.display_url && (
                            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              {bookmark.twitterMetadata.url_card.display_url}
                            </p>
                          )}
                        </div>
                      )}
                    </a>
                  </div>
                )}
                
                {/* Inline Quoted Tweet Display - Expanded View */}
                {bookmark.twitterMetadata?.quoted_tweet && (
                  <div className={`mt-4 rounded-xl border overflow-hidden ${isDarkMode ? 'bg-slate-800/70 border-slate-600' : 'bg-slate-100 border-slate-300'}`}>
                    {/* Author Header - X style */}
                    <div className={`px-3 pt-3 pb-2 flex items-center gap-2`}>
                      {/* Profile pic */}
                      {bookmark.twitterMetadata.quoted_tweet.author_profile_image_url ? (
                        <img 
                          src={bookmark.twitterMetadata.quoted_tweet.author_profile_image_url}
                          alt=""
                          className="w-5 h-5 rounded-full flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className={`w-5 h-5 rounded-full flex-shrink-0 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`} />
                      )}
                      {/* Author info */}
                      <div className="flex items-center gap-1 min-w-0 flex-1 flex-wrap">
                        <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {bookmark.twitterMetadata.quoted_tweet.author_name || 'Author'}
                        </span>
                        {bookmark.twitterMetadata.quoted_tweet.author_verified && (
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                          </svg>
                        )}
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          @{bookmark.twitterMetadata.quoted_tweet.author_username || 'user'}
                        </span>
                        {bookmark.twitterMetadata.quoted_tweet.created_at && (
                          <span className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            · {new Date(bookmark.twitterMetadata.quoted_tweet.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Tweet Text - preserve line breaks, remove t.co links */}
                    <div className={`px-3 pb-3 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      <div className="whitespace-pre-wrap">
                        {bookmark.twitterMetadata.quoted_tweet.text?.replace(/https:\/\/t\.co\/\w+/g, '').trim()}
                      </div>
                    </div>
                    
                    {/* URL Card / Article Preview - show as full image since Twitter returns image as media */}
                    {bookmark.twitterMetadata.quoted_tweet.url_card?.images?.[0]?.url && (
                      <div className="mx-3 mb-3">
                        <img 
                          src={bookmark.twitterMetadata.quoted_tweet.url_card.images[0].url}
                          alt=""
                          className="rounded-xl w-full"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {bookmark.twitterMetadata.quoted_tweet.url_card.display_url && (
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            {bookmark.twitterMetadata.quoted_tweet.url_card.display_url}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Media images - only show if NO url_card images (to avoid duplicates) */}
                    {!bookmark.twitterMetadata.quoted_tweet.url_card?.images?.[0]?.url && bookmark.twitterMetadata.quoted_tweet.media?.length > 0 && (
                      <div className="mx-3 mb-3">
                        {bookmark.twitterMetadata.quoted_tweet.media.map((media, idx) => (
                          <img 
                            key={idx}
                            src={media.url || media.preview_image_url}
                            alt=""
                            className="rounded-xl w-full"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Ticker badges */}
                    {bookmark.twitterMetadata.quoted_tweet.tickers?.length > 0 && (
                      <div className={`px-3 pb-3 flex flex-wrap gap-1.5`}>
                        {bookmark.twitterMetadata.quoted_tweet.tickers.map((ticker, i) => (
                          <span key={i} className={`px-2 py-0.5 text-xs font-medium rounded ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            ${ticker}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                  </> 
                  );
                })()}
                
                {/* Engagement Metrics - AFTER content per X display guidelines */}
                {(() => {
                  const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || null;
                  const metrics = twitterData?.metrics || twitterData?.public_metrics;
                  
                  if (!metrics) return null;
                  
                  const hasAnyMetrics = (metrics.reply_count > 0 || metrics.replyCount > 0 || 
                                       metrics.retweet_count > 0 || metrics.retweetCount > 0 || 
                                       metrics.like_count > 0 || metrics.likeCount > 0);
                  
                  if (!hasAnyMetrics) return null;
                  
                  return (
                    <div className={`flex items-center gap-4 mt-3 pt-2 text-sm border-t ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-slate-200'}`} style={{marginLeft: '60px', marginRight: '16px'}}>
                      {(metrics.reply_count > 0 || metrics.replyCount > 0) && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                          </svg>
                          {(metrics.reply_count || metrics.replyCount || 0).toLocaleString()}
                        </span>
                      )}
                      {(metrics.retweet_count > 0 || metrics.retweetCount > 0) && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                          </svg>
                          {(metrics.retweet_count || metrics.retweetCount || 0).toLocaleString()}
                        </span>
                      )}
                      {(metrics.like_count > 0 || metrics.likeCount > 0) && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                          </svg>
                          {(metrics.like_count || metrics.likeCount || 0).toLocaleString()}
                        </span>
                      )}
                    </div>
                  );
                })()}
                
                {/* Full Thread Display - Show all tweets in a thread if available */}
                {(() => {
                  const twitterData = bookmark.twitterMetadata || bookmark.metadata?.twitter_content?.tweet_data || null;
                  const fullThread = twitterData?.full_thread;
                  const isThread = twitterData?.is_thread;
                  
                  if (!isThread || !fullThread || fullThread.length <= 1) return null;
                  
                  const threadId = bookmark.id || bookmark._id;
                  const isThreadExpanded = expandedThreadIds.has(threadId);
                  
                  // For single-idea cards, skip the first tweet (already shown above)
                  // For multi-idea cards, show all tweets since we might be showing a post from middle of thread
                  const isMultiIdeaCard = bookmark.ticker_ideas && bookmark.ticker_ideas.length > 1;
                  const tweetsToShow = isMultiIdeaCard ? fullThread : fullThread.slice(1);
                  const remainingCount = tweetsToShow.length;
                  
                  if (remainingCount === 0) return null;
                  
                  return (
                    <div className={`mt-4 rounded-xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                      {/* Collapsible header */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedThreadIds(prev => {
                            const next = new Set(prev);
                            if (next.has(threadId)) {
                              next.delete(threadId);
                            } else {
                              next.add(threadId);
                            }
                            return next;
                          });
                        }}
                        className={`w-full p-4 flex items-center justify-between ${isDarkMode ? 'text-slate-300 hover:bg-slate-700/50' : 'text-gray-700 hover:bg-gray-100'} rounded-xl transition-colors`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🧵</span>
                          <span className="font-semibold">
                            {isThreadExpanded 
                              ? 'Hide Thread' 
                              : isMultiIdeaCard 
                                ? `View Full Thread (${remainingCount} tweet${remainingCount > 1 ? 's' : ''})`
                                : `View ${remainingCount} more tweet${remainingCount > 1 ? 's' : ''} in thread`
                            }
                          </span>
                        </div>
                        <svg 
                          className={`w-5 h-5 transition-transform ${isThreadExpanded ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      
                      {/* Thread content - for single-idea, show only subsequent tweets; for multi-idea, show all */}
                      {isThreadExpanded && (
                      <div className="p-4 pt-0 space-y-3">
                        {tweetsToShow.map((tweet, index) => {
                          // Get author info for this tweet (may be same as main author or different for replies)
                          const tweetAuthorName = tweet.author?.name || tweet.name || twitterData?.name || twitterData?.author?.name;
                          const tweetAuthorUsername = tweet.author?.username || tweet.username || twitterData?.username || twitterData?.author?.username;
                          const tweetProfileImage = tweet.author?.profile_image_url || tweet.profile_image_url || tweet.profileImageUrl || twitterData?.profileImageUrl || twitterData?.profile_image_url || twitterData?.author?.profile_image_url;
                          
                          // X profile URL for linking - Display Requirements compliance
                          const tweetXProfileUrl = tweetAuthorUsername ? `https://x.com/${tweetAuthorUsername}` : null;
                          const tweetPermalink = tweet.url;
                          
                          // Calculate the actual position in the full thread
                          const actualIndex = isMultiIdeaCard ? index : index + 1;
                          
                          return (
                          <div 
                            key={tweet.id || index}
                            className={`rounded-lg border p-3 ${isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'}`}
                          >
                            {/* Author Profile Header - X Display Requirements compliant with white space clickable */}
                            <a 
                              href={tweetPermalink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="block cursor-pointer mb-2"
                            >
                              <div className="flex items-start gap-3">
                                {/* Profile Image - MUST link to X profile */}
                                {tweetProfileImage ? (
                                  <span 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      window.open(tweetXProfileUrl, '_blank');
                                    }}
                                    className="flex-shrink-0"
                                  >
                                    <img 
                                      src={tweetProfileImage} 
                                      alt={tweetAuthorName || tweetAuthorUsername}
                                      className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  </span>
                                ) : (
                                  <span 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      window.open(tweetXProfileUrl, '_blank');
                                    }}
                                    className="flex-shrink-0"
                                  >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                      <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {(tweetAuthorUsername || 'U')[0].toUpperCase()}
                                      </span>
                                    </div>
                                  </span>
                                )}
                                
                                <div className="flex-1 min-w-0">
                                  {/* Author info with X Logo */}
                                  <div className="flex items-start justify-between gap-2">
                                    {/* Name and handle stacked vertically */}
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        {tweetAuthorName && (
                                          <span 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              window.open(tweetXProfileUrl, '_blank');
                                            }}
                                            className={`font-bold truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                                          >
                                            {tweetAuthorName}
                                          </span>
                                        )}
                                        <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0">
                                          {actualIndex + 1}/{fullThread.length}
                                        </span>
                                      </div>
                                      {tweetAuthorUsername && (
                                        <span 
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            window.open(tweetXProfileUrl, '_blank');
                                          }}
                                          className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                        >
                                          @{tweetAuthorUsername}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* X Logo - upper right per X Display Requirements */}
                                    <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            </a>
                            
                            {/* Timestamp + View on X - below author header */}
                            <div className="flex items-center gap-2 mb-2" style={{marginLeft: '52px'}}>
                              {tweet.created_at && (
                                <a
                                  href={tweet.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                >
                                  {new Date(tweet.created_at).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </a>
                              )}
                              {/* View on X next to timestamp */}
                              {tweet.url && (
                                <>
                                  <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                                  <a 
                                    href={tweet.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-sm text-blue-500 hover:underline"
                                  >
                                    View on X
                                  </a>
                                </>
                              )}
                            </div>
                            
                            <div 
                              className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-gray-800'}`}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize((() => {
                                  let text = decodeHtmlEntities(tweet.text || '')
                                    .replace(/</g, '&lt;')
                                    .replace(/>/g, '&gt;');
                                  
                                  // Track media URLs to hide them
                                  const mediaUrls = new Set();
                                  
                                  if (tweet.entities?.urls) {
                                    tweet.entities.urls.forEach(urlEntity => {
                                      if (urlEntity.url) {
                                        // Check if this is a media URL (pic.x.com, pic.twitter.com, photo links)
                                        const isMediaUrl = urlEntity.display_url?.startsWith('pic.') || 
                                                          urlEntity.expanded_url?.includes('/photo/') ||
                                                          urlEntity.expanded_url?.includes('/video/');
                                        
                                        if (isMediaUrl) {
                                          mediaUrls.add(urlEntity.url);
                                        }
                                      }
                                    });
                                  }
                                  
                                  // Remove/replace URLs
                                  text = text.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                                    let cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
                                    const punctuation = url.substring(cleanUrl.length);
                                    
                                    // Hide media URLs completely
                                    if (mediaUrls.has(cleanUrl)) {
                                      return punctuation;
                                    }
                                    
                                    // For other URLs, show as clickable link
                                    const displayUrl = cleanUrl.length > 40 ? cleanUrl.substring(0, 37) + '...' : cleanUrl;
                                    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}" onclick="event.stopPropagation()">${displayUrl}</a>${punctuation}`;
                                  });
                                  
                                  return text;
                                })(), { ADD_ATTR: ['target', 'onclick'] })
                              }}
                            />
                            
                            {/* Tweet media in thread */}
                            {tweet.media && tweet.media.length > 0 && (
                              <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: tweet.media.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
                                {tweet.media.map((media, mediaIdx) => (
                                  <img 
                                    key={mediaIdx}
                                    src={media.url || media.preview_image_url} 
                                    alt="Tweet media"
                                    className="rounded-lg w-full object-cover max-h-48"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ))}
                              </div>
                            )}
                            
                            {/* Engagement Metrics */}
                            {(tweet.metrics || tweet.public_metrics) && (
                              <div className={`flex items-center gap-4 mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                                {((tweet.metrics || tweet.public_metrics)?.reply_count > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                    </svg>
                                    {((tweet.metrics || tweet.public_metrics)?.reply_count || 0).toLocaleString()}
                                  </span>
                                )}
                                {((tweet.metrics || tweet.public_metrics)?.retweet_count > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                    </svg>
                                    {((tweet.metrics || tweet.public_metrics)?.retweet_count || 0).toLocaleString()}
                                  </span>
                                )}
                                {((tweet.metrics || tweet.public_metrics)?.like_count > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                    </svg>
                                    {((tweet.metrics || tweet.public_metrics)?.like_count || 0).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          
          {/* Company Profile section moved to ticker button modal */}
          {expandedIdeaCards.has(bookmark.id) && (
            <div data-expanded-content onClick={(e) => e.stopPropagation()} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
              {(() => {
                const primaryTicker = bookmark.primary_ticker?.toUpperCase() || bookmark.tickers?.[0]?.toUpperCase();
                const tdMetrics = primaryTicker ? tickerMetricsCache[primaryTicker] : null;
                const activeTab = ideaCardMetricsTab[bookmark.id] || 'valuation';
                
                // Show loading state while metrics are being fetched
                if (!tdMetrics || (!tdMetrics.company_name && !tdMetrics.market_cap)) {
                  return (
                    <div className={`p-3 sm:p-4 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-slate-50/50'}`}>
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Loading metrics for {primaryTicker || 'this idea'}...
                        </p>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <>
                    {/* Pill Toggle */}
                    <div className={`flex justify-center py-3 ${isDarkMode ? 'bg-[#141c2e]/30' : 'bg-white'}`}>
                      <div className={`inline-flex rounded-full p-1 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setIdeaCardMetricsTab(prev => ({ ...prev, [bookmark.id]: 'profile' })); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                            activeTab === 'profile'
                              ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                              : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                          }`}
                        >
                          Profile
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setIdeaCardMetricsTab(prev => ({ ...prev, [bookmark.id]: 'valuation' })); }}
                          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                            activeTab === 'valuation'
                              ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                              : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                          }`}
                        >
                          Valuation
                        </button>
                        <button
                          onClick={async (e) => { 
                            e.stopPropagation(); 
                            setIdeaCardMetricsTab(prev => ({ ...prev, [bookmark.id]: 'grok' }));
                            // Fetch WWNBT if not already in cache or stored on bookmark
                            if (!wwnbtCache[bookmark.id] && !bookmark.wwnbt_analysis) {
                              const result = await fetchWwnbtAnalysis(bookmark.id);
                              console.log(`Grok click result for ${bookmark.id}:`, result);
                            }
                          }}
                          disabled={wwnbtLoading[bookmark.id]}
                          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                            activeTab === 'grok'
                              ? (isDarkMode ? 'bg-orange-600 text-white shadow' : 'bg-orange-500 text-white shadow')
                              : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                          }`}
                        >
                          {wwnbtLoading[bookmark.id] ? (
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : 'Grok'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Tab Content */}
                    <div className={`px-3 sm:px-4 py-3 ${isDarkMode ? 'bg-[#141c2e]/20' : 'bg-white'}`}>
                      {activeTab === 'profile' ? (
                        /* PROFILE TAB */
                        <div className="space-y-1">
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Company</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.company_name || '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Exchange</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.exchange || '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Industry</span>
                            <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.industry || '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sector</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.sector || bookmark.primary_sector || '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CEO</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ceo || '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Employees</span>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.employees ? tdMetrics.employees.toLocaleString() : '-'}</span>
                          </div>
                          {tdMetrics?.phone && (
                            <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Phone</span>
                              <a href={`tel:${tdMetrics.phone}`} onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.phone}</a>
                            </div>
                          )}
                          {tdMetrics?.website && (
                            <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Website</span>
                              <a href={tdMetrics.website.startsWith('http') ? tdMetrics.website : `https://${tdMetrics.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.website.replace(/^https?:\/\//, '')}</a>
                            </div>
                          )}
                          {(tdMetrics?.address || tdMetrics?.city) && (
                            <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Address</span>
                              <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                {[tdMetrics?.address, tdMetrics?.city, tdMetrics?.state, tdMetrics?.zip].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          {tdMetrics?.description && (
                            <div className={`pt-2`}>
                              <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mb-1`}>Description</div>
                              <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{tdMetrics.description}</div>
                            </div>
                          )}
                          
                          {/* IR Contacts Section */}
                          <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Investor Relations</span>
                              <div className="flex items-center gap-2">
                                {irContactsLoading[primaryTicker?.toUpperCase()] && (
                                  <div className="flex items-center gap-1">
                                    <svg className="animate-spin h-3 w-3 text-purple-500" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Searching...</span>
                                  </div>
                                )}
                                {!irContactsLoading[primaryTicker?.toUpperCase()] && (
                                  <>
                                    {irContactsCache[primaryTicker?.toUpperCase()] && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); primaryTicker && fetchIrContacts(primaryTicker, true); }}
                                        title="Refresh IR info"
                                        className={`text-xs px-2 py-1 rounded transition-colors ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      </button>
                                    )}
                                    {!irContactsCache[primaryTicker?.toUpperCase()] && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); primaryTicker && fetchIrContacts(primaryTicker); }}
                                        className={`text-xs px-2 py-1 rounded transition-colors ${isDarkMode ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-900' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                                      >
                                        Find IR Info
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {irContactsCache[primaryTicker?.toUpperCase()] && !irContactsCache[primaryTicker?.toUpperCase()]?.error && (
                              <div className="space-y-1">
                                {irContactsCache[primaryTicker?.toUpperCase()]?.ir_website && irContactsCache[primaryTicker?.toUpperCase()]?.ir_website !== 'Not found' && (
                                  <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>IR Website</span>
                                    <a href={irContactsCache[primaryTicker?.toUpperCase()].ir_website.startsWith('http') ? irContactsCache[primaryTicker?.toUpperCase()].ir_website : `https://${irContactsCache[primaryTicker?.toUpperCase()].ir_website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} truncate max-w-[180px]`}>
                                      {irContactsCache[primaryTicker?.toUpperCase()].ir_website.replace(/^https?:\/\//, '').split('/')[0]}
                                    </a>
                                  </div>
                                )}
                                {irContactsCache[primaryTicker?.toUpperCase()]?.ir_email && irContactsCache[primaryTicker?.toUpperCase()]?.ir_email !== 'Not found' && (
                                  <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>IR Email</span>
                                    <button 
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        openIrEmailComposer(
                                          primaryTicker?.toUpperCase(),
                                          irContactsCache[primaryTicker?.toUpperCase()].ir_email,
                                          irContactsCache[primaryTicker?.toUpperCase()].company_name || primaryTicker?.toUpperCase(),
                                          irContactsCache[primaryTicker?.toUpperCase()].ir_contact_name
                                        );
                                      }} 
                                      className={`text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} flex items-center gap-1`}
                                    >
                                      {irContactsCache[primaryTicker?.toUpperCase()].ir_email}
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                                {irContactsCache[primaryTicker?.toUpperCase()]?.ir_phone && irContactsCache[primaryTicker?.toUpperCase()]?.ir_phone !== 'Not found' && (
                                  <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>IR Phone</span>
                                    <a href={`tel:${irContactsCache[primaryTicker?.toUpperCase()].ir_phone}`} onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      {irContactsCache[primaryTicker?.toUpperCase()].ir_phone}
                                    </a>
                                  </div>
                                )}
                                {irContactsCache[primaryTicker?.toUpperCase()]?.ir_contact_name && irContactsCache[primaryTicker?.toUpperCase()]?.ir_contact_name !== 'Not found' && (
                                  <div className={`flex justify-between py-1.5 ${isDarkMode ? '' : ''}`}>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>IR Contact</span>
                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                      {irContactsCache[primaryTicker?.toUpperCase()].ir_contact_name}
                                    </span>
                                  </div>
                                )}
                                {/* Show source links for verification */}
                                {irContactsCache[primaryTicker?.toUpperCase()]?.sources && irContactsCache[primaryTicker?.toUpperCase()]?.sources.length > 0 && (
                                  <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                    <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                      Sources: {irContactsCache[primaryTicker?.toUpperCase()].sources.slice(0, 2).map((src, i) => (
                                        <a key={i} href={src} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`${isDarkMode ? 'text-blue-400/70 hover:text-blue-400' : 'text-blue-500/70 hover:text-blue-600'} ml-1`}>
                                          [{i + 1}]
                                        </a>
                                      ))}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : activeTab === 'valuation' ? (
                        /* VALUATION/STATISTICS TAB - 16 metrics in 2 even columns */
                        <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                          {/* Column 1 */}
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Mkt Cap</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.market_cap ? formatFinancialValue(tdMetrics.market_cap) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.enterprise_value ? formatFinancialValue(tdMetrics.enterprise_value) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Fwd P/E</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.forward_pe ? formatRatio(tdMetrics.forward_pe) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Trail P/E</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.trailing_pe ? formatRatio(tdMetrics.trailing_pe) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>PEG</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.peg_ratio ? formatRatio(tdMetrics.peg_ratio) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/B</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_book ? formatRatio(tdMetrics.price_to_book) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/S</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_sales ? formatRatio(tdMetrics.price_to_sales) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/Rev</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_revenue ? formatRatio(tdMetrics.ev_to_revenue) : '-'}</span>
                          </div>
                          {/* Column 2 */}
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/EBITDA</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_ebitda ? formatRatio(tdMetrics.ev_to_ebitda) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Op Margin</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.operating_margin ? `${formatRatio(tdMetrics.operating_margin * 100)}%` : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Shrs Out</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_outstanding ? formatLargeNumber(tdMetrics.shares_outstanding) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Float</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_float ? formatLargeNumber(tdMetrics.shares_float) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Short Ratio</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.short_ratio ? formatRatio(tdMetrics.short_ratio) : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Insider %</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.insider_percent != null ? `${formatRatio(tdMetrics.insider_percent < 1 ? tdMetrics.insider_percent * 100 : tdMetrics.insider_percent)}%` : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Inst %</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.institution_percent != null ? `${formatRatio(tdMetrics.institution_percent < 1 ? tdMetrics.institution_percent * 100 : tdMetrics.institution_percent)}%` : '-'}</span>
                          </div>
                          <div className={`flex justify-between py-1.5 ${isDarkMode ? '' : ''}`}>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Div Yield</span>
                            <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.dividend_yield ? `${formatRatio(tdMetrics.dividend_yield * 100)}%` : '-'}</span>
                          </div>
                        </div>
                      ) : (
                        /* GROK TAB - WWNBT Analysis */
                        <div className="space-y-3">
                          {wwnbtCache[bookmark.id] || bookmark.wwnbt_analysis ? (
                            (() => {
                              const wwnbt = wwnbtCache[bookmark.id] || bookmark.wwnbt_analysis;
                              // Detect effectively-empty WWNBT (no thesis + no assumptions + no killers)
                              const isEmpty = !wwnbt.thesis_summary
                                && (!wwnbt.assumptions || wwnbt.assumptions.length === 0)
                                && (!wwnbt.thesis_killers || wwnbt.thesis_killers.length === 0)
                                && !wwnbt.most_fragile
                                && !wwnbt.one_thing_to_check;
                              if (isEmpty) {
                                return (
                                  <div
                                    data-testid="grok-empty-state"
                                    className={`p-4 rounded text-center ${isDarkMode ? 'bg-slate-800/40 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}
                                  >
                                    <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                      Not enough context to analyze
                                    </p>
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                                      This post is too short for Grok to extract a thesis (e.g. just a ticker, image, or link).
                                      {wwnbt.extraction_error ? ` (${wwnbt.extraction_error})` : ''}
                                    </p>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); fetchWwnbtAnalysis(bookmark.id, true); }}
                                      data-testid="grok-retry-btn"
                                      className={`mt-3 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                                        isDarkMode ? 'bg-orange-600 hover:bg-orange-500 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
                                      }`}
                                    >
                                      Retry analysis
                                    </button>
                                  </div>
                                );
                              }
                              return (
                                <>
                                  {/* Author's Thesis Summary - clearly labeled */}
                                  {wwnbt.thesis_summary && (
                                    <div className={`mb-3 pb-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                      <p className={`text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                        AUTHOR'S THESIS:
                                      </p>
                                      <p className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                        {wwnbt.thesis_summary}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* Grok's Analysis Header */}
                                  <div className={`flex items-center gap-2 mb-2 pb-2 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${isDarkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                                      GROK ANALYSIS
                                    </span>
                                  </div>
                                  
                                  {/* Assumptions */}
                                  {wwnbt.assumptions?.length > 0 && (
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <p className={`text-xs font-semibold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                          FOR THIS TO WORK:
                                        </p>
                                        {/* Legend */}
                                        <div className={`flex items-center gap-2 text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                          <span className="flex items-center gap-1">
                                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-green-500' : 'bg-green-500'}`}></span>
                                            Likely
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-yellow-500' : 'bg-yellow-500'}`}></span>
                                            Uncertain
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <span className={`w-2 h-2 rounded-full ${isDarkMode ? 'bg-red-500' : 'bg-red-500'}`}></span>
                                            Risky
                                          </span>
                                        </div>
                                      </div>
                                      <ul className="space-y-1.5">
                                        {wwnbt.assumptions.map((a, i) => {
                                          // Check if this assumption matches the most fragile one
                                          const isFragile = wwnbt.most_fragile?.assumption && 
                                            (a.assumption === wwnbt.most_fragile.assumption ||
                                             a.assumption?.toLowerCase().includes(wwnbt.most_fragile.assumption?.toLowerCase()?.slice(0, 30)) ||
                                             wwnbt.most_fragile.assumption?.toLowerCase().includes(a.assumption?.toLowerCase()?.slice(0, 30)));
                                          
                                          return (
                                            <li key={i} className="flex items-start gap-2">
                                              <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                isFragile
                                                  ? isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                                                  : a.confidence === 'high' 
                                                  ? isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                                                  : a.confidence === 'medium'
                                                  ? isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                                                  : isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                                              }`}>
                                                {i + 1}
                                              </span>
                                              <span className={`flex-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{a.assumption}</span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {/* Most Fragile */}
                                  {wwnbt.most_fragile && (
                                    <div className={`p-2.5 rounded ${isDarkMode ? 'bg-red-900/20 border border-red-800/30' : 'bg-red-50 border border-red-200'}`}>
                                      <p className={`text-xs font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                        MOST FRAGILE:
                                      </p>
                                      <p className={`mt-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{wwnbt.most_fragile.assumption}</p>
                                      {wwnbt.most_fragile.why_fragile && (
                                        <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          {wwnbt.most_fragile.why_fragile}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Thesis Killers */}
                                  {wwnbt.thesis_killers?.length > 0 && (
                                    <div>
                                      <p className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        WHAT WOULD KILL IT:
                                      </p>
                                      <ul className="mt-1 space-y-0.5">
                                        {wwnbt.thesis_killers.map((killer, i) => (
                                          <li key={i} className="flex items-start gap-1">
                                            <span className={isDarkMode ? 'text-red-400' : 'text-red-500'}>×</span>
                                            <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{killer}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {/* One Thing to Check */}
                                  {wwnbt.one_thing_to_check && (
                                    <div className={`p-2.5 rounded ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'}`}>
                                      <p className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                        ONE THING TO CHECK:
                                      </p>
                                      <p className={`mt-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>→ {wwnbt.one_thing_to_check.action}</p>
                                      {wwnbt.one_thing_to_check.how && (
                                        <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          {wwnbt.one_thing_to_check.how}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Edge Type & Thesis Expiration */}
                                  {(wwnbt.edge_type || wwnbt.thesis_expiration) && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {wwnbt.edge_type && wwnbt.edge_type !== 'none' && (
                                        <span className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                          Edge: {wwnbt.edge_type}
                                        </span>
                                      )}
                                      {wwnbt.thesis_expiration && (
                                        <span className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                          Timing: {wwnbt.thesis_expiration}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Variant Perception */}
                                  {wwnbt.variant_perception && (
                                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                      <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        VARIANT PERCEPTION:
                                      </p>
                                      <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                        {wwnbt.variant_perception}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* WWNBT Action Agent Chat - HIDDEN FOR NOW */}
                                  {false && (
                                  <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                                        ACTION AGENT
                                      </span>
                                      {!wwnbtAgentChat[bookmark.id]?.length && !wwnbtAgentLoading[bookmark.id] && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            sendWwnbtAgentMessage(bookmark.id, '');
                                          }}
                                          className={`text-xs px-2 py-1 rounded transition-colors ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                                        >
                                          Get Suggestions
                                        </button>
                                      )}
                                    </div>
                                    
                                    {/* Chat Messages */}
                                    {wwnbtAgentChat[bookmark.id]?.length > 0 && (
                                      <div className={`space-y-3 mb-3 max-h-80 overflow-y-auto rounded-lg p-3 ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                                        {wwnbtAgentChat[bookmark.id].map((msg, idx) => (
                                          <div key={idx} className={`text-sm ${msg.role === 'user' ? 'text-right' : ''}`}>
                                            {msg.role === 'user' ? (
                                              <span className={`inline-block px-3 py-2 rounded-lg ${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>
                                                {msg.content}
                                              </span>
                                            ) : (
                                              <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {msg.content.split('\n').map((line, i) => {
                                                  // Check for action tags
                                                  const actionMatch = line.match(/\[(SEC_FILINGS|FINANCIAL_METRICS|INSIDER_TRADING|NEWS|COMPETITORS)\]/);
                                                  const actionMap = {
                                                    'SEC_FILINGS': { id: 'sec_filings', label: '📄 Fetch', color: 'green' },
                                                    'FINANCIAL_METRICS': { id: 'financial_metrics', label: '📊 Fetch', color: 'blue' },
                                                    'INSIDER_TRADING': { id: 'insider_trading', label: '👤 Check', color: 'purple' },
                                                    'NEWS': { id: 'news', label: '📰 Search', color: 'orange' },
                                                    'COMPETITORS': { id: 'competitors', label: '🏢 Compare', color: 'teal' }
                                                  };
                                                  
                                                  if (actionMatch) {
                                                    const action = actionMap[actionMatch[1]];
                                                    const cleanLine = line.replace(/\[.*?\]/g, '').trim();
                                                    return (
                                                      <div key={i} className="mt-3 flex items-start gap-2">
                                                        <div className="flex-1">
                                                          <p className={`font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                            {cleanLine}
                                                          </p>
                                                        </div>
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            executeWwnbtAction(bookmark.id, action.id);
                                                          }}
                                                          disabled={wwnbtAgentLoading[bookmark.id]}
                                                          className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded transition-colors ${
                                                            action.color === 'green' ? (isDarkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white') :
                                                            action.color === 'blue' ? (isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white') :
                                                            action.color === 'purple' ? (isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white') :
                                                            action.color === 'orange' ? (isDarkMode ? 'bg-orange-700 hover:bg-orange-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white') :
                                                            (isDarkMode ? 'bg-teal-700 hover:bg-teal-600 text-white' : 'bg-teal-500 hover:bg-teal-600 text-white')
                                                          } ${wwnbtAgentLoading[bookmark.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                          {action.label} →
                                                        </button>
                                                      </div>
                                                    );
                                                  }
                                                  
                                                  // Regular line or action result
                                                  if (line.startsWith('**') && line.includes(':**')) {
                                                    // Section header in results
                                                    return (
                                                      <p key={i} className={`mt-3 font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                        {line.replace(/\*\*/g, '')}
                                                      </p>
                                                    );
                                                  }
                                                  if (line.startsWith('|')) {
                                                    // Table row - render as simple text for now
                                                    return <p key={i} className="font-mono text-xs">{line}</p>;
                                                  }
                                                  return (
                                                    <p key={i} className={line.startsWith('•') || line.startsWith('_') ? 'ml-2' : (line.startsWith('Should I') || line.match(/^\d\./) ? 'mt-2' : '')}>
                                                      {line.startsWith('_') ? <em>{line.replace(/_/g, '')}</em> : line}
                                                    </p>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {/* Loading State */}
                                    {wwnbtAgentLoading[bookmark.id] && (
                                      <div className="flex items-center gap-2 py-2">
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Thinking...</span>
                                      </div>
                                    )}
                                    
                                    {/* Input Field - Show after initial suggestions */}
                                    {wwnbtAgentChat[bookmark.id]?.length > 0 && !wwnbtAgentLoading[bookmark.id] && (
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          value={wwnbtAgentInput[bookmark.id] || ''}
                                          onChange={(e) => setWwnbtAgentInput(prev => ({ ...prev, [bookmark.id]: e.target.value }))}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && wwnbtAgentInput[bookmark.id]?.trim()) {
                                              e.stopPropagation();
                                              sendWwnbtAgentMessage(bookmark.id, wwnbtAgentInput[bookmark.id]);
                                            }
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="Ask a follow-up question..."
                                          className={`flex-1 text-sm px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'}`}
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (wwnbtAgentInput[bookmark.id]?.trim()) {
                                              sendWwnbtAgentMessage(bookmark.id, wwnbtAgentInput[bookmark.id]);
                                            }
                                          }}
                                          disabled={!wwnbtAgentInput[bookmark.id]?.trim()}
                                          className={`px-3 py-2 rounded-lg transition-colors ${wwnbtAgentInput[bookmark.id]?.trim() ? (isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white') : (isDarkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-200 text-slate-400')}`}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  )}
                                </>
                              );
                            })()
                          ) : wwnbtLoading[bookmark.id] ? (
                            <div className="flex items-center justify-center gap-2 py-4">
                              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Analyzing with Grok...</span>
                            </div>
                          ) : wwnbtError[bookmark.id] ? (
                            <div data-testid="grok-error-state" className={`p-4 rounded text-center ${isDarkMode ? 'bg-red-900/20 border border-red-800/50' : 'bg-red-50 border border-red-200'}`}>
                              <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                                Grok analysis failed
                              </p>
                              <p className={`text-xs ${isDarkMode ? 'text-red-400/70' : 'text-red-500'}`}>
                                {wwnbtError[bookmark.id]}
                              </p>
                              <button
                                onClick={(e) => { e.stopPropagation(); fetchWwnbtAnalysis(bookmark.id, true); }}
                                data-testid="grok-error-retry-btn"
                                className={`mt-3 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                                  isDarkMode ? 'bg-orange-600 hover:bg-orange-500 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
                                }`}
                              >
                                Retry analysis
                              </button>
                            </div>
                          ) : (
                            <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Click Grok to analyze this investment thesis
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          </>
          )}
          </CardContent>
      </Card>
      
    </div>
    
    {/* Ideas List EXPANDED ticker rows - REMOVED: Now handled by dedicated Ideas List block in main render loop */}
    {/* The dedicated block at lines 9760-9870 handles the expanded rendering with ticker rows */}
    </div>
    </>
    );
    }

  // Helper function to render Schedule tab body content - moved to component level for reuse in workspace
  // This is the exact same content as the main Schedule tab, enabling reuse in workspace panels
  function renderScheduleBodyContent(isCompact = false) {
    // Show loading state if data isn't ready
    if (!scheduleDataInitialized || scheduledResearchLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }
    
    return (
      <div className={isCompact ? 'p-2' : 'p-3 sm:p-6'}>
                  {scheduleTabView === 'calendar' && (
                    <div>
                      {(() => {
                        // Use appropriate schedule based on auto-schedule setting
                        // If auto-schedule is ON, use researchSchedule (auto-scheduled items)
                        // If auto-schedule is OFF, use scheduledResearch (manual reminders only)
                        let scheduleItems = dossierSettings.enabled 
                          ? researchSchedule.map(item => ({
                              ...item,
                              source: 'auto'
                            }))
                          : scheduledResearch.map(item => ({
                              ticker: item.ticker,
                              company_name: item.company_name || '',
                              scheduled_date: item.reminder_date,
                              scheduled_time: item.reminder_time,
                              status: item.status,
                              source: 'manual'
                            }));
                        
                        // Apply filter - use LOCAL timezone, not UTC
                        const today = new Date();
                        // Get local date string in YYYY-MM-DD format
                        const todayStr = today.getFullYear() + '-' + 
                          String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(today.getDate()).padStart(2, '0');
                        
                        // Calculate 31 days from today for the 31days filter
                        const thirtyOneDaysLater = new Date(today);
                        thirtyOneDaysLater.setDate(today.getDate() + 31);
                        const thirtyOneDaysStr = thirtyOneDaysLater.getFullYear() + '-' + 
                          String(thirtyOneDaysLater.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(thirtyOneDaysLater.getDate()).padStart(2, '0');
                        
                        if (scheduleFilterBy === '31days') {
                          // Show items within the next 31 days
                          scheduleItems = scheduleItems.filter(item => 
                            item.scheduled_date >= todayStr && item.scheduled_date <= thirtyOneDaysStr
                          );
                        } else if (scheduleFilterBy === 'auto') {
                          scheduleItems = scheduleItems.filter(item => item.source === 'auto');
                        } else if (scheduleFilterBy === 'manual') {
                          scheduleItems = scheduleItems.filter(item => item.source === 'manual');
                        } else if (scheduleFilterBy === 'past') {
                          // Show items from before today
                          scheduleItems = scheduleItems.filter(item => item.scheduled_date < todayStr);
                        }
                        // 'all' shows everything without date filtering
                        
                        // Apply completion filter
                        if (scheduleCompletionFilter === 'completed') {
                          scheduleItems = scheduleItems.filter(item => isScheduleItemCompleted(item));
                        } else if (scheduleCompletionFilter === 'incomplete') {
                          scheduleItems = scheduleItems.filter(item => !isScheduleItemCompleted(item));
                        }
                        
                        // Apply ticker search filter (from scheduleTickerSearch OR global searchQuery)
                        // This ensures the global search modal filters calendar items too
                        const activeSearchFilter = scheduleTickerSearch || (searchQuery && searchQuery.trim() && !searchQuery.startsWith('@') && !searchQuery.includes(':') ? searchQuery.trim() : '');
                        if (activeSearchFilter) {
                          const searchUpper = activeSearchFilter.toUpperCase().replace('$', ''); // Remove $ prefix for ticker search
                          scheduleItems = scheduleItems.filter(item => 
                            item.ticker?.toUpperCase().includes(searchUpper) ||
                            item.company_name?.toUpperCase().includes(searchUpper)
                          );
                        }
                        
                        // Apply sort
                        if (scheduleSortBy === 'date-asc') {
                          scheduleItems.sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));
                        } else if (scheduleSortBy === 'date-desc') {
                          scheduleItems.sort((a, b) => (b.scheduled_date || '').localeCompare(a.scheduled_date || ''));
                        } else if (scheduleSortBy === 'ticker-asc') {
                          scheduleItems.sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
                        }
                        
                        // Group schedule by date
                        const scheduleByDate = {};
                        scheduleItems.forEach(item => {
                          const date = item.scheduled_date;
                          if (!scheduleByDate[date]) {
                            scheduleByDate[date] = [];
                          }
                          scheduleByDate[date].push(item);
                        });

                        // Get dates based on filter
                        const dates = [];
                        if (scheduleFilterBy === 'past') {
                          // Get past 30 days (most recent first)
                          for (let i = 1; i <= 30; i++) {
                            const d = new Date(today);
                            d.setDate(d.getDate() - i);
                            const dateStr = d.getFullYear() + '-' + 
                              String(d.getMonth() + 1).padStart(2, '0') + '-' + 
                              String(d.getDate()).padStart(2, '0');
                            dates.push(dateStr);
                          }
                        } else if (scheduleFilterBy === 'completed') {
                          // For completed, get all unique dates from completed items
                          const completedDates = new Set();
                          scheduleItems.forEach(item => {
                            if (item.scheduled_date) {
                              completedDates.add(item.scheduled_date);
                            }
                          });
                          // Sort dates descending (most recent first)
                          dates.push(...Array.from(completedDates).sort((a, b) => b.localeCompare(a)));
                        } else {
                          // Default: next 31 days
                          for (let i = 0; i < 31; i++) {
                            const d = new Date(today);
                            d.setDate(d.getDate() + i);
                            const dateStr = d.getFullYear() + '-' + 
                              String(d.getMonth() + 1).padStart(2, '0') + '-' + 
                              String(d.getDate()).padStart(2, '0');
                            dates.push(dateStr);
                          }
                        }

                        // Group by week for calendar layout (desktop)
                        const weeks = [];
                        let currentWeek = [];
                        dates.forEach((date, idx) => {
                          // Parse date and get day of week in local timezone
                          const [year, month, day] = date.split('-').map(Number);
                          const dateObj = new Date(year, month - 1, day);
                          const dayOfWeek = dateObj.getDay();
                          if (idx === 0) {
                            for (let i = 0; i < dayOfWeek; i++) {
                              currentWeek.push(null);
                            }
                          }
                          currentWeek.push(date);
                          if (dayOfWeek === 6 || idx === dates.length - 1) {
                            weeks.push(currentWeek);
                            currentWeek = [];
                          }
                        });

                        // Filter dates with scheduled items for mobile timeline
                        let datesWithItems = dates.filter(date => scheduleByDate[date]?.length > 0);
                        
                        // Apply date sort to datesWithItems
                        if (scheduleSortBy === 'date-desc') {
                          datesWithItems = [...datesWithItems].reverse();
                        }
                        
                        // Check if we're sorting by ticker (A-Z) - show list view instead of calendar
                        const isTickerSort = scheduleSortBy === 'ticker-asc';

                        return (
                          <>
                            {/* Calendar/List View Toggle - Desktop only, right-justified */}
                            <div className={`hidden sm:flex items-center justify-end mb-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => setShowCalendarView(false)}
                                  className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${
                                    !showCalendarView
                                      ? isDarkMode ? 'bg-slate-700 text-white font-semibold' : 'bg-slate-200 text-slate-800 font-semibold'
                                      : isDarkMode ? 'hover:text-white' : 'hover:text-slate-700'
                                  }`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                                  </svg>
                                  List
                                </button>
                                <button
                                  onClick={() => setShowCalendarView(true)}
                                  className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${
                                    showCalendarView
                                      ? isDarkMode ? 'bg-slate-700 text-white font-semibold' : 'bg-slate-200 text-slate-800 font-semibold'
                                      : isDarkMode ? 'hover:text-white' : 'hover:text-slate-700'
                                  }`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                  Calendar
                                </button>
                              </div>
                            </div>
                            
                            {/* 12-Month View - Shows monthly folders for the next 12 months */}
                            {scheduleViewMode === '12month' && (
                              <div className="space-y-2">
                                {(() => {
                                  const today = new Date();
                                  const months = [];
                                  
                                  // Generate next 12 months
                                  for (let i = 0; i < 12; i++) {
                                    const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
                                    const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
                                    const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                    
                                    // Filter items for this month
                                    const monthItems = scheduleItems.filter(item => {
                                      if (!item.scheduled_date) return false;
                                      return item.scheduled_date.startsWith(monthKey);
                                    });
                                    
                                    months.push({ monthKey, monthName, monthDate, items: monthItems });
                                  }
                                  
                                  return months.map(({ monthKey, monthName, monthDate, items }) => {
                                    const isExpanded = expanded12MonthMonth === monthKey;
                                    const isCurrentMonth = monthDate.getMonth() === today.getMonth() && monthDate.getFullYear() === today.getFullYear();
                                    
                                    return (
                                      <div key={monthKey} className={`relative rounded-lg overflow-hidden border transition-all hover:-translate-y-[1px] ${
                                        isDarkMode ? 'border-white/5 bg-[#1a2235] hover:border-white/10 hover:shadow-card-hover' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                                      }`}>
                                        {/* Status Bar - Left Edge (orange=current month, slate=other) */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${isCurrentMonth ? 'bg-orange-500' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`} />
                                        {/* Month Header - Clickable */}
                                        <button
                                          onClick={() => setExpanded12MonthMonth(isExpanded ? null : monthKey)}
                                          className={`w-full flex items-center justify-between p-3 transition-all ${
                                            isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                                          }`}
                                        >
                                          <div className="flex items-center gap-3">
                                            {/* Folder Icon - Only orange when expanded */}
                                            {isExpanded ? (
                                              <FontAwesomeIcon icon={faFolderOpen} className="h-5 w-5" style={{ color: '#F58346' }} />
                                            ) : (
                                              <FontAwesomeIcon icon={faFolder} className="h-5 w-5 text-slate-400" />
                                            )}
                                            <div className="text-left">
                                              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'} ${isCurrentMonth ? 'text-orange-500' : ''}`}>
                                                {monthName}
                                                {isCurrentMonth && <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Current</span>}
                                              </p>
                                              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                {(() => {
                                                  const pendingCount = items.filter(item => !isScheduleItemCompleted(item)).length;
                                                  const totalCount = items.length;
                                                  if (pendingCount === totalCount) {
                                                    return `${totalCount} ${totalCount === 1 ? 'company' : 'companies'}`;
                                                  }
                                                  return `${pendingCount} pending · ${totalCount} total`;
                                                })()}
                                              </p>
                                            </div>
                                          </div>
                                          <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                          </svg>
                                        </button>
                                        
                                        {/* Month Content - Expanded */}
                                        {isExpanded && (
                                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                            {items.length === 0 ? (
                                              <p className={`p-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                No companies scheduled for this month
                                              </p>
                                            ) : (
                                              <div className="p-2 space-y-1">
                                                {items.map((item, idx) => {
                                                  const itemKey = getScheduleItemKey(item.ticker, item.scheduled_date);
                                                  const isCompleted = isScheduleItemCompleted(item);
                                                  const itemDate = new Date(item.scheduled_date + 'T00:00:00');
                                                  const isItemExpanded = expandedScheduleItem === itemKey;
                                                  
                                                  return (
                                                    <div key={idx} className="space-y-1">
                                                      {/* Item Row - Modern Card with Status Bar */}
                                                      <div 
                                                        onClick={() => setExpandedScheduleItem(isItemExpanded ? null : itemKey)}
                                                        data-schedule-ticker={item.ticker?.toUpperCase()}
                                                        className={`relative flex items-center gap-3 p-2 pl-4 rounded-lg cursor-pointer transition-all overflow-hidden border ${
                                                          isDarkMode 
                                                            ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover hover:-translate-y-[1px]' 
                                                            : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md hover:-translate-y-[1px]'
                                                        } ${isCompleted ? 'opacity-50' : ''} ${workspaceHighlightedTicker && item.ticker?.toUpperCase() === workspaceHighlightedTicker ? 'workspace-highlight' : ''}`}
                                                      >
                                                        {/* Status Bar - Left Edge (green=completed, blue=pending) */}
                                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${isCompleted ? 'bg-emerald-500' : 'bg-electric'}`} />
                                                        {/* Completion Toggle - Circle Radio */}
                                                        <button
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleScheduleItemComplete(itemKey, item.ticker, item.scheduled_date);
                                                          }}
                                                          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                            isCompleted
                                                              ? 'bg-green-500 border-green-500 text-white'
                                                              : isDarkMode
                                                                ? 'border-slate-500 hover:border-slate-400 hover:bg-slate-700'
                                                                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                                          }`}
                                                        >
                                                          {isCompleted && (
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                                            </svg>
                                                          )}
                                                        </button>
                                                        
                                                        {/* Ticker & Company */}
                                                        <div className="flex-1 min-w-0">
                                                          <div className="flex items-center gap-2">
                                                            <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'} ${isCompleted ? 'line-through' : ''}`}>
                                                              ${item.ticker}
                                                            </span>
                                                            {item.company_name && (
                                                              <span className={`text-sm truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} ${isCompleted ? 'line-through' : ''}`}>
                                                                {item.company_name}
                                                              </span>
                                                            )}
                                                          </div>
                                                        </div>
                                                        
                                                        {/* Date Badge */}
                                                        <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                                          {itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                        </span>
                                                        
                                                        {/* Expand Arrow */}
                                                        <svg className={`w-4 h-4 transition-transform ${isItemExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                        </svg>
                                                      </div>
                                                      
                                                      {/* Expanded Action Buttons */}
                                                      {isItemExpanded && (
                                                        <div className={`schedule-item-dropdown p-3 pl-10 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-100'}`}>
                                                          {/* Button Order (left to right): File → Recycle → Ticker → Share → Open */}
                                                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                                                            {/* File Button */}
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                setFilesSearchQuery(item.ticker);
                                                                setActiveTab('files');
                                                              }}
                                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                            >
                                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                              </svg>
                                                              <span className="text-[10px] sm:text-xs">File</span>
                                                            </button>
                                                            
                                                            {/* Recycle Button */}
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                openRescheduleModal(item);
                                                              }}
                                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                                                            >
                                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                                              </svg>
                                                              <span className="text-[10px] sm:text-xs">Recycle</span>
                                                            </button>
                                                            
                                                            {/* Watchlist/Ticker Button */}
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTickerForWatchlist({ ticker: item.ticker });
                                                                setShowWatchlistModal(true);
                                                              }}
                                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                                            >
                                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                              </svg>
                                                              <span className="text-[10px] sm:text-xs">Tickers</span>
                                                            </button>
                                                            
                                                            {/* Share Button */}
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTickerForScheduling({
                                                                  ticker: item.ticker,
                                                                  company_name: item.company_name,
                                                                  tweetId: item.twitterMetadata?.tweet_id || item.global_tweet_id,
                                                                  shareType: 'idea'
                                                                });
                                                                setShowShareModal(true);
                                                              }}
                                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                                            >
                                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                              </svg>
                                                              <span className="text-[10px] sm:text-xs">Share</span>
                                                            </button>
                                                            
                                                            {/* Original Button - Navigate to idea in Ideas tab */}
                                                            <button
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (item.idea_id) {
                                                                  // Navigate to Ideas tab and scroll to the idea card
                                                                  setScrollToIdeaTarget({ ticker: item.ticker, ideaId: item.idea_id });
                                                                  setActiveTab('dashboard');
                                                                } else {
                                                                  showNotification(`No idea card found for ${item.ticker}`, 'info');
                                                                }
                                                              }}
                                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                                            >
                                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                              </svg>
                                                              <span className="text-[10px] sm:text-xs">Open</span>
                                                            </button>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                            )}
                            
                            {/* Ticker A-Z List View - Shows all items sorted by ticker */}
                            {scheduleViewMode !== '12month' && isTickerSort && scheduleItems.length > 0 && (
                              <div className="space-y-2">
                                <div className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {scheduleItems.length} idea{scheduleItems.length !== 1 ? 's' : ''} sorted by ticker
                                </div>
                                {scheduleItems.map((item, i) => {
                                  // Parse date in local timezone
                                  const [year, month, day] = (item.scheduled_date || '').split('-').map(Number);
                                  const itemDate = new Date(year, month - 1, day);
                                  const isItemToday = item.scheduled_date === todayStr;
                                  const itemKey = getScheduleItemKey(item.ticker, item.scheduled_date);
                                  const isExpanded = expandedScheduleItem === itemKey;
                                  const isItemCompleted = isScheduleItemCompleted(item);
                                  return (
                                    <div key={i} className={`relative schedule-item-dropdown flex items-center gap-3 ${workspaceHighlightedTicker && item.ticker?.toUpperCase() === workspaceHighlightedTicker ? 'workspace-highlight rounded-lg' : ''}`} data-schedule-ticker={item.ticker?.toUpperCase()}>
                                      {/* Checkbox outside the row - Gmail style */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleScheduleItemComplete(itemKey, item.ticker, item.scheduled_date);
                                        }}
                                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                          isItemCompleted
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : isDarkMode
                                              ? 'border-slate-500 hover:border-slate-400 hover:bg-slate-700'
                                              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                        }`}
                                      >
                                        {isItemCompleted && (
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                          </svg>
                                        )}
                                      </button>
                                      {/* Content row - Modern Card */}
                                      <div className="flex-1">
                                      <div
                                        data-schedule-item="true"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedScheduleItem(isExpanded ? null : itemKey);
                                        }}
                                        className={`relative flex items-center justify-between p-3 pl-4 rounded-lg cursor-pointer transition-all overflow-hidden border ${
                                          isItemCompleted
                                            ? isDarkMode 
                                              ? 'bg-emerald-900/30 border-emerald-700/50 hover:border-emerald-600' 
                                              : 'bg-emerald-50 border-emerald-200 hover:border-emerald-300'
                                            : isDarkMode 
                                              ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover hover:-translate-y-[1px]' 
                                              : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md hover:-translate-y-[1px]'
                                        } ${workspaceHighlightedTicker && item.ticker?.toUpperCase() === workspaceHighlightedTicker ? 'workspace-highlight' : ''}`}
                                      >
                                        {/* Status Bar - Left Edge */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                                          isItemCompleted ? 'bg-emerald-500' : isItemToday ? 'bg-orange-500' : 'bg-electric'
                                        }`} />
                                        <div className="flex items-center gap-3">
                                          <span className={`font-mono font-bold ${
                                            isItemCompleted 
                                              ? 'line-through opacity-75 text-emerald-400' 
                                              : isItemToday 
                                                ? (isDarkMode ? 'text-orange-300' : 'text-orange-600')
                                                : (isDarkMode ? 'text-white' : 'text-slate-800')
                                          }`}>
                                            ${item.ticker}
                                          </span>
                                          {item.company_name && (
                                            <span className={`text-sm ${isItemCompleted ? 'line-through opacity-75' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {item.company_name}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            {itemDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                          </span>
                                          {isItemToday && (
                                            <span className="text-xs text-orange-500 font-semibold">(Today's Schedule)</span>
                                          )}
                                          {item.source === 'auto' && !isItemToday && (
                                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                            </svg>
                                          )}
                                          <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                          </svg>
                                        </div>
                                      </div>
                                      {/* Dropdown Actions */}
                                      {isExpanded && (
                                        <div className={`schedule-item-dropdown mt-1 p-2 pl-10 rounded-lg ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-slate-50 border border-slate-100 shadow-sm'}`}>
                                          {/* Summary Card Section with swipe for multiple ideas */}
                                          {(() => {
                                            const itemKey = `${item.ticker}-${item.scheduled_date}`;
                                            
                                            // Use ideas array directly from schedule item (stored during generation)
                                            // This avoids pagination issues with the bookmarks array
                                            const ideasArray = item.ideas || [];
                                            const totalIdeas = ideasArray.length || 1;
                                            const currentIndex = scheduleIdeaIndex[itemKey] || 0;
                                            const currentIdea = ideasArray[currentIndex];
                                            
                                            // Get content - prioritize per-idea summary first
                                            const originalContent = currentIdea?.summary ||
                                                                   currentIdea?.original_content ||
                                                                   currentIdea?.twitterMetadata?.text ||
                                                                   currentIdea?.content ||
                                                                   item.original_content ||
                                                                   item.twitterMetadata?.text ||
                                                                   item.content;
                                            
                                            // Get AI summary content - PRIORITIZE preview (hook) then per-idea data
                                            const previewHook = currentIdea?.preview || item.preview;
                                            const perIdeaAiSummary = previewHook ||
                                                                   currentIdea?.wwnbt_analysis?.thesis_summary || 
                                                                   currentIdea?.thesis_summary ||
                                                                   currentIdea?.one_liner_thesis ||
                                                                   currentIdea?.thesis ||
                                                                   currentIdea?.summary;
                                            const itemLevelAiSummary = item.wwnbt_analysis?.thesis_summary || item.one_liner_thesis;
                                            const aiSummary = perIdeaAiSummary || itemLevelAiSummary || '';
                                            const hasAiContent = !!aiSummary;
                                            
                                            // View mode for this schedule item
                                            const viewModeKey = `${itemKey}-${currentIndex}`;
                                            const currentViewMode = scheduleCardViewMode[viewModeKey] || 'ai';
                                            const isShowingAi = currentViewMode === 'ai' && hasAiContent;
                                            
                                            // Determine what to display
                                            const displayText = isShowingAi ? aiSummary : (originalContent || currentIdea?.summary || item.summary || item.title);
                                            const authorName = currentIdea?.author || item.author;
                                            const contentKey = `schedule-${itemKey}-${currentIndex}`;
                                            const isLong = (displayText || '').length > 280;
                                            const isContentExpanded = scheduleContentExpanded[contentKey];
                                            const truncatedContent = isContentExpanded ? displayText : (displayText || '').substring(0, 280);
                                            
                                            // Get idea ID for linking back
                                            const ideaId = currentIdea?.id || item.idea_id;
                                            
                                            return (
                                              <div 
                                                className={`mb-3 p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-[#1a2235] border border-slate-600 hover:bg-[#1e2940]' : 'bg-white border border-slate-200 hover:bg-slate-50'}`}
                                                onClick={() => {
                                                  // Toggle between summary and full post view
                                                  const newMode = isShowingAi ? 'tweet' : 'ai';
                                                  setScheduleCardViewMode(prev => ({ ...prev, [viewModeKey]: newMode }));
                                                }}
                                              >
                                                {/* Navigation header for multiple ideas */}
                                                {totalIdeas > 1 && (
                                                  <div className="flex items-center justify-between mb-2">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setScheduleIdeaIndex(prev => ({
                                                          ...prev,
                                                          [itemKey]: (currentIndex - 1 + totalIdeas) % totalIdeas
                                                        }));
                                                      }}
                                                      className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                                                    >
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                                                      </svg>
                                                    </button>
                                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                      {currentIndex + 1} of {totalIdeas} ideas
                                                    </span>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setScheduleIdeaIndex(prev => ({
                                                          ...prev,
                                                          [itemKey]: (currentIndex + 1) % totalIdeas
                                                        }));
                                                      }}
                                                      className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                                                    >
                                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                                      </svg>
                                                    </button>
                                                  </div>
                                                )}
                                                
                                                {truncatedContent ? (
                                                  <div>
                                                    {/* Author Profile Header - X Display Requirements compliant */}
                                                    {(() => {
                                                      const twitterData = currentIdea?.twitterMetadata || currentIdea?.metadata?.twitter_content?.tweet_data || item.twitterMetadata || item.metadata?.twitter_content?.tweet_data || null;
                                                      const authorDisplayName = twitterData?.name || twitterData?.author?.name || currentIdea?.tweet_author_name || currentIdea?.from_name || item.from_name;
                                                      const username = twitterData?.username || twitterData?.author?.username || authorName?.replace('@', '') || currentIdea?.author || currentIdea?.tweet_author?.replace('@', '');
                                                      const profileImage = twitterData?.profileImageUrl || twitterData?.profile_image_url || twitterData?.author?.profile_image_url || currentIdea?.profile_image || currentIdea?.author_profile_image || item.profile_image;
                                                      const tweetUrl = twitterData?.url || twitterData?.tweet_url || currentIdea?.url || currentIdea?.tweet_url || item.url;
                                                      const tweetDate = twitterData?.created_at || twitterData?.createdAt || currentIdea?.created_at;
                                                      
                                                      // Build X profile URL for linking - Display Requirements compliance
                                                      const xProfileUrl = username ? `https://x.com/${username}` : null;
                                                      
                                                      return (
                                                        <div className="mb-3">
                                                          {/* Clickable white space area linking to permalink */}
                                                          <a 
                                                            href={tweetUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="block cursor-pointer"
                                                          >
                                                            <div className="flex items-start gap-2">
                                                              {/* Profile Image - MUST link to X profile */}
                                                              {profileImage ? (
                                                                <span 
                                                                  onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    window.open(xProfileUrl, '_blank');
                                                                  }}
                                                                  className="flex-shrink-0"
                                                                >
                                                                  <img 
                                                                    src={profileImage} 
                                                                    alt={authorDisplayName || username}
                                                                    className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                  />
                                                                </span>
                                                              ) : (
                                                                <span 
                                                                  onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    window.open(xProfileUrl, '_blank');
                                                                  }}
                                                                  className="flex-shrink-0"
                                                                >
                                                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                                      {(username || 'U')[0].toUpperCase()}
                                                                    </span>
                                                                  </div>
                                                                </span>
                                                              )}
                                                              
                                                              <div className="flex-1 min-w-0">
                                                                <div className="flex items-start justify-between gap-2">
                                                                  {/* Name and handle stacked vertically */}
                                                                  <div className="min-w-0 flex-1">
                                                                    {/* Display Name - MUST link to X profile */}
                                                                    <span 
                                                                      onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        window.open(xProfileUrl, '_blank');
                                                                      }}
                                                                      className={`font-bold block truncate hover:underline cursor-pointer text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                                                    >
                                                                      {authorDisplayName || username || 'Unknown'}
                                                                    </span>
                                                                    {/* @username below name - MUST link to X profile */}
                                                                    {username && (
                                                                      <span 
                                                                        onClick={(e) => {
                                                                          e.preventDefault();
                                                                          e.stopPropagation();
                                                                          window.open(xProfileUrl, '_blank');
                                                                        }}
                                                                        className={`block text-xs truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                                                      >
                                                                        @{username}
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                  
                                                                  {/* X Logo - upper right per X Display Requirements */}
                                                                  <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                                                  </svg>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </a>
                                                          
                                                          {/* Timestamp + View on X - below author header */}
                                                          <div className="flex items-center gap-2 mt-1" style={{marginLeft: '48px'}}>
                                                            {tweetDate && (
                                                              <a
                                                                href={tweetUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className={`text-xs hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                                              >
                                                                {new Date(tweetDate).toLocaleString('en-US', {
                                                                  month: 'short',
                                                                  day: 'numeric',
                                                                  year: 'numeric',
                                                                  hour: 'numeric',
                                                                  minute: '2-digit',
                                                                  hour12: true
                                                                })}
                                                              </a>
                                                            )}
                                                            {/* View on X next to timestamp */}
                                                            {tweetUrl && (
                                                              <>
                                                                <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                                                                <a
                                                                  href={tweetUrl}
                                                                  target="_blank"
                                                                  rel="noopener noreferrer"
                                                                  onClick={(e) => e.stopPropagation()}
                                                                  className="text-xs text-blue-500 hover:underline"
                                                                >
                                                                  View on X
                                                                </a>
                                                              </>
                                                            )}
                                                            {/* Grok Toggle - at the end */}
                                                            {hasAiContent && originalContent && (
                                                              <div className="flex items-center gap-1.5 ml-auto">
                                                                <button
                                                                  onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const newMode = isShowingAi ? 'tweet' : 'ai';
                                                                    setScheduleCardViewMode(prev => ({ ...prev, [viewModeKey]: newMode }));
                                                                  }}
                                                                  className={`relative w-8 h-4 rounded-full cursor-pointer transition-colors duration-200 bg-black`}
                                                                  data-testid="folder-grok-toggle"
                                                                >
                                                                  <div 
                                                                    className={`absolute top-0.5 w-3 h-3 transition-all duration-200 ease-in-out ${
                                                                      !isShowingAi ? 'left-[18px]' : 'left-0.5'
                                                                    }`}
                                                                  >
                                                                    <svg className="w-3 h-3" viewBox="0 0 512 509.641" fill="#000000">
                                                                      <path d="M115.612 0h280.776C459.975 0 512 52.026 512 115.612v278.416c0 63.587-52.025 115.613-115.612 115.613H115.612C52.026 509.641 0 457.615 0 394.028V115.612C0 52.026 52.026 0 115.612 0z"/>
                                                                      <path fill="#ffffff" d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
                                                                    </svg>
                                                                  </div>
                                                                </button>
                                                                <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                                                  {!isShowingAi ? 'Original' : 'Preview'}
                                                                </span>
                                                              </div>
                                                            )}
                                                          </div>
                                                          
                                                          {/* Metrics - shown in full post mode */}
                                                          {!isShowingAi && (() => {
                                                            const metrics = twitterData?.metrics || twitterData?.public_metrics || currentIdea?.twitterMetadata?.metrics || currentIdea?.twitterMetadata?.public_metrics;
                                                            if (!metrics) return null;
                                                            const hasAnyMetrics = (metrics.reply_count > 0 || metrics.replyCount > 0 || 
                                                                                   metrics.retweet_count > 0 || metrics.retweetCount > 0 || 
                                                                                   metrics.like_count > 0 || metrics.likeCount > 0);
                                                            if (!hasAnyMetrics) return null;
                                                            return (
                                                              <div className={`flex items-center gap-4 text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} style={{marginLeft: '48px'}}>
                                                                {(metrics.reply_count > 0 || metrics.replyCount > 0) && (
                                                                  <span className="flex items-center gap-1">
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                                                    </svg>
                                                                    {(metrics.reply_count || metrics.replyCount || 0).toLocaleString()}
                                                                  </span>
                                                                )}
                                                                {(metrics.retweet_count > 0 || metrics.retweetCount > 0) && (
                                                                  <span className="flex items-center gap-1">
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                                                    </svg>
                                                                    {(metrics.retweet_count || metrics.retweetCount || 0).toLocaleString()}
                                                                  </span>
                                                                )}
                                                                {(metrics.like_count > 0 || metrics.likeCount > 0) && (
                                                                  <span className="flex items-center gap-1">
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                                                    </svg>
                                                                    {(metrics.like_count || metrics.likeCount || 0).toLocaleString()}
                                                                  </span>
                                                                )}
                                                              </div>
                                                            );
                                                          })()}
                                                          
                                                          {/* Engagement metrics removed - View in Ideas functionality moved to Open button */}
                                                        </div>
                                                      );
                                                    })()}
                                                    
                                                    {/* Content with clickable links */}
                                                    <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                      {renderTextWithLinks(decodeHtmlEntities(truncatedContent), isDarkMode)}{isLong && !isContentExpanded && '...'}
                                                    </div>
                                                    {isLong && !isContentExpanded && (
                                                      <button
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          setScheduleContentExpanded(prev => ({ ...prev, [contentKey]: true }));
                                                        }}
                                                        className={`mt-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                                                      >
                                                        more...
                                                      </button>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <p className={`text-sm italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                    No content available for this idea.
                                                  </p>
                                                )}
                                                
                                                {/* Dot indicators for multiple ideas */}
                                                {totalIdeas > 1 && (
                                                  <div className="flex justify-center gap-1.5 mt-3">
                                                    {Array.from({ length: totalIdeas }).map((_, idx) => (
                                                      <button
                                                        key={idx}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setScheduleIdeaIndex(prev => ({ ...prev, [itemKey]: idx }));
                                                        }}
                                                        className={`w-2 h-2 rounded-full transition-colors ${
                                                          idx === currentIndex
                                                            ? (isDarkMode ? 'bg-blue-400' : 'bg-blue-600')
                                                            : (isDarkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-300 hover:bg-slate-400')
                                                        }`}
                                                      />
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          
                                          {/* Button Order (left to right): File → Recycle → Ticker → Share → Open */}
                                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                                            {/* File Button */}
                                            <button
                                              onClick={(e) => { 
                                                e.stopPropagation(); 
                                                // Open Briefs tab with this ticker's research documents
                                                setFilesSearchQuery(item.ticker);
                                                setActiveTab('files');
                                              }}
                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                            >
                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                              </svg>
                                              <span className="text-[10px] sm:text-xs">File</span>
                                            </button>
                                            {/* Recycle Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Open reschedule modal with options
                                                openRescheduleModal(item);
                                              }}
                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                                            >
                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                              </svg>
                                              <span className="text-[10px] sm:text-xs">Recycle</span>
                                            </button>
                                            {/* Tickers Button */}
                                            <button
                                              onClick={async (e) => { 
                                                e.stopPropagation();
                                                // Add ticker to user's default watchlist directly
                                                try {
                                                  const response = await fetch(`${API_BASE_URL}/api/watchlists/add-ticker`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                      user_id: user?.id || 'demo_user',
                                                      ticker: item.ticker
                                                    })
                                                  });
                                                  if (response.ok) {
                                                    showNotification(`Added $${item.ticker} to your watchlist!`, 'success');
                                                  } else {
                                                    showNotification(`Failed to add ${item.ticker} to watchlist`, 'error');
                                                  }
                                                } catch (error) {
                                                  console.error('Error adding to watchlist:', error);
                                                  showNotification(`Error adding ${item.ticker} to watchlist`, 'error');
                                                }
                                              }}
                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                            >
                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                              </svg>
                                              <span className="text-[10px] sm:text-xs">Tickers</span>
                                            </button>
                                            {/* Share Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Find the bookmark - try multiple ways
                                                let tickerBookmark = null;
                                                
                                                // 1. Try by idea_id
                                                if (item.idea_id) {
                                                  tickerBookmark = bookmarks.find(b => b.id === item.idea_id);
                                                }
                                                
                                                // 2. Try by idea_ids array
                                                if (!tickerBookmark && item.idea_ids && item.idea_ids.length > 0) {
                                                  tickerBookmark = bookmarks.find(b => item.idea_ids.includes(b.id));
                                                }
                                                
                                                // 3. Try by ticker match (case-insensitive)
                                                if (!tickerBookmark) {
                                                  const tickerUpper = item.ticker?.toUpperCase();
                                                  tickerBookmark = bookmarks.find(b => 
                                                    b.tickers?.some(t => t?.toUpperCase() === tickerUpper || t?.toUpperCase() === `$${tickerUpper}`) ||
                                                    b.tickers?.some(t => t?.toUpperCase().replace('$', '') === tickerUpper) ||
                                                    b.primary_ticker?.toUpperCase() === tickerUpper ||
                                                    b.primary_ticker?.toUpperCase() === `$${tickerUpper}` ||
                                                    b.primary_ticker?.toUpperCase().replace('$', '') === tickerUpper
                                                  );
                                                }
                                                
                                                // 4. Try by ticker in title/content
                                                if (!tickerBookmark) {
                                                  const tickerUpper = item.ticker?.toUpperCase();
                                                  tickerBookmark = bookmarks.find(b => 
                                                    b.title?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                    b.title?.toUpperCase().includes(tickerUpper) ||
                                                    b.content?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                    b.content?.toUpperCase().includes(tickerUpper)
                                                  );
                                                }
                                                
                                                if (tickerBookmark) {
                                                  setSelectedTickerForScheduling({
                                                    ticker: tickerBookmark.tickers?.[0] || item.ticker,
                                                    company_name: tickerBookmark.company_name || tickerBookmark.title,
                                                    ideaId: tickerBookmark.id,
                                                    tweetId: tickerBookmark.twitterMetadata?.tweet_id || tickerBookmark.global_tweet_id,
                                                    shareType: 'idea'
                                                  });
                                                  setShowShareModal(true);
                                                } else {
                                                  showNotification(`No idea found for ${item.ticker} to share. Save the original idea first.`, 'info');
                                                }
                                              }}
                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                            >
                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                              </svg>
                                              <span className="text-[10px] sm:text-xs">Share</span>
                                            </button>
                                            {/* Original Button - Navigate to idea in Ideas tab */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const itemKey = `${item.ticker}-${item.scheduled_date}`;
                                                const ideasArray = item.ideas || [];
                                                const currentIndex = scheduleIdeaIndex[itemKey] || 0;
                                                const currentIdea = ideasArray[currentIndex];
                                                const ideaId = currentIdea?.id || item.idea_id;
                                                
                                                if (ideaId) {
                                                  // Navigate to Ideas tab and scroll to the idea card
                                                  setScrollToIdeaTarget({ ticker: item.ticker, ideaId: ideaId });
                                                  setActiveTab('dashboard');
                                                } else {
                                                  showNotification(`No idea card found for ${item.ticker}`, 'info');
                                                }
                                              }}
                                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                            >
                                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                              </svg>
                                              <span className="text-[10px] sm:text-xs">Open</span>
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Empty state for ticker sort */}
                            {scheduleViewMode !== '12month' && isTickerSort && scheduleItems.length === 0 && !scheduledResearchLoading && (
                              <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                <p>No scheduled research yet</p>
                              </div>
                            )}

                            {/* List View - Shows on mobile always, and on desktop when calendar toggle is OFF */}
                            {scheduleViewMode !== '12month' && !isTickerSort && (
                              <div className={`space-y-2 ${showCalendarView ? 'sm:hidden' : ''}`}>
                                {scheduledResearchLoading ? (
                                  <div className="flex items-center justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                ) : datesWithItems.length === 0 ? (
                                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    <p>No scheduled research yet</p>
                                  </div>
                                ) : (
                                  datesWithItems.map(date => {
                                    const items = scheduleByDate[date] || [];
                                    // Parse date in local timezone
                                    const [year, month, day] = date.split('-').map(Number);
                                    const dateObj = new Date(year, month - 1, day);
                                    const isToday = date === todayStr;
                                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                                    const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                    const isFolderExpanded = expandedFolderDate === date;

                                    return (
                                      <div
                                        key={date}
                                        data-folder-date={date}
                                        className={`rounded-lg overflow-hidden border ${
                                          isDarkMode ? 'border-slate-700 bg-[#1a2235]' : 'border-slate-200 bg-white'
                                        }`}
                                      >
                                        {/* Date Header - Clickable folder row */}
                                        <button
                                          onClick={() => setExpandedFolderDate(isFolderExpanded ? null : date)}
                                          className={`w-full px-3 py-2.5 transition-all ${
                                            isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                                          }`}
                                        >
                                          <div className="space-y-0.5">
                                            {/* Row 1: Folder icon + Weekday name | Today pill | company count (far right) */}
                                            <div className="flex items-center justify-between gap-2">
                                              <div className="flex items-center gap-2">
                                                {/* Folder Icon - orange open when expanded, grey closed otherwise */}
                                                {isFolderExpanded ? (
                                                  <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" style={{ color: '#F58346' }} />
                                                ) : (
                                                  <FontAwesomeIcon icon={faFolder} className="h-4 w-4 text-slate-400" />
                                                )}
                                                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'} ${isToday ? 'text-orange-500' : ''}`}>
                                                  {dayName}
                                                </span>
                                                {isToday && (
                                                  <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Today</span>
                                                )}
                                              </div>
                                              <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                {(() => {
                                                  const pendingCount = items.filter(item => !isScheduleItemCompleted(item)).length;
                                                  const totalCount = items.length;
                                                  if (pendingCount === totalCount) {
                                                    return `${totalCount} ${totalCount === 1 ? 'company' : 'companies'}`;
                                                  }
                                                  return `${pendingCount} pending · ${totalCount} total`;
                                                })()}
                                              </span>
                                            </div>
                                            
                                            {/* Row 2: Date | down arrow (far right) */}
                                            <div className="flex items-center justify-between">
                                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} ml-6`}>
                                                {monthDay}
                                              </span>
                                              <svg className={`w-5 h-5 transition-transform ${isFolderExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                              </svg>
                                            </div>
                                          </div>
                                        </button>
                                        
                                        {/* Ticker List - Only visible when folder is expanded */}
                                        {isFolderExpanded && (
                                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                            <div className="p-2 space-y-1">
                                              {items.map((item, i) => {
                                                const itemKey = getScheduleItemKey(item.ticker, date);
                                                const isItemExpanded = expandedScheduleItem === itemKey;
                                                const isCompleted = isScheduleItemCompleted(item);
                                                return (
                                                  <div key={i} className="space-y-1">
                                                    {/* Item Row */}
                                                    <div 
                                                      data-schedule-item="true"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedScheduleItem(isItemExpanded ? null : itemKey);
                                                      }}
                                                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                                        isDarkMode 
                                                          ? 'hover:bg-slate-700/50' 
                                                          : 'hover:bg-slate-50'
                                                      } ${isCompleted ? 'opacity-50' : ''}`}
                                                    >
                                                      {/* Circle radio button for completing items */}
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          toggleScheduleItemComplete(itemKey, item.ticker, date);
                                                        }}
                                                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                          isCompleted
                                                            ? 'bg-green-500 border-green-500 text-white'
                                                            : isDarkMode
                                                              ? 'border-slate-500 hover:border-slate-400 hover:bg-slate-700'
                                                              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                                                        }`}
                                                      >
                                                        {isCompleted && (
                                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                                          </svg>
                                                        )}
                                                      </button>
                                                      {/* Ticker info */}
                                                      <div className="flex-1 min-w-0">
                                                        <span className={`font-bold text-sm ${
                                                          isCompleted 
                                                            ? 'line-through text-green-600' 
                                                            : isToday 
                                                              ? (isDarkMode ? 'text-orange-300' : 'text-orange-600')
                                                              : (isDarkMode ? 'text-slate-200' : 'text-slate-700')
                                                        }`}>
                                                          ${item.ticker}
                                                        </span>
                                                        {item.company_name && (
                                                          <span className={`ml-2 text-sm truncate ${isCompleted ? 'line-through' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                            {item.company_name}
                                                          </span>
                                                        )}
                                                      </div>
                                                      <svg 
                                                        className={`w-4 h-4 flex-shrink-0 transition-transform ${isItemExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} 
                                                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                      >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                      </svg>
                                                    </div>
                                                    {/* Dropdown Actions for individual item */}
                                                    {isItemExpanded && (
                                                      <div 
                                                        className={`schedule-item-dropdown p-2 pl-10 rounded-lg ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-slate-50 border border-slate-100 shadow-sm'}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                      >
                                                        {/* Summary Card Section with swipe for multiple ideas */}
                                                        {(() => {
                                                          const itemKey = `${item.ticker}-${item.scheduled_date}`;
                                                          
                                                          // Use ideas array directly from schedule item (stored during generation)
                                                          // This avoids pagination issues with the bookmarks array
                                                          const ideasArray = item.ideas || [];
                                                          const totalIdeas = ideasArray.length || 1;
                                                          const currentIndex = scheduleIdeaIndex[itemKey] || 0;
                                                          const currentIdea = ideasArray[currentIndex];
                                                          
                                                          // Get content - prioritize per-idea summary first
                                                          const originalContent2 = currentIdea?.summary ||
                                                                                 currentIdea?.original_content ||
                                                                                 currentIdea?.twitterMetadata?.text ||
                                                                                 currentIdea?.content ||
                                                                                 item.original_content ||
                                                                                 item.twitterMetadata?.text ||
                                                                                 item.content;
                                                          
                                                          // Get AI summary content - PRIORITIZE preview (hook) then per-idea data
                                                          // First check if currentIdea has its own preview/summary/thesis
                                                          const previewHook2 = currentIdea?.preview || item.preview;
                                                          const perIdeaAiSummary = previewHook2 ||
                                                                                  currentIdea?.wwnbt_analysis?.thesis_summary || 
                                                                                  currentIdea?.thesis_summary ||
                                                                                  currentIdea?.one_liner_thesis ||
                                                                                  currentIdea?.thesis ||
                                                                                  currentIdea?.summary;
                                                          
                                                          // Only fall back to item-level if no per-idea content
                                                          const itemLevelAiSummary = item.wwnbt_analysis?.thesis_summary || item.one_liner_thesis;
                                                          
                                                          const aiSummary2 = perIdeaAiSummary || itemLevelAiSummary || '';
                                                          const hasAiContent2 = !!aiSummary2;
                                                          
                                                          // View mode for this schedule item
                                                          const viewModeKey2 = `${itemKey}-${currentIndex}`;
                                                          const currentViewMode2 = scheduleCardViewMode[viewModeKey2] || 'ai';
                                                          const isShowingAi2 = currentViewMode2 === 'ai' && hasAiContent2;
                                                          
                                                          // Determine what to display
                                                          const displayText2 = isShowingAi2 ? aiSummary2 : (originalContent2 || currentIdea?.summary || item.summary || item.title);
                                                          const authorName = currentIdea?.author || item.author;
                                                          const contentKey2 = `schedule-${itemKey}-${currentIndex}`;
                                                          const isLong2 = (displayText2 || '').length > 280;
                                                          const isContentExpanded2 = scheduleContentExpanded[contentKey2];
                                                          const truncatedContent2 = isContentExpanded2 ? displayText2 : (displayText2 || '').substring(0, 280);
                                                          
                                                          // Get idea ID for linking back
                                                          const ideaId2 = currentIdea?.id || item.idea_id;
                                                          
                                                          return (
                                                            <div 
                                                              className={`mb-3 p-3 rounded-lg cursor-pointer ${isDarkMode ? 'bg-[#1a2235] border border-slate-600 hover:bg-[#1e2940]' : 'bg-white border border-slate-200 hover:bg-slate-50'}`}
                                                              onClick={() => {
                                                                // Toggle between summary and full post view
                                                                const newMode = isShowingAi2 ? 'tweet' : 'ai';
                                                                setScheduleCardViewMode(prev => ({ ...prev, [viewModeKey2]: newMode }));
                                                              }}
                                                            >
                                                              {/* Navigation header for multiple ideas */}
                                                              {totalIdeas > 1 && (
                                                                <div className="flex items-center justify-between mb-2">
                                                                  <button
                                                                    onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      e.preventDefault();
                                                                      setScheduleIdeaIndex(prev => ({
                                                                        ...prev,
                                                                        [itemKey]: (currentIndex - 1 + totalIdeas) % totalIdeas
                                                                      }));
                                                                    }}
                                                                    className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                                                                  >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                                                                    </svg>
                                                                  </button>
                                                                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                    {currentIndex + 1} of {totalIdeas} ideas
                                                                  </span>
                                                                  <button
                                                                    onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      e.preventDefault();
                                                                      setScheduleIdeaIndex(prev => ({
                                                                        ...prev,
                                                                        [itemKey]: (currentIndex + 1) % totalIdeas
                                                                      }));
                                                                    }}
                                                                    className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                                                                  >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                                                    </svg>
                                                                  </button>
                                                                </div>
                                                              )}
                                                              
                                                              {truncatedContent2 ? (
                                                                <div>
                                                                  {/* Author Profile Header - compact mobile layout like Ideas tab */}
                                                                  {(() => {
                                                                    const twitterData = currentIdea?.twitterMetadata || currentIdea?.metadata?.twitter_content?.tweet_data || item.twitterMetadata || item.metadata?.twitter_content?.tweet_data || null;
                                                                    const authorDisplayName = twitterData?.name || twitterData?.author?.name || currentIdea?.tweet_author_name || currentIdea?.from_name || item.from_name;
                                                                    const username = twitterData?.username || twitterData?.author?.username || authorName?.replace('@', '') || currentIdea?.author || currentIdea?.tweet_author?.replace('@', '');
                                                                    const profileImage = twitterData?.profileImageUrl || twitterData?.profile_image_url || twitterData?.author?.profile_image_url || currentIdea?.profile_image || currentIdea?.author_profile_image || item.profile_image;
                                                                    const tweetUrl = twitterData?.url || twitterData?.tweet_url || currentIdea?.url || currentIdea?.tweet_url || item.url;
                                                                    
                                                                    return (
                                                                      <div className="space-y-0.5 mb-3">
                                                                        {/* Row 1: X logo + Profile pic + @handle + Toggle */}
                                                                        <div className="flex items-center gap-1.5">
                                                                          {/* X logo */}
                                                                          {tweetUrl && (
                                                                            <a 
                                                                              href={tweetUrl}
                                                                              target="_blank"
                                                                              rel="noopener noreferrer"
                                                                              className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                                                                              onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                                              </svg>
                                                                            </a>
                                                                          )}
                                                                          {/* Profile Image */}
                                                                          {profileImage ? (
                                                                            <img 
                                                                              src={profileImage} 
                                                                              alt={authorDisplayName || username}
                                                                              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                                                              onError={(e) => { e.target.style.display = 'none'; }}
                                                                            />
                                                                          ) : (
                                                                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                                                              <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                                                {(username || 'U')[0].toUpperCase()}
                                                                              </span>
                                                                            </div>
                                                                          )}
                                                                          {/* Handle */}
                                                                          {username && (
                                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                              @{username}
                                                                            </span>
                                                                          )}
                                                                          {/* Toggle - pushed to right */}
                                                                          {hasAiContent2 && originalContent2 && (
                                                                            <div className="flex items-center gap-1.5 ml-auto">
                                                                              <button
                                                                                onClick={(e) => {
                                                                                  e.stopPropagation();
                                                                                  e.preventDefault();
                                                                                  const newMode = isShowingAi2 ? 'tweet' : 'ai';
                                                                                  setScheduleCardViewMode(prev => ({ ...prev, [viewModeKey2]: newMode }));
                                                                                }}
                                                                                className={`relative w-8 h-4 rounded-full cursor-pointer transition-colors duration-200 bg-black`}
                                                                                data-testid="folder-grok-toggle-2"
                                                                              >
                                                                                <div 
                                                                                  className={`absolute top-0.5 w-3 h-3 transition-all duration-200 ease-in-out ${
                                                                                    !isShowingAi2 ? 'left-[18px]' : 'left-0.5'
                                                                                  }`}
                                                                                >
                                                                                  {/* Official Grok colors: always black/white */}
                                                                                  <svg className="w-3 h-3" viewBox="0 0 512 509.641" fill="#000000">
                                                                                    <path d="M115.612 0h280.776C459.975 0 512 52.026 512 115.612v278.416c0 63.587-52.025 115.613-115.612 115.613H115.612C52.026 509.641 0 457.615 0 394.028V115.612C0 52.026 52.026 0 115.612 0z"/>
                                                                                    <path fill="#ffffff" d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
                                                                                  </svg>
                                                                                </div>
                                                                              </button>
                                                                              <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                                                                {!isShowingAi2 ? 'Original' : 'Preview'}
                                                                              </span>
                                                                            </div>
                                                                          )}
                                                                        </div>
                                                                        {/* Row 2: Author name */}
                                                                        <div className="flex items-center justify-between" style={{ marginLeft: tweetUrl ? '22px' : '0px' }}>
                                                                          {authorDisplayName && (
                                                                            <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                                              {authorDisplayName}
                                                                            </span>
                                                                          )}
                                                                        </div>
                                                                        {/* Full post mode: Date with time, metrics, and View on X */}
                                                                        {!isShowingAi2 && (
                                                                          <div style={{ marginLeft: tweetUrl ? '22px' : '0px' }} className="space-y-1">
                                                                            {/* Date and time */}
                                                                            {(twitterData?.created_at || twitterData?.createdAt || currentIdea?.created_at) && (
                                                                              <div className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                                                {new Date(twitterData?.created_at || twitterData?.createdAt || currentIdea?.created_at).toLocaleString('en-US', { 
                                                                                  month: 'short', 
                                                                                  day: 'numeric', 
                                                                                  year: 'numeric',
                                                                                  hour: 'numeric',
                                                                                  minute: '2-digit',
                                                                                  hour12: true
                                                                                })}
                                                                              </div>
                                                                            )}
                                                                            {/* Metrics with icons */}
                                                                            {(() => {
                                                                              const metrics = twitterData?.metrics || twitterData?.public_metrics || currentIdea?.twitterMetadata?.metrics || currentIdea?.twitterMetadata?.public_metrics;
                                                                              if (!metrics) return null;
                                                                              const hasAnyMetrics = (metrics.reply_count > 0 || metrics.replyCount > 0 || 
                                                                                                     metrics.retweet_count > 0 || metrics.retweetCount > 0 || 
                                                                                                     metrics.like_count > 0 || metrics.likeCount > 0);
                                                                              if (!hasAnyMetrics) return null;
                                                                              return (
                                                                                <div className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                                  {(metrics.reply_count > 0 || metrics.replyCount > 0) && (
                                                                                    <span className="flex items-center gap-1">
                                                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                                                                      </svg>
                                                                                      {(metrics.reply_count || metrics.replyCount || 0).toLocaleString()}
                                                                                    </span>
                                                                                  )}
                                                                                  {(metrics.retweet_count > 0 || metrics.retweetCount > 0) && (
                                                                                    <span className="flex items-center gap-1">
                                                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                                                                      </svg>
                                                                                      {(metrics.retweet_count || metrics.retweetCount || 0).toLocaleString()}
                                                                                    </span>
                                                                                  )}
                                                                                  {(metrics.like_count > 0 || metrics.likeCount > 0) && (
                                                                                    <span className="flex items-center gap-1">
                                                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                                                                      </svg>
                                                                                      {(metrics.like_count || metrics.likeCount || 0).toLocaleString()}
                                                                                    </span>
                                                                                  )}
                                                                                </div>
                                                                              );
                                                                            })()}
                                                                            {/* View original post on X */}
                                                                            {tweetUrl && (
                                                                              <a 
                                                                                href={tweetUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                              >
                                                                                View original post on X
                                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                                                </svg>
                                                                              </a>
                                                                            )}
                                                                          </div>
                                                                        )}
                                                                      </div>
                                                                    );
                                                                  })()}
                                                                  
                                                                  {/* Content with clickable links */}
                                                                  <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                                    {renderTextWithLinks(decodeHtmlEntities(truncatedContent2), isDarkMode)}{isLong2 && !isContentExpanded2 && '...'}
                                                                  </div>
                                                                  {isLong2 && !isContentExpanded2 && (
                                                                    <button
                                                                      onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setScheduleContentExpanded(prev => ({ ...prev, [contentKey2]: true }));
                                                                      }}
                                                                      className={`mt-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                                                                    >
                                                                      more...
                                                                    </button>
                                                                  )}
                                                                  
                                                                </div>
                                                              ) : (
                                                                <p className={`text-sm italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                                  No content available for this idea.
                                                                </p>
                                                              )}
                                                              
                                                              {/* Dot indicators for multiple ideas */}
                                                              {totalIdeas > 1 && (
                                                                <div className="flex justify-center gap-1.5 mt-3">
                                                                  {Array.from({ length: totalIdeas }).map((_, idx) => (
                                                                    <button
                                                                      key={idx}
                                                                      onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        setScheduleIdeaIndex(prev => ({ ...prev, [itemKey]: idx }));
                                                                      }}
                                                                      className={`w-2 h-2 rounded-full transition-colors ${
                                                                        idx === currentIndex
                                                                          ? (isDarkMode ? 'bg-blue-400' : 'bg-blue-600')
                                                                          : (isDarkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-300 hover:bg-slate-400')
                                                                      }`}
                                                                    />
                                                                  ))}
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        })()}
                                                        
                                                        {/* Button Order (left to right): Recycle → File → Ticker → Share → Open */}
                                                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                                                          {/* Recycle Button */}
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              // Open reschedule modal with options
                                                              openRescheduleModal(item);
                                                            }}
                                                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                                                          >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                                            </svg>
                                                            <span className="text-[10px] sm:text-xs">Recycle</span>
                                                          </button>
                                                          {/* File Button */}
                                                          <button
                                                            onClick={(e) => { 
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              // Open Briefs tab with this ticker's research documents
                                                              setFilesSearchQuery(item.ticker);
                                                              setActiveTab('files');
                                                            }}
                                                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                          >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                            </svg>
                                                            <span className="text-[10px] sm:text-xs">File</span>
                                                          </button>
                                                          {/* Tickers Button */}
                                                          <button
                                                            onClick={(e) => { 
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              // Use the addToWatchlist function to show Auto/Manual popup
                                                              addToWatchlist(item.ticker, null);
                                                            }}
                                                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                                          >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                            </svg>
                                                            <span className="text-[10px] sm:text-xs">Tickers</span>
                                                          </button>
                                                          {/* Share Button */}
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              // Find the bookmark - try multiple ways
                                                              let tickerBookmark = null;
                                                              if (item.idea_id) {
                                                                tickerBookmark = bookmarks.find(b => b.id === item.idea_id);
                                                              }
                                                              if (!tickerBookmark && item.idea_ids && item.idea_ids.length > 0) {
                                                                tickerBookmark = bookmarks.find(b => item.idea_ids.includes(b.id));
                                                              }
                                                              if (!tickerBookmark) {
                                                                const tickerUpper = item.ticker?.toUpperCase();
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.tickers?.some(t => t?.toUpperCase() === tickerUpper || t?.toUpperCase() === `$${tickerUpper}`) ||
                                                      b.tickers?.some(t => t?.toUpperCase().replace('$', '') === tickerUpper) ||
                                                                  b.primary_ticker?.toUpperCase() === tickerUpper ||
                                                      b.primary_ticker?.toUpperCase() === `$${tickerUpper}` ||
                                                      b.primary_ticker?.toUpperCase().replace('$', '') === tickerUpper
                                                                );
                                                              }
                                                              if (!tickerBookmark) {
                                                                const tickerUpper = item.ticker?.toUpperCase();
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.title?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.title?.toUpperCase().includes(tickerUpper) ||
                                                                  b.content?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.content?.toUpperCase().includes(tickerUpper)
                                                                );
                                                              }
                                                              if (tickerBookmark) {
                                                                setSelectedTickerForScheduling({
                                                                  ticker: tickerBookmark.tickers?.[0] || item.ticker,
                                                                  company_name: tickerBookmark.company_name || tickerBookmark.title,
                                                                  ideaId: tickerBookmark.id,
                                                                  tweetId: tickerBookmark.twitterMetadata?.tweet_id || tickerBookmark.global_tweet_id,
                                                                  shareType: 'idea'
                                                                });
                                                                setShowShareModal(true);
                                                              } else {
                                                                showNotification(`No idea found for ${item.ticker} to share. Save the original idea first.`, 'info');
                                                              }
                                                            }}
                                                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                                          >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                            </svg>
                                                            <span className="text-[10px] sm:text-xs">Share</span>
                                                          </button>
                                                          {/* Original Button - Navigate to idea in Ideas tab */}
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              const itemKey = `folder-${item.ticker}-${item.scheduled_date}`;
                                                              const ideasArray = item.ideas || [];
                                                              const currentIndex = scheduleIdeaIndex[itemKey] || 0;
                                                              const currentIdea = ideasArray[currentIndex];
                                                              const ideaId = currentIdea?.id || item.idea_id;
                                                              
                                                              if (ideaId) {
                                                                // Navigate to Ideas tab and scroll to the idea card
                                                                setScrollToIdeaTarget({ ticker: item.ticker, ideaId: ideaId });
                                                                setActiveTab('dashboard');
                                                              } else {
                                                                showNotification(`No idea card found for ${item.ticker}`, 'info');
                                                              }
                                                            }}
                                                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                                          >
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                            </svg>
                                                            <span className="text-[10px] sm:text-xs">Open</span>
                                                          </button>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}

                            {/* Desktop Calendar View - Only show when calendar toggle is ON, not sorting by ticker and not 12month view */}
                            {scheduleViewMode !== '12month' && !isTickerSort && showCalendarView && (
                              <div className="hidden sm:block space-y-4">
                                {scheduledResearchLoading ? (
                                  <div className="flex items-center justify-center py-12">
                                    <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                ) : (
                                  <>
                                {/* Week day headers */}
                                <div className="grid grid-cols-7 gap-2 mb-2">
                                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className={`text-center text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                      {day}
                                    </div>
                                  ))}
                                </div>

                                {/* Calendar grid */}
                                {weeks.map((week, weekIdx) => (
                                  <div key={weekIdx} className="grid grid-cols-7 gap-2">
                                    {week.map((date, dayIdx) => {
                                      if (!date) {
                                        return <div key={dayIdx} className="min-h-[90px]"></div>;
                                      }
                                      const items = scheduleByDate[date] || [];
                                      // Parse date in local timezone
                                      const [year, month, day] = date.split('-').map(Number);
                                      const dateObj = new Date(year, month - 1, day);
                                      const isToday = date === todayStr;
                                      const dayNum = dateObj.getDate();
                                      const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });

                                      return (
                                        <div
                                          key={dayIdx}
                                          data-folder-date={date}
                                          className={`min-h-[90px] rounded-lg p-2 transition-all ${
                                            isToday
                                              ? (isDarkMode ? 'bg-[#1a2235] border border-slate-600' : 'bg-white border border-slate-300')
                                              : items.length > 0
                                                ? isDarkMode 
                                                  ? 'bg-slate-800 border border-slate-600' 
                                                  : 'bg-white border border-slate-300'
                                                : isDarkMode 
                                                  ? 'bg-slate-800 border border-slate-700' 
                                                  : 'bg-slate-50 border border-slate-200'
                                          }`}
                                        >
                                          <div className={`text-xs font-semibold mb-1.5 ${
                                            isToday 
                                              ? 'text-orange-500' 
                                              : items.length > 0 
                                                ? isDarkMode ? 'text-slate-300' : 'text-slate-700'
                                                : isDarkMode ? 'text-slate-500' : 'text-slate-400'
                                          }`}>
                                            {dayNum === 1 || dayIdx === 0 ? `${monthName} ${dayNum}` : dayNum}
                                            {isToday && <span className="ml-1 text-xs text-orange-500">(Today)</span>}
                                          </div>
                                          {items.length > 0 && (
                                            <div className="space-y-1">
                                              {items.slice(0, 3).map((item, i) => {
                                                const itemKey = `cal-${item.ticker}-${date}`;
                                                const isExpanded = expandedScheduleItem === itemKey;
                                                const isCompleted = completedScheduleItems.has(itemKey);
                                                return (
                                                  <div key={i} className={`relative schedule-item-dropdown flex items-center gap-1 ${workspaceHighlightedTicker && item.ticker?.toUpperCase() === workspaceHighlightedTicker ? 'workspace-highlight rounded' : ''}`} data-schedule-ticker={item.ticker?.toUpperCase()}>
                                                    {/* Folder icon - orange open when expanded, grey closed otherwise */}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleScheduleItemComplete(itemKey, item.ticker, date);
                                                      }}
                                                      className={`flex-shrink-0 w-4 h-4 flex items-center justify-center transition-all`}
                                                    >
                                                      {isExpanded ? (
                                                        <FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" style={{ color: '#F58346' }} />
                                                      ) : (
                                                        <FontAwesomeIcon icon={faFolder} className="h-3.5 w-3.5 text-slate-400" />
                                                      )}
                                                    </button>
                                                    {/* Content */}
                                                    <div
                                                      onClick={(e) => { e.stopPropagation(); setExpandedScheduleItem(isExpanded ? null : itemKey); }}
                                                      className={`cursor-pointer text-xs px-1.5 py-1 rounded transition-all flex-1 min-w-0 ${
                                                        isCompleted
                                                          ? isDarkMode
                                                            ? 'bg-green-900/30 text-green-400 line-through opacity-70'
                                                            : 'bg-green-50 text-green-700 line-through opacity-70 border border-green-200'
                                                          : isDarkMode 
                                                            ? 'hover:bg-slate-700/50' 
                                                            : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
                                                      }`}
                                                      title={`Click for options - ${item.ticker}${item.company_name ? ` - ${item.company_name}` : ''}`}
                                                    >
                                                      <div className={`font-semibold ${isCompleted ? '' : isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>${item.ticker}</div>
                                                      {item.company_name && (
                                                        <div className={`text-[9px] truncate font-normal leading-tight ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.company_name}</div>
                                                      )}
                                                    </div>
                                                    {/* Popup Actions */}
                                                    {isExpanded && (
                                                      <div className={`schedule-item-dropdown absolute z-50 left-0 top-full mt-1 p-2 rounded-lg shadow-lg min-w-[200px] ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-slate-200'}`}>
                                                        <div className={`text-xs font-semibold mb-2 pb-1 border-b ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-slate-300 text-slate-700'}`}>
                                                          ${item.ticker} {item.company_name && <span className="font-normal opacity-70">- {item.company_name}</span>}
                                                        </div>
                                                        <div className="space-y-1">
                                                          <button
                                                            onClick={(e) => { 
                                                              e.stopPropagation();
                                                              // Find the bookmark - try multiple ways
                                                              let tickerBookmark = null;
                                                              const tickerUpper = item.ticker?.toUpperCase();
                                                              if (item.idea_id) {
                                                                tickerBookmark = bookmarks.find(b => b.id === item.idea_id);
                                                              }
                                                              if (!tickerBookmark && item.idea_ids && item.idea_ids.length > 0) {
                                                                tickerBookmark = bookmarks.find(b => item.idea_ids.includes(b.id));
                                                              }
                                                              if (!tickerBookmark) {
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.tickers?.some(t => t?.toUpperCase() === tickerUpper || t?.toUpperCase() === `$${tickerUpper}`) ||
                                                    b.tickers?.some(t => t?.toUpperCase().replace('$', '') === tickerUpper) ||
                                                                  b.primary_ticker?.toUpperCase() === tickerUpper ||
                                                    b.primary_ticker?.toUpperCase() === `$${tickerUpper}` ||
                                                    b.primary_ticker?.toUpperCase().replace('$', '') === tickerUpper
                                                                );
                                                              }
                                                              if (!tickerBookmark) {
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.title?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.title?.toUpperCase().includes(tickerUpper) ||
                                                                  b.content?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.content?.toUpperCase().includes(tickerUpper)
                                                                );
                                                              }
                                                              if (tickerBookmark) {
                                                                setSelectedOriginalContent(tickerBookmark);
                                                                setShowOriginalContentModal(true);
                                                              } else {
                                                                setActiveTab('dashboard');
                                                                setSearchQuery(`$${item.ticker}`);
                                                                showNotification(`Searching for ${item.ticker} ideas...`, 'info');
                                                              }
                                                            }}
                                                            className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isDarkMode ? 'hover:bg-[#141c2e] text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
                                                          >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                            </svg>
                                                            Original
                                                          </button>
                                                          <button
                                                            onClick={(e) => { 
                                                              e.stopPropagation(); 
                                                              setSelectedTickerForScheduling({ ticker: item.ticker, bookmarkId: null, content: null });
                                                              checkSECAvailability(item.ticker);
                                                              setShowDocumentModal(true);
                                                            }}
                                                            className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isDarkMode ? 'hover:bg-[#141c2e] text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}
                                                          >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                                            </svg>
                                                            Dossier
                                                          </button>
                                                          <button
                                                            onClick={async (e) => { 
                                                              e.stopPropagation();
                                                              try {
                                                                const response = await fetch(`${API_BASE_URL}/api/watchlists/add-ticker`, {
                                                                  method: 'POST',
                                                                  headers: { 'Content-Type': 'application/json' },
                                                                  body: JSON.stringify({
                                                                    user_id: user?.id || 'demo_user',
                                                                    ticker: item.ticker
                                                                  })
                                                                });
                                                                if (response.ok) {
                                                                  showNotification(`Added $${item.ticker} to your watchlist!`, 'success');
                                                                } else {
                                                                  showNotification(`Failed to add ${item.ticker} to watchlist`, 'error');
                                                                }
                                                              } catch (error) {
                                                                console.error('Error adding to watchlist:', error);
                                                                showNotification(`Error adding ${item.ticker} to watchlist`, 'error');
                                                              }
                                                            }}
                                                            className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isDarkMode ? 'hover:bg-[#141c2e] text-green-400' : 'hover:bg-green-50 text-green-600'}`}
                                                          >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                                            </svg>
                                                            Watchlist
                                                          </button>
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              // Find the bookmark - try multiple ways
                                                              let tickerBookmark = null;
                                                              const tickerUpper = item.ticker?.toUpperCase();
                                                              if (item.idea_id) {
                                                                tickerBookmark = bookmarks.find(b => b.id === item.idea_id);
                                                              }
                                                              if (!tickerBookmark && item.idea_ids && item.idea_ids.length > 0) {
                                                                tickerBookmark = bookmarks.find(b => item.idea_ids.includes(b.id));
                                                              }
                                                              if (!tickerBookmark) {
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.tickers?.some(t => t?.toUpperCase() === tickerUpper || t?.toUpperCase() === `$${tickerUpper}`) ||
                                                    b.tickers?.some(t => t?.toUpperCase().replace('$', '') === tickerUpper) ||
                                                                  b.primary_ticker?.toUpperCase() === tickerUpper ||
                                                    b.primary_ticker?.toUpperCase() === `$${tickerUpper}` ||
                                                    b.primary_ticker?.toUpperCase().replace('$', '') === tickerUpper
                                                                );
                                                              }
                                                              if (!tickerBookmark) {
                                                                tickerBookmark = bookmarks.find(b => 
                                                                  b.title?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.title?.toUpperCase().includes(tickerUpper) ||
                                                                  b.content?.toUpperCase().includes(`$${tickerUpper}`) ||
                                                                  b.content?.toUpperCase().includes(tickerUpper)
                                                                );
                                                              }
                                                              if (tickerBookmark) {
                                                                setSelectedTickerForScheduling({
                                                                  ticker: tickerBookmark.tickers?.[0] || item.ticker,
                                                                  company_name: tickerBookmark.company_name || tickerBookmark.title,
                                                                  ideaId: tickerBookmark.id,
                                                                  tweetId: tickerBookmark.twitterMetadata?.tweet_id || tickerBookmark.global_tweet_id,
                                                                  shareType: 'idea'
                                                                });
                                                                setShowShareModal(true);
                                                              } else {
                                                                showNotification(`No idea found for ${item.ticker} to share. Save the original idea first.`, 'info');
                                                              }
                                                            }}
                                                            className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isDarkMode ? 'hover:bg-[#141c2e] text-orange-400' : 'hover:bg-orange-50 text-orange-600'}`}
                                                          >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                            </svg>
                                                            Share
                                                          </button>
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              if (item.source === 'manual' && item.id) {
                                                                cancelScheduledResearch(item.id, item.ticker);
                                                              } else {
                                                                showNotification('Cannot delete auto-scheduled items', 'info');
                                                              }
                                                            }}
                                                            className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isDarkMode ? 'hover:bg-[#141c2e] text-red-400' : 'hover:bg-red-50 text-red-600'}`}
                                                          >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                            </svg>
                                                            Delete
                                                          </button>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                              {items.length > 3 && (
                                                <div 
                                                  onClick={() => {
                                                    // Open modal with all tickers from this day
                                                    const dayTickers = items.map(i => i.ticker);
                                                    openWatchlistModal(dayTickers, `Ideas for ${dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
                                                  }}
                                                  className={`text-xs font-medium cursor-pointer hover:underline ${isToday ? 'text-white/80' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                                >
                                                  +{items.length - 3} more
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}

                                {/* Legend */}
                                <div className={`flex items-center gap-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                  <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faFolderOpen} className="h-3 w-3" style={{ color: '#F58346' }} />
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Today</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faFolder} className="h-3 w-3 text-slate-400" />
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Scheduled</span>
                                  </div>
                                </div>
                                </>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* ========== SETTINGS TAB CONTENT ========== */}
                  {scheduleTabView === 'settings' && (
                    <div className="space-y-6">
                      
                      {/* 1. FOLDER SCHEDULE CARD - Auto/Manual toggle */}
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Folder Schedule
                            </h4>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                            Schedule ideas to folders automatically or manually
                          </p>
                          
                          {/* Auto Schedule Toggle Card */}
                          <div 
                            className={`p-4 rounded-lg cursor-pointer transition-all ${
                              dossierSettings.enabled 
                                ? isDarkMode ? 'bg-blue-900/30 border border-blue-500/50' : 'bg-blue-50 border border-blue-200'
                                : isDarkMode ? 'bg-[#1a2235] border border-slate-700 hover:border-slate-600' : 'bg-slate-50 border border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={async () => {
                              const newEnabled = !dossierSettings.enabled;
                              const newSettings = { ...dossierSettings, enabled: newEnabled };
                              setDossierSettings(newSettings);
                              await saveDossierSettings(newSettings, false);
                              if (newEnabled) {
                                await generateResearchSchedule();
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  dossierSettings.enabled 
                                    ? 'bg-blue-500' 
                                    : isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                                }`}>
                                  <svg className={`w-5 h-5 ${dossierSettings.enabled ? 'text-white' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                  </svg>
                                </div>
                                <div>
                                  <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                    Auto Schedule
                                  </div>
                                  <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {dossierSettings.enabled 
                                      ? 'All ideas scheduled automatically' 
                                      : 'Schedule all ideas across 31 days'}
                                  </div>
                                </div>
                              </div>
                              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                dossierSettings.enabled 
                                  ? 'bg-blue-500' 
                                  : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                              }`}>
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                    dossierSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Schedule Summary - when auto-schedule is on */}
                          {dossierSettings.enabled && researchSchedule.length > 0 && (
                            <div className="mt-4">
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{totalPendingIdeas} ideas</span>
                                {' · '}{(() => {
                                  // Only count pending/scheduled items for the per-day calculation
                                  const byDate = {};
                                  researchSchedule.filter(item => item.status === 'scheduled' || !item.status).forEach(item => {
                                    const date = item.scheduled_date;
                                    byDate[date] = (byDate[date] || 0) + 1;
                                  });
                                  const counts = Object.values(byDate);
                                  if (counts.length === 0) return '0';
                                  const min = Math.min(...counts);
                                  const max = Math.max(...counts);
                                  return min === max ? `${min}` : `${min}-${max}`;
                                })()}/day over 31 days
                              </p>
                              <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                                Starting {new Date(researchSchedule[0]?.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                          )}
                          
                          {/* Compact Calendar Sync Section */}
                          <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Sync to Calendar</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(`${API_BASE_URL}/api/research-schedule/${user?.id || user?.email}/export.ics?days=30`);
                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'tickler-schedule.ics';
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                        showNotification('Calendar file downloaded!', 'success');
                                      }
                                    } catch (error) {
                                      showNotification('Error exporting calendar', 'error');
                                    }
                                  }}
                                  title="Download .ics file"
                                  className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                                >
                                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                  </svg>
                                </button>
                                <a
                                  href={`https://calendar.google.com/calendar/r?cid=webcal://${window.location.host}/api/research-schedule/${user?.id || user?.email}/export.ics`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Add to Google Calendar"
                                  className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                                >
                                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/>
                                    <path fill="#EA4335" d="M9 14h2v2H9zm4 0h2v2h-2zm-4-3h2v2H9zm4 0h2v2h-2z"/>
                                  </svg>
                                </a>
                                <a
                                  href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(`${window.location.origin}/api/research-schedule/${user?.id || user?.email}/export.ics`)}&name=Tickler%20Research`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Add to Outlook Calendar"
                                  className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                                >
                                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="#0078D4">
                                    <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
                                  </svg>
                                </a>
                                <a
                                  href={`webcal://${window.location.host}/api/research-schedule/${user?.id || user?.email}/export.ics`}
                                  title="Add to Apple Calendar"
                                  className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                                >
                                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill={isDarkMode ? '#ffffff' : '#333333'}>
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                  </svg>
                                </a>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 2. FOLDER EMAILS CARD */}
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Folder Emails
                            </h4>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                            Receive research from your daily tickler folder
                          </p>
                          
                          {/* Folder Emails Toggle */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                              </svg>
                              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                Folder Emails
                              </span>
                            </div>
                            <button
                              onClick={async () => {
                                const newFrequency = emailSettings.folder_email_frequency === 'off' ? 'daily' : 'off';
                                const newSettings = { ...emailSettings, folder_email_frequency: newFrequency, folder_email_enabled: newFrequency !== 'off' };
                                setEmailSettings(newSettings);
                                await saveEmailSettings(newSettings);
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                                emailSettings.folder_email_frequency !== 'off'
                                  ? 'bg-green-500' 
                                  : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                              }`}
                              title={emailSettings.folder_email_frequency !== 'off' ? 'Pause emails' : 'Resume emails'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                  emailSettings.folder_email_frequency !== 'off' ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                          {emailSettings.folder_email_frequency !== 'off' ? (
                            <div className="space-y-1">
                              <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ✓ {(emailSettings.folder_email_frequency || 'daily').charAt(0).toUpperCase() + (emailSettings.folder_email_frequency || 'daily').slice(1)}: {(() => {
                                  const time = emailSettings.folder_email_time || '08:00';
                                  const [hourStr, minute] = time.split(':');
                                  const hour = parseInt(hourStr);
                                  const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                  return `${displayHour}:${minute || '00'} ${hour >= 12 ? 'PM' : 'AM'}`;
                                })()}
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ✓ Documents: {(emailSettings.folder_email_docs || []).length > 0 
                                  ? (emailSettings.folder_email_docs || []).join(', ')
                                  : 'None selected'}
                              </p>
                            </div>
                          ) : (
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Emails paused
                            </p>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                            <button
                              onClick={() => setShowDossierConfigModal(true)}
                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                isDarkMode 
                                  ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10'
                                  : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                              }`}
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              </svg>
                              Configure
                            </button>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 3. DIGEST EMAILS CARD */}
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Digest Emails
                            </h4>
                          </div>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                            Receive a summary of your new investment ideas
                          </p>
                          
                          {/* Digest Emails Toggle */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                              </svg>
                              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                Digest Emails
                              </span>
                            </div>
                            {/* Pill Toggle */}
                            <button
                              onClick={async () => {
                                const isCurrentlyEnabled = emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled;
                                const newSettings = {
                                  ...emailSettings,
                                  weekly_digest_enabled: !isCurrentlyEnabled,
                                  daily_digest_enabled: false
                                };
                                setEmailSettings(newSettings);
                                await saveEmailSettings(newSettings);
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                                (emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled)
                                  ? 'bg-green-500' 
                                  : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                              }`}
                              title={(emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? 'Pause emails' : 'Resume emails'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                  (emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                          {(emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? (
                            <div className="space-y-1">
                              {emailSettings.weekly_digest_enabled && (
                                <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  ✓ Weekly: {emailSettings.weekly_digest_day || 'Sunday'} at {(() => {
                                    const time = emailSettings.weekly_digest_time || '09:00';
                                    const [hourStr, minute] = time.split(':');
                                    const hour = parseInt(hourStr);
                                    const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                    return `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                  })()}
                                </p>
                              )}
                              {emailSettings.daily_digest_enabled && (
                                <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  ✓ Daily: {(() => {
                                    const time = emailSettings.daily_digest_time || emailSettings.digest_time || '08:00';
                                    const [hourStr, minute] = time.split(':');
                                    const hour = parseInt(hourStr);
                                    const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                    return `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                  })()}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Emails paused
                            </p>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                            <button
                              onClick={() => setShowEmailConfigModal(true)}
                              className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                isDarkMode 
                                  ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10'
                                  : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                              }`}
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              </svg>
                              Configure
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
      </div>
    );
  }


  return (
    <div className={`${showWorkspace ? 'h-screen flex flex-col overflow-hidden' : 'min-h-screen flex flex-col pb-20 sm:pb-0'} ${isDarkMode ? 'bg-[#0f1419]' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
      {/* Custom Notification System */}
      <NotificationBox />
      
      {/* Rich Import Progress Indicator with Category Breakdown */}
      <ImportProgressIndicator
        importProgress={importProgress}
        importing={importing}
        onCancel={handleCancelImport}
        cancelling={cancelling}
        isDarkMode={isDarkMode}
      />
      
      {/* Trial Banner */}
      <TrialExpirationBanner 
        subscriptionStatus={subscriptionStatus}
        onUpgradeClick={() => setShowPricingModal(true)}
        isDarkMode={isDarkMode}
      />
      
      {/* Pricing Modal */}
      <SubscriptionPricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        userEmail={user?.email}
        userId={user?.id || user?.user_id}
        apiBaseUrl={API_BASE_URL}
      />
      
      {/* Post-Import Subscribe Modal - shows after free import completes */}
      {showPostImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="text-center">
              {/* Success Icon */}
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <svg className={`w-8 h-8 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Your bookmarks are importing!
              </h2>
              
              <p className={`mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                You've seen what Tickler can do. Subscribe to sync new bookmarks automatically every night.
              </p>
              
              {/* Benefits */}
              <div className={`text-left mb-6 p-4 rounded-xl ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Automatic nightly bookmark sync</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Up to 1,000 saved posts</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Daily digest emails with insights</span>
                  </li>
                </ul>
              </div>
              
              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowPostImportModal(false);
                    setShowPricingModal(true);
                  }}
                  className="w-full py-3 px-4 rounded-xl font-semibold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white transition-all"
                >
                  Subscribe - $49/month
                </button>
                <button
                  onClick={() => setShowPostImportModal(false)}
                  className={`w-full py-3 px-4 rounded-xl font-medium transition-all ${
                    isDarkMode 
                      ? 'text-slate-400 hover:text-white hover:bg-slate-700' 
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  I'll explore first
                </button>
              </div>
              
              <p className={`mt-4 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Your first import (50 bookmarks) is free
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      {/* User Profile - Remove from top, will move to Settings */}

      {/* Main Header - Hidden in workspace mode */}
      {!showWorkspace && (
      <header className={`bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <a
                ref={folderIconRef}
                href="#"
                draggable="true"
                className="cursor-move hover:opacity-80 transition-opacity no-underline relative"
                onClick={(e) => e.preventDefault()}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'link';
                  e.dataTransfer.setData('text/uri-list', bookmarkletCode);
                  e.dataTransfer.setData('text/plain', '📁 Save to Tickler');
                  // Create a custom drag ghost
                  const dragGhost = document.createElement('div');
                  dragGhost.textContent = '📁 Save to Tickler';
                  dragGhost.style.position = 'absolute';
                  dragGhost.style.top = '-1000px';
                  dragGhost.style.fontSize = '14px';
                  dragGhost.style.padding = '8px 12px';
                  dragGhost.style.background = 'white';
                  dragGhost.style.border = '1px solid #ccc';
                  dragGhost.style.borderRadius = '4px';
                  document.body.appendChild(dragGhost);
                  e.dataTransfer.setDragImage(dragGhost, 0, 0);
                  setTimeout(() => document.body.removeChild(dragGhost), 0);
                }}
                title="Drag to bookmark bar to save pages"
              >
                {/* Hidden text for bookmark name */}
                <span style={{ position: 'absolute', left: '-9999px' }}>📁 Save to Tickler</span>
                <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-1.5 sm:p-2 rounded-lg">
                  <FontAwesomeIcon icon={faFolderOpen} className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: '#F58346' }} />
                </div>
              </a>
              <div 
                className="cursor-pointer"
                onClick={() => setActiveTab('dashboard')}
              >
                <h1 className="text-xl sm:text-2xl font-bold text-[#1a2235]">
                  TICK<span className="text-orange-500">L</span>ER
                </h1>
                <p className="text-xs sm:text-sm text-slate-600 hidden xs:block">IDEAS WORTH VETTING</p>
              </div>
            </div>
            <div className="flex items-center justify-end flex-1 ml-4">
              {/* Shared and Received badges */}
              <div className="flex items-center space-x-1 sm:space-x-3 mr-6 sm:mr-8">
                <Badge 
                  variant="outline" 
                  className="bg-orange-500/20 text-orange-600 border-orange-300 font-semibold text-xs sm:text-sm px-2 sm:px-3 py-1 cursor-pointer hover:bg-orange-500/30 transition-all duration-200"
                  onClick={fetchSharedByMeIdeas}
                >
                  {sharedByMeCount} Shared
                </Badge>
                <div className="relative">
                  <Badge 
                    variant="outline" 
                    className={`font-semibold text-xs sm:text-sm px-2 sm:px-3 py-1 cursor-pointer transition-all duration-300 hover:bg-green-100 ${
                      pendingIdeas.length > 0 
                        ? (hasNewSharedIdeas 
                            ? 'animate-pulse bg-green-50 border-green-300 text-green-600' 
                            : 'bg-green-50 border-green-300 text-green-600')
                        : 'bg-slate-50 text-slate-700 border-slate-200'
                    } ${tourStep === 2 ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
                    onClick={() => {
                      if (pendingIdeas.length > 0) {
                        // Mark all current pending ideas as seen - persist to localStorage
                        const allIds = pendingIdeas.map(idea => idea.id);
                        const newSeenIds = [...new Set([...seenPendingIds, ...allIds])];
                        setSeenPendingIds(newSeenIds);
                        localStorage.setItem('seenPendingIds', JSON.stringify(newSeenIds));
                        setHasNewSharedIdeas(false);
                        setShowReceiveModal(true);
                      }
                    }}
                  >
                    {pendingIdeas.length} Received
                  </Badge>
                
                {/* Tour Step 2: Shared Ideas - Desktop only */}
                {tourStep === 2 && (
                  <div className="hidden sm:block absolute top-full right-0 mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-80 max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                        Step {tourStep} of {TOUR_STEPS}
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          skipTour();
                        }}
                        className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                      >
                        Skip tour
                      </button>
                    </div>
                    <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <span>Shared Ideas</span>
                    </h4>
                    <p className="text-sm text-slate-600 mb-3 break-words">
                      When someone shares a ticker with you, it appears here. Click to review and import.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          nextTourStep();
                        }}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:block absolute -top-2 left-1/2 -translate-x-1/2 sm:right-6 sm:left-auto sm:translate-x-0 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                  </div>
                )}
                </div>
              </div>
              
              {/* Profile Menu - Top right corner */}
              <div className="flex items-center gap-2">
                {/* Workspace Toggle Button */}
                <button
                  onClick={() => setShowWorkspace(true)}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    isDarkMode 
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white border border-slate-600' 
                      : 'bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200 shadow-sm'
                  }`}
                  title="Open Research Workspace"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
                  </svg>
                  Workspace
                </button>
                
                <ProfileMenu
                user={user}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                onLogout={logout}
                onNavigateToAccount={() => {
                  setActiveTab('settings');
                  setSettingsView('account');
                }}
                onRestartTour={() => {
                  localStorage.removeItem('tickler_tour_completed');
                  setTourStep(1);
                }}
              />
              </div>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Subscribe Banner - Persistent reminder for free users who used their import */}
      {showSubscribeBanner && !showWorkspace && (
        <div className={`border-b ${isDarkMode ? 'bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-800/30' : 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  <span className="font-medium">Sync paused</span>
                  <span className="hidden sm:inline"> — Subscribe to import new bookmarks automatically</span>
                </p>
              </div>
              <button
                onClick={() => setShowPricingModal(true)}
                className="flex-shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white transition-all"
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WORKSPACE MODE - 3-column layout */}
      {showWorkspace ? (
        <div className={`flex-1 flex flex-col min-h-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          {/* Workspace Header - Compact with all controls */}
          <div className={`flex-shrink-0 flex items-center justify-between px-4 py-2 border-b ${isDarkMode ? 'bg-obsidian-paper border-white/5' : 'bg-slate-800 border-slate-700'}`}>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowWorkspace(false)}
                className="flex items-center gap-2 text-zinc-400 hover:text-white btn-press transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
                <span className="text-sm font-medium">Exit</span>
              </button>
              <span className="text-white/20">|</span>
              <span className="font-heading font-semibold text-sm text-white tracking-wide">Research Workspace</span>
              <span className="text-xs text-zinc-500">
                Market data by{' '}
                <a 
                  href="https://twelvedata.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-electric-light transition-colors"
                >
                  Twelve Data
                </a>
              </span>
            </div>
            
            {/* Right: Shared, Received, Save Layout, and Profile */}
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline" 
                className="bg-orange-500/20 text-orange-400 border-orange-500/50 font-semibold text-xs px-2 py-0.5 cursor-pointer hover:bg-orange-500/30 transition-all"
                onClick={fetchSharedByMeIdeas}
              >
                {sharedByMeCount} Shared
              </Badge>
              <Badge 
                variant="outline" 
                className={`font-semibold text-xs px-2 py-0.5 cursor-pointer transition-all ${
                  pendingIdeas.length > 0 
                    ? (hasNewSharedIdeas 
                        ? 'animate-pulse bg-green-500/20 border-green-500/50 text-green-400' 
                        : 'bg-green-500/20 border-green-500/50 text-green-400')
                    : 'bg-slate-700 text-slate-400 border-slate-600'
                }`}
                onClick={() => {
                  if (pendingIdeas.length > 0) {
                    const allIds = pendingIdeas.map(idea => idea.id);
                    const newSeenIds = [...new Set([...seenPendingIds, ...allIds])];
                    setSeenPendingIds(newSeenIds);
                    localStorage.setItem('seenPendingIds', JSON.stringify(newSeenIds));
                    setHasNewSharedIdeas(false);
                    setShowReceiveModal(true);
                  }
                }}
              >
                {pendingIdeas.length} Received
              </Badge>
              <button 
                onClick={() => {
                  // TODO: Implement save layout functionality
                  showNotification('Layout saved!', 'success');
                }}
                className="text-xs px-3 py-1.5 rounded bg-electric/10 text-electric-light border border-electric/30 hover:bg-electric/20 hover:border-electric/50 btn-press transition-all duration-200"
              >
                Save Layout
              </button>
              <ProfileMenu
                user={user}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                onLogout={logout}
                onNavigateToAccount={() => {
                  setActiveTab('settings');
                  setSettingsView('account');
                  setShowWorkspace(false);
                }}
                onRestartTour={() => {
                  localStorage.removeItem('tickler_tour_completed');
                  setTourStep(1);
                }}
              />
            </div>
          </div>

          {/* 3-Column Resizable Panels */}
          <PanelGroup key={workspaceResetKey} direction="horizontal" className={`flex-1 min-h-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
            {/* Left Panel */}
            <Panel defaultSize={45} minSize={15} maxSize={60}>
              <div className={`h-full flex flex-col relative min-h-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <div className="flex-1 flex flex-col min-h-0 pb-12 overflow-hidden">
                <Tabs value={workspacePanels.left} className="w-full h-full flex flex-col">
                  <TabsContent value="ideas" forceMount className={`h-full flex flex-col ${workspacePanels.left !== 'ideas' ? 'hidden' : ''}`}>
                        <div className="flex-shrink-0 p-3 bg-slate-800 text-white">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <DashboardViewTabs
                              ideaView={ideaView}
                              setIdeaView={setIdeaView}
                              myIdeasCount={myIdeasCount}
                              receivedCount={receivedIdeasCount}
                            />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative sort-dropdown-container">
                                <button onClick={() => setShowSortDropdown(!showSortDropdown)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showSortDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                                    <div className="py-1">
                                      {[{key:'newest',label:'All (newest)',color:'bg-slate-400'},{key:'fresh',label:'New (<10d)',color:'bg-green-500'},{key:'warm',label:'Recent (10-30d)',color:'bg-orange-500'},{key:'stale',label:'Older (30+d)',color:'bg-red-500'}].map(o=>(
                                        <button key={o.key} onClick={()=>{setSortBy(o.key);setShowSortDropdown(false);}} className={`w-full text-left px-3 py-2 text-sm ${isDarkMode?'hover:bg-slate-700':'hover:bg-gray-100'} ${sortBy===o.key?(isDarkMode?'bg-blue-900/50 text-blue-300':'bg-blue-50 text-blue-700'):(isDarkMode?'text-slate-300':'text-gray-700')}`}>
                                          <div className="flex items-center"><div className={`w-2 h-2 rounded-full ${o.color} mr-2`}></div>{o.label}</div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowSourceDropdown(!showSourceDropdown)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Filter">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                              </button>
                              <button onClick={handleDirectBookmarkImport} disabled={loading} className={`p-2 rounded-lg ${loading?'bg-orange-300':'bg-orange-500 hover:bg-orange-600'} text-white`} title="Import">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div 
                          className={`flex-1 overflow-y-auto p-2 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                          onScroll={(e) => {
                            const el = e.currentTarget;
                            const scrollPosition = el.scrollTop + el.clientHeight;
                            const scrollHeight = el.scrollHeight;
                            const threshold = scrollHeight - 300;
                            
                            if (scrollPosition >= threshold && !loadingMore && hasMoreIdeas) {
                              console.log('[Workspace Scroll] Left panel ideas - loading more ideas', { scrollPosition, threshold, scrollHeight });
                              loadMoreIdeas();
                            }
                          }}
                        >
                          {loading ? (
                            <div className="space-y-2">{[1,2,3].map(i=>(<div key={i} className={`rounded p-3 animate-pulse ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}><div className={`h-4 rounded w-1/3 mb-2 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}></div><div className={`h-3 rounded w-2/3 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}></div></div>))}</div>
                          ) : groupedIdeasForView.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{ideaView==='received'?'No received ideas':'No ideas yet - Import bookmarks to start'}</div>
                          ) : (
                            <div className="space-y-2">
                              {groupedIdeasForView.map((item, groupIndex) => {
                                // Handle thread/multi-idea items
                                if (item.type === 'thread') {
                                  const { parentIdea, childIdeas, childColor = 'green' } = item;
                                  if (childIdeas.length === 1) {
                                    return (
                                      <div key={`standalone-thread-${childIdeas[0].id}`} data-idea-id={childIdeas[0].id}>
                                        {renderIdeaCard(childIdeas[0], groupIndex, false, null, false, false)}
                                      </div>
                                    );
                                  }
                                  const isGroupExpanded = expandedCardGroups.has(parentIdea.id);
                                  const stackCount = childIdeas.length;
                                  return (
                                    <div key={`thread-${parentIdea.id}`} data-idea-id={parentIdea.id}>
                                      {isGroupExpanded ? (
                                        <div className="border-l-4 border-slate-500 pl-3 space-y-2">
                                          {childIdeas.map((childIdea, childIndex) => (
                                            <div key={childIdea.id}>
                                              {renderIdeaCard(childIdea, groupIndex + childIndex, true, parentIdea.id, childIndex === 0, childColor === 'green')}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        renderIdeaCard(childIdeas[0], groupIndex, false, parentIdea.id, true, false, false, stackCount)
                                      )}
                                    </div>
                                  );
                                }
                                // Handle standalone items
                                const idea = item.idea || item.parentIdea || item;
                                return (
                                  <div key={idea.id || groupIndex} data-idea-id={idea.id}>
                                    {renderIdeaCard(idea, groupIndex, false, null, false, false)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                  </TabsContent>
                  <TabsContent value="calendar" forceMount className={workspacePanels.left !== 'calendar' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <ScheduleViewTabs
                              scheduleTabView={scheduleTabView}
                              setScheduleTabView={setScheduleTabView}
                            />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => setShowScheduleSortDropdown(!showScheduleSortDropdown)} className={`p-2 border rounded-lg ${scheduleSortBy !== 'date-asc' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showScheduleSortDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleSortBy('date-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"/></svg>
                                        Soonest first
                                      </button>
                                      <button onClick={() => { setScheduleSortBy('date-desc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-desc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
                                        Latest first
                                      </button>
                                      <button onClick={() => { setScheduleSortBy('ticker-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'ticker-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9"/></svg>
                                        Ticker A-Z
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowScheduleFilterDropdown(!showScheduleFilterDropdown)} className={`p-2 border rounded-lg ${scheduleFilterBy !== '31days' || scheduleViewMode === '12month' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showScheduleFilterDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleFilterBy('31days'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === '31days' && scheduleViewMode !== '12month' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>31 Days</button>
                                      <button onClick={() => { setScheduleViewMode('12month'); setScheduleFilterBy('all'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleViewMode === '12month' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>12 Months</button>
                                      <div className={`border-t my-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                                      <button onClick={() => { setScheduleFilterBy('auto'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'auto' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Auto Only</button>
                                      <button onClick={() => { setScheduleFilterBy('manual'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'manual' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Manual Only</button>
                                      <button onClick={() => { setScheduleFilterBy('past'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'past' ? (isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Past Items</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowAddToScheduleModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Add">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Calendar body - using same content as main Calendar tab */}
                        <div data-calendar-scroll-container="left" className={`max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {renderScheduleBodyContent(true)}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  <TabsContent value="tickers" forceMount className={`h-full flex flex-col overflow-hidden ${workspacePanels.left !== 'tickers' ? 'hidden' : ''}`}>
                    <div className="flex-1 overflow-y-auto">
                      <WatchlistSpreadsheet 
                        user={user}
                        onTickerClick={(ticker) => openWatchlistModal([ticker], `${ticker} Ideas`)} 
                        isDarkMode={isDarkMode}
                        setIsDarkMode={setIsDarkMode}
                        onSearch={() => setShowMobileSearch(true)}
                        showNotification={showNotification}
                        sharedWatchlists={sharedWatchlists}
                        watchlistView={watchlistView}
                        setWatchlistView={setWatchlistView}
                        ideasLists={ideasListsForCollections}
                        polls={fishingThreads}
                        sharedPolls={sharedPolls}
                        onWatchlist={(rowData) => openWatchlistModal([rowData.ticker], `Add ${rowData.ticker} to Tickers`)}
                        onCreatePoll={(mode) => { if (mode) setNewPollMode(mode); setShowTweetGenerator(true); }}
                        onDeletePoll={(pollId, pollTitle) => deletePoll(pollId, pollTitle)}
                        isWorkspaceMode={true}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="files" forceMount className={`h-full flex flex-col ${workspacePanels.left !== 'files' ? 'hidden' : ''}`}>
                    <FilesTab 
                      isDarkMode={isDarkMode}
                      user={user}
                      openFileViewerId={openFileViewerId}
                      setOpenFileViewerId={setOpenFileViewerId}
                      showToast={showNotification}
                      isWorkspaceMode={true}
                      highlightedTicker={workspaceHighlightedTicker}
                      refreshTrigger={filesRefreshTrigger}
                    />
                  </TabsContent>
                  <TabsContent value="groups" forceMount className={workspacePanels.left !== 'groups' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <GroupViewTabs
                              groupView={groupView}
                              setGroupView={setGroupView}
                              myGroupsCount={userGroups.filter(g => g.user_role === 'admin').length}
                              invitedCount={userGroups.filter(g => g.user_role !== 'admin').length + pendingInvitations.length}
                            />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => selectedGroupIdeas ? setShowGroupAgeSortMenu(!showGroupAgeSortMenu) : null} disabled={!selectedGroupIdeas} className={`p-2 border rounded-lg ${!selectedGroupIdeas ? 'text-white/30 border-white/10 cursor-not-allowed' : groupAgeSortBy !== 'newest' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showGroupAgeSortMenu && selectedGroupIdeas && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupAgeSortBy('newest'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Newest First</button>
                                      <button onClick={() => { setGroupAgeSortBy('fresh'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>New (&lt;10 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('warm'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Recent (10-30 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('stale'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Older (&gt;30 days)</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowGroupTypeFilterMenu(!showGroupTypeFilterMenu)} className={`p-2 border rounded-lg ${groupTypeFilter !== 'all' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showGroupTypeFilterMenu && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupTypeFilter('all'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'all' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>All Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('my'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'my' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>My Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('invited'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'invited' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Invited</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowCreateGroupModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Create Group">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className={`p-2 max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {userGroups.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No groups yet - Create one to get started</div>
                          ) : (
                            <div className="space-y-2">
                              {userGroups.map((group, index) => (
                                <div 
                                  key={group.id || index} 
                                  onClick={() => setSelectedGroup(group)}
                                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                    selectedGroup?.id === group.id 
                                      ? (isDarkMode ? 'bg-orange-900/30 border-orange-500' : 'bg-orange-50 border-orange-500')
                                      : (isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300')
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{group.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${group.user_role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {group.user_role === 'admin' ? 'Admin' : 'Member'}
                                    </span>
                                  </div>
                                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {group.members?.length || 0} members • {group.ideas_count || 0} ideas
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Bottom Tab Bar - Left Panel */}
                </Tabs>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 flex items-center justify-around py-1.5 border-t ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, left: 'groups' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.left === 'groups' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Groups">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, left: 'tickers' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.left === 'tickers' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Tickers">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, left: 'ideas' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.left === 'ideas' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Ideas">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, left: 'files' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.left === 'files' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Files">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, left: 'calendar' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.left === 'calendar' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Folders">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
                    </button>
                </div>
              </div>
            </Panel>

            {/* Left Resize Handle */}
            <PanelResizeHandle className={`w-0.5 transition-colors hover:bg-orange-500 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />

            {/* Middle Panel */}
            <Panel defaultSize={30} minSize={15} maxSize={60}>
              <div className={`h-full flex flex-col relative min-h-0 ${isDarkMode ? 'bg-slate-900 border-r border-slate-700' : 'bg-white border-r border-slate-200'}`}>
                <div className="flex-1 flex flex-col min-h-0 pb-12 overflow-hidden">
                <Tabs value={workspacePanels.middle} className="w-full h-full flex flex-col">
                  <TabsContent value="ideas" forceMount className={`h-full flex flex-col ${workspacePanels.middle !== 'ideas' ? 'hidden' : ''}`}>
                        <div className="flex-shrink-0 p-3 bg-slate-800 text-white">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <DashboardViewTabs ideaView={ideaView} setIdeaView={setIdeaView} myIdeasCount={myIdeasCount} receivedCount={receivedIdeasCount} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <button onClick={handleDirectBookmarkImport} disabled={loading} className={`p-2 rounded-lg ${loading?'bg-orange-300':'bg-orange-500 hover:bg-orange-600'} text-white`} title="Import">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div 
                          className={`p-2 max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                          onScroll={(e) => {
                            const el = e.currentTarget;
                            const scrollPosition = el.scrollTop + el.clientHeight;
                            const scrollHeight = el.scrollHeight;
                            const threshold = scrollHeight - 300;
                            
                            if (scrollPosition >= threshold && !loadingMore && hasMoreIdeas) {
                              console.log('[Workspace Scroll] Middle panel ideas - loading more ideas', { scrollPosition, threshold, scrollHeight });
                              loadMoreIdeas();
                            }
                          }}
                        >
                          {groupedIdeasForView.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{ideaView==='received'?'No received ideas':'No ideas yet'}</div>
                          ) : (
                            <div className="space-y-2">
                              {groupedIdeasForView.map((item, groupIndex) => {
                                if (item.type === 'thread') {
                                  const { parentIdea, childIdeas, childColor = 'green' } = item;
                                  if (childIdeas.length === 1) {
                                    return (
                                      <div key={`standalone-thread-${childIdeas[0].id}`} data-idea-id={childIdeas[0].id}>
                                        {renderIdeaCard(childIdeas[0], groupIndex, false, null, false, false)}
                                      </div>
                                    );
                                  }
                                  const isGroupExpanded = expandedCardGroups.has(parentIdea.id);
                                  const stackCount = childIdeas.length;
                                  return (
                                    <div key={`thread-${parentIdea.id}`} data-idea-id={parentIdea.id}>
                                      {isGroupExpanded ? (
                                        <div className="border-l-4 border-slate-500 pl-3 space-y-2">
                                          {childIdeas.map((childIdea, childIndex) => (
                                            <div key={childIdea.id}>
                                              {renderIdeaCard(childIdea, groupIndex + childIndex, true, parentIdea.id, childIndex === 0, childColor === 'green')}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        renderIdeaCard(childIdeas[0], groupIndex, false, parentIdea.id, true, false, false, stackCount)
                                      )}
                                    </div>
                                  );
                                }
                                const idea = item.idea || item.parentIdea || item;
                                return (
                                  <div key={idea.id || groupIndex} data-idea-id={idea.id}>
                                    {renderIdeaCard(idea, groupIndex, false, null, false, false)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                  </TabsContent>
                  <TabsContent value="calendar" forceMount className={workspacePanels.middle !== 'calendar' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <ScheduleViewTabs scheduleTabView={scheduleTabView} setScheduleTabView={setScheduleTabView} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => setShowScheduleSortDropdown(!showScheduleSortDropdown)} className={`p-2 border rounded-lg ${scheduleSortBy !== 'date-asc' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showScheduleSortDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleSortBy('date-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Soonest first</button>
                                      <button onClick={() => { setScheduleSortBy('date-desc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-desc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Latest first</button>
                                      <button onClick={() => { setScheduleSortBy('ticker-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'ticker-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Ticker A-Z</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowScheduleFilterDropdown(!showScheduleFilterDropdown)} className={`p-2 border rounded-lg ${scheduleFilterBy !== '31days' || scheduleViewMode === '12month' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showScheduleFilterDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleFilterBy('31days'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === '31days' && scheduleViewMode !== '12month' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>31 Days</button>
                                      <button onClick={() => { setScheduleViewMode('12month'); setScheduleFilterBy('all'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleViewMode === '12month' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>12 Months</button>
                                      <div className={`border-t my-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                                      <button onClick={() => { setScheduleFilterBy('auto'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'auto' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Auto Only</button>
                                      <button onClick={() => { setScheduleFilterBy('manual'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'manual' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Manual Only</button>
                                      <button onClick={() => { setScheduleFilterBy('past'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'past' ? (isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Past Items</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowAddToScheduleModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Add">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Calendar body - using same content as main Calendar tab */}
                        <div data-calendar-scroll-container="middle" className={`max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {renderScheduleBodyContent(true)}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  <TabsContent value="tickers" forceMount className={`h-full flex flex-col overflow-hidden ${workspacePanels.middle !== 'tickers' ? 'hidden' : ''}`}>
                    <div className="flex-1 overflow-y-auto">
                      <WatchlistSpreadsheet 
                        user={user}
                        onTickerClick={(ticker) => openWatchlistModal([ticker], `${ticker} Ideas`)} 
                        isDarkMode={isDarkMode}
                        setIsDarkMode={setIsDarkMode}
                        onSearch={() => setShowMobileSearch(true)}
                        showNotification={showNotification}
                        sharedWatchlists={sharedWatchlists}
                        watchlistView={watchlistView}
                        setWatchlistView={setWatchlistView}
                        ideasLists={ideasListsForCollections}
                        polls={fishingThreads}
                        sharedPolls={sharedPolls}
                        isWorkspaceMode={true}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="files" forceMount className={`h-full flex flex-col ${workspacePanels.middle !== 'files' ? 'hidden' : ''}`}>
                    <FilesTab 
                      isDarkMode={isDarkMode}
                      user={user}
                      openFileViewerId={openFileViewerId}
                      setOpenFileViewerId={setOpenFileViewerId}
                      showToast={showNotification}
                      isWorkspaceMode={true}
                      highlightedTicker={workspaceHighlightedTicker}
                      refreshTrigger={filesRefreshTrigger}
                    />
                  </TabsContent>
                  <TabsContent value="groups" forceMount className={workspacePanels.middle !== 'groups' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <GroupViewTabs groupView={groupView} setGroupView={setGroupView} myGroupsCount={userGroups.filter(g => g.user_role === 'admin').length} invitedCount={userGroups.filter(g => g.user_role !== 'admin').length + pendingInvitations.length} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => selectedGroupIdeas ? setShowGroupAgeSortMenu(!showGroupAgeSortMenu) : null} disabled={!selectedGroupIdeas} className={`p-2 border rounded-lg ${!selectedGroupIdeas ? 'text-white/30 border-white/10 cursor-not-allowed' : groupAgeSortBy !== 'newest' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showGroupAgeSortMenu && selectedGroupIdeas && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupAgeSortBy('newest'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Newest First</button>
                                      <button onClick={() => { setGroupAgeSortBy('fresh'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>New (&lt;10 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('warm'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Recent (10-30 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('stale'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Older (&gt;30 days)</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowGroupTypeFilterMenu(!showGroupTypeFilterMenu)} className={`p-2 border rounded-lg ${groupTypeFilter !== 'all' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showGroupTypeFilterMenu && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupTypeFilter('all'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'all' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>All Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('my'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'my' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>My Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('invited'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'invited' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Invited</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowCreateGroupModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Create Group">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className={`p-2 max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {userGroups.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No groups yet - Create one to get started</div>
                          ) : (
                            <div className="space-y-2">
                              {userGroups.map((group, index) => (
                                <div key={group.id || index} onClick={() => setSelectedGroup(group)} className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedGroup?.id === group.id ? (isDarkMode ? 'bg-orange-900/30 border-orange-500' : 'bg-orange-50 border-orange-500') : (isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300')}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{group.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${group.user_role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{group.user_role === 'admin' ? 'Admin' : 'Member'}</span>
                                  </div>
                                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{group.members?.length || 0} members • {group.ideas_count || 0} ideas</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Bottom Tab Bar - Middle Panel */}
                </Tabs>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 flex items-center justify-around py-1.5 border-t ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, middle: 'groups' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.middle === 'groups' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Groups">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, middle: 'tickers' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.middle === 'tickers' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Tickers">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, middle: 'ideas' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.middle === 'ideas' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Ideas">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, middle: 'files' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.middle === 'files' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Files">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, middle: 'calendar' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.middle === 'calendar' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Folders">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
                    </button>
                </div>
              </div>
            </Panel>

            {/* Right Resize Handle */}
            <PanelResizeHandle className={`w-0.5 transition-colors hover:bg-orange-500 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />

            {/* Right Panel */}
            <Panel defaultSize={25} minSize={15} maxSize={60}>
              <div className={`h-full flex flex-col relative min-h-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <div className="flex-1 flex flex-col min-h-0 pb-12 overflow-hidden">
                <Tabs value={workspacePanels.right} className="w-full h-full flex flex-col">
                  <TabsContent value="ideas" forceMount className={`h-full flex flex-col ${workspacePanels.right !== 'ideas' ? 'hidden' : ''}`}>
                        <div className="flex-shrink-0 p-3 bg-slate-800 text-white">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <DashboardViewTabs ideaView={ideaView} setIdeaView={setIdeaView} myIdeasCount={myIdeasCount} receivedCount={receivedIdeasCount} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <button onClick={handleDirectBookmarkImport} disabled={loading} className={`p-2 rounded-lg ${loading?'bg-orange-300':'bg-orange-500 hover:bg-orange-600'} text-white`} title="Import">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div 
                          className={`p-2 max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                          onScroll={(e) => {
                            const el = e.currentTarget;
                            const scrollPosition = el.scrollTop + el.clientHeight;
                            const scrollHeight = el.scrollHeight;
                            const threshold = scrollHeight - 300;
                            
                            if (scrollPosition >= threshold && !loadingMore && hasMoreIdeas) {
                              console.log('[Workspace Scroll] Right panel ideas - loading more ideas', { scrollPosition, threshold, scrollHeight });
                              loadMoreIdeas();
                            }
                          }}
                        >
                          {groupedIdeasForView.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{ideaView==='received'?'No received ideas':'No ideas yet'}</div>
                          ) : (
                            <div className="space-y-2">
                              {groupedIdeasForView.map((item, groupIndex) => {
                                if (item.type === 'thread') {
                                  const { parentIdea, childIdeas, childColor = 'green' } = item;
                                  if (childIdeas.length === 1) {
                                    return (
                                      <div key={`standalone-thread-${childIdeas[0].id}`} data-idea-id={childIdeas[0].id}>
                                        {renderIdeaCard(childIdeas[0], groupIndex, false, null, false, false)}
                                      </div>
                                    );
                                  }
                                  const isGroupExpanded = expandedCardGroups.has(parentIdea.id);
                                  const stackCount = childIdeas.length;
                                  return (
                                    <div key={`thread-${parentIdea.id}`} data-idea-id={parentIdea.id}>
                                      {isGroupExpanded ? (
                                        <div className="border-l-4 border-slate-500 pl-3 space-y-2">
                                          {childIdeas.map((childIdea, childIndex) => (
                                            <div key={childIdea.id}>
                                              {renderIdeaCard(childIdea, groupIndex + childIndex, true, parentIdea.id, childIndex === 0, childColor === 'green')}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        renderIdeaCard(childIdeas[0], groupIndex, false, parentIdea.id, true, false, false, stackCount)
                                      )}
                                    </div>
                                  );
                                }
                                const idea = item.idea || item.parentIdea || item;
                                return (
                                  <div key={idea.id || groupIndex} data-idea-id={idea.id}>
                                    {renderIdeaCard(idea, groupIndex, false, null, false, false)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                  </TabsContent>
                  <TabsContent value="calendar" forceMount className={workspacePanels.right !== 'calendar' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <ScheduleViewTabs scheduleTabView={scheduleTabView} setScheduleTabView={setScheduleTabView} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => setShowScheduleSortDropdown(!showScheduleSortDropdown)} className={`p-2 border rounded-lg ${scheduleSortBy !== 'date-asc' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showScheduleSortDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleSortBy('date-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Soonest first</button>
                                      <button onClick={() => { setScheduleSortBy('date-desc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'date-desc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Latest first</button>
                                      <button onClick={() => { setScheduleSortBy('ticker-asc'); setShowScheduleSortDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${scheduleSortBy === 'ticker-asc' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Ticker A-Z</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowScheduleFilterDropdown(!showScheduleFilterDropdown)} className={`p-2 border rounded-lg ${scheduleFilterBy !== '31days' || scheduleViewMode === '12month' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showScheduleFilterDropdown && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setScheduleFilterBy('31days'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === '31days' && scheduleViewMode !== '12month' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>31 Days</button>
                                      <button onClick={() => { setScheduleViewMode('12month'); setScheduleFilterBy('all'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleViewMode === '12month' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>12 Months</button>
                                      <div className={`border-t my-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                                      <button onClick={() => { setScheduleFilterBy('auto'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'auto' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Auto Only</button>
                                      <button onClick={() => { setScheduleFilterBy('manual'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'manual' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Manual Only</button>
                                      <button onClick={() => { setScheduleFilterBy('past'); setScheduleViewMode('list'); setShowScheduleFilterDropdown(false); }} className={`w-full text-left px-4 py-2 text-sm ${scheduleFilterBy === 'past' ? (isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Past Items</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowAddToScheduleModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Add">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Calendar body - using same content as main Calendar tab */}
                        <div data-calendar-scroll-container="right" className={`max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {renderScheduleBodyContent(true)}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  <TabsContent value="tickers" forceMount className={`h-full flex flex-col overflow-hidden ${workspacePanels.right !== 'tickers' ? 'hidden' : ''}`}>
                    <div className="flex-1 overflow-y-auto">
                      <WatchlistSpreadsheet 
                        user={user}
                        onTickerClick={(ticker) => openWatchlistModal([ticker], `${ticker} Ideas`)} 
                        isDarkMode={isDarkMode}
                        setIsDarkMode={setIsDarkMode}
                        onSearch={() => setShowMobileSearch(true)}
                        showNotification={showNotification}
                        sharedWatchlists={sharedWatchlists}
                        watchlistView={watchlistView}
                        setWatchlistView={setWatchlistView}
                        ideasLists={ideasListsForCollections}
                        polls={fishingThreads}
                        sharedPolls={sharedPolls}
                        isWorkspaceMode={true}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="files" forceMount className={`h-full flex flex-col ${workspacePanels.right !== 'files' ? 'hidden' : ''}`}>
                    <FilesTab 
                      isDarkMode={isDarkMode}
                      user={user}
                      openFileViewerId={openFileViewerId}
                      setOpenFileViewerId={setOpenFileViewerId}
                      showToast={showNotification}
                      isWorkspaceMode={true}
                      highlightedTicker={workspaceHighlightedTicker}
                      refreshTrigger={filesRefreshTrigger}
                    />
                  </TabsContent>
                  <TabsContent value="groups" forceMount className={workspacePanels.right !== 'groups' ? 'hidden' : ''}>
                    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                      <CardContent className="p-0">
                        <div className="p-3 bg-slate-800 text-white">
                          <div className="flex items-center justify-between">
                            <GroupViewTabs groupView={groupView} setGroupView={setGroupView} myGroupsCount={userGroups.filter(g => g.user_role === 'admin').length} invitedCount={userGroups.filter(g => g.user_role !== 'admin').length + pendingInvitations.length} />
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowMobileSearch(true)} className="p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" title="Search">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                              </button>
                              <div className="relative">
                                <button onClick={() => selectedGroupIdeas ? setShowGroupAgeSortMenu(!showGroupAgeSortMenu) : null} disabled={!selectedGroupIdeas} className={`p-2 border rounded-lg ${!selectedGroupIdeas ? 'text-white/30 border-white/10 cursor-not-allowed' : groupAgeSortBy !== 'newest' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Sort">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/></svg>
                                </button>
                                {showGroupAgeSortMenu && selectedGroupIdeas && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupAgeSortBy('newest'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Newest First</button>
                                      <button onClick={() => { setGroupAgeSortBy('fresh'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>New (&lt;10 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('warm'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Recent (10-30 days)</button>
                                      <button onClick={() => { setGroupAgeSortBy('stale'); setShowGroupAgeSortMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Older (&gt;30 days)</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="relative">
                                <button onClick={() => setShowGroupTypeFilterMenu(!showGroupTypeFilterMenu)} className={`p-2 border rounded-lg ${groupTypeFilter !== 'all' ? 'bg-white/20 text-white border-white/40' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`} title="Filter">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                                </button>
                                {showGroupTypeFilterMenu && (
                                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
                                    <div className="py-1">
                                      <button onClick={() => { setGroupTypeFilter('all'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'all' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>All Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('my'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'my' ? (isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>My Groups</button>
                                      <button onClick={() => { setGroupTypeFilter('invited'); setShowGroupTypeFilterMenu(false); }} className={`w-full text-left px-4 py-2 text-sm ${groupTypeFilter === 'invited' ? (isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700') : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50')}`}>Invited</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <button onClick={() => setShowCreateGroupModal(true)} className="p-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white" title="Create Group">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className={`p-2 max-h-[calc(100vh-200px)] overflow-y-auto ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                          {userGroups.length === 0 ? (
                            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No groups yet - Create one to get started</div>
                          ) : (
                            <div className="space-y-2">
                              {userGroups.map((group, index) => (
                                <div key={group.id || index} onClick={() => setSelectedGroup(group)} className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedGroup?.id === group.id ? (isDarkMode ? 'bg-orange-900/30 border-orange-500' : 'bg-orange-50 border-orange-500') : (isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300')}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{group.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${group.user_role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{group.user_role === 'admin' ? 'Admin' : 'Member'}</span>
                                  </div>
                                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{group.members?.length || 0} members • {group.ideas_count || 0} ideas</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  {/* Bottom Tab Bar - Right Panel */}
                </Tabs>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 flex items-center justify-around py-1.5 border-t ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, right: 'groups' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.right === 'groups' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Groups">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, right: 'tickers' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.right === 'tickers' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Tickers">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, right: 'ideas' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.right === 'ideas' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Ideas">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, right: 'files' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.right === 'files' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Files">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    </button>
                    <button onClick={() => setWorkspacePanels(prev => ({ ...prev, right: 'calendar' }))} className={`flex flex-col items-center p-1.5 rounded-lg transition-colors ${workspacePanels.right === 'calendar' ? 'text-orange-500' : isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`} title="Folders">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
                    </button>
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </div>
      ) : (
      /* NORMAL TAB MODE */
      <main className="flex-1 flex flex-col">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 sm:pb-8 w-full flex-1">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Desktop: Top navigation tabs */}
          <TabsList className="hidden sm:grid w-full grid-cols-5 mb-4 sm:mb-8 h-auto overflow-visible">
            {/* 1. Groups */}
            <TabsTrigger value="groups" className="flex-col p-1.5 sm:p-3 text-[10px] sm:text-sm gap-0.5 relative overflow-visible">
              {/* Users icon - represents groups/teams */}
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <span>Groups</span>
              
              {/* Tour Step 4: Groups */}
              {tourStep === 4 && (
                <div className="fixed sm:absolute bottom-20 sm:bottom-auto sm:top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 sm:mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-[calc(100vw-2rem)] sm:w-80 sm:max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                      Step {tourStep} of {TOUR_STEPS}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        skipTour();
                      }}
                      className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                    >
                      Skip tour
                    </button>
                  </div>
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                    <span>Groups</span>
                  </h4>
                  <p className="text-sm text-slate-600 mb-3 break-words">
                    Create private groups to share and discuss ideas with friends or team members.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextTourStep();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:block absolute -top-2 left-6 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                </div>
              )}
            </TabsTrigger>
            {/* 2. Tickers */}
            <TabsTrigger value="watchlists" className="flex-col p-1.5 sm:p-3 text-[10px] sm:text-sm gap-0.5 relative overflow-visible">
              {/* Dollar sign icon - represents stock tickers */}
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>Tickers</span>
              
              {/* Tour Step 5: Tickers */}
              {tourStep === 5 && (
                <div className="fixed sm:absolute bottom-20 sm:bottom-auto sm:top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 sm:mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-[calc(100vw-2rem)] sm:w-80 sm:max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                      Step {tourStep} of {TOUR_STEPS}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        skipTour();
                      }}
                      className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                    >
                      Skip tour
                    </button>
                  </div>
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span>Tickers</span>
                  </h4>
                  <p className="text-sm text-slate-600 mb-3 break-words">
                    Organize tickers into Watchlists, create Polls to crowdsource ideas, and save Curations from others.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextTourStep();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:block absolute -top-2 left-6 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                </div>
              )}
            </TabsTrigger>
            {/* 3. Ideas */}
            <TabsTrigger 
              value="dashboard" 
              className={`flex-col p-1.5 sm:p-3 text-[10px] sm:text-sm relative gap-0.5 overflow-visible ${tourStep === 3 ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
              onClick={(e) => {
                // If already on Ideas tab and there are new items, refresh
                if (activeTab === 'dashboard' && hasNewIdeas) {
                  e.preventDefault();
                  const now = new Date().toISOString();
                  fetchData();
                  setIdeasLastChecked(now);
                  localStorage.setItem('tickler_ideas_last_checked', now);
                  setHasNewIdeas(false);
                }
              }}
            >
              {/* SAVED OLD ICON - Stacked cards:
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <rect x="4" y="3" width="16" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 16h14" strokeLinecap="round"/>
                <path d="M6 18h12" strokeLinecap="round"/>
                <path d="M7 20h10" strokeLinecap="round"/>
              </svg>
              */}
              {/* Lightbulb icon */}
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              <span>Ideas</span>
              {hasNewIdeas && (
                <span className="absolute top-0.5 right-1 sm:top-1 sm:right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
              
              {/* Tour Step 3: Ideas Tab - Desktop only */}
              {tourStep === 3 && (
                <div className="hidden sm:block absolute top-full left-0 mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-80 max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                      Step {tourStep} of {TOUR_STEPS}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        skipTour();
                      }}
                      className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                    >
                      Skip tour
                    </button>
                  </div>
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                    </svg>
                    <span>Your Ideas</span>
                  </h4>
                  <p className="text-sm text-slate-600 mb-3 break-words">
                    Your investment ideas live here. Search by ticker, organize, and schedule research dates.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextTourStep();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:block absolute -top-2 left-6 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                </div>
              )}
            </TabsTrigger>
            {/* LIBRARY TAB - TEMPORARILY HIDDEN - To restore: uncomment this TabsTrigger and change grid-cols-5 to grid-cols-6 above */}
            {/* <TabsTrigger 
              value="research" 
              className="flex-col sm:flex-row p-2 sm:p-3 text-xs sm:text-sm relative"
              onClick={(e) => {
                // If already on Library tab and there are new items, refresh
                if (activeTab === 'research' && hasNewLibrary) {
                  e.preventDefault();
                  const now = new Date().toISOString();
                  fetchResearchItems();
                  setLibraryLastChecked(now);
                  localStorage.setItem('tickler_library_last_checked', now);
                  setHasNewLibrary(false);
                }
              }}
            >
              <svg className="h-4 w-4 mb-1 sm:mb-0 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span className="hidden xs:inline">Library</span>
              {hasNewLibrary && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
            </TabsTrigger> */}
            {/* TEST Tab - Removed from navigation but keeping code for future use */}
            {/* <TabsTrigger value="test" className="flex-col sm:flex-row p-2 sm:p-3 text-xs sm:text-sm">
              <svg className="h-4 w-4 mb-1 sm:mb-0 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span className="hidden xs:inline">TEST</span>
            </TabsTrigger> */}
            {/* 4. Files */}
            <TabsTrigger value="files" className="flex-col p-1.5 sm:p-3 text-[10px] sm:text-sm relative gap-0.5 overflow-visible">
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span>Files</span>
              {hasNewFiles && (
                <span className="absolute top-0.5 right-1 sm:top-1 sm:right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
              
              {/* Tour Step 6: Files */}
              {tourStep === 6 && (
                <div className="fixed sm:absolute bottom-20 sm:bottom-auto sm:top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 sm:mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-[calc(100vw-2rem)] sm:w-80 sm:max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                      Step {tourStep} of {TOUR_STEPS}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        skipTour();
                      }}
                      className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                    >
                      Skip tour
                    </button>
                  </div>
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                    </svg>
                    <span>Files</span>
                  </h4>
                  <p className="text-sm text-slate-600 mb-3">
                    Research compiled for your tickers. SEC filings, documents, and key information in one place.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextTourStep();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:block absolute -top-2 left-6 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                </div>
              )}
            </TabsTrigger>
            {/* 5. Schedule */}
            <TabsTrigger value="schedule" className="flex-col p-1.5 sm:p-3 text-[10px] sm:text-sm gap-0.5 relative overflow-visible">
              <svg className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
              </svg>
              <span>Folders</span>
              
              {/* Tour Step 7: Folders - Final Step */}
              {tourStep === 7 && (
                <div className="fixed sm:absolute bottom-20 sm:bottom-auto sm:top-full left-1/2 -translate-x-1/2 sm:right-0 sm:left-auto sm:translate-x-0 sm:mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-[calc(100vw-2rem)] sm:w-80 sm:max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                      Step {tourStep} of {TOUR_STEPS}
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        skipTour();
                      }}
                      className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                    >
                      Skip tour
                    </button>
                  </div>
                  <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                    <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                    </svg>
                    <span>Folders</span>
                  </h4>
                  <p className="text-sm text-slate-600 mb-3 break-words">
                    Schedule research for specific dates. We'll send you a daily folder with research for each ticker due that day.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        completeTour();
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Got it!
                    </button>
                  </div>
                  <div className="hidden sm:block absolute -top-2 right-6 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                </div>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Mobile: Bottom navigation bar */}
          {/* Order: Polls | Watchlist | Ideas (center) | Dossiers | Groups */}
          <div className={`sm:hidden fixed bottom-0 left-0 right-0 border-t z-50 safe-area-inset-bottom ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'}`} style={{ transform: 'translateZ(0)' }}>
            <div className="grid grid-cols-5 h-16">
              {/* 1. Groups - far left */}
              <button
                onClick={() => handleTabChange('groups')}
                className={`flex flex-col items-center justify-center gap-1 relative ${
                  activeTab === 'groups' ? 'text-orange-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-500')
                } ${tourStep === 4 ? 'ring-2 ring-orange-500 ring-offset-2 rounded-lg bg-orange-50' : ''}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
                <span className="text-[10px] font-medium">Groups</span>
              </button>
              {/* 2. Tickers - left of center */}
              <button
                onClick={() => handleTabChange('watchlists')}
                className={`flex flex-col items-center justify-center gap-1 relative ${
                  activeTab === 'watchlists' ? 'text-orange-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-500')
                } ${tourStep === 5 ? 'ring-2 ring-orange-500 ring-offset-2 rounded-lg bg-orange-50' : ''}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="text-[10px] font-medium">Tickers</span>
                {hasNewWatchlists && (
                  <span className="absolute top-2 right-1/2 translate-x-3 w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </button>
              {/* 3. Ideas - CENTER (default tab) */}
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`flex flex-col items-center justify-center gap-1 relative ${
                  activeTab === 'dashboard' ? 'text-orange-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-500')
                } ${tourStep === 3 ? 'ring-2 ring-orange-500 ring-offset-2 rounded-lg bg-orange-50' : ''}`}
              >
                {/* SAVED OLD ICON - Stacked cards:
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <rect x="4" y="3" width="16" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 16h14" strokeLinecap="round"/>
                  <path d="M6 18h12" strokeLinecap="round"/>
                  <path d="M7 20h10" strokeLinecap="round"/>
                </svg>
                */}
                {/* Lightbulb icon */}
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                </svg>
                <span className="text-[10px] font-medium">Ideas</span>
                {hasNewIdeas && (
                  <span className="absolute top-2 right-1/2 translate-x-3 w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </button>
              {/* 4. Briefs - right of center */}
              <button
                onClick={() => handleTabChange('files')}
                className={`flex flex-col items-center justify-center gap-1 relative ${
                  activeTab === 'files' ? 'text-orange-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-500')
                } ${tourStep === 6 ? 'ring-2 ring-orange-500 ring-offset-2 rounded-lg bg-orange-50' : ''}`}
                data-testid="mobile-nav-files"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span className="text-[10px] font-medium">Files</span>
                {hasNewFiles && (
                  <span className="absolute top-2 right-1/2 translate-x-3 w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </button>
              {/* 5. Schedule - far right */}
              <button
                onClick={() => handleTabChange('schedule')}
                className={`flex flex-col items-center justify-center gap-1 relative ${
                  activeTab === 'schedule' ? 'text-orange-500' : (isDarkMode ? 'text-slate-400' : 'text-gray-500')
                } ${tourStep === 7 ? 'ring-2 ring-orange-500 ring-offset-2 rounded-lg bg-orange-50' : ''}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                </svg>
                <span className="text-[10px] font-medium">Folders</span>
              </button>
            </div>
          </div>

          {/* Mobile Tour Tooltips - Rendered as fixed overlay above everything */}
          {tourStep >= 1 && tourStep <= 7 && (
            <div className="sm:hidden fixed inset-0 z-[100] pointer-events-none">
              {/* Semi-transparent backdrop */}
              <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={skipTour}></div>
              
              {/* Tour tooltip - positioned above bottom nav */}
              <div className="absolute bottom-20 left-4 right-4 bg-white border-2 border-orange-500 rounded-xl shadow-2xl p-4 pointer-events-auto animate-fade-in">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded">
                    Step {tourStep} of {TOUR_STEPS}
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      skipTour();
                    }}
                    className="text-slate-400 hover:text-slate-600 text-sm font-medium"
                  >
                    Skip
                  </button>
                </div>
                
                {tourStep === 1 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      Import from X
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Import your X bookmarks. AI extracts tickers and investment ideas automatically.
                    </p>
                  </>
                )}
                {tourStep === 2 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                      </svg>
                      Shared Ideas
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      When someone shares a ticker with you, it appears here. Review and import ideas to your dashboard.
                    </p>
                  </>
                )}
                {tourStep === 3 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                      </svg>
                      Ideas
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Your investment ideas live here. Search by ticker, filter by conviction, and track your research.
                    </p>
                  </>
                )}
                {tourStep === 4 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                      </svg>
                      Groups
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Create private groups to share ideas with friends or team members.
                    </p>
                  </>
                )}
                {tourStep === 5 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Tickers
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Organize tickers into Watchlists, create Polls to crowdsource ideas, and save Curations from others.
                    </p>
                  </>
                )}
                {tourStep === 6 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      Files
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Research compiled for your tickers. SEC filings, documents, and key information in one place.
                    </p>
                  </>
                )}
                {tourStep === 7 && (
                  <>
                    <h4 className="font-bold text-sm mb-2 text-slate-800 flex items-center gap-2">
                      <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                      </svg>
                      Folders
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Schedule research for specific dates. We'll send you a daily folder with research for each ticker due that day.
                    </p>
                  </>
                )}
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (tourStep === 7) {
                      completeTour();
                    } else {
                      nextTourStep();
                    }
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {tourStep === 7 ? 'Got it!' : 'Next'}
                </button>
                
                {/* Arrow pointing down to the tabs */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 border-orange-500 rotate-45"></div>
              </div>
            </div>
          )}

          {/* Dashboard Tab - Modern Card Interface */}
          <TabsContent value="dashboard">
            
            {/* Investment Ideas - Modern Cards with swipe support */}
            <div {...dashboardSwipeHandlers}>
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                <div className="p-4 bg-slate-800 text-white">
                  {/* My Ideas/Received toggle on left, action buttons on right */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: View Toggle - My Ideas / Received */}
                    <DashboardViewTabs
                      ideaView={ideaView}
                      setIdeaView={setIdeaView}
                      myIdeasCount={myIdeasCount}
                      receivedCount={receivedIdeasCount}
                    />
                    
                    {/* Center: Keyboard shortcuts hint - Desktop only, subtle */}
                    <button
                      onClick={() => setShowKeyboardShortcutsModal(true)}
                      className="hidden sm:flex items-center text-white/50 text-sm"
                      title="View keyboard shortcuts"
                    >
                      <span>Press </span>
                      <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                      <span> for shortcuts</span>
                    </button>
                    
                    {/* Right side: Action buttons and Search - equal width on mobile, uniform spacing on desktop */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2 relative">
                        {/* Search Box with Hints Dropdown */}
                        <div className="relative sm:w-24">
                          {/* Mobile: Icon button that expands to full search */}
                          <button
                            onClick={() => setShowMobileSearch(true)}
                            className={`sm:hidden w-full flex items-center justify-center px-2 py-2 border rounded-lg transition-all duration-200 ${
                              searchQuery 
                                ? 'text-white border-white/40 bg-white/10' 
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                            }`}
                            title="Search"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                          
                          {/* Search modal moved to global level */}

                          
                          {/* Desktop: Search button matching other buttons */}
                          <div className="hidden sm:block relative">
                            <button
                              onClick={() => setShowMobileSearch(true)}
                              className="w-24 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                              title="Search (Press / or ⌘K)"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              Search
                            </button>
                          </div>
                          
                          {/* Search Hints Dropdown - Desktop only */}
                          {showSearchHints && !searchQuery && (
                            <div className={`hidden sm:block absolute left-0 right-0 w-64 mt-1 rounded-lg shadow-lg z-50 p-3 ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                              <div className="space-y-1.5 text-sm text-slate-600">
                                <p><span className="font-mono bg-slate-100 px-1 rounded">$NVDA</span> <span className="text-slate-400">ticker</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">Nvidia</span> <span className="text-slate-400">company</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">semiconductor</span> <span className="text-slate-400">keyword</span></p>
                                <div className="border-t border-slate-200 pt-1.5 mt-1.5">
                                  <p className="mb-1"><span className="font-mono bg-slate-100 px-1 rounded">@</span> <span className="text-slate-400">{totalHandles > 0 ? `${totalHandles} handles` : 'person'}</span></p>
                                  {uniqueHandlesList.length > 0 && (
                                    <div className="pl-4 max-h-24 overflow-y-auto">
                                      {uniqueHandlesList.slice(0, 8).map((handle) => (
                                        <button
                                          key={handle}
                                          onClick={() => {
                                            setSearchQuery(`@${handle}`);
                                            setShowSearchHints(false);
                                          }}
                                          className="block w-full text-left text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 py-0.5 px-1 rounded"
                                        >
                                          @{handle}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">groups:</span> <span className="text-slate-400">search groups</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">tickers:</span> <span className="text-slate-400">browse tickers</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">ideas:</span> <span className="text-slate-400">search ideas</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">files:</span> <span className="text-slate-400">search files</span></p>
                                <p><span className="font-mono bg-slate-100 px-1 rounded">folders:</span> <span className="text-slate-400">search folders</span></p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Sort Dropdown Button */}
                        <div className="relative sort-dropdown-container">
                          <button 
                            onClick={() => setShowSortDropdown(!showSortDropdown)}
                            className="w-full sm:w-24 inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                            title="Sort ideas"
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                            </svg>
                            <span className="hidden sm:inline">Sort</span>
                          </button>
                          
                          {/* Dropdown Menu */}
                          {showSortDropdown && (
                            <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setSortBy('newest');
                                    setShowSortDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-slate-400 mr-2"></div>
                                    All (newest first)
                                  </div>
                                </button>
                                <button
                                  onClick={() => {
                                    setSortBy('fresh');
                                    setShowSortDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                    New (&lt; 10 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => {
                                    setSortBy('warm');
                                    setShowSortDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                                    Recent (10-30 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => {
                                    setSortBy('stale');
                                    setShowSortDropdown(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${sortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                                    Older (30+ days)
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Source Filter */}
                        <div className="relative">
                          <button
                            onClick={() => setShowSourceDropdown(!showSourceDropdown)}
                            className="w-full sm:w-24 inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                            title="Filter by source"
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                            </svg>
                            <span className="hidden sm:inline">Filter</span>
                          </button>
                          
                          {/* Dynamic Dropdown Menu */}
                          {showSourceDropdown && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowSourceDropdown(false)}
                              />
                              <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 z-50 max-h-80 overflow-y-auto ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                                {(() => {
                                  const { sourceTypes, sourceCategories } = extractDynamicSources(bookmarks);
                                  
                                  return sourceTypes.map(sourceType => {
                                    if (sourceType === 'all') {
                                      return (
                                        <button
                                          key="all"
                                          onClick={() => {
                                            setSourceFilter('all');
                                            setShowSourceDropdown(false);
                                          }}
                                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${
                                            isDarkMode 
                                              ? `hover:bg-slate-700 ${sourceFilter === 'all' ? 'bg-slate-700 text-blue-400' : 'text-slate-200'}` 
                                              : `hover:bg-gray-50 ${sourceFilter === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`
                                          }`}
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"/>
                                          </svg>
                                          All
                                        </button>
                                      );
                                    }
                                    
                                    const category = sourceCategories.get(sourceType);
                                    if (!category) return null;
                                    
                                    return (
                                      <button
                                        key={sourceType}
                                        onClick={() => {
                                          setSourceFilter(sourceType);
                                          setShowSourceDropdown(false);
                                        }}
                                        className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 ${
                                          isDarkMode 
                                            ? `hover:bg-slate-700 ${sourceFilter === sourceType ? 'bg-slate-700 text-blue-400' : 'text-slate-200'}` 
                                            : `hover:bg-gray-50 ${sourceFilter === sourceType ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`
                                        }`}
                                      >
                                        <div className="w-4 h-4 flex items-center justify-center">
                                          {category.icon}
                                        </div>
                                        {category.label}
                                      </button>
                                    );
                                  });
                                })()}
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Import X Bookmarks Button - Orange, Last position - Direct import, no modal */}
                        <div className="relative">
                          <button
                            onClick={handleDirectBookmarkImport}
                            disabled={loading}
                            className={`w-full sm:w-24 sm:inline-flex sm:items-center sm:justify-center sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center order-last ${
                              loading
                                ? 'bg-orange-300 text-white/60 cursor-not-allowed' 
                                : 'bg-orange-500 text-white border border-orange-500 hover:bg-orange-600'
                            } ${tourStep === 1 ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-slate-800' : ''}`}
                            title="Import X Bookmarks"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                            </svg>
                            <span className="hidden sm:inline sm:ml-2">Import</span>
                          </button>
                          
                          {/* Tour Step 1: Import X Bookmarks - Desktop only (mobile uses fixed overlay) */}
                          {tourStep === 1 && (
                            <div className="hidden sm:block absolute top-full right-0 mt-3 bg-white border-2 border-orange-500 text-slate-800 px-4 py-3 rounded-xl shadow-2xl w-80 max-w-[calc(100vw-2rem)] z-[100] animate-fade-in whitespace-normal">
                              <div className="flex items-start justify-between mb-2">
                                <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded whitespace-nowrap">
                                  Step {tourStep} of {TOUR_STEPS}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    skipTour();
                                  }}
                                  className="text-slate-400 hover:text-slate-600 text-sm font-medium whitespace-nowrap ml-2"
                                >
                                  Skip tour
                                </button>
                              </div>
                              <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                                <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Import from X</span>
                              </h4>
                              <p className="text-sm text-slate-600 mb-3 break-words">
                                Import your X bookmarks. We'll extract investment ideas with AI.
                              </p>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    nextTourStep();
                                  }}
                                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                              <div className="hidden sm:block absolute -top-2 left-1/2 -translate-x-1/2 sm:right-6 sm:left-auto sm:translate-x-0 w-4 h-4 bg-white border-t-2 border-l-2 border-orange-500 rotate-45"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                
                {/* Content area */}
                <div className="p-3 sm:p-4">
                  {loading ? (
                    // Loading skeleton
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 animate-pulse">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                            <div className="flex-1 space-y-3">
                              <div className="flex items-center gap-2">
                                <div className="h-5 w-20 bg-slate-200 rounded"></div>
                                <div className="h-4 w-32 bg-slate-100 rounded"></div>
                              </div>
                              <div className="h-4 w-full bg-slate-100 rounded"></div>
                              <div className="h-4 w-3/4 bg-slate-100 rounded"></div>
                              <div className="flex gap-2">
                                <div className="h-6 w-16 bg-slate-100 rounded-full"></div>
                                <div className="h-6 w-16 bg-slate-100 rounded-full"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : filteredIdeasForView.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      {/* Check if this is due to ideaView filter or sort filter */}
                      {ideaView === 'received' ? (
                        <>
                          {/* Empty State - No received ideas */}
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                            </svg>
                          </div>
                          <h2 className="text-xl font-semibold text-slate-700 mb-2">
                            No Received Ideas Yet
                          </h2>
                          <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            Ideas shared with you by others will appear here. Join a group or ask someone to share ideas with you!
                          </p>
                          <button
                            onClick={() => setIdeaView('my')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                          >
                            View My Ideas
                          </button>
                        </>
                      ) : sortBy !== 'newest' && hasEverHadIdeas ? (
                        <>
                          {/* Empty State - No matching ideas for filter */}
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <h2 className="text-xl font-semibold text-slate-700 mb-2">
                            No {sortBy === 'fresh' ? 'New' : sortBy === 'warm' ? 'Recent' : 'Older'} Ideas
                          </h2>
                          <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            {sortBy === 'fresh' && "No ideas newer than 10 days. All your ideas are older."}
                            {sortBy === 'warm' && "No ideas between 10-30 days old."}
                            {sortBy === 'stale' && "No ideas older than 30 days. All your ideas are more recent."}
                          </p>
                          <button
                            onClick={() => setSortBy('newest')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Show All Ideas
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Empty State - Import from X */}
                          <h2 className="text-2xl sm:text-9xl font-bold text-slate-800 mb-3">
                            Import Your X Bookmarks
                          </h2>
                          
                          <p className="text-slate-600 text-base sm:text-lg mb-8 max-w-xl mx-auto">
                            Get started by importing your X bookmarks. We'll automatically analyze investment ideas, extract tickers, and organize everything for you.
                          </p>
                          
                          {/* Big Import Button - Direct import, no modal */}
                          <button
                            onClick={handleDirectBookmarkImport}
                            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                          >
                            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            Import Bookmarks from X
                          </button>
                          
                          <p className="text-xs text-slate-500 mt-6 max-w-md mx-auto">
                            We'll import your last 50 bookmarks. This takes about 2 minutes.
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {dashboardView === 'ideas' ? (
                        // Ideas View - Current individual card layout using memoized data
                        <>
                        {/* Search Idea Filter Indicator - from clicking idea in search results */}
                        {searchIdeaFilter && searchIdeaFilter.length > 0 && (
                          <div className={`flex items-center justify-between px-4 py-3 rounded-lg mb-4 ${isDarkMode ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'}`}>
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              <span className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                                Showing <strong>{searchIdeaFilter.length}</strong> {searchIdeaFilter.length === 1 ? 'idea' : 'ideas'} from search
                                {searchIdeaFilter[0]?.tickers?.[0] && (
                                  <span className="ml-1">
                                    ({searchIdeaFilter.map(i => '$' + (i.tickers?.[0]?.ticker || i.tickers?.[0] || '')).join(', ')})
                                  </span>
                                )}
                              </span>
                            </div>
                            <button
                              onClick={() => { setSearchIdeaFilter(null); setSearchQuery(''); }}
                              className={`text-sm font-medium px-3 py-1 rounded-lg transition-colors ${isDarkMode ? 'text-orange-400 hover:bg-orange-500/20' : 'text-orange-600 hover:bg-orange-100'}`}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                        {/* Global Search Results Indicator - hide when search idea filter is active */}
                        {searchQuery && searchQuery.trim() && !searchIdeaFilter && (
                          <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                            <div className="flex items-center gap-2">
                              <SearchIcon className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-800">
                                {isSearching ? (
                                  <span className="flex items-center gap-2">
                                    <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></span>
                                    Searching entire database...
                                  </span>
                                ) : searchResults !== null ? (
                                  groupedSearchResults.length > 0 ? (
                                    <>Found <strong>{searchTickerCount}</strong> {searchTickerCount === 1 ? 'idea' : 'ideas'} across <strong>{groupedSearchResults.length}</strong> {groupedSearchResults.length === 1 ? 'post' : 'posts'} matching "<strong>{searchQuery}</strong>"</>
                                  ) : (
                                    <>No results found for "<strong>{searchQuery}</strong>"</>
                                  )
                                ) : null}
                              </span>
                            </div>
                            <button
                              onClick={() => setSearchQuery('')}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Clear
                            </button>
                          </div>
                        )}
                        
                        {(() => {
                          // Track index for numbering
                          let cardIndex = 0;
                          
                          // Check if there are any expandable cards (threads, groups, or ideas lists with multiple items)
                          const hasExpandableCards = displayGroupedIdeas.some(item => 
                            (item.type === 'thread' && item.childIdeas?.length > 1) ||
                            (item.type === 'group' && item.ideas?.length > 1) ||
                            (item.type === 'standalone' && item.idea?.is_ticker_list && (item.idea.all_tickers || item.idea.tickers || []).length > 1)
                          );
                            
                          return (
                            <>
                              {displayGroupedIdeas.map((item, groupIndex) => {
                            // DEBUG: Log all items being rendered
                            console.log('[RENDER ITEM]', groupIndex, 'type:', item.type, 'isIdeasList:', item.idea?.is_ticker_list, 'title:', item.idea?.title?.substring(0, 30));
                            
                            // Handle THREAD/MULTI-IDEA items - stacked cards with colored shading
                            // Green = thread children, Yellow = multi-idea post children
                            if (item.type === 'thread') {
                              const { parentIdea, childIdeas, childColor = 'green' } = item;
                              
                              // FIX: If only ONE child idea, render as standalone card (not multi-idea)
                              // This prevents the "Multi-Idea Post (1/1)" bug
                              if (childIdeas.length === 1) {
                                const idea = childIdeas[0];
                                const currentIndex = cardIndex;
                                cardIndex++;
                                return (
                                  <div key={`standalone-thread-${idea.id}`} data-idea-id={idea.id} className="relative mb-4">
                                    {renderIdeaCard(idea, currentIndex, false, null, false, false)}
                                  </div>
                                );
                              }
                              
                              const isGroupExpanded = expandedCardGroups.has(parentIdea.id);
                              const stackCount = childIdeas.length;
                              const startIndex = cardIndex;
                              cardIndex += stackCount;
                              
                              const labelText = childColor === 'green' ? 'Multi-Idea Thread' : 'Multi-Idea Post'; // DISABLED Quoted Tweet: (childColor === 'yellow' ? 'Multi-Idea Post' : 'Quoted Tweet')
                              // X/Twitter source - use X branding (black with white X logo)
                              const isXSource = parentIdea.source?.startsWith('x_') || parentIdea.platform === 'x';
                              const isSubstackSource = parentIdea.source?.includes('substack') || parentIdea.platform === 'substack';
                              const isRedditSource = parentIdea.source?.includes('reddit') || parentIdea.platform === 'reddit';
                              const isInvestorLetter = parentIdea.source?.includes('investor_letter') || parentIdea.classification === 'investor_letter';
                              
                              // Source-based border color for expanded state
                              const borderColorClass = isXSource ? 'border-black' 
                                : isSubstackSource ? 'border-orange-500' 
                                : isRedditSource ? 'border-orange-600' 
                                : isInvestorLetter ? 'border-green-500' 
                                : 'border-purple-400';
                              const labelTextColor = isDarkMode ? 'text-blue-300' : 'text-blue-700';
                              
                              // Neutral colors for stacked card shadows
                              const stackBgColor = isDarkMode ? 'bg-slate-600' : 'bg-slate-100';
                              const stackBorderColor = isDarkMode ? 'border-slate-500' : 'border-slate-300';
                              
                              const badgeLabel = `+${stackCount - 1} more`;
                              const collapseLabel = `Collapse ${stackCount} cards`;
                              
                              return (
                                <div key={`thread-${parentIdea.id}`} data-idea-id={parentIdea.id} className="relative mb-4">
                                  {/* Stacked cards container */}
                                  <div className="relative">
                                    {/* Background cards (stacking effect) - only show when collapsed */}
                                    {!isGroupExpanded && stackCount > 1 && (
                                      <>
                                        {/* Third card shadow (if 3+ cards) */}
                                        {stackCount >= 3 && (
                                          <div 
                                            className={`absolute top-3 left-3 right-0 h-full rounded-lg border-2 ${stackBorderColor} ${stackBgColor} shadow-sm`}
                                            style={{ zIndex: 1 }}
                                          />
                                        )}
                                        {/* Second card shadow */}
                                        <div 
                                          className={`absolute top-1.5 left-1.5 right-0 h-full rounded-lg border-2 ${stackBorderColor} ${stackBgColor} shadow-sm`}
                                          style={{ zIndex: 2 }}
                                        />
                                      </>
                                    )}
                                    
                                    {/* Main card or expanded cards */}
                                    {isGroupExpanded ? (
                                      // Expanded: show all cards with colored line
                                      <div className={`border-l-4 ${borderColorClass} pl-3 space-y-3`}>
                                        {childIdeas.map((childIdea, childIndex) => {
                                          const currentIndex = startIndex + childIndex;
                                          // Get the primary ticker for this child idea
                                          const childTicker = childIdea.tickers?.[0]?.ticker || childIdea.tickers?.[0] || childIdea.primary_ticker;
                                          return (
                                            <div key={childIdea.id} className="relative" data-child-idea-id={childIdea.id} data-child-ticker={childTicker}>
                                              {/* Nested card indicator - text only (no bubble) with source branding */}
                                              <div className={`flex items-center gap-2 mb-2 font-semibold text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                {isXSource && (
                                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                  </svg>
                                                )}
                                                {!isXSource && (
                                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                  </svg>
                                                )}
                                                <span>{labelText} ({childIndex + 1}/{stackCount})</span>
                                              </div>
                                              {/* No background shading - pass false for childColor */}
                                              {renderIdeaCard(childIdea, currentIndex, false, null, false, false)}
                                            </div>
                                          );
                                        })}
                                        {/* Collapse button */}
                                        <button
                                          onClick={() => toggleCardGroup(parentIdea.id)}
                                          className={`w-full py-2 text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'} hover:underline`}
                                        >
                                          ▲ {collapseLabel}
                                        </button>
                                      </div>
                                    ) : (
                                      // Collapsed: show first card with stack effect (no shading)
                                      <div className="relative" style={{ zIndex: 3 }}>
                                        <div className="relative">
                                          {renderIdeaCard(childIdeas[0], startIndex, true, parentIdea.id, true, false, false, stackCount)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Handle grouped cards (stacked effect)
                            if (item.type === 'group') {
                              const { baseId, ideas, primaryIdea } = item;
                              
                              // Filter out quoted tweets from the group
                              const filteredGroupIdeas = ideas.filter(i => i.source !== 'x_bookmark_quoted');
                              if (filteredGroupIdeas.length === 0) return null;
                              
                              // If only one card left after filtering, render as standalone with data-idea-id wrapper
                              if (filteredGroupIdeas.length === 1) {
                                const idea = filteredGroupIdeas[0];
                                const currentIndex = cardIndex;
                                cardIndex++;
                                return (
                                  <div key={`standalone-${idea.id}`} data-idea-id={idea.id} className="relative mb-4">
                                    {renderIdeaCard(idea, currentIndex, false, null, false, false)}
                                  </div>
                                );
                              }
                              
                              const isGroupExpanded = expandedCardGroups.has(baseId);
                              const stackCount = filteredGroupIdeas.length;
                              const startIndex = cardIndex;
                              cardIndex += stackCount;
                              
                              // Check source for branding
                              const isXSource = primaryIdea?.source?.startsWith('x_') || primaryIdea?.platform === 'x' || filteredGroupIdeas[0]?.source?.startsWith('x_');
                              const isSubstackSource = primaryIdea?.source?.includes('substack') || filteredGroupIdeas[0]?.source?.includes('substack');
                              const isRedditSource = primaryIdea?.source?.includes('reddit') || filteredGroupIdeas[0]?.source?.includes('reddit');
                              const isInvestorLetter = primaryIdea?.source?.includes('investor_letter') || filteredGroupIdeas[0]?.classification === 'investor_letter';
                              
                              // Source-based border color for expanded state
                              const groupBorderColor = isXSource ? 'border-black' 
                                : isSubstackSource ? 'border-orange-500' 
                                : isRedditSource ? 'border-orange-600' 
                                : isInvestorLetter ? 'border-green-500' 
                                : 'border-purple-400';
                              
                              // Check if this is just a quoted tweet (same ticker, not multiple tickers)
                              const uniqueTickers = [...new Set(filteredGroupIdeas.flatMap(i => i.tickers || []))];
                              const isJustQuotedTweet = uniqueTickers.length === 1 && filteredGroupIdeas.some(i => i.source === 'x_bookmark_quoted');
                              
                              // Choose appropriate label - based on card count
                              const badgeLabel = `+${stackCount - 1} more`;
                              const collapseLabel = `Collapse ${stackCount} cards`;
                              
                              return (
                                <div key={`group-${baseId}`} data-idea-id={primaryIdea?.id || filteredGroupIdeas[0]?.id} className="relative mb-4">
                                  {/* Stacked cards container */}
                                  <div className="relative">
                                    {/* Background cards (stacking effect) - only show when collapsed */}
                                    {!isGroupExpanded && stackCount > 1 && (
                                      <>
                                        {/* Third card shadow (if 3+ cards) */}
                                        {stackCount >= 3 && (
                                          <div 
                                            className={`absolute top-3 left-3 right-0 h-full rounded-lg border-2 border-indigo-200 ${isDarkMode ? 'bg-slate-600' : 'bg-indigo-50'} shadow-sm`}
                                            style={{ zIndex: 1 }}
                                          />
                                        )}
                                        {/* Second card shadow */}
                                        <div 
                                          className={`absolute top-1.5 left-1.5 right-0 h-full rounded-lg border-2 border-indigo-300 ${isDarkMode ? 'bg-slate-650' : 'bg-indigo-100'} shadow-sm`}
                                          style={{ zIndex: 2 }}
                                        />
                                      </>
                                    )}
                                    
                                    {/* Main card or expanded cards */}
                                    {isGroupExpanded ? (
                                      // Expanded: show all cards in the group with source-branded bar
                                      <div className={`border-l-4 ${groupBorderColor} pl-3 space-y-3`}>
                                        {filteredGroupIdeas.map((bookmark, ideaIndex) => {
                                          const currentIndex = startIndex + ideaIndex;
                                          return (
                                            <div key={bookmark.id} className="relative">
                                              {/* Label between cards - text only (no bubble) with source branding */}
                                              <div className={`flex items-center gap-2 mb-2 font-semibold text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                {isXSource && (
                                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                  </svg>
                                                )}
                                                {!isXSource && (
                                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                  </svg>
                                                )}
                                                <span>{filteredGroupIdeas.some(i => i.source === 'x_bookmark_thread_child' || i.source === 'x_bookmark_thread_summary') ? 'Multi-Idea Thread' : 'Multi-Idea Post'} ({ideaIndex + 1}/{stackCount})</span>
                                              </div>
                                              {renderIdeaCard(bookmark, currentIndex, true, baseId, ideaIndex === 0)}
                                            </div>
                                          );
                                        })}
                                        {/* Collapse button */}
                                        <button
                                          onClick={() => toggleCardGroup(baseId)}
                                          className={`w-full py-2 text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'} hover:underline`}
                                        >
                                          ▲ {collapseLabel}
                                        </button>
                                      </div>
                                    ) : (
                                      // Collapsed: show primary card with stack indicator
                                      <div 
                                        className="relative cursor-pointer"
                                        style={{ zIndex: 3 }}
                                        onClick={() => {
                                          // Get all tickers from filtered group ideas
                                          const allTickers = filteredGroupIdeas.flatMap(idea => idea.tickers || []);
                                          toggleCardGroup(baseId, allTickers);
                                        }}
                                      >
                                        <div className="relative">
                                          {renderIdeaCard(filteredGroupIdeas[0], startIndex, true, baseId, true, false, false, stackCount)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Handle IDEAS LIST cards - stacked card with compact ticker rows
                            if (item.type === 'standalone' && item.idea && (item.idea.is_ticker_list || item.idea.title === "Ideas List")) {
                              console.log('[IDEAS LIST BLOCK ENTERED]', item.idea.id, 'is_ticker_list:', item.idea.is_ticker_list, 'title:', item.idea.title);
                              const bookmark = item.idea;
                              const currentIndex = cardIndex;
                              cardIndex++;
                              
                              const tickersList = bookmark.all_tickers || bookmark.tickers || [];
                              const tickerCount = tickersList.length;
                              const isIdeasListExpanded = expandedCardGroups.has(bookmark.id);
                              
                              // Source-based border color for expanded state
                              const isXSource = bookmark.source?.startsWith('x_') || bookmark.platform === 'x';
                              const isSubstackSource = bookmark.source?.includes('substack') || bookmark.platform === 'substack';
                              const isRedditSource = bookmark.source?.includes('reddit') || bookmark.platform === 'reddit';
                              const isInvestorLetter = bookmark.source?.includes('investor_letter') || bookmark.classification === 'investor_letter';
                              
                              const ideasListBorderColor = isXSource ? 'border-black' 
                                : isSubstackSource ? 'border-orange-500' 
                                : isRedditSource ? 'border-orange-600' 
                                : isInvestorLetter ? 'border-green-500' 
                                : 'border-purple-400';
                              
                              // Stacked card styling - matches thread/multi-idea pattern
                              const stackBgColor = isDarkMode ? 'bg-slate-600' : 'bg-slate-100';
                              const stackBorderColor = isDarkMode ? 'border-slate-500' : 'border-slate-300';
                              const labelTextColor = isDarkMode ? 'text-blue-300' : 'text-blue-700';
                              
                              return (
                                <div key={`ideas-list-${bookmark.id}`} data-idea-id={bookmark.id} className="relative mb-4">
                                  <div className="relative">
                                    {/* Background cards (stacking effect) - only show when collapsed */}
                                    {!isIdeasListExpanded && tickerCount > 1 && (
                                      <>
                                        {tickerCount >= 3 && (
                                          <div 
                                            className={`absolute top-3 left-3 right-0 h-full rounded-lg border-2 ${stackBorderColor} ${stackBgColor} shadow-sm`}
                                            style={{ zIndex: 1 }}
                                          />
                                        )}
                                        <div 
                                          className={`absolute top-1.5 left-1.5 right-0 h-full rounded-lg border-2 ${stackBorderColor} ${stackBgColor} shadow-sm`}
                                          style={{ zIndex: 2 }}
                                        />
                                      </>
                                    )}
                                    
                                    {isIdeasListExpanded ? (
                                      // Expanded: Source-branded bar wraps everything (main card + ticker rows)
                                      <div className={`border-l-4 ${ideasListBorderColor} pl-3 space-y-3`}>
                                        {/* Main Ideas List card */}
                                        <div>
                                          {renderIdeaCard(bookmark, currentIndex, false, null, false, false)}
                                        </div>
                                        
                                        {/* Separator between main card and ticker rows */}
                                        <div className={`border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'} my-2`}></div>
                                        
                                        {/* Categorized ticker display OR flat list */}
                                        {bookmark.categorized_tickers && bookmark.categorized_tickers.length > 0 ? (
                                          // Render tickers grouped by category - TABLE FORMAT
                                          <div className={`rounded-lg overflow-hidden border ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                            {bookmark.categorized_tickers.map((category, catIdx) => (
                                              <div key={catIdx}>
                                                {/* Category header */}
                                                <div className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 border-b ${isDarkMode ? 'bg-[#141c2e] text-slate-200 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                                  <span className="text-lg">{category.emoji}</span>
                                                  <span>{category.category}</span>
                                                  <span className={`text-xs font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    ({category.tickers?.length || 0})
                                                  </span>
                                                </div>
                                                
                                                {/* Table wrapper */}
                                                <div className="overflow-x-auto relative">
                                                  <table className="w-full text-sm">
                                                    <thead className={`text-xs uppercase tracking-wide ${isDarkMode ? 'bg-[#141c2e] text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                                                      <tr>
                                                        <th 
                                                          className={`px-1 py-1 text-left sticky left-0 cursor-pointer hover:text-orange-500 ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'ticker', direction: prev.key === 'ticker' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center">
                                                            Ticker
                                                            {ideasListSortConfig.key === 'ticker' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th 
                                                          className="px-1 py-1 text-center cursor-pointer hover:text-orange-500"
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'market_cap', direction: prev.key === 'market_cap' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center justify-center">
                                                            MCap
                                                            {ideasListSortConfig.key === 'market_cap' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th 
                                                          className="px-1 py-1 text-center cursor-pointer hover:text-orange-500"
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'enterprise_value', direction: prev.key === 'enterprise_value' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center justify-center">
                                                            EV
                                                            {ideasListSortConfig.key === 'enterprise_value' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th 
                                                          className="px-1 py-1 text-center cursor-pointer hover:text-orange-500"
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'forward_pe', direction: prev.key === 'forward_pe' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center justify-center">
                                                            P/E
                                                            {ideasListSortConfig.key === 'forward_pe' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th 
                                                          className="px-1 py-1 text-center cursor-pointer hover:text-orange-500"
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'ev_to_sales', direction: prev.key === 'ev_to_sales' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center justify-center">
                                                            EV/S
                                                            {ideasListSortConfig.key === 'ev_to_sales' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th 
                                                          className="px-1 py-1 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                                                          onClick={() => setIdeasListSortConfig(prev => ({ key: 'dividend_yield', direction: prev.key === 'dividend_yield' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                                                        >
                                                          <div className="flex items-center justify-center">
                                                            Div
                                                            {ideasListSortConfig.key === 'dividend_yield' 
                                                              ? (ideasListSortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                                                              : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                                                            }
                                                          </div>
                                                        </th>
                                                        <th className="px-0.5 py-1 text-right hidden sm:table-cell"></th>
                                                      </tr>
                                                    </thead>
                                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-600' : 'divide-slate-100'}`}>
                                                      {sortIdeasListTickers(category.tickers || [], ideasListSortConfig, tickerMetricsCache).map((tickerName, idx) => {
                                                        const tickerMetrics = tickerMetricsCache[tickerName?.toUpperCase()];
                                                        const rowKey = `${bookmark.id}-${tickerName}`;
                                                        const isRowExpanded = expandedIdeasRowKey === rowKey;
                                                        
                                                        return (
                                                          <React.Fragment key={idx}>
                                                            <tr 
                                                              className={`cursor-pointer sm:cursor-default ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-white hover:bg-slate-50'} ${isRowExpanded ? (isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-100') : ''}`}
                                                              onClick={() => setExpandedIdeasRowKey(prev => prev === rowKey ? null : rowKey)}
                                                            >
                                                              <td className={`px-1 py-1 sticky left-0 ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} ${isRowExpanded ? (isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-100') : ''}`}>
                                                                <span className={`font-mono font-bold text-sm sm:text-[11px] ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                                                                  ${tickerName}
                                                                </span>
                                                              </td>
                                                              <td className={`px-1 py-1 text-center font-mono text-xs sm:text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                {tickerMetrics?.market_cap ? formatFinancialValue(tickerMetrics.market_cap) : '-'}
                                                              </td>
                                                              <td className={`px-1 py-1 text-center font-mono text-xs sm:text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                {tickerMetrics?.enterprise_value ? formatFinancialValue(tickerMetrics.enterprise_value) : '-'}
                                                              </td>
                                                              <td className={`px-1 py-1 text-center font-mono text-xs sm:text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                {tickerMetrics?.forward_pe ? formatRatio(tickerMetrics.forward_pe) : '-'}
                                                              </td>
                                                              <td className={`px-1 py-1 text-center font-mono text-xs sm:text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                {tickerMetrics?.ev_to_sales ? formatRatio(tickerMetrics.ev_to_sales) : '-'}
                                                              </td>
                                                              <td className={`px-1 py-1 text-center font-mono text-[10px] hidden sm:table-cell ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                {tickerMetrics?.dividend_yield ? `${formatRatio(tickerMetrics.dividend_yield)}%` : '-'}
                                                              </td>
                                                              {/* Expand arrow indicator */}
                                                              <td className="px-1 py-1 text-center" onClick={(e) => e.stopPropagation()}>
                                                                <svg 
                                                                  className={`w-4 h-4 transform transition-transform ${isRowExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} 
                                                                  fill="none" 
                                                                  stroke="currentColor" 
                                                                  viewBox="0 0 24 24"
                                                                >
                                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                              </td>
                                                            </tr>
                                                            
                                                            {/* Expandable action buttons row - shown for both mobile and desktop */}
                                                            {isRowExpanded && (
                                                              <tr>
                                                                <td colSpan={7} className="p-0">
                                                                  <div className={`px-3 py-3 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                                                      {/* Original button */}
                                                                      <button 
                                                                        onClick={(e) => { 
                                                                          e.stopPropagation();
                                                                          const originalText = bookmark.original_content || 
                                                                            bookmark.twitterMetadata?.text || 
                                                                            bookmark.tweet_content ||
                                                                            bookmark.metadata?.original_email_content ||
                                                                            bookmark.content || 
                                                                            bookmark.title || 
                                                                            `Investment idea for $${tickerName}`;
                                                                          setSelectedOriginalContent({
                                                                            content: originalText,
                                                                            source: bookmark.source || 'Curation',
                                                                            title: bookmark.title || `$${tickerName}`,
                                                                            url: bookmark.source_url || bookmark.url
                                                                          });
                                                                          setShowOriginalContentModal(true);
                                                                        }} 
                                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-blue-400 text-blue-300 hover:bg-blue-500 hover:text-white hover:border-blue-500' : 'border-blue-400 text-blue-600 hover:bg-blue-500 hover:text-white'}`}
                                                                      >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                                                        <span>Original</span>
                                                                      </button>
                                                                      {/* File button */}
                                                                      <button 
                                                                        onClick={(e) => { 
                                                                          e.stopPropagation(); 
                                                                          setSelectedTickerForScheduling({ bookmarkId: bookmark.id, ticker: tickerName, content: `Research $${tickerName}` });
                                                                          setShowDocumentModal(true);
                                                                          checkSECAvailability(tickerName);
                                                                        }} 
                                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-slate-400 text-slate-300 hover:bg-slate-500 hover:text-white hover:border-slate-500' : 'border-slate-400 text-slate-600 hover:bg-slate-500 hover:text-white'}`}
                                                                      >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                                        <span>File</span>
                                                                      </button>
                                                                      {/* Folder button */}
                                                                      <button 
                                                                        onClick={(e) => { 
                                                                          e.stopPropagation(); 
                                                                          openCalendarModal({ bookmarkId: bookmark.id, ticker: tickerName, content: `Review $${tickerName}` });
                                                                        }} 
                                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-purple-400 text-purple-300 hover:bg-purple-500 hover:text-white hover:border-purple-500' : 'border-purple-400 text-purple-600 hover:bg-purple-500 hover:text-white'}`}
                                                                      >
                                                                        <FolderIcon className="w-3.5 h-3.5" />
                                                                        <span>Folder</span>
                                                                      </button>
                                                                      {/* Share button */}
                                                                      <button 
                                                                        onClick={(e) => { 
                                                                          e.stopPropagation(); 
                                                                          setSelectedTickerForScheduling({ 
                                                                            ticker: tickerName, 
                                                                            bookmarkId: bookmark.id, 
                                                                            content: bookmark.content || `Investment idea for $${tickerName}`,
                                                                            shareType: 'idea'
                                                                          });
                                                                          setShowShareModal(true);
                                                                        }} 
                                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-yellow-400 text-yellow-300 hover:bg-yellow-500 hover:text-white hover:border-yellow-500' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-500 hover:text-white'}`}
                                                                      >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                                        <span>Share</span>
                                                                      </button>
                                                                      {/* Tickers button */}
                                                                      <button 
                                                                        onClick={async (e) => { 
                                                                          e.stopPropagation();
                                                                          const sectorToUse = bookmark.primary_sector && bookmark.primary_sector !== 'X/Twitter' ? bookmark.primary_sector : null;
                                                                          const industryInfo = sectorToUse ? { sector: sectorToUse } : null;
                                                                          addToWatchlist(tickerName, industryInfo);
                                                                        }} 
                                                                        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-green-400 text-green-300 hover:bg-green-500 hover:text-white hover:border-green-500' : 'border-green-400 text-green-600 hover:bg-green-500 hover:text-white'}`}
                                                                      >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                                        <span>Ticker</span>
                                                                      </button>
                                                                    </div>
                                                                  </div>
                                                                </td>
                                                              </tr>
                                                            )}
                                                          </React.Fragment>
                                                        );
                                                      })}
                                                    </tbody>
                                                </table>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          // Flat ticker list (no categories)
                                          <>
                                            {/* Ticker rows section header */}
                                            <div className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              Individual Ideas ({tickerCount})
                                            </div>
                                            
                                            {/* Compact ticker rows */}
                                            {tickersList.map((tickerItem, idx) => {
                                              const tickerName = typeof tickerItem === 'string' ? tickerItem : tickerItem.ticker;
                                              const tickerMetrics = tickerMetricsCache[tickerName?.toUpperCase()];
                                              const rowKey = `${bookmark.id}-flat-${tickerName}`;
                                              const isRowExpanded = expandedIdeasRowKey === rowKey;
                                              
                                              return (
                                                <div key={idx}>
                                                  {/* Main row - clickable on mobile */}
                                                  <div 
                                                    className={`flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer sm:cursor-default ${isDarkMode ? 'bg-slate-700 hover:bg-slate-650' : 'bg-white hover:bg-slate-50'} border ${isDarkMode ? 'border-slate-600' : 'border-slate-200'} ${isRowExpanded ? (isDarkMode ? 'rounded-b-none border-b-0' : 'rounded-b-none border-b-0') : ''}`}
                                                    onClick={() => setExpandedIdeasRowKey(prev => prev === rowKey ? null : rowKey)}
                                                  >
                                                    {/* Ticker + Metrics */}
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                      <span className={`font-mono font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                                                        ${tickerName}
                                                      </span>
                                                      <div className={`hidden sm:flex items-center gap-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        {tickerMetrics?.enterprise_value && (
                                                          <span>EV: <span className="font-mono">{formatFinancialValue(tickerMetrics.enterprise_value)}</span></span>
                                                        )}
                                                        {tickerMetrics?.forward_pe && (
                                                          <span>P/E: <span className="font-mono">{formatRatio(tickerMetrics.forward_pe)}</span></span>
                                                        )}
                                                        {tickerMetrics?.market_cap && (
                                                          <span>MCap: <span className="font-mono">{formatFinancialValue(tickerMetrics.market_cap)}</span></span>
                                                        )}
                                                      </div>
                                                    </div>
                                                    
                                                    {/* Desktop action buttons - hidden on mobile */}
                                                    <div className="hidden sm:flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setSelectedTickerForScheduling({ bookmarkId: bookmark.id, ticker: tickerName, content: `Research $${tickerName}` });
                                                          setShowDocumentModal(true);
                                                          checkSECAvailability(tickerName);
                                                        }}
                                                        className={`p-1.5 rounded ${isDarkMode ? 'hover:bg-slate-600 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                                                        title={`Documents for $${tickerName}`}
                                                      >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                        </svg>
                                                      </button>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          openCalendarModal({ bookmarkId: bookmark.id, ticker: tickerName, content: `Review $${tickerName}` });
                                                        }}
                                                        className={`p-1.5 rounded ${isDarkMode ? 'hover:bg-slate-600 text-purple-400' : 'hover:bg-purple-100 text-purple-600'}`}
                                                        title={`Schedule reminder for $${tickerName}`}
                                                      >
                                                        <CalendarIcon className="w-4 h-4" />
                                                      </button>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setSelectedTickerForScheduling({ 
                                                            ticker: tickerName, 
                                                            bookmarkId: bookmark.id, 
                                                            content: bookmark.content || `Investment idea for $${tickerName}`,
                                                            shareType: 'idea'
                                                          });
                                                          setShowShareModal(true);
                                                        }}
                                                        className={`p-1.5 rounded ${isDarkMode ? 'hover:bg-slate-600 text-orange-400' : 'hover:bg-orange-100 text-orange-600'}`}
                                                        title={`Share $${tickerName}`}
                                                      >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                        </svg>
                                                      </button>
                                                    </div>
                                                    
                                                    {/* Mobile tap indicator */}
                                                    <div className={`sm:hidden text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                      {isRowExpanded ? '▲' : '▼'}
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Mobile dropdown with action buttons - full width */}
                                                  <div className={`sm:hidden overflow-hidden transition-all duration-300 ease-in-out ${isRowExpanded ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'}`}>
                                                    <div className={`px-2 py-2 rounded-b-lg border border-t-0 ${isDarkMode ? 'bg-[#141c2e] border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                                      <div className="flex items-center justify-around">
                                                        {/* Original button */}
                                                        <button 
                                                          onClick={(e) => { 
                                                            e.stopPropagation();
                                                            setSelectedOriginalContent({
                                                              content: bookmark.content || bookmark.title || `Investment idea for $${tickerName}`,
                                                              source: bookmark.source || 'Curation',
                                                              title: bookmark.title || `$${tickerName}`,
                                                              url: bookmark.source_url || bookmark.url
                                                            });
                                                            setShowOriginalContentModal(true);
                                                          }} 
                                                          className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                                        >
                                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                                          <span className="text-[10px] sm:text-xs">Open</span>
                                                        </button>
                                                        {/* Docs button */}
                                                        <button 
                                                          onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            setSelectedTickerForScheduling({ bookmarkId: bookmark.id, ticker: tickerName, content: `Research $${tickerName}` });
                                                            setShowDocumentModal(true);
                                                            checkSECAvailability(tickerName);
                                                          }} 
                                                          className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                                                        >
                                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                          <span className="text-[10px] sm:text-xs">Docs</span>
                                                        </button>
                                                        {/* Schedule button */}
                                                        <button 
                                                          onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            openCalendarModal({ bookmarkId: bookmark.id, ticker: tickerName, content: `Review $${tickerName}` });
                                                          }} 
                                                          className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
                                                        >
                                                          <FolderIcon className="w-6 h-6" />
                                                          <span className="text-[10px] sm:text-xs">Folder</span>
                                                        </button>
                                                        {/* Share button */}
                                                        <button 
                                                          onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            setSelectedTickerForScheduling({ 
                                                              ticker: tickerName, 
                                                              bookmarkId: bookmark.id, 
                                                              content: bookmark.content || `Investment idea for $${tickerName}`,
                                                              shareType: 'idea'
                                                            });
                                                            setShowShareModal(true);
                                                          }} 
                                                          className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                                        >
                                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                          <span className="text-[10px] sm:text-xs">Share</span>
                                                        </button>
                                                        {/* Collection button */}
                                                        <button 
                                                          onClick={async (e) => { 
                                                            e.stopPropagation();
                                                            // Get sector info if available
                                                            const sectorToUse = bookmark.primary_sector && bookmark.primary_sector !== 'X/Twitter' ? bookmark.primary_sector : null;
                                                            const industryInfo = sectorToUse ? { sector: sectorToUse } : null;
                                                            addToWatchlist(tickerName, industryInfo);
                                                          }} 
                                                          className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                                        >
                                                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                          <span className="text-[10px] sm:text-xs">Tickers</span>
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </>
                                        )}
                                        
                                        {/* Collapse button */}
                                        <button
                                          onClick={() => toggleCardGroup(bookmark.id)}
                                          className="w-full py-2 text-sm font-medium text-blue-700 hover:underline"
                                        >
                                          Collapse {tickerCount} ideas
                                        </button>
                                      </div>
                                    ) : (
                                      // Collapsed: Show the Ideas List card
                                      <div className="relative" style={{ zIndex: 3 }}>
                                        {(() => { console.log('[IDEAS LIST COLLAPSED] Rendering collapsed card:', bookmark.id); return null; })()}
                                        {renderIdeaCard(bookmark, currentIndex, true, bookmark.id, true, false)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Handle standalone cards (regular rendering)
                            const bookmark = item.idea;
                            const currentIndex = cardIndex;
                            cardIndex++;
                            // Disable rollup view for search results (searchResults !== null means we're searching)
                            const isSearchResult = searchResults !== null;
                            return (
                              <div key={`standalone-${bookmark.id}`} data-idea-id={bookmark.id} className="relative mb-4">
                                {renderIdeaCard(bookmark, currentIndex, false, null, false, false, isSearchResult)}
                              </div>
                            );
                          })}
                            </>
                          );
                          
                        })()}
                      
                      {/* Load More Button - Manual Fallback for Infinite Scroll - Only show for My Ideas view */}
                      {ideaView === 'my' && (hasMoreIdeas || bookmarks.length < totalCards) && !loadingMore && totalCards > 0 && (
                        <div className="flex justify-center py-6">
                          <button
                            onClick={() => {
                              console.log('🔘 Load More button clicked - current page ref:', currentPageRef.current, 'hasMore:', hasMoreIdeas, 'displayed:', bookmarks.length, 'totalCards:', totalCards);
                              loadMoreIdeas();
                            }}
                            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                              isDarkMode
                                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            Load More Ideas ({bookmarks.length} of {totalCards} cards)
                          </button>
                        </div>
                      )}
                      
                      {/* Loading spinner - only show for My Ideas view pagination */}
                      {ideaView === 'my' && loadingMore && (
                        <div className="flex justify-center py-6">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                      
                      {/* "All loaded" message - show appropriate count based on current view */}
                      {ideaView === 'my' && !hasMoreIdeas && bookmarks.length > 0 && bookmarks.length >= totalCards && (
                        <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {myIdeasCount} ideas across {totalCards} posts
                        </div>
                      )}
                      
                      {/* Received view footer - show count of received items */}
                      {ideaView === 'received' && filteredIdeasForView.length > 0 && (
                        <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {filteredIdeasForView.length} received {filteredIdeasForView.length === 1 ? 'idea' : 'ideas'}
                        </div>
                      )}
                      </>
                      ) : (
                        // Source View - Grouped by source with expandable nested ideas using memoized data
                        (() => {
                          return groupedSourcesForView.map((source, index) => {
                            const isExpanded = expandedSources.has(source.sourceKey) || globalExpanded;
                            
                            const toggleSource = () => {
                              const newExpanded = new Set(expandedSources);
                              if (isExpanded) {
                                newExpanded.delete(source.sourceKey);
                              } else {
                                newExpanded.add(source.sourceKey);
                              }
                              setExpandedSources(newExpanded);
                            };
                            
                            return (
                              <Card key={`source-${source.sourceKey}`} className={`transition-all duration-200 hover:shadow-lg ${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white border-slate-200'}`}>
                                <CardContent className="p-0">
                                  {/* Source Header - Clickable to expand/collapse */}
                                  <button
                                    onClick={toggleSource}
                                    className={`w-full p-4 text-left transition-all duration-200 ${
                                      isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        {/* Company Logo/Icon */}
                                        <div className="w-10 h-10 rounded-lg bg-white shadow-sm border border-slate-200 flex items-center justify-center flex-shrink-0">
                                          {source.sourceIcon}
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                              {source.sourceTitle}
                                            </h4>
                                            {source.domain && (
                                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 font-mono">
                                                {source.domain}
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                            <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-700 border-slate-200">
                                              {source.ideas.length} idea{source.ideas.length !== 1 ? 's' : ''}
                                            </Badge>
                                            {source.sourceType === 'investor_letter' && (
                                              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                                Investor Letter
                                              </Badge>
                                            )}
                                            {source.sourceType === 'twitter' && (
                                              <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200">
                                                Tweet
                                              </Badge>
                                            )}
                                            {source.author && (
                                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                @{source.author}
                                              </span>
                                            )}
                                          </div>
                                          {/* Date under the author/badges */}
                                          <div className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            {new Date(source.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{source.viaText}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <svg 
                                          className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`} 
                                          fill="none" 
                                          stroke="currentColor" 
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                        </svg>
                                      </div>
                                    </div>
                                  </button>
                                  
                                  {/* Nested Ideas - Show when expanded */}
                                  {isExpanded && (
                                    <div className={`border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                                      <div className="p-4 space-y-4">
                                        {source.ideas.map((idea, ideaIndex) => {
                                          // Calculate same age/styling as Ideas View
                                          const daysOld = calculateDaysOld(idea.created_at);
                                          const ageCategory = getAgeCategory(idea.created_at);
                                          const contentBasedId = `nested-content-${idea.content?.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '')}`;
                                          
                                          let ageBadgeColor = 'bg-gray-100 text-gray-800';
                                          let cardBorderColor = 'border-gray-200';
                                          
                                          if (ageCategory === 'New') {
                                            ageBadgeColor = 'bg-green-100 text-green-800';
                                            cardBorderColor = 'border-green-500 border-2';
                                          } else if (ageCategory === 'Recent') {
                                            ageBadgeColor = 'bg-orange-100 text-orange-800';
                                            cardBorderColor = 'border-orange-500 border-2';
                                          } else if (ageCategory === 'Older') {
                                            ageBadgeColor = 'bg-red-100 text-red-800';
                                            cardBorderColor = 'border-red-500 border-2';
                                          }
                                          
                                          return (
                                            <Card key={`nested-${idea.id}-${ideaIndex}`} className={`${cardBorderColor} ${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white'} hover:shadow-lg transition-all duration-200`}>
                                              <CardContent className="p-4 sm:p-6">
                                                {/* Header with Number, Ticker, and Age - EXACT COPY from Ideas View */}
                                                <div className="flex items-start justify-between mb-4">
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-2">
                                                      {/* Number Badge */}
                                                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-slate-700 to-slate-800 text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
                                                        {ideaIndex + 1}
                                                      </div>
                                                      
                                                      {/* Company Name & Ticker Display - EXACT COPY */}
                                                      <div className={`${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                                        <div className="mb-1">
                                                          {/* Special display for Ideas List cards */}
                                                          {idea.is_ticker_list || idea.title === "Ideas List" ? (
                                                            <>
                                                              <div className={`font-bold text-lg sm:text-xl leading-tight`}>
                                                                Curation
                                                              </div>
                                                              <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mt-1`}>
                                                                {idea.all_tickers?.length || idea.tickers?.length || 0} ideas referenced
                                                              </div>
                                                              {/* List all tickers */}
                                                              <div className="flex flex-wrap gap-1.5 mt-3">
                                                                {(idea.all_tickers || idea.tickers || []).map((ticker, idx) => (
                                                                  <span key={idx} className={`px-2 py-1 text-sm font-mono font-semibold rounded ${isDarkMode ? 'bg-slate-700 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                                                                    {typeof ticker === 'string' ? ticker : ticker.ticker}
                                                                  </span>
                                                                ))}
                                                              </div>
                                                            </>
                                                          ) : (
                                                            <>
                                                              <div className={`font-bold text-lg sm:text-xl leading-tight`}>
                                                                {(() => {
                                                                  const companyName = idea.company_intelligence?.company_name || idea.company_name;
                                                                  const title = idea.title;
                                                                  
                                                                  // First try: Use company_name field directly
                                                                  if (companyName && !companyName.startsWith('Company with ticker')) {
                                                                    // Show company name + thesis from title
                                                                    const thesisMatch = title?.match(/\):\s*(.+)$/);
                                                                    const thesis = thesisMatch ? thesisMatch[1] : '';
                                                                    return thesis ? `${companyName}: ${thesis}` : companyName;
                                                                  }
                                                                  
                                                                  // Second try: Parse title format "$TICKER (Company Name): thesis"
                                                                  if (title) {
                                                                    const match = title.match(/^\$[\w.]+\s+\(([^)]+)\):\s*(.+)$/);
                                                                    if (match) {
                                                                      return `${match[1]}: ${match[2]}`;
                                                                    }
                                                                  }
                                                                  
                                                                  if (title?.includes('Investment Analysis')) {
                                                                    return title.replace(/ Investment Analysis.*$/, '');
                                                                  }
                                                                  
                                                                  return title || `${idea.tickers?.[0] || 'Unknown'} Company`;
                                                                })()}
                                                              </div>
                                                              <div className={`font-mono font-bold text-lg sm:text-xl tracking-wide ${isDarkMode ? 'text-blue-300' : 'text-blue-600'} mt-1`}>
                                                                ${idea.tickers?.[0] || 'N/A'}
                                                                {idea.company_intelligence?.exchange && idea.company_intelligence.exchange !== 'Unknown' && (
                                                                  <span className={`text-sm font-normal ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                                    • {idea.company_intelligence.exchange}
                                                                    {idea.company_intelligence.country && idea.company_intelligence.country !== 'Unknown' && (
                                                                      <span> • {idea.company_intelligence.country}</span>
                                                                    )}
                                                                  </span>
                                                                )}
                                                              </div>
                                                              {/* Ticker Badges for List-type Cards - [TICKER] format */}
                                                              {idea.supporting_tickers && idea.supporting_tickers.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                  {idea.supporting_tickers.map((ticker, idx) => (
                                                                    <span key={idx} className={`px-1.5 py-0.5 text-xs font-mono font-medium rounded ${isDarkMode ? 'bg-slate-700 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                                                                      [{typeof ticker === 'string' ? ticker : ticker.ticker}]
                                                                    </span>
                                                                  ))}
                                                                </div>
                                                              )}
                                                            </>
                                                          )}
                                                        </div>
                                                      </div>
                                                      
                                                      {/* Industry + Financial Metrics - All on One Line */}
                                                      {/* RECALL: Industry classification - Content-based fallback is available but hidden */}
                                                      {(() => {
                                                        // Use primary_sector from backend (Perplexity GICS lookup)
                                                        let displayCategory = idea.primary_sector;
                                                        
                                                        /* RECALL CODE: Content-based industry classification (currently disabled)
                                                        // Fallback to content-based classification if primary_sector not available
                                                        if (!displayCategory || displayCategory === 'Uncategorized' || displayCategory === 'General' || displayCategory === 'Email Content') {
                                                          const primaryTicker = idea.tickers && idea.tickers[0];
                                                          const contentForAnalysis = idea.summary || idea.content || '';
                                                          
                                                          if (primaryTicker && contentForAnalysis) {
                                                            displayCategory = categorizeTickerForDisplay(primaryTicker, contentForAnalysis);
                                                          }
                                                        }
                                                        */
                                                        
                                                        const hasIndustry = displayCategory && displayCategory !== 'Uncategorized' && displayCategory !== 'General' && displayCategory !== 'Email Content';
                                                        const hasFinancials = idea.primary_market_cap || idea.primary_enterprise_value || idea.pe_ratio;
                                                        
                                                        return (hasIndustry || hasFinancials) ? (
                                                          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} ml-11`}>
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                              {hasIndustry && (
                                                                <span className="font-medium">{displayCategory}</span>
                                                              )}
                                                              {hasIndustry && hasFinancials && <span className="text-slate-300">|</span>}
                                                              {idea.primary_market_cap && (
                                                                <span>MCap: <span className="font-mono">{formatFinancialValue(idea.primary_market_cap)}</span></span>
                                                              )}
                                                              {idea.primary_enterprise_value && (
                                                                <>
                                                                  {idea.primary_market_cap && <span className="text-slate-300">|</span>}
                                                                  <span>EV: <span className="font-mono">{formatFinancialValue(idea.primary_enterprise_value)}</span></span>
                                                                </>
                                                              )}
                                                              {idea.pe_ratio && (
                                                                <>
                                                                  {(idea.primary_market_cap || idea.primary_enterprise_value) && <span className="text-slate-300">|</span>}
                                                                  <span>P/E: <span className="font-mono">{formatRatio(idea.pe_ratio)}</span></span>
                                                                </>
                                                              )}
                                                            </div>
                                                          </div>
                                                        ) : null;
                                                      })()}
                                                    </div>
                                                    
                                                  </div>
                                                  
                                                </div>
                                                
                                                {/* Content - EXACT COPY from Ideas View */}
                                                <div className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                  <div className="flex flex-col gap-1 mb-2">
                                                    <div className="flex items-center gap-2">
                                                      {/* Smart Domain-Based Categorization with Real Logos */}
                                                      {(() => {
                                                        // Check if this is an investment letter by type, not name
                                                        const isInvestorLetter = idea.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                                        
                                                        // Special handling for Investor Letter - show proper document icon (matches dropdown)
                                                        if (isInvestorLetter) {
                                                          return (
                                                            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                            </svg>
                                                          );
                                                        }
                                                        
                                                        // Otherwise use normal domain-based categorization
                                                        const category = categorizByDomain(idea);
                                                        const domain = extractDomain(idea.url) || extractDomain(idea.from_address);
                                                        
                                                        // Render icon - prioritize real logos
                                                        if (category.svg) {
                                                          // Extract text-color from category.color (e.g., "bg-orange-50 text-orange-700" → "text-orange-700")
                                                          const textColor = category.color?.split(' ').find(c => c.startsWith('text-')) || '';
                                                          return (
                                                            <svg className={`w-4 h-4 ${textColor}`} fill="currentColor" viewBox="0 0 24 24">
                                                              <path d={category.svg} />
                                                            </svg>
                                          );
                                                        } else if (domain) {
                                                          // Use Google Favicon for real company logo
                                                          return (
                                                            <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} 
                                                                 alt={domain} 
                                                                 className="w-4 h-4 object-contain" />
                                                          );
                                                        } else {
                                                          // Default email icon
                                                          return (
                                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                                            </svg>
                                                          );
                                                        }
                                                      })()}
                                                      <span className={`font-medium text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                        {idea.source === 'email' || idea.source === 'ai_analysis' || idea.source === 'bookmarklet' ? 
                                                          (() => {
                                                            // Get base source name
                                                            const baseName = (idea.source_info?.ai_attribution?.original_source_name && 
                                                             idea.source_info.ai_attribution.original_source_name.toLowerCase() !== 'unknown' ? 
                                                             idea.source_info.ai_attribution.original_source_name : null) ||
                                                            // Priority 2: Clean up source_name - extract just the main source, remove "via" part
                                                            (idea.source_info?.source_name ? 
                                                              idea.source_info.source_name.split(' (via ')[0] || idea.source_info.source_name : null) ||
                                                            // Priority 3: Source address
                                                            idea.source_info?.source_address ||
                                                            // Priority 4: From name - clean up "via" part
                                                            (idea.from_name && idea.from_name !== 'Unknown' ? 
                                                              idea.from_name.split(' (via ')[0] || idea.from_name : null) ||
                                                            // Priority 5: Author cleanup
                                                            (idea.author && idea.author !== 'undefined' ? idea.author.replace('@', '') : null) ||
                                                              (() => {
                                                                const fromAddr = idea.from_address || '';
                                                                if (fromAddr.includes('@substack.com')) return 'Substack';
                                                                if (fromAddr.includes('@seeking')) return 'Seeking Alpha';
                                                                if (fromAddr.includes('@bloomberg')) return 'Bloomberg';
                                                                if (fromAddr.includes('@barrons')) return 'Barrons';
                                                                if (fromAddr.includes('@wsj')) return 'Wall Street Journal';
                                                                return null;
                                                              })() ||
                                                              (() => {
                                                                const emailSubject = idea.email_subject || idea.source_info?.source_subject || '';
                                                                const emailSubjectLower = emailSubject.toLowerCase();
                                                                if (emailSubjectLower.match(/\b(investor\s+letter|annual\s+letter|quarterly\s+letter|shareholder\s+letter)\b/)) return 'Investor Letter';
                                                                if (emailSubjectLower.match(/\b(letter\s+to\s+(shareholders|investors))\b/)) return 'Investor Letter';
                                                                if (emailSubjectLower.match(/\b[qQ]\d{1,2}\s*\d{2,4}\s+letter\b/)) return 'Investor Letter';
                                                                if (emailSubjectLower.match(/\b\d{4}\s+letter\b/)) return 'Investor Letter';
                                                                if (emailSubjectLower.match(/\bletter\s*$/)) return 'Investor Letter';
                                                                
                                                                if (idea.title) {
                                                                  const titleLower = idea.title.toLowerCase();
                                                                  if (titleLower.includes('value investors club')) return 'Value Investors Club';
                                                                  if (titleLower.includes('seeking alpha')) return 'Seeking Alpha';
                                                                  if (titleLower.includes('motley fool')) return 'The Motley Fool';
                                                                  if (titleLower.includes('bloomberg')) return 'Bloomberg';
                                                                  if (titleLower.includes('barrons')) return 'Barrons';
                                                                  if (titleLower.match(/\b(investor\s+letter|annual\s+letter|quarterly\s+letter|shareholder\s+letter)\b/)) return 'Investor Letter';
                                                                  if (titleLower.match(/\b(letter\s+to\s+(shareholders|investors))\b/)) return 'Investor Letter';
                                                                  if (titleLower.match(/\b[qQ]\d{1,2}\s*\d{2,4}\s+letter\b/)) return 'Investor Letter';
                                                                  if (titleLower.match(/\b\d{4}\s+letter\b/)) return 'Investor Letter';
                                                                  if (titleLower.match(/\bletter\s*$/)) return 'Investor Letter';
                                                                  if (titleLower.match(/\bnewsletter\b/)) return 'Newsletter';
                                                                }
                                                                return null;
                                                              })() ||
                                                              'Email';
                                                            
                                                            // If it's an investor letter, append "(Investor Letter)" to source name
                                                            const isInvestorLetter = idea.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                                            return isInvestorLetter && baseName ? `${baseName} (Investor Letter)` : baseName;
                                                          })() : 
                                                          `@${(idea.author || 'unknown').replace(/^@/, '')}`
                                                        }
                                                      </span>
                                                    </div>
                                                    <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                      {formatDate(idea.created_at)}
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Full Content - Show AI summary if available, otherwise show raw content */}
                                                  {console.log('IDEA RENDER:', {title: idea.title?.substring(0,30), hasSummary: !!idea.summary, hasContent: !!idea.content, summaryLen: idea.summary?.length, contentLen: idea.content?.length})}
                                                  <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                    {cleanContentForDisplay(idea.summary || idea.content, 1200, contentBasedId, expandedRows[contentBasedId] || globalExpanded, isAISummaryContent(idea))}
                                                  </div>
                                                  
                                                  {/* Expand/Collapse Button if needed */}
                                                  {isContentTruncated(idea.summary || idea.content, 1200, contentBasedId, idea) && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleBookmarkExpansion(contentBasedId);
                                                      }}
                                                      className={`mt-2 inline-flex items-center px-2 py-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 bg-slate-600 border-slate-500 hover:bg-slate-500' : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'} border rounded-md transition-all duration-200`}
                                                    >
                                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedRows[contentBasedId] || globalExpanded ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"} />
                                                      </svg>
                                                      {expandedRows[contentBasedId] || globalExpanded ? 'Show Less' : 'Show More'}
                                                    </button>
                                                  )}
                                                </div>
                                                
                                                {/* Action Buttons - EXACT COPY from Ideas View */}
                                                <div className="flex items-center justify-center gap-1">
                                                  {/* View Original Button - EXACT COPY */}
                                                  {(idea.content || idea.tweet_content || idea.original_content) && (
                                                    <button
                                                      onClick={async () => {
                                                        // Get full formatted tweet content (includes media URLs, links, etc.)
                                                        // For threads, prioritize the lead tweet (first in full_thread)
                                                        const twitterMeta = idea.twitterMetadata || idea.metadata?.twitter_content?.tweet_data || idea.metadata?.twitter_data?.[0] || null;
                                                        const hasThread = twitterMeta?.is_thread && twitterMeta?.full_thread?.length > 0;
                                                        const leadTweetText = hasThread ? twitterMeta.full_thread[0]?.text : null;
                                                        const fullTweetContent = leadTweetText ||
                                                                                idea.original_content ||  // Individual tweet text (best for nested cards)
                                                                                idea.twitterMetadata?.text ||  // Full thread text (for parent cards)
                                                                                idea.metadata?.twitter_content?.tweets?.[0]?.text ||
                                                                                idea.metadata?.original_email_content || 
                                                                                idea.metadata?.twitter_data?.[0]?.text || 
                                                                                idea.tweet_content ||
                                                                                '';
                                                        const shouldUseTweet = (idea.source === 'bookmark' || idea.source?.startsWith('x_bookmark') || idea.is_twitter_content) && fullTweetContent;
                                                        
                                                        setSelectedOriginalContent(null);
                                                        
                                                        let fullPdfData = idea.metadata?.pdf_data || null;
                                                        if (idea.metadata?.has_pdf_attachments && idea.id) {
                                                          try {
                                                            const response = await fetch(`${API_BASE_URL}/api/pdf-content/${idea.id}`);
                                                            if (response.ok) {
                                                              const pdfResponse = await response.json();
                                                              fullPdfData = pdfResponse.pdf_data;
                                                            }
                                                          } catch (error) {
                                                            console.error('Error fetching PDF content:', error);
                                                          }
                                                        }
                                                        
                                                        setTimeout(() => {
                                                          // Extract Twitter metadata if available - check multiple locations
                                                          const twitterData = idea.twitterMetadata ||  // Root level (newer format)
                                                                             idea.metadata?.twitter_content?.tweet_data || 
                                                                             idea.metadata?.twitter_data?.[0] ||
                                                                             null;
                                                          
                                                          setSelectedOriginalContent({
                                                            id: idea.id, // Add the missing id field for PDF URL generation
                                                            title: idea.title,
                                                            content: shouldUseTweet ? fullTweetContent : (idea.original_content || idea.content),
                                                            htmlContent: shouldUseTweet ? null : (() => {
                                                              // Check for HTML content in multiple locations
                                                              const htmlContent = idea.html_content || 
                                                                                 idea.original_email_content ||
                                                                                 idea.metadata?.email_html_content || 
                                                                                 idea.metadata?.original_email_content ||
                                                                                 null;
                                                              
                                                              // Simple HTML detection
                                                              if (htmlContent && (
                                                                htmlContent.includes('<div') ||
                                                                htmlContent.includes('<p>') ||
                                                                htmlContent.includes('<html>') ||
                                                                htmlContent.includes('<body')
                                                              )) {
                                                                return htmlContent;
                                                              }
                                                              return null;
                                                            })(),
                                                            subject: idea.metadata?.email_subject || idea.title || 'Email Content',
                                                            from: idea.source_info?.source_name || idea.from_name || idea.from_address,
                                                            isTwitterContent: idea.is_twitter_content || idea.source?.startsWith('x_bookmark') || false,
                                                            // X/Twitter Display Requirements - all required metadata INCLUDING THREAD DATA
                                                            twitterMetadata: twitterData ? {
                                                              authorName: twitterData.name || twitterData.author?.name,
                                                              username: twitterData.username || twitterData.author?.username,
                                                              profileImageUrl: twitterData.profileImageUrl || twitterData.profile_image_url || twitterData.author?.profile_image_url,
                                                              tweetUrl: twitterData.url || twitterData.tweet_url || idea.metadata?.twitter_content?.tweet_url,
                                                              createdAt: twitterData.created_at || twitterData.createdAt,
                                                              metrics: twitterData.metrics,
                                                              // PRESERVE THREAD DATA
                                                              is_thread: twitterData.is_thread || false,
                                                              thread_length: twitterData.thread_length || 1,
                                                              full_thread: twitterData.full_thread || null,
                                                              thread_info: twitterData.thread_info || null,
                                                              // CONVERSATION CONTEXT FOR REPLIES
                                                              conversation_context: twitterData.conversation_context || null,
                                                              has_context: twitterData.has_context || false,
                                                              // MEDIA DATA
                                                              media: twitterData.media || null
                                                            } : null,
                                                            hasPdfAttachments: idea.metadata?.has_pdf_attachments || false,
                                                            pdfData: fullPdfData,
                                                            platformSource: idea.metadata?.platform_source || 'Email'
                                                          });
                                                        }, 10);
                                                        setShowOriginalContentModal(true);
                                                      }}
                                                      className={`px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0 flex-shrink-0 border-2 ${
                                        isDarkMode
                                          ? 'border-blue-300 text-blue-200 hover:bg-blue-700 hover:text-white hover:border-blue-200'
                                          : 'border-blue-400 text-blue-600 hover:bg-blue-500 hover:text-white hover:border-blue-500'
                                      }`}
                                                      title="View full original email content"
                                                    >
                                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                                      </svg>
                                                      <span className="text-[10px] sm:text-xs">Open</span>
                                                    </button>
                                                  )}

                                                  {/* Brief Button - EXACT COPY */}
                                                  <button
                                                    onClick={() => {
                                                      setSelectedTickerForScheduling({
                                                        bookmarkId: idea.id,
                                                        ticker: idea.tickers?.[0],
                                                        content: idea.content
                                                      });
                                                      setShowDocumentModal(true);
                                                      checkSECAvailability(idea.tickers?.[0]);
                                                    }}
                                                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0 flex-shrink-0 border-2 ${
                                      isDarkMode
                                        ? 'border-slate-300 text-slate-100 hover:bg-slate-600 hover:text-white hover:border-slate-100'
                                        : 'border-slate-400 text-slate-600 hover:bg-slate-500 hover:text-white hover:border-slate-500'
                                    }`}
                                                    title={`Generate Research for ${idea.tickers?.[0]}`}
                                                  >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                    </svg>
                                                    <span className="text-[10px] sm:text-xs">File</span>
                                                  </button>
                                                  
                                                  {/* Schedule Button - EXACT COPY */}
                                                  <button
                                                    onClick={() => {
                                                      openCalendarModal({
                                                        bookmarkId: idea.id,
                                                        ticker: idea.tickers?.[0],
                                                        content: idea.content
                                                      });
                                                    }}
                                                    className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                                      isDarkMode
                                                        ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'
                                                        : 'border-purple-300 text-purple-600 hover:bg-purple-50'
                                                    }`}
                                                    title={`Schedule research reminder for ${idea.tickers?.[0]}`}
                                                  >
                                                    <FolderIcon className="w-6 h-6" />
                                                    <span className="text-[10px] sm:text-xs">Folder</span>
                                                  </button>
                                                  
                                                  {/* Share Button - EXACT COPY */}
                                                  <button
                                                    onClick={() => {
                                                      setSelectedTickerForScheduling({
                                                        bookmarkId: idea.id,
                                                        ticker: idea.tickers?.[0],
                                                        content: idea.content,
                                                        tweetId: idea.twitterMetadata?.tweet_id || idea.global_tweet_id,
                                                        shareType: 'idea'
                                                      });
                                                      setShowShareModal(true);
                                                    }}
                                                    className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                                      isDarkMode
                                                        ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10'
                                                        : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'
                                                    }`}
                                                    title={`Share ${idea.tickers?.[0]} investment idea with colleague`}
                                                  >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                    </svg>
                                                    <span className="text-[10px] sm:text-xs">Share</span>
                                                  </button>
                                                  
                                                  {/* Watchlist Button - EXACT COPY */}
                                                  <button
                                                    onClick={() => {
                                                      const ticker = idea.tickers?.[0];
                                                      if (ticker) {
                                                        // Use Twelve Data sector info from cache
                                                        const tdMetrics = ticker ? tickerMetricsCache[ticker.toUpperCase()] : null;
                                                        const sectorToUse = tdMetrics?.sector || (idea.primary_sector !== 'X/Twitter' ? idea.primary_sector : null);
                                                        const industryInfo = sectorToUse ? {
                                                          sector: sectorToUse
                                                        } : null;
                                                        addToWatchlist(ticker, industryInfo);
                                                      }
                                                    }}
                                                    disabled={!idea.tickers?.[0]}
                                                    className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                                      idea.tickers?.[0] 
                                                        ? (isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50')
                                                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                                    title={`Add ${idea.tickers?.[0]} to ${(() => {
                                                      const ticker = idea.tickers?.[0];
                                                      const tdMetrics = ticker ? tickerMetricsCache[ticker.toUpperCase()] : null;
                                                      return tdMetrics?.sector || (idea.primary_sector !== 'X/Twitter' ? idea.primary_sector : 'General');
                                                    })()} watchlist`}
                                                  >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                    </svg>
                                                    <span className="text-[10px] sm:text-xs">Tickers</span>
                                                  </button>
                                                  
                                                  {/* Delete Button - EXACT COPY */}
                                                  <button
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      
                                                      const tickerName = idea.primary_ticker || idea.tickers?.[0] || 'this idea';
                                                      
                                                      setConfirmModal({
                                                        isOpen: true,
                                                        title: 'Delete Investment Idea',
                                                        message: `Are you sure you want to delete the idea for $${tickerName}? This cannot be undone.`,
                                                        confirmText: 'Delete',
                                                        cancelText: 'Cancel',
                                                        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
                                                        onConfirm: async () => {
                                                          try {
                                                            const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${idea.id}`, {
                                                              method: 'DELETE'
                                                            });
                                                            
                                                            const responseData = await response.json();
                                                            
                                                            if (response.ok && responseData.success !== false) {
                                                              setBookmarks(bookmarks.filter(b => b.id !== idea.id));
                                                              showNotification(`Deleted idea for $${tickerName}`, 'success');
                                                            } else {
                                                              const errorMsg = responseData.error || responseData.message || 'Delete failed';
                                                              showNotification('Delete failed: ' + errorMsg, 'error');
                                                            }
                                                          } catch (error) {
                                                            console.error('DELETE error:', error);
                                                            showNotification('Error: ' + error.message, 'error');
                                                          }
                                                        }
                                                      });
                                                    }}
                                                    className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                                      isDarkMode
                                                        ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                                        : 'border-red-300 text-red-600 hover:bg-red-50'
                                                    }`}
                                                    title="Delete this investment idea"
                                                  >
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                    </svg>
                                                    <span className="text-[10px] sm:text-xs">Delete</span>
                                                  </button>
                                                </div>
                                              </CardContent>
                                            </Card>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            );
                          });
                        })()
                      )}
                    </div>
                  )}
                </div>
                
                {/* Loading More Indicator */}
                {loadingMore && (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span>Loading more ideas...</span>
                    </div>
                  </div>
                )}
                
                {/* Freemium upgrade prompt */}
                {subscription.tier === 'free' && subscription.total_available > subscription.limit && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-orange-800 mb-1">
                          Unlock Your Complete Investment History
                        </h3>
                        <p className="text-sm text-orange-700">
                          You have {subscription.total_available || bookmarks.length} total investment ideas. Upgrade to see all {(subscription.total_available || bookmarks.length) - subscription.limit} remaining ideas and your complete tracking history.
                        </p>
                        {paymentError && (
                          <p className="text-sm text-red-600 mt-2">{paymentError}</p>
                        )}
                      </div>
                      <Button 
                        onClick={() => initiateSubscriptionPayment('pro_monthly')}
                        disabled={isProcessingPayment}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium ml-4 px-6"
                      >
                        {isProcessingPayment ? 'Processing...' : 'Unlock Full History'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>



          {/* TEST Tab - Commented out but keeping TestDashboard component code for future use */}
          {/* <TabsContent value="test">
            <TestDashboard />
          </TabsContent> */}

          {/* Polls Tab - Global blue header bar + Spreadsheet content */}
          <TabsContent value="threads">
            <div {...pollSwipeHandlers}>
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                {/* Single-Row Header Bar (like Dashboard) */}
                <div className="p-4 bg-slate-800 text-white">
                  {/* Mobile: Two rows | Desktop: Single row */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: My Polls / Received toggle */}
                    <PollViewTabs
                      pollView={pollView}
                      setPollView={setPollView}
                      myPollsCount={fishingThreads.filter(p => p.is_own_poll).length}
                      receivedCount={sharedPolls.length}
                    />
                    
                    {/* Center: Keyboard shortcuts hint - Desktop only, subtle */}
                    <button
                      onClick={() => setShowKeyboardShortcutsModal(true)}
                      className="hidden sm:flex items-center text-white/50 text-sm"
                      title="View keyboard shortcuts"
                    >
                      <span>Press </span>
                      <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                      <span> for shortcuts</span>
                    </button>
                    
                    {/* Right side: Action buttons - Grid on mobile, flex on desktop */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2">
                        {/* Search Button - Always first/far left */}
                        <button
                          onClick={() => setShowMobileSearch(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                          title="Search"
                        >
                          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                          </svg>
                          <span className="hidden sm:inline">Search</span>
                        </button>
                        
                        {/* Sort Button - Age-based sorting like Dashboard (disabled when viewing a specific poll) */}
                        <div className="relative poll-sort-dropdown-container">
                          <button
                            onClick={() => !selectedPoll && setShowPollSortMenu(!showPollSortMenu)}
                            disabled={selectedPoll && !viewAllPollsMode}
                            className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                              selectedPoll && !viewAllPollsMode
                                ? 'text-white/30 border-white/10 cursor-not-allowed'
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                            }`}
                            title={selectedPoll && !viewAllPollsMode ? 'Sort not available when viewing a poll' : 'Sort polls by age'}
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                            </svg>
                            <span className="hidden sm:inline">Sort</span>
                          </button>
                          
                          {showPollSortMenu && !selectedPoll && (
                            <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                              <div className="py-1">
                                <button
                                  onClick={() => { setPollAgeSortBy('newest'); setShowPollSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-slate-400 mr-2"></div>
                                    All (newest first)
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setPollAgeSortBy('fresh'); setShowPollSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                    New (&lt; 3 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setPollAgeSortBy('warm'); setShowPollSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                                    Recent (3-7 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setPollAgeSortBy('stale'); setShowPollSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                                    Older (7+ days)
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Poll Type Filter Button (Funnel) */}
                        <div className="relative poll-type-filter-container">
                          <button
                            onClick={() => setShowPollTypeFilterMenu(!showPollTypeFilterMenu)}
                            className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                              pollTypeFilter !== 'all'
                                ? 'text-orange-400 border-orange-400/50 bg-orange-500/10'
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                            }`}
                            title="Filter by poll type"
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                            </svg>
                            <span className="hidden sm:inline">Filter</span>
                          </button>
                          
                          {showPollTypeFilterMenu && (
                            <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                              <div className="py-1">
                                <button
                                  onClick={() => { setPollTypeFilter('all'); setShowPollTypeFilterMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollTypeFilter === 'all' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
                                    </svg>
                                    All Polls
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setPollTypeFilter('ideas'); setShowPollTypeFilterMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollTypeFilter === 'ideas' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                                    </svg>
                                    Ideas
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setPollTypeFilter('x_poll'); setShowPollTypeFilterMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${pollTypeFilter === 'x_poll' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2 text-sky-500" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.910 11.078h-3.246z"/>
                                    </svg>
                                    X Poll
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      
                      {/* New Poll Button - Orange solid with dropdown */}
                      <div className="relative new-poll-dropdown-container">
                        <button
                          onClick={() => setShowNewPollDropdown(!showNewPollDropdown)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200"
                        >
                          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                          </svg>
                          <span className="hidden sm:inline">New</span>
                          <svg className="w-3 h-3 ml-1 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Dropdown menu */}
                        {showNewPollDropdown && (
                          <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-50 py-1 ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-slate-200'}`}>
                            {/* Custom List */}
                            <button
                              onClick={() => {
                                setNewPollMode('custom_list');
                                setShowNewPollDropdown(false);
                                setShowTweetGenerator(true);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700'}`}
                            >
                              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                              </svg>
                              <span>Custom List</span>
                            </button>
                            
                            {/* Requests (Ask for Ideas) */}
                            <button
                              onClick={() => {
                                setNewPollMode('requests');
                                setShowNewPollDropdown(false);
                                setShowTweetGenerator(true);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700'}`}
                            >
                              <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                              <span>Requests</span>
                            </button>
                            
                            {/* X Poll */}
                            <button
                              onClick={() => {
                                setNewPollMode('x_poll');
                                setShowNewPollDropdown(false);
                                setShowTweetGenerator(true);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700'}`}
                            >
                              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                              </svg>
                              <span>X Poll</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Collapse button to return to View All mode - at the top */}
                {selectedPoll && !viewAllPollsMode && (
                  <div className="p-6 pb-0">
                    <div className={`mb-4 flex items-center justify-start`}>
                    <button
                      onClick={() => {
                        setSelectedPoll(null);
                        setViewAllPollsMode(true);
                      }}
                      className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        isDarkMode 
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-800 border border-slate-300'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                      <span>Back to All</span>
                    </button>
                  </div>
                  </div>
                )}
                
                {/* View All Polls Mode - Accordion */}
                {viewAllPollsMode && !selectedPoll && (
                  <div className="p-6 space-y-3">
                    {/* Show polls based on pollView tab selection */}
                    {pollView === 'my' ? (
                      /* My Polls View */
                      fishingThreads.filter(p => p.is_own_poll).length > 0 ? (
                        <div className="space-y-2">
                          {fishingThreads
                            .filter(p => {
                              if (!p.is_own_poll) return false;
                              
                              // Apply poll type filter first
                              if (pollTypeFilter === 'x_poll') {
                                // X Polls have original_tweet data from Twitter
                                const isXPoll = p.original_tweet?.twitter_id || p.source === 'twitter';
                                if (!isXPoll) return false;
                              } else if (pollTypeFilter === 'ideas') {
                                // Ideas polls are manually created (no original_tweet from Twitter)
                                const isIdeasPoll = !p.original_tweet?.twitter_id && p.source !== 'twitter';
                                if (!isIdeasPoll) return false;
                              }
                              
                              // Then apply age-based filtering
                              if (pollAgeSortBy === 'newest') return true;
                              const days = calculateDaysOld(p.created_at || p.updated_at);
                              if (pollAgeSortBy === 'fresh') return days < 10;
                              if (pollAgeSortBy === 'warm') return days >= 10 && days <= 30;
                              if (pollAgeSortBy === 'stale') return days > 30;
                              return true;
                            })
                            .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
                            .map(poll => {
                              const daysOld = calculateDaysOld(poll.created_at || poll.updated_at);
                              const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                              const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                              const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                              
                              return (
                                <div key={poll.id} className={`rounded-lg border-2 ${ageBorderColor} ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} overflow-hidden`}>
                                  <button
                                    onClick={() => {
                                      setViewAllPollsMode(false);
                                      setSelectedPoll(poll);
                                    }}
                                    className={`w-full px-4 py-3 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{poll.title}</div>
                                      <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {poll.ticker_count || 0} tickers · {poll.reply_count || 0} replies
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                      </svg>
                                    </div>
                                  </button>
                                  <div className={`flex gap-2 justify-center px-3 py-2 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-100 bg-slate-50/50'}`}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTickerForScheduling({
                                          ticker: poll.title,
                                          bookmarkId: poll.id,
                                          content: `Poll: ${poll.title}`,
                                          shareType: 'poll',
                                          tickerCount: poll.ticker_count || 0,
                                          pollId: poll.id
                                        });
                                        setShowShareModal(true);
                                      }}
                                      className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                        isDarkMode
                                          ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10'
                                          : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'
                                      }`}
                                      title="Share poll"
                                    >
                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                      </svg>
                                      <span className="text-[10px] sm:text-xs">Share</span>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deletePoll(poll.id, poll.title);
                                      }}
                                      className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                        isDarkMode
                                          ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                          : 'border-red-300 text-red-600 hover:bg-red-50'
                                      }`}
                                      title="Delete poll"
                                    >
                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                      </svg>
                                      <span className="text-[10px] sm:text-xs">Delete</span>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                          <p className="font-medium">No polls created yet</p>
                          <p className="text-sm mt-2 max-w-sm mx-auto">
                            To create a poll, post a tweet asking for investment ideas and include <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">@savetotickler</span> in your tweet.
                          </p>
                          <p className="text-sm mt-2 max-w-sm mx-auto">
                            Then bookmark your tweet and sync your bookmarks - we'll automatically create a poll and collect ticker suggestions from replies.
                          </p>
                        </div>
                      )
                    ) : (
                      /* Received Polls View - Show polls shared with you */
                      sharedPolls.length > 0 ? (
                        <div className="space-y-2">
                          {sharedPolls
                            .filter(poll => {
                              // Apply poll type filter first
                              if (pollTypeFilter === 'x_poll') {
                                const isXPoll = poll.original_tweet?.twitter_id || poll.source === 'twitter';
                                if (!isXPoll) return false;
                              } else if (pollTypeFilter === 'ideas') {
                                const isIdeasPoll = !poll.original_tweet?.twitter_id && poll.source !== 'twitter';
                                if (!isIdeasPoll) return false;
                              }
                              
                              // Then apply age-based filtering
                              if (pollAgeSortBy === 'newest') return true;
                              const days = calculateDaysOld(poll.shared_at || poll.created_at);
                              if (pollAgeSortBy === 'fresh') return days < 10;
                              if (pollAgeSortBy === 'warm') return days >= 10 && days <= 30;
                              if (pollAgeSortBy === 'stale') return days > 30;
                              return true;
                            })
                            .sort((a, b) => new Date(b.shared_at || b.created_at || 0) - new Date(a.shared_at || a.created_at || 0))
                            .map(poll => {
                              const daysOld = calculateDaysOld(poll.shared_at || poll.created_at);
                              const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                              const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                              const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                              
                              return (
                                <div key={poll.id} className={`rounded-lg border-2 ${ageBorderColor} ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} overflow-hidden`}>
                                  <button
                                    onClick={() => {
                                      setViewAllPollsMode(false);
                                      setSelectedPoll(poll);
                                    }}
                                    className={`w-full px-4 py-3 flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{poll.title || poll.ticker}</div>
                                      <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        From: {poll.sender_name || poll.sender_email?.split('@')[0]} · {poll.ticker_count || 0} tickers
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                      </svg>
                                    </div>
                                  </button>
                                  <div className={`flex gap-2 justify-center px-3 py-2 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-100 bg-slate-50/50'}`}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Remove from received polls using confirm modal
                                        setConfirmModal({
                                          isOpen: true,
                                          title: 'Remove Received Poll',
                                          message: `Remove "${poll.title || poll.ticker}" from your received polls?`,
                                          confirmText: 'Remove',
                                          cancelText: 'Cancel',
                                          confirmButtonClass: 'bg-red-600 hover:bg-red-700',
                                          onConfirm: async () => {
                                            try {
                                              const response = await fetch(`${API_BASE_URL}/api/shared-items/${poll.id}`, {
                                                method: 'DELETE'
                                              });
                                              if (response.ok) {
                                                fetchSharedPolls();
                                                showNotification('Poll removed', 'success');
                                              }
                                            } catch (error) {
                                              console.error('Error removing poll:', error);
                                            }
                                          }
                                        });
                                      }}
                                      className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                        isDarkMode
                                          ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                          : 'border-red-300 text-red-600 hover:bg-red-50'
                                      }`}
                                      title="Remove poll"
                                    >
                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                      </svg>
                                      <span className="text-[10px] sm:text-xs">Remove</span>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                          </svg>
                          <p className="font-medium">No received polls yet</p>
                          <p className="text-sm mt-1">Polls shared with you will appear here</p>
                        </div>
                      )
                    )}
                    
                    {/* Empty state when no polls exist at all */}
                    {fishingThreads.length === 0 && sharedPolls.length === 0 && (
                      <div className={`p-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        <p className="font-medium mb-2">Create your first poll</p>
                        <p className="text-sm">Post a tweet with <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">@savetotickler</span>, bookmark it, then sync.</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Poll Content - Spreadsheet (only show when not in View All mode) */}
                {!viewAllPollsMode && (
                  <>
                  <PollSpreadsheet 
                  selectedPoll={selectedPoll}
                  sortConfig={pollSortConfig}
                  isDarkMode={isDarkMode}
                  onSort={(key) => setPollSortConfig(prev => ({
                    key,
                    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                  }))}
                  onTickerClick={(ticker) => openWatchlistModal([ticker], `${ticker} Ideas`)} 
                  onPollUpdated={(pollId, updates) => {
                    setFishingThreads(prev => prev.map(p => 
                      p.id === pollId ? { ...p, ...updates } : p
                    ));
                  }}
                  onDocuments={(rowData) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'documents', source: 'polls', ticker: rowData.ticker });
                    setSelectedTickerForScheduling({ ticker: rowData.ticker, bookmarkId: null, content: null });
                    setShowDocumentModal(true);
                    checkSECAvailability(rowData.ticker);
                  }}
                  onSchedule={(rowData) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'schedule', source: 'polls', ticker: rowData.ticker });
                    setSelectedTickerForScheduling({ ticker: rowData.ticker, bookmarkId: null, content: null });
                    setUseManualScheduleFlow(false); // Reset to show 2-choice menu
                    setIdeaScheduleLoading(false);
                    setIdeaScheduleInfo(null);
                    setShowCalendarModal(true);
                    setCurrentCalendarStep(1);
                    checkSECAvailability(rowData.ticker);
                  }}
                  onShare={(rowData) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'share', source: 'polls', ticker: rowData.ticker });
                    setSelectedTickerForScheduling({ ticker: rowData.ticker, bookmarkId: null, content: null });
                    setShowShareModal(true);
                  }}
                  onWatchlist={async (rowData) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'watchlist', source: 'polls', ticker: rowData.ticker });
                    // Add ticker to user's default watchlist
                    try {
                      const response = await fetch(`${API_BASE_URL}/api/watchlists/add-ticker`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: user?.id || 'demo_user',
                          ticker: rowData.ticker
                        })
                      });
                      if (response.ok) {
                        showNotification(`Added $${rowData.ticker} to your watchlist!`, 'success');
                      } else {
                        showNotification(`Failed to add ${rowData.ticker} to watchlist`, 'error');
                      }
                    } catch (error) {
                      console.error('Error adding to watchlist:', error);
                      showNotification(`Error adding ${rowData.ticker} to watchlist`, 'error');
                    }
                  }}
                  onViewOriginal={(ticker, reply) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'original', source: 'polls', ticker: ticker });
                    // Open the original content modal with reply data
                    if (reply?.url) {
                      setSelectedOriginalContent({
                        subject: `$${ticker} - Poll Reply`,
                        from: `@${reply.author_username || 'unknown'}`,
                        url: reply.url,
                        content: reply.text || reply.content || '',
                        rawContent: reply.text || reply.content || '',
                        platformSource: 'Twitter',
                        isTwitterContent: true,
                        twitterMetadata: {
                          authorName: reply.author_name || reply.author_username || 'Unknown',
                          username: reply.author_username || 'unknown',
                          tweetId: reply.id || null,
                          tweetUrl: reply.url
                        },
                        ticker: ticker,
                        tickers: [ticker]
                      });
                      setShowOriginalContentModal(true);
                    }
                  }}
                  onDelete={async (rowData) => {
                    trackEngagement('modal_click', 'modal', { button_name: 'delete', source: 'polls', ticker: rowData.ticker });
                    if (window.confirm(`Remove $${rowData.ticker} from this poll?`)) {
                      try {
                        const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${selectedFishingThread?.id}/remove-ticker`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            user_id: user?.id,
                            ticker: rowData.ticker
                          })
                        });
                        if (response.ok) {
                          showNotification(`Removed $${rowData.ticker} from poll`, 'success');
                          // Refresh the poll data
                          fetchFishingThreads();
                        } else {
                          showNotification(`Failed to remove ${rowData.ticker}`, 'error');
                        }
                      } catch (error) {
                        console.error('Error removing ticker from poll:', error);
                        showNotification(`Error removing ${rowData.ticker}`, 'error');
                      }
                    }
                  }}
                />
                </>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Watchlists Tab - Now using Spreadsheet View as the main interface */}
          <TabsContent value="watchlists" forceMount className={activeTab !== 'watchlists' ? 'hidden' : ''}>
            <WatchlistSpreadsheet 
              user={user}
              onTickerClick={(ticker) => openWatchlistModal([ticker], `${ticker} Ideas`)} 
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              initialWatchlistId={selectedWatchlistId}
              onSearch={() => setShowMobileSearch(true)}
              showNotification={showNotification}
              sharedWatchlists={sharedWatchlists}
              watchlistView={watchlistView}
              setWatchlistView={setWatchlistView}
              ideasLists={ideasListsForCollections}
              polls={fishingThreads}
              sharedPolls={sharedPolls}
              onWatchlist={(rowData) => {
                openWatchlistModal([rowData.ticker], `Add ${rowData.ticker} to Tickers`);
              }}
              onPollUpdated={(pollId, updates) => {
                setFishingThreads(prev => prev.map(p => 
                  p.id === pollId ? { ...p, ...updates } : p
                ));
              }}
              onCreatePoll={(mode) => {
                if (mode) setNewPollMode(mode);
                setShowTweetGenerator(true);
              }}
              onDeletePoll={(pollId, pollTitle) => deletePoll(pollId, pollTitle)}
              onLogout={logout}
              onNavigateToAccount={() => {
                setActiveTab('settings');
                setSettingsView('account');
              }}
              onNavigateToSchedules={() => {
                setActiveTab('settings');
                setSettingsView('schedules');
              }}
              onConfirmAction={(options) => {
                setConfirmModal({
                  isOpen: true,
                  title: options.title || 'Confirm',
                  message: options.message || 'Are you sure?',
                  warningMessage: options.warningMessage || null,
                  confirmText: options.confirmText || 'Confirm',
                  cancelText: options.cancelText || 'Cancel',
                  confirmButtonClass: options.confirmButtonClass || 'bg-red-600 hover:bg-red-700',
                  onConfirm: options.onConfirm || (() => {})
                });
              }}
              onOriginal={async (rowData) => {
                trackEngagement('modal_click', 'modal', { button_name: 'original', source: 'watchlist', ticker: rowData.ticker });
                
                // For Ideas Lists, fetch the original bookmark content using the new endpoint
                if (rowData.isIdeasList && rowData.bookmarkId) {
                  try {
                    // Fetch the original idea to get its full content
                    const response = await fetch(`${API_BASE_URL}/api/idea/${rowData.bookmarkId}`);
                    if (response.ok) {
                      const idea = await response.json();
                      if (idea.success) {
                        // Prefer twitterMetadata from rowData if passed, otherwise try to build from API response
                        const twitterMeta = rowData.twitterMetadata || (idea.twitter_content ? {
                          authorName: idea.from_name,
                          username: idea.author_handle?.replace('@', ''),
                          profileImageUrl: idea.profile_image,
                          tweetUrl: idea.twitter_url,
                          text: idea.content,
                          createdAt: idea.created_at,
                          metrics: idea.metrics
                        } : null);
                        
                        setSelectedOriginalContent({
                          title: idea.title || rowData.ticker || 'Curation',
                          // Use original_email_content for the full document, fall back to content
                          content: idea.original_email_content || idea.content || rowData.content,
                          subject: idea.subject || idea.title || 'Ideas List',
                          from: idea.from_name || idea.author_handle || rowData.author || 'Unknown',
                          is_twitter_content: rowData.isTwitterContent || idea.is_twitter_content || rowData.sourceType === 'twitter',
                          isTwitterContent: rowData.isTwitterContent || idea.is_twitter_content || rowData.sourceType === 'twitter',
                          platformSource: rowData.platformSource || (rowData.sourceType === 'twitter' ? 'Twitter' : idea.platform_source),
                          twitter_url: idea.twitter_url || rowData.sourceUrl,
                          source_url: idea.source_url || rowData.sourceUrl,
                          twitterMetadata: twitterMeta
                        });
                        setShowOriginalContentModal(true);
                        return;
                      }
                    }
                  } catch (error) {
                    console.error('Error fetching Ideas List original content:', error);
                  }
                  
                  // Fallback: show what we have from the watchlist/rowData
                  setSelectedOriginalContent({
                    title: rowData.ticker || 'Ideas List',
                    content: rowData.content || 'Original content not available',
                    subject: rowData.ticker || 'Ideas List',
                    from: rowData.author || 'Unknown',
                    is_twitter_content: rowData.isTwitterContent || rowData.sourceType === 'twitter' || rowData.sourceType === 'x_bookmark',
                    isTwitterContent: rowData.isTwitterContent || rowData.sourceType === 'twitter' || rowData.sourceType === 'x_bookmark',
                    platformSource: rowData.platformSource || 'Twitter',
                    twitter_url: rowData.sourceUrl,
                    source_url: rowData.sourceUrl,
                    twitterMetadata: rowData.twitterMetadata || null
                  });
                  setShowOriginalContentModal(true);
                  return;
                }
                
                // For regular watchlist items, show ticker info
                setSelectedOriginalContent({
                  title: `$${rowData.ticker}`,
                  content: `${rowData.company_name || rowData.ticker} - Watchlist Item`,
                  subject: `Watchlist: ${rowData.ticker}`,
                  from: 'Watchlist',
                  is_twitter_content: false
                });
                setShowOriginalContentModal(true);
              }}
              onDocuments={(rowData) => {
                trackEngagement('modal_click', 'modal', { button_name: 'documents', source: 'watchlist', ticker: rowData.ticker });
                setSelectedTickerForScheduling({ ticker: rowData.ticker, bookmarkId: null, content: null });
                setShowDocumentModal(true);
                checkSECAvailability(rowData.ticker);
              }}
              onSchedule={(rowData) => {
                trackEngagement('modal_click', 'modal', { button_name: 'schedule', source: 'watchlist', ticker: rowData.ticker });
                
                // Pass whatever data is available - backend will look up source idea if needed
                openRescheduleModal({
                  ticker: rowData.ticker,
                  company_name: rowData.company_name || rowData.ticker,
                  idea_id: rowData.id || rowData.idea_id,
                  content: rowData.content || rowData.original_content || rowData.summary,
                  summary: rowData.summary,
                  author: rowData.author || rowData.source,
                  title: rowData.title,
                  source_url: rowData.source_url || rowData.url
                }, true); // true = isNewSchedule, shows "Schedule" instead of "Reschedule"
              }}
              onShare={(rowData) => {
                trackEngagement('modal_click', 'modal', { button_name: 'share', source: 'watchlist', ticker: rowData.ticker });
                setSelectedTickerForScheduling({ 
                  ticker: rowData.ticker, 
                  bookmarkId: rowData.watchlistId || null, 
                  content: rowData.content || null,
                  shareType: rowData.shareType || null,
                  tickerCount: rowData.tickerCount || null,
                  watchlistId: rowData.watchlistId || null
                });
                setShowShareModal(true);
              }}
              onDelete={(rowData) => {
                trackEngagement('modal_click', 'modal', { button_name: 'delete', source: 'watchlist', ticker: rowData.ticker });
              }}
              onDeleteSharedItem={(sharedItemId, itemName) => {
                setConfirmModal({
                  isOpen: true,
                  title: 'Remove Shared Watchlist',
                  message: `Are you sure you want to remove "${itemName}" from your shared items?`,
                  confirmText: 'Remove',
                  cancelText: 'Cancel',
                  confirmButtonClass: 'bg-red-600 hover:bg-red-700',
                  onConfirm: async () => {
                    const deleteKey = `deleting_${sharedItemId}`;
                    if (window[deleteKey]) return;
                    window[deleteKey] = true;
                    
                    try {
                      const response = await fetch(`${API_BASE_URL}/api/shared-items/${sharedItemId}`, {
                        method: 'DELETE'
                      });
                      if (response.ok) {
                        showNotification(`Removed "${itemName}" from shared items`, 'success');
                        fetchSharedWatchlists();
                      } else {
                        showNotification('Failed to remove shared item', 'error');
                      }
                    } catch (error) {
                      console.error('Error deleting shared item:', error);
                      showNotification('Error removing shared item', 'error');
                    } finally {
                      delete window[deleteKey];
                    }
                  }
                });
              }}
            />
          </TabsContent>

          {/* 
          ===========================================
          OLD WATCHLIST TAB CODE - PRESERVED FOR FUTURE USE
          To restore the old card-based watchlist view, uncomment this section
          and change the TabsContent value above from "watchlists" to something else
          ===========================================
          
          The old watchlist code that displayed cards has been removed from here
          to avoid JSX parsing issues. If you need to restore it, please check
          git history or the backup in /app/frontend/src/pages/WatchlistCardsBackup.js
          
          */}

          {/* Watchlist Modal */}
          {watchlistModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setWatchlistModalOpen(false)}>
              <div 
                className={`max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-lg ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 z-10 p-3 sm:p-6 border-b bg-white border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className={`text-lg sm:text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                      {watchlistModalTitle}
                    </h2>
                    <button
                      onClick={() => setWatchlistModalOpen(false)}
                      className={`p-1.5 sm:p-2 rounded-full hover:bg-slate-100 ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'text-slate-600'}`}
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className={`mt-1 sm:mt-2 text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {watchlistModalIdeas.length} {watchlistModalIdeas.length === 1 ? 'idea' : 'ideas'} found for {watchlistModalTickers.length} {watchlistModalTickers.length === 1 ? 'ticker' : 'tickers'}
                  </p>
                </div>
                
                <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
                  {watchlistModalIdeas.length === 0 ? (
                    <div className="text-center py-12">
                      <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        No ideas found for {watchlistModalTickers.join(', ')}
                      </p>
                    </div>
                  ) : (
                    watchlistModalIdeas.map((idea, ideaIndex) => {
                      // Use same age calculation as feed
                      const daysOld = calculateDaysOld(idea.created_at);
                      const ageCategory = getAgeCategory(idea.created_at);
                      const contentBasedId = `watchlist-content-${idea.content?.substring(0, 50).replace(/[^a-zA-Z0-9]/g, '')}`;
                      
                      let ageBadgeColor = 'bg-gray-100 text-gray-800';
                      let cardBorderColor = 'border-gray-200';
                      
                      if (ageCategory === 'New') {
                        ageBadgeColor = 'bg-green-100 text-green-800';
                        cardBorderColor = 'border-green-500 border-2';
                      } else if (ageCategory === 'Recent') {
                        ageBadgeColor = 'bg-orange-100 text-orange-800';
                        cardBorderColor = 'border-orange-500 border-2';
                      } else if (ageCategory === 'Older') {
                        ageBadgeColor = 'bg-red-100 text-red-800';
                        cardBorderColor = 'border-red-500 border-2';
                      }
                      
                      return (
                        <Card 
                          key={`watchlist-${idea.id}-${ideaIndex}`} 
                          className={`${cardBorderColor} ${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white'} hover:shadow-lg transition-all duration-200`}
                        >
                          <CardContent className="p-3 sm:p-6">
                            {/* Header with Number, Ticker, and Age */}
                            <div className="flex items-start justify-between mb-3 sm:mb-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2 sm:gap-3 mb-2">
                                  {/* Number Badge */}
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-slate-700 to-slate-800 text-white font-bold text-xs sm:text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                                    {ideaIndex + 1}
                                  </div>
                                  
                                  {/* Company Name & Ticker Display */}
                                  <div className={`flex-1 min-w-0 ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                    <div className="mb-1">
                                      {/* Special display for Ideas List cards */}
                                      {idea.is_ticker_list || idea.title === "Ideas List" ? (
                                        <>
                                          <div className={`font-bold text-base sm:text-xl leading-tight`}>
                                            Ideas List
                                          </div>
                                          <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mt-0.5 sm:mt-1`}>
                                            {idea.all_tickers?.length || idea.tickers?.length || 0} ideas referenced
                                          </div>
                                          {/* List all tickers */}
                                          <div className="flex flex-wrap gap-0.5 mt-2 sm:mt-3">
                                            {(idea.all_tickers || idea.tickers || []).map((ticker, idx) => (
                                              <span key={idx} className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-sm font-mono font-semibold rounded ${isDarkMode ? 'bg-slate-700 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                                                {typeof ticker === 'string' ? ticker : ticker.ticker}
                                              </span>
                                            ))}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className={`font-bold text-sm sm:text-xl leading-tight line-clamp-2`}>
                                            {(() => {
                                              const companyName = idea.company_intelligence?.company_name || idea.company_name;
                                              const title = idea.title;
                                              
                                              // First try: Use company_name field directly
                                              if (companyName && !companyName.startsWith('Company with ticker')) {
                                                // Show company name + thesis from title
                                                const thesisMatch = title?.match(/\):\s*(.+)$/);
                                                const thesis = thesisMatch ? thesisMatch[1] : '';
                                                return thesis ? `${companyName}: ${thesis}` : companyName;
                                              }
                                              
                                              // Second try: Parse title format "$TICKER (Company Name): thesis"
                                              if (title) {
                                                const match = title.match(/^\$[\w.]+\s+\(([^)]+)\):\s*(.+)$/);
                                                if (match) {
                                                  return `${match[1]}: ${match[2]}`;
                                                }
                                              }
                                              
                                              if (title?.includes('Investment Analysis')) {
                                                return title.replace(/ Investment Analysis.*$/, '');
                                              }
                                              
                                              return title || `${idea.tickers?.[0] || 'Unknown'} Company`;
                                            })()}
                                          </div>
                                          <div className={`font-mono font-bold text-base sm:text-xl tracking-wide ${isDarkMode ? 'text-blue-300' : 'text-blue-600'} mt-0.5 sm:mt-1`}>
                                            ${idea.tickers?.[0] || 'N/A'}
                                            {idea.company_intelligence?.exchange && idea.company_intelligence.exchange !== 'Unknown' && (
                                              <span className={`text-xs sm:text-sm font-normal ml-1 sm:ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                • {idea.company_intelligence.exchange}
                                                {idea.company_intelligence.country && idea.company_intelligence.country !== 'Unknown' && (
                                                  <span className="hidden sm:inline"> • {idea.company_intelligence.country}</span>
                                                )}
                                              </span>
                                            )}
                                          </div>
                                          {/* Ticker Badges for List-type Cards - [TICKER] format */}
                                          {idea.supporting_tickers && idea.supporting_tickers.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1 sm:mt-2">
                                              {idea.supporting_tickers.map((ticker, idx) => (
                                                <span key={idx} className={`px-1 sm:px-1.5 py-0.5 text-xs font-mono font-medium rounded ${isDarkMode ? 'bg-slate-700 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                                                  [{typeof ticker === 'string' ? ticker : ticker.ticker}]
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Industry + Financial Metrics */}
                                {(() => {
                                  let displayCategory = idea.primary_sector;
                                  const hasIndustry = displayCategory && displayCategory !== 'Uncategorized' && displayCategory !== 'General' && displayCategory !== 'Email Content' && displayCategory !== 'X/Twitter';
                                  const hasFinancials = idea.primary_market_cap || idea.primary_enterprise_value;
                                  
                                  return (hasIndustry || hasFinancials) ? (
                                    <div className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} ml-8 sm:ml-11 mt-1`}>
                                      <div className="flex items-center gap-0.5 flex-wrap">
                                        {hasIndustry && <span className="font-medium">{displayCategory}</span>}
                                        {hasIndustry && hasFinancials && <span className="text-slate-300">|</span>}
                                        {idea.primary_market_cap && <span>MCap: <span className="font-mono">{formatFinancialValue(idea.primary_market_cap)}</span></span>}
                                        {idea.primary_enterprise_value && <><span className="text-slate-300">|</span> <span>EV: <span className="font-mono">{formatFinancialValue(idea.primary_enterprise_value)}</span></span></>}
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                              
                              {/* Age Badge */}
                            </div>
                            
                            {/* Content */}
                            <div className={`mb-3 sm:mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              <div className="flex flex-col gap-1 mb-2">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                  {/* Smart Domain-Based Categorization with Real Logos */}
                                  {(() => {
                                    // Check if this is an investment letter by type, not name
                                    const isInvestorLetter = idea.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                    
                                    // Special handling for Investor Letter - show proper document icon (matches dropdown)
                                    if (isInvestorLetter) {
                                      return (
                                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                      );
                                    }
                                    
                                    // Otherwise use normal domain-based categorization
                                    const category = categorizByDomain(idea);
                                    const domain = extractDomain(idea.url) || extractDomain(idea.from_address);
                                    
                                    // Render icon - prioritize real logos
                                    if (category.svg) {
                                      // Extract text-color from category.color (e.g., "bg-orange-50 text-orange-700" → "text-orange-700")
                                      const textColor = category.color?.split(' ').find(c => c.startsWith('text-')) || '';
                                      return (
                                        <svg className={`w-4 h-4 ${textColor}`} fill="currentColor" viewBox="0 0 24 24">
                                          <path d={category.svg} />
                                        </svg>
                                      );
                                    } else if (domain) {
                                      // Use Google Favicon for real company logo
                                      return (
                                        <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} 
                                             alt={domain} 
                                             className="w-4 h-4 object-contain" />
                                      );
                                    } else {
                                      // Default email icon
                                      return (
                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                        </svg>
                                      );
                                    }
                                  })()}
                                  <span className={`font-medium text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                    {idea.source === 'email' || idea.source === 'ai_analysis' || idea.source === 'bookmarklet' ? 
                                      (() => {
                                        // Get base source name
                                        const baseName = (idea.source_info?.ai_attribution?.original_source_name && 
                                         idea.source_info.ai_attribution.original_source_name.toLowerCase() !== 'unknown' ? 
                                         idea.source_info.ai_attribution.original_source_name : null) ||
                                        // Priority 2: Clean up source_name - extract just the main source, remove "via" part
                                        (idea.source_info?.source_name ? 
                                          idea.source_info.source_name.split(' (via ')[0] || idea.source_info.source_name : null) ||
                                        // Priority 3: Source address
                                        idea.source_info?.source_address ||
                                        // Priority 4: From name - clean up "via" part
                                        (idea.from_name && idea.from_name !== 'Unknown' ? 
                                          idea.from_name.split(' (via ')[0] || idea.from_name : null) ||
                                        // Priority 5: Author cleanup
                                        (idea.author && idea.author !== 'undefined' ? idea.author.replace('@', '') : null) ||
                                          (() => {
                                            const fromAddr = idea.from_address || '';
                                            if (fromAddr.includes('@substack.com')) return 'Substack';
                                            if (fromAddr.includes('@seeking')) return 'Seeking Alpha';
                                            if (fromAddr.includes('@bloomberg')) return 'Bloomberg';
                                            if (fromAddr.includes('@barrons')) return 'Barrons';
                                            if (fromAddr.includes('@wsj')) return 'Wall Street Journal';
                                            return null;
                                          })() ||
                                          (() => {
                                            const emailSubject = idea.email_subject || idea.source_info?.source_subject || '';
                                            const emailSubjectLower = emailSubject.toLowerCase();
                                            if (emailSubjectLower.match(/\b(investor\s+letter|annual\s+letter|quarterly\s+letter|shareholder\s+letter)\b/)) return 'Investor Letter';
                                            if (emailSubjectLower.match(/\b(letter\s+to\s+(shareholders|investors))\b/)) return 'Investor Letter';
                                            if (emailSubjectLower.match(/\b[qQ]\d{1,2}\s*\d{2,4}\s+letter\b/)) return 'Investor Letter';
                                            if (emailSubjectLower.match(/\b\d{4}\s+letter\b/)) return 'Investor Letter';
                                            if (emailSubjectLower.match(/\bletter\s*$/)) return 'Investor Letter';
                                            
                                            if (idea.title) {
                                              const titleLower = idea.title.toLowerCase();
                                              if (titleLower.includes('value investors club')) return 'Value Investors Club';
                                              if (titleLower.includes('seeking alpha')) return 'Seeking Alpha';
                                              if (titleLower.includes('motley fool')) return 'The Motley Fool';
                                              if (titleLower.includes('bloomberg')) return 'Bloomberg';
                                              if (titleLower.includes('barrons')) return 'Barrons';
                                              if (titleLower.match(/\b(investor\s+letter|annual\s+letter|quarterly\s+letter|shareholder\s+letter)\b/)) return 'Investor Letter';
                                              if (titleLower.match(/\b(letter\s+to\s+(shareholders|investors))\b/)) return 'Investor Letter';
                                              if (titleLower.match(/\b[qQ]\d{1,2}\s*\d{2,4}\s+letter\b/)) return 'Investor Letter';
                                              if (titleLower.match(/\b\d{4}\s+letter\b/)) return 'Investor Letter';
                                              if (titleLower.match(/\bletter\s*$/)) return 'Investor Letter';
                                              if (titleLower.match(/\bnewsletter\b/)) return 'Newsletter';
                                            }
                                            return null;
                                          })() ||
                                          'Email';
                                        
                                        // If it's an investor letter, append "(Investor Letter)" to source name
                                        const isInvestorLetter = idea.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                        return isInvestorLetter && baseName ? `${baseName} (Investor Letter)` : baseName;
                                      })() : 
                                      `@${(idea.author || 'unknown').replace(/^@/, '')}`
                                    }
                                  </span>
                                </div>
                                <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {formatDate(idea.created_at)}
                                </div>
                              </div>
                              
                              {/* Full Content - Show AI summary if available, otherwise show raw content */}
                              <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {cleanContentForDisplay(idea.summary || idea.content, 1200, contentBasedId, expandedRows[contentBasedId] || globalExpanded, isAISummaryContent(idea))}
                              </div>
                              
                              {/* Expand/Collapse Button if needed */}
                              {isContentTruncated(idea.summary || idea.content, 1200, contentBasedId, idea) && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleBookmarkExpansion(contentBasedId);
                                  }}
                                  className={`mt-2 inline-flex items-center px-2 py-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 bg-slate-600 border-slate-500 hover:bg-slate-500' : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'} border rounded-md transition-all duration-200`}
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedRows[contentBasedId] || globalExpanded ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"} />
                                  </svg>
                                  {expandedRows[contentBasedId] || globalExpanded ? 'Show Less' : 'Show More'}
                                </button>
                              )}
                            </div>
                            
                            {/* Action Buttons - Mobile optimized grid, Desktop expanded bubbles */}
                            <div className="flex items-center justify-center gap-1">
                              {/* View Original Button */}
                              {(idea.content || idea.tweet_content || idea.original_content) && (
                                <button
                                  onClick={async () => {
                                    // DEBUG: Log what we have
                                    console.log('🔍 Original button clicked - Debugging:', {
                                      id: idea.id,
                                      source: idea.source,
                                      is_twitter_content: idea.is_twitter_content,
                                      has_original_content: !!idea.original_content,
                                      has_content: !!idea.content,
                                      has_twitterMetadata: !!idea.twitterMetadata,
                                      original_content_length: idea.original_content?.length,
                                      content_length: idea.content?.length
                                    });
                                    
                                    // Get full formatted tweet content (includes media URLs, links, etc.)
                                    // For threads, prioritize the lead tweet (first in full_thread)
                                    const twitterMeta = idea.twitterMetadata || idea.metadata?.twitter_content?.tweet_data || idea.metadata?.twitter_data?.[0] || null;
                                    const hasThread = twitterMeta?.is_thread && twitterMeta?.full_thread?.length > 0;
                                    const leadTweetText = hasThread ? twitterMeta.full_thread[0]?.text : null;
                                    const fullTweetContent = leadTweetText ||
                                                            idea.original_content ||  // Individual tweet text (best for nested cards)
                                                            idea.twitterMetadata?.text ||  // Full thread text (for parent cards)
                                                            idea.metadata?.twitter_content?.tweets?.[0]?.text ||
                                                            idea.metadata?.original_email_content || 
                                                            idea.metadata?.twitter_data?.[0]?.text || 
                                                            idea.tweet_content ||
                                                            '';
                                    const shouldUseTweet = (idea.source === 'bookmark' || idea.source?.startsWith('x_bookmark') || idea.is_twitter_content) && fullTweetContent;
                                    
                                    console.log('🔍 Computed values:', {
                                      fullTweetContent_length: fullTweetContent.length,
                                      shouldUseTweet,
                                      will_use: shouldUseTweet ? 'fullTweetContent' : 'original_content || content'
                                    });
                                    
                                    setSelectedOriginalContent(null);
                                    
                                    let fullPdfData = idea.metadata?.pdf_data || null;
                                    if (idea.metadata?.has_pdf_attachments && idea.id) {
                                      try {
                                        const response = await fetch(`${API_BASE_URL}/api/pdf-content/${idea.id}`);
                                        if (response.ok) {
                                          const pdfResponse = await response.json();
                                          fullPdfData = pdfResponse.pdf_data;
                                        }
                                      } catch (error) {
                                        console.error('Error fetching PDF content:', error);
                                      }
                                    }
                                    
                                    setTimeout(() => {
                                      // Extract Twitter metadata if available - check multiple locations
                                      const twitterData = idea.twitterMetadata ||  // Root level (newer format)
                                                         idea.metadata?.twitter_content?.tweet_data || 
                                                         idea.metadata?.twitter_data?.[0] ||
                                                         null;
                                      
                                      // For NESTED thread cards, always use plain text (not Twitter formatting)
                                      const isNestedCard = !!idea.parent_id;
                                      const contentToSet = isNestedCard ? (idea.original_content || idea.content) : 
                                                          (shouldUseTweet ? fullTweetContent : (idea.original_content || idea.content));
                                      
                                      console.log('🔍 Setting modal content:', {
                                        isNestedCard,
                                        contentToSet_length: contentToSet?.length,
                                        contentToSet_preview: contentToSet?.substring(0, 100)
                                      });
                                      
                                      setSelectedOriginalContent({
                                        id: idea.id, // Add the missing id field for PDF URL generation
                                        title: idea.title,
                                        content: contentToSet,
                                        htmlContent: shouldUseTweet ? null : (() => {
                                          // Check for HTML content in multiple locations
                                          const htmlContent = idea.html_content || 
                                                             idea.original_email_content ||
                                                             idea.metadata?.email_html_content || 
                                                             idea.metadata?.original_email_content ||
                                                             null;
                                          
                                          // Simple HTML detection
                                          if (htmlContent && (
                                            htmlContent.includes('<div') ||
                                            htmlContent.includes('<p>') ||
                                            htmlContent.includes('<html>') ||
                                            htmlContent.includes('<body')
                                          )) {
                                            return htmlContent;
                                          }
                                          return null;
                                        })(),
                                        subject: idea.metadata?.email_subject || idea.title || 'Email Content',
                                        from: idea.source_info?.source_name || idea.from_name || idea.from_address,
                                        isTwitterContent: isNestedCard ? false : (idea.is_twitter_content || idea.source?.startsWith('x_bookmark') || false),  // Force plain text for nested cards
                                        // X/Twitter Display Requirements - all required metadata INCLUDING THREAD DATA
                                        twitterMetadata: twitterData ? {
                                          authorName: twitterData.name || twitterData.author?.name,
                                          username: twitterData.username || twitterData.author?.username,
                                          profileImageUrl: twitterData.profileImageUrl || twitterData.profile_image_url || twitterData.author?.profile_image_url,
                                          tweetUrl: twitterData.url || twitterData.tweet_url || idea.metadata?.twitter_content?.tweet_url,
                                          createdAt: twitterData.created_at || twitterData.createdAt,
                                          metrics: twitterData.metrics,
                                          // PRESERVE THREAD DATA
                                          is_thread: twitterData.is_thread || false,
                                          thread_length: twitterData.thread_length || 1,
                                          full_thread: twitterData.full_thread || null,
                                          thread_info: twitterData.thread_info || null,
                                          // CONVERSATION CONTEXT FOR REPLIES
                                          conversation_context: twitterData.conversation_context || null,
                                          has_context: twitterData.has_context || false,
                                          // MEDIA DATA
                                          media: twitterData.media || null
                                        } : null,
                                        hasPdfAttachments: idea.metadata?.has_pdf_attachments || false,
                                        pdfData: fullPdfData,
                                        platformSource: idea.metadata?.platform_source || 'Email'
                                      });
                                    }, 10);
                                    setShowOriginalContentModal(true);
                                  }}
                                  className={`px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0 flex-shrink-0 border-2 ${
                    isDarkMode
                      ? 'border-blue-300 text-blue-200 hover:bg-blue-700 hover:text-white hover:border-blue-200'
                      : 'border-blue-400 text-blue-600 hover:bg-blue-500 hover:text-white hover:border-blue-500'
                  }`}
                                  title="View full original email content"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                  </svg>
                                  <span className="text-[10px] sm:text-xs">Open</span>
                                </button>
                              )}

                              {/* Check if this is an Ideas List card */}
                              {(() => {
                                const isIdeasListCard = idea.is_ticker_list || idea.title === "Ideas List";
                                
                                if (isIdeasListCard) {
                                  // IDEAS LIST: 4 buttons - Original, Save List, Share, Delete
                                  return (
                                    <>
                                      {/* Save List Button */}
                                      <button
                                        onClick={async () => {
                                          const tickersList = idea.all_tickers || idea.tickers || [];
                                          if (tickersList.length > 0) {
                                            const summaryText = idea.one_liner_thesis || idea.summary || "Ideas List";
                                            let watchlistName = summaryText.split(/[.!?]/)[0].trim();
                                            if (watchlistName.length > 50) {
                                              watchlistName = watchlistName.substring(0, 50) + "...";
                                            }
                                            try {
                                              const tickerNames = tickersList.map(t => typeof t === 'string' ? t : t.ticker);
                                              const response = await fetch(`${API_BASE_URL}/api/watchlists`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                  user_id: user?.id || 'demo_user',
                                                  name: watchlistName,
                                                  tickers: tickerNames
                                                })
                                              });
                                              if (response.ok) {
                                                alert(`✅ Saved "${watchlistName}" with ${tickerNames.length} tickers to Watchlists!`);
                                              }
                                            } catch (error) {
                                              console.error('Error saving watchlist:', error);
                                            }
                                          }
                                        }}
                                        className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                          isDarkMode
                                            ? 'border-green-300 text-green-200 hover:bg-green-700 hover:text-white hover:border-green-200'
                                            : 'border-green-400 text-green-600 hover:bg-green-500 hover:text-white hover:border-green-500'
                                        }`}
                                        title="Save all tickers as a new ticker list"
                                      >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                                        </svg>
                                        <span className="text-[10px] sm:text-xs">Save List</span>
                                      </button>
                                      
                                      {/* Share Button */}
                                      <button
                                        onClick={() => {
                                          setSelectedTickerForScheduling({
                                            bookmarkId: idea.id,
                                            ticker: idea.tickers?.[0],
                                            content: idea.content,
                                            tweetId: idea.twitterMetadata?.tweet_id || idea.global_tweet_id,
                                            shareType: 'idea'
                                          });
                                          setShowShareModal(true);
                                        }}
                                        className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                          isDarkMode
                                            ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10'
                                            : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'
                                        }`}
                                        title="Share this Ideas List"
                                      >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                        </svg>
                                        <span className="text-[10px] sm:text-xs">Share</span>
                                      </button>
                                    </>
                                  );
                                } else {
                                  // REGULAR CARDS: 5 more buttons - Brief, Schedule, Share, Watchlist
                                  return (
                                    <>
                                      {/* Brief Button */}
                                      <button
                                        onClick={() => {
                                          setSelectedTickerForScheduling({
                                            bookmarkId: idea.id,
                                            ticker: idea.tickers?.[0],
                                            content: idea.content
                                          });
                                          setShowDocumentModal(true);
                                          checkSECAvailability(idea.tickers?.[0]);
                                        }}
                                        className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 min-w-0 flex-shrink-0 border-2 ${
                                          isDarkMode
                                            ? 'border-slate-300 text-slate-100 hover:bg-slate-600 hover:text-white hover:border-slate-100'
                                            : 'border-slate-400 text-slate-600 hover:bg-slate-500 hover:text-white hover:border-slate-500'
                                        }`}
                                        title={`Generate Research for ${idea.tickers?.[0]}`}
                                      >
                                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                        <span>File</span>
                                      </button>
                                      
                                      {/* Schedule Button */}
                                      <button
                                        onClick={() => {
                                          openCalendarModal({
                                            bookmarkId: idea.id,
                                            ticker: idea.tickers?.[0],
                                            content: idea.content
                                          });
                                        }}
                                        className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 border-2 ${
                                          isDarkMode
                                            ? 'border-purple-300 text-purple-200 hover:bg-purple-700 hover:text-white hover:border-purple-200'
                                            : 'border-purple-400 text-purple-600 hover:bg-purple-500 hover:text-white hover:border-purple-500'
                                        }`}
                                        title={`Schedule research reminder for ${idea.tickers?.[0]}`}
                                      >
                                        <FolderIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        <span>Folder</span>
                                      </button>
                                      
                                      {/* Share Button */}
                                      <button
                                        onClick={() => {
                                          setSelectedTickerForScheduling({
                                            bookmarkId: idea.id,
                                            ticker: idea.tickers?.[0],
                                            content: idea.content,
                                            tweetId: idea.twitterMetadata?.tweet_id || idea.global_tweet_id,
                                            shareType: 'idea'
                                          });
                                          setShowShareModal(true);
                                        }}
                                        className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 border-2 ${
                                          isDarkMode
                                            ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10'
                                            : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'
                                        }`}
                                        title={`Share ${idea.tickers?.[0]} investment idea with colleague`}
                                      >
                                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                        </svg>
                                        <span>Share</span>
                                      </button>
                                      
                                      {/* Watchlist Button */}
                                      <button
                                        onClick={async () => {
                                          const ticker = idea.tickers?.[0];
                                          if (ticker) {
                                            try {
                                              const response = await fetch(`${API_BASE_URL}/api/watchlists/add-ticker`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({
                                                  user_id: user?.id || 'demo_user',
                                                  ticker: ticker
                                                })
                                              });
                                              if (response.ok) {
                                                alert(`✅ Added ${ticker} to your watchlist!`);
                                              }
                                            } catch (error) {
                                              console.error('Error adding to watchlist:', error);
                                            }
                                          }
                                        }}
                                        className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 border-2 ${
                                          isDarkMode
                                            ? 'border-green-300 text-green-200 hover:bg-green-700 hover:text-white hover:border-green-200'
                                            : 'border-green-400 text-green-600 hover:bg-green-500 hover:text-white hover:border-green-500'
                                        }`}
                                        title={`Add ${idea.tickers?.[0]} to tickers`}
                                      >
                                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                        <span>Tickers</span>
                                      </button>
                                    </>
                                  );
                                }
                              })()}
                              
                              {/* Delete Button - Always shown */}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  
                                  const tickerName = idea.primary_ticker || idea.tickers?.[0] || 'this idea';
                                  
                                  setConfirmModal({
                                    isOpen: true,
                                    title: 'Delete Investment Idea',
                                    message: `Are you sure you want to delete the idea for $${tickerName}? This cannot be undone.`,
                                    confirmText: 'Delete',
                                    cancelText: 'Cancel',
                                    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
                                    onConfirm: async () => {
                                      try {
                                        const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${idea.id}`, {
                                          method: 'DELETE'
                                        });
                                        
                                        const responseData = await response.json();
                                        
                                        if (response.ok && responseData.success !== false) {
                                          setWatchlistModalIdeas(watchlistModalIdeas.filter(i => i.id !== idea.id));
                                          setBookmarks(bookmarks.filter(b => b.id !== idea.id));
                                          showNotification(`Deleted idea for $${tickerName}`, 'success');
                                        } else {
                                          const errorMsg = responseData.error || responseData.message || 'Delete failed';
                                          showNotification('Delete failed: ' + errorMsg, 'error');
                                        }
                                      } catch (error) {
                                        console.error('DELETE error:', error);
                                        showNotification('Error: ' + error.message, 'error');
                                      }
                                    }
                                  });
                                }}
                                className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 border-2 ${
                                  isDarkMode
                                    ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                    : 'border-red-300 text-red-600 hover:bg-red-50'
                                }`}
                                title="Delete this investment idea"
                              >
                                <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                                <span>Delete</span>
                              </button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* LIBRARY TAB CONTENT - TEMPORARILY HIDDEN - To restore: change false to true below */}
          {false && <TabsContent value="research">
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                <div className="p-4 bg-slate-800 text-white">
                  {/* Feed/Sources toggle on left, action buttons on right */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: View Toggle */}
                    <div className="flex items-center bg-black/20 rounded-lg p-1">
                      <button
                        onClick={() => setLibraryView('feed')}
                        className={`flex-1 sm:flex-none px-3 py-1 text-xs sm:text-sm font-medium rounded transition-all duration-200 ${
                          libraryView === 'feed' 
                            ? 'bg-white text-slate-800 shadow-sm' 
                            : 'text-white/70 hover:text-white'
                        }`}
                      >
                        Feed
                      </button>
                      <button
                        onClick={() => setLibraryView('sources')}
                        className={`flex-1 sm:flex-none px-3 py-1 text-xs sm:text-sm font-medium rounded transition-all duration-200 ${
                          libraryView === 'sources' 
                            ? 'bg-white text-slate-800 shadow-sm' 
                            : 'text-white/70 hover:text-white'
                        }`}
                      >
                        Sources
                      </button>
                    </div>
                    
                    {/* Right side: Action buttons with wider Sort button on mobile */}
                    <div className="grid grid-cols-8 gap-1 sm:flex sm:gap-3 sm:flex-nowrap">
                        {/* Import X Bookmarks Button - 1 column - Direct import, no modal */}
                        <button
                          onClick={handleDirectBookmarkImport}
                          disabled={loading}
                          className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1 sm:gap-2 col-span-1 w-full justify-center ${
                            loading
                              ? 'text-white/40 cursor-not-allowed' 
                              : 'text-white/70 hover:text-white hover:bg-white/10'
                          }`}
                          title="Import X Bookmarks"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                          </svg>
                          <span className="hidden sm:inline">Import</span>
                        </button>
                        
                        {/* Source Filter - 2 columns */}
                        <div className="relative col-span-2">
                          <button
                            onClick={() => setShowLibrarySourceDropdown(!showLibrarySourceDropdown)}
                            className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 w-full justify-center"
                          >
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                            </svg>
                            <span className="hidden sm:inline">All</span>
                            <span className="sm:hidden">Filter</span>
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showLibrarySourceDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}/>
                            </svg>
                          </button>
                          
                          {/* Dropdown Menu */}
                          {showLibrarySourceDropdown && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowLibrarySourceDropdown(false)}
                              />
                              <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 z-50 max-h-80 overflow-y-auto ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                                {/* Always show "All" option */}
                                <button
                                  onClick={() => {
                                    setLibrarySourceFilter('all');
                                    setShowLibrarySourceDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 ${librarySourceFilter === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"/>
                                  </svg>
                                  All
                                </button>
                                
                                {/* Show only sources that exist in library items */}
                                {libraryAvailableSources.map(source => {
                                  // Map platform name to URL for categorizByDomain
                                  const platformToUrl = {
                                    'X': 'https://x.com',
                                    'Substack': 'https://substack.com',
                                    'Reddit': 'https://reddit.com',
                                    'CNBC': 'https://cnbc.com',
                                    'Bloomberg': 'https://bloomberg.com',
                                    'Wall Street Journal': 'https://wsj.com',
                                    'Financial Times': 'https://ft.com',
                                    'Seeking Alpha': 'https://seekingalpha.com',
                                    'The Motley Fool': 'https://fool.com',
                                    'MarketWatch': 'https://marketwatch.com',
                                    'Reuters': 'https://reuters.com',
                                    "Barron's": 'https://barrons.com',
                                    'Value Investors Club': 'https://valueinvestorsclub.com',
                                    'SumZero': 'https://sumzero.com',
                                    'Medium': 'https://medium.com',
                                    'LinkedIn': 'https://linkedin.com'
                                  };
                                  
                                  // Create a mock item with URL for categorizByDomain
                                  const mockItem = {
                                    url: platformToUrl[source] || null,
                                    source_info: {
                                      source_name: source
                                    },
                                    from_address: null
                                  };
                                  
                                  const category = categorizByDomain(mockItem);
                                  
                                  return (
                                    <button
                                      key={source}
                                      onClick={() => {
                                        setLibrarySourceFilter(source);
                                        setShowLibrarySourceDropdown(false);
                                      }}
                                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 ${librarySourceFilter === source ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                                    >
                                      <div className="w-4 h-4 flex items-center justify-center">
                                        {(() => {
                                          // Render icon using same logic as Library cards
                                          if (category.svg) {
                                            return (
                                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                <path d={category.svg} />
                                              </svg>
                                            );
                                          } else if (category.domain) {
                                            return (
                                              <img 
                                                src={`https://www.google.com/s2/favicons?domain=${category.domain}&sz=64`} 
                                                alt={source} 
                                                className="w-4 h-4 object-contain" 
                                              />
                                            );
                                          } else {
                                            return (
                                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                              </svg>
                                            );
                                          }
                                        })()}
                                      </div>
                                      {source}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Sort Dropdown Button - 2 columns */}
                        <div className="relative sort-dropdown-container col-span-2">
                          <button 
                            onClick={() => setShowLibrarySortDropdown(!showLibrarySortDropdown)}
                            className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-orange-500 text-white border border-orange-500 rounded-lg hover:bg-orange-600 transition-all duration-200 w-full justify-center"
                          >
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                            </svg>
                            <span className="hidden sm:inline">Sort</span>
                            <span className="sm:hidden">Sort</span>
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {/* Sort Dropdown Menu */}
                          {showLibrarySortDropdown && (
                            <>
                              <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowLibrarySortDropdown(false)}
                              />
                              <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 z-50 ${isDarkMode ? 'bg-[#1a2235] border border-slate-700' : 'bg-white border border-gray-200'}`}>
                                <button
                                  onClick={() => {
                                    setLibrarySortBy('newest');
                                    setShowLibrarySortDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${librarySortBy === 'newest' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                                >
                                  Newest First
                                </button>
                                <button
                                  onClick={() => {
                                    setLibrarySortBy('oldest');
                                    setShowLibrarySortDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${librarySortBy === 'oldest' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                                >
                                  Oldest First
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                    </div>
                  </div>
                </div>
                
                <div className={`p-6 ${isDarkMode ? 'bg-[#0f1419]' : 'bg-white'}`}>
                  {researchItems.length === 0 ? (
                    /* Empty State */
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        No Library Items Yet
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} max-w-md mx-auto`}>
                        Your saved research, articles, charts, and reports will appear here automatically. The AI will classify content as you save it.
                      </p>
                      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
                        <p className="text-sm text-blue-800">
                          <strong>Next Steps:</strong> Save content using the bookmarklet, extension, or email. The AI will automatically determine if it's an Investment Idea or Research and place it in the correct tab.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Research Items Display - Same format as Ideas using memoized data */
                    <div className="space-y-6">
                      {(() => {
                        // Track index for numbering
                        let itemIndex = 0;
                        
                        return sortedResearchItems.map((item) => {
                        const currentIndex = itemIndex;
                        itemIndex++;
                        const daysOld = calculateDaysOld(item.created_at);
                        const ageCategory = getAgeCategory(item.created_at);
                        
                        let ageBadgeColor = 'bg-gray-100 text-gray-800';
                        let cardBorderColor = 'border-gray-200';
                        
                        if (ageCategory === 'New') {
                          ageBadgeColor = 'bg-green-100 text-green-800';
                          cardBorderColor = 'border-green-500 border-2';
                        } else if (ageCategory === 'Recent') {
                          ageBadgeColor = 'bg-orange-100 text-orange-800';
                          cardBorderColor = 'border-orange-500 border-2';
                        } else if (ageCategory === 'Older') {
                          ageBadgeColor = 'bg-red-100 text-red-800';
                          cardBorderColor = 'border-red-500 border-2';
                        }
                        
                        return (
                          <Card key={`research-${item.id || item._id || item.created_at}`} className={`${cardBorderColor} ${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white'} hover:shadow-lg transition-all duration-200`}>
                            <CardContent className="p-4 sm:p-6">
                              {/* Header with Number, Title, Age, and Move Button */}
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2">
                                    {/* Number Badge */}
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
                                      {currentIndex + 1}
                                    </div>
                                    
                                    {/* Title */}
                                    <div className={`${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                      <div className="font-bold text-lg sm:text-xl leading-tight">
                                        {item.title || 'Library Item'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Age Badge - Right Side */}
                              </div>
                              
                              {/* Content */}
                              <div className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-1">
                                    {(() => {
                                      // Check if this is an investment letter by type, not name
                                      const isInvestorLetter = item.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                      
                                      // Special handling for Investor Letter - show proper document icon (matches dropdown)
                                      if (isInvestorLetter) {
                                        return (
                                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                          </svg>
                                        );
                                      }
                                      
                                      // Otherwise use normal domain-based categorization
                                      const category = categorizByDomain(item);
                                      const domain = extractDomain(item.url) || extractDomain(item.from_address);
                                      
                                      if (category.svg) {
                                        // Extract text-color from category.color (e.g., "bg-orange-50 text-orange-700" → "text-orange-700")
                                        const textColor = category.color?.split(' ').find(c => c.startsWith('text-')) || '';
                                        return (
                                          <svg className={`w-4 h-4 ${textColor}`} fill="currentColor" viewBox="0 0 24 24">
                                            <path d={category.svg} />
                                          </svg>
                                        );
                                      } else if (domain) {
                                        return (
                                          <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} 
                                               alt={domain} 
                                               className="w-4 h-4 object-contain" />
                                        );
                                      } else {
                                        return (
                                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                          </svg>
                                        );
                                      }
                                    })()}
                                    <span className={`font-medium text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                      {item.source === 'email' || item.source === 'ai_analysis' || item.source === 'shared_idea' ? 
                                        (() => {
                                          // Get base source name
                                          const baseName = (item.source_info?.ai_attribution?.original_source_name && 
                                           item.source_info.ai_attribution.original_source_name.toLowerCase() !== 'unknown' ? 
                                           item.source_info.ai_attribution.original_source_name : null) ||
                                          // Priority 2: For shared ideas, use the full source_name (includes "via" attribution)
                                          (item.source === 'shared_idea' && item.source_info?.source_name ? 
                                            item.source_info.source_name : null) ||
                                          // Priority 3: Clean up source_name - extract just the main source, remove "via" part
                                          (item.source_info?.source_name ? 
                                            item.source_info.source_name.split(' (via ')[0] || item.source_info.source_name : null) ||
                                          // Priority 4: Source address
                                          item.source_info?.source_address ||
                                          // Priority 5: From name - clean up "via" part
                                          (item.from_name ? 
                                            item.from_name.split(' (via ')[0] || item.from_name : null) ||
                                          // Priority 6: Author cleanup
                                            (item.author && item.author !== 'undefined' ? item.author.replace('@', '') : null) ||
                                            'Email';
                                          
                                          // If it's an investor letter, append "(Investor Letter)" to source name
                                          const isInvestorLetter = item.source_info?.ai_attribution?.original_source_type === 'investment_letter';
                                          return isInvestorLetter && baseName ? `${baseName} (Investor Letter)` : baseName;
                                        })() : 
                                        (item.author && item.author !== 'undefined' ? `@${item.author.replace(/^@/, '')}` : 'Unknown')
                                      }
                                    </span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      reclassifyItem(item.id, 'idea');
                                    }}
                                    className={`text-xs px-2 py-1 rounded border-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                                      isDarkMode
                                        ? 'border-blue-300 text-blue-300 hover:bg-blue-700 hover:text-white hover:border-blue-200'
                                        : 'border-blue-300 text-blue-600 hover:bg-blue-500 hover:text-white'
                                    }`}
                                    title="Move to Ideas"
                                  >
                                    → Move to Ideas
                                  </button>
                                </div>
                                
                                <div className={`prose prose-sm max-w-none text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                  {item.summary || item.content?.substring(0, 400) + (item.content?.length > 400 ? '...' : '') || 'No content available'}
                                </div>
                              </div>
                              
                              {/* Action Buttons - Original, Schedule, Share, Delete (No Documents) */}
                              <div className="flex items-center justify-center gap-1">
                                {/* View Original Button */}
                                {(item.content || item.original_content) && (
                                  <button
                                    onClick={() => {
                                      setSelectedOriginalContent(null);
                                      setTimeout(() => {
                                        setSelectedOriginalContent({
                                          id: item.id,
                                          title: item.title || 'Research Content',
                                          content: item.original_email_content || item.metadata?.email_html_content || item.metadata?.original_email_content || item.original_content || item.content,
                                          htmlContent: item.original_email_content || item.metadata?.email_html_content || item.html_content || null,
                                          subject: item.metadata?.email_subject || item.title || 'Research Content',
                                          from: item.from_name || item.author || 'Unknown',
                                          platform: item.platform || 'Email',
                                          platformSource: item.is_twitter_content ? 'Twitter' :
                                                         item.metadata?.platform_source || item.platform || 'Email',
                                          isTwitterContent: item.is_twitter_content || false,
                                          twitterMetadata: item.twitterMetadata || null,  // Changed from twitterData to twitterMetadata for consistency
                                          metadata: item.metadata || {}
                                        });
                                        setShowOriginalContentModal(true);
                                      }, 10);
                                    }}
                                    className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-blue-300 text-blue-600 hover:bg-blue-50"
                                    title="View original content"
                                  >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                    </svg>
                                    <span className="text-[10px] sm:text-xs">Open</span>
                                  </button>
                                )}
                                
                                {/* Schedule Button */}
                                <button
                                  onClick={() => {
                                    openCalendarModal({
                                      bookmarkId: item.id,
                                      ticker: item.tickers?.[0] || 'RESEARCH',
                                      content: item.content
                                    });
                                  }}
                                  className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-purple-300 text-purple-600 hover:bg-purple-50"
                                  title="Schedule library item reminder"
                                >
                                  <FolderIcon className="w-6 h-6" />
                                  <span className="text-[10px] sm:text-xs">Folder</span>
                                </button>
                                
                                {/* Share Button */}
                                <button
                                  onClick={() => {
                                    setSelectedTickerForScheduling({
                                      bookmarkId: item.id,
                                      ticker: item.tickers?.[0] || 'RESEARCH',
                                      content: item.content,
                                      tweetId: item.twitterMetadata?.tweet_id || item.global_tweet_id,
                                      shareType: 'idea'
                                    });
                                    setShowShareModal(true);
                                  }}
                                  className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                                  title="Share library item with colleague"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                  </svg>
                                  <span className="text-[10px] sm:text-xs">Share</span>
                                </button>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    
                                    try {
                                      const response = await fetch(`${API_BASE_URL}/api/investment-ideas/${item.id}`, {
                                        method: 'DELETE'
                                      });
                                      
                                      const responseData = await response.json();
                                      
                                      // CRITICAL FIX: Check the JSON success field, not just HTTP status
                                      if (response.ok && responseData.success !== false) {
                                        setResearchItems(researchItems.filter(r => r.id !== item.id));
                                      } else {
                                        const errorMsg = responseData.error || responseData.message || 'Delete failed';
                                        alert('Delete failed: ' + errorMsg);
                                      }
                                    } catch (error) {
                                      console.error('DELETE error:', error);
                                      alert('Error: ' + error.message);
                                    }
                                  }}
                                  className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-red-300 text-red-600 hover:bg-red-50"
                                  title="Delete this library item"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                  <span className="text-[10px] sm:text-xs">Delete</span>
                                </button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      });
                      })()}
                      
                      {/* Load More Button for Library Pagination */}
                      {libraryHasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={loadMoreLibraryItems}
                            disabled={libraryLoadingMore}
                            className={`px-6 py-3 rounded-lg font-medium transition-all ${
                              libraryLoadingMore 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                : isDarkMode 
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            {libraryLoadingMore ? (
                              <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading...
                              </span>
                            ) : (
                              `Load More (${researchItems.length} of ${libraryTotalCount})`
                            )}
                          </button>
                        </div>
                      )}
                      
                      {/* Pagination Info */}
                      {!libraryHasMore && researchItems.length > 0 && (
                        <div className="text-center mt-6 text-sm text-gray-500">
                          Showing all {libraryTotalCount} library items
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>}





          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {/* Show loading until data has been initialized - multiple checks to prevent flash */}
            {(() => {
              // Force loading state on first render - check multiple conditions
              const shouldShowLoading = !scheduleDataInitialized || scheduledResearchLoading || !user;
              console.log('📅 Schedule tab render check:', { scheduleDataInitialized, scheduledResearchLoading, user: !!user, shouldShowLoading });
              return shouldShowLoading;
            })() ? (
              <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <CardContent className="p-0">
                  <div className="p-4 bg-slate-800 text-white">
                    <ScheduleViewTabs
                      scheduleTabView={scheduleTabView}
                      setScheduleTabView={setScheduleTabView}
                    />
                  </div>
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </CardContent>
              </Card>
            ) : (
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                {/* Header - Same pattern as Ideas, Polls, Watchlists, Dossiers */}
                <div className="p-4 bg-slate-800 text-white overflow-visible">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: My Schedule / Settings toggle */}
                    <ScheduleViewTabs
                      scheduleTabView={scheduleTabView}
                      setScheduleTabView={setScheduleTabView}
                    />
                    
                    {/* Center: Keyboard shortcuts hint - Desktop only */}
                    <button
                      onClick={() => setShowKeyboardShortcutsModal(true)}
                      className="hidden sm:flex items-center text-white/50 text-sm"
                      title="View keyboard shortcuts"
                    >
                      <span>Press </span>
                      <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                      <span> for shortcuts</span>
                    </button>
                    
                    {/* Right side: 4 Action Buttons - Same pattern as other tabs */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2">
                      {/* Search Button */}
                      <button
                        onClick={() => setShowMobileSearch(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                        title="Search"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                        <span className="hidden sm:inline">Search</span>
                      </button>
                      
                      {/* Sort Button */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            if (scheduleTabView === 'settings') return;
                            setShowScheduleSortDropdown(!showScheduleSortDropdown);
                            setShowScheduleFilterDropdown(false);
                          }}
                          disabled={scheduleTabView === 'settings'}
                          className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                            scheduleTabView === 'settings'
                              ? 'text-white/30 border-white/10 cursor-not-allowed'
                              : scheduleSortBy !== 'date-asc'
                                ? 'bg-white/20 text-white border-white/40'
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                          }`}
                          title={scheduleTabView === 'settings' ? 'Not available in Settings' : 'Sort schedule'}
                        >
                          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                          </svg>
                          <span className="hidden sm:inline">Sort</span>
                        </button>
                        
                        {/* Sort Dropdown */}
                        {showScheduleSortDropdown && (
                          <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl border z-[100] ${
                            isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'
                          }`}>
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  setScheduleSortBy('date-asc');
                                  setScheduleViewMode('list');
                                  setShowScheduleSortDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleSortBy === 'date-asc'
                                    ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"/>
                                </svg>
                                Date (Soonest first)
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleSortBy('date-desc');
                                  setScheduleViewMode('list');
                                  setShowScheduleSortDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleSortBy === 'date-desc'
                                    ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                </svg>
                                Date (Latest first)
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleSortBy('ticker-asc');
                                  setScheduleViewMode('list');
                                  setShowScheduleSortDropdown(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleSortBy === 'ticker-asc'
                                    ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9"/>
                                </svg>
                                Ticker (A-Z)
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Filter Button */}
                      <div className="relative">
                        <button
                          onClick={() => {
                            if (scheduleTabView === 'settings') return;
                            setShowScheduleFilterDropdown(!showScheduleFilterDropdown);
                            setShowScheduleSortDropdown(false);
                          }}
                          disabled={scheduleTabView === 'settings'}
                          className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                            scheduleTabView === 'settings'
                              ? 'text-white/30 border-white/10 cursor-not-allowed'
                              : scheduleFilterBy !== '31days' || scheduleViewMode === '12month'
                                ? 'bg-white/20 text-white border-white/40'
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                          }`}
                          title={scheduleTabView === 'settings' ? 'Not available in Settings' : 'Filter schedule'}
                        >
                          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                          </svg>
                          <span className="hidden sm:inline">Filter</span>
                        </button>
                        
                        {/* Filter Dropdown */}
                        {showScheduleFilterDropdown && (
                          <div className={`absolute right-0 mt-2 w-52 rounded-lg shadow-xl border z-[100] ${
                            isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'
                          }`}>
                            <div className="py-1">
                              {/* Time-based Views Section */}
                              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                                isDarkMode ? 'text-slate-500' : 'text-slate-400'
                              }`}>
                                Views
                              </div>
                              <button
                                onClick={() => {
                                  setScheduleFilterBy('31days');
                                  setScheduleCompletionFilter('all');
                                  setScheduleViewMode('list');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-31days"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleFilterBy === '31days' && scheduleViewMode !== '12month'
                                    ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                31 Days
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleViewMode('12month');
                                  setScheduleFilterBy('all');
                                  setScheduleCompletionFilter('all');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-12months"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleViewMode === '12month'
                                    ? isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                                </svg>
                                12 Months
                              </button>
                              
                              <div className={`border-t my-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                              
                              {/* Source Filters */}
                              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                                isDarkMode ? 'text-slate-500' : 'text-slate-400'
                              }`}>
                                Source
                              </div>
                              <button
                                onClick={() => {
                                  setScheduleFilterBy('auto');
                                  setScheduleCompletionFilter('all');
                                  setScheduleViewMode('list');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-auto-scheduled"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleFilterBy === 'auto' && scheduleViewMode !== '12month'
                                    ? isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-50 text-orange-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                </svg>
                                Auto Scheduled
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleFilterBy('manual');
                                  setScheduleCompletionFilter('all');
                                  setScheduleViewMode('list');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-manual-only"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleFilterBy === 'manual' && scheduleViewMode !== '12month'
                                    ? isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                </svg>
                                Manual Only
                              </button>
                              
                              <div className={`border-t my-1 ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}></div>
                              
                              {/* History Section */}
                              <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                                isDarkMode ? 'text-slate-500' : 'text-slate-400'
                              }`}>
                                History
                              </div>
                              <button
                                onClick={() => {
                                  setScheduleFilterBy('past');
                                  setScheduleCompletionFilter('all');
                                  setScheduleViewMode('list');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-past-items"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleFilterBy === 'past' && scheduleViewMode !== '12month'
                                    ? isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                Past Items
                              </button>
                              <button
                                onClick={() => {
                                  setScheduleCompletionFilter('completed');
                                  setScheduleFilterBy('all');
                                  setScheduleViewMode('list');
                                  setShowScheduleFilterDropdown(false);
                                }}
                                data-testid="filter-completed"
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                  scheduleCompletionFilter === 'completed'
                                    ? isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-50 text-green-700'
                                    : isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                </svg>
                                Completed
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Add/New Button - Orange action button */}
                      <button
                        onClick={() => {
                          if (scheduleTabView === 'settings') return;
                          setShowAddToScheduleModal(true);
                        }}
                        disabled={scheduleTabView === 'settings'}
                        className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
                          scheduleTabView === 'settings'
                            ? 'bg-orange-300 text-white/50 cursor-not-allowed'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                        title={scheduleTabView === 'settings' ? 'Not available in Settings' : 'Add to schedule'}
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                        </svg>
                        <span className="hidden sm:inline">New</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Schedule body content - reused from renderScheduleBodyContent function */}
                {renderScheduleBodyContent()}
              </CardContent>
            </Card>
            )}
          </TabsContent>



          {/* Groups Tab */}
          <TabsContent value="groups">
            <div {...groupSwipeHandlers}>
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                {/* Single-Row Header (like Dashboard) */}
                <div className="p-4 bg-slate-800 text-white">
                  {/* Mobile: Two rows | Desktop: Single row */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: My Groups / Invited toggle */}
                    <GroupViewTabs
                      groupView={groupView}
                      setGroupView={setGroupView}
                      myGroupsCount={userGroups.filter(g => g.user_role === 'admin').length}
                      invitedCount={userGroups.filter(g => g.user_role !== 'admin').length + pendingInvitations.length}
                    />
                    
                    {/* Center: Keyboard shortcuts hint - Desktop only, subtle */}
                    <button
                      onClick={() => setShowKeyboardShortcutsModal(true)}
                      className="hidden sm:flex items-center text-white/50 text-sm"
                      title="View keyboard shortcuts"
                    >
                      <span>Press </span>
                      <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                      <span> for shortcuts</span>
                    </button>
                    
                    {/* Right side: Action Buttons */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2">
                        {/* Search Button */}
                        <button
                          onClick={() => setShowMobileSearch(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                          title="Search"
                        >
                          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                          </svg>
                          <span className="hidden sm:inline">Search</span>
                        </button>
                        
                        {/* Sort by Age Button - Only enabled when viewing group ideas */}
                        <div className="relative group-age-sort-container">
                          <button
                            onClick={() => selectedGroupIdeas ? setShowGroupAgeSortMenu(!showGroupAgeSortMenu) : null}
                            disabled={!selectedGroupIdeas}
                            className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                              !selectedGroupIdeas
                                ? 'text-white/30 border-white/10 cursor-not-allowed'
                                : groupAgeSortBy !== 'newest'
                                  ? 'text-orange-400 border-orange-400/50 bg-orange-500/10'
                                  : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                            }`}
                            title={selectedGroupIdeas ? "Sort by age" : "Select a group or member to enable sorting"}
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                            </svg>
                            <span className="hidden sm:inline">Sort</span>
                          </button>
                          
                          {showGroupAgeSortMenu && selectedGroupIdeas && (
                            <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                              <div className="py-1">
                                <button
                                  onClick={() => { setGroupAgeSortBy('newest'); setShowGroupAgeSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${groupAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  Newest First
                                </button>
                                <button
                                  onClick={() => { setGroupAgeSortBy('fresh'); setShowGroupAgeSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${groupAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                    New (&lt;10 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setGroupAgeSortBy('warm'); setShowGroupAgeSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${groupAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                                    Recent (10-30 days)
                                  </div>
                                </button>
                                <button
                                  onClick={() => { setGroupAgeSortBy('stale'); setShowGroupAgeSortMenu(false); }}
                                  className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${groupAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                                >
                                  <div className="flex items-center">
                                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                                    Older (&gt;30 days)
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Filter Button (Funnel) - No options yet */}
                        <div className="relative group-type-filter-container">
                          <button
                            onClick={() => setShowGroupTypeFilterMenu(!showGroupTypeFilterMenu)}
                            className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                            title="Filter groups"
                          >
                            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                            </svg>
                            <span className="hidden sm:inline">Filter</span>
                          </button>
                          
                          {showGroupTypeFilterMenu && (
                            <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'} border`}>
                              <div className="py-3 px-4 text-center">
                                <svg className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                                </svg>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No filter options yet</p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Coming soon!</p>
                              </div>
                            </div>
                          )}
                        </div>
                      
                      {/* New Group Button - Orange */}
                      <button
                        type="button"
                        onClick={() => setShowCreateGroupModal(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                        </svg>
                        <span className="hidden sm:inline">New</span>
                      </button>
                      
                    </div>
                  </div>
                </div>

                {/* Groups Content */}
                <div className={`p-3 sm:p-4 ${isDarkMode ? 'bg-[#0f1419]' : 'bg-white'}`}>
                  
                  {/* Empty state when no group selected (and not in View All mode) */}
                  {!selectedGroupFilter && !viewAllGroupsMode && (
                    <div className={`p-6 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      <p className="text-lg font-medium mb-2">Select a group to view details</p>
                      <p className="text-sm">Choose a group from the dropdown above to see members and shared ideas, or create a new group.</p>
                    </div>
                  )}
                  
                  {/* View All Groups Mode - Show full group cards directly */}
                  {viewAllGroupsMode && !selectedGroupFilter && !selectedGroupIdeas && (
                    <div className="space-y-3">
                      {/* Real Groups */}
                      {userGroups
                        .filter(group => {
                          // Apply age-based filtering
                          if (groupAgeSortBy === 'newest') return true;
                          const days = calculateDaysOld(group.created_at || group.updated_at);
                          if (groupAgeSortBy === 'fresh') return days < 10;
                          if (groupAgeSortBy === 'warm') return days >= 10 && days <= 30;
                          if (groupAgeSortBy === 'stale') return days > 30;
                          return true;
                        })
                        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                        .map((group) => {
                          const isExpanded = expandedGroups[group.group_id];
                          const members = group.members || [];
                          const memberCount = members.length || group.member_count || 1;
                          const firstTwoMembers = members.slice(0, 2).map(m => m.name || m.email?.split('@')[0] || 'Member').join(', ') || 'You';
                          const extraMembers = memberCount > 2 ? memberCount - 2 : 0;
                          const ideasCount = groupSharedIdeas[group.group_id]?.length || 0;
                          
                          return (
                        <div key={group.group_id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                          {/* Status Bar - Left Edge (orange=admin, purple=member) */}
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${group.user_role === 'admin' ? 'bg-orange-500' : 'bg-purple-500'}`} />
                          {/* Card content - clickable to expand */}
                          <button 
                            onClick={() => toggleGroupExpansion(group.group_id)}
                            className={`w-full px-4 pl-5 py-3 ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} transition-colors text-left`}
                          >
                            <div className="space-y-0.5">
                              {/* Row 1: Group icon + name | member count · ideas count (far right) */}
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                  </svg>
                                  <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{group.name}</span>
                                </div>
                                <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {memberCount} member{memberCount !== 1 ? 's' : ''} · <span className="text-orange-500">{ideasCount} idea{ideasCount !== 1 ? 's' : ''}</span>
                                </span>
                              </div>
                              
                              {/* Row 2: Latest idea sharer info | arrow (far right) */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 ml-7">
                                  {group.latest_idea?.shared_by_image && (
                                    <img src={group.latest_idea.shared_by_image} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                  )}
                                  {group.latest_idea?.shared_by && (
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      @{group.latest_idea.shared_by.replace('@', '')}
                                    </span>
                                  )}
                                  {group.latest_idea?.ticker && (
                                    <span className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      {group.latest_idea.ticker}
                                    </span>
                                  )}
                                  {group.latest_idea?.company_name && (
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} truncate max-w-[150px]`}>
                                      {group.latest_idea.company_name}
                                    </span>
                                  )}
                                </div>
                                <svg className={`w-5 h-5 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                            
                          {/* Row 3: Action buttons - Only show when expanded */}
                          {isExpanded && (
                          <div className={`flex flex-wrap items-center justify-center gap-1 px-2 sm:px-4 py-3 border-t ${isDarkMode ? 'border-slate-700 bg-[#1a2235]/50' : 'border-slate-100 bg-slate-50'}`}>
                              {group.user_role === 'admin' ? (
                                <>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); inviteToGroup(group.group_id); }}
                                    className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                  >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                                    </svg>
                                    <span className="text-[10px] sm:text-xs">Invite</span>
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); leaveGroup(group.group_id, group.name); }}
                                    className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                                  >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                                    </svg>
                                    <span className="text-[10px] sm:text-xs">Leave</span>
                                  </button>
                                </>
                              ) : (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); leaveGroup(group.group_id, group.name); }}
                                  className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                                  </svg>
                                  <span className="text-[10px] sm:text-xs">Leave</span>
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Expanded Content - Shared Ideas */}
                          {isExpanded && (
                            <div className={`border-t-2 ${isDarkMode ? 'border-slate-600 bg-[#1a2235]/50' : 'border-slate-100 bg-slate-50'}`}>
                              <div className="p-4 space-y-4">
                                {/* Members */}
                                <div data-testid="group-members-section">
                                  <h4 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Members ({(groupMembers[group.group_id] || []).length || group.member_count || 0})
                                  </h4>
                                  {groupMembers[group.group_id] ? (
                                    <div className="flex flex-wrap gap-2">
                                      {groupMembers[group.group_id].map((member) => (
                                        <div key={member.user_id || member.email} className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDarkMode ? 'bg-slate-700/60' : 'bg-white border border-slate-200'}`}>
                                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>
                                            {(member.name || member.email || '?')[0].toUpperCase()}
                                          </div>
                                          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                            {member.name || member.email}
                                          </span>
                                          {member.role === 'admin' && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>Admin</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Loading members...</p>
                                  )}
                                  {/* Pending invites for this group */}
                                  {sentInvitations.filter(inv => inv.group_id === group.group_id && inv.status === 'pending').map((inv) => (
                                    <div key={inv.invitation_id} className="mt-2 flex items-center gap-2" data-testid="group-pending-invite-row">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>
                                        {(inv.invited_email || '?')[0].toUpperCase()}
                                      </div>
                                      <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{inv.invited_email}</span>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>Invited — awaiting response</span>
                                    </div>
                                  ))}
                                </div>
                                <h4 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Shared Ideas</h4>
                                {groupSharedIdeas[group.group_id] ? (
                                  (() => {
                                    // Apply age-based filtering and sorting to ideas
                                    const sortedAndFilteredIdeas = [...(groupSharedIdeas[group.group_id] || [])]
                                      .filter(idea => {
                                        if (groupAgeSortBy === 'newest') return true; // Show all
                                        const days = calculateDaysOld(idea.shared_at);
                                        if (groupAgeSortBy === 'fresh') return days < 10;
                                        if (groupAgeSortBy === 'warm') return days >= 10 && days <= 30;
                                        if (groupAgeSortBy === 'stale') return days > 30;
                                        return true;
                                      })
                                      .sort((a, b) => new Date(b.shared_at || 0) - new Date(a.shared_at || 0)); // Newest first
                                    
                                    return sortedAndFilteredIdeas.length > 0 ? (
                                      sortedAndFilteredIdeas.map((idea) => {
                                    const ideaDaysOld = calculateDaysOld(idea.shared_at);
                                    const ideaAge = ideaDaysOld < 10 ? 'New' : ideaDaysOld <= 30 ? 'Recent' : 'Older';
                                    const ideaBorderColor = ideaAge === 'New' ? 'border-green-500' : ideaAge === 'Recent' ? 'border-orange-500' : 'border-red-500';
                                    const ideaBgColor = ideaAge === 'New' ? 'bg-green-100 text-green-800' : ideaAge === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                                    
                                    return (
                                      <Card 
                                        key={idea.id} 
                                        data-testid={`group-idea-card-${idea.id}`}
                                        className={`border-2 ${ideaBorderColor} ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} hover:shadow-lg transition-all cursor-pointer overflow-hidden`}
                                        onClick={(e) => {
                                          if (e.target.closest('button') || e.target.closest('a')) return;
                                          handleExpandGroupIdeaCard(idea.id, idea.ticker);
                                        }}
                                      >
                                        <CardContent className="p-3 sm:p-4">
                                          {/* Header Row - Matching Ideas Tab Style */}
                                          <div className="flex items-start justify-between mb-2">
                                            {/* Left: Source Icon + Company + Ticker */}
                                            <div className="flex-1 min-w-0">
                                              <div className={`${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                                <div className="flex items-center gap-2">
                                                  {/* Shared idea icon (matching Ideas tab source icon style) */}
                                                  <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                  </svg>
                                                  <div>
                                                    <span className="font-bold text-lg sm:text-xl leading-tight">
                                                      {(() => {
                                                        const tdMetrics = idea.ticker ? tickerMetricsCache[idea.ticker.toUpperCase()] : null;
                                                        return tdMetrics?.company_name || idea.company_name || idea.ticker || 'Shared Idea';
                                                      })()}
                                                    </span>
                                                    {idea.ticker && (
                                                      <span className={`font-bold text-lg sm:text-xl ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                                        {' '}({idea.ticker})
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {/* Right: Expand Arrow */}
                                            <div className="flex-shrink-0 ml-2">
                                              <button
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleExpandGroupIdeaCard(idea.id, idea.ticker);
                                                }}
                                                className={`p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                                              >
                                                <svg 
                                                  className={`w-5 h-5 transition-transform ${expandedGroupIdeaCards.has(idea.id) ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} 
                                                  fill="none" 
                                                  stroke="currentColor" 
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                          
                                          {/* Thesis Row with inline date - Matching Ideas Tab Style */}
                                          <div className={`text-sm flex items-start justify-between gap-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                            <span className="flex-1 min-w-0">
                                              {idea.one_liner || idea.content || 'Shared investment idea'}
                                            </span>
                                            <span className={`text-xs flex-shrink-0 whitespace-nowrap ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {formatDateShort(idea.shared_at)}
                                            </span>
                                          </div>
                                        </CardContent>
                                        
                                        {/* Expanded Content - Full Profile/Valuation Tabs */}
                                        {expandedGroupIdeaCards.has(idea.id) && (
                                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                            {(() => {
                                              const primaryTicker = idea.ticker?.toUpperCase();
                                              const tdMetrics = primaryTicker ? tickerMetricsCache[primaryTicker] : null;
                                              const activeTab = groupIdeaCardMetricsTab[idea.id] || 'valuation';
                                              
                                              // Show loading state while metrics are being fetched
                                              if (!tdMetrics || (!tdMetrics.company_name && !tdMetrics.market_cap)) {
                                                return (
                                                  <div className={`p-3 sm:p-4 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-slate-50/50'}`}>
                                                    <div className="flex items-center justify-center gap-2">
                                                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                      </svg>
                                                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        Loading metrics for {primaryTicker || 'this idea'}...
                                                      </p>
                                                    </div>
                                                  </div>
                                                );
                                              }
                                              
                                              return (
                                                <>
                                                  {/* Pill Toggle */}
                                                  <div className={`flex justify-center py-3 ${isDarkMode ? 'bg-[#141c2e]/30' : 'bg-white'}`}>
                                                    <div className={`inline-flex rounded-full p-1 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); setGroupIdeaCardMetricsTab(prev => ({ ...prev, [idea.id]: 'profile' })); }}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                                          activeTab === 'profile'
                                                            ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                                                            : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                                                        }`}
                                                      >
                                                        Profile
                                                      </button>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); setGroupIdeaCardMetricsTab(prev => ({ ...prev, [idea.id]: 'valuation' })); }}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                                          activeTab === 'valuation'
                                                            ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                                                            : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                                                        }`}
                                                      >
                                                        Valuation
                                                      </button>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Tab Content */}
                                                  <div className={`px-3 sm:px-4 py-3 ${isDarkMode ? 'bg-[#141c2e]/20' : 'bg-white'}`}>
                                                    {activeTab === 'profile' ? (
                                                      /* PROFILE TAB */
                                                      <div className="space-y-1">
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Company</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.company_name || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Exchange</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.exchange || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Industry</span>
                                                          <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.industry || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sector</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.sector || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CEO</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ceo || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Employees</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.employees ? tdMetrics.employees.toLocaleString() : '-'}</span>
                                                        </div>
                                                        {tdMetrics?.phone && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Phone</span>
                                                            <a href={`tel:${tdMetrics.phone}`} onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.phone}</a>
                                                          </div>
                                                        )}
                                                        {tdMetrics?.website && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Website</span>
                                                            <a href={tdMetrics.website.startsWith('http') ? tdMetrics.website : `https://${tdMetrics.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.website.replace(/^https?:\/\//, '')}</a>
                                                          </div>
                                                        )}
                                                        {(tdMetrics?.address || tdMetrics?.city) && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Address</span>
                                                            <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                                              {[tdMetrics?.address, tdMetrics?.city, tdMetrics?.state, tdMetrics?.zip].filter(Boolean).join(', ')}
                                                            </span>
                                                          </div>
                                                        )}
                                                        {tdMetrics?.description && (
                                                          <div className={`pt-2`}>
                                                            <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mb-1`}>Description</div>
                                                            <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{tdMetrics.description}</div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    ) : (
                                                      /* VALUATION/STATISTICS TAB - 16 metrics in 2 even columns */
                                                      <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                                                        {/* Column 1 */}
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Mkt Cap</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.market_cap ? formatFinancialValue(tdMetrics.market_cap) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.enterprise_value ? formatFinancialValue(tdMetrics.enterprise_value) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Fwd P/E</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.forward_pe ? formatRatio(tdMetrics.forward_pe) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Trail P/E</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.trailing_pe ? formatRatio(tdMetrics.trailing_pe) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>PEG</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.peg_ratio ? formatRatio(tdMetrics.peg_ratio) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/B</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_book ? formatRatio(tdMetrics.price_to_book) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/S</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_sales ? formatRatio(tdMetrics.price_to_sales) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/Rev</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_revenue ? formatRatio(tdMetrics.ev_to_revenue) : '-'}</span>
                                                        </div>
                                                        {/* Column 2 */}
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/EBITDA</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_ebitda ? formatRatio(tdMetrics.ev_to_ebitda) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Op Margin</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.operating_margin ? `${formatRatio(tdMetrics.operating_margin * 100)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Shrs Out</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_outstanding ? formatLargeNumber(tdMetrics.shares_outstanding) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Float</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_float ? formatLargeNumber(tdMetrics.shares_float) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Short Ratio</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.short_ratio ? formatRatio(tdMetrics.short_ratio) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Insider %</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.insider_percent != null ? `${formatRatio(tdMetrics.insider_percent < 1 ? tdMetrics.insider_percent * 100 : tdMetrics.insider_percent)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Inst %</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.institution_percent != null ? `${formatRatio(tdMetrics.institution_percent < 1 ? tdMetrics.institution_percent * 100 : tdMetrics.institution_percent)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 ${isDarkMode ? '' : ''}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Div Yield</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.dividend_yield ? `${formatRatio(tdMetrics.dividend_yield * 100)}%` : '-'}</span>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Action Buttons */}
                                                  <div className={`flex flex-wrap items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/30' : 'border-slate-100 bg-white'}`}>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (idea.ticker) addToWatchlist(idea.ticker);
                                                      }}
                                                      className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-initial gap-0.5 sm:gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                                    >
                                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                      </svg>
                                                      <span className="text-[9px] sm:text-xs">Ticker</span>
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedTickerForScheduling({ ticker: idea.ticker, content: idea.content || idea.one_liner, tweetId: idea.twitterMetadata?.tweet_id || idea.global_tweet_id, shareType: 'idea' });
                                                        setShowShareModal(true);
                                                      }}
                                                      className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-initial gap-0.5 sm:gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                                    >
                                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                      </svg>
                                                      <span className="text-[9px] sm:text-xs">Share</span>
                                                    </button>
                                                  </div>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </Card>
                                    );
                                  })
                                ) : (
                                  <div className={`p-6 text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                    </svg>
                                    {groupSharedIdeas[group.group_id]?.length > 0 ? (
                                      <>
                                        <p className="text-sm">
                                          {groupAgeSortBy === 'fresh' && "No ideas newer than 10 days."}
                                          {groupAgeSortBy === 'warm' && "No ideas between 10-30 days old."}
                                          {groupAgeSortBy === 'stale' && "No ideas older than 30 days."}
                                        </p>
                                        <p className="text-xs mt-1">Try a different filter.</p>
                                      </>
                                    ) : (
                                      <p className="text-sm">No ideas shared yet</p>
                                    )}
                                  </div>
                                );
                                })()
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>
                          );
                        })}
                      
                      {userGroups.length === 0 && (
                        <div className={`text-center py-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          <p className="text-sm">No groups yet. Create your first group to share ideas with others!</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                      
                      {/* Sent Invitations Section */}
                      {viewAllGroupsMode && sentInvitations.length > 0 && (
                        <div className={`mt-4 p-4 border rounded-lg ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-200 bg-slate-50'}`}>
                          <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                            </svg>
                            Pending Invites Sent ({sentInvitations.length})
                          </h4>
                          <div className="space-y-2">
                            {sentInvitations.map((inv) => (
                              <div key={inv.invitation_id} className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-white border border-slate-200'}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{inv.invited_email}</p>
                                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      Invited to: {inv.group_name} · {inv.invited_at ? new Date(inv.invited_at).toLocaleDateString() : 'Recently'}
                                    </p>
                                  </div>
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                                    Awaiting Response
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  
                  
                  {/* Groups List - only show selected group */}
                  {selectedGroupFilter && !viewAllGroupsMode && (
                  <div className="space-y-4 mb-8">
                    {userGroups.filter(g => g.group_id === selectedGroupFilter.group_id).map((group) => (
                      <div key={group.group_id} className={`border rounded-lg ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <div className="p-4">
                          {/* Group Info Section */}
                          <div className="flex items-center gap-3 mb-2">
                            {editingGroupId === group.group_id ? (
                              // Editing mode
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="text"
                                  value={editGroupName}
                                  onChange={(e) => setEditGroupName(e.target.value)}
                                  className={`flex-1 px-3 py-1 text-lg font-semibold border-2 rounded-md focus:outline-none focus:ring-2 ${
                                    isDarkMode 
                                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                                      : 'bg-white border-slate-300 text-slate-900 focus:ring-blue-500 focus:border-blue-500'
                                  }`}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      saveGroupName(group.group_id);
                                    } else if (e.key === 'Escape') {
                                      cancelEditingGroup();
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => saveGroupName(group.group_id)}
                                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm font-medium transition-colors flex items-center gap-1"
                                  title="Save"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditingGroup}
                                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                                    isDarkMode 
                                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                                      : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                                  }`}
                                  title="Cancel"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              // Display mode
                              <>
                                <div className="flex items-center gap-2">
                                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                  </svg>
                                  <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {group.name}
                                  </h4>
                                </div>
                                {group.user_role === 'admin' && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => startEditingGroup(group.group_id, group.name)}
                                      className={`p-1 rounded hover:bg-slate-200 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'} transition-colors`}
                                      title="Edit group name"
                                    >
                                      <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                                      </svg>
                                    </button>
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                      Admin
                                    </span>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                          
                          {group.description && (
                            <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              {group.description}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm mb-3">
                            <button
                              type="button"
                              onClick={() => toggleGroupExpansion(group.group_id)}
                              className={`flex items-center gap-1 hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                              </svg>
                              {group.member_count} members
                              <svg className={`w-4 h-4 transition-transform ${expandedGroups[group.group_id] ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                console.log('📋 SHARED IDEAS BUTTON CLICKED for group:', group.group_id);
                                toggleGroupIdeasExpansion(group.group_id);
                              }}
                              className={`flex items-center gap-1 hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                              </svg>
                              Shared Ideas
                              <svg className={`w-4 h-4 transition-transform ${expandedGroupIdeas[group.group_id] ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                              </svg>
                            </button>
                            <span className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Created {new Date(group.created_at).toLocaleDateString()}
                            </span>
                          </div>

                          {/* Action Buttons - Full width spread */}
                          <div className={`flex items-center gap-1.5 sm:gap-2 px-3 py-2 justify-center border-t ${isDarkMode ? 'border-slate-700 bg-[#1a2235]/50' : 'border-slate-100 bg-slate-50'} -mx-4 -mb-4 mt-4`}>
                            {/* Leave button */}
                            <button
                              type="button"
                              onClick={() => {
                                console.log('🚪 LEAVE BUTTON CLICKED for group:', group.group_id);
                                leaveGroup(group.group_id);
                              }}
                              className={`flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                              </svg>
                              <span className="text-[10px] sm:text-xs">Leave</span>
                            </button>
                            {group.user_role === 'admin' && (
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('🗑️ DELETE GROUP CLICKED for group:', group.group_id);
                                  deleteGroup(group.group_id, group.name);
                                }}
                                className={`flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                console.log('👥 INVITE BUTTON CLICKED for group:', group.group_id);
                                inviteToGroup(group.group_id);
                              }}
                              className={`flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                              </svg>
                              <span className="text-[10px] sm:text-xs">Invite</span>
                            </button>
                            {/* Open button */}
                            <button
                              type="button"
                              onClick={() => {
                                console.log('👁️ OPEN GROUP CLICKED for group:', group.group_id);
                                toggleGroupExpansion(group.group_id);
                              }}
                              className={`flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-[10px] sm:text-xs">Open</span>
                            </button>
                          </div>
                        </div>

                        {/* Expandable Members Section */}
                        {expandedGroups[group.group_id] && (
                          <div className={`border-t p-4 ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-200 bg-slate-50'}`}>
                            <h5 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Group Members
                            </h5>
                            {groupMembers[group.group_id] ? (
                              <div className="space-y-2">
                                {groupMembers[group.group_id].map((member) => (
                                  <div key={member.user_id} className={`flex items-center justify-between p-3 rounded-md ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                          {member.email ? member.email[0].toUpperCase() : 'U'}
                                        </span>
                                      </div>
                                      <div>
                                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                          {member.email}
                                        </p>
                                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          {member.role === 'admin' ? '👑 Admin' : 'Member'} • Joined {new Date(member.joined_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    {group.user_role === 'admin' && (
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => changeMemberRole(group.group_id, member.user_id, member.role, member.email, group.name)}
                                          className={`px-3 py-1 text-xs border rounded-md transition-colors ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                                        >
                                          {member.role === 'admin' ? 'Demote' : 'Promote'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => removeMember(group.group_id, member.user_id, member.email, group.name)}
                                          className={`px-3 py-1 text-xs border rounded-md transition-colors ${isDarkMode ? 'border-red-600 text-red-400 hover:bg-red-900' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={`text-center py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                <p className="text-sm">Loading members...</p>
                              </div>
                            )}
                            {/* Pending invitations for this group */}
                            {sentInvitations.filter(inv => inv.group_id === group.group_id).length > 0 && (
                              <div className="mt-4" data-testid="group-pending-invites">
                                <h5 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  Pending Invitations
                                </h5>
                                <div className="space-y-2">
                                  {sentInvitations.filter(inv => inv.group_id === group.group_id).map((inv) => (
                                    <div key={inv.invitation_id} className={`flex items-center justify-between p-3 rounded-md ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
                                      <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-900/40' : 'bg-amber-100'}`}>
                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                            {inv.invited_email ? inv.invited_email[0].toUpperCase() : '?'}
                                          </span>
                                        </div>
                                        <div>
                                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                            {inv.invited_email}
                                          </p>
                                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Invited {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''} • Awaiting response
                                          </p>
                                        </div>
                                      </div>
                                      <span className={`px-2 py-1 text-xs rounded-full ${isDarkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                                        Pending
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Expandable Shared Ideas Section */}
                        {expandedGroupIdeas[group.group_id] && (
                          <div className={`border-t p-4 ${isDarkMode ? 'border-slate-700 bg-[#1a2235]' : 'border-slate-200 bg-slate-50'}`}>
                            <h5 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Shared Ideas
                            </h5>
                            {groupSharedIdeas[group.group_id] ? (
                              (() => {
                                // Apply age-based filtering and sorting to ideas
                                const sortedAndFilteredIdeas = [...(groupSharedIdeas[group.group_id] || [])]
                                  .filter(idea => {
                                    if (groupAgeSortBy === 'newest') return true; // Show all
                                    const days = calculateDaysOld(idea.shared_at);
                                    if (groupAgeSortBy === 'fresh') return days < 10;
                                    if (groupAgeSortBy === 'warm') return days >= 10 && days <= 30;
                                    if (groupAgeSortBy === 'stale') return days > 30;
                                    return true;
                                  })
                                  .sort((a, b) => new Date(b.shared_at || 0) - new Date(a.shared_at || 0)); // Newest first
                                
                                return sortedAndFilteredIdeas.length > 0 ? (
                                <div className="space-y-3">
                                  {sortedAndFilteredIdeas.map((idea) => {
                                    const ideaDaysOld = calculateDaysOld(idea.shared_at);
                                    const ideaAge = ideaDaysOld < 10 ? 'New' : ideaDaysOld <= 30 ? 'Recent' : 'Older';
                                    const ideaBorderColor = ideaAge === 'New' ? 'border-green-500' : ideaAge === 'Recent' ? 'border-orange-500' : 'border-red-500';
                                    const ideaBgColor = ideaAge === 'New' ? 'bg-green-100 text-green-800' : ideaAge === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                                    
                                    return (
                                      <Card 
                                        key={idea.id} 
                                        className={`border-2 ${ideaBorderColor} ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} hover:shadow-lg transition-all cursor-pointer overflow-hidden`}
                                        onClick={(e) => {
                                          if (e.target.closest('button') || e.target.closest('a')) return;
                                          handleExpandGroupIdeaCard(idea.id, idea.ticker);
                                        }}
                                      >
                                        <CardContent className="p-3">
                                          {/* Header: Ticker + Age */}
                                          <div className="flex items-start justify-between gap-2 mb-1">
                                            <div>
                                              <span className={`font-bold text-lg ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                                {(() => {
                                                  const tdMetrics = idea.ticker ? tickerMetricsCache[idea.ticker.toUpperCase()] : null;
                                                  return tdMetrics?.company_name || idea.ticker || 'Idea';
                                                })()}
                                              </span>
                                              {idea.ticker && (
                                                <span className={`font-bold text-lg ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}> ({idea.ticker})</span>
                                              )}
                                            </div>
                                            <div className="flex flex-col items-center gap-1">
                                              <button className={`p-1 rounded ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                                                <svg className={`w-4 h-4 transition-transform ${expandedGroupIdeaCards.has(idea.id) ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                </svg>
                                              </button>
                                              <Badge className={`text-xs whitespace-nowrap ${ideaBgColor}`}>{ideaAge}</Badge>
                                            </div>
                                          </div>
                                          
                                          {/* One-liner */}
                                          {idea.content && (
                                            <p className={`text-sm italic mb-1 line-clamp-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                              {idea.content}
                                            </p>
                                          )}
                                          
                                          {/* Author + Date */}
                                          <div className="flex items-center justify-between gap-2">
                                            <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              @{idea.sender_name || idea.sender_email?.split('@')[0] || 'unknown'}
                                            </div>
                                            <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {formatRelativeTime(idea.shared_at)}
                                            </div>
                                          </div>
                                        </CardContent>
                                        
                                        {/* Expanded Content - Full Profile/Valuation Tabs */}
                                        {expandedGroupIdeaCards.has(idea.id) && (
                                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                            {(() => {
                                              const primaryTicker = idea.ticker?.toUpperCase();
                                              const tdMetrics = primaryTicker ? tickerMetricsCache[primaryTicker] : null;
                                              const activeTab = groupIdeaCardMetricsTab[idea.id] || 'valuation';
                                              
                                              // Show loading state while metrics are being fetched
                                              if (!tdMetrics || (!tdMetrics.company_name && !tdMetrics.market_cap)) {
                                                return (
                                                  <div className={`p-3 sm:p-4 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-slate-50/50'}`}>
                                                    <div className="flex items-center justify-center gap-2">
                                                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                      </svg>
                                                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        Loading metrics for {primaryTicker || 'this idea'}...
                                                      </p>
                                                    </div>
                                                  </div>
                                                );
                                              }
                                              
                                              return (
                                                <>
                                                  {/* Pill Toggle */}
                                                  <div className={`flex justify-center py-3 ${isDarkMode ? 'bg-[#141c2e]/30' : 'bg-white'}`}>
                                                    <div className={`inline-flex rounded-full p-1 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); setGroupIdeaCardMetricsTab(prev => ({ ...prev, [idea.id]: 'profile' })); }}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                                          activeTab === 'profile'
                                                            ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                                                            : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                                                        }`}
                                                      >
                                                        Profile
                                                      </button>
                                                      <button
                                                        onClick={(e) => { e.stopPropagation(); setGroupIdeaCardMetricsTab(prev => ({ ...prev, [idea.id]: 'valuation' })); }}
                                                        className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                                          activeTab === 'valuation'
                                                            ? (isDarkMode ? 'bg-[#1a2235] text-white shadow' : 'bg-white text-slate-900 shadow')
                                                            : (isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900')
                                                        }`}
                                                      >
                                                        Valuation
                                                      </button>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Tab Content */}
                                                  <div className={`px-3 sm:px-4 py-3 ${isDarkMode ? 'bg-[#141c2e]/20' : 'bg-white'}`}>
                                                    {activeTab === 'profile' ? (
                                                      /* PROFILE TAB */
                                                      <div className="space-y-1">
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Company</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.company_name || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Exchange</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.exchange || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Industry</span>
                                                          <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.industry || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sector</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.sector || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CEO</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ceo || '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Employees</span>
                                                          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.employees ? tdMetrics.employees.toLocaleString() : '-'}</span>
                                                        </div>
                                                        {tdMetrics?.phone && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Phone</span>
                                                            <a href={`tel:${tdMetrics.phone}`} onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.phone}</a>
                                                          </div>
                                                        )}
                                                        {tdMetrics?.website && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Website</span>
                                                            <a href={tdMetrics.website.startsWith('http') ? tdMetrics.website : `https://${tdMetrics.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{tdMetrics.website.replace(/^https?:\/\//, '')}</a>
                                                          </div>
                                                        )}
                                                        {(tdMetrics?.address || tdMetrics?.city) && (
                                                          <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Address</span>
                                                            <span className={`text-sm font-medium text-right ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                                                              {[tdMetrics?.address, tdMetrics?.city, tdMetrics?.state, tdMetrics?.zip].filter(Boolean).join(', ')}
                                                            </span>
                                                          </div>
                                                        )}
                                                        {tdMetrics?.description && (
                                                          <div className={`pt-2`}>
                                                            <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mb-1`}>Description</div>
                                                            <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{tdMetrics.description}</div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    ) : (
                                                      /* VALUATION/STATISTICS TAB - 16 metrics in 2 even columns */
                                                      <div className="grid grid-cols-2 gap-x-6 gap-y-0">
                                                        {/* Column 1 */}
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Mkt Cap</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.market_cap ? formatFinancialValue(tdMetrics.market_cap) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.enterprise_value ? formatFinancialValue(tdMetrics.enterprise_value) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Fwd P/E</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.forward_pe ? formatRatio(tdMetrics.forward_pe) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Trail P/E</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.trailing_pe ? formatRatio(tdMetrics.trailing_pe) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>PEG</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.peg_ratio ? formatRatio(tdMetrics.peg_ratio) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/B</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_book ? formatRatio(tdMetrics.price_to_book) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>P/S</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.price_to_sales ? formatRatio(tdMetrics.price_to_sales) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/Rev</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_revenue ? formatRatio(tdMetrics.ev_to_revenue) : '-'}</span>
                                                        </div>
                                                        {/* Column 2 */}
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>EV/EBITDA</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.ev_to_ebitda ? formatRatio(tdMetrics.ev_to_ebitda) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Op Margin</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.operating_margin ? `${formatRatio(tdMetrics.operating_margin * 100)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Shrs Out</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_outstanding ? formatLargeNumber(tdMetrics.shares_outstanding) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Float</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.shares_float ? formatLargeNumber(tdMetrics.shares_float) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Short Ratio</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.short_ratio ? formatRatio(tdMetrics.short_ratio) : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Insider %</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.insider_percent != null ? `${formatRatio(tdMetrics.insider_percent < 1 ? tdMetrics.insider_percent * 100 : tdMetrics.insider_percent)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 border-b ${isDarkMode ? 'border-slate-700/50' : 'border-slate-100'}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Inst %</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.institution_percent != null ? `${formatRatio(tdMetrics.institution_percent < 1 ? tdMetrics.institution_percent * 100 : tdMetrics.institution_percent)}%` : '-'}</span>
                                                        </div>
                                                        <div className={`flex justify-between py-1.5 ${isDarkMode ? '' : ''}`}>
                                                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Div Yield</span>
                                                          <span className={`text-sm font-mono font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>{tdMetrics?.dividend_yield ? `${formatRatio(tdMetrics.dividend_yield * 100)}%` : '-'}</span>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Action Buttons */}
                                                  <div className={`flex flex-wrap items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/30' : 'border-slate-100 bg-white'}`}>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (idea.ticker) addToWatchlist(idea.ticker);
                                                      }}
                                                      className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-initial gap-0.5 sm:gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                                    >
                                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                      </svg>
                                                      <span className="text-[9px] sm:text-xs">Ticker</span>
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedTickerForScheduling({ ticker: idea.ticker, content: idea.content });
                                                        setShowShareModal(true);
                                                      }}
                                                      className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-initial gap-0.5 sm:gap-1.5 px-1.5 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                                    >
                                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                                                      </svg>
                                                      <span className="text-[9px] sm:text-xs">Share</span>
                                                    </button>
                                                  </div>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </Card>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className={`text-center py-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                  </svg>
                                  {groupSharedIdeas[group.group_id]?.length > 0 ? (
                                    <>
                                      <p className="text-sm">
                                        {groupAgeSortBy === 'fresh' && "No ideas newer than 10 days in this group."}
                                        {groupAgeSortBy === 'warm' && "No ideas between 10-30 days old in this group."}
                                        {groupAgeSortBy === 'stale' && "No ideas older than 30 days in this group."}
                                      </p>
                                      <p className="text-xs mt-1">Try selecting a different filter.</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-sm">No ideas shared in this group yet</p>
                                      <p className="text-xs mt-1">Share an idea from your dashboard to get started!</p>
                                    </>
                                  )}
                                </div>
                              );
                              })()
                            ) : (
                              <div className={`text-center py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                <p className="text-sm">Loading shared ideas...</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}

                  {/* Pending Invitations Section */}
                  <div className={`border-t pt-6 mt-8 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-slate-900' : 'text-slate-900'}`}>
                        Pending Invitations ({pendingInvitations.length})
                      </h4>
                    </div>
                    
                    {pendingInvitations.length > 0 ? (
                      <div className="space-y-3">
                        {pendingInvitations.map((invitation) => (
                          <div key={invitation.invitation_id} className={`border rounded-lg p-4 ${isDarkMode ? 'border-slate-600 bg-[#1a2235]' : 'border-slate-200 bg-white'}`}>
                            {/* Invitation Info */}
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                {invitation.group_name}
                              </h5>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                New
                              </span>
                            </div>
                            <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              Invited by {invitation.invited_by_email}
                            </p>
                            
                            {/* Action Buttons - Below info, centered */}
                            <div className="flex gap-2 flex-wrap justify-center">
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('✅ ACCEPT INVITATION CLICKED for:', invitation.invitation_id);
                                  acceptInvitation(invitation.invitation_id, invitation.group_name);
                                }}
                                className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-green-300 text-green-600 hover:bg-green-50"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                </svg>
                                Accept
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  console.log('❌ REJECT INVITATION CLICKED for:', invitation.invitation_id);
                                  rejectInvitation(invitation.invitation_id, invitation.group_name);
                                }}
                                className="flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                                Reject
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={`text-center py-6 ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                        <p className="text-sm">No pending invitations</p>
                      </div>
                    )}
                  </div>

                </div>
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Folders Tab */}
          <TabsContent value="files">
            <div {...filesSwipeHandlers}>
              <FilesTab 
                user={user}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                showNotification={showNotification}
                searchTicker={filesSearchQuery}
                onSearchComplete={() => setFilesSearchQuery(null)}
                openFileViewerId={openFileViewerId}
                onOpenFileComplete={() => setOpenFileViewerId(null)}
                onOpenGlobalSearch={() => {
                  // Open the same global search modal used everywhere else
                  setShowMobileSearch(true);
                }}
                activeView={filesView}
                onViewChange={setFilesView}
                onShareFile={(ticker, viewerId) => {
                  setSelectedTickerForScheduling({
                    ticker: ticker,
                    title: `${ticker} SEC Filings`,
                    content: `Check out these ${ticker} SEC filings`,
                    shareType: 'file',
                    viewerId: viewerId
                  });
                  setShowShareModal(true);
                }}
                showConfirmModal={showConfirmModal}
                closeConfirmModal={closeConfirmModal}
                onFileCreated={() => {
                  // Trigger blue dot notification for files
                  console.log('🔵 App.js: onFileCreated callback fired!');
                  console.log('🔵 App.js: Current hasNewFiles state BEFORE:', hasNewFiles);
                  setHasNewFiles(true);
                  console.log('🔵 App.js: setHasNewFiles(true) called - blue dot should appear on mobile nav');
                }}
                userTickers={{
                  ideas: bookmarks?.map(b => ({ ticker: b.tickers?.[0], company: b.company_name || b.title, source: 'idea' })).filter(t => t.ticker) || [],
                  watchlists: watchlists?.flatMap(w => w.tickers?.map(t => ({ ticker: t, company: w.name, source: 'watchlist' })) || []) || [],
                  polls: fishingThreads?.map(p => ({ ticker: p.tickers?.[0], company: p.title, source: 'poll' })).filter(t => t.ticker) || []
                }}
                onLogout={logout}
                onNavigateToAccount={() => {
                  setActiveTab('settings');
                  setSettingsView('account');
                }}
                onNavigateToSchedules={() => {
                  setActiveTab('settings');
                  setSettingsView('schedules');
                }}
              />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div {...settingsSwipeHandlers}>
            <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <CardContent className="p-0">
                <div className="p-4 bg-slate-800 text-white">
                  {/* Row 1: Account/Schedules toggle on left, action buttons on right */}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left side: View Toggle - Account / Schedules */}
                    <SettingsViewTabs
                      settingsView={settingsView}
                      setSettingsView={setSettingsView}
                    />
                    
                    {/* Center: Shortcut hint - desktop only */}
                    <button 
                      onClick={() => setShowKeyboardShortcuts(true)}
                      className="hidden sm:flex items-center text-white/50 text-sm"
                      title="View keyboard shortcuts"
                    >
                      <span>Press </span>
                      <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                      <span> for shortcuts</span>
                    </button>
                    
                    {/* Right side: Search (left), Light/Dark (middle), Sign Out (right) */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2">
                      {/* Search Button */}
                      <button
                        onClick={() => setShowMobileSearch(true)}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"
                        title="Search"
                        data-testid="settings-search-btn"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span className="hidden sm:inline">Search</span>
                      </button>
                      
                      {/* Light Mode Button */}
                      <button 
                        onClick={() => setIsDarkMode(false)}
                        className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                          !isDarkMode 
                            ? 'bg-white text-slate-800 border-white' 
                            : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                        }`}
                        title="Light Mode"
                        data-testid="settings-light-mode-btn"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/>
                        </svg>
                        <span className="hidden sm:inline">Light</span>
                      </button>
                      
                      {/* Dark Mode Button */}
                      <button 
                        onClick={() => setIsDarkMode(true)}
                        className={`w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${
                          isDarkMode 
                            ? 'bg-white text-slate-800 border-white' 
                            : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                        }`}
                        title="Dark Mode"
                        data-testid="settings-dark-mode-btn"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
                        </svg>
                        <span className="hidden sm:inline">Dark</span>
                      </button>
                      
                      {/* Sign Out Button - Orange */}
                      <button 
                        onClick={() => logout()}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white border border-orange-500 rounded-lg transition-all duration-200"
                        title="Sign Out"
                        data-testid="settings-sign-out-btn"
                      >
                        <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="hidden sm:inline">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className={`p-6 ${isDarkMode ? 'bg-[#0f1419]' : 'bg-white'}`}>
                  <div className="space-y-6">
                    
                    {/* ========== ACCOUNT TAB CONTENT ========== */}
                    {settingsView === 'account' && (
                      <>
                    {/* TEMPORARILY HIDDEN - MVP simplification. X account is already connected via login. Will add back when adding more import methods (email, bookmarklet, etc.) */}
                    {false && (
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          <div className="mb-4">
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2 flex items-center`}>
                              <BookmarkIcon className="h-5 w-5 mr-2 text-orange-600" />
                              How to Save Investment Ideas
                            </h4>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              Connect your X (Twitter) account to import bookmarks and automatically capture investment ideas
                            </p>
                          </div>
                          
                          {/* Action Buttons - Full width spread */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                            {/* Hidden for simplicity - keep code for easy re-implementation */}
                            {false && (
                              <>
                                <button
                                  onClick={() => setShowBookmarkletModal(true)}
                                  className="flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-orange-300 text-orange-600 hover:bg-orange-50"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                                  </svg>
                                  Bookmarklet
                                </button>
                                
                                <button
                                  onClick={() => setShowExtensionModal(true)}
                                  className="flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-blue-300 text-blue-600 hover:bg-blue-50"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
                                  </svg>
                                  Extension
                                </button>
                                
                                <button
                                  onClick={() => setShowEmailInstructionsModal(true)}
                                  className="flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-purple-300 text-purple-600 hover:bg-purple-50"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                  </svg>
                                  E-Mail
                                </button>
                                
                                <button
                                  onClick={() => setShowPublisherButtonModal(true)}
                                  className="flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-green-300 text-green-600 hover:bg-green-50"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                                  </svg>
                                  Publisher
                                </button>
                              </>
                            )}
                            
                            <button
                              onClick={() => setShowXAccountModal(true)}
                              className="flex flex-row items-center justify-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm border-sky-200 text-sky-600 hover:bg-sky-50"
                            >
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                              Connect X
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Account & Subscription Card */}
                    {accountSubscription && (
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          <div className="mb-4">
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-4`}>
                              Account & Subscription
                            </h4>
                            
                            {/* Current Tier Display */}
                            <div className={`rounded-lg p-4 mb-4 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Current Plan</span>
                                  <div className="flex items-center gap-2">
                                    <h5 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-blue-900'} capitalize`}>
                                      {subscriptionStatus?.tier === 'trial' || subscriptionStatus?.trial_active
                                        ? 'Free Trial' 
                                        : subscriptionStatus?.tier === 'pro' 
                                        ? 'Pro' 
                                        : (accountSubscription?.tier || 'Free')}
                                    </h5>
                                    {(subscriptionStatus?.tier === 'trial' || subscriptionStatus?.trial_active) && subscriptionStatus?.trial_days_left !== undefined && (
                                      <span className={`text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'} font-semibold`}>
                                        ({subscriptionStatus.trial_days_left} {subscriptionStatus.trial_days_left === 1 ? 'day' : 'days'} left)
                                      </span>
                                    )}
                                    {accountSubscription.billing_cycle && subscriptionStatus?.tier === 'pro' && (
                                      <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'} capitalize`}>
                                        ({accountSubscription.billing_cycle})
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {subscriptionStatus?.tier === 'pro' && accountSubscription.next_billing_date ? (
                                    <>
                                      <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Next Billing</span>
                                      <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                        {new Date(accountSubscription.next_billing_date).toLocaleDateString()}
                                      </div>
                                    </>
                                  ) : subscriptionStatus?.tier === 'trial' ? (
                                    <>
                                      <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Trial Ends</span>
                                      <div className={`text-sm font-medium ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                        {new Date(Date.now() + subscriptionStatus.trial_days_left * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              
                              {/* Pending Changes Alert */}
                              {accountSubscription.pending_changes && (
                                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                                  <p className="text-sm text-amber-800">
                                    <span className="font-semibold capitalize">{accountSubscription.pending_changes.action}</span> to{' '}
                                    <span className="font-semibold">{accountSubscription.pending_changes.new_tier}</span> scheduled for{' '}
                                    {new Date(accountSubscription.pending_changes.effective_date).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons - Full width spread */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center">
                            {accountSubscription.can_upgrade && (
                              <button
                                onClick={() => setShowPricingModal(true)}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                  isDarkMode 
                                    ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10'
                                    : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                                }`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Upgrade</span>
                              </button>
                            )}
                            {accountSubscription.can_downgrade && (
                              <button
                                onClick={() => openPricingModal('downgrade')}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                  isDarkMode 
                                    ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10'
                                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                }`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Downgrade</span>
                              </button>
                            )}
                            {accountSubscription.tier !== 'Free' && (
                              <button
                                onClick={() => setShowBillingInfoModal(true)}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                  isDarkMode 
                                    ? 'border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10'
                                    : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                                }`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Billing</span>
                              </button>
                            )}
                            {accountSubscription.can_cancel && (
                              <button
                                onClick={() => setShowCancelModal(true)}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                  isDarkMode 
                                    ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                                    : 'border-red-300 text-red-600 hover:bg-red-50'
                                }`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Cancel</span>
                              </button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Help & Support Card - Part of Account tab */}
                    <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>
                            Help & Support
                          </h4>
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            Get help, submit suggestions, or browse frequently asked questions
                          </p>
                        </div>
                        
                        {/* Action Buttons - Full width spread */}
                        <div className="flex items-center gap-1.5 sm:gap-2 justify-center">
                          <button
                            onClick={() => setShowSuggestionsModal(true)}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                              isDarkMode 
                                ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10'
                                : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Suggestions</span>
                          </button>
                          
                          <button
                            onClick={() => setShowFAQModal(true)}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                              isDarkMode 
                                ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                                : 'border-green-300 text-green-600 hover:bg-green-50'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">FAQ</span>
                          </button>
                          
                          <button
                            onClick={() => setShowHelpModal(true)}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                              isDarkMode 
                                ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'
                                : 'border-purple-300 text-purple-600 hover:bg-purple-50'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Help</span>
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                      </>
                    )}

                    {/* ========== SCHEDULES TAB CONTENT ========== */}
                    {settingsView === 'schedules' && (
                      <>
                    {/* 1. FOLDER SCHEDULE CARD - Auto/Manual toggle */}
                    <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Folder Schedule
                          </h4>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                          Schedule ideas to folders automatically or manually
                        </p>
                        
                        {/* Auto Schedule Toggle Card */}
                        <div 
                          className={`p-4 rounded-lg cursor-pointer transition-all ${
                            dossierSettings.enabled 
                              ? isDarkMode ? 'bg-blue-900/30 border border-blue-500/50' : 'bg-blue-50 border border-blue-200'
                              : isDarkMode ? 'bg-[#1a2235] border border-slate-700 hover:border-slate-600' : 'bg-slate-50 border border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={async () => {
                            const newEnabled = !dossierSettings.enabled;
                            const newSettings = { ...dossierSettings, enabled: newEnabled };
                            setDossierSettings(newSettings);
                            await saveDossierSettings(newSettings, false);
                            if (newEnabled) {
                              await generateResearchSchedule();
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                dossierSettings.enabled 
                                  ? 'bg-blue-500' 
                                  : isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                              }`}>
                                <svg className={`w-5 h-5 ${dossierSettings.enabled ? 'text-white' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                </svg>
                              </div>
                              <div>
                                <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  Auto Schedule
                                </div>
                                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {dossierSettings.enabled 
                                    ? 'All ideas scheduled automatically' 
                                    : 'Schedule all ideas across 31 days'}
                                </div>
                              </div>
                            </div>
                            <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              dossierSettings.enabled 
                                ? 'bg-blue-500' 
                                : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                            }`}>
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                  dossierSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Schedule Summary - when auto-schedule is on */}
                        {dossierSettings.enabled && researchSchedule.length > 0 && (
                          <div className="mt-4">
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{totalPendingIdeas} ideas</span>
                              {' · '}{(() => {
                                // Only count pending/scheduled items for the per-day calculation
                                const byDate = {};
                                researchSchedule.filter(item => item.status === 'scheduled' || !item.status).forEach(item => {
                                  const date = item.scheduled_date;
                                  byDate[date] = (byDate[date] || 0) + 1;
                                });
                                const counts = Object.values(byDate);
                                if (counts.length === 0) return '0';
                                const min = Math.min(...counts);
                                const max = Math.max(...counts);
                                return min === max ? `${min}` : `${min}-${max}`;
                              })()}/day over 31 days
                            </p>
                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                              Starting {new Date(researchSchedule[0]?.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        )}
                        
                        {/* Compact Calendar Sync Section */}
                        <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                              </svg>
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Sync to Calendar</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`${API_BASE_URL}/api/research-schedule/${user?.id || user?.email}/export.ics?days=30`);
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = 'tickler-schedule.ics';
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                      showNotification('Calendar file downloaded!', 'success');
                                    }
                                  } catch (error) {
                                    showNotification('Error exporting calendar', 'error');
                                  }
                                }}
                                title="Download .ics file"
                                className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                              >
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                </svg>
                              </button>
                              <a
                                href={`https://calendar.google.com/calendar/r?cid=webcal://${window.location.host}/api/research-schedule/${user?.id || user?.email}/export.ics`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Add to Google Calendar"
                                className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                              >
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
                                  <path fill="#4285F4" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/>
                                  <path fill="#EA4335" d="M9 14h2v2H9zm4 0h2v2h-2zm-4-3h2v2H9zm4 0h2v2h-2z"/>
                                </svg>
                              </a>
                              <a
                                href={`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(`${window.location.origin}/api/research-schedule/${user?.id || user?.email}/export.ics`)}&name=Tickler%20Research`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Add to Outlook Calendar"
                                className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                              >
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="#0078D4">
                                  <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
                                </svg>
                              </a>
                              <a
                                href={`webcal://${window.location.host}/api/research-schedule/${user?.id || user?.email}/export.ics`}
                                title="Add to Apple Calendar"
                                className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                              >
                                <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill={isDarkMode ? '#ffffff' : '#333333'}>
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                              </a>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* 2. FOLDER EMAILS CARD */}
                    <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                          </svg>
                          <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Folder Emails
                          </h4>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                          Receive research from your daily tickler folder
                        </p>
                        
                        {/* Folder Emails Toggle */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Folder Emails
                            </span>
                          </div>
                          <button
                            onClick={async () => {
                              const newFrequency = emailSettings.folder_email_frequency === 'off' ? 'daily' : 'off';
                              const newSettings = { ...emailSettings, folder_email_frequency: newFrequency, folder_email_enabled: newFrequency !== 'off' };
                              setEmailSettings(newSettings);
                              await saveEmailSettings(newSettings);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                              emailSettings.folder_email_frequency !== 'off'
                                ? 'bg-green-500' 
                                : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                            }`}
                            title={emailSettings.folder_email_frequency !== 'off' ? 'Pause emails' : 'Resume emails'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                emailSettings.folder_email_frequency !== 'off' ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        {emailSettings.folder_email_frequency !== 'off' ? (
                          <div className="space-y-1">
                            <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              ✓ {(emailSettings.folder_email_frequency || 'daily').charAt(0).toUpperCase() + (emailSettings.folder_email_frequency || 'daily').slice(1)}: {(() => {
                                const time = emailSettings.folder_email_time || '08:00';
                                const [hourStr, minute] = time.split(':');
                                const hour = parseInt(hourStr);
                                const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                return `${displayHour}:${minute || '00'} ${hour >= 12 ? 'PM' : 'AM'}`;
                              })()}
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              ✓ Documents: {(emailSettings.folder_email_docs || []).length > 0 
                                ? (emailSettings.folder_email_docs || []).join(', ')
                                : 'None selected'}
                            </p>
                          </div>
                        ) : (
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Emails paused
                          </p>
                        )}
                        
                        {/* Action Buttons - Full width spread */}
                        <div className="flex items-center gap-1.5 sm:gap-2 justify-center mt-4">
                          <button
                            onClick={() => setShowDossierConfigModal(true)}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                              isDarkMode 
                                ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10'
                                : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Configure</span>
                          </button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* 3. DIGEST EMAILS CARD */}
                    <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235] text-white' : 'border-slate-200 bg-white'}`}>
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                          </svg>
                          <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Digest Emails
                          </h4>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
                          Receive a summary of your new investment ideas
                        </p>
                        
                        {/* Digest Emails Toggle */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Digest Emails
                            </span>
                          </div>
                          {/* Pill Toggle */}
                          <button
                            onClick={async () => {
                              const isCurrentlyEnabled = emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled;
                              const newSettings = {
                                ...emailSettings,
                                weekly_digest_enabled: !isCurrentlyEnabled,
                                daily_digest_enabled: false
                              };
                              setEmailSettings(newSettings);
                              await saveEmailSettings(newSettings);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                              (emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled)
                                ? 'bg-green-500' 
                                : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                            }`}
                            title={(emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? 'Pause emails' : 'Resume emails'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                                (emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        {(emailSettings.weekly_digest_enabled || emailSettings.daily_digest_enabled) ? (
                          <div className="space-y-1">
                            {emailSettings.weekly_digest_enabled && (
                              <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ✓ Weekly: {emailSettings.weekly_digest_day || 'Sunday'} at {(() => {
                                  const time = emailSettings.weekly_digest_time || '09:00';
                                  const [hourStr, minute] = time.split(':');
                                  const hour = parseInt(hourStr);
                                  const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                  return `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                })()}
                              </p>
                            )}
                            {emailSettings.daily_digest_enabled && (
                              <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ✓ Daily: {(() => {
                                  const time = emailSettings.daily_digest_time || emailSettings.digest_time || '08:00';
                                  const [hourStr, minute] = time.split(':');
                                  const hour = parseInt(hourStr);
                                  const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                  return `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                })()}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Emails paused
                          </p>
                        )}
                        
                        {/* Action Buttons - Full width spread */}
                        <div className="flex items-center gap-1.5 sm:gap-2 justify-center mt-4">
                          <button
                            onClick={() => setShowEmailConfigModal(true)}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                              isDarkMode 
                                ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10'
                                : 'border-orange-300 text-orange-600 hover:bg-orange-50'
                            }`}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Configure</span>
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                      </>
                    )}

                    {/* Admin Section - Only visible to admins (shown on both tabs) */}
                    {isAdmin && (
                      <Card className={`border ${isDarkMode ? 'border-slate-700 bg-[#1a2235]' : 'border-slate-200 bg-white'}`}>
                        <CardContent className="p-6">
                          <div className="mb-4">
                            <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2 flex items-center gap-2`}>
                              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              Admin Dashboard
                            </h4>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              View aggregate analytics, user metrics, and system health.
                            </p>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              Logged in as: {actualUserEmail}
                            </p>
                          </div>
                          
                          <div className="flex justify-end">
                            <button
                              onClick={() => setShowAdminDashboard(true)}
                              className={`flex flex-row items-center gap-0.5 flex-1 px-2 sm:px-3 py-1 sm:py-0.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${
                                isDarkMode 
                                  ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'
                                  : 'border-purple-300 text-purple-600 hover:bg-purple-50'
                              }`}
                            >
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <span className="text-[10px] sm:text-xs">Dashboard</span>
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </TabsContent>


          {/* OLD Pricing Modal - DISABLED - Using new Stripe modal instead */}
          {false && showPricingModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedAction === 'upgrade' ? 'Upgrade Your Plan' : 'Change Your Plan'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowPricingModal(false);
                        setSelectedTier(null);
                      }}
                      className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>

                  {/* Monthly/Annual Toggle */}
                  {selectedAction === 'upgrade' && (
                    <div className="flex justify-center mb-6">
                      <div className="bg-slate-100 rounded-lg p-1 inline-flex">
                        <button
                          onClick={() => setSelectedBillingCycle('monthly')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            selectedBillingCycle === 'monthly'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setSelectedBillingCycle('annual')}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            selectedBillingCycle === 'annual'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Annual <span className="text-green-600 text-xs ml-1">(Save 17%)</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Pricing Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Pro Tier */}
                    {((selectedAction === 'upgrade' && accountSubscription?.tier === 'Free') || 
                      (selectedAction === 'downgrade' && ['Elite', 'Enterprise'].includes(accountSubscription?.tier))) && (
                      <div 
                        className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          selectedTier === 'Pro' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                        onClick={() => setSelectedTier('Pro')}
                      >
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Pro</h4>
                        <div className="mb-4">
                          <span className="text-9xl font-bold text-slate-900">
                            ${selectedBillingCycle === 'monthly' ? '29' : '295'}
                          </span>
                          <span className="text-slate-600">
                            /{selectedBillingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Advanced analytics
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Priority support
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Unlimited ideas
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* Elite Tier */}
                    {((selectedAction === 'upgrade' && ['Free', 'Pro'].includes(accountSubscription?.tier)) || 
                      (selectedAction === 'downgrade' && accountSubscription?.tier === 'Enterprise')) && (
                      <div 
                        className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          selectedTier === 'Elite' 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-slate-200 hover:border-purple-300'
                        }`}
                        onClick={() => setSelectedTier('Elite')}
                      >
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Elite</h4>
                        <div className="mb-4">
                          <span className="text-9xl font-bold text-slate-900">
                            ${selectedBillingCycle === 'monthly' ? '49' : '499'}
                          </span>
                          <span className="text-slate-600">
                            /{selectedBillingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            All Pro features
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            AI-powered insights
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Custom integrations
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* Enterprise Tier */}
                    {selectedAction === 'upgrade' && (
                      <div 
                        className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          selectedTier === 'Enterprise' 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-slate-200 hover:border-orange-300'
                        }`}
                        onClick={() => setSelectedTier('Enterprise')}
                      >
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Enterprise</h4>
                        <div className="mb-4">
                          <span className="text-9xl font-bold text-slate-900">
                            ${selectedBillingCycle === 'monthly' ? '149' : '1499'}
                          </span>
                          <span className="text-slate-600">
                            /{selectedBillingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            All Elite features
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Dedicated account manager
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            White-label options
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* Free Tier (for downgrades) */}
                    {selectedAction === 'downgrade' && (
                      <div 
                        className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          selectedTier === 'Free' 
                            ? 'border-slate-500 bg-slate-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedTier('Free')}
                      >
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Free</h4>
                        <div className="mb-4">
                          <span className="text-9xl font-bold text-slate-900">$0</span>
                          <span className="text-slate-600">/month</span>
                        </div>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Basic features
                          </li>
                          <li className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Limited ideas
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Confirmation Message */}
                  {selectedTier && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        {selectedAction === 'upgrade' && accountSubscription?.tier === 'Free' ? (
                          <>Your {selectedTier} plan will start immediately.</>
                        ) : (
                          <>Your plan will change to {selectedTier} at the end of your current billing cycle ({accountSubscription?.next_billing_date ? new Date(accountSubscription.next_billing_date).toLocaleDateString() : 'N/A'}).</>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3">
                    <Button
                      onClick={() => {
                        setShowPricingModal(false);
                        setSelectedTier(null);
                      }}
                      variant="outline"
                      className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleUpgradeDowngrade}
                      disabled={!selectedTier || subscriptionLoading}
                      className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                    >
                      {subscriptionLoading ? 'Processing...' : 'Confirm'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Subscription Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>
                      Cancel Subscription?
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'} mb-4`}>
                      Your subscription will be cancelled and you'll be downgraded to the Free plan at the end of your current billing cycle on{' '}
                      <span className="font-semibold">
                        {accountSubscription?.next_billing_date ? new Date(accountSubscription.next_billing_date).toLocaleDateString() : 'N/A'}
                      </span>.
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      You'll continue to have access to all {accountSubscription?.tier} features until then.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    onClick={() => setShowCancelModal(false)}
                    variant="outline"
                    className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}
                  >
                    Keep Subscription
                  </Button>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={subscriptionLoading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {subscriptionLoading ? 'Processing...' : 'Cancel Subscription'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Bookmarklet Instructions Modal */}
          {/* Bookmarklet Modal - Working version with vanilla JS button */}
          {showBookmarkletModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                 onClick={() => setShowBookmarkletModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-8 border-4 border-orange-500"
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-orange-600">🔖 Bookmarklet Setup</h2>
                  <button
                    onClick={() => setShowBookmarkletModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border-2 border-orange-400">
                    <p className="text-slate-700 font-medium text-center mb-4">
                      ↓ Drag this button to your bookmarks bar ↓
                    </p>
                    <div className="flex justify-center" ref={bookmarkletButtonRef}>
                      {/* Button created via vanilla JS */}
                    </div>
                  </div>

                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h3 className="font-semibold text-slate-900 mb-2">How to Install:</h3>
                    <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
                      <li>Make sure your bookmarks bar is visible (Ctrl+Shift+B or Cmd+Shift+B)</li>
                      <li>Drag the button above to your bookmarks bar</li>
                      <li>Visit any webpage and click it to save</li>
                      <li>Choose "💡 Idea" (actionable) or "📚 Library" (reference)</li>
                    </ol>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Tip:</strong> This bookmarklet works on any webpage. A beautiful card will slide down from the top when you click it!
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowBookmarkletModal(false)}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Publisher Button Instructions Modal */}
          {showPublisherButtonModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Publisher Button Setup
                  </h3>
                  <button
                    onClick={() => setShowPublisherButtonModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <p className="mb-4">
                    Add a "TICKLER" button to your Substack, blog, or website. Readers can save your investment content with one click.
                  </p>

                  {/* Compact Button Preview - Pocket Style */}
                  <div style={{backgroundColor: '#27313C'}} className="rounded-lg p-4 mb-6 border border-slate-600">
                    <p className="text-xs text-slate-300 mb-3 text-center">
                      Button Preview:
                    </p>
                    <div className="flex justify-center">
                      <button className="inline-flex items-center px-1 py-0.5 rounded transition-all" style={{background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)', color: '#1e293b', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)', border: '1px solid #cbd5e1', fontSize: '9px', lineHeight: '1'}}>
                        <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded mr-1" style={{padding: '2px'}}>
                          <FontAwesomeIcon icon={faFolderOpen} className="h-3 w-3" style={{ color: '#F58346' }} />
                        </div>
                        <span style={{color: '#1e293b', fontWeight: '700'}}>TICK</span><span style={{color: '#F58346', fontWeight: '700'}}>L</span><span style={{color: '#1e293b', fontWeight: '700'}}>ER</span>
                      </button>
                    </div>
                  </div>

                  {/* Embed Code */}
                  <div className="mb-6">
                    <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      📋 Copy & Paste This Code:
                    </h4>
                    
                    <div className="bg-slate-900 rounded-lg p-4 relative">
                      <code className="text-green-400 text-sm block overflow-x-auto whitespace-pre">
{`<!-- Tickler Publisher Button -->
<script src="${API_BASE_URL}/tickler-publisher-button.js"></script>
<a href="#" class="tickler-save-button">TICKLER</a>`}
                      </code>
                      <button
                        onClick={() => {
                          const code = '<!-- Tickler Publisher Button -->\\n<script src="' + API_BASE_URL + '/tickler-publisher-button.js"></script>\\n<a href="#" class="tickler-save-button">TICKLER</a>';
                          navigator.clipboard.writeText(code);
                          alert('✅ Button code copied!');
                        }}
                        className="absolute top-2 right-2 px-3 py-1 text-white text-xs rounded transition-colors" style={{backgroundColor: '#5AA4FF'}}
                      >
                        Copy Code
                      </button>
                    </div>
                    <p className="text-xs mt-2" style={{color: '#5AA4FF'}}>
                      ⚡ Simple integration: Just two lines of code!
                    </p>
                  </div>

                  {/* Where to Add */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4" style={{backgroundColor: '#2D3748', borderColor: '#4A5568'}}>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5" style={{color: '#A17FFF'}} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5z"/>
                        </svg>
                        <h5 className="font-semibold text-white">Substack</h5>
                      </div>
                      <p className="text-sm text-slate-300">
                        Go to <strong>Settings → Publication details → Custom footer</strong><br/>
                        Paste the code in the footer HTML section
                      </p>
                    </div>
                    
                    <div className="border rounded-lg p-4" style={{backgroundColor: '#2D3748', borderColor: '#4A5568'}}>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5" style={{color: '#4ADE80'}} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                        <h5 className="font-semibold text-white">Custom Sites</h5>
                      </div>
                      <p className="text-sm text-slate-300">
                        Add the code wherever you want the button<br/>
                        Works with WordPress, Medium, Ghost, any HTML site
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowPublisherButtonModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}


          {/* Browser Extension Instructions Modal */}
          {showExtensionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Browser Extension Setup
                  </h3>
                  <button
                    onClick={() => setShowExtensionModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <p className="mb-4">
                    Install the Chrome extension for the most seamless saving experience with full formatting preservation and automatic ticker detection.
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-sm">One-click save</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-sm">Full formatting</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-sm">Works on any website</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-sm">Auto ticker detection</span>
                    </div>
                  </div>

                  {/* Installation Steps */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                    <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-slate-900' : 'text-slate-900'} flex items-center gap-2`}>
                      <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      Installation Steps:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-slate-700">
                      <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">1</div>
                        <p className="text-xs font-medium">Download extension below</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">2</div>
                        <p className="text-xs font-medium">Open chrome://extensions/</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">3</div>
                        <p className="text-xs font-medium">Enable developer mode</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 font-bold">4</div>
                        <p className="text-xs font-medium">Load unpacked extension</p>
                      </div>
                    </div>
                  </div>

                  {/* Download Buttons */}
                  <div className="flex flex-row gap-3 mb-4">
                    <a 
                      href={`${API_BASE_URL}/api/chrome-extension-download?v=${Date.now()}`}
                      download="tickler-web-clipper.zip"
                      className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center rounded-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      </svg>
                      Download Extension
                    </a>
                    <Button 
                      onClick={() => {
                        navigator.clipboard.writeText('chrome://extensions/');
                        alert('✅ Copied! Paste in address bar');
                      }}
                      variant="outline"
                      className="flex-1 h-12"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                      Copy Extensions URL
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => setShowExtensionModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Email Forwarding Instructions Modal */}
          {showEmailInstructionsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Email Forwarding Setup
                  </h3>
                  <button
                    onClick={() => setShowEmailInstructionsModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <p className="mb-4">
                    Forward emails from newsletters, research reports, and analysts directly to your dashboard. Use the appropriate email address to classify content automatically!
                  </p>
                  
                  {/* Important Note */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-yellow-800">
                      ⚠️ <strong>Important:</strong> Use the <strong>Copy</strong> button to get your unique email address. Your user ID is already embedded in the addresses below.
                    </p>
                  </div>

                  {/* TWO Email Addresses - Ideas and Library */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Ideas Email */}
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                        💡 Ideas
                      </h4>
                      <p className="text-xs text-orange-800 mb-3">
                        For actionable investment ideas with tickers
                      </p>
                      <code className="block bg-white px-3 py-2 rounded border border-orange-300 text-orange-900 font-mono text-xs break-all mb-2">
                        ideas+{user?.id || user?.user_id || 'LOADING'}@sandbox6099684e18ba4d7a984bcf8b928d45b0.mailgun.org
                      </code>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(`ideas+${user?.id || user?.user_id}@sandbox6099684e18ba4d7a984bcf8b928d45b0.mailgun.org`);
                          alert('✅ Ideas email copied!');
                        }}
                        variant="outline"
                        className="w-full text-xs border-orange-300 text-orange-700"
                      >
                        Copy Ideas Email
                      </Button>
                    </div>

                    {/* Library Email */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        📚 Library
                      </h4>
                      <p className="text-xs text-blue-800 mb-3">
                        For research, reports, and reference material
                      </p>
                      <code className="block bg-white px-3 py-2 rounded border border-blue-300 text-blue-900 font-mono text-xs break-all mb-2">
                        library+{user?.id || user?.user_id || 'LOADING'}@sandbox6099684e18ba4d7a984bcf8b928d45b0.mailgun.org
                      </code>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(`library+${user?.id || user?.user_id}@sandbox6099684e18ba4d7a984bcf8b928d45b0.mailgun.org`);
                          alert('✅ Library email copied!');
                        }}
                        variant="outline"
                        className="w-full text-xs border-blue-300 text-blue-700"
                      >
                        Copy Library Email
                      </Button>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="space-y-6">
                    <div>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        How to Use:
                      </h4>
                      <ol className="list-decimal list-inside space-y-3 text-sm">
                        <li>Copy the email address for your desired classification (Ideas or Library)</li>
                        <li>Forward content to the appropriate address:
                          <ul className="ml-6 mt-2 space-y-1 list-disc">
                            <li><strong className="text-orange-600">Ideas:</strong> Actionable investment ideas, stock picks, trade alerts</li>
                            <li><strong className="text-blue-600">Library:</strong> Research reports, market commentary, educational content</li>
                          </ul>
                        </li>
                        <li>The content will automatically appear in the correct section</li>
                        <li>Works great with newsletters like Substack, research reports, and analyst emails</li>
                      </ol>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Pro Tips:
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          <span>Set up automatic forwarding rules in Gmail to route specific newsletters to Ideas or Library</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          <span>The system will automatically extract ticker symbols and company names</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          <span>No AI classification costs - you control where content goes!</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowEmailInstructionsModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* X Account Modal */}
          {showXAccountModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-lg w-full p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`rounded-lg p-2 ${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                      <svg className={`h-6 w-6 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        X Account
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                        Link to use @SaveToTickler & import bookmarks
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowXAccountModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                {/* X Account Status */}
                <TwitterAccountSection 
                  user={user} 
                  isDarkMode={isDarkMode}
                  API_BASE_URL={API_BASE_URL}
                  onClose={() => setShowXAccountModal(false)}
                  showNotification={showNotification}
                  setShowPricingModal={setShowPricingModal}
                />

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowXAccountModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Reauthorize X Account Modal - Shows when token expires */}
          {showReauthorizeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full overflow-hidden`}>
                {/* Header with warning icon */}
                <div className={`p-6 ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'} border-b ${isDarkMode ? 'border-orange-800/50' : 'border-orange-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                      <svg className={`w-7 h-7 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        X Authorization Expired
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                        Your session needs to be refreshed
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Your X (Twitter) authorization has expired. This happens periodically for security reasons. 
                    Please re-authorize to continue importing bookmarks and refreshing polls.
                  </p>
                  
                  <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2">
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        @{user?.x_username || 'your account'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className={`p-4 flex gap-3 border-t ${isDarkMode ? 'border-slate-700 bg-slate-800/30' : 'border-slate-100 bg-slate-50'}`}>
                  <button
                    onClick={() => setShowReauthorizeModal(false)}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isDarkMode 
                        ? 'text-slate-300 hover:bg-slate-700' 
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Later
                  </button>
                  <button
                    onClick={async () => {
                      setShowReauthorizeModal(false);
                      // Redirect to X OAuth - this triggers re-auth without full logout
                      try {
                        const response = await fetch(`${API_BASE_URL}/api/auth/twitter/login`);
                        const data = await response.json();
                        if (data.auth_url) {
                          window.location.href = data.auth_url;
                        } else {
                          console.error('No auth_url in response:', data);
                        }
                      } catch (error) {
                        console.error('Error initiating Twitter re-auth:', error);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Re-authorize X
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AI Models Comparison Modal */}
          {showLLMPreview && llmPreviewData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                {(() => {
                  console.log('🎭 MODAL IS RENDERING', {
                    showLLMPreview,
                    hasData: !!llmPreviewData,
                    modelCount: llmPreviewData?.models?.length
                  });
                  return null;
                })()}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      🧪 AI Model Comparison
                    </h3>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      {llmPreviewData.idea?.title}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowLLMPreview(false);
                      setLlmPreviewData(null);
                    }}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                {/* Model Comparisons - Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {llmPreviewData.models?.map((model, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {model.name}
                          </h4>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Cost: ${model.cost_input}/${model.cost_output} per 1M tokens
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          model.status === 'success' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {model.status === 'success' ? '✓ Success' : '✗ Failed'}
                        </span>
                      </div>
                      
                      <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        <p className="whitespace-pre-wrap">
                          {model.summary || 'Failed to generate summary'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Close Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setShowLLMPreview(false);
                      setLlmPreviewData(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Email Configuration Modal */}
          {showEmailConfigModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Email Scheduling Configuration
                  </h3>
                  <button
                    onClick={() => setShowEmailConfigModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Email Address */}
                  <div>
                    <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={emailSettings.email_address || emailSettings.email || ''}
                      onChange={(e) => setEmailSettings({...emailSettings, email_address: e.target.value, email: e.target.value})}
                      className={`h-11 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    />
                  </div>

                  {/* Investment Digest */}
                  <div className="space-y-4">
                    <div>
                      <label className={`text-sm font-medium block mb-3 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Investment Digest
                      </label>
                      
                      {/* Frequency Selection */}
                      <div className="flex gap-4">
                        <button
                          onClick={() => setEmailSettings({
                            ...emailSettings, 
                            weekly_digest_enabled: true,
                            daily_digest_enabled: false
                          })}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                            emailSettings.weekly_digest_enabled
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                          }`}
                        >
                          <div className="font-medium">Weekly</div>
                        </button>
                        
                        <button
                          onClick={() => setEmailSettings({
                            ...emailSettings, 
                            weekly_digest_enabled: false,
                            daily_digest_enabled: true
                          })}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                            emailSettings.daily_digest_enabled
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                          }`}
                        >
                          <div className="font-medium">Daily</div>
                        </button>
                        
                        <button
                          onClick={() => setEmailSettings({
                            ...emailSettings, 
                            weekly_digest_enabled: false,
                            daily_digest_enabled: false
                          })}
                          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                            !emailSettings.weekly_digest_enabled && !emailSettings.daily_digest_enabled
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                          }`}
                        >
                          <div className="font-medium">Off</div>
                        </button>
                      </div>
                    </div>

                    {/* Weekly Options */}
                    {emailSettings.weekly_digest_enabled && (
                      <div className="ml-6 pl-4 border-l-2 border-indigo-200 space-y-4">
                        <div className="space-y-3">
                          <div>
                            <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Day</label>
                            <select
                              value={emailSettings.weekly_digest_day || 'Sunday'}
                              onChange={(e) => setEmailSettings({...emailSettings, weekly_digest_day: e.target.value})}
                              className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            >
                              <option>Sunday</option>
                              <option>Monday</option>
                              <option>Tuesday</option>
                              <option>Wednesday</option>
                              <option>Thursday</option>
                              <option>Friday</option>
                              <option>Saturday</option>
                            </select>
                          </div>
                          <div>
                            <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Time</label>
                            <select
                              value={emailSettings.weekly_digest_time || '09:00'}
                              onChange={(e) => setEmailSettings({...emailSettings, weekly_digest_time: e.target.value})}
                              className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            >
                              {['00:00', '00:15', '00:30', '00:45', '01:00', '01:15', '01:30', '01:45', '02:00', '02:15', '02:30', '02:45', '03:00', '03:15', '03:30', '03:45', '04:00', '04:15', '04:30', '04:45', '05:00', '05:15', '05:30', '05:45', '06:00', '06:15', '06:30', '06:45', '07:00', '07:15', '07:30', '07:45', '08:00', '08:15', '08:30', '08:45', '09:00', '09:15', '09:30', '09:45', '10:00', '10:15', '10:30', '10:45', '11:00', '11:15', '11:30', '11:45', '12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45', '14:00', '14:15', '14:30', '14:45', '15:00', '15:15', '15:30', '15:45', '16:00', '16:15', '16:30', '16:45', '17:00', '17:15', '17:30', '17:45', '18:00', '18:15', '18:30', '18:45', '19:00', '19:15', '19:30', '19:45', '20:00', '20:15', '20:30', '20:45', '21:00', '21:15', '21:30', '21:45', '22:00', '22:15', '22:30', '22:45', '23:00', '23:15', '23:30', '23:45'].map(time => {
                                const hour = parseInt(time.split(':')[0]);
                                const minute = time.split(':')[1];
                                const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                const displayTime = `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                return <option key={time} value={time}>{displayTime}</option>;
                              })}
                            </select>
                          </div>
                          <div>
                            <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Timezone</label>
                            <select
                              value={emailSettings.timezone || 'America/New_York'}
                              onChange={(e) => setEmailSettings({...emailSettings, timezone: e.target.value})}
                              className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            >
                              <option value="America/New_York">Eastern (ET)</option>
                              <option value="America/Chicago">Central (CT)</option>
                              <option value="America/Denver">Mountain (MT)</option>
                              <option value="America/Los_Angeles">Pacific (PT)</option>
                              <option value="America/Phoenix">Arizona</option>
                              <option value="Europe/London">London</option>
                              <option value="Europe/Paris">Paris</option>
                              <option value="Asia/Tokyo">Tokyo</option>
                              <option value="Asia/Hong_Kong">Hong Kong</option>
                              <option value="Australia/Sydney">Sydney</option>
                              <option value="UTC">UTC</option>
                            </select>
                          </div>
                        </div>
                        <button
                          onClick={testWeeklyDigest}
                          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          Test Weekly Digest
                        </button>
                      </div>
                    )}

                    {/* Daily Options */}
                    {emailSettings.daily_digest_enabled && (
                      <div className="ml-4 sm:ml-6 pl-3 sm:pl-4 border-l-2 border-indigo-200 space-y-4">
                        <div className="space-y-3">
                          <div>
                            <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Time</label>
                            <select
                              value={emailSettings.daily_digest_time || emailSettings.digest_time || '08:00'}
                              onChange={(e) => setEmailSettings({...emailSettings, daily_digest_time: e.target.value, digest_time: e.target.value})}
                              className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            >
                              {['00:00', '00:15', '00:30', '00:45', '01:00', '01:15', '01:30', '01:45', '02:00', '02:15', '02:30', '02:45', '03:00', '03:15', '03:30', '03:45', '04:00', '04:15', '04:30', '04:45', '05:00', '05:15', '05:30', '05:45', '06:00', '06:15', '06:30', '06:45', '07:00', '07:15', '07:30', '07:45', '08:00', '08:15', '08:30', '08:45', '09:00', '09:15', '09:30', '09:45', '10:00', '10:15', '10:30', '10:45', '11:00', '11:15', '11:30', '11:45', '12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45', '14:00', '14:15', '14:30', '14:45', '15:00', '15:15', '15:30', '15:45', '16:00', '16:15', '16:30', '16:45', '17:00', '17:15', '17:30', '17:45', '18:00', '18:15', '18:30', '18:45', '19:00', '19:15', '19:30', '19:45', '20:00', '20:15', '20:30', '20:45', '21:00', '21:15', '21:30', '21:45', '22:00', '22:15', '22:30', '22:45', '23:00', '23:15', '23:30', '23:45'].map(time => {
                                const hour = parseInt(time.split(':')[0]);
                                const minute = time.split(':')[1];
                                const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                                const displayTime = `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                                return <option key={time} value={time}>{displayTime}</option>;
                              })}
                            </select>
                          </div>
                          <div>
                            <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Timezone</label>
                            <select
                              value={emailSettings.timezone || 'America/New_York'}
                              onChange={(e) => setEmailSettings({...emailSettings, timezone: e.target.value})}
                              className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            >
                              <option value="America/New_York">Eastern (ET)</option>
                              <option value="America/Chicago">Central (CT)</option>
                              <option value="America/Denver">Mountain (MT)</option>
                              <option value="America/Los_Angeles">Pacific (PT)</option>
                              <option value="America/Phoenix">Arizona</option>
                              <option value="Europe/London">London</option>
                              <option value="Europe/Paris">Central EU</option>
                              <option value="Asia/Tokyo">Tokyo</option>
                              <option value="Asia/Hong_Kong">Hong Kong</option>
                              <option value="Australia/Sydney">Sydney</option>
                              <option value="UTC">UTC</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    onClick={() => setShowEmailConfigModal(false)}
                    variant="outline"
                    className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      await saveEmailSettings();
                      setShowEmailConfigModal(false);
                    }}
                    disabled={emailSettingsLoading}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {emailSettingsLoading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Daily Dossier Configuration Modal */}
          {showDossierConfigModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Daily Dossier Settings
                  </h3>
                  <button
                    onClick={() => setShowDossierConfigModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Frequency Selection */}
                  <div>
                    <label className={`text-sm font-medium mb-3 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      Frequency
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setEmailSettings(prev => ({ ...prev, folder_email_frequency: 'weekly', folder_email_enabled: true }));
                        }}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                          emailSettings.folder_email_frequency === 'weekly'
                            ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                            : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                        }`}
                      >
                        <div className="font-medium">Weekly</div>
                      </button>
                      
                      <button
                        onClick={() => {
                          setEmailSettings(prev => ({ ...prev, folder_email_frequency: 'daily', folder_email_enabled: true }));
                        }}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                          emailSettings.folder_email_frequency === 'daily'
                            ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                            : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                        }`}
                      >
                        <div className="font-medium">Daily</div>
                      </button>
                      
                      <button
                        onClick={() => {
                          setEmailSettings(prev => ({ ...prev, folder_email_frequency: 'off', folder_email_enabled: false }));
                        }}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                          emailSettings.folder_email_frequency === 'off'
                            ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                            : (isDarkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-300 bg-white text-slate-700')
                        }`}
                      >
                        <div className="font-medium">Off</div>
                      </button>
                    </div>
                  </div>

                  {/* Time & Timezone - only show if not Off */}
                  {emailSettings.folder_email_frequency !== 'off' && (
                    <>
                      {/* Time Selection */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Time
                        </label>
                        <select
                          value={emailSettings.folder_email_time || '08:00'}
                          onChange={(e) => {
                            setEmailSettings(prev => ({ ...prev, folder_email_time: e.target.value }));
                          }}
                          className={`w-full px-3 py-2 rounded-lg text-sm ${
                            isDarkMode 
                              ? 'bg-slate-800 border-slate-600 text-white' 
                              : 'bg-white border-slate-300 text-slate-800'
                          } border`}
                        >
                          {['00:00', '00:15', '00:30', '00:45', '01:00', '01:15', '01:30', '01:45', '02:00', '02:15', '02:30', '02:45', '03:00', '03:15', '03:30', '03:45', '04:00', '04:15', '04:30', '04:45', '05:00', '05:15', '05:30', '05:45', '06:00', '06:15', '06:30', '06:45', '07:00', '07:15', '07:30', '07:45', '08:00', '08:15', '08:30', '08:45', '09:00', '09:15', '09:30', '09:45', '10:00', '10:15', '10:30', '10:45', '11:00', '11:15', '11:30', '11:45', '12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45', '14:00', '14:15', '14:30', '14:45', '15:00', '15:15', '15:30', '15:45', '16:00', '16:15', '16:30', '16:45', '17:00', '17:15', '17:30', '17:45', '18:00', '18:15', '18:30', '18:45', '19:00', '19:15', '19:30', '19:45', '20:00', '20:15', '20:30', '20:45', '21:00', '21:15', '21:30', '21:45', '22:00', '22:15', '22:30', '22:45', '23:00', '23:15', '23:30', '23:45'].map(time => {
                            const hour = parseInt(time.split(':')[0]);
                            const minute = time.split(':')[1];
                            const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
                            const displayTime = `${displayHour}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
                            return <option key={time} value={time}>{displayTime}</option>;
                          })}
                        </select>
                      </div>

                      {/* Timezone Selection */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Timezone
                        </label>
                        <select
                          value={emailSettings.timezone || 'America/New_York'}
                          onChange={(e) => {
                            setEmailSettings(prev => ({ ...prev, timezone: e.target.value }));
                          }}
                          className={`w-full px-3 py-2 rounded-lg text-sm ${
                            isDarkMode 
                              ? 'bg-slate-800 border-slate-600 text-white' 
                              : 'bg-white border-slate-300 text-slate-800'
                          } border`}
                        >
                          <option value="America/New_York">Eastern (ET)</option>
                          <option value="America/Chicago">Central (CT)</option>
                          <option value="America/Denver">Mountain (MT)</option>
                          <option value="America/Los_Angeles">Pacific (PT)</option>
                          <option value="America/Phoenix">Arizona</option>
                          <option value="Europe/London">London</option>
                          <option value="Europe/Paris">Central EU</option>
                          <option value="Asia/Tokyo">Tokyo</option>
                          <option value="Asia/Hong_Kong">Hong Kong</option>
                          <option value="Australia/Sydney">Sydney</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>

                      {/* Document Selection */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Include Documents
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { id: '10-K', label: '10-K' },
                            { id: '10-Q', label: '10-Q' },
                            { id: '8-K', label: '8-K' },
                            { id: 'proxy', label: 'Proxy' },
                            { id: 'statements', label: 'Statements' }
                          ].map(doc => (
                            <button
                              key={doc.id}
                              onClick={() => {
                                const currentDocs = emailSettings.folder_email_docs || [];
                                const newDocs = currentDocs.includes(doc.id)
                                  ? currentDocs.filter(d => d !== doc.id)
                                  : [...currentDocs, doc.id];
                                setEmailSettings(prev => ({ ...prev, folder_email_docs: newDocs }));
                              }}
                              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                (emailSettings.folder_email_docs || []).includes(doc.id)
                                  ? 'bg-blue-500 text-white'
                                  : isDarkMode 
                                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600' 
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                              }`}
                            >
                              {doc.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    onClick={() => setShowDossierConfigModal(false)}
                    variant="outline"
                    className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      await saveEmailSettings(emailSettings);
                      setShowDossierConfigModal(false);
                    }}
                    disabled={emailSettingsLoading}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {emailSettingsLoading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Add to Schedule Modal */}
          {showAddToScheduleModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full overflow-hidden`}>
                {/* Header */}
                <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-slate-700 bg-[#141c2e]' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Add to Schedule
                        </h3>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowAddToScheduleModal(false);
                        setScheduleModalTicker('');
                        setScheduleModalTickerSuggestions([]);
                        setScheduleModalDate('');
                      }}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className={`p-6 ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
                  {dossierSettings.enabled ? (
                    // Auto-schedule is ON - show message to turn it off
                    <div className="text-center py-4">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} flex items-center justify-center`}>
                        <svg className={`w-8 h-8 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                        </svg>
                      </div>
                      <h4 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Auto-Schedule is Active
                      </h4>
                      <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        Your ideas are being automatically scheduled across 31 days. To manually add ideas to specific dates, please turn off auto-schedule first.
                      </p>
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={async () => {
                            const newSettings = { ...dossierSettings, enabled: false };
                            setDossierSettings(newSettings);
                            await saveDossierSettings(newSettings, false);
                          }}
                          className="w-full px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                        >
                          Turn Off Auto-Schedule
                        </button>
                        <button
                          onClick={() => setShowAddToScheduleModal(false)}
                          className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors ${
                            isDarkMode 
                              ? 'bg-slate-700 text-white hover:bg-slate-600' 
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Auto-schedule is OFF - show ticker selection and date picker
                    <div className="space-y-5">
                      {/* Ticker Input with Autocomplete */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Select Ticker
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={scheduleModalTicker}
                            onChange={(e) => {
                              const val = e.target.value.toUpperCase();
                              setScheduleModalTicker(val);
                              // Search user's tickers
                              if (val.length >= 1) {
                                const suggestions = [];
                                // Search ideas
                                bookmarks?.forEach(b => {
                                  const ticker = b.tickers?.[0];
                                  if (ticker && (ticker.toUpperCase().includes(val) || (b.company_name || b.title || '').toUpperCase().includes(val))) {
                                    if (!suggestions.find(s => s.ticker === ticker)) {
                                      suggestions.push({ ticker, company: b.company_name || b.title || ticker, source: 'idea' });
                                    }
                                  }
                                });
                                // Search watchlists
                                watchlists?.forEach(w => {
                                  w.tickers?.forEach(ticker => {
                                    if (ticker.toUpperCase().includes(val)) {
                                      if (!suggestions.find(s => s.ticker === ticker)) {
                                        suggestions.push({ ticker, company: w.name, source: 'watchlist' });
                                      }
                                    }
                                  });
                                });
                                setScheduleModalTickerSuggestions(suggestions.slice(0, 8));
                              } else {
                                setScheduleModalTickerSuggestions([]);
                              }
                            }}
                            placeholder="Search ticker (e.g., AAPL)"
                            className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:border-orange-500 ${
                              isDarkMode 
                                ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-400' 
                                : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
                            }`}
                            autoFocus
                          />
                          {/* Ticker Suggestions */}
                          {scheduleModalTickerSuggestions.length > 0 && (
                            <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg border z-10 max-h-64 overflow-y-auto ${
                              isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'
                            }`}>
                              {scheduleModalTickerSuggestions.map((item, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    setScheduleModalTicker(item.ticker);
                                    setScheduleModalTickerSuggestions([]);
                                  }}
                                  className={`w-full text-left px-4 py-3 flex items-center justify-between border-b last:border-b-0 ${
                                    isDarkMode ? 'text-white hover:bg-slate-700 border-slate-700' : 'text-slate-800 hover:bg-orange-50 border-slate-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="font-bold text-orange-500 min-w-[60px]">{item.ticker}</span>
                                    <span className={`text-sm truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{item.company}</span>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    item.source === 'idea' 
                                      ? 'bg-blue-100 text-blue-700' 
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {item.source === 'idea' ? 'Idea' : 'Watchlist'}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Quick Add to Today's Folder Button */}
                      {scheduleModalTicker && (
                        <div className={`p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-blue-500/50 bg-blue-900/20' : 'border-blue-300 bg-blue-50'}`}>
                          <button
                            onClick={async () => {
                              if (!scheduleModalTicker) return;
                              setScheduleModalLoading(true);
                              try {
                                const response = await fetch(`${API_BASE_URL}/api/research-schedule/${user?.id || user?.email}/add-to-today`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    ticker: scheduleModalTicker,
                                    company_name: scheduleModalTickerSuggestions.find(s => s.ticker === scheduleModalTicker)?.company || scheduleModalTicker
                                  })
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  showNotification(data.message, 'success');
                                  fetchScheduledResearch();
                                  // Delay blue dot to give file time to generate in background
                                  setTimeout(() => {
                                    setHasNewFiles(true);
                                  }, 4000);
                                  setShowAddToScheduleModal(false);
                                  setScheduleModalTicker('');
                                  setScheduleModalTickerSuggestions([]);
                                  setScheduleModalDate('');
                                  setScheduleModalTime('08:00');
                                } else {
                                  showNotification('Failed to add to today', 'error');
                                }
                              } catch (error) {
                                console.error('Error adding to today:', error);
                                showNotification('Error adding to today', 'error');
                              } finally {
                                setScheduleModalLoading(false);
                              }
                            }}
                            disabled={scheduleModalLoading}
                            className={`w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                              scheduleModalLoading
                                ? isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-200 text-slate-400'
                                : 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:shadow-blue-500/25'
                            }`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                            </svg>
                            {scheduleModalLoading ? 'Adding...' : "Add to Today's Folder"}
                          </button>
                          <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                            Include in today's folder
                          </p>
                        </div>
                      )}

                      {/* Divider */}
                      {scheduleModalTicker && (
                        <div className="flex items-center gap-3">
                          <div className={`flex-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>OR SCHEDULE FOR LATER</span>
                          <div className={`flex-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>
                        </div>
                      )}

                      {/* Date Selection */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Select Date
                        </label>
                        <input
                          type="date"
                          value={scheduleModalDate}
                          onChange={(e) => setScheduleModalDate(e.target.value)}
                          min={(() => {
                            const now = new Date();
                            return now.getFullYear() + '-' + 
                              String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                              String(now.getDate()).padStart(2, '0');
                          })()}
                          className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:border-orange-500 ${
                            isDarkMode 
                              ? 'bg-slate-800 border-slate-700 text-white' 
                              : 'bg-white border-slate-300 text-slate-800'
                          }`}
                        />
                      </div>

                      {/* Time Selection */}
                      <div>
                        <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          Select Time
                        </label>
                        <select
                          value={scheduleModalTime}
                          onChange={(e) => setScheduleModalTime(e.target.value)}
                          className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:border-orange-500 ${
                            isDarkMode 
                              ? 'bg-slate-800 border-slate-700 text-white' 
                              : 'bg-white border-slate-300 text-slate-800'
                          }`}
                        >
                          {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(hour => (
                            <option key={hour} value={`${hour.toString().padStart(2, '0')}:00`}>
                              {hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-3 pt-2">
                        <button
                          onClick={async () => {
                            if (!scheduleModalTicker || !scheduleModalDate) {
                              return;
                            }
                            setScheduleModalLoading(true);
                            try {
                              const response = await fetch(`${API_BASE_URL}/api/scheduled-research`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${user?.access_token}`
                                },
                                body: JSON.stringify({
                                  ticker: scheduleModalTicker,
                                  reminder_date: scheduleModalDate,
                                  reminder_time: scheduleModalTime,
                                  user_id: user?.id
                                })
                              });
                              if (response.ok) {
                                // Refresh scheduled research
                                fetchScheduledResearch();
                                setShowAddToScheduleModal(false);
                                setScheduleModalTicker('');
                                setScheduleModalDate('');
                                setScheduleModalTime('08:00');
                              }
                            } catch (error) {
                              console.error('Error scheduling research:', error);
                            } finally {
                              setScheduleModalLoading(false);
                            }
                          }}
                          disabled={!scheduleModalTicker || !scheduleModalDate || scheduleModalLoading}
                          className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors ${
                            scheduleModalTicker && scheduleModalDate && !scheduleModalLoading
                              ? 'bg-orange-500 text-white hover:bg-orange-600'
                              : isDarkMode 
                                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          }`}
                        >
                          {scheduleModalLoading ? 'Scheduling...' : 'Add to Schedule'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddToScheduleModal(false);
                            setScheduleModalTicker('');
                            setScheduleModalTickerSuggestions([]);
                            setScheduleModalDate('');
                          }}
                          className={`w-full px-4 py-2.5 rounded-lg font-medium transition-colors ${
                            isDarkMode 
                              ? 'bg-slate-700 text-white hover:bg-slate-600' 
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Schedule Calendar Modal - 30 Day View */}
          {showScheduleCalendarModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden`}>
                {/* Header */}
                <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${isDarkMode ? 'border-slate-700 bg-[#141c2e]' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className={`text-base sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {dossierSettings.enabled ? 'Research Schedule' : 'Manual Schedule'}
                        </h3>
                        <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {dossierSettings.enabled 
                            ? `${researchSchedule.length} companies`
                            : `${scheduledResearch.length} reminder${scheduledResearch.length !== 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowScheduleCalendarModal(false)}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-200 text-slate-500'}`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Content - Timeline on mobile, Calendar on desktop */}
                <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-140px)]">
                  {(() => {
                    // Use appropriate schedule based on auto-schedule setting
                    // If auto-schedule is ON, use researchSchedule (auto-scheduled items)
                    // If auto-schedule is OFF, use scheduledResearch (manual reminders only)
                    const scheduleItems = dossierSettings.enabled 
                      ? researchSchedule.map(item => ({
                          ...item,
                          source: 'auto'
                        }))
                      : scheduledResearch.map(item => ({
                          ticker: item.ticker,
                          company_name: item.company_name || '',
                          scheduled_date: item.reminder_date,
                          scheduled_time: item.reminder_time,
                          status: item.status,
                          source: 'manual'
                        }));
                    
                    // Debug log
                    console.log('Schedule items sample:', scheduleItems.slice(0, 3).map(i => ({ ticker: i.ticker, company_name: i.company_name })));
                    
                    // Group schedule by date
                    const scheduleByDate = {};
                    scheduleItems.forEach(item => {
                      const date = item.scheduled_date;
                      if (!scheduleByDate[date]) {
                        scheduleByDate[date] = [];
                      }
                      scheduleByDate[date].push(item);
                    });

                    // Get all dates for next 31 days
                    const today = new Date();
                    // Use local time for today, not UTC
                    const todayStr = today.getFullYear() + '-' + 
                      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(today.getDate()).padStart(2, '0');
                    const dates = [];
                    for (let i = 0; i < 31; i++) {
                      const d = new Date(today);
                      d.setDate(d.getDate() + i);
                      // Use local time for dates
                      dates.push(d.getFullYear() + '-' + 
                        String(d.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(d.getDate()).padStart(2, '0'));
                    }

                    // Group by week for calendar layout (desktop)
                    const weeks = [];
                    let currentWeek = [];
                    dates.forEach((date, idx) => {
                      const dayOfWeek = new Date(date + 'T00:00:00').getDay();
                      if (idx === 0) {
                        for (let i = 0; i < dayOfWeek; i++) {
                          currentWeek.push(null);
                        }
                      }
                      currentWeek.push(date);
                      if (dayOfWeek === 6 || idx === dates.length - 1) {
                        weeks.push(currentWeek);
                        currentWeek = [];
                      }
                    });

                    // Filter dates with scheduled items for mobile timeline
                    const datesWithItems = dates.filter(date => scheduleByDate[date]?.length > 0);
                    
                    // Calculate total items
                    const totalItems = scheduleItems.length;

                    return (
                      <>
                        {/* Mobile Timeline View */}
                        <div className="sm:hidden space-y-3">
                          {scheduledResearchLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          ) : datesWithItems.length === 0 ? (
                            <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              <p>No scheduled research yet</p>
                            </div>
                          ) : (
                            datesWithItems.map(date => {
                              const items = scheduleByDate[date] || [];
                              const dateObj = new Date(date + 'T00:00:00');
                              const isToday = date === todayStr;
                              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                              const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                              return (
                                <div
                                  key={date}
                                  className="rounded-lg overflow-hidden"
                                >
                                  {/* Date Header */}
                                  <div className={`px-3 py-2 flex items-center justify-between ${
                                    isDarkMode 
                                      ? 'bg-slate-800 text-slate-300' 
                                      : 'bg-slate-100 text-slate-700'
                                  }`}>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-bold ${isToday ? 'text-orange-500' : ''}`}>{dayName}</span>
                                      <span className={`text-sm ${isToday ? 'text-orange-500' : 'opacity-80'}`}>{monthDay}</span>
                                      {isToday && <span className="text-orange-500 text-xs font-semibold">(Today)</span>}
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                                    }`}>
                                      {items.length} {items.length === 1 ? 'idea' : 'ideas'}
                                    </span>
                                  </div>
                                  
                                  {/* Ticker List */}
                                  <div className={`px-3 py-2 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-white border border-slate-200'}`}>
                                    <div className="space-y-2">
                                      {items.map((item, i) => (
                                        <div
                                          key={i}
                                          className={`flex items-center gap-2 p-2 rounded-lg ${
                                            isDarkMode 
                                              ? 'bg-slate-700/50' 
                                              : 'bg-slate-50'
                                          }`}
                                        >
                                          <span className={`font-bold text-sm ${
                                            isToday 
                                              ? (isDarkMode ? 'text-orange-300' : 'text-orange-600')
                                              : (isDarkMode ? 'text-slate-300' : 'text-slate-600')
                                          }`}>
                                            ${item.ticker}
                                          </span>
                                          {item.company_name && (
                                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                              {item.company_name}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Desktop Calendar View */}
                        <div className="hidden sm:block space-y-4">
                          {/* Week day headers */}
                          <div className="grid grid-cols-7 gap-2 mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                              <div key={day} className={`text-center text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {day}
                              </div>
                            ))}
                          </div>

                          {/* Calendar grid */}
                          {weeks.map((week, weekIdx) => (
                            <div key={weekIdx} className="grid grid-cols-7 gap-2">
                              {week.map((date, dayIdx) => {
                                if (!date) {
                                  return <div key={dayIdx} className="min-h-[90px]"></div>;
                                }
                                const items = scheduleByDate[date] || [];
                                const dateObj = new Date(date + 'T00:00:00');
                                const isToday = date === todayStr;
                                const dayNum = dateObj.getDate();
                                const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });

                                return (
                                  <div
                                    key={dayIdx}
                                    className={`min-h-[90px] rounded-lg p-2 transition-all ${
                                      items.length > 0
                                        ? isDarkMode 
                                          ? 'bg-blue-900/30 border border-blue-500/50' 
                                          : 'bg-blue-50 border border-blue-200'
                                        : isDarkMode 
                                          ? 'bg-slate-800 border border-slate-700' 
                                          : 'bg-slate-50 border border-slate-200'
                                    } ${isToday ? 'ring-2 ring-orange-500' : ''}`}
                                  >
                                    <div className={`text-xs font-semibold mb-1.5 ${
                                      isToday 
                                        ? 'text-orange-500' 
                                        : items.length > 0 
                                          ? isDarkMode ? 'text-blue-300' : 'text-blue-600'
                                          : isDarkMode ? 'text-slate-500' : 'text-slate-400'
                                    }`}>
                                      {dayNum === 1 || dayIdx === 0 ? `${monthName} ${dayNum}` : dayNum}
                                    </div>
                                    {items.length > 0 && (
                                      <div className="space-y-1">
                                        {items.slice(0, 3).map((item, i) => (
                                          <div
                                            key={i}
                                            className={`text-xs px-1.5 py-1 rounded ${
                                              isDarkMode 
                                                ? 'hover:bg-slate-700/50' 
                                                : 'bg-slate-100 border border-slate-200'
                                            }`}
                                            title={item.company_name ? `${item.ticker} - ${item.company_name}` : item.ticker}
                                          >
                                            <div className={`font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>${item.ticker}</div>
                                            {item.company_name && (
                                              <div className={`text-[9px] truncate font-normal leading-tight ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.company_name}</div>
                                            )}
                                          </div>
                                        ))}
                                        {items.length > 3 && (
                                          <div className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            +{items.length - 3} more
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ))}

                          {/* Legend */}
                          <div className={`flex items-center gap-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faFolder} className="h-3 w-3 text-slate-400" />
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Scheduled</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faFolderOpen} className="h-3 w-3" style={{ color: '#F58346' }} />
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Today</span>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Footer */}
                <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${isDarkMode ? 'border-slate-700 bg-[#141c2e]' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowScheduleCalendarModal(false)}
                      style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
                      className="px-4 py-2 text-sm rounded-lg text-white font-medium hover:opacity-90"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Info Modal (Stripe Integration Placeholder) */}
          {showBillingInfoModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Billing Information
                  </h3>
                  <button
                    onClick={() => setShowBillingInfoModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {/* Stripe Integration Notice */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                    <div className="flex items-start">
                      <svg className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <div>
                        <h4 className="text-sm font-semibold text-blue-900 mb-1">Stripe Integration Coming Soon</h4>
                        <p className="text-sm text-blue-800">
                          Secure payment processing via Stripe will be available shortly. You'll be able to manage your payment methods and billing information here.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Current Subscription Info */}
                  <div className="mb-6">
                    <h4 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Current Subscription
                    </h4>
                    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Plan</p>
                          <p className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {accountSubscription?.tier}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Billing Cycle</p>
                          <p className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} capitalize`}>
                            {accountSubscription?.billing_cycle || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Next Billing Date</p>
                          <p className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {accountSubscription?.next_billing_date 
                              ? new Date(accountSubscription.next_billing_date).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Status</p>
                          <p className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {accountSubscription?.status || 'Active'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* What Will Be Available */}
                  <div className="mb-6">
                    <h4 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Features Available After Stripe Integration
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Payment Method Management
                          </p>
                          <p className="text-sm text-slate-500">
                            Add, update, or remove credit cards and payment methods
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Billing History
                          </p>
                          <p className="text-sm text-slate-500">
                            View and download past invoices and payment receipts
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Billing Address
                          </p>
                          <p className="text-sm text-slate-500">
                            Update your billing address and contact information
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Tax Information
                          </p>
                          <p className="text-sm text-slate-500">
                            Add tax IDs and view tax documents for your subscription
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            Automatic Billing
                          </p>
                          <p className="text-sm text-slate-500">
                            Secure automated payments processed through Stripe
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Secure Payment Processing:</span> All payment information will be securely processed through Stripe. We never store your credit card details on our servers.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowBillingInfoModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Suggestions Modal */}
          {showSuggestionsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Submit a Suggestion
                  </h3>
                  <button
                    onClick={() => setShowSuggestionsModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <p className="mb-4">
                    We'd love to hear your ideas! Help us improve Tickler by sharing your suggestions for new features or improvements.
                  </p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Suggestion Title
                      </label>
                      <Input
                        type="text"
                        placeholder="Brief description of your suggestion"
                        className={`h-11 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                      />
                    </div>

                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Details
                      </label>
                      <textarea
                        placeholder="Tell us more about your suggestion..."
                        rows="6"
                        className={`w-full rounded-md border px-3 py-2 text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-400' : 'bg-white border-slate-300'}`}
                      />
                    </div>

                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Category (Optional)
                      </label>
                      <select className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                        <option>General</option>
                        <option>New Feature</option>
                        <option>User Interface</option>
                        <option>Performance</option>
                        <option>Integration</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> This is a placeholder form. In production, submissions will be sent to the support team for review.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button onClick={() => setShowSuggestionsModal(false)} variant="outline" className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      alert('Thank you for your suggestion! (Feature coming soon)');
                      setShowSuggestionsModal(false);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Submit Suggestion
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Help Modal */}
          {showHelpModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Get Help
                  </h3>
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <p className="mb-6">
                    Need assistance? Contact our support team and we'll help you resolve any issues.
                  </p>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Subject
                      </label>
                      <Input
                        type="text"
                        placeholder="What do you need help with?"
                        className={`h-11 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                      />
                    </div>

                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Issue Type
                      </label>
                      <select className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                        <option>Technical Issue</option>
                        <option>Account Question</option>
                        <option>Billing Question</option>
                        <option>Feature Question</option>
                        <option>Bug Report</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Description
                      </label>
                      <textarea
                        placeholder="Please describe your issue in detail..."
                        rows="6"
                        className={`w-full rounded-md border px-3 py-2 text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-400' : 'bg-white border-slate-300'}`}
                      />
                    </div>

                    <div>
                      <label className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Priority
                      </label>
                      <select className={`w-full h-10 rounded-md border px-3 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
                        <option>Low - General question</option>
                        <option>Medium - Issue affecting usage</option>
                        <option>High - Critical issue</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-md p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <div>
                        <p className="text-sm text-purple-800">
                          <strong>Response Time:</strong> We typically respond within 24 hours. High priority issues are addressed within 4 hours during business days.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button onClick={() => setShowHelpModal(false)} variant="outline" className={isDarkMode ? 'border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      alert('Your help request has been submitted! (Feature coming soon)');
                      setShowHelpModal(false);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Submit Request
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* FAQ Modal */}
          {showFAQModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Frequently Asked Questions
                  </h3>
                  <button
                    onClick={() => setShowFAQModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className={`space-y-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {/* FAQ Items */}
                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      How do I save investment ideas?
                    </h4>
                    <p className="text-sm">
                      The easiest way is to import your X (Twitter) bookmarks directly. Simply connect your X account in Settings and import your bookmarks - we'll automatically analyze them and extract investment ideas. You can also forward emails to your unique Tickler email address.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      What subscription plans are available?
                    </h4>
                    <p className="text-sm">
                      We offer a single plan at $49/month or $39/month billed annually ($468/year - save $120). This includes 1,000 items per month, and your first import of up to 50 bookmarks is free.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      How do Groups work?
                    </h4>
                    <p className="text-sm">
                      Groups allow you to collaborate with team members by sharing investment ideas. Admins can invite members, manage roles, and control group settings. Members can view shared ideas and contribute to group discussions.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Can I cancel my subscription anytime?
                    </h4>
                    <p className="text-sm">
                      Yes! You can cancel your subscription at any time from the Settings page. Your subscription will remain active until the end of your current billing cycle, then automatically downgrade to the Free tier.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      How do email digests work?
                    </h4>
                    <p className="text-sm">
                      You can configure Weekly Investment Digests (receive all ideas in a numbered list) and Daily Research Alerts (notifications for new filings and events). Set your preferred days and times in the Email Scheduling section of Settings.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Is my payment information secure?
                    </h4>
                    <p className="text-sm">
                      Yes. All payment information is securely processed through Stripe. We never store your credit card details on our servers.
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      How do I upgrade or downgrade my plan?
                    </h4>
                    <p className="text-sm">
                      Visit the Account & Subscription section in Settings. Click "Upgrade" to choose a higher tier or "Downgrade" to select a lower tier. Changes take effect at the end of your current billing cycle (except Free to Paid upgrades, which are immediate).
                    </p>
                  </div>

                  <div className={`border-b pb-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Why aren't Twitter threads being imported?
                    </h4>
                    <p className="text-sm">
                      Twitter's API only allows searching for thread replies from the last 7 days. If you bookmark a thread that's older than 7 days, we can only import the original tweet - the thread replies won't be available. For best results, bookmark threads soon after they're posted.
                    </p>
                  </div>

                  <div className="pb-4">
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Why am I missing replies to my polls/requests?
                    </h4>
                    <p className="text-sm">
                      Twitter's API only returns replies from the last 7 days. If someone replied to your poll more than 7 days ago and you haven't refreshed since, that reply cannot be retrieved. To avoid missing replies, refresh your polls at least once every 7 days. We'll remind you in your daily digest when polls need refreshing.
                    </p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mt-6">
                    <p className="text-sm text-orange-800">
                      <strong>Still have questions?</strong> Click the "Help" button to submit a support request, and our team will get back to you within 24 hours.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowFAQModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Keyboard Shortcuts Modal */}
          {showKeyboardShortcutsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"/>
                    </svg>
                    Keyboard Shortcuts
                  </h3>
                  <button
                    onClick={() => setShowKeyboardShortcutsModal(false)}
                    className={`${isDarkMode ? 'text-white hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* General Section */}
                  <div>
                    <h4 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      General
                    </h4>
                    <div className="space-y-2">
                      <div className={`flex items-center justify-between py-2 px-3 rounded ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Show shortcuts</span>
                        <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>?</kbd>
                      </div>
                      <div className={`flex items-center justify-between py-2 px-3 rounded ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Toggle dark mode</span>
                        <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>D</kbd>
                      </div>
                      <div className={`flex items-center justify-between py-2 px-3 rounded ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Close modal / Cancel</span>
                        <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>Esc</kbd>
                      </div>
                    </div>
                  </div>

                  {/* Search Section */}
                  <div>
                    <h4 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Search
                    </h4>
                    <div className="space-y-2">
                      <div className={`flex items-center justify-between py-2 px-3 rounded ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Focus search</span>
                        <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>/</kbd>
                      </div>
                      <div className={`flex items-center justify-between py-2 px-3 rounded ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-50'}`}>
                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Focus search (alt)</span>
                        <div className="flex items-center justify-center gap-1">
                          <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>⌘</kbd>
                          <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>+</span>
                          <kbd className={`px-2 py-1 text-xs font-mono rounded ${isDarkMode ? 'bg-slate-700 text-slate-300 border border-slate-600' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'}`}>K</kbd>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pro tip */}
                  <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-orange-900/20 border border-orange-800' : 'bg-orange-50 border border-orange-200'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                      <strong>Pro tip:</strong> Press <kbd className={`px-1.5 py-0.5 text-xs font-mono rounded mx-1 ${isDarkMode ? 'bg-slate-700 border border-slate-600' : 'bg-white border border-slate-300'}`}>?</kbd> anytime to see this help.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setShowKeyboardShortcutsModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

 




        </Tabs>
      </div>
      </main>
      )}
      
      {/* Footer with compliance links - Mobile Responsive - Hidden in workspace mode */}
      {!showWorkspace && (
      <footer className={`border-t mt-auto ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-center sm:text-left ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
              <span className="hidden sm:inline">© 2025 Tickler. IDEAS WORTH VETTING.</span>
              <span className="sm:hidden">© 2025 Tickler</span>
              <span className={`hidden sm:inline ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`}>|</span>
              <span className={isDarkMode ? 'text-slate-500' : 'text-slate-500'}>
                <span className="hidden sm:inline">Market data by </span>
                <a 
                  href="https://twelvedata.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`hover:underline transition-colors ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                >
                  Twelve Data
                </a>
              </span>
            </div>
            <div className="flex flex-row gap-3 sm:gap-4 items-center justify-center sm:justify-end">
              <Link to="/privacy" className={`transition-colors whitespace-nowrap ${isDarkMode ? 'hover:text-slate-200' : 'hover:text-slate-800'}`}>
                Privacy
              </Link>
              <span className={isDarkMode ? 'text-slate-600' : 'text-slate-300'}>·</span>
              <Link to="/terms" className={`transition-colors whitespace-nowrap ${isDarkMode ? 'hover:text-slate-200' : 'hover:text-slate-800'}`}>
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>
      )}

      {/* Document Selection Modal */}
      {showDocumentModal && (() => {
        // Compute filing status variables
        const noFilingsAtAll = !secAvailability.loading && !secAvailability.hasFilings && !secAvailability.hasForeignFilings;
        const isUSFiler = secAvailability.hasFilings;
        const isForeignFiler = secAvailability.hasForeignFilings && !secAvailability.hasFilings;
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>File</h2>
                <button 
                  onClick={() => setShowDocumentModal(false)}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                >✕</button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    Select Documents for {selectedTickerForScheduling?.ticker?.toUpperCase()}
                  </h3>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    Choose which information sources and financial data to include in your Research.
                  </p>
                  
                  {/* SEC Availability Status */}
                  <div className={`p-3 rounded-lg mb-4 ${
                    secAvailability.loading 
                      ? (isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200')
                      : secAvailability.hasFilings 
                        ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200')
                        : secAvailability.hasForeignFilings
                          ? (isDarkMode ? 'bg-purple-900/30 border border-purple-700' : 'bg-purple-50 border border-purple-200')
                          : (isDarkMode ? 'bg-[#141c2e]/50 border border-slate-600' : 'bg-slate-100 border border-slate-300')
                  }`}>
                    <div className="flex items-center gap-2">
                      {secAvailability.loading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Checking SEC filing availability...</span>
                        </>
                      ) : secAvailability.hasFilings ? (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}><strong>US Company</strong> — SEC filings available</span>
                        </>
                      ) : secAvailability.hasForeignFilings ? (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}><strong>Foreign Company with US Filings</strong> — 20-F/6-K available</span>
                        </>
                      ) : (
                        <>
                          <svg className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}><strong>File Not Available</strong></span>
                        </>
                      )}
                    </div>
                    {/* Stay tuned message for non-SEC filers */}
                    {!secAvailability.loading && !secAvailability.hasFilings && !secAvailability.hasForeignFilings && (
                      <p className={`text-sm mt-2 ml-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {selectedTickerForScheduling?.ticker?.toUpperCase()} doesn't have SEC filings. We're working on expanding coverage — stay tuned!
                      </p>
                    )}
                    {(secAvailability.hasFilings || secAvailability.hasForeignFilings) && (
                      <p className={`text-xs mt-1 ml-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{secAvailability.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {/* ONE-PAGE SUMMARY OPTION - TEMPORARILY HIDDEN - To restore: change false to true */}
                    {false && (
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mr-3">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">One-Page Investor Summary</span>
                          <p className="text-sm text-gray-500">
                            AI-powered overview with financials, performance, risks, and peer comparison
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeOnePageSummary}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeOnePageSummary: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </label>
                    )}

                    {/* MANAGEMENT Q&A OPTION - TEMPORARILY HIDDEN - To restore: change false to true */}
                    {false && (
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg mr-3">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                          </svg>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Management Q&A Template</span>
                          <p className="text-sm text-gray-500">
                            Cross-functional questions for buyside intro calls with executives
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeManagementQA}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeManagementQA: e.target.checked})}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                    </label>
                    )}
                    
                    {/* 10-K Annual Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-blue-400' : 'text-blue-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>10-K Annual Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            {noFilingsAtAll ? 'Not available' : 'Comprehensive annual business and financial disclosure'}
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.include10K}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include10K: e.target.checked})}
                        disabled={noFilingsAtAll || !isUSFiler}
                        className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'} focus:ring-blue-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>
                    
                    {/* 10-Q Quarterly Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-green-900/50' : 'bg-green-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-green-400' : 'text-green-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>10-Q Quarterly Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            {noFilingsAtAll ? 'Not available' : 'Quarterly financial reports'}
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.include10Q}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include10Q: e.target.checked})}
                        disabled={noFilingsAtAll || !isUSFiler}
                        className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-green-600'} focus:ring-green-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>
                    
                    {/* 8-K Current Reports */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-orange-400' : 'text-orange-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${!secAvailability.hasFilings ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>8-K Current Reports</span>
                          <p className={`text-sm ${!secAvailability.hasFilings ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Material events and corporate changes
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.include8K}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include8K: e.target.checked})}
                        disabled={!secAvailability.hasFilings}
                        className={`h-4 w-4 ${!secAvailability.hasFilings ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600'} focus:ring-orange-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>

                    {/* Proxy Statements */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isUSFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-pink-900/50' : 'bg-pink-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-pink-400' : 'text-pink-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>Proxy Statements</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isUSFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Executive compensation & governance
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeProxy}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeProxy: e.target.checked})}
                        disabled={noFilingsAtAll || !isUSFiler}
                        className={`h-4 w-4 ${(noFilingsAtAll || !isUSFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-pink-600'} focus:ring-pink-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>

                    {/* Divider for Foreign Filings */}
                    <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>FOREIGN ISSUER FILINGS</p>
                    </div>
                    
                    {/* 20-F Annual Report - Foreign Issuers */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isForeignFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-purple-400' : 'text-purple-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>20-F Annual Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Foreign company annual reports
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.include20F}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include20F: e.target.checked})}
                        disabled={noFilingsAtAll || !isForeignFiler}
                        className={`h-4 w-4 ${(noFilingsAtAll || !isForeignFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-purple-600'} focus:ring-purple-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>
                    
                    {/* 6-K Current Report - Foreign Issuers */}
                    <label className={`flex items-center justify-between ${(noFilingsAtAll || !isForeignFiler) ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-indigo-400' : 'text-indigo-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-200' : 'text-gray-700')}`}>6-K Reports</span>
                          <p className={`text-sm ${(noFilingsAtAll || !isForeignFiler) ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            Foreign company interim reports
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.include6K}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, include6K: e.target.checked})}
                        disabled={noFilingsAtAll || !isForeignFiler}
                        className={`h-4 w-4 ${(noFilingsAtAll || !isForeignFiler) ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600'} focus:ring-indigo-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>

                    {/* Divider for Additional Data */}
                    <div className={`border-t pt-3 mt-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>ADDITIONAL DATA</p>
                    </div>

                    {/* Financial Statements */}
                    <label className={`flex items-center justify-between ${noFilingsAtAll ? 'opacity-50' : ''}`}>
                      <div className="flex items-center">
                        <div className={`flex items-center justify-center w-8 h-8 ${noFilingsAtAll ? (isDarkMode ? 'bg-slate-700' : 'bg-gray-100') : (isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-100')} rounded-lg mr-3`}>
                          <svg className={`w-4 h-4 ${noFilingsAtAll ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-emerald-300' : 'text-emerald-600')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                        </div>
                        <div>
                          <span className={`font-medium ${noFilingsAtAll ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-emerald-400' : 'text-emerald-700')}`}>Financial Statements</span>
                          <p className={`text-sm ${noFilingsAtAll ? (isDarkMode ? 'text-slate-500' : 'text-gray-400') : (isDarkMode ? 'text-slate-400' : 'text-gray-500')}`}>
                            {noFilingsAtAll ? 'Not available' : 'Income Statement, Balance Sheet, Cash Flow'}
                          </p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeStatements || false}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeStatements: e.target.checked})}
                        disabled={noFilingsAtAll}
                        className={`h-4 w-4 ${noFilingsAtAll ? 'text-gray-400 cursor-not-allowed' : 'text-emerald-600'} focus:ring-emerald-500 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-gray-300'} rounded`}
                      />
                    </label>
                  </div>

                  {/* Temporarily removed AI Investment Analysis and Earnings Call Summary Pro features
                  <div className="border-t pt-4">
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg mr-3">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-purple-700">AI Investment Analysis</span>
                            <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold rounded-full">PRO</span>
                          </div>
                          <p className="text-sm text-gray-500">AI-powered critique of investment ideas based on selected documents</p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeAIAnalysis || false}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeAIAnalysis: e.target.checked})}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                    </label>

                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mr-3">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-blue-700">Earnings Call Summary</span>
                            <span className="px-2 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-semibold rounded-full">PRO</span>
                          </div>
                          <p className="text-sm text-gray-500">750-word summary of the latest earnings conference call</p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={secFilingPreferences.includeEarningsCallSummary || false}
                        onChange={(e) => setSecFilingPreferences({...secFilingPreferences, includeEarningsCallSummary: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </label>
                  </div>
                  */}
                  
                </div>

                {/* Generate Button */}
                <div className={`flex justify-end space-x-3 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <button
                    onClick={() => setShowDocumentModal(false)}
                    className={`px-4 py-2 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-600 hover:text-gray-800'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDocumentGeneration}
                    disabled={!selectedTickerForScheduling || (!secAvailability.hasFilings && !secAvailability.hasForeignFilings && !secAvailability.loading)}
                    className={`px-6 py-2 ${
                      !secAvailability.hasFilings && !secAvailability.hasForeignFilings && !secAvailability.loading
                        ? 'bg-slate-400 cursor-not-allowed'
                        : 'bg-orange-500 hover:bg-orange-600'
                    } disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    {!secAvailability.hasFilings && !secAvailability.hasForeignFilings && !secAvailability.loading 
                      ? 'File Not Available' 
                      : 'Generate File'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Reschedule Modal */}
      {showRescheduleModal && rescheduleItem && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRescheduleModal(false)}
        >
          <div 
            className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-sm w-full shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {isScheduleMode ? 'Schedule' : 'Recycle'} ${rescheduleItem.ticker}
                </h3>
                <button 
                  onClick={() => setShowRescheduleModal(false)}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-700'}`}
                >×</button>
              </div>
              
              <div className="space-y-3">
                {/* Quick Add to Today's Folder - Blue - Fastest option */}
                <button
                  onClick={async () => {
                    if (!rescheduleItem?.ticker) return;
                    setRescheduling(true);
                    try {
                      const response = await fetch(`${API_BASE_URL}/api/research-schedule/${user?.id || user?.email}/add-to-today`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          ticker: rescheduleItem.ticker,
                          company_name: rescheduleItem.company_name || rescheduleItem.ticker,
                          // Pass original content for "Open Original" functionality
                          idea_id: rescheduleItem.idea_id || rescheduleItem.id,
                          content: rescheduleItem.content || rescheduleItem.original_content || rescheduleItem.summary,
                          summary: rescheduleItem.summary,
                          author: rescheduleItem.author || rescheduleItem.source,
                          title: rescheduleItem.title,
                          source_url: rescheduleItem.source_url || rescheduleItem.url
                        })
                      });
                      if (response.ok) {
                        const data = await response.json();
                        showNotification(data.message, 'success');
                        fetchScheduledResearch();
                        // Delay blue dot to give file time to generate in background
                        setTimeout(() => {
                          setHasNewFiles(true);
                        }, 4000);
                        setShowRescheduleModal(false);
                      } else {
                        const error = await response.json();
                        showNotification(`Failed: ${error.detail || 'Unknown error'}`, 'error');
                      }
                    } catch (error) {
                      console.error('Error adding to today:', error);
                      showNotification('Error adding to today', 'error');
                    } finally {
                      setRescheduling(false);
                    }
                  }}
                  disabled={rescheduling}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-blue-500/50 bg-blue-900/20 hover:border-blue-400 hover:bg-blue-900/30' 
                      : 'border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100'
                  } ${rescheduling ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Add to Today's Folder</p>
                      <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                        Include in today's folder
                      </p>
                    </div>
                  </div>
                </button>

                {/* Auto Schedule/Recycle - Orange */}
                <button
                  onClick={() => handleReschedule('auto')}
                  disabled={rescheduling}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-orange-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-orange-500 hover:bg-orange-50'
                  } ${rescheduling ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{isScheduleMode ? 'Auto Schedule' : 'Auto Recycle'}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Let the system pick the best time
                      </p>
                    </div>
                  </div>
                </button>
                
                {/* After Next Earnings - Green - For both Files and Collections */}
                <button
                  onClick={() => handleReschedule('earnings')}
                  disabled={rescheduling || rescheduleEarningsLoading || !rescheduleEarningsData}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-green-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-green-500 hover:bg-green-50'
                  } ${(rescheduling || rescheduleEarningsLoading || !rescheduleEarningsData) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>After Next Earnings</p>
                      {rescheduleEarningsLoading ? (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading earnings date...</p>
                      ) : rescheduleEarningsData ? (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Est. {new Date(rescheduleEarningsData.day_after_earnings).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          <span className={`ml-1 text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            (Earnings est. {new Date(rescheduleEarningsData.next_earnings_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                          </span>
                        </p>
                      ) : (
                        <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No earnings data available</p>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Manual - Pick a Date - Purple */}
                <button
                  onClick={() => handleReschedule('manual')}
                  disabled={rescheduling}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    isDarkMode 
                      ? 'border-slate-600 hover:border-purple-500 hover:bg-slate-800' 
                      : 'border-slate-200 hover:border-purple-500 hover:bg-purple-50'
                  } ${rescheduling ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                      <svg className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Pick a Date</p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Choose a custom date manually</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IR Email Composer Modal */}
      {irEmailComposer && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setIrEmailComposer(null)}>
          <div 
            className={`rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    Email IR - {irEmailComposer.companyName}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    To: {irEmailComposer.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIrEmailComposer(null)}
                className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className={`p-4 space-y-4 ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
              {/* Subject */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Subject</label>
                <input
                  type="text"
                  value={irEmailComposer.subject}
                  onChange={(e) => setIrEmailComposer({...irEmailComposer, subject: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
              
              {/* Body */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Message</label>
                <textarea
                  value={irEmailComposer.body}
                  onChange={(e) => setIrEmailComposer({...irEmailComposer, body: e.target.value})}
                  rows={12}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm`}
                />
              </div>
              
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Tip: Edit the template above, then click "Open in Email" to compose in your default email app (Outlook, Gmail, etc.)
              </p>
            </div>
            
            {/* Footer */}
            <div className={`px-4 py-3 border-t flex items-center justify-between ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <button
                onClick={() => setIrEmailComposer(null)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const mailtoUrl = `mailto:${irEmailComposer.email}?subject=${encodeURIComponent(irEmailComposer.subject)}&body=${encodeURIComponent(irEmailComposer.body)}`;
                  window.open(mailtoUrl, '_blank');
                  setIrEmailComposer(null);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                Open in Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Watchlist Type Selection Popup (Auto vs Manual) */}
      {showWatchlistTypePopup && pendingWatchlistTicker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => {
          setShowWatchlistTypePopup(false);
          setPendingWatchlistTicker(null);
          setPendingWatchlistIndustry(null);
        }}>
          <div 
            className={`rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Add ${pendingWatchlistTicker} to Tickers
              </h3>
              <button
                onClick={() => {
                  setShowWatchlistTypePopup(false);
                  setPendingWatchlistTicker(null);
                  setPendingWatchlistIndustry(null);
                }}
                className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            {/* Options */}
            <div className="p-4 space-y-3">
              {/* Auto Option */}
              <button
                onClick={() => {
                  // Save preference if checkbox is checked
                  if (rememberCollectionChoice) {
                    localStorage.setItem('tickler_collection_choice', 'auto');
                    setSavedCollectionChoice('auto');
                  }
                  setShowWatchlistTypePopup(false);
                  autoAddToWatchlist(pendingWatchlistTicker, pendingWatchlistIndustry);
                  setPendingWatchlistTicker(null);
                  setPendingWatchlistIndustry(null);
                }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  isDarkMode 
                    ? 'border-slate-700 hover:border-blue-500 bg-slate-800/50 hover:bg-slate-800' 
                    : 'border-slate-200 hover:border-blue-500 bg-slate-50 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Auto</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Add to sector-based watchlist automatically
                    </p>
                  </div>
                </div>
              </button>
              
              {/* Manual Option */}
              <button
                onClick={() => {
                  // Save preference if checkbox is checked
                  if (rememberCollectionChoice) {
                    localStorage.setItem('tickler_collection_choice', 'manual');
                    setSavedCollectionChoice('manual');
                  }
                  setShowWatchlistTypePopup(false);
                  // Open create collection modal
                  setCollectionModalMode('create');
                  setShowCreateWatchlist(true);
                }}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  isDarkMode 
                    ? 'border-slate-700 hover:border-orange-500 bg-slate-800/50 hover:bg-slate-800' 
                    : 'border-slate-200 hover:border-orange-500 bg-slate-50 hover:bg-orange-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Manual</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Choose which ticker list to add to
                    </p>
                  </div>
                </div>
              </button>
              
              {/* Remember Choice Checkbox */}
              <label className={`flex items-center gap-3 pt-2 cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                <input
                  type="checkbox"
                  checked={rememberCollectionChoice}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberCollectionChoice(checked);
                    localStorage.setItem('tickler_remember_collection_choice', checked.toString());
                    if (!checked) {
                      // Clear saved choice when unchecking
                      localStorage.removeItem('tickler_collection_choice');
                      setSavedCollectionChoice(null);
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-500 text-orange-500 focus:ring-orange-500 focus:ring-offset-0"
                />
                <span className="text-sm">Remember my choice</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Collection Modal */}
      {showCreateWatchlist && (
        <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/80' : 'bg-black/50'} z-50 flex items-start justify-center pt-16 sm:pt-24 p-4`} onClick={() => {
          setShowCreateWatchlist(false);
          setCreateCollectionSelectedTickers([]);
          setCreateCollectionTickerSearch('');
          setCreateCollectionSearchResults([]);
          setNewWatchlistName('');
          setPendingWatchlistTicker(null);
          setPendingWatchlistIndustry(null);
          setCollectionModalMode('create');
        }}>
          <div 
            className={`rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {collectionModalMode === 'create' ? 'Create or Add to List' : 'Add to Existing List'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateWatchlist(false);
                  setCreateCollectionSelectedTickers([]);
                  setCreateCollectionTickerSearch('');
                  setNewWatchlistName('');
                  setPendingWatchlistTicker(null);
                  setPendingWatchlistIndustry(null);
                  setCollectionModalMode('create');
                }}
                className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            {/* Tabs */}
            <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <button
                onClick={() => setCollectionModalMode('create')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  collectionModalMode === 'create'
                    ? `text-orange-500 border-b-2 border-orange-500 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-orange-50'}`
                    : `${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                }`}
              >
                Create New
              </button>
              <button
                onClick={() => setCollectionModalMode('existing')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  collectionModalMode === 'existing'
                    ? `text-orange-500 border-b-2 border-orange-500 ${isDarkMode ? 'bg-[#141c2e]/50' : 'bg-orange-50'}`
                    : `${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                }`}
              >
                Add to Existing
              </button>
            </div>
            
            {collectionModalMode === 'create' ? (
              <>
                {/* Ticker List Name Input with Autocomplete */}
                <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Ticker List Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g., Tech Growth, Value Picks..."
                      value={newWatchlistName}
                      onChange={(e) => setNewWatchlistName(e.target.value)}
                      className={`w-full rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                          ? 'bg-slate-800 border border-slate-600 text-white placeholder-slate-500' 
                          : 'bg-white border border-slate-300 text-slate-800 placeholder-slate-400'
                      }`}
                      autoFocus
                    />
                    {/* Autocomplete dropdown for existing lists */}
                    {newWatchlistName.trim().length > 0 && (() => {
                      const sectorNames = ['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'];
                      const matchingLists = watchlists.filter(w => 
                        w.id &&  // Must have a valid ID (not a ghost list)
                        !w.id.startsWith('temp_') &&  // Not a temporary/unsaved list
                        !w.source_info && 
                        !sectorNames.includes(w.name) &&
                        w.name.toLowerCase().includes(newWatchlistName.toLowerCase().trim()) &&
                        Array.isArray(w.tickers)  // Must have a valid tickers array
                      );
                      
                      if (matchingLists.length === 0) return null;
                      
                      return (
                        <div className={`absolute left-0 right-0 top-full mt-1 rounded-lg shadow-lg border z-50 max-h-48 overflow-y-auto ${
                          isDarkMode ? 'bg-[#1a2235] border-slate-600' : 'bg-white border-slate-200'
                        }`}>
                          <div className={`px-3 py-2 text-xs font-medium uppercase tracking-wide border-b ${
                            isDarkMode ? 'text-slate-400 border-slate-700 bg-[#1a2235]/50' : 'text-slate-500 border-slate-100 bg-slate-50'
                          }`}>
                            Add to existing list
                          </div>
                          {matchingLists.map(list => (
                            <button
                              key={list.id}
                              onClick={() => {
                                if (pendingWatchlistTicker) {
                                  manualAddToWatchlist(pendingWatchlistTicker, list.id);
                                  setShowCreateWatchlist(false);
                                  setNewWatchlistName('');
                                } else if (createCollectionSelectedTickers.length > 0) {
                                  // Add selected tickers to this existing list
                                  const tickersToAdd = createCollectionSelectedTickers.map(t => t.ticker);
                                  tickersToAdd.forEach(ticker => manualAddToWatchlist(ticker, list.id));
                                  setShowCreateWatchlist(false);
                                  setNewWatchlistName('');
                                  setCreateCollectionSelectedTickers([]);
                                }
                              }}
                              className={`w-full text-left px-3 py-2.5 flex items-center justify-between transition-colors ${
                                isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-teal-400' : 'text-teal-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                                </svg>
                                <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                  {list.name}
                                </span>
                              </div>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {list.tickers?.length || 0} tickers
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Search Input */}
                <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Add Tickers
                  </label>
                  <div className="relative">
                    <svg className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by $ticker, @handle, company name..."
                      value={createCollectionTickerSearch}
                      onChange={(e) => setCreateCollectionTickerSearch(e.target.value)}
                      className={`w-full rounded-lg pl-11 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                          ? 'bg-slate-800 border border-slate-600 text-white placeholder-slate-500' 
                          : 'bg-white border border-slate-300 text-slate-800 placeholder-slate-400'
                      }`}
                    />
                    {createCollectionSearching && (
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                    )}
                  </div>
                </div>
                
                {/* Search Results or Selected Tickers */}
                <div className="max-h-64 overflow-y-auto">
                  {createCollectionTickerSearch && createCollectionSearchResults.length > 0 ? (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-500 text-sm font-medium">
                          Found {createCollectionSearchResults.length} result{createCollectionSearchResults.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        {createCollectionSearchResults.slice(0, 8).map((idea, idx) => {
                          const isTickerAlreadyAdded = createCollectionSelectedTickers.find(t => t.ticker === idea.ticker);
                          return (
                            <button
                              key={idea.idea_id || idx}
                              onClick={() => !isTickerAlreadyAdded && addTickerToCollectionSelection(idea)}
                              className={`w-full text-left p-3 rounded-lg transition-colors ${
                                isTickerAlreadyAdded 
                                  ? 'bg-orange-500/20 border border-orange-500/50 cursor-default' 
                                  : isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isTickerAlreadyAdded ? 'bg-orange-500' : isDarkMode ? 'bg-slate-700' : 'bg-slate-300'
                                }`}>
                                  {isTickerAlreadyAdded ? (
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{idx + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`font-bold text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                      ${idea.ticker}
                                    </span>
                                    {isTickerAlreadyAdded && (
                                      <span className="px-1.5 py-0.5 bg-orange-500/30 text-orange-600 text-xs rounded">
                                        Added
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                    {idea.company_name || idea.ticker}
                                  </p>
                                </div>
                                {!isTickerAlreadyAdded && (
                                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                                  </svg>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : createCollectionTickerSearch && !createCollectionSearching && createCollectionSearchResults.length === 0 ? (
                    <div className="p-4 text-center">
                      <svg className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No results found</p>
                    </div>
                  ) : createCollectionTickerSearch && createCollectionSearching ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Searching...</p>
                    </div>
                  ) : createCollectionSelectedTickers.length > 0 ? (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Selected Tickers ({createCollectionSelectedTickers.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {createCollectionSelectedTickers.map((ticker, idx) => (
                          <div
                            key={ticker.ticker}
                            className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-100'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-white">{idx + 1}</span>
                              </div>
                              <div>
                                <span className={`font-bold text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>${ticker.ticker}</span>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{ticker.company_name}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeTickerFromCollectionSelection(ticker.ticker)}
                              className={`p-1.5 rounded-full hover:bg-red-500/20 transition-colors ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} hover:text-red-500`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4">
                      <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        Search by $ticker, @handle, company name, sector, or any keyword
                      </p>
                      <div className="space-y-2">
                        <p className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>Try searching for:</p>
                        {['$NVDA', '$AAPL', '@author', 'semiconductor'].map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() => setCreateCollectionTickerSearch(suggestion)}
                            className={`block w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                              isDarkMode ? 'bg-[#1a2235] text-slate-300 hover:bg-[#141c2e]' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className={`px-4 py-3 border-t flex items-center justify-between ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {createCollectionSelectedTickers.length > 0 && (
                      <span>{createCollectionSelectedTickers.length} ticker{createCollectionSelectedTickers.length !== 1 ? 's' : ''} selected</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setShowCreateWatchlist(false);
                        setNewWatchlistName('');
                        setCreateCollectionSelectedTickers([]);
                        setCreateCollectionTickerSearch('');
                        setPendingWatchlistTicker(null);
                        setPendingWatchlistIndustry(null);
                      }}
                      className={`px-4 py-2 transition-colors font-medium ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateCollectionFromModal}
                      disabled={!newWatchlistName.trim() || creatingWatchlist}
                      className={`px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        (!newWatchlistName.trim() || creatingWatchlist) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {creatingWatchlist ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                          </svg>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                          </svg>
                          Create Ticker List
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Existing Collections List */}
                <div className="max-h-80 overflow-y-auto">
                  {pendingWatchlistTicker && (
                    <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700 bg-[#141c2e]/50' : 'border-slate-200 bg-slate-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Adding <span className={`font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>${pendingWatchlistTicker}</span> to ticker list
                      </p>
                    </div>
                  )}
                  
                  {/* Filter out sector-based watchlists using the same list as Collections tab */}
                  {(() => {
                    const sectorNames = ['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'];
                    const customCollections = watchlists.filter(w => 
                      !w.source_info && 
                      !sectorNames.includes(w.name)
                    );
                    
                    return customCollections.length === 0 ? (
                    <div className="p-8 text-center">
                      <svg className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                      </svg>
                      <p className={`mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No custom ticker lists yet</p>
                      <button
                        onClick={() => setCollectionModalMode('create')}
                        className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                      >
                        Create your first ticker list →
                      </button>
                    </div>
                  ) : (
                    <div className="p-4 space-y-2">
                      <p className={`text-xs font-medium uppercase tracking-wide mb-3 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                        Custom Ticker Lists
                      </p>
                      {customCollections.map(watchlist => (
                          <button
                            key={watchlist.id}
                            onClick={() => {
                              if (pendingWatchlistTicker) {
                                manualAddToWatchlist(pendingWatchlistTicker, watchlist.id);
                                setShowCreateWatchlist(false);
                                setCollectionModalMode('create');
                              }
                            }}
                            className={`w-full p-3 rounded-lg text-left transition-all flex items-center justify-between border ${
                              isDarkMode 
                                ? 'hover:bg-slate-800 border-slate-700' 
                                : 'hover:bg-slate-100 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#141c2e]' : 'bg-slate-100'}`}>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                                </svg>
                              </div>
                              <div>
                                <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                  {watchlist.name}
                                </h4>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {watchlist.tickers?.length || 0} tickers
                                </p>
                              </div>
                            </div>
                            <svg className={`w-5 h-5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                            </svg>
                          </button>
                        ))}
                    </div>
                  );
                  })()}
                </div>
                
                {/* Footer for existing mode */}
                <div className={`px-4 py-3 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                  <button
                    onClick={() => {
                      setShowCreateWatchlist(false);
                      setPendingWatchlistTicker(null);
                      setPendingWatchlistIndustry(null);
                      setCollectionModalMode('create');
                    }}
                    className={`w-full px-4 py-2 transition-colors font-medium ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedTickerForScheduling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="p-6">
              {/* Header - Dynamic based on share type */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {selectedTickerForScheduling.shareType === 'poll' ? 'Share Poll' : 
                   selectedTickerForScheduling.shareType === 'watchlist' ? 'Share Watchlist' : 
                   selectedTickerForScheduling.shareType === 'file' ? 'Share File' :
                   'Share Investment Idea'}
                </h3>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareToGroup(false);
                    setSelectedGroupId('');
                  }}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'} transition-colors`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              {/* Item Preview - Clean design */}
              <div className={`${isDarkMode ? 'bg-[#141c2e] border-slate-600' : 'bg-slate-50 border-slate-200'} border rounded-lg p-4 mb-6`}>
                <div className="flex items-center justify-between">
                  <div className={`font-semibold text-base ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    {selectedTickerForScheduling.shareType === 'poll' || selectedTickerForScheduling.shareType === 'watchlist' 
                      ? selectedTickerForScheduling.ticker 
                      : selectedTickerForScheduling.shareType === 'file'
                      ? `${selectedTickerForScheduling.ticker} SEC Filings`
                      : `$${selectedTickerForScheduling.ticker.toUpperCase()}`}
                  </div>
                  {selectedTickerForScheduling.tickerCount > 0 && (
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {selectedTickerForScheduling.tickerCount} ticker{selectedTickerForScheduling.tickerCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Share Type Toggle */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Share with:
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShareToGroup(false);
                      setSelectedGroupId('');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                      !shareToGroup
                        ? (isDarkMode ? 'border-indigo-400 bg-indigo-900/30 text-indigo-300' : 'border-indigo-500 bg-indigo-50 text-indigo-700')
                        : (isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-300')
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                      Individual
                    </div>
                  </button>
                  <button
                    onClick={() => setShareToGroup(true)}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                      shareToGroup
                        ? (isDarkMode ? 'border-green-400 bg-green-900/30 text-green-300' : 'border-green-500 bg-green-50 text-green-700')
                        : (isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-300')
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                      </svg>
                      Group
                    </div>
                  </button>
                </div>
              </div>

              {/* Conditional Fields */}
              <div className="space-y-4">
                {!shareToGroup ? (
                  <>
                    {/* Individual Email Input */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Colleague's Email Address
                      </label>
                      <input
                        type="email"
                        value={shareData.friendEmail || ''}
                        onChange={(e) => {
                          const email = e.target.value;
                          setShareData({...shareData, friendEmail: email});
                          setShareRecipientStatus(null);
                          
                          // Debounced check - wait 500ms after typing stops
                          if (window.emailCheckTimeout) clearTimeout(window.emailCheckTimeout);
                          
                          if (email.trim() && email.includes('@') && email.includes('.')) {
                            setCheckingRecipient(true);
                            window.emailCheckTimeout = setTimeout(async () => {
                              try {
                                const response = await fetch(`${API_BASE_URL}/api/users/check-email/${encodeURIComponent(email.trim())}`);
                                if (response.ok) {
                                  const data = await response.json();
                                  setShareRecipientStatus(data);
                                }
                              } catch (error) {
                                console.error('Error checking recipient:', error);
                              }
                              setCheckingRecipient(false);
                            }, 500);
                          } else {
                            setCheckingRecipient(false);
                          }
                        }}
                        placeholder="colleague@company.com"
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-slate-300'}`}
                      />
                      {/* Recipient status feedback */}
                      {checkingRecipient && (
                        <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Checking...
                        </p>
                      )}
                      {!checkingRecipient && shareRecipientStatus && (
                        <div className={`flex items-center gap-2 mt-2 text-xs ${shareRecipientStatus.exists ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-blue-400' : 'text-blue-600')}`}>
                          {shareRecipientStatus.exists ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )}
                          <span>{shareRecipientStatus.message}</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Group Selection */}
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Select Group
                      </label>
                      {userGroups.length > 0 ? (
                        <select
                          value={selectedGroupId}
                          onChange={(e) => setSelectedGroupId(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                        >
                          <option value="">Choose a group...</option>
                          {userGroups.map((group) => (
                            <option key={group.group_id} value={group.group_id}>
                              {group.name} ({group.member_count} members)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={`text-sm p-3 rounded-lg border ${isDarkMode ? 'text-slate-400 bg-slate-700 border-slate-600' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                          No groups available. Create a group in Settings first.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className={`flex justify-end space-x-3 pt-6 border-t mt-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareToGroup(false);
                    setSelectedGroupId('');
                  }}
                  className={`px-4 py-2 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareInvestmentIdea}
                  disabled={shareToGroup ? !selectedGroupId : !shareData.friendEmail}
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                  </svg>
                  {shareToGroup ? 'Share to Group' : 'Share'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      <CalendarModal />

      {/* Invite to Group Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Invite to Group</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteGroupId('');
                  }}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'} transition-colors`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              {/* Group Info */}
              <div className={`${isDarkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-50 border-green-200'} border rounded-lg p-4 mb-6`}>
                <div className="flex items-center gap-3">
                  <svg className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  <div>
                    <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {userGroups.find(g => g.group_id === inviteGroupId)?.name}
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      {userGroups.find(g => g.group_id === inviteGroupId)?.member_count} members
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Input */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-slate-300'}`}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      sendGroupInvitation();
                    }
                  }}
                />
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  An invitation email will be sent to this address
                </p>
              </div>

              {/* Action Buttons */}
              <div className={`flex justify-end space-x-3 pt-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteGroupId('');
                  }}
                  className={`px-4 py-2 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={sendGroupInvitation}
                  disabled={!inviteEmail.trim()}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Create New Group</h3>
                <button
                  onClick={() => {
                    setShowCreateGroupModal(false);
                    setNewGroupName('');
                    setNewGroupDescription('');
                    setNewGroupMembers([]);
                    setNewMemberEmail('');
                  }}
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'} transition-colors`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              {/* Info Box */}
              <div className={`${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4 mb-6`}>
                <div className="flex gap-3">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <div className="text-sm">
                    <p className={`font-medium mb-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>About Sharing Groups</p>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Create a group to share investment ideas with specific collaborators. Members will receive email notifications when you share ideas with the group.</p>
                  </div>
                </div>
              </div>

              {/* Group Name Input */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., Tech Investors, Family Portfolio..."
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-slate-300'}`}
                  autoFocus
                />
              </div>

              {/* Description Input */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>(Optional)</span>
                </label>
                <textarea
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="Describe the purpose of this group..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-slate-300'}`}
                />
              </div>

              {/* Add Members Section */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Add Members <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>(Optional)</span>
                </label>
                
                {/* Member Input */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-slate-300'}`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newMemberEmail.trim() && !newGroupMembers.includes(newMemberEmail.trim())) {
                          setNewGroupMembers([...newGroupMembers, newMemberEmail.trim()]);
                          setNewMemberEmail('');
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newMemberEmail.trim() && !newGroupMembers.includes(newMemberEmail.trim())) {
                        setNewGroupMembers([...newGroupMembers, newMemberEmail.trim()]);
                        setNewMemberEmail('');
                      }
                    }}
                    disabled={!newMemberEmail.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Add
                  </button>
                </div>

                {/* Members List */}
                {newGroupMembers.length > 0 && (
                  <div className="space-y-2">
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Members to invite ({newGroupMembers.length}):</p>


      {/* One-Page Summary Modal */}
      {showOnePageSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">One-Page Investor Summary</h3>
                  <p className="text-blue-100 mt-1">{selectedTickerForScheduling?.ticker?.toUpperCase()}</p>
                </div>
                <button
                  onClick={() => {
                    setShowOnePageSummary(false);
                    setOnePageSummaryData(null);
                  }}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {summaryLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                  <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Generating Summary...</p>
                  <p className={`text-sm text-center max-w-md ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Analyzing latest financials, earnings, news, and peer comparisons...
                  </p>
                </div>
              ) : onePageSummaryData ? (
                onePageSummaryData.success === false ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className={`${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'} border rounded-lg p-6 max-w-2xl`}>
                      <div className="flex items-start gap-3">
                        <svg className={`w-6 h-6 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <div>
                          <h4 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>Unable to Generate Summary</h4>
                          <p className={`text-sm mb-4 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                            {onePageSummaryData.error || 'An error occurred while generating the summary'}
                          </p>
                          <div className="bg-white border border-red-200 rounded p-3 text-xs text-slate-600">
                            <p className="font-medium mb-1">Possible reasons:</p>
                            <ul className="list-disc ml-4 space-y-1">
                              <li>Perplexity API key may be invalid or expired</li>
                              <li>Rate limit exceeded</li>
                              <li>Temporary API outage</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-slate max-w-none">
                    {onePageSummaryData.cached && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                        </svg>
                        <span className="text-sm text-green-800">
                          Cached summary (generated {onePageSummaryData.cache_age_hours ? `${Math.round(onePageSummaryData.cache_age_hours)} hours ago` : 'recently'})
                        </span>
                      </div>
                    )}
                    
                    {/* Add CSS styles for markdown content */}
                    <style>{`
                      .markdown-content {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                        line-height: 1.6;
                        color: #334155;
                      }
                      
                      .markdown-content h1 {
                        font-size: 2rem;
                        font-weight: 700;
                        margin-top: 2rem;
                        margin-bottom: 1rem;
                        color: #0f172a;
                        border-bottom: 2px solid #e2e8f0;
                        padding-bottom: 0.5rem;
                      }
                      
                      .markdown-content h2 {
                        font-size: 1.5rem;
                        font-weight: 700;
                        margin-top: 1.5rem;
                        margin-bottom: 0.75rem;
                        color: #1e293b;
                      }
                      
                      .markdown-content h3 {
                        font-size: 1.25rem;
                        font-weight: 600;
                        margin-top: 1.25rem;
                        margin-bottom: 0.5rem;
                        color: #334155;
                      }
                      
                      .markdown-content h4 {
                        font-size: 1.1rem;
                        font-weight: 600;
                        margin-top: 1rem;
                        margin-bottom: 0.5rem;
                        color: #475569;
                      }
                      
                      .markdown-content p {
                        margin-bottom: 1rem;
                      }
                      
                      .markdown-content ul, .markdown-content ol {
                        margin-bottom: 1rem;
                        padding-left: 1.5rem;
                      }
                      
                      .markdown-content li {
                        margin-bottom: 0.5rem;
                      }
                      
                      .markdown-content table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 1.5rem 0;
                        background: white;
                        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
                        border-radius: 0.5rem;
                        overflow: hidden;
                      }
                      
                      .markdown-content thead {
                        background: linear-gradient(to right, #3b82f6, #2563eb);
                      }
                      
                      .markdown-content th {
                        padding: 0.75rem 1rem;
                        text-align: left;
                        font-weight: 600;
                        font-size: 0.875rem;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        color: white;
                        border-bottom: 2px solid #1d4ed8;
                      }
                      
                      .markdown-content td {
                        padding: 0.75rem 1rem;
                        border-bottom: 1px solid #e2e8f0;
                        font-size: 0.95rem;
                      }
                      
                      .markdown-content tbody tr:hover {
                        background-color: #f8fafc;
                      }
                      
                      .markdown-content tbody tr:last-child td {
                        border-bottom: none;
                      }
                      
                      .markdown-content strong {
                        font-weight: 600;
                        color: #1e293b;
                      }
                      
                      .markdown-content em {
                        font-style: italic;
                        color: #64748b;
                      }
                      
                      .markdown-content code {
                        background: #f1f5f9;
                        padding: 0.2rem 0.4rem;
                        border-radius: 0.25rem;
                        font-size: 0.875rem;
                        font-family: 'Courier New', monospace;
                        color: #dc2626;
                      }
                      
                      .markdown-content blockquote {
                        border-left: 4px solid #3b82f6;
                        padding-left: 1rem;
                        margin: 1rem 0;
                        color: #64748b;
                        font-style: italic;
                      }
                      
                      .markdown-content hr {
                        border: none;
                        border-top: 2px solid #e2e8f0;
                        margin: 2rem 0;
                      }
                    `}</style>
                    
                    {/* Render markdown content with proper parsing */}
                    <div 
                      className="markdown-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize((() => {
                          try {
                            // Configure marked for better table and content rendering
                            marked.setOptions({
                              breaks: true,
                              gfm: true, // GitHub Flavored Markdown
                            });
                            
                            // Parse markdown to HTML
                            return marked.parse(onePageSummaryData.summary);
                          } catch (e) {
                            console.error('Markdown parsing error:', e);
                            // Fallback to plain text if parsing fails
                            return onePageSummaryData.summary.replace(/\n/g, '<br/>');
                          }
                        })())
                      }}
                    />
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-600">No summary data available</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t flex justify-between items-center">
              <p className="text-xs text-slate-500">
                Powered by Perplexity AI • Sources cited in summary
              </p>
              <button
                onClick={() => {
                  setShowOnePageSummary(false);
                  setOnePageSummaryData(null);
                }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

                    {newGroupMembers.map((email, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                          <span className="text-sm text-slate-700">{email}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setNewGroupMembers(newGroupMembers.filter((_, i) => i !== index));
                          }}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-slate-500 mt-2">
                  Invitations will be sent after the group is created
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  onClick={() => {
                    setShowCreateGroupModal(false);
                    setNewGroupName('');
                    setNewGroupDescription('');
                    setNewGroupMembers([]);
                    setNewMemberEmail('');
                  }}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createGroup}
                  disabled={!newGroupName.trim() || groupsLoading}
                  className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                  </svg>
                  {groupsLoading ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Tweet Generator Modal - Modern Design with Dark Mode - 3 Modes */}
      {showTweetGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
            <div className="p-6">
              {/* Header - Dynamic based on mode */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                  {newPollMode === 'custom_list' && 'Create Custom List'}
                  {newPollMode === 'requests' && 'Request Ideas'}
                  {newPollMode === 'x_poll' && 'Create X Poll'}
                </h3>
                <Button
                  onClick={() => setShowTweetGenerator(false)}
                  variant="ghost"
                  className={`${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  ✕
                </Button>
              </div>

              {/* Custom List Mode - Create a local list of tickers */}
              {newPollMode === 'custom_list' && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>List Name</label>
                    <input 
                      type="text"
                      value={pollQuestion}
                      onChange={(e) => setPollQuestion(e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      placeholder="e.g., Q1 2025 Small Cap Picks"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      Add Tickers
                      {ideasText && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-normal ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {ideasText.split(/[,\s]+/).filter(t => t.trim()).length} added
                        </span>
                      )}
                    </label>
                    <div className={`border rounded-lg ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-300 bg-white'}`}>
                      {/* Ticker chips display */}
                      {ideasText && ideasText.split(/[,\s]+/).filter(t => t.trim()).length > 0 && (
                        <div className={`flex flex-wrap gap-2 p-3 border-b border-dashed ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                          {ideasText.split(/[,\s]+/).filter(t => t.trim()).map((ticker, idx) => {
                            const cleanTicker = ticker.toUpperCase().replace('$', '');
                            return (
                              <span 
                                key={idx}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${
                                  isDarkMode 
                                    ? 'bg-blue-900/50 text-blue-300 border border-blue-700' 
                                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                                }`}
                              >
                                ${cleanTicker}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const tickers = ideasText.split(/[,\s]+/).filter(t => t.trim());
                                    tickers.splice(idx, 1);
                                    setIdeasText(tickers.join(', '));
                                  }}
                                  className={`ml-0.5 rounded-full p-0.5 hover:bg-red-500 hover:text-white transition-colors ${
                                    isDarkMode ? 'text-blue-400 hover:text-white' : 'text-blue-500 hover:text-white'
                                  }`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Input for adding tickers */}
                      <input 
                        type="text"
                        className={`w-full p-3 bg-transparent focus:outline-none ${
                          isDarkMode ? 'text-slate-200 placeholder-slate-400' : 'text-slate-900 placeholder-slate-500'
                        }`}
                        placeholder={ideasText ? "Add more tickers (press Enter or comma)" : "Type ticker and press Enter (e.g., AAPL)"}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            const value = e.target.value.trim().toUpperCase().replace('$', '').replace(',', '');
                            if (value) {
                              const currentTickers = ideasText ? ideasText.split(/[,\s]+/).filter(t => t.trim()) : [];
                              if (!currentTickers.includes(value)) {
                                setIdeasText([...currentTickers, value].join(', '));
                              }
                              e.target.value = '';
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value.trim().toUpperCase().replace('$', '');
                          if (value) {
                            const currentTickers = ideasText ? ideasText.split(/[,\s]+/).filter(t => t.trim()) : [];
                            if (!currentTickers.includes(value)) {
                              setIdeasText([...currentTickers, value].join(', '));
                            }
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                    <p className={`mt-1.5 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Press Enter after each ticker, or paste multiple separated by commas
                    </p>
                  </div>
                  {/* Quick add common tickers */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Quick Add</label>
                    <div className="flex flex-wrap gap-1.5">
                      {['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA'].map(ticker => {
                        const isAdded = ideasText && ideasText.toUpperCase().split(/[,\s]+/).includes(ticker);
                        return (
                          <button
                            key={ticker}
                            type="button"
                            onClick={() => {
                              if (isAdded) {
                                const tickers = ideasText.split(/[,\s]+/).filter(t => t.trim().toUpperCase() !== ticker);
                                setIdeasText(tickers.join(', '));
                              } else {
                                const currentTickers = ideasText ? ideasText.split(/[,\s]+/).filter(t => t.trim()) : [];
                                setIdeasText([...currentTickers, ticker].join(', '));
                              }
                            }}
                            className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                              isAdded
                                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                                : (isDarkMode ? 'bg-slate-600 text-slate-300 hover:bg-slate-500' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                            }`}
                          >
                            {isAdded ? '✓ ' : ''}${ticker}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Requests Mode (Ask for Ideas) */}
              {newPollMode === 'requests' && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Ask Your Network</label>
                    <textarea 
                      value={ideasText}
                      onChange={(e) => setIdeasText(e.target.value)}
                      className={`w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      rows="3" 
                      placeholder="Looking for undervalued small-cap ideas in any sector. Drop your best picks with thesis below!"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Quick Templates</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={() => setIdeasText("What's your top dividend pick? Drop your DD below!")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        Dividend Ideas
                      </Button>
                      <Button 
                        onClick={() => setIdeasText("Looking for beaten-down stocks with solid fundamentals.")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        Value Picks
                      </Button>
                      <Button 
                        onClick={() => setIdeasText("Small-cap gems trading below book value?")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        Small-Cap
                      </Button>
                      <Button 
                        onClick={() => setIdeasText("REITs with sustainable 7%+ yields in this rate environment?")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        REITs
                      </Button>
                      <Button 
                        onClick={() => setIdeasText("International stocks trading at a discount to US peers?")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        International
                      </Button>
                      <Button 
                        onClick={() => setIdeasText("Growth stocks with strong moats trading at reasonable valuations?")}
                        variant="ghost"
                        className={`text-xs p-2 rounded text-left h-auto ${
                          isDarkMode 
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                      >
                        Growth + Moat
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* X Poll Mode */}
              {newPollMode === 'x_poll' && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Your Question</label>
                    <textarea 
                      value={pollQuestion}
                      onChange={(e) => setPollQuestion(e.target.value)}
                      className={`w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      rows="3" 
                      placeholder="Best cloud infrastructure play for 2025?&#10;&#10;@savetotickler"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Poll Options</label>
                    <input 
                      value={pollOptions[0]}
                      onChange={(e) => setPollOptions([e.target.value, pollOptions[1], pollOptions[2], pollOptions[3]])}
                      type="text" 
                      className={`w-full p-2 border rounded mb-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      placeholder="$AMZN - AWS"
                    />
                    <input 
                      value={pollOptions[1]}
                      onChange={(e) => setPollOptions([pollOptions[0], e.target.value, pollOptions[2], pollOptions[3]])}
                      type="text" 
                      className={`w-full p-2 border rounded mb-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      placeholder="$MSFT - Azure"
                    />
                    <input 
                      value={pollOptions[2]}
                      onChange={(e) => setPollOptions([pollOptions[0], pollOptions[1], e.target.value, pollOptions[3]])}
                      type="text" 
                      className={`w-full p-2 border rounded mb-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      placeholder="$GOOGL - GCP"
                    />
                    <input 
                      value={pollOptions[3]}
                      onChange={(e) => setPollOptions([pollOptions[0], pollOptions[1], pollOptions[2], e.target.value])}
                      type="text" 
                      className={`w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400' 
                        : 'bg-white border-slate-300 text-slate-900'
                      }`}
                      placeholder="$ORCL - OCI"
                    />
                  </div>
                </div>
              )}

              {/* Generate Button - Changes based on mode */}
              <div className="mt-6 space-y-3">
                {newPollMode === 'custom_list' ? (
                  <Button 
                    onClick={async () => {
                      // Create custom list locally
                      const tickers = ideasText.split(/[,\s]+/).filter(t => t.trim()).map(t => t.toUpperCase().replace('$', ''));
                      if (!pollQuestion.trim() || tickers.length === 0) {
                        alert('Please enter a list name and at least one ticker');
                        return;
                      }
                      try {
                        const userId = user?.id || user?.user_id;
                        const response = await fetch(`${API_BASE_URL}/api/fishing-threads`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            user_id: userId,
                            title: pollQuestion,
                            source: 'custom_list',
                            tickers: tickers,
                            is_own_poll: true
                          })
                        });
                        if (response.ok) {
                          const result = await response.json();
                          setShowTweetGenerator(false);
                          setPollQuestion('');
                          setIdeasText('');
                          // Show success notification
                          showNotification(`Custom list "${result.title}" created with ${result.ticker_count} tickers!`, 'success');
                          // Refresh fishing threads
                          const threadsRes = await fetch(`${API_BASE_URL}/api/fishing-threads/${userId}`);
                          if (threadsRes.ok) {
                            const data = await threadsRes.json();
                            setFishingThreads(data.threads || []);
                          }
                          // Navigate to Tickers tab to show the new list
                          setActiveTab('watchlists');
                        } else {
                          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
                          showNotification(error.detail || 'Failed to create custom list', 'error');
                        }
                      } catch (error) {
                        console.error('Error creating custom list:', error);
                        showNotification('Error creating custom list', 'error');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg font-semibold"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    Create Custom List
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={generateTweet}
                      className={`w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-3 rounded-lg font-semibold`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                      </svg>
                      {newPollMode === 'x_poll' ? 'Open X with Poll' : 'Open X with Request'}
                    </Button>
                    
                    {/* Important reminder to bookmark */}
                    <div className={`flex items-center gap-2 p-3 rounded-lg border ${isDarkMode ? 'bg-amber-900/30 border-amber-700 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                      </svg>
                      <p className="text-sm">
                        <span className="font-semibold">After posting:</span> Remember to hit bookmark!
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Landing Page Modal */}
      {showLandingModal && (
        <div className={`fixed inset-0 z-50 overflow-auto ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}>
          <div className="min-h-screen">
            {/* Close button */}
            <div className="absolute top-4 right-4 z-50">
              <button
                onClick={() => setShowLandingModal(false)}
                className={`${isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-800 hover:bg-slate-700'} text-white p-2 rounded-full shadow-lg transition-colors`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <LandingPageComponent />
          </div>
        </div>
      )}

      {/* Original Content Modal */}
      {showOriginalContentModal && selectedOriginalContent && (
        <div 
          data-original-content-modal="true"
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden" 
          style={{ isolation: 'isolate' }}
        >
          <div 
            className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-6xl w-full h-[95vh] flex flex-col`}
            style={{ contain: 'layout style', zoom: 'normal' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-gray-50 border-gray-200'} px-3 sm:px-6 py-2 sm:py-4 border-b flex-shrink-0`}>
              <div className="flex justify-between items-center">
                <div className="min-w-0 flex-1 mr-4">
                  <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Original Content</h2>
                  {/* Only show Subject/From for non-Twitter content */}
                  {!(selectedOriginalContent.isTwitterContent && selectedOriginalContent.twitterMetadata) && (
                    <>
                      <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'} mt-1 truncate`}>
                        <strong>Subject:</strong> {selectedOriginalContent.subject}
                      </p>
                      <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'} truncate`}>
                        <strong>From:</strong> {selectedOriginalContent.from}
                      </p>
                    </>
                  )}
                  {/* Only show platform badge if NOT Twitter with metadata (Twitter profile box shows X logo already) */}
                  {!selectedOriginalContent.twitterMetadata && (
                  <div className="mt-2 flex gap-2">
                    {(() => {
                      const platformSource = selectedOriginalContent.platformSource;
                      
                      let platformIcon, platformName, platformColor;
                      
                      switch (platformSource) {
                        case 'Substack':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
                            </svg>
                          );
                          platformName = 'Substack';
                          platformColor = 'bg-orange-100 text-orange-800';
                          break;
                        case 'Twitter':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                            </svg>
                          );
                          platformName = 'X';
                          platformColor = 'bg-blue-100 text-blue-800';
                          break;
                        case 'LinkedIn':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          );
                          platformName = 'LinkedIn';
                          platformColor = 'bg-blue-100 text-blue-800';
                          break;
                        case 'Reddit':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                            </svg>
                          );
                          platformName = 'Reddit';
                          platformColor = 'bg-orange-100 text-orange-800';
                          break;
                        case 'Medium':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
                            </svg>
                          );
                          platformName = 'Medium';
                          platformColor = 'bg-green-100 text-green-800';
                          break;
                        case 'Personal':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                            </svg>
                          );
                          platformName = 'Personal';
                          platformColor = 'bg-purple-100 text-purple-800';
                          break;
                        case 'Business':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                          );
                          platformName = 'Business';
                          platformColor = 'bg-gray-100 text-gray-800';
                          break;
                        case 'Financial_Newsletter':
                          platformIcon = (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                            </svg>
                          );
                          platformName = 'Financial Newsletter';
                          platformColor = 'bg-green-100 text-green-800';
                          break;
                        default:
                          platformIcon = (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                          );
                          platformName = platformSource || 'Email';
                          platformColor = 'bg-gray-100 text-gray-800';
                      }
                      
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${platformColor}`}>
                          {platformIcon} {platformName}
                        </span>
                      );
                    })()}
                  </div>
                  )}
                </div>
                <button 
                  onClick={() => {
                    setShowOriginalContentModal(false);
                    setSelectedOriginalContent(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content area - optimized for PDF viewing */}
            <div className={`${selectedOriginalContent?.hasPdfAttachments ? 'p-1 sm:p-2' : 'p-4 sm:p-6'} flex-1 overflow-y-auto min-h-0`}>
                
                {/* X Content Compliance - Deleted/Protected Tweet Notice */}
                {selectedOriginalContent.isTwitterContent && (tweetValidationStatus.is_deleted || selectedOriginalContent.contentDeleted) && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      <div>
                        <h4 className="font-semibold text-amber-800">Original Post Unavailable</h4>
                        <p className="text-amber-700 text-sm mt-1">
                          {selectedOriginalContent.deletionMessage || tweetValidationStatus.message || 'This post has been removed from X.'}
                        </p>
                        <p className="text-amber-600 text-xs mt-2">
                          Your AI-generated analysis and notes are preserved below.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* X Content Compliance - Protected Account Notice */}
                {selectedOriginalContent.isTwitterContent && tweetValidationStatus.is_protected && !tweetValidationStatus.is_deleted && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      <div>
                        <h4 className="font-semibold text-blue-800">Protected Account</h4>
                        <p className="text-blue-700 text-sm mt-1">
                          This account is now protected. You may not see the latest content.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* X Content Compliance - Validation Loading Indicator */}
                {selectedOriginalContent.isTwitterContent && tweetValidationStatus.validating && (
                  <div className="mb-4 p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-gray-500">Verifying post availability...</span>
                  </div>
                )}
                
                {/* X/Twitter Display Requirements - Show tweet header if Twitter content */}
                {selectedOriginalContent.isTwitterContent && selectedOriginalContent.twitterMetadata && (() => {
                  // Build X profile URL for linking - Display Requirements compliance
                  const modalUsername = selectedOriginalContent.twitterMetadata.username;
                  const modalXProfileUrl = modalUsername ? `https://x.com/${modalUsername}` : null;
                  const modalTweetUrl = selectedOriginalContent.twitterMetadata.tweetUrl;
                  
                  return (
                  <div className={`mb-6 p-4 border rounded-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                    {/* Clickable white space area linking to permalink */}
                    <a 
                      href={modalTweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        {/* Profile Image - MUST link to X profile */}
                        {selectedOriginalContent.twitterMetadata.profileImageUrl && (
                          <span 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(modalXProfileUrl, '_blank');
                            }}
                            className="flex-shrink-0"
                          >
                            <img 
                              src={selectedOriginalContent.twitterMetadata.profileImageUrl}
                              alt={`${selectedOriginalContent.twitterMetadata.authorName} profile`}
                              className="w-12 h-12 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                            />
                          </span>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            {/* Name and handle stacked vertically */}
                            <div className="min-w-0">
                              {/* Author Name - MUST link to X profile */}
                              <span 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(modalXProfileUrl, '_blank');
                                }}
                                className={`font-bold block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                              >
                                {selectedOriginalContent.twitterMetadata.authorName}
                              </span>
                              
                              {/* @username below name - MUST link to X profile */}
                              <span 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(modalXProfileUrl, '_blank');
                                }}
                                className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                              >
                                @{selectedOriginalContent.twitterMetadata.username}
                              </span>
                            </div>
                            
                            {/* X Logo - upper right */}
                            <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                              <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </a>
                    
                    {/* Timestamp + View on X - below author header */}
                    <div className="flex items-center gap-2 mt-2" style={{marginLeft: '60px'}}>
                      {selectedOriginalContent.twitterMetadata.createdAt && (
                        <a
                          href={modalTweetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                        >
                          {new Date(selectedOriginalContent.twitterMetadata.createdAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </a>
                      )}
                      {/* View on X next to timestamp */}
                      {modalTweetUrl && (
                        <>
                          <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                          <a
                            href={modalTweetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-500 hover:underline"
                          >
                            View on X
                          </a>
                        </>
                      )}
                    </div>
                    
                    {/* Engagement Metrics */}
                    {selectedOriginalContent.twitterMetadata.metrics && (
                      <div className={`flex items-center gap-4 mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} style={{marginLeft: '60px'}}>
                        {selectedOriginalContent.twitterMetadata.metrics.reply_count > 0 && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                            </svg>
                            {selectedOriginalContent.twitterMetadata.metrics.reply_count.toLocaleString()}
                          </span>
                        )}
                        {selectedOriginalContent.twitterMetadata.metrics.retweet_count > 0 && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                            </svg>
                            {selectedOriginalContent.twitterMetadata.metrics.retweet_count.toLocaleString()}
                          </span>
                        )}
                        {selectedOriginalContent.twitterMetadata.metrics.like_count > 0 && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                            </svg>
                            {selectedOriginalContent.twitterMetadata.metrics.like_count.toLocaleString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })()}
                
                {/* Thread Indicator - separate block */}
                {selectedOriginalContent.isTwitterContent && selectedOriginalContent.twitterMetadata?.is_thread && selectedOriginalContent.twitterMetadata?.thread_length > 1 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                      </svg>
                      <span className="font-semibold">
                        🧵 Thread ({selectedOriginalContent.twitterMetadata.thread_length} tweets)
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Tweet Text Content - Display BEFORE media (matching X layout) */}
                {/* Always show the main content - Full Thread section will show additional tweets */}
                {selectedOriginalContent.isTwitterContent && selectedOriginalContent.content && (
                  <div 
                    className={`mt-3 whitespace-pre-wrap leading-relaxed text-base ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize((() => {
                        let text = (selectedOriginalContent.content || '')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;');
                        
                        // Build URL mapping from entities if available
                        const urlMap = {};
                        const entities = selectedOriginalContent.twitterMetadata?.entities;
                        if (entities?.urls) {
                          entities.urls.forEach(urlEntity => {
                            if (urlEntity.url && urlEntity.expanded_url) {
                              const displayUrl = urlEntity.display_url || urlEntity.expanded_url;
                              urlMap[urlEntity.url] = {
                                href: urlEntity.expanded_url,
                                display: displayUrl.length > 50 ? displayUrl.substring(0, 47) + '...' : displayUrl
                              };
                            }
                          });
                        }
                        
                        // Replace URLs - use expanded URLs if available
                        text = text.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                          let cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
                          const punctuation = url.substring(cleanUrl.length);
                          
                          if (urlMap[cleanUrl]) {
                            return `<a href="${urlMap[cleanUrl].href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${urlMap[cleanUrl].display}</a>${punctuation}`;
                          }
                          return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${cleanUrl}</a>${punctuation}`;
                        });
                        
                        return text.replace(/\n/g, '<br>');
                      })(), { ADD_ATTR: ['target'] })
                    }}
                  />
                )}
                
                {/* Media Gallery - Display images/videos AFTER text (matching X layout) */}
                {/* Always show main media - Full Thread section will show its own media per tweet */}
                {selectedOriginalContent.isTwitterContent && selectedOriginalContent.twitterMetadata?.media && selectedOriginalContent.twitterMetadata.media.length > 0 && (
                  <div className="mt-4">
                    <div className={`grid gap-2 ${
                      selectedOriginalContent.twitterMetadata.media.length === 1 ? 'grid-cols-1' :
                      selectedOriginalContent.twitterMetadata.media.length === 2 ? 'grid-cols-2' :
                      selectedOriginalContent.twitterMetadata.media.length === 3 ? 'grid-cols-2' :
                      'grid-cols-2'
                    }`}>
                      {selectedOriginalContent.twitterMetadata.media.map((mediaItem, idx) => (
                        <div 
                          key={idx} 
                          className={`relative rounded-lg overflow-hidden bg-gray-100 ${
                            selectedOriginalContent.twitterMetadata.media.length === 3 && idx === 0 ? 'row-span-2' : ''
                          }`}
                        >
                          {mediaItem.type === 'video' || mediaItem.type === 'animated_gif' ? (
                            <div className="relative">
                              <img 
                                src={mediaItem.preview_image_url || mediaItem.url}
                                alt={mediaItem.alt_text || 'Video thumbnail'}
                                className="w-full h-auto object-contain rounded-lg"
                                style={{ maxHeight: '500px' }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                <div className="w-12 h-12 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                                  <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                              </div>
                              {mediaItem.type === 'animated_gif' && (
                                <span className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">GIF</span>
                              )}
                            </div>
                          ) : (
                            <a href={mediaItem.url} target="_blank" rel="noopener noreferrer">
                              <img 
                                src={mediaItem.url}
                                alt={mediaItem.alt_text || 'Post image'}
                                className="w-full h-auto object-contain rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
                                style={{ maxHeight: selectedOriginalContent.twitterMetadata.media.length === 1 ? '600px' : '400px' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Conversation Context - Show parent tweets for replies */}
                {selectedOriginalContent.twitterMetadata?.has_context && 
                 selectedOriginalContent.twitterMetadata?.conversation_context && 
                 selectedOriginalContent.twitterMetadata.conversation_context.length > 0 && (
                  <div className={`mb-4 rounded-xl p-4 border ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                    <div className={`flex items-center gap-2 mb-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      <span className="text-lg">💬</span>
                      <span className="font-semibold">
                        Replying to ({selectedOriginalContent.twitterMetadata.conversation_context.length} {selectedOriginalContent.twitterMetadata.conversation_context.length === 1 ? 'tweet' : 'tweets'})
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {selectedOriginalContent.twitterMetadata.conversation_context.map((tweet, index) => {
                        // X profile URL for linking - Display Requirements compliance
                        const contextUsername = tweet.author?.username;
                        const contextXProfileUrl = contextUsername ? `https://x.com/${contextUsername}` : null;
                        const contextPermalink = tweet.url;
                        
                        return (
                        <div 
                          key={tweet.id || index}
                          className={`rounded-lg border p-3 shadow-sm ${isDarkMode ? 'bg-slate-800 border-blue-700' : 'bg-white border-blue-200'}`}
                        >
                          {/* Clickable white space area linking to permalink */}
                          <a 
                            href={contextPermalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block cursor-pointer"
                          >
                            <div className="flex items-start gap-3">
                              {/* Profile Image - MUST link to X profile */}
                              {tweet.author?.profile_image_url && (
                                <span 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(contextXProfileUrl, '_blank');
                                  }}
                                  className="flex-shrink-0"
                                >
                                  <img 
                                    src={tweet.author.profile_image_url}
                                    alt={`${tweet.author?.name} profile`}
                                    className="w-10 h-10 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                  />
                                </span>
                              )}
                              <div className="flex-1 min-w-0">
                                {/* Author info stacked vertically with X logo */}
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    {/* Author Name - MUST link to X profile */}
                                    <span 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.open(contextXProfileUrl, '_blank');
                                      }}
                                      className={`font-semibold block text-sm truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                    >
                                      {tweet.author?.name}
                                    </span>
                                    {/* @username below name - MUST link to X profile */}
                                    <span 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.open(contextXProfileUrl, '_blank');
                                      }}
                                      className={`block text-sm truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                    >
                                      @{tweet.author?.username}
                                    </span>
                                  </div>
                                  
                                  {/* X Logo - upper right */}
                                  <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </a>
                          
                          {/* Timestamp + View on X */}
                          <div className="flex items-center gap-2 mt-1" style={{marginLeft: '52px'}}>
                            {tweet.created_at && (
                              <a
                                href={contextPermalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-xs hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}
                              >
                                {new Date(tweet.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </a>
                            )}
                            {contextPermalink && (
                              <>
                                <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                                <a
                                  href={contextPermalink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-500 hover:underline"
                                >
                                  View on X
                                </a>
                              </>
                            )}
                          </div>
                          
                          {/* Tweet content */}
                          <div className={`text-sm whitespace-pre-wrap mt-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`} style={{marginLeft: '52px'}}>
                            {tweet.text}
                          </div>
                        </div>
                      );})}
                    </div>
                    
                    <div className={`mt-3 pt-3 border-t text-sm flex items-center gap-1 ${isDarkMode ? 'border-blue-700 text-blue-300' : 'border-blue-300 text-blue-600'}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                      </svg>
                      <span>Your bookmarked tweet replies to the above</span>
                    </div>
                  </div>
                )}
                
                {/* Full Thread Display - Show all tweets in a thread */}
                {selectedOriginalContent.twitterMetadata?.is_thread && 
                 selectedOriginalContent.twitterMetadata?.full_thread && 
                 selectedOriginalContent.twitterMetadata.full_thread.length > 1 && (
                  <div className={`mt-4 rounded-xl p-4 border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className={`flex items-center gap-2 mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                      <span className="text-lg">🧵</span>
                      <span className="font-semibold">
                        Full Thread ({selectedOriginalContent.twitterMetadata.full_thread.length} tweets)
                      </span>
                    </div>
                    
                    <div className="space-y-4">
                      {selectedOriginalContent.twitterMetadata.full_thread.map((tweet, index) => {
                        // Get author name - try multiple possible property paths
                        const tweetAuthorName = tweet.author?.name || tweet.name || tweet.author_name || 
                                               selectedOriginalContent.twitterMetadata?.authorName ||
                                               selectedOriginalContent.twitterMetadata?.name ||
                                               selectedOriginalContent.twitterMetadata?.author?.name;
                        const tweetAuthorUsername = tweet.author?.username || tweet.username || tweet.author_username ||
                                                   selectedOriginalContent.twitterMetadata?.username ||
                                                   selectedOriginalContent.twitterMetadata?.author?.username;
                        const tweetProfileImage = tweet.author?.profile_image_url || tweet.profile_image_url || tweet.profileImageUrl ||
                                                 selectedOriginalContent.twitterMetadata?.profileImageUrl ||
                                                 selectedOriginalContent.twitterMetadata?.profile_image_url ||
                                                 selectedOriginalContent.twitterMetadata?.author?.profile_image_url;
                        
                        // X profile URL for linking - Display Requirements compliance
                        const threadTweetXProfileUrl = tweetAuthorUsername ? `https://x.com/${tweetAuthorUsername}` : null;
                        const threadTweetPermalink = tweet.url;
                        
                        return (
                        <div 
                          key={tweet.id || index}
                          className={`rounded-lg border p-4 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
                        >
                          {/* Author Profile Header - X Display Requirements compliant with white space clickable */}
                          <a 
                            href={threadTweetPermalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block cursor-pointer mb-3"
                          >
                            <div className="flex items-start gap-3">
                              {/* Profile Image - MUST link to X profile */}
                              {tweetProfileImage ? (
                                <span 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(threadTweetXProfileUrl, '_blank');
                                  }}
                                  className="flex-shrink-0"
                                >
                                  <img 
                                    src={tweetProfileImage} 
                                    alt={tweetAuthorName || tweetAuthorUsername}
                                    className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </span>
                              ) : (
                                <span 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(threadTweetXProfileUrl, '_blank');
                                  }}
                                  className="flex-shrink-0"
                                >
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                      {(tweetAuthorUsername || 'U')[0].toUpperCase()}
                                    </span>
                                  </div>
                                </span>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                {/* Author info with X Logo - stacked vertically */}
                                <div className="flex items-start justify-between gap-2">
                                  {/* Name and handle stacked vertically */}
                                  <div className="min-w-0 flex-1">
                                    {/* Display Name - always show, fall back to username if needed */}
                                    <div className="flex items-center gap-2">
                                      <span 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          window.open(threadTweetXProfileUrl, '_blank');
                                        }}
                                        className={`font-bold truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                      >
                                        {tweetAuthorName || tweetAuthorUsername || 'Unknown'}
                                      </span>
                                      <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0">
                                        {index + 1}/{selectedOriginalContent.twitterMetadata.full_thread.length}
                                      </span>
                                    </div>
                                    {/* @username always shown below name */}
                                    {tweetAuthorUsername && (
                                      <span 
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          window.open(threadTweetXProfileUrl, '_blank');
                                        }}
                                        className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                      >
                                        @{tweetAuthorUsername}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* X Logo - upper right per X Display Requirements */}
                                  <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </a>
                          
                          {/* Timestamp + View on X - below author header */}
                          <div className="flex items-center gap-2 mb-3" style={{marginLeft: '52px'}}>
                            {tweet.created_at && (
                              <a
                                href={threadTweetPermalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                              >
                                {new Date(tweet.created_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </a>
                            )}
                            {/* View on X next to timestamp */}
                            {threadTweetPermalink && (
                              <>
                                <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
                                <a 
                                  href={threadTweetPermalink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-500 hover:underline"
                                >
                                  View on X
                                </a>
                              </>
                            )}
                          </div>
                          
                          {/* Tweet content - text aligned with author info */}
                          <div 
                            className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}
                            style={{marginLeft: '52px'}}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize((() => {
                                let text = (tweet.text || '')
                                  .replace(/</g, '&lt;')
                                  .replace(/>/g, '&gt;');
                                
                                // Build URL mapping from entities if available
                                const urlMap = {};
                                const mediaUrls = new Set(); // Track media URLs to hide them
                                
                                if (tweet.entities?.urls) {
                                  tweet.entities.urls.forEach(urlEntity => {
                                    if (urlEntity.url && urlEntity.expanded_url) {
                                      // Check if this is a media URL (pic.x.com, pic.twitter.com, photo links)
                                      const isMediaUrl = urlEntity.display_url?.startsWith('pic.') || 
                                                        urlEntity.expanded_url?.includes('/photo/') ||
                                                        urlEntity.expanded_url?.includes('/video/');
                                      
                                      if (isMediaUrl) {
                                        // Mark for removal - media is displayed separately
                                        mediaUrls.add(urlEntity.url);
                                      } else {
                                        // Use display_url for display, expanded_url for href
                                        const displayUrl = urlEntity.display_url || urlEntity.expanded_url;
                                        urlMap[urlEntity.url] = {
                                          href: urlEntity.expanded_url,
                                          display: displayUrl.length > 50 ? displayUrl.substring(0, 47) + '...' : displayUrl
                                        };
                                      }
                                    }
                                  });
                                }
                                
                                // Replace URLs - use expanded URLs if available, hide media URLs
                                text = text.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
                                  let cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
                                  const punctuation = url.substring(cleanUrl.length);
                                  
                                  // Hide media URLs completely (they're displayed as images below)
                                  if (mediaUrls.has(cleanUrl)) {
                                    return punctuation; // Just return any trailing punctuation
                                  }
                                  
                                  if (urlMap[cleanUrl]) {
                                    return `<a href="${urlMap[cleanUrl].href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${urlMap[cleanUrl].display}</a>${punctuation}`;
                                  }
                                  return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${cleanUrl}</a>${punctuation}`;
                                });
                                
                                return text.replace(/\n/g, '<br>');
                              })(), { ADD_ATTR: ['target'] })
                            }}
                          />
                          
                          {/* Thread tweet media - aligned with content */}
                          {tweet.media && tweet.media.length > 0 && (
                            <div className="mt-3 grid gap-2 grid-cols-1 sm:grid-cols-2" style={{marginLeft: '52px'}}>
                              {tweet.media.map((mediaItem, mediaIdx) => (
                                <div key={mediaIdx} className="rounded-lg overflow-hidden">
                                  {mediaItem.type === 'photo' && mediaItem.url && (
                                    <a href={mediaItem.url} target="_blank" rel="noopener noreferrer">
                                      <img 
                                        src={mediaItem.url}
                                        alt={mediaItem.alt_text || 'Tweet image'}
                                        className="w-full h-auto object-contain rounded-lg hover:opacity-90"
                                        style={{ maxHeight: '400px' }}
                                      />
                                    </a>
                                  )}
                                  {(mediaItem.type === 'video' || mediaItem.type === 'animated_gif') && mediaItem.preview_image_url && (
                                    <div className="relative">
                                      <img 
                                        src={mediaItem.preview_image_url}
                                        alt="Video thumbnail"
                                        className="w-full h-auto object-contain rounded-lg"
                                        style={{ maxHeight: '400px' }}
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                        <div className="w-10 h-10 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                                          <svg className="w-5 h-5 text-gray-800 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8 5v14l11-7z"/>
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Tweet metrics - aligned with content */}
                          {tweet.metrics && (
                            <div className={`flex items-center gap-4 mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} style={{marginLeft: '52px'}}>
                              {tweet.metrics.reply_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                  </svg>
                                  {tweet.metrics.reply_count.toLocaleString()}
                                </span>
                              )}
                              {tweet.metrics.retweet_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                  </svg>
                                  {tweet.metrics.retweet_count.toLocaleString()}
                                </span>
                              )}
                              {tweet.metrics.like_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                  </svg>
                                  {tweet.metrics.like_count.toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Display the original content properly formatted - ONLY if no PDF attachments AND NOT Twitter content (Twitter displays in header) */}
                {console.log('ORIGINAL MODAL DEBUG:', {
                  hasPdfAttachments: selectedOriginalContent.hasPdfAttachments,
                  hasHtmlContent: !!selectedOriginalContent.htmlContent,
                  htmlContentLength: selectedOriginalContent.htmlContent?.length,
                  isTwitterContent: selectedOriginalContent.isTwitterContent,
                  willUseIframe: !selectedOriginalContent.hasPdfAttachments && selectedOriginalContent.htmlContent && selectedOriginalContent.htmlContent.length > 200
                })}
                {!selectedOriginalContent.hasPdfAttachments && !selectedOriginalContent.isTwitterContent && (selectedOriginalContent.htmlContent && selectedOriginalContent.htmlContent.length > 0 ? (
                  <div className="h-full">
                    <iframe
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: 'white'
                      }}
                      srcDoc={(() => {
                        let content = selectedOriginalContent.htmlContent;
                        
                        // Simple cleaning - just remove scripts
                        content = content.replace(/<script[^>]*>.*?<\/script>/gis, '');
                        content = content.replace(/href="javascript:[^"]*"/g, '');
                        content = content.replace(/on\w+="[^"]*"/g, '');
                        
                        // Remove standalone SVG icons (usually arrows, buttons, UI elements)
                        // Keep SVGs that are inside figures or have meaningful content
                        content = content.replace(/<svg[^>]*>(?:(?!<\/svg>).)*<\/svg>/gis, function(match) {
                          // Keep SVGs if they're data visualizations (have many paths/large size)
                          if (match.length > 1000 || match.includes('viewBox="0 0 100')) {
                            return match;
                          }
                          // Remove small UI icons
                          return '';
                        });
                        
                        return `
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <base target="_blank">
                            <style>
                              /* Base styling */
                              body {
                                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                                font-size: 16px;
                                line-height: 1.6;
                                color: #333;
                                background: white;
                                margin: 0;
                                padding: 20px;
                                max-width: 100%;
                                margin: 0 auto;
                              }
                              
                              h1, h2, h3 { 
                                margin-top: 1.5em; 
                                margin-bottom: 0.5em;
                                line-height: 1.3;
                              }
                              p { margin: 1em 0; }
                              img { 
                                max-width: 100% !important;
                                height: auto !important;
                              }
                              a { color: #0066cc; }
                              
                              /* Hide or constrain leftover UI elements */
                              svg {
                                max-width: 24px !important;
                                max-height: 24px !important;
                              }
                              
                              /* Hide iframes (charts/embeds that didn't convert) */
                              iframe {
                                display: none;
                              }
                              
                              /* Show a placeholder for removed interactive content */
                              .interactive-removed::before {
                                content: "[Interactive content not available]";
                                display: block;
                                padding: 12px;
                                background: #f3f4f6;
                                border-left: 3px solid #9ca3af;
                                color: #6b7280;
                                font-style: italic;
                                margin: 1em 0;
                              }
                            </style>
                          </head>
                          <body>
                            ${content}
                          </body>
                          </html>
                        `;
                      })()}
                      sandbox="allow-same-origin"
                      title="Original Content"
                    />
                  </div>
                ) : (
                  <div className="bg-white rounded-lg overflow-hidden h-full">
                    {/* Email-like viewer with proper HTML rendering */}
                    <div 
                      className="email-content h-full"
                      style={{
                        overflowY: 'auto',
                        padding: '20px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        color: '#333',
                        backgroundColor: 'white'
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(formatOriginalContent(selectedOriginalContent.content, selectedOriginalContent.htmlContent, selectedOriginalContent.twitterMetadata), { ADD_ATTR: ['target'] })
                      }}
                    />
                  </div>
                ))}
                
                {/* FALLBACK: Display plain text for Twitter content without proper metadata (nested cards) */}
                {selectedOriginalContent.isTwitterContent && !selectedOriginalContent.twitterMetadata && selectedOriginalContent.content && (
                  <div className="bg-white rounded-lg p-5 h-full overflow-y-auto">
                    <div 
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#0f1419'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedOriginalContent.content.replace(/\n/g, '<br>'))
                      }}
                    />
                  </div>
                )}
                
                {/* PDF Viewer - Maximized space */}
                {selectedOriginalContent.hasPdfAttachments && selectedOriginalContent.pdfData && (
                  <div className="w-full h-full">
                    {/* Compact PDF info header */}
                    <div className="flex items-center justify-between mb-1 sm:mb-2 pb-1 border-b">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-gray-700 truncate">
                          📄 {selectedOriginalContent.pdfData.filename}
                        </h4>
                      </div>
                      <div className="flex gap-1 sm:gap-2 text-xs flex-shrink-0">
                        <a 
                          href={`${API_BASE_URL}/api/pdf-file/${selectedOriginalContent.id || (selectedOriginalContent.title ? selectedOriginalContent.title.replace(/\s+/g, '-').toLowerCase() : 'document')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline px-1"
                        >
                          Open
                        </a>
                        <span className="text-gray-300 hidden sm:inline">•</span>
                        <a 
                          href={`${API_BASE_URL}/api/pdf-file/${selectedOriginalContent.id || (selectedOriginalContent.title ? selectedOriginalContent.title.replace(/\s+/g, '-').toLowerCase() : 'document')}`}
                          download={selectedOriginalContent.pdfData.filename}
                          className="text-blue-600 hover:text-blue-800 underline px-1"
                        >
                          Save
                        </a>
                      </div>
                    </div>
                    
                    {/* Maximized PDF Viewer */}
                    <div className="w-full" style={{height: 'calc(95vh - 140px)'}}>
                      <iframe
                        src={`/pdf-viewer.html?url=${encodeURIComponent(`${API_BASE_URL}/api/pdf-file/${selectedOriginalContent.id || (selectedOriginalContent.title ? selectedOriginalContent.title.replace(/\s+/g, '-').toLowerCase() : 'document')}`)}`}
                        width="100%"
                        height="100%"
                        title="PDF Viewer"
                        className="border-0 rounded"
                        style={{backgroundColor: 'white'}}
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}


      {/* AI Reaction Modal */}
      {showAIReactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="min-w-0 flex-1 mr-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>💭</span>
                    AI Reaction & Opinion
                  </h2>
                  <p className="text-sm text-purple-100 mt-1">
                    Critical analysis of veracity, thesis strength, and counterpoints
                  </p>
                </div>
                <button 
                  onClick={() => {
                    setShowAIReactionModal(false);
                    setAIReaction(null);
                  }}
                  className="text-white hover:text-purple-200 transition-colors p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto p-6" style={{maxHeight: 'calc(90vh - 100px)'}}>
              {aiReactionLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mb-4"></div>
                  <p className="text-gray-600 text-lg">Generating AI reaction...</p>
                  <p className="text-gray-500 text-sm mt-2">Analyzing veracity, thesis strength, and counterpoints</p>
                </div>
              ) : aiReaction ? (
                <div className="prose prose-sm max-w-none">
                  <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-4 rounded">
                    <p className="text-xs text-purple-700 font-medium mb-1">
                      Generated by {aiReaction.model} • {new Date(aiReaction.generated_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                    {aiReaction.content}
                  </div>
                  
                  {/* Citations info for Perplexity */}
                  {aiReaction.model && aiReaction.model.includes('perplexity') && (
                    <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                      <p className="text-xs text-blue-700 font-medium">
                        📚 Citations: Numbers in brackets [1], [2], etc. refer to sources found by Perplexity's web search, including investor presentations and earnings calls.
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 italic">
                      This AI-generated analysis is for informational purposes only and should not be considered investment advice. 
                      Always conduct your own research and consult with qualified financial professionals before making investment decisions.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No reaction available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Receive Ideas Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-2xl w-full shadow-2xl max-h-[80vh] overflow-hidden`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-4h-2M5 9H3"/>
                    </svg>
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    Ideas Received ({pendingIdeas.length})
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {pendingIdeas.length > 0 && (
                    <button
                      onClick={clearAllReceivedIdeas}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setShowReceiveModal(false)}
                    className={`transition-colors p-1 ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Pending Ideas List */}
              <div className="max-h-96 overflow-y-auto space-y-4">
                {pendingIdeas.length === 0 ? (
                  <div className="text-center py-8">
                    <div className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-4h-2M5 9H3"/>
                      </svg>
                    </div>
                    <p className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No pending investment ideas</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Share ideas by sending emails to your tickler address</p>
                  </div>
                ) : (
                  pendingIdeas.map((idea) => (
                    <div key={idea.id} className={`${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-lg p-4`}>
                      {/* Type Badge */}
                      {idea.share_type && idea.share_type !== 'idea' && (
                        <div className="mb-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            idea.share_type === 'poll' 
                              ? 'bg-purple-100 text-purple-700' 
                              : idea.share_type === 'watchlist'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {idea.share_type === 'poll' && (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                              </svg>
                            )}
                            {idea.share_type === 'watchlist' && (
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                              </svg>
                            )}
                            {idea.share_type === 'poll' ? 'Poll' : idea.share_type === 'watchlist' ? 'Watchlist' : idea.share_type}
                            {idea.ticker_count > 0 && ` · ${idea.ticker_count} tickers`}
                          </span>
                        </div>
                      )}
                      {/* Idea Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`font-mono font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                              {idea.share_type === 'poll' || idea.share_type === 'watchlist' 
                                ? (idea.ticker || idea.title || 'Shared Item')
                                : `$${(idea.investment_idea?.primary_ticker || idea.ticker || 'N/A').toUpperCase()}`}
                            </div>
                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              from {idea.sender_name || idea.sender_email}
                            </span>
                          </div>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Shared on {new Date(idea.created_at || idea.shared_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Personal Message */}
                      {idea.personal_message && (
                        <div className={`${isDarkMode ? 'bg-blue-900/30 border-l-4 border-blue-500' : 'bg-blue-50 border-l-4 border-blue-200'} p-3 mb-3`}>
                          <p className={`text-sm italic ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                            "{idea.personal_message}"
                          </p>
                        </div>
                      )}

                      {/* Investment Content - Full Analysis or Pointer Preview */}
                      <div className="mb-4">
                        {idea.compliance_mode === 'pointer' || (idea.shared_content?.mode === 'pointer') ? (
                          // Pointer mode - show link to preview
                          <>
                            {idea.tweet_id && (
                              <a 
                                href={`https://x.com/i/status/${idea.tweet_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                                Preview on X
                              </a>
                            )}
                          </>
                        ) : (
                          // Full content mode - show the analysis
                          <>
                            <h4 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2V7a2 2 0 012-2h2a2 2 0 002 2v2a2 2 0 002 2h2a2 2 0 012-2V7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 00-2 2h-2a2 2 0 00-2 2v6a2 2 0 01-2 2H9z"/>
                              </svg>
                              {idea.investment_idea?.title || 'Investment Opportunity'}
                            </h4>
                            <div className={`${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white border-slate-200'} border rounded-lg p-4`}>
                              <div className={`text-sm whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {idea.investment_idea?.content || idea.content || 'No analysis available'}
                              </div>
                              {idea.investment_idea?.supporting_tickers && idea.investment_idea.supporting_tickers.length > 0 && (
                                <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Related Tickers:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {idea.investment_idea.supporting_tickers.slice(0, 8).map(ticker => (
                                      <span key={ticker} className={`px-2 py-1 text-xs rounded font-mono ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                        {ticker}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className={`flex gap-2 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                        <button
                          onClick={() => handleRespondToIdea(idea.id, 'reject')}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                        >
                          Dismiss
                        </button>
                        <button
                          onClick={() => handleRespondToIdea(idea.id, 'accept')}
                          className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          Import from
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Close Button */}
              <div className={`flex justify-end pt-6 border-t mt-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                  onClick={() => setShowReceiveModal(false)}
                  className="px-6 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shared By Me Modal */}
      {showSharedByMeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-lg max-w-2xl w-full shadow-2xl max-h-[80vh] overflow-hidden`}>
            <div className="p-6">
              {/* Header - matching Ideas Received format exactly */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                    </svg>
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                    Ideas Shared ({sharedByMeIdeas.length})
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {sharedByMeIdeas.length > 0 && (
                    <button
                      onClick={clearAllSentIdeas}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setShowSharedByMeModal(false)}
                    className={`transition-colors p-1 ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Shared Ideas List - matching received format */}
              <div className="max-h-96 overflow-y-auto space-y-4">
                {sharedByMeIdeas.length === 0 ? (
                  <div className="text-center py-8">
                    <div className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                      </svg>
                    </div>
                    <p className={`font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No shared ideas yet</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Share investment ideas with your network to see them here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sharedByMeIdeas.map((idea) => (
                      <div key={idea.id} className={`${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-lg p-4`}>
                        {/* Idea Header - matching received format exactly */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`font-mono font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                {idea.share_type === 'poll' || idea.share_type === 'watchlist' 
                                  ? (idea.ticker || idea.title || 'Shared Item')
                                  : `$${(idea.ticker || 'N/A').toUpperCase()}`}
                              </div>
                              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                to {idea.is_group_share ? (idea.group_info?.group_name || 'Group') : idea.recipient_email}
                              </span>
                            </div>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              Shared on {new Date(idea.shared_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {/* Personal Message - matching received format */}
                        {idea.personal_message && (
                          <div className={`${isDarkMode ? 'bg-blue-900/30 border-l-4 border-blue-500' : 'bg-blue-50 border-l-4 border-blue-200'} p-3 mb-3`}>
                            <p className={`text-sm italic ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                              "{idea.personal_message}"
                            </p>
                          </div>
                        )}

                        {/* Investment Content - matching received format exactly */}
                        {idea.content && (
                          <div className="mb-4">
                            <h4 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2V7a2 2 0 012-2h2a2 2 0 002 2v2a2 2 0 002 2h2a2 2 0 012-2V7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 00-2 2h-2a2 2 0 00-2 2v6a2 2 0 01-2 2H9z"/>
                              </svg>
                              Investment Opportunity
                            </h4>
                            <div className={`${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white border-slate-200'} border rounded-lg p-4`}>
                              <div className={`text-sm whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                {idea.content}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Response Status - shows recipient response */}
                        <div className="mb-4">
                          <h4 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            Response Status
                          </h4>
                          <div className={`${isDarkMode ? 'bg-[#1a2235] border-slate-700/50' : 'bg-white border-slate-200'} border rounded-lg p-4`}>
                            <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {idea.is_group_share ? (
                                <>
                                  <div className="flex items-center justify-between mb-2">
                                    <span>Shared with {idea.total_count} group members</span>
                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                      idea.status === 'all_accepted' ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700') :
                                      idea.status === 'all_rejected' ? (isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700') :
                                      idea.status === 'partial' ? (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700') :
                                      (isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                                    }`}>
                                      {idea.accepted_count}/{idea.total_count} accepted
                                    </span>
                                  </div>
                                  {idea.members && idea.members.length > 0 && (
                                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                      <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Member Responses:</p>
                                      <div className="space-y-1">
                                        {idea.members.map((member, idx) => (
                                          <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                                            <span className={`truncate ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{member.email}</span>
                                            <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0 ${
                                              member.status === 'accepted' ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700') :
                                              member.status === 'rejected' ? (isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700') :
                                              (isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                                            }`}>
                                              {member.status === 'accepted' ? '✓ Imported' :
                                               member.status === 'rejected' ? '✗ Dismissed' :
                                               '• Pending'}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center justify-between gap-2">
                                  <span>Awaiting response</span>
                                  <span className={`px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap flex-shrink-0 ${
                                    idea.status === 'accepted' ? (isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700') :
                                    idea.status === 'rejected' ? (isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700') :
                                    (isDarkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                                  }`}>
                                    {idea.status === 'accepted' ? '✓ Imported' :
                                     idea.status === 'rejected' ? '✗ Dismissed' :
                                     '• Pending'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Recipient Information - matching "Sender Information" layout from received */}
                        <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-100'}`}>
                          <div className="flex items-center gap-2 text-sm">
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"/>
                            </svg>
                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>
                              <span className="font-medium">Shared to:</span> {idea.is_group_share ? (idea.group_info?.group_name || 'Group') : idea.recipient_email}
                            </span>
                          </div>
                          <div className={`flex items-center gap-2 text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            Sent on {new Date(idea.shared_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Close Button - matching received format */}
              <div className={`flex justify-end pt-6 border-t mt-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                  onClick={() => setShowSharedByMeModal(false)}
                  className="px-6 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Dashboard Modal */}
      {showAdminDashboard && isAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-slate-50'} rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700 bg-[#1a2235]' : 'border-slate-200 bg-white'} flex items-center justify-between`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Admin Dashboard
              </h3>
              <button
                onClick={() => setShowAdminDashboard(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(95vh-60px)]">
              <AdminDashboard user={{...user, email: actualUserEmail}} API_BASE_URL={API_BASE_URL} isDarkMode={isDarkMode} />
            </div>
          </div>
        </div>
      )}
      
      {/* Global Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        warningMessage={confirmModal.warningMessage}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        confirmButtonClass={confirmModal.confirmButtonClass}
        isDarkMode={isDarkMode}
      />


      {/* Global Search Modal */}
      {showMobileSearch && (
        <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/80' : 'bg-black/50'} z-50 flex items-start justify-center pt-16 sm:pt-24 p-4`} onClick={() => { setShowMobileSearch(false); setSearchQuery(''); setSearchIdeaFilter(null); }}>
          <div 
            className={`${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search header */}
            <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Search</h3>
              <button
                onClick={() => { setShowMobileSearch(false); setSearchQuery(''); setSearchIdeaFilter(null); }}
                className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Search input */}
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="relative">
                <svg className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="$ticker • @handle • keyword • shortcut:"
                  value={searchQuery}
                  onChange={(e) => {
                    console.log('📝 MOBILE SEARCH INPUT onChange:', e.target.value);
                    setSearchQuery(e.target.value);
                  }}
                  autoFocus
                  className={`w-full border rounded-lg pl-11 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' 
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>
            </div>
          
            {/* Live search results */}
            <div 
              className={`max-h-80 overflow-y-auto ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'}`}
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: isDarkMode ? '#475569 #1e293b' : '#cbd5e1 #f1f5f9'
              }}
            >
              {searchQuery.trim() ? (
                <>
                  {/* Company name suggestions - show at top when typing */}
                  {companySuggestions.length > 0 && searchQuery && !searchQuery.startsWith('$') && !searchQuery.startsWith('@') && !searchQuery.includes(':') && (
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`}>
                          Companies
                        </span>
                      </div>
                      <div className="space-y-1">
                        {companySuggestions.map((company, idx) => (
                          <button
                            key={`${company.ticker}-${idx}`}
                            onClick={() => {
                              setSearchQuery(`$${company.ticker}`);
                              setCompanySuggestions([]);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                              isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                            }`}
                          >
                            <span className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {company.name}
                            </span>
                            <span className={`text-sm font-mono font-semibold ml-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              {'$'}{company.ticker}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Poll search results */}
                  {pollSearchResults !== null ? (
                    <div className="p-4">
                      {pollSearchResults.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                            </svg>
                            <span className="text-orange-500 text-sm font-medium">
                              {pollSearchResults.length} {pollSearchResults.length === 1 ? 'poll' : 'polls'}
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            {pollSearchResults.slice(0, 5).map((poll, idx) => (
                              <button
                                key={poll.id || idx}
                                onClick={() => {
                                  setSelectedPoll(poll);
                                  setActiveTab('threads');
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                }}
                                className={`w-full text-left p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                      {poll.title || 'Investment Poll'}
                                    </p>
                                    <p className={`text-xs mt-1 truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      {poll.ticker_count || 0} tickers · {poll.tickers_discovered?.slice(0, 3).map(t => `$${t}`).join(', ')}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No polls found</p>
                        </div>
                      )}
                    </div>
                  ) : tickersCategoryResults !== null ? (
                    /* Tickers category search results */
                    <div className="p-4">
                      {/* Header with back button when category selected */}
                      <div className="flex items-center gap-2 mb-4">
                        {tickersCategoryResults.selectedCategory ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setTickersCategoryResults({
                                  ...tickersCategoryResults,
                                  selectedCategory: null
                                });
                              }}
                              className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
                            >
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                              </svg>
                            </button>
                            <span className={`text-sm font-medium capitalize ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {tickersCategoryResults.selectedCategory} ({tickersCategoryResults[tickersCategoryResults.selectedCategory]?.count || 0})
                            </span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className="text-yellow-500 text-sm font-medium">Tickers Categories</span>
                          </>
                        )}
                      </div>
                      
                      {/* Show category items if a category is selected */}
                      {tickersCategoryResults.selectedCategory ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {tickersCategoryResults[tickersCategoryResults.selectedCategory]?.items?.length > 0 ? (
                            tickersCategoryResults[tickersCategoryResults.selectedCategory].items.map((item, idx) => (
                              <button
                                key={item.id || item._id || idx}
                                type="button"
                                onClick={() => {
                                  // Navigate to the specific item
                                  const category = tickersCategoryResults.selectedCategory;
                                  
                                  if (category === 'curations') {
                                    // For curations (watchlists), set the selected watchlist and open it
                                    setSelectedWatchlistId(item.id || item._id);
                                    setWatchlistView('my');
                                    setActiveTab('watchlists');
                                  } else if (category === 'polls' || category === 'requests') {
                                    // For polls and requests, set the selected poll and open it
                                    setSelectedPoll(item);
                                    setViewAllPollsMode(false); // Exit View All mode to show the selected poll
                                    setActiveTab('threads');
                                  } else if (category === 'watchlists') {
                                    // For watchlists, set the selected watchlist
                                    setSelectedWatchlistId(item.id || item._id);
                                    setWatchlistView('my');
                                    setActiveTab('watchlists');
                                  } else if (category === 'received') {
                                    // For received items, check if it's a poll or watchlist
                                    if (item.tweet_id || item.replies_count !== undefined) {
                                      // It's a poll
                                      setSelectedPoll(item);
                                      setViewAllPollsMode(false); // Exit View All mode to show the selected poll
                                      setActiveTab('threads');
                                    } else {
                                      // It's a watchlist
                                      setSelectedWatchlistId(item.id || item._id);
                                      setWatchlistView('received');
                                      setActiveTab('watchlists');
                                    }
                                  }
                                  
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setTickersCategoryResults(null);
                                }}
                                className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {item.name || item.title || item.question || item.tweet_text?.slice(0, 50) || 'Untitled'}
                                </p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {item.tickers?.length ? `${item.tickers.length} ticker${item.tickers.length > 1 ? 's' : ''}` : 
                                   item.unique_tickers?.length ? `${item.unique_tickers.length} ticker${item.unique_tickers.length > 1 ? 's' : ''}` :
                                   item.replies_count ? `${item.replies_count} replies` : 
                                   'View details'}
                                </p>
                              </button>
                            ))
                          ) : (
                            <p className={`text-center py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              No items found
                            </p>
                          )}
                        </div>
                      ) : (
                        /* Show category list */
                        <div className="space-y-2">
                        {/* Curations - clipboard/checklist icon */}
                        {tickersCategoryResults.curations && (
                          <button
                            type="button"
                            data-testid="tickers-category-curations"
                            onClick={() => {
                              // Show curations in the search results - set a filtered view
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'curations'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Curations</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.curations.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        
                        {/* Requests - question mark icon - ROSE */}
                        {tickersCategoryResults.requests && (
                          <button
                            type="button"
                            data-testid="tickers-category-requests"
                            onClick={() => {
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'requests'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-rose-500/20' : 'bg-rose-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Requests</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.requests.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        
                        {/* Sectors - tag icon - EMERALD */}
                        {tickersCategoryResults.sectors && (
                          <button
                            type="button"
                            data-testid="tickers-category-sectors"
                            onClick={() => {
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'sectors'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Sectors</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.sectors.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        
                        {/* Custom Lists - checklist icon - CYAN */}
                        {tickersCategoryResults.custom && (
                          <button
                            type="button"
                            data-testid="tickers-category-custom"
                            onClick={() => {
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'custom'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Custom Lists</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.custom.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        
                        {/* X Polls - bar chart icon - AMBER */}
                        {tickersCategoryResults.polls && (
                          <button
                            type="button"
                            data-testid="tickers-category-polls"
                            onClick={() => {
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'polls'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>X Polls</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.polls.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        
                        {/* Received - share network icon */}
                        {tickersCategoryResults.received && (
                          <button
                            type="button"
                            data-testid="tickers-category-received"
                            onClick={() => {
                              // Show received items in the search results
                              setTickersCategoryResults({
                                ...tickersCategoryResults,
                                selectedCategory: 'received'
                              });
                            }}
                            className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                                  <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                  </svg>
                                </div>
                                <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Received</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tickersCategoryResults.received.count}</span>
                                <svg className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                                </svg>
                              </div>
                            </div>
                          </button>
                        )}
                        </div>
                      )}
                      
                      {/* Show "View All in Tickers Tab" button */}
                      <button
                        type="button"
                        data-testid="tickers-view-all"
                        onClick={() => {
                          setActiveTab('watchlists');
                          setShowMobileSearch(false);
                          setSearchQuery('');
                          setTickersCategoryResults(null);
                        }}
                        className={`w-full mt-4 p-3 rounded-lg text-center text-sm font-medium transition-colors cursor-pointer ${
                          isDarkMode 
                            ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        View All in Tickers Tab →
                      </button>
                    </div>
                  ) : listSearchResults !== null ? (
                    <div className="p-4">
                      {listSearchResults.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                            </svg>
                            <span className="text-yellow-500 text-sm font-medium">
                              {listSearchResults.length} {listSearchResults.length === 1 ? 'ticker' : 'tickers'}
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            {listSearchResults.slice(0, 5).map((wl, idx) => (
                              <button
                                key={wl.id || idx}
                                onClick={() => {
                                  setSelectedWatchlistId(wl.id);
                                  setActiveTab('watchlists');
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setListSearchResults(null);
                                }}
                                className={`w-full text-left p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                      {wl.name}
                                    </p>
                                    <p className={`text-xs mt-1 truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      {wl.tickers?.length || 0} tickers
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No tickers found</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                  {/* Special handling for @ search - show handles list using searchDisplayMode state */}
                  {searchDisplayMode === 'handles' ? (
                    <div className="p-4">
                      {uniqueHandlesList && uniqueHandlesList.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-green-500 text-sm font-medium">
                              {uniqueHandlesList.length} handles
                            </span>
                          </div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {uniqueHandlesList.map((handle, idx) => (
                              <button
                                key={handle || idx}
                                onClick={() => {
                                  const displayHandle = handle.startsWith('@') ? handle : `@${handle}`;
                                  setSearchQuery(displayHandle);
                                  setSearchDisplayMode('results'); // Switch to results mode when clicking a handle
                                }}
                                className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                {handle.startsWith('@') ? handle : `@${handle}`}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No handles found</p>
                          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Import bookmarks to see authors</p>
                        </div>
                      )}
                    </div>
                  ) : searchDisplayMode === 'briefs' ? (
                    <div className="p-4">
                      {filesSearchResults && filesSearchResults.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-blue-500 text-sm font-medium">
                              {filesSearchResults.length} {filesSearchResults.length === 1 ? 'file' : 'files'}
                            </span>
                          </div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {filesSearchResults.map((file, idx) => (
                              <button
                                key={file.viewer_id || idx}
                                onClick={() => {
                                  // Navigate to Files tab and open this specific file
                                  if (file.viewer_id) {
                                    setOpenFileViewerId(file.viewer_id);
                                    setActiveTab('files');
                                  }
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                }}
                                className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                <span className="font-medium">{file.ticker || 'Unknown'}</span>
                                {file.company_name && (
                                  <span className={`ml-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {file.company_name}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No files found</p>
                          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Type a ticker after files: to search</p>
                        </div>
                      )}
                    </div>
                  ) : groupsSearchResults !== null && groupsSearchResults.triggered ? (
                    /* Groups search results - use fetched groups from API */
                    (() => {
                      const filterQuery = groupsSearchResults.query || '';
                      // Use groups from API response, fallback to userGroups
                      const allGroups = groupsSearchResults.groups || userGroups;
                      const filteredGroups = filterQuery 
                        ? allGroups.filter(g => {
                            const name = (g.name || g.group_name || '').toLowerCase();
                            return name.includes(filterQuery);
                          })
                        : allGroups;
                      return (
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-purple-500 text-sm font-medium">
                              {filteredGroups.length} {filteredGroups.length === 1 ? 'group' : 'groups'}
                            </span>
                          </div>
                          {filteredGroups.length > 0 ? (
                            <div className="space-y-1 max-h-96 overflow-y-auto">
                              {filteredGroups.map((group, idx) => (
                                <button
                                  key={group.group_id || idx}
                                  onClick={() => {
                                    setActiveTab('groups');
                                    setShowMobileSearch(false);
                                    setSearchQuery('');
                                  }}
                                  className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                    isDarkMode 
                                      ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{group.name || group.group_name || 'Unnamed Group'}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      group.user_role === 'admin' 
                                        ? (isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600')
                                        : (isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600')
                                    }`}>
                                      {group.user_role === 'admin' ? 'Admin' : 'Member'}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No groups found</p>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : ideasSearchResults !== null ? (
                    /* Ideas search results */
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span className="text-yellow-500 text-sm font-medium">
                          {ideasSearchResults.length} {ideasSearchResults.length === 1 ? 'idea' : 'ideas'}
                        </span>
                      </div>
                      {ideasSearchResults.length > 0 ? (
                        <div className="space-y-1 max-h-96 overflow-y-auto">
                          {ideasSearchResults.map((idea, idx) => {
                            const displayTicker = idea.tickers?.[0]?.ticker || idea.tickers?.[0] || '';
                            return (
                              <button
                                key={idea.id || idx}
                                onClick={() => {
                                  // Navigate to Ideas page and filter to show this idea
                                  setSearchIdeaFilter([idea]);
                                  setActiveTab('dashboard');
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                }}
                                className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  {displayTicker && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      isDarkMode 
                                        ? 'bg-yellow-500/20 text-yellow-400' 
                                        : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      ${displayTicker}
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {idea.title || idea.one_liner_thesis || idea.summary?.slice(0, 60) || 'Investment idea'}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No ideas found</p>
                        </div>
                      )}
                    </div>
                  ) : foldersSearchResults !== null && foldersSearchResults.triggered ? (
                    /* Folders/Scheduled research results - use fetched items from API */
                    (() => {
                      const filterQuery = foldersSearchResults.query || '';
                      // Use items fetched from API (or empty array if not yet loaded)
                      const allScheduleItems = foldersSearchResults.items || [];
                      const filteredFolders = filterQuery 
                        ? allScheduleItems.filter(item => {
                            const ticker = (item.ticker || '').toLowerCase();
                            const notes = (item.notes || '').toLowerCase();
                            const company = (item.company_name || '').toLowerCase();
                            return ticker.includes(filterQuery) || notes.includes(filterQuery) || company.includes(filterQuery);
                          })
                        : allScheduleItems;
                      return (
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            <span className="text-green-500 text-sm font-medium">
                              {filteredFolders.length} {filteredFolders.length === 1 ? 'scheduled item' : 'scheduled items'}
                            </span>
                          </div>
                          {filteredFolders.length > 0 ? (
                            <div className="space-y-1 max-h-96 overflow-y-auto">
                              {filteredFolders.map((item, idx) => (
                                <button
                                  key={item.id || idx}
                                  onClick={() => {
                                    // Navigate to the folder with the correct date expanded
                                    if (item.scheduled_date) {
                                      const itemDate = new Date(item.scheduled_date + 'T00:00:00');
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      
                                      // Set appropriate filter based on date
                                      if (itemDate < today) {
                                        setScheduleFilterBy('past');
                                      } else {
                                        setScheduleFilterBy('31days');
                                      }
                                      
                                      // Set the expanded date to the item's date
                                      setExpandedFolderDate(item.scheduled_date);
                                      
                                      // Also expand the specific item within the folder
                                      if (item.ticker) {
                                        const itemKey = `list-${item.ticker}-${item.scheduled_date}`;
                                        setExpandedScheduleItem(itemKey);
                                      }
                                    }
                                    setActiveTab('schedule');
                                    setShowMobileSearch(false);
                                    setSearchQuery('');
                                  }}
                                  className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                    isDarkMode 
                                      ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
                                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        isDarkMode 
                                          ? 'bg-green-500/20 text-green-400' 
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        ${item.ticker || 'Unknown'}
                                      </span>
                                      {item.company_name && (
                                        <span className={`text-xs truncate max-w-[150px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                          {item.company_name}
                                        </span>
                                      )}
                                    </div>
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      {item.scheduled_date ? new Date(item.scheduled_date + 'T00:00:00').toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No scheduled items found</p>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (groupedSearchResults && groupedSearchResults.length > 0) || (globalTickerResults?.watchlists?.length > 0) || (globalTickerResults?.files?.length > 0) || (globalTickerResults?.research_items?.length > 0) || (globalKeywordResults?.watchlists?.length > 0) || (globalKeywordResults?.polls?.length > 0) || (globalKeywordResults?.tickers_by_company?.length > 0) ? (
                    <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                      {/* IDEAS - Show from groupedSearchResults */}
                      {groupedSearchResults && groupedSearchResults.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              Ideas ({groupedSearchResults.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {groupedSearchResults.slice(0, 5).map((item, idx) => {
                              const idea = item.type === 'thread' ? item.parentIdea : 
                                          item.type === 'ideas-list' ? item.bookmark :
                                          item.idea || item;
                              if (!idea) return null;
                              const queryLower = searchQuery.toLowerCase().replace('$', '');
                              const tickers = idea.tickers || [];
                              const matchedTicker = tickers.find(t => {
                                const tickerStr = typeof t === 'string' ? t : t?.ticker || '';
                                return tickerStr.toLowerCase().includes(queryLower);
                              });
                              const displayTicker = matchedTicker 
                                ? (typeof matchedTicker === 'string' ? matchedTicker : matchedTicker?.ticker)
                                : (tickers[0]?.ticker || tickers[0]);
                              return (
                                <button
                                  key={idea.id || idx}
                                  onClick={() => {
                                    setSearchIdeaFilter([idea]);
                                    setActiveTab('dashboard');
                                    setShowMobileSearch(false);
                                  }}
                                  className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {displayTicker && (
                                      <span className={`font-bold text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`}>
                                        ${displayTicker}
                                      </span>
                                    )}
                                    <span className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                      {idea.title || idea.one_liner_thesis || 'Investment idea'}
                                    </span>
                                  </div>
                                  <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {idea.author || idea.tweet_author || idea.from_name || 'Unknown source'}
                                  </p>
                                </button>
                              );
                            })}
                            {groupedSearchResults.length > 5 && (
                              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{groupedSearchResults.length - 5} more
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* TICKERS - from globalTickerResults.watchlists */}
                      {globalTickerResults?.watchlists?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              Tickers ({globalTickerResults.watchlists.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalTickerResults.watchlists.map((wl, idx) => (
                              <button
                                key={wl.id || `wl-${idx}`}
                                data-testid={`global-search-tickers-${idx}`}
                                onClick={() => { 
                                  setSelectedWatchlistId(wl.id);
                                  setWatchlistView('my');
                                  setActiveTab('watchlists'); 
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setGlobalTickerResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {wl.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* FILES - from globalTickerResults.files */}
                      {globalTickerResults?.files?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              Files ({globalTickerResults.files.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalTickerResults.files.map((file, idx) => (
                              <button
                                key={file.viewer_id || `file-${idx}`}
                                data-testid={`global-search-file-${idx}`}
                                onClick={() => {
                                  // Navigate to Files tab and open this specific file
                                  if (file.viewer_id) {
                                    setOpenFileViewerId(file.viewer_id);
                                    setActiveTab('files');
                                  }
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setGlobalTickerResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {file.company_name || file.ticker}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* FOLDERS - from globalTickerResults.research_items */}
                      {globalTickerResults?.research_items?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              Folders ({globalTickerResults.research_items.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalTickerResults.research_items.map((item, idx) => (
                              <button
                                key={item.id || `folder-${idx}`}
                                data-testid={`global-search-folder-${idx}`}
                                onClick={() => { 
                                  // Navigate to the specific folder/schedule item
                                  if (item.scheduled_date) {
                                    setExpandedFolderDate(item.scheduled_date);
                                    // Expand the specific item within the folder
                                    if (item.ticker) {
                                      const itemKey = `list-${item.ticker}-${item.scheduled_date}`;
                                      setExpandedScheduleItem(itemKey);
                                    }
                                  }
                                  setActiveTab('schedule'); 
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setGlobalTickerResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {item.ticker ? `${item.ticker.toUpperCase()} - ` : ''}{item.scheduled_date ? new Date(item.scheduled_date).toLocaleDateString() : item.title || 'Research'}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* LISTS/CURATIONS - from globalKeywordResults.watchlists */}
                      {globalKeywordResults?.watchlists?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                              Lists ({globalKeywordResults.watchlists.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalKeywordResults.watchlists.slice(0, 5).map((wl, idx) => (
                              <button
                                key={wl.id || `kw-wl-${idx}`}
                                data-testid={`global-keyword-list-${idx}`}
                                onClick={() => { 
                                  setSelectedWatchlistId(wl.id);
                                  setWatchlistView('my');
                                  setActiveTab('watchlists'); 
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setGlobalKeywordResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {wl.name}
                                  </span>
                                  <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {wl.ticker_count} tickers
                                  </span>
                                </div>
                              </button>
                            ))}
                            {globalKeywordResults.watchlists.length > 5 && (
                              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{globalKeywordResults.watchlists.length - 5} more lists
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* POLLS - from globalKeywordResults.polls */}
                      {globalKeywordResults?.polls?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-pink-500/10' : 'bg-pink-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-pink-400' : 'text-pink-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                              Polls ({globalKeywordResults.polls.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalKeywordResults.polls.slice(0, 5).map((poll, idx) => (
                              <button
                                key={poll.id || `kw-poll-${idx}`}
                                data-testid={`global-keyword-poll-${idx}`}
                                onClick={() => { 
                                  setSelectedPollId(poll.id);
                                  setActiveTab('watchlists');
                                  setShowMobileSearch(false);
                                  setSearchQuery('');
                                  setGlobalKeywordResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'} line-clamp-2`}>
                                  {poll.title || 'Poll'}
                                </span>
                              </button>
                            ))}
                            {globalKeywordResults.polls.length > 5 && (
                              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{globalKeywordResults.polls.length - 5} more polls
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* RELATED COMPANIES - from globalKeywordResults.tickers_by_company */}
                      {globalKeywordResults?.tickers_by_company?.length > 0 && (
                        <div>
                          <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded ${isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                              Related Companies ({globalKeywordResults.tickers_by_company.length})
                            </span>
                          </div>
                          <div className="space-y-2">
                            {globalKeywordResults.tickers_by_company.slice(0, 5).map((company, idx) => (
                              <button
                                key={company.ticker || `kw-company-${idx}`}
                                data-testid={`global-keyword-company-${idx}`}
                                onClick={() => { 
                                  // Search for this ticker
                                  setSearchQuery(`$${company.ticker}`);
                                  setShowMobileSearch(true);
                                  setGlobalKeywordResults(null);
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-[#1a2235] hover:bg-[#141c2e]' : 'bg-slate-100 hover:bg-slate-200'}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                      ${company.ticker}
                                    </span>
                                    <span className={`text-sm ml-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                      {company.company_name}
                                    </span>
                                  </div>
                                </div>
                                {company.industry && (
                                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {company.industry}
                                  </p>
                                )}
                              </button>
                            ))}
                            {globalKeywordResults.tickers_by_company.length > 5 && (
                              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{globalKeywordResults.tickers_by_company.length - 5} more companies
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (globalTickerResults !== null || globalKeywordResults !== null || searchResults !== null) ? (
                    <div className="p-4 text-center">
                      <svg className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No results for &quot;{searchQuery}&quot;</p>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Try a different search term</p>
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                      <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Searching...</p>
                    </div>
                  )}
                    </>
                  )}
                </>
              ) : (
                <div className="p-4">
                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>Quick shortcuts:</p>
                  <div className="space-y-2">
                    {[
                      { query: 'groups:', hint: 'search groups' },
                      { query: 'tickers:', hint: 'browse tickers' },
                      { query: 'ideas:', hint: 'search ideas' },
                      { query: 'files:', hint: 'search files' },
                      { query: 'folders:', hint: 'search folders' },
                    ].map((item) => (
                      <button
                        key={item.query}
                        onClick={() => setSearchQuery(item.query)}
                        className={`block w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                          isDarkMode 
                            ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' 
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <span className="font-mono">{item.query}</span>
                        <span className={`ml-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{item.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal - For opening files from search */}
      {searchFileViewer.show && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex flex-col">
          {/* Header with back button */}
          <div className={`flex items-center justify-between px-4 py-3 ${isDarkMode ? 'bg-[#1a2235]' : 'bg-white'} border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              onClick={() => setSearchFileViewer({ show: false, url: null, ticker: null })}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-white' : 'hover:bg-slate-100 text-slate-900'}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back</span>
            </button>
            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {searchFileViewer.ticker}
            </span>
            <div className="w-20"></div>
          </div>
          {/* Iframe for document */}
          <div className="flex-1 overflow-hidden">
            <iframe
              src={searchFileViewer.url}
              className="w-full h-full border-0"
              title={`Document for ${searchFileViewer.ticker}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Analysis Page Component - Requires Authentication
function AnalysisPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);

  // Check authentication on page load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user data exists in localStorage
        const userData = localStorage.getItem('tickler_user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Verify authentication with backend
          const response = await fetch(`${API_BASE_URL}/api/auth/twitter/status/${parsedUser.user_id}`);
          if (response.ok) {
            const authStatus = await response.json();
            if (authStatus.authenticated) {
              // User is authenticated, generate analysis
              await generateAnalysis(ticker);
            } else {
              // Authentication expired, redirect to login
              redirectToLogin();
            }
          } else {
            redirectToLogin();
          }
        } else {
          // No user data, redirect to login
          redirectToLogin();
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        redirectToLogin();
      }
    };

    checkAuth();
  }, [ticker]);

  const redirectToLogin = () => {
    // Store the intended destination for after login
    localStorage.setItem('tickler_redirect_after_login', `/analysis/${ticker}`);
    // Redirect to main dashboard which will show login screen
    navigate(`/?login=required&ticker=${ticker}`);
  };

  const generateAnalysis = async (ticker) => {
    try {
      setIsLoading(true);
      setError(null);

      // Generate web viewer for the ticker
      const response = await fetch(`${API_BASE_URL}/api/generate-web-viewer/${ticker}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filing_types: ['10-K', '10-Q', '8-K'],
          user_id: user?.id  // REQUIRED for data isolation
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.viewer_id) {
          // Track file view
          try {
            await fetch(`${API_BASE_URL}/api/engagement`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                event_type: 'file_view',
                event_category: 'files',
                event_data: { ticker: ticker, viewer_id: result.viewer_id, source: 'ticker_analysis' }
              })
            });
          } catch (e) { console.log('Engagement tracking failed:', e); }
          // Redirect to the generated viewer
          window.location.href = `${API_BASE_URL}/api/viewer/${result.viewer_id}`;
        } else {
          setError('Failed to generate analysis viewer');
        }
      } else {
        const errorText = await response.text();
        setError(`Failed to generate analysis: ${errorText}`);
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      setError('Error generating analysis. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      // Generate a temporary user ID for the OAuth flow
      const tempUserId = `temp_${Date.now()}`;
      
      // Initiate Twitter OAuth
      const response = await fetch(`${API_BASE_URL}/api/auth/twitter/login?user_id=${tempUserId}`);
      if (response.ok) {
        const data = await response.json();
        
        // Store temp user ID and redirect URL for callback verification
        localStorage.setItem('tickler_temp_user', tempUserId);
        localStorage.setItem('tickler_redirect_after_login', `/analysis/${ticker}`);
        
        // Redirect to Twitter OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to initiate Twitter login');
      }
    } catch (error) {
      console.error('Error initiating Twitter login:', error);
      setError('Failed to start Twitter login. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Generating Investment Analysis
          </h2>
          <p className="text-gray-600">
            Preparing comprehensive research for ${ticker?.toUpperCase()}...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-lg p-8">
          <div className="mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3Icon className="w-8 h-8 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Login Required
            </h2>
            <p className="text-gray-600 mb-4">
              Please sign in to access the ${ticker?.toUpperCase()} investment analysis.
            </p>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          <button
            onClick={handleLogin}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-md transition-colors mb-4"
          >
            Sign in with X
          </button>
          
          <p className="text-xs text-gray-500">
            Sign in to access comprehensive SEC filings, financial analysis, and investment research.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-lg p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExternalLinkIcon className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Analysis Error
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => generateAnalysis(ticker)}
            className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null; // This component redirects to the viewer, so no render needed
}

// App with Routing - Routes moved to index.js with auth protection
function App() {
  return (
    <Routes>
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/save" element={<SaveMethodsPage />} />
      <Route path="/quick-save" element={<QuickSavePage />} />
      <Route path="/watchlist-spreadsheet" element={<WatchlistSpreadsheet />} />
      <Route path="/landing" element={<LandingPageComponent />} />
      <Route path="/analysis/:ticker" element={<AnalysisPage />} />
      <Route path="/*" element={<MainDashboard />} />
    </Routes>
  );
}

export default App;
